// FUTURE GEMINI: This is the core visual component. It is a complex, stateful canvas renderer.
// - Pay close attention to the `draw` function. This is where all points, lines, and symbols are rendered.
// - The `transform` state (scale, offsetX, offsetY) controls the user's viewport (pan and zoom).
// - Event handlers (`handleWheel`, `handleMouseDown`, etc.) manage user interaction. When modifying them, be very careful with dependency arrays in `useCallback` to avoid stale closures.
// - The floating toolbar's default seat is the right edge, directly below the north arrow. Preserve its contents and functionality unless specifically asked to change it.

import React, { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
    AgentType,
    type SurveyPoint,
    type SurveyLine,
    type ContourLabel,
    type StreetLabel,
    type Centerline,
    type CustomSymbol,
    type AnnotationRule,
    type Settings,
    type WmsService,
    type ActiveWmsLayer,
    type OfflineMapArea,
    type PointList,
    type DeedMetadata,
    type AnnotationDimension,
    type AlignedAnnotationDimension,
    type HorizontalAnnotationDimension,
    type VerticalAnnotationDimension,
    type RadiusAnnotationDimension,
    type DiameterAnnotationDimension,
    type ArcLengthAnnotationDimension,
    type CurveDataAnnotationDimension,
    type AngularAnnotationDimension,
    type OrdinateAnnotationDimension,
    type DimensionGrip,
    type DimensionGripType,
    type BoundaryFile,
    type BoundaryFileCall,
    type TinSurface,
    type ParcelCadTextStyle,
    type ParcelLabelFormatter
} from '../types.ts';
import type { LinetypeDefinition } from '../contexts/types/CadManager.types.ts';
import { calculateCenterlineLength, calculateCurveGeometry, calculatePointFromStationOffset, formatStation } from '../utils/stationing.ts';
import { parseBearingToRadians, parseDistance, direct, inverse, formatBearing, calculateClosure, directCurve, calculateChordDistance } from '../utils/cogo.ts';
import { solveRigidCornerAlign } from '../utils/cornerAlign.ts';
import { buildSymbolMatchers, resolveSymbol } from '../utils/symbolResolver.ts';
import { findMatchingSymbol, libraryToSymbolDefinition } from '../data/surveySymbolLibrary.ts';
import { buildAnnotationMatchers, resolveAnnotation, renderAnnotationTemplate } from '../utils/annotationResolver.ts';
import { fetchImageBlobViaProxy } from '../utils/corsProxy.ts';
import { getCachedBitmap, planMapTiles, mapWithConcurrency, decodeImageCached, trackTileLoad, subscribeTileActivity, getPendingTileCount, type PlannedTile } from '../utils/mapTileCache.ts';
import { WebGpuBasemap, GpuLineBatch, parseCssColor, isWebGpuEnabled, WEBGPU_PREF_EVENT, type BasemapLayer, type BasemapSource, type WorldQuad } from '../utils/webgpuBasemap.ts';
import { buildParcelLabelLines, resolveParcelTextStyle } from '../utils/parcelLabelFormatter.ts';
import { applyAnnotationTextCase, resolveAnnotationCategoryTextStyle, resolveAnnotationTextSize } from '../utils/annotationTextStyle.ts';
import { getStaticMap, type StaticMapRequest } from '../services/staticMapsService.ts';
import { useCadManager } from '../contexts/CadManagerContext.tsx';
import { useAppState, useErrorReporter } from '../contexts/AppStateContext.tsx';
import { useDocumentScrollLock } from '../hooks/useDocumentScrollLock.ts';
import { StandardsEditor } from './CadManager/StandardsEditor.tsx';
import type { StandardDefinition } from '../contexts/types/CadManager.types';
import { convertLinearUnits, getLinearUnitAbbreviation, type LinearUnit } from '../utils/linearUnits.ts';
import { placeFlippingMenu, type FlippingMenuPlacement } from '../utils/floatingMenuPosition.ts';
import { LayersIcon, RedrawIcon, ZoomExtentsIcon, ScaleIcon, AttributeScaleIcon, DownloadIcon, ZoomToPointIcon, XMarkIcon, EyeIcon, PencilSquareIcon, BreaklineIcon, InclusionLineIcon, ExclusionLineIcon, ScissorsIcon, UndoIcon, RedoIcon, ExtendIcon, PolylineIcon, ListBulletIcon, CircleIcon, CrosshairsIcon, TrashIcon, DocumentDuplicateIcon } from './icons.tsx';
import {
    ToolbarZoomIcon,
    ToolbarAnnotationScaleIcon,
    ToolbarLayersIcon,
    ToolbarSurfaceIcon,
    ToolbarSelectIcon,
    ToolbarAddPointIcon,
    ToolbarDrawToolsIcon,
    ToolbarDimensionIcon,
    ToolbarStandardsIcon,
    ToolbarSymbolExclusionIcon,
    ToolbarShrinkwrapIcon,
} from './toolbarIcons.tsx';
import proj4 from 'proj4';
import { createFloatingOriginContext } from '../utils/floatingOriginContext.ts';
import { PointAgent } from '../services/PointAgent.ts';
import { DimensionSettingsModal, type DimensionStyleSettings, DEFAULT_DIMENSION_SETTINGS } from './DimensionSettingsModal.tsx';
import { DimensionSettingsPanel } from './DimensionSettingsPanel.tsx';
import {
    formatDMS,
    formatBearingFromPoints,
    calculateCurveData,
    calculateLinearDimensionGeometry,
    calculateRadialDimensionGeometry,
    calculateDiameterDimensionGeometry,
    calculateArcLengthDimensionGeometry,
    calculateAngularDimensionGeometry,
    hitTestDimension,
} from '../utils/dimensionGeometry.ts';

/**
 * Compute the unique circular arc that starts at `start`, leaves tangent to
 * the unit vector `tangent` (world frame: x=easting, y=northing), and passes
 * through `end`. Returns null when the geometry is degenerate (endpoint
 * coincident with the start or lying on the tangent line) so callers can
 * fall back to a straight segment. Used by the polyline tool's Arc submode.
 */
export const computeTangentArc = (
    start: { easting: number; northing: number },
    tangent: { x: number; y: number },
    end: { easting: number; northing: number },
): {
    centerEasting: number;
    centerNorthing: number;
    radius: number;
    arcLength: number;
    sweep: number;
    direction: 'left' | 'right';
    tangentBearing: string;
    chordBearing: string;
    endTangent: { x: number; y: number };
} | null => {
    const cx = end.easting - start.easting;
    const cy = end.northing - start.northing;
    const chordLenSq = cx * cx + cy * cy;
    if (chordLenSq < 1e-12) return null;
    // Center lies on the tangent's left normal (world frame is y-up, CCW+).
    const nx = -tangent.y;
    const ny = tangent.x;
    const denom = 2 * (cx * nx + cy * ny);
    if (Math.abs(denom) < 1e-9) return null;
    const signedRadius = chordLenSq / denom; // positive → center left of travel
    const radius = Math.abs(signedRadius);
    const direction: 'left' | 'right' = signedRadius > 0 ? 'left' : 'right';
    const centerEasting = start.easting + signedRadius * nx;
    const centerNorthing = start.northing + signedRadius * ny;
    const a0 = Math.atan2(start.northing - centerNorthing, start.easting - centerEasting);
    const a1 = Math.atan2(end.northing - centerNorthing, end.easting - centerEasting);
    const rawSweep = direction === 'left' ? a1 - a0 : a0 - a1;
    const sweep = ((rawSweep % (2 * Math.PI)) + 2 * Math.PI) % (2 * Math.PI);
    if (sweep < 1e-9 || 2 * Math.PI - sweep < 1e-9) return null;
    const signedSweep = direction === 'left' ? sweep : -sweep;
    const cosS = Math.cos(signedSweep);
    const sinS = Math.sin(signedSweep);
    const normalizeBearing = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
    return {
        centerEasting,
        centerNorthing,
        radius,
        arcLength: radius * sweep,
        sweep,
        direction,
        tangentBearing: formatBearing(normalizeBearing(Math.atan2(tangent.x, tangent.y))),
        chordBearing: formatBearing(normalizeBearing(Math.atan2(cx, cy))),
        endTangent: {
            x: tangent.x * cosS - tangent.y * sinS,
            y: tangent.x * sinS + tangent.y * cosS,
        },
    };
};


export interface DrawingCanvasHandles {
  zoomExtents: () => void;
  /** Zoom canvas to a specific bounding box in project coordinates. */
  zoomToBbox: (minE: number, maxE: number, minN: number, maxN: number, padding?: number) => void;
  redraw: () => void;
  finishDrawing: () => void;
  cancelDeleteMode: () => void;
  confirmDelete: () => void;
  getTransform: () => { scale: number; offsetX: number; offsetY: number };
  setTransform: (transform: { scale: number; offsetX: number; offsetY: number }) => void;
  getViewCenterWorld?: () => { easting: number; northing: number };
  isPointInView?: (easting: number, northing: number) => boolean;
  isBboxInView?: (minE: number, maxE: number, minN: number, maxN: number) => boolean;
  getViewportBboxWorld?: () => { minE: number; maxE: number; minN: number; maxN: number; minX: number; maxX: number; minY: number; maxY: number };
  getMapOverlayTiles?: () => Array<{ image: CanvasImageSource; bbox: [number, number, number, number]; quad?: WorldQuad; opacity?: number }>;
  /** Arms the Orientation Tuple pivot-origin picker from the toolbar instrument. */
  beginOtOriginPick: () => void;
  /** Arms the Orientation Tuple align-to-line picker from the toolbar instrument. */
  beginOtLineAlign: () => void;
}

import { CanvasTerminal, type TerminalTone } from './CanvasTerminal';
import type { CommandReference } from './CommandHelpView';
import { ArrayManipulator } from './ArrayManipulator';
import { PolarArrayManipulator } from './PolarArrayManipulator';
import { PolygonInputHud } from './PolygonInputHud';
import { CommandPromptHud } from './CommandPromptHud';
import { PropertiesDialog } from './PropertiesDialog';
import { parseCanvasCommand, executeCanvasCommand, parseCoordinateToken, MAX_RECTANGULAR_ARRAY_ITEMS, type CommandContext, type CommandPromptSpec } from '../utils/canvasCommands';
import {
    buildClosedSegments,
    polygonVertices,
    rectangleVertices,
    reflectionTransform,
    rotationTransform,
    scalingTransform,
    transformSurveyLine,
    translationTransform,
    type PlanarPoint,
    type PlanarTransform,
} from '../utils/cadTransforms.ts';
import { orderConnectedChain, offsetOrderedChain, offsetCircleRadius, resolveArcGeometry, type ChainSegment, type OffsetEntitySegment } from '../utils/offsetGeometry';

interface DrawingCanvasProps {
    /** Initial 2D canvas transform (scale/zoom & pan offsets) restored from saved session state. */
    initialTransform?: { scale: number; offsetX: number; offsetY: number } | null;
    /** Notified whenever the viewport transform (scale/pan) changes. */
    onTransformChange?: (transform: { scale: number; offsetX: number; offsetY: number }) => void;
    points: SurveyPoint[];
    lines: SurveyLine[];
    contourLabels: ContourLabel[];
    /** Named road labels placed on the plan (v26.05.19.4). */
    streetLabels?: StreetLabel[];
    centerlines: Centerline[];
    customSymbols: CustomSymbol[];
    /**
     * Ids of built-in survey-library symbols (tree, manhole, …) the user has
     * opted into showing. Built-in symbols default to OFF; a point only renders
     * its built-in glyph when the matched library id is in this set. The '*'
     * sentinel means "all built-in symbols enabled".
     */
    enabledBuiltinSymbolIds?: Set<string>;
    pointLayers: { pointNumber: { visible: boolean; color: string; }; description: { visible: boolean; color: string; }; elevation: { visible: boolean; color: string; }; };
    attributeScale: number;
    setAttributeScale: React.Dispatch<React.SetStateAction<number>>;
    lineLabelScale: number;
    setLineLabelScale: React.Dispatch<React.SetStateAction<number>>;
    lineThickness: number;
    setLineThickness: React.Dispatch<React.SetStateAction<number>>;
    contourLabelScale: number;
    onLayerColorChange: (layer: 'pointNumber' | 'description' | 'elevation', color: string) => void;
    activeAgent: AgentType;
    settings: Settings;
    zoomTarget: SurveyPoint | null;
    onZoomComplete: () => void;
    /** Point numbers queued for deletion — rendered with a pulsing red ring so the user can verify before confirming. */
    pendingDeletePointNumbers?: Set<string>;
    onUpdatePointLabelOffset: (pointNumber: string, offset: { x: number; y: number }) => void;
    // Per-point override for the annotation leader offset. Ctrl+drag updates;
    // Shift+click on the note mirrors dx (left ↔ right side of the point).
    onUpdatePointAnnotationOffset?: (pointNumber: string, offset: { dx: number; dy: number }) => void;
    onUpdateLineLabelOffset?: (lineId: string, offset: { x: number; y: number }) => void;
    onToggleLineLabelRotation?: (lineId: string) => void;
    onSwapLineBearingDirection?: (lineId: string) => void;
    onUpdateDeedMetadataOffset?: (offset: { x: number; y: number }) => void;
    onUpdateOwnerNameOffset?: (offset: { x: number; y: number }) => void;
    deedMetadataOffset?: { x: number; y: number }; // Offset for deed metadata box in world coordinates
    ownerNameOffset?: { x: number; y: number }; // Offset for owner name label in world coordinates
    onAddLine: (newLine: SurveyLine) => void;
    onDeleteLine: (lineId: string) => void;
    /** Select-mode point picks — delete one or more project points by number. */
    onDeletePoints?: (pointNumbers: string[]) => void;
    /** Select-mode centerline picks — delete a centerline by id. */
    onDeleteCenterline?: (centerlineId: string) => void;
    /** Apply a multi-entity edit as a single undo step (used by typed CAD commands). */
    onBatchLinesChange?: (removeIds: string[], addLines: SurveyLine[]) => void;
    canUndo: boolean;
    canRedo: boolean;
    onUndo: () => void;
    onRedo: () => void;
    wmsServices: WmsService[];
    activeWmsLayers: ActiveWmsLayer[];
    offlineMapAreas: OfflineMapArea[];
    onRedrawAndShowAll: () => void;
    onOpenDxfExportModal: () => void;
    isLayerPanelOpen: boolean;
    setIsLayerPanelOpen: React.Dispatch<React.SetStateAction<boolean>>;
    onLayerToggle: (layerName: "pointNumber" | "description" | "elevation") => void;
    activeAccent: string;
    currentPosition: GeolocationPosition | null;
    projectedPosition: { northing: number, easting: number } | null;
    onZoomToPoint: (point: SurveyPoint) => void;
    linesVisible: boolean;
    onSetLinesVisible: React.Dispatch<React.SetStateAction<boolean>>;
    centerlinesVisible: boolean;
    onSetCenterlinesVisible: React.Dispatch<React.SetStateAction<boolean>>;
    pointLists: PointList[];
    orientationTuple?: {
        angle: number;
        originX: number;
        originY: number;
        mode?: 'drafting' | 'view';
        draftingAngle?: number;
        draftingOriginX?: number;
        draftingOriginY?: number;
        viewAngle?: number;
        viewOriginX?: number;
        viewOriginY?: number;
        placed?: boolean;
    };
    onSetOrientationTuple?: React.Dispatch<React.SetStateAction<{
        angle: number;
        originX: number;
        originY: number;
        mode?: 'drafting' | 'view';
        draftingAngle?: number;
        draftingOriginX?: number;
        draftingOriginY?: number;
        viewAngle?: number;
        viewOriginX?: number;
        viewOriginY?: number;
        placed?: boolean;
    }>>;
    onTogglePointListVisibility: (listId: string) => void;
    deedMetadata?: DeedMetadata; // Deed information to display on canvas
    onToolStateChange?: (state: {
        isDrawing: boolean;
        isTrimming: boolean;
        isExtending: boolean;
        isDeleting: boolean;
        selectedCount?: number;
        segmentCount?: number;
        lineType?: 'normal' | 'breakline' | 'inclusion' | 'exclusion';
    }) => void;
    onShowSettings?: () => void;
    // C3D Integration props
    isC3DConnected?: boolean;
    /** Faint futuristic background grid — toggled from the app header. */
    isGridVisible?: boolean;
    /** TIN shading — toggled from the app header. */
    tinShading?: boolean;
    // Agent draw mode
    agentDrawMode: 'both' | 'points' | 'lines';
    onCycleAgentDrawMode: () => void;
    // Annotation mode (lifted to App.tsx so the header toggle can control it)
    agentAnnotMode: 'both' | 'bearing' | 'distance';
    onCycleAgentAnnotMode: () => void;
    // Boundary line draw callback (called when user confirms a bearing+distance or curve call)
    onAddBoundaryCall?: (from: SurveyPoint, bearing: string, distance: string, onComplete: (endPt: SurveyPoint) => void, curveOptions?: { curveRadius: number; arcLength: number; curveDirection: 'left' | 'right' }) => void;
    // Closure report callback — receives accumulated drawn calls, returns closure summary or null on error
    onComputeCurrentClosure?: (calls: Array<{ from: string; to: string; bearing: string; distance: string; isCurve?: boolean; curveRadius?: number; arcLength?: number; curveDirection?: 'left' | 'right'; tangentBearing?: string }>) => { precision: string; area: number; misclosureDistance: number; misclosureBearing: string } | null;
    // Convert selected canvas lines to a BoundaryFile in the boundary editor
    onConvertSelectionToBoundary?: (lines: SurveyLine[]) => void;
    // Annotation dimensions
    dimensions: AnnotationDimension[];
    onAddDimension: (dim: AnnotationDimension) => void;
    onUpdateDimension?: (dim: AnnotationDimension) => void;
    onDeleteDimension?: (dimId: string) => void;
    onClearDimensions?: () => void;
    onAddPoint?: (point: SurveyPoint) => void;
    nextAvailablePointNumber?: string;
    dimensionScale: number;
    setDimensionScale: React.Dispatch<React.SetStateAction<number>>;
    symbolScale: number;
    setSymbolScale: React.Dispatch<React.SetStateAction<number>>;
    customAnnotations?: AnnotationRule[];
    annotationScale?: number;
    setAnnotationScale?: React.Dispatch<React.SetStateAction<number>>;
    /** Parcel centroid annotation labels (v26.05.19.15). */
    parcelLabels?: import('../types.ts').ParcelLabel[];
    /** Parcel label field include/exclude and prefix controls. */
    parcelLabelFormatter?: ParcelLabelFormatter;
    /** CAD Manager text style catalog used when parcel styleSource='cad-manager'. */
    parcelCadTextStyles?: ParcelCadTextStyle[];
    /** Per-category annotation styles (v26.05.19.15). */
    annotationCategories?: import('../types.ts').AnnotationCategoryStyle[];
    /** Imported/generated TIN surfaces for wireframe display. */
    tinSurfaces?: TinSurface[];
    // Boundary file overlays
    boundaryFiles?: BoundaryFile[];
    /** Id of the active boundary in the editor. Rendered in a distinct color
     *  (blue) so the user can tell at a glance which tract their transform/POB
     *  edits will apply to. Other (visible) tracts stay amber. */
    activeBoundaryFileId?: string;
    /** When true, the BFOverlay bearing labels for the active boundary are
     *  rendered as the rotated (as-drawn) bearing rather than the original
     *  deed bearing. Only has an effect when the active file has a non-zero
     *  rotationDeg. */
    showRotatedBearings?: boolean;
    // Boundary call label interaction callbacks (Alt = rotate, Shift = swap direction)
    onToggleBoundaryCallLabelRotation?: (fileId: string, callId: string) => void;
    onSwapBoundaryCallBearingDirection?: (fileId: string, callId: string) => void;
    // Line layer query/edit
    onUpdateLine?: (lineId: string, changes: Partial<SurveyLine>) => void;
    /** Open the app text editor with long command results (terminal links). */
    onOpenTextEditor?: (content: string, tone?: TerminalTone, reference?: CommandReference) => void;
    availableLayers?: string[];
    // Active drawing layer — applied to manually drawn polylines (not AI lines)
    activeDrawingLayer?: string;
    onSetActiveDrawingLayer?: (layer: string) => void;
    /** Opens the boundary editor as a canvas-toolbar panel. */
    onOpenBoundaryEditor?: () => void;
    /** Whether the boundary editor is currently open. */
    isBoundaryEditorOpen?: boolean;
    /** Pulse the boundary editor toolbar button to hint the user can open the floating editor. */
    isBoundaryEditorButtonPulsing?: boolean;
    /** Opens the shrinkwrap editor using the visible canvas points. */
    onOpenShrinkwrap?: () => void;
    /** Whether the shrinkwrap editor is currently open. */
    isShrinkwrapOpen?: boolean;
    /** Active shrinkwrap source pns (already filtered: excludedPns removed). Enables hover-to-highlight + click-to-remove. */
    shrinkwrapSourcePns?: string[];
    /** Called when the user clicks a shrinkwrap source point on the canvas. */
    onShrinkwrapPointClick?: (pn: string) => void;
    /** General point selection — called whenever a survey point is clicked in normal (non-drawing) mode. Used by DeedSummaryPanel alignment and other panels. */
    onPointSelected?: (point: SurveyPoint) => void;
    /** Boundary-align tool — patch translationE/N/rotationDeg on a boundary file.
     *  Mirrors BoundaryEditor's onTransformChange so the silent-redraw debounce is shared. */
    onUpdateBoundaryTransform?: (id: string, patch: { translationE?: number | null; translationN?: number | null; rotationDeg?: number | null }, opts?: { immediate?: boolean }) => void;
    /** When set (an incrementing token + movable bf id), arms the boundary-align tool with
     *  movable preset and waits for the user to click one line on movable, one on target. */
    boundaryAlignRequest?: { token: number; movableBfId: string } | null;
    /** Called when the tool exits (commit or cancel) so the parent can clear the request. */
    onBoundaryAlignRequestHandled?: () => void;
    /** When set (an incrementing token + bf id), arms the corner-align tool: the user clicks a
     *  corner on the highlighted deed silhouette, then clicks the found point it belongs on.
     *  Every extra pair refines a rigid (translate + rotate) best fit. */
    boundaryCornerAlignRequest?: { token: number; movableBfId: string } | null;
    /** Called when the corner-align tool exits (commit or cancel) so the parent can clear the request. */
    onBoundaryCornerAlignRequestHandled?: () => void;
    /** Called on commit with the point numbers the user paired, so the parent can associate
     *  them with the boundary (they then hide/persist together). */
    onBoundaryCornerAlignCommitted?: (bfId: string, pointIds: string[]) => void;
    /** User-defined linetypes from the CAD Manager standard. Looked up by name when rendering line patterns. */
    linetypes?: LinetypeDefinition[];
    /** Global linetype scale (AutoCAD LTSCALE equivalent). Multiplies every rendered dash pattern. Default 1.0. */
    globalLinetypeScale?: number;
    /** Map of CAD layer name → default linetype name, derived from the standard's codes.
     *  Used as a fallback when a SurveyLine has a layer but no explicit lineType — common
     *  for lines created before the linetype system existed or imported from DXF. */
    layerLinetypeMap?: Record<string, string>;
    /** Key info passed from App.tsx for display in the interactive command terminal. */
    keyInfo?: {
        label: string;
        detail: string;
        tone?: string;
        onClick?: () => void;
        hasServiceAndInference?: boolean;
        isServiceEnabled?: boolean;
        isInferenceEnabled?: boolean;
    };
    /** Whether the screen is in desktop breakpoint (>=768px). */
    isDesktop?: boolean;
    /** Whether the agent chat panel is currently open/visible. */
    isChatPanelVisible?: boolean;
    /** Current width in pixels of the agent chat panel in desktop mode. */
    chatPanelWidth?: number;
    /** True while the chat panel edge is being dragged — defers expensive canvas reallocation. */
    isChatResizing?: boolean;
    onRedo?: () => void;
    canRedo?: boolean;
    canUndo?: boolean;
    onClearAll?: () => void;
    /** Unified Agent Mode props for the Command Bar */
    agentMessages?: import('../types.ts').ChatMessage[];
    onSendAgentMessage?: (query: string) => void;
    onStopGenerating?: () => void;
    isAgentLoading?: boolean;
    thinkingTime?: number;
    currentModelName?: string;
    suggestedQuestions?: string[];
    onToggleExpandMessage?: (msgIndex: number) => void;
    chatHeaderControls?: React.ReactNode;
    chatBarAccessories?: React.ReactNode;
    toolsRowAccessories?: React.ReactNode;
    hasDockableTools?: boolean;
    isToolDockOpen?: boolean;
    onToggleToolDock?: () => void;
    /** A2UI surface currently docked in the shelf, so the chat can show its placeholder. */
    dockedA2UIKey?: string | null;
    onToggleA2UIDock?: (message: import('../types.ts').ChatMessage) => void;
    /** Docked tool panels relevant to the active agent, mirrored under the chat feed. */
    chatToolDialogs?: import('./AgentToolDock.tsx').DockableToolItem[];
    /** Increment to pull the command bar's agent feed back open (e.g. Return to chat). */
    agentChatOpenSignal?: number;
    /** Boundary Agent deed-intake choices for the command bar quick thought. */
    boundaryQuickActions?: {
        onUpload: () => void;
        onAuthor: () => void;
        onGis: () => void;
    };
    /** Reports the command bar height so overlay drawers can rest on its top edge. */
    onCommandBarHeightChange?: (height: number) => void;
    /** Pixels the command bar extends right, underneath the open agent tool shelf. */
    commandBarRightExtension?: number;
}

type OsnapMode = 'endpoint' | 'midpoint' | 'intersection' | 'center' | 'nearest' | 'perpendicular' | 'tangent';
type OsnapResult = {
    easting: number;
    northing: number;
    mode: OsnapMode;
    sourcePoint?: SurveyPoint;
};

export interface TtrEntity {
    kind: 'line' | 'circle' | 'arc';
    line?: SurveyLine;
    p1?: { x: number; y: number };
    p2?: { x: number; y: number };
    center?: { x: number; y: number };
    radius?: number;
    startAngle?: number;
    centralAngle?: number;
    isLeftCurve?: boolean;
    pickPoint: { x: number; y: number };
}

export interface TrimPreviewData {
    kind: 'line' | 'circle' | 'arc';
    p1?: { x: number; y: number };
    p2?: { x: number; y: number };
    center?: { x: number; y: number };
    radius?: number;
    startAngle?: number;
    endAngle?: number;
    arcAnticlockwise?: boolean;
    hitPoint: { x: number; y: number };
}

export type GripType = 'start' | 'mid' | 'end' | 'center' | 'quadrant';

export interface EntityGrip {
    id: string;
    lineId: string;
    type: GripType;
    x: number;
    y: number;
    quadrantIndex?: number;
    line: SurveyLine;
}

export interface ViewToolbarPosition {
    x?: number;
    y?: number;
    isDocked?: boolean;
}

const NORTH_ARROW_SIZE: Record<'classic' | 'modern' | 'elegant', { width: number; height: number }> = {
    classic: { width: 40, height: 78 },
    modern: { width: 40, height: 40 },
    elegant: { width: 45, height: 45 },
};

export const NORTH_ARROW_TOP_INSET = 16;
export const NORTH_ARROW_RIGHT_INSET = 16;
export const NORTH_ARROW_TOOLBAR_GAP = 12;

/**
 * Default floating-toolbar seat: flush with the right edge, directly below the
 * north arrow. The y position is clamped so the toolbar still clears the
 * command bar on short canvases.
 */
export function calculateDefaultToolbarPosition(params: {
    containerWidth: number;
    containerHeight: number;
    toolbarWidth: number;
    toolbarHeight: number;
    commandBarInset: number;
    northArrowHeight: number;
}): { x: number; y: number } {
    const {
        containerWidth,
        containerHeight,
        toolbarWidth,
        toolbarHeight,
        commandBarInset,
        northArrowHeight,
    } = params;
    const x = Math.max(8, containerWidth - toolbarWidth - NORTH_ARROW_RIGHT_INSET);
    const belowNorthArrowY = NORTH_ARROW_TOP_INSET + northArrowHeight + NORTH_ARROW_TOOLBAR_GAP;
    const maxY = Math.max(8, containerHeight - toolbarHeight - commandBarInset - 8);
    return { x, y: Math.max(8, Math.min(maxY, belowNorthArrowY)) };
}

/**
 * Calculates toolbar position and docking state during or after drag operations.
 * When dragging within snapThreshold of the right edge in desktop mode, it snaps
 * directly to the right edge (the agent chat boundary) and sets isDocked: true.
 */
export function calculateToolbarDockingPosition(params: {
    rawX: number;
    rawY: number;
    maxW: number;
    maxH: number;
    toolbarW: number;
    toolbarH: number;
    isDesktop: boolean;
    snapThreshold?: number;
}): { x: number; y: number; isDocked: boolean } {
    const { rawX, rawY, maxW, maxH, toolbarW, toolbarH, isDesktop, snapThreshold = 36 } = params;
    const clampedY = Math.max(8, Math.min(maxH - toolbarH - 8, rawY));
    const rightDockX = maxW - toolbarW - 16;
    const isSnappingToRight = isDesktop && (rawX >= rightDockX - snapThreshold);
    const newX = isSnappingToRight
        ? rightDockX
        : Math.max(8, Math.min(rightDockX, rawX));

    return {
        x: newX,
        y: clampedY,
        isDocked: isSnappingToRight,
    };
}

/**
 * If the toolbar is NOT docked but positioned behind the agent chat (e.g. user opens
 * the agent chat in desktop mode), computes the pushed-out position so the toolbar
 * remains visible on the canvas and does not get lost behind the chat drawer.
 * Returns null if no push-out is needed.
 */
export function calculateToolbarPushOutOfChat(params: {
    currentPos: ViewToolbarPosition | null;
    containerWidth: number;
    isDesktop: boolean;
    isChatPanelVisible: boolean;
    chatPanelWidth: number;
    toolbarWidth: number;
    windowWidth?: number;
}): ViewToolbarPosition | null {
    const {
        currentPos,
        containerWidth,
        isDesktop,
        isChatPanelVisible,
        chatPanelWidth,
        toolbarWidth,
        windowWidth = typeof window !== 'undefined' ? window.innerWidth : 1200,
    } = params;

    // If docked or null (default docked), CSS right: 1rem keeps it anchored to right edge
    const isDocked = currentPos ? (currentPos.isDocked ?? false) : true;
    if (isDocked) return null;

    const currentX = currentPos?.x;
    if (currentX === undefined) return null;

    let availableWidth = containerWidth;
    if (isDesktop && isChatPanelVisible) {
        const expectedWidth = windowWidth - chatPanelWidth;
        if (expectedWidth > 0 && expectedWidth < availableWidth) {
            availableWidth = expectedWidth;
        }
    }
    if (availableWidth <= 0) return null;

    const maxAllowedX = Math.max(16, availableWidth - toolbarWidth - 16);

    // If toolbar is positioned behind the agent chat (or off the right edge)
    if (currentX > maxAllowedX) {
        return {
            ...currentPos,
            x: maxAllowedX,
            isDocked: false, // Stays undocked, pushed out only to be visible
        };
    }

    return null;
}

const RUNNING_OSNAP_STORAGE_KEY = 'landsurv.runningOsnaps.v1';
const RUNNING_OSNAP_ENABLED_STORAGE_KEY = 'landsurv.runningOsnapsEnabled.v1';
const OSNP_MENU_WIDTH = 224;
const OSNP_MENU_MAX_HEIGHT = 440;
const OSNAP_LABELS: Record<OsnapMode, string> = {
    endpoint: 'Endpoint',
    midpoint: 'Midpoint',
    intersection: 'Intersection',
    center: 'Center',
    nearest: 'Nearest',
    perpendicular: 'Perpendicular',
    tangent: 'Tangent',
};
const OSNAP_GLYPHS: Record<OsnapMode, string> = {
    endpoint: 'E',
    midpoint: 'M',
    intersection: 'X',
    center: 'C',
    nearest: 'N',
    perpendicular: '⊥',
    tangent: 'T',
};
const OSNAP_ORDER: OsnapMode[] = ['endpoint', 'midpoint', 'intersection', 'center', 'nearest', 'perpendicular', 'tangent'];

const NorthArrow: React.FC<{ style: 'classic' | 'modern' | 'elegant', position: 'left' | 'right', isLightTheme: boolean, rotationRad?: number, onClick?: () => void }> = ({ style, position, isLightTheme, rotationRad = 0, onClick }) => {
    const positionClass = position === 'left' ? 'top-4 left-20' : 'top-4 right-4';
    const rotationDeg = -(rotationRad * 180) / Math.PI;

    const classicArrow = (
        <svg width={NORTH_ARROW_SIZE.classic.width} height={NORTH_ARROW_SIZE.classic.height} viewBox="0 0 80 155">
            <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="1" dy="1" stdDeviation="1.5" floodColor="#000000" floodOpacity="0.4"/>
                </filter>
            </defs>
            <g filter="url(#shadow)">
                {/* Arrow shape with border */}
                <path d="M40 5 L10 95 L40 75 L70 95 Z" stroke={isLightTheme ? "#333" : "#DDD"} strokeWidth="2" fill="none"/>

                {/* Dark half (left) */}
                <path d="M40 5 L10 95 L40 75 Z" fill={isLightTheme ? "#4A5568" : "#2D3748"} />

                {/* Light half (right) */}
                <path d="M40 5 L70 95 L40 75 Z" fill={isLightTheme ? "#E2E8F0" : "#A0AEC0"} />
                
                <text x="40" y="145" fontFamily="sans-serif" fontSize="48" fill={isLightTheme ? "#333" : "#DDD"} textAnchor="middle" fontWeight="bold">N</text>
            </g>
        </svg>
    );

    const modernArrow = (
         <svg width={NORTH_ARROW_SIZE.modern.width} height={NORTH_ARROW_SIZE.modern.height} viewBox="0 0 100 100">
             <defs>
                <filter id="shadow-modern" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="1" dy="2" stdDeviation="1.5" floodColor="#000" floodOpacity="0.4"/>
                </filter>
            </defs>
            <g filter="url(#shadow-modern)">
                <path d="M50 5 L15 95 L50 75 L85 95 Z" fill={isLightTheme ? "rgba(113, 128, 150, 0.8)" : "rgba(45, 55, 72, 0.8)"} stroke={isLightTheme ? "#A0AEC0" : "#4A5568"} strokeWidth="3" />
                <path d="M50 5 L50 75" stroke={isLightTheme ? "#A0AEC0" : "#4A5568"} strokeWidth="3" />
                <text x="50" y="38" fontFamily="sans-serif" fontSize="28" fill="#f87171" textAnchor="middle" fontWeight="bold">N</text>
            </g>
        </svg>
    );
    
    const elegantArrow = (
        <svg width={NORTH_ARROW_SIZE.elegant.width} height={NORTH_ARROW_SIZE.elegant.height} viewBox="0 0 100 100">
            <defs>
                <filter id="shadow-elegant" x="-20%" y="-20%" width="140%" height="140%">
                    <feDropShadow dx="1" dy="2" stdDeviation="1.5" floodColor="#000" floodOpacity="0.3"/>
                </filter>
            </defs>
            <g filter="url(#shadow-elegant)">
                <path d="M50 5 L50 95" stroke={isLightTheme ? "#4A5568" : "#A0AEC0"} strokeWidth="2"/>
                <path d="M50 5 L40 25 L60 25 Z" fill={isLightTheme ? "#f56565" : "#f56565"}/>
                <text x="50" y="45" fontFamily="serif" fontSize="24" fill={isLightTheme ? "#333" : "#DDD"} textAnchor="middle" fontWeight="bold">N</text>
            </g>
        </svg>
    );

    let arrowSvg;
    switch (style) {
        case 'modern':
            arrowSvg = modernArrow;
            break;
        case 'elegant':
            arrowSvg = elegantArrow;
            break;
        case 'classic':
        default:
            arrowSvg = classicArrow;
            break;
    }

    return (
        <div
            className={`group absolute ${positionClass} z-30 cursor-pointer hover:scale-110 transition-transform`}
            onClick={onClick}
            onKeyDown={event => {
                if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onClick?.();
                }
            }}
            role="button"
            tabIndex={0}
            aria-label="Settings"
        >
            <span
                className={`pointer-events-none absolute top-1/2 -translate-y-1/2 whitespace-nowrap rounded-md border border-white/10 bg-[#0a0f16]/95 px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-cyan-200 opacity-0 shadow-lg backdrop-blur transition-opacity duration-150 group-hover:opacity-100 group-focus-visible:opacity-100 ${position === 'right' ? 'right-full mr-2' : 'left-full ml-2'}`}
            >
                Settings
            </span>
            <div
                style={{
                    transform: `rotate(${rotationDeg}deg)`,
                    transformOrigin: 'center center',
                    transition: 'transform 0.3s ease-out'
                }}
            >
                {arrowSvg}
            </div>
        </div>
    );
};

const debounce = (func: (...args: any[]) => void, delay: number) => {
    let timeoutId: number;
    return (...args: any[]) => {
        clearTimeout(timeoutId);
        timeoutId = window.setTimeout(() => func(...args), delay);
    };
};

function latToWorldY(latDeg: number): number {
    const lat = Math.max(-85.05112878, Math.min(85.05112878, latDeg));
    const sinLat = Math.sin((lat * Math.PI) / 180);
    return 0.5 - Math.log((1 + sinLat) / (1 - sinLat)) / (4 * Math.PI);
}

function worldYToLat(worldY: number): number {
    const rad = Math.atan(Math.sinh(Math.PI * (1 - 2 * worldY)));
    return (rad * 180) / Math.PI;
}

function bboxFromCenterZoom(lat: number, lng: number, zoom: number, width: number, height: number): {
    west: number;
    south: number;
    east: number;
    north: number;
} {
    const scale = 256 * Math.pow(2, zoom);
    const cx = ((lng + 180) / 360) * scale;
    const cy = latToWorldY(lat) * scale;

    const leftX = cx - width / 2;
    const rightX = cx + width / 2;
    const topY = cy - height / 2;
    const bottomY = cy + height / 2;

    const west = (leftX / scale) * 360 - 180;
    const east = (rightX / scale) * 360 - 180;
    const north = worldYToLat(topY / scale);
    const south = worldYToLat(bottomY / scale);

    return { west, south, east, north };
}

const BoundaryEditorGlyph: React.FC<{ className?: string }> = ({ className = 'w-6 h-6' }) => (
    <svg viewBox="0 0 24 24" className={className} fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <path d="M5 6.5 11.5 3l6.5 3.5 1 7-5.5 6-7.5-2-2-6z" />
        <path d="M5 6.5 14 19.5M18 6.5 6 17.5" strokeOpacity="0.35" />
        <circle cx="5" cy="6.5" r="1.2" fill="currentColor" />
        <circle cx="18" cy="6.5" r="1.2" fill="currentColor" />
        <circle cx="19" cy="13.5" r="1.2" fill="currentColor" />
        <circle cx="13.5" cy="19.5" r="1.2" fill="currentColor" />
        <circle cx="6" cy="17.5" r="1.2" fill="currentColor" />
    </svg>
);

function zoomForBboxWgs84(bboxWgs84: [number, number, number, number]): number {
    const widthDeg = Math.max(1e-6, bboxWgs84[2] - bboxWgs84[0]);
    const heightDeg = Math.max(1e-6, bboxWgs84[3] - bboxWgs84[1]);
    const span = Math.max(widthDeg, heightDeg) * 1.15;
    const zoom = Math.floor(Math.log2((360 * 640) / (span * 256)));
    return Math.max(12, Math.min(20, zoom));
}

export const DrawingCanvas = forwardRef<DrawingCanvasHandles, DrawingCanvasProps>((props, ref) => {
    useDocumentScrollLock(true);

    const { 
        initialTransform,
        onTransformChange,
        points, lines, contourLabels, centerlines, customSymbols, enabledBuiltinSymbolIds, pointLayers, attributeScale, setAttributeScale, lineLabelScale, setLineLabelScale, lineThickness, setLineThickness, contourLabelScale, onLayerColorChange, activeAgent, settings, zoomTarget, onZoomComplete,
        onUpdatePointLabelOffset, onAddLine, onDeleteLine, onDeletePoints, onDeleteCenterline, canUndo, canRedo, onUndo, onRedo, wmsServices, activeWmsLayers, offlineMapAreas, onRedrawAndShowAll, onOpenDxfExportModal,
        onUpdatePointAnnotationOffset,
        isLayerPanelOpen, setIsLayerPanelOpen, onLayerToggle, activeAccent, currentPosition, projectedPosition, onZoomToPoint,
        linesVisible, onSetLinesVisible, centerlinesVisible, onSetCenterlinesVisible, pointLists, onTogglePointListVisibility, deedMetadata, onToolStateChange, onShowSettings,
        isC3DConnected, isGridVisible, tinShading, agentDrawMode, onCycleAgentDrawMode, agentAnnotMode, onCycleAgentAnnotMode, onAddBoundaryCall, onConvertSelectionToBoundary, onComputeCurrentClosure,
        dimensions, onAddDimension, onUpdateDimension, onDeleteDimension, onClearDimensions, onAddPoint, nextAvailablePointNumber, dimensionScale, setDimensionScale, symbolScale, setSymbolScale,
        customAnnotations = [], annotationScale = 1, setAnnotationScale,
        parcelLabels, annotationCategories,
        tinSurfaces = [],
        boundaryFiles,
        activeBoundaryFileId,
        showRotatedBearings,
        activeDrawingLayer,
        linetypes: standardLinetypes,
        globalLinetypeScale = 1,
        layerLinetypeMap,
        orientationTuple = {
            angle: 0,
            originX: 0,
            originY: 0,
            mode: 'drafting',
            draftingAngle: 0,
            draftingOriginX: 0,
            draftingOriginY: 0,
            viewAngle: 0,
            viewOriginX: 0,
            viewOriginY: 0,
        },
        onSetOrientationTuple,
        isDesktop: propIsDesktop,
        isChatPanelVisible: propIsChatPanelVisible,
        chatPanelWidth: propChatPanelWidth,
        isChatResizing: propIsChatResizing,
    } = props;
    const isDesktop = propIsDesktop ?? (typeof window !== 'undefined' ? window.innerWidth >= 768 : true);
    const isChatPanelVisible = propIsChatPanelVisible ?? true;
    const chatPanelWidth = propChatPanelWidth ?? 450;
    const isChatResizing = propIsChatResizing ?? false;
    const { addNotification, setSettings } = useAppState();
    const { reportError } = useErrorReporter();

    // Helper to notify boundary errors to the notification bar instead of displaying inline
    const notifyBoundaryError = useCallback((message: string, isClosure: boolean = false) => {
        addNotification({
            kind: 'boundary-processing',
            severity: 'error',
            title: isClosure ? 'Closure Error' : 'Boundary Input Error',
            message,
        });
    }, [addNotification]);

    console.log('[DC] render — boundaryFiles:', boundaryFiles ? boundaryFiles.length : 'undef', 'points:', points.length);
    
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isChatResizingRef = useRef(isChatResizing);
    isChatResizingRef.current = isChatResizing;
    const wasChatResizingRef = useRef(false);
    const gpuCanvasRef = useRef<HTMLCanvasElement>(null);
    const gpuBasemapRef = useRef<WebGpuBasemap | null>(null);
    /** Bumped when the user changes a WebGPU preference so the renderer re-initialises. */
    const [gpuPrefVersion, setGpuPrefVersion] = useState(0);
    const containerRef = useRef<HTMLDivElement>(null);
    const [transform, setTransform] = useState<{ scale: number; offsetX: number; offsetY: number }>(() => initialTransform || { scale: 1, offsetX: 0, offsetY: 0 });
    const transformRef = useRef(transform);
    const hasAutoFitOnceRef = useRef(Boolean(initialTransform));

    useEffect(() => {
        transformRef.current = transform;
        onTransformChange?.(transform);
    }, [transform, onTransformChange]);

    useEffect(() => {
        if (initialTransform) {
            setTransform(initialTransform);
            hasAutoFitOnceRef.current = true;
        }
    }, [initialTransform]);

    const worldToScreen = useCallback((easting: number, northing: number): { x: number; y: number } => {
        let x = easting;
        let y = northing;
        const viewAngle = orientationTuple?.viewAngle ?? (orientationTuple?.mode === 'view' ? orientationTuple.angle : 0);
        const viewOriginX = orientationTuple?.viewOriginX ?? (orientationTuple?.mode === 'view' ? orientationTuple.originX : 0);
        const viewOriginY = orientationTuple?.viewOriginY ?? (orientationTuple?.mode === 'view' ? orientationTuple.originY : 0);

        if (viewAngle !== 0) {
            const dx = easting - viewOriginX;
            const dy = northing - viewOriginY;
            const cos = Math.cos(viewAngle);
            const sin = Math.sin(viewAngle);
            x = dx * cos - dy * sin + viewOriginX;
            y = dx * sin + dy * cos + viewOriginY;
        }
        return {
            x: x * transform.scale + transform.offsetX,
            y: -y * transform.scale + transform.offsetY,
        };
    }, [transform, orientationTuple]);

    const screenToWorld = useCallback((screenX: number, screenY: number): { easting: number; northing: number } => {
        const rawX = (screenX - transform.offsetX) / transform.scale;
        const rawY = -(screenY - transform.offsetY) / transform.scale;
        const viewAngle = orientationTuple?.viewAngle ?? (orientationTuple?.mode === 'view' ? orientationTuple.angle : 0);
        const viewOriginX = orientationTuple?.viewOriginX ?? (orientationTuple?.mode === 'view' ? orientationTuple.originX : 0);
        const viewOriginY = orientationTuple?.viewOriginY ?? (orientationTuple?.mode === 'view' ? orientationTuple.originY : 0);

        if (viewAngle !== 0) {
            const dx = rawX - viewOriginX;
            const dy = rawY - viewOriginY;
            const cos = Math.cos(viewAngle);
            const sin = Math.sin(viewAngle);
            return {
                easting: dx * cos + dy * sin + viewOriginX,
                northing: -dx * sin + dy * cos + viewOriginY,
            };
        }
        return {
            easting: rawX,
            northing: rawY,
        };
    }, [transform, orientationTuple]);

    const [isPanning, setIsPanning] = useState(false);
    const lastMousePos = useRef({ x: 0, y: 0 });
    const pinchDistRef = useRef(0);
    /** Point number currently hovered in shrinkwrap mode (null = none). */
    const hoveredShrinkwrapPnRef = useRef<string | null>(null);
    const [hoveredShrinkwrapPn, setHoveredShrinkwrapPn] = useState<string | null>(null);
    // Dynamic input / Cursor HUD screen position
    const [cursorScreenPos, setCursorScreenPos] = useState<{ x: number; y: number } | null>(null);
    // Multi-value command prompt (ARRAY / POLARARRAY / REC / POL / DI): values
    // are owned here so the arrow pad and the command line edit the same state.
    const [activeCommandPrompt, setActiveCommandPrompt] = useState<CommandPromptSpec | null>(null);
    const [commandPromptValues, setCommandPromptValues] = useState<Record<string, string>>({});
    const [commandPromptIndex, setCommandPromptIndex] = useState(0);
    const commandPromptOpenedRef = useRef(false);
    /** POLARARRAY: the next canvas left-click sets the rotation center. */
    const [isPickingPolarCenter, setIsPickingPolarCenter] = useState(false);
    /** Sticky side/fit for the array widget so tiny bounds changes don't flicker it. */
    const arrayWidgetSideRef = useRef<string | null>(null);
    // Shift+hover quick-info tooltip
    const [shiftHoverPoints, setShiftHoverPoints] = useState<SurveyPoint[]>([]);
    const [shiftHoverPos, setShiftHoverPos] = useState<{ clientX: number; clientY: number } | null>(null);
    const shiftHoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const shiftHoverAnchorRef = useRef<{ mx: number; my: number } | null>(null);
    const [zoomToPointNumber, setZoomToPointNumber] = useState('');
    const [isZoomToPointOpen, setIsZoomToPointOpen] = useState(false);
    // Description-search variant of zoom-to-point: "Show me sign points",
    // "find iron pins", "EP", etc. Opens a floating list of matching points
    // and stays open so the user can zoom to each one in sequence.
    const [isFindPointsOpen, setIsFindPointsOpen] = useState(false);
    const [findPointsQuery, setFindPointsQuery] = useState('');
    const [isZoomExtentsOpen, setIsZoomExtentsOpen] = useState(false);
    // TIN shading is lifted to App.tsx so the app header can control it.
    const isTinShadingEnabled = tinShading ?? false;
    const [isAttributeFlyoutOpen, setIsAttributeFlyoutOpen] = useState(false);
    const [isOtPopupOpen, setIsOtPopupOpen] = useState(false);
    const [isPickingOtOrigin, setIsPickingOtOrigin] = useState(false);
    const [isPickingOtLineAlign, setIsPickingOtLineAlign] = useState(false);
    const [drawingMode, setDrawingMode] = useState<
        | 'none'
        | 'polylines'
        | 'breaklines'
        | 'inclusion'
        | 'exclusion'
        | 'circle'
        | 'polygon'
        | 'boundary-line'
        | 'aligned-dim'
        | 'horiz-dim'
        | 'vert-dim'
        | 'radius-dim'
        | 'diameter-dim'
        | 'arc-length-dim'
        | 'curve-data-dim'
        | 'angular-dim'
        | 'ordinate-dim'
        | 'add-point'
        | 'boundary-align'
        | 'boundary-corner-align'
    >('none');
    // Add Point tool state
    const [isAddPointFlyoutOpen, setIsAddPointFlyoutOpen] = useState(false);
    const [addPointNumber, setAddPointNumber] = useState<string>(() => nextAvailablePointNumber || '1');
    const [addPointNorthing, setAddPointNorthing] = useState('');
    const [addPointEasting, setAddPointEasting] = useState('');
    const [addPointElevation, setAddPointElevation] = useState('0.00');
    const [addPointDescription, setAddPointDescription] = useState('CALC');

    // Circle drawing state (triggered by 'C' key or Drawing Tools)
    const [circleCenter, setCircleCenter] = useState<{ easting: number; northing: number } | null>(null);
    const [circleSizeParam, setCircleSizeParam] = useState<'radius' | 'diameter'>('radius');
    const [typedCircleValue, setTypedCircleValue] = useState('');
    const [circleSubMode, setCircleSubMode] = useState<'center-radius' | 'ttr'>('center-radius');
    const [ttrStep, setTtrStep] = useState<1 | 2 | 3>(1);
    const [ttrEntity1, setTtrEntity1] = useState<TtrEntity | null>(null);
    const [ttrEntity2, setTtrEntity2] = useState<TtrEntity | null>(null);
    const [ttrHoverEntity, setTtrHoverEntity] = useState<TtrEntity | null>(null);
    // Polygon drawing state (POL command): snapped center, live radial preview.
    const [polygonCenter, setPolygonCenter] = useState<{ easting: number; northing: number } | null>(null);
    const [polygonSides, setPolygonSides] = useState(6);
    const [typedPolygonDiameter, setTypedPolygonDiameter] = useState('');
    const [polylinePoints, setPolylinePoints] = useState<SurveyPoint[]>([]);
    const [typedSegmentLength, setTypedSegmentLength] = useState('');
    // AutoCAD-style Arc submode for the polyline tool (A toggles on, L back off).
    const [polylineArcMode, setPolylineArcMode] = useState(false);
    // Unit tangent (world frame) leaving the current polyline anchor — the arc
    // submode locks to this so chained segments stay tangent-continuous.
    const lastSegmentTangentRef = useRef<{ x: number; y: number } | null>(null);
    const [isMultiSegmentMode, setIsMultiSegmentMode] = useState(false); // For continuous polyline drawing
    const [isDeletingLines, setIsDeletingLines] = useState(false);
    const [isTrimmingLines, setIsTrimmingLines] = useState(false);
    const [trimPoint, setTrimPoint] = useState<{ x: number; y: number } | null>(null);
    const [trimHoverPreview, setTrimHoverPreview] = useState<TrimPreviewData | null>(null);
    const [isExtendingLines, setIsExtendingLines] = useState(false);
    const [extendFromLine, setExtendFromLine] = useState<string | null>(null);
    // Dimension tool state
    const [isDimFlyoutOpen, setIsDimFlyoutOpen] = useState(false);
    const [isDimSettingsOpen, setIsDimSettingsOpen] = useState(false);
    const [dimStyleSettings, setDimStyleSettings] = useState<DimensionStyleSettings>(() => {
        try {
            const saved = localStorage.getItem('landsurv-dim-style-settings');
            if (saved) return { ...DEFAULT_DIMENSION_SETTINGS, ...JSON.parse(saved) };
        } catch { /* ignore */ }
        return DEFAULT_DIMENSION_SETTINGS;
    });
    const [dimPhase, setDimPhase] = useState<0 | 1 | 2>(0); // 0=pick p1/curve, 1=pick p2/textPos, 2=pick offset/p3
    const [dimP1, setDimP1] = useState<{ easting: number; northing: number } | null>(null);
    const [dimP2, setDimP2] = useState<{ easting: number; northing: number } | null>(null);
    const [dimP3, setDimP3] = useState<{ easting: number; northing: number } | null>(null);
    const [dimSelectedCurve, setDimSelectedCurve] = useState<{
        kind: 'arc' | 'circle';
        center: { easting: number; northing: number };
        radius: number;
        arcLength: number;
        p1: { easting: number; northing: number };
        p2: { easting: number; northing: number };
        isLeftCurve: boolean;
        chordBearing?: string;
        chordLength?: number;
        deltaRad?: number;
        tangentBearing?: string;
    } | null>(null);
    const [dimHoveredCurve, setDimHoveredCurve] = useState<{
        kind: 'arc' | 'circle';
        center: { easting: number; northing: number };
        radius: number;
        startAngle?: number;
        centralAngle?: number;
        isLeftCurve?: boolean;
    } | null>(null);
    const [selectedDimensionId, setSelectedDimensionId] = useState<string | null>(null);
    const [dimContextMenu, setDimContextMenu] = useState<{ x: number; y: number; dimId: string } | null>(null);
    const [isOverDim, setIsOverDim] = useState(false);
    const [hoveredDimGrip, setHoveredDimGrip] = useState<DimensionGrip | null>(null);
    const [activeDimGrip, setActiveDimGrip] = useState<DimensionGrip | null>(null);
    const activeDimGripDragStartRef = useRef<{ clientX: number; clientY: number } | null>(null);
    const [dimPreviewMouse, setDimPreviewMouse] = useState<{ easting: number; northing: number } | null>(null);
    const [dimSnapPoint, setDimSnapPoint] = useState<{ easting: number; northing: number } | null>(null);
    const [activeOsnapPoint, setActiveOsnapPoint] = useState<OsnapResult | null>(null);
    const [runningOsnaps, setRunningOsnaps] = useState<Set<OsnapMode>>(() => {
        try {
            const raw = localStorage.getItem(RUNNING_OSNAP_STORAGE_KEY);
            if (!raw) return new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection']);
            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) return new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection']);
            const valid = parsed.filter((m): m is OsnapMode => OSNAP_ORDER.includes(m));
            return new Set<OsnapMode>(valid.length > 0 ? valid : ['endpoint', 'midpoint', 'intersection']);
        } catch {
            return new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection']);
        }
    });
    const [isRunningOsnapsEnabled, setIsRunningOsnapsEnabled] = useState<boolean>(() => {
        try {
            return localStorage.getItem(RUNNING_OSNAP_ENABLED_STORAGE_KEY) !== 'false';
        } catch {
            return true;
        }
    });
    const [inlineOsnapOverride, setInlineOsnapOverride] = useState<OsnapMode | null>(null);
    const [inlineOsnapMenuPos, setInlineOsnapMenuPos] = useState<FlippingMenuPlacement | null>(null);

    // Boundary-align tool state — pick a line on one boundary file, a line on
    // another, auto-rotate+translate the first so the picked edges are
    // collinear, then slide along the target line until a click commits.
    const [boundaryAlignMovable, setBoundaryAlignMovable] = useState<{ bfId: string; callId: string } | null>(null);
    const [boundaryAlignTarget, setBoundaryAlignTarget]   = useState<{ bfId: string; callId: string } | null>(null);
    const [boundaryAlignSliding, setBoundaryAlignSliding] = useState<boolean>(false);
    // 180° flip toggle — swaps which movable-line endpoint snaps to which target-line endpoint.
    const [boundaryAlignFlipped, setBoundaryAlignFlipped] = useState<boolean>(false);
    // Captured at slide entry — drives the per-frame translation update on mouse move.
    const boundaryAlignSlideRef = useRef<{
        movableBfId: string;
        rotDegFixed: number;            // movable bf.rotationDeg locked during slide
        baseTxE: number;                // movable bf.translationE at slide start (movable.from snapped to target.from)
        baseTxN: number;                // movable bf.translationN at slide start
        dirE: number;                   // target line unit direction (E component)
        dirN: number;                   // target line unit direction (N component)
        targetLen: number;              // target line length (world units)
        movableLen: number;             // movable line length (world units)
        mouseStartProj: number;         // mouse projection onto target line at slide start
        sMin: number;                   // min slide parameter (allowed range)
        sMax: number;                   // max slide parameter (allowed range)
    } | null>(null);
    // Per-bf cached geometry stashed during BFOverlay render; read by the
    // boundary-align hit-test on mousedown without re-walking the COGO chain.
    const bfGeometryCacheRef = useRef<Map<string, {
        synthMap: Map<string, { easting: number; northing: number }>;
        pob: { easting: number; northing: number };
        txE: number;
        txN: number;
        rotDeg: number;
    }>>(new Map());

    // When the parent fires a boundary-align request (token + movableBfId),
    // arm the tool with movable pre-bound: user clicks one line on movable,
    // then one line on target, snap is committed and the tool exits.
    const lastAlignTokenRef = useRef<number | null>(null);
    useEffect(() => {
        const req = props.boundaryAlignRequest;
        if (!req || req.token === lastAlignTokenRef.current) return;
        lastAlignTokenRef.current = req.token;
        setDrawingMode('boundary-align');
        setBoundaryAlignMovable({ bfId: req.movableBfId, callId: '__pending__' });
        setBoundaryAlignTarget(null);
        setBoundaryAlignSliding(false);
        boundaryAlignSlideRef.current = null;
    }, [props.boundaryAlignRequest]);

    // ---------------------------------------------------------------------
    // Corner-align tool state (v26.06 — "align deed silhouette to found points")
    //
    // Workflow: arm from the Boundary Editor, then repeatedly
    //   1. click a CORNER of the highlighted deed silhouette (amber/blue dashed
    //      overlay), and
    //   2. click the FOUND POINT that corner should sit on. Shift+click opens a
    //      picker listing every point in the nest under the cursor so stacked
    //      monuments can be disambiguated.
    // Each committed pair re-solves a rigid-body (translate + rotate, no scale)
    // best fit and live-previews it through onUpdateBoundaryTransform.
    // ---------------------------------------------------------------------
    type CornerAlignPair = {
        /** Deed vertex point-number label as it appears in the calls. */
        pn: string;
        /** Un-baked (COGO-local) deed vertex — the fit's source frame. */
        srcE: number;
        srcN: number;
        /** Found survey point the corner is being pinned to. */
        targetPn: string;
        targetE: number;
        targetN: number;
    };
    const [cornerAlignBfId, setCornerAlignBfId] = useState<string | null>(null);
    const [cornerAlignPairs, setCornerAlignPairs] = useState<CornerAlignPair[]>([]);
    /** Corner picked but not yet paired to a found point. */
    const [cornerAlignPendingCorner, setCornerAlignPendingCorner] = useState<{ pn: string; srcE: number; srcN: number; x: number; y: number } | null>(null);
    /** Shift+click nest picker: candidate points under the cursor awaiting a choice. */
    const [cornerAlignPicker, setCornerAlignPicker] = useState<{ x: number; y: number; items: SurveyPoint[] } | null>(null);
    /** Residual summary of the most recent fit, shown in the banner. */
    const [cornerAlignFit, setCornerAlignFit] = useState<{ rmse: number; maxResidual: number; rotDeg: number } | null>(null);
    /** Corner under the cursor while waiting for a corner pick (hover affordance). */
    const [cornerAlignHover, setCornerAlignHover] = useState<{ pn: string; x: number; y: number } | null>(null);
    /** Transform the bf had when the tool was armed, restored on Cancel. */
    const cornerAlignBaseTxRef = useRef<{ translationE: number; translationN: number; rotationDeg: number } | null>(null);

    const lastCornerAlignTokenRef = useRef<number | null>(null);
    useEffect(() => {
        const req = props.boundaryCornerAlignRequest;
        if (!req || req.token === lastCornerAlignTokenRef.current) return;
        lastCornerAlignTokenRef.current = req.token;
        const bf = boundaryFiles?.find(b => b.id === req.movableBfId);
        cornerAlignBaseTxRef.current = {
            translationE: bf?.translationE ?? 0,
            translationN: bf?.translationN ?? 0,
            rotationDeg: bf?.rotationDeg ?? 0,
        };
        setDrawingMode('boundary-corner-align');
        setCornerAlignBfId(req.movableBfId);
        setCornerAlignPairs([]);
        setCornerAlignPendingCorner(null);
        setCornerAlignPicker(null);
        setCornerAlignFit(null);
        setCornerAlignHover(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.boundaryCornerAlignRequest]);

    // Boundary-line drawing state
    const [boundaryLineFrom, setBoundaryLineFrom] = useState<SurveyPoint | null>(null);
    // boundaryLineTo: null = waiting for 2nd click, 'snapped' or coords once filled by 2nd click
    const [boundaryClickedTo, setBoundaryClickedTo] = useState<{easting: number; northing: number} | null>(null);
    const [boundaryBearing, setBoundaryBearing] = useState('');
    const [boundaryDistance, setBoundaryDistance] = useState('');
    const [boundaryInputError, setBoundaryInputError] = useState('');
    const boundaryBearingRef = useRef<HTMLInputElement>(null);
    // Curve mode state
    const [boundaryCurveMode, setBoundaryCurveMode] = useState(false);
    const [boundaryCurveRadius, setBoundaryCurveRadius] = useState('');
    const [boundaryCurveArcLength, setBoundaryCurveArcLength] = useState('');
    const [boundaryCurveDirection, setBoundaryCurveDirection] = useState<'left' | 'right'>('right');
    const [boundaryCurveChordBearing, setBoundaryCurveChordBearing] = useState('');
    // Accumulated drawn calls for closure report
    const [drawnBoundaryCalls, setDrawnBoundaryCalls] = useState<Array<{ from: string; to: string; bearing: string; distance: string; isCurve?: boolean; curveRadius?: number; arcLength?: number; curveDirection?: 'left' | 'right'; tangentBearing?: string; chordBearing?: string }>>([]);
    const [boundaryClosureResult, setBoundaryClosureResult] = useState<{ precision: string; area: number; misclosureDistance: number; misclosureBearing: string } | null>(null);
    const [boundaryClosureError, setBoundaryClosureError] = useState('');
    // Annotation mode: controls what is shown on line labels and boundary overlay labels.
    // Lifted to App.tsx (see props) so the toggle button can live in the app header.

    const [isLabelDragging, setIsLabelDragging] = useState(false);
    const labelDragTarget = useRef<{pointNumber: string, startX: number, startY: number, startOffsetX: number, startOffsetY: number} | null>(null);
    const lineLabelDragTarget = useRef<{lineId: string, startX: number, startY: number, startOffsetX: number, startOffsetY: number} | null>(null);
    // Per-point annotation leader drag (Ctrl+drag on the note). Offsets are stored
    // in world units as { dx, dy } where dx<0 means the note is mirrored left.
    const annotationDragTarget = useRef<{pointNumber: string, startX: number, startY: number, startDx: number, startDy: number} | null>(null);
    // Screen-space hit boxes for annotation notes, refreshed every draw frame.
    // Used by Ctrl+click (drag) and Shift+click (mirror) hit-testing.
    const annotationHitBoxesRef = useRef<Map<string, { x: number; y: number; w: number; h: number }>>(new Map());
    // World-space hit boxes for curve annotation labels (R / L / Δ stack),
    // refreshed every draw frame. Used by Ctrl+drag to reposition the curve
    // label — mirrors the labelOffset flow already used by straight-line B+D.
    const curveLabelBoxesRef = useRef<Map<string, { wx: number; wy: number; ww: number; wh: number }>>(new Map());
    const deedMetadataDragTarget = useRef<{startX: number, startY: number, startOffsetX: number, startOffsetY: number} | null>(null);
    const ownerNameDragTarget = useRef<{startX: number, startY: number, startOffsetX: number, startOffsetY: number} | null>(null);
    
    const [isDrawingToolsMenuOpen, setIsDrawingToolsMenuOpen] = useState(false);
    const [isSelectionMode, setIsSelectionMode] = useState(false);
    const [isBoxSelectMode, setIsBoxSelectMode] = useState(false);
    
    // Toolbar orientation: default vertical, flippable to horizontal
    const [toolbarOrientation, setToolbarOrientation] = useState<'vertical' | 'horizontal'>(() => {
        try {
            const saved = localStorage.getItem('landsurv-canvas-toolbar-orientation-v2');
            return saved === 'horizontal' ? 'horizontal' : 'vertical';
        } catch {
            return 'vertical';
        }
    });

    const toolbarRef = useRef<HTMLDivElement>(null);
    // Toolbar dragging state — the default is the right edge, below the north arrow.
    // The position key is versioned so the new right-side default is picked up once.
    const [viewToolbarPosition, setViewToolbarPosition] = useState<ViewToolbarPosition | null>(() => {
        try {
            const saved = localStorage.getItem('landsurv-canvas-toolbar-position-v3');
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Number.isFinite(parsed.y) && parsed.y > 0) {
                    const hasFiniteX = Number.isFinite(parsed.x) && parsed.x > 0;
                    let isDocked: boolean;
                    if (parsed.isDocked !== undefined) {
                        isDocked = Boolean(parsed.isDocked);
                    } else if (!hasFiniteX) {
                        isDocked = true;
                    } else if (typeof window !== 'undefined' && parsed.x >= window.innerWidth - 120) {
                        isDocked = true;
                    } else {
                        isDocked = false;
                    }
                    return {
                        x: hasFiniteX ? parsed.x : undefined,
                        y: parsed.y,
                        isDocked,
                    };
                }
            }
        } catch { /* ignore */ }
        return null;
    });

    const latestToolbarPosRef = useRef<ViewToolbarPosition | null>(viewToolbarPosition);
    latestToolbarPosRef.current = viewToolbarPosition;

    const [isViewToolbarDragging, setIsViewToolbarDragging] = useState(false);
    const viewToolbarDragStart = useRef<{ clientX: number; clientY: number; toolbarX: number; toolbarY: number } | null>(null);
    const viewToolbarRafRef = useRef<number | null>(null);

    // Live height of the command bar, reported by CanvasTerminal. The floating
    // view toolbar rests on its top edge instead of using a hardcoded offset.
    const [commandBarHeight, setCommandBarHeight] = useState(0);
    const handleCommandBarHeightChange = useCallback((height: number) => {
        setCommandBarHeight(prev => (Math.abs(prev - height) < 1 ? prev : height));
        props.onCommandBarHeightChange?.(height);
    }, [props.onCommandBarHeightChange]);

    // If toolbar is NOT docked but positioned behind the agent chat (e.g. chat just expanded or container resized),
    // push it out so it remains visible and does not get lost behind the chat.
    const pushToolbarOutOfChatIfNeeded = useCallback(() => {
        if (isViewToolbarDragging) return;
        const container = containerRef.current;
        if (!container) return;

        const currentPos = latestToolbarPosRef.current;
        const toolbarW = toolbarRef.current?.offsetWidth ?? (toolbarOrientation === 'vertical' ? 50 : 380);

        const nextPos = calculateToolbarPushOutOfChat({
            currentPos,
            containerWidth: container.clientWidth,
            isDesktop,
            isChatPanelVisible,
            chatPanelWidth,
            toolbarWidth: toolbarW,
            windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1200,
        });

        if (nextPos) {
            latestToolbarPosRef.current = nextPos;
            setViewToolbarPosition(nextPos);
            try {
                localStorage.setItem('landsurv-canvas-toolbar-position-v3', JSON.stringify(nextPos));
            } catch { /* ignore */ }
        }
    }, [isViewToolbarDragging, isDesktop, isChatPanelVisible, chatPanelWidth, toolbarOrientation]);

    // Handle chat panel expand/collapse transitions & width updates
    useEffect(() => {
        pushToolbarOutOfChatIfNeeded();
        const t1 = setTimeout(pushToolbarOutOfChatIfNeeded, 60);
        const t2 = setTimeout(pushToolbarOutOfChatIfNeeded, 160);
        const t3 = setTimeout(pushToolbarOutOfChatIfNeeded, 320);
        return () => {
            clearTimeout(t1);
            clearTimeout(t2);
            clearTimeout(t3);
        };
    }, [isChatPanelVisible, chatPanelWidth, isDesktop, pushToolbarOutOfChatIfNeeded]);

    // Adjust position if orientation toggle causes toolbar to exceed container bounds
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const maxH = container.clientHeight;
        const toolbarH = toolbarRef.current?.offsetHeight ?? (toolbarOrientation === 'vertical' ? 450 : 50);
        if (maxH > 0 && viewToolbarPosition?.y !== undefined) {
            const maxY = Math.max(8, maxH - toolbarH - commandBarHeight - 8);
            if (viewToolbarPosition.y > maxY) {
                setViewToolbarPosition(prev => prev ? { ...prev, y: maxY } : null);
            }
        }
        pushToolbarOutOfChatIfNeeded();
    }, [toolbarOrientation, pushToolbarOutOfChatIfNeeded, commandBarHeight]);

    // Terminal incoming notification/event feed
    const [terminalIncoming, setTerminalIncoming] = useState<{ text: string; tone?: 'normal' | 'ok' | 'error' | 'info' } | null>(null);
    /** Modify/array command waiting for canvas picks — left-click selects, Enter applies, Esc cancels. */
    const [pendingSelectionCommand, setPendingSelectionCommand] = useState<{ raw: string; label: string } | null>(null);
    const runPendingSelectionCommandRef = useRef<() => void>(() => {});
    /** Bumped when a command finishes so the terminal can take the next typed command. */
    const [commandFocusRequest, setCommandFocusRequest] = useState(0);
    /** Interactive ROTATE: base point first, then a clicked point or typed angle. */
    const [isRotatingSelection, setIsRotatingSelection] = useState(false);
    const [rotatePivot, setRotatePivot] = useState<{ easting: number; northing: number } | null>(null);
    /** Interactive MIRROR: two snapped axis points, with a live reflected ghost. */
    const [isMirroringSelection, setIsMirroringSelection] = useState(false);
    const [mirrorAxisStart, setMirrorAxisStart] = useState<{ easting: number; northing: number } | null>(null);
    /** MIRROR [L] sub-mode: the next line pick becomes the mirror axis. */
    const [isPickingMirrorLine, setIsPickingMirrorLine] = useState(false);
    /** Interactive MOVE: snapped base point, then snapped destination. */
    const [isMovingSelection, setIsMovingSelection] = useState(false);
    const [moveBasePoint, setMoveBasePoint] = useState<{ easting: number; northing: number } | null>(null);
    /** Angle typed on the canvas while ROTATE is live (no command-line focus needed). */
    const [typedRotateAngle, setTypedRotateAngle] = useState('');
    /** Interactive SCALE: reference point first, then a clicked point or typed factor. */
    const [isScalingSelection, setIsScalingSelection] = useState(false);
    const [scaleBasePoint, setScaleBasePoint] = useState<{ easting: number; northing: number } | null>(null);
    /** Factor typed on the canvas while SCALE is live. */
    const [typedScaleFactor, setTypedScaleFactor] = useState('');
    /** The old auto selection card is retired in favour of the Properties dialog. */
    const [isSelectionInfoEnabled] = useState(false);
    const [isPropertiesOpen, setIsPropertiesOpen] = useState(false);

    // Helper to determine flyout direction based on toolbar position
    const getFlyoutDirection = useCallback((toolbarX: number, toolbarY: number) => {
        const screenWidth = typeof window !== 'undefined' ? window.innerWidth : 1200;
        const screenHeight = typeof window !== 'undefined' ? window.innerHeight : 800;
        
        // Determine if closer to top or bottom
        const isCloserToTop = toolbarY < screenHeight / 2;
        // Determine if closer to left or right
        const isCloserToLeft = toolbarX < screenWidth / 2;
        
        return {
            vertical: isCloserToTop ? 'below' : 'above',
            horizontal: isCloserToLeft ? 'right' : 'left'
        };
    }, []);

    const [isCtrlPressed, setIsCtrlPressed] = useState(false);
    const [isAltPressed, setIsAltPressed] = useState(false);
    const [isOrthoEnabled, setIsOrthoEnabled] = useState(false);
    const [isOverLabel, setIsOverLabel] = useState(false);
    // Line selection mode (active when drawing tools menu open, no sub-tool)
    const [selectedLineIds, setSelectedLineIds] = useState<Set<string>>(new Set());
    /** Select-mode survey point picks (stationing / point cleanup). */
    const [selectedPointNumbers, setSelectedPointNumbers] = useState<Set<string>>(new Set());
    /** Select-mode centerline alignment picks (Stationing & CL). */
    const [selectedCenterlineIds, setSelectedCenterlineIds] = useState<Set<string>>(new Set());
    // Point / centerline picks are only meaningful while the SELECT tool is live.
    // Drop them when the canvas leaves selection mode so their rings can never
    // linger behind a drawing or transform command.
    useEffect(() => {
        if (isSelectionMode) return;
        setSelectedPointNumbers(prev => (prev.size === 0 ? prev : new Set()));
        setSelectedCenterlineIds(prev => (prev.size === 0 ? prev : new Set()));
    }, [isSelectionMode]);
    const [isOverLine, setIsOverLine] = useState(false);
    const [shiftBoxSelect, setShiftBoxSelect] = useState<{ startX: number; startY: number; endX: number; endY: number } | null>(null);
    const [hoveredGrip, setHoveredGrip] = useState<EntityGrip | null>(null);
    const [activeGrip, setActiveGrip] = useState<EntityGrip | null>(null);
    const activeGripDragStartRef = useRef<{ clientX: number; clientY: number } | null>(null);
    // Line query panel drag state
    const [lineQueryPanelPos, setLineQueryPanelPos] = useState<{ x: number; y: number } | null>(null);
    const lineQueryDragState = useRef<{ dragging: boolean; startX: number; startY: number; startPosX: number; startPosY: number }>({ dragging: false, startX: 0, startY: 0, startPosX: 0, startPosY: 0 });

    // COPY / OFFSET command-line interaction state
    const [isCopyingSelection, setIsCopyingSelection] = useState(false);
    const [copyBasePoint, setCopyBasePoint] = useState<{ easting: number; northing: number } | null>(null);
    const [isOffsetMode, setIsOffsetMode] = useState(false);
    const [offsetDistance, setOffsetDistance] = useState<number | null>(null);
    const [offsetTargetId, setOffsetTargetId] = useState<string | null>(null);
    const [offsetPickStart, setOffsetPickStart] = useState<{ easting: number; northing: number } | null>(null);
    const offsetPreviewSegmentsRef = useRef<OffsetEntitySegment[] | null>(null);
    const offsetPreviewKeyRef = useRef<string | null>(null);
    /** Dashed transform ghost segments (rotated / scaled / mirrored / moved selection). */
    const rotatePreviewSegmentsRef = useRef<OffsetEntitySegment[] | null>(null);
    const rotatePreviewAngleRef = useRef<number | null>(null);
    /** Distance from the SCALE reference point to the selection edge, captured on pick. */
    const scaleReferenceRadiusRef = useRef<number | null>(null);
    /** True while the buffered ghost is a SCALE preview (drawn in emerald). */
    const scaleGhostActiveRef = useRef(false);
    /** Key of the ghost currently buffered, so identical mousemove events skip the rebuild. */
    const previewKeyRef = useRef<string | null>(null);
    /** Base geometry + array offsets shown while the AR X/Y manipulator is open. */
    const arrayPreviewRef = useRef<{
        baseSegments: OffsetEntitySegment[];
        rows: number;
        columns: number;
        rowGap: number;
        columnGap: number;
        cos: number;
        sin: number;
    } | null>(null);
    /** Base geometry + polar sweep shown while the PA manipulator is open. */
    const polarPreviewRef = useRef<{
        baseSegments: OffsetEntitySegment[];
        center: { x: number; y: number };
        count: number;
        fillAngle: number;
    } | null>(null);
    
    // Use ref for cursor position to avoid re-creating draw callback on every mouse move
    const cursorWorldPosRef = useRef<{ x: number, y: number } | null>(null);
    const drawRef = useRef<(() => void) | null>(null);
    // Track pending animation frame to prevent multiple queued draws
    const animationFrameRef = useRef<number | null>(null);
    
    const [wmsImages, setWmsImages] = useState<{ image: CanvasImageSource; bbox: [number, number, number, number]; quad?: WorldQuad; opacity: number }[]>([]);
    const [offlineImages, setOfflineImages] = useState<Map<string, HTMLImageElement>>(new Map());
    const [googleOverlayImage, setGoogleOverlayImage] = useState<{
        image: HTMLImageElement;
        bbox: [number, number, number, number];
        /** All tiles to draw; absent for single-image overlays. */
        tiles?: { image: HTMLImageElement; bbox: [number, number, number, number]; quad?: WorldQuad }[];
        opacity: number;
        source: 'user-maps' | 'user-gemini' | 'proxy' | 'naip-proxy';
        mapType: NonNullable<StaticMapRequest['maptype']>;
    } | null>(null);
    const [googleOverlayDiagnostic, setGoogleOverlayDiagnostic] = useState<{ tone: 'info' | 'error'; message: string } | null>(null);
    const googleOverlaySeqRef = useRef(0);
    const overlayTilesRef = useRef<{ image: HTMLImageElement; bbox: [number, number, number, number]; quad?: WorldQuad; source: 'user-maps' | 'user-gemini' | 'proxy' | 'naip-proxy' }[]>([]);
    const naipMercatorEpsgRef = useRef<Set<number>>(new Set());
    const [pendingTiles, setPendingTiles] = useState(0);
    const lastOverlayDiagnosticNotificationRef = useRef<string | null>(null);

    useEffect(() => {
        if (!settings.googleMaps?.enabled || !googleOverlayDiagnostic) {
            lastOverlayDiagnosticNotificationRef.current = null;
            return;
        }
        const key = `${googleOverlayDiagnostic.tone}:${googleOverlayDiagnostic.message}`;
        if (lastOverlayDiagnosticNotificationRef.current === key) return;
        lastOverlayDiagnosticNotificationRef.current = key;
        addNotification({
            kind: 'map-imagery-overlay',
            severity: googleOverlayDiagnostic.tone === 'error' ? 'error' : 'warning',
            title: 'Map Imagery Overlay',
            message: googleOverlayDiagnostic.message,
        });
    }, [settings.googleMaps?.enabled, googleOverlayDiagnostic, addNotification]);

    useEffect(() => {
        try {
            localStorage.setItem(RUNNING_OSNAP_STORAGE_KEY, JSON.stringify([...runningOsnaps]));
        } catch {
            // Ignore localStorage failures (private mode / quota).
        }
    }, [runningOsnaps]);

    useEffect(() => {
        try {
            localStorage.setItem(RUNNING_OSNAP_ENABLED_STORAGE_KEY, String(isRunningOsnapsEnabled));
        } catch {
            // Ignore localStorage failures (private mode / quota).
        }
    }, [isRunningOsnapsEnabled]);

    const toggleRunningOsnap = useCallback((mode: OsnapMode) => {
        setRunningOsnaps(prev => {
            const next = new Set(prev);
            if (next.has(mode)) {
                // The global SNAP switch is the way to turn all snapping off;
                // keep one running type selected so enabling it again works.
                if (next.size <= 1) return prev;
                next.delete(mode);
            } else next.add(mode);
            return next;
        });
    }, []);

    const toggleRunningOsnapsEnabled = useCallback(() => {
        setIsRunningOsnapsEnabled(prev => !prev);
    }, []);

    useEffect(() => {
        if (!isRunningOsnapsEnabled && !inlineOsnapOverride) setActiveOsnapPoint(null);
    }, [isRunningOsnapsEnabled, inlineOsnapOverride]);

    const runningOsnapLabel = useMemo(() => {
        if (!isRunningOsnapsEnabled) return 'Off';
        const active = OSNAP_ORDER.filter(mode => runningOsnaps.has(mode));
        return active.length > 0 ? active.map(mode => OSNAP_LABELS[mode]).join(', ') : 'None';
    }, [isRunningOsnapsEnabled, runningOsnaps]);


    const pointMap = useMemo(() => new Map(points.map(p => [p.pointNumber, p])), [points]);

    const findNearestSurveyPoint = useCallback((screenX: number, screenY: number, thresholdPx: number, allowWhenSnapsOff = false): SurveyPoint | null => {
        // Survey-point snapping is part of the running-snap system. Point
        // selection and boundary picks can opt out with allowWhenSnapsOff.
        if (!isRunningOsnapsEnabled && !inlineOsnapOverride && !allowWhenSnapsOff) return null;
        let nearestPoint: SurveyPoint | null = null;
        let minDistSq = thresholdPx * thresholdPx;
        for (const p of points) {
            if (p.hidden) continue;
            const ptScreen = worldToScreen(p.easting, p.northing);
            const dSq = (screenX - ptScreen.x) ** 2 + (screenY - ptScreen.y) ** 2;
            if (dSq < minDistSq) {
                minDistSq = dSq;
                nearestPoint = p;
            }
        }
        return nearestPoint;
    }, [points, worldToScreen, isRunningOsnapsEnabled, inlineOsnapOverride]);

    const makeTempSnapPoint = useCallback((snap: OsnapResult): SurveyPoint => ({
        pointNumber: `SNAP_${snap.mode}_${Date.now()}_${Math.round(snap.easting * 1000)}_${Math.round(snap.northing * 1000)}`,
        easting: snap.easting,
        northing: snap.northing,
        hidden: true,
    }), []);

    const makeTempWorldPoint = useCallback((easting: number, northing: number): SurveyPoint => ({
        pointNumber: `FREE_${Date.now()}_${Math.round(easting * 1000)}_${Math.round(northing * 1000)}`,
        easting,
        northing,
        hidden: true,
    }), []);

    const applyOrthoConstraint = useCallback((
        anchor: { easting: number; northing: number },
        target: { easting: number; northing: number }
    ) => {
        const dx = target.easting - anchor.easting;
        const dy = target.northing - anchor.northing;
        const isDraftingMode = (orientationTuple?.mode ?? 'drafting') === 'drafting';
        const theta = isDraftingMode
            ? (orientationTuple?.draftingAngle ?? orientationTuple?.angle ?? 0)
            : (orientationTuple?.viewAngle ?? orientationTuple?.angle ?? 0);

        if (theta !== 0) {
            // Rotate (dx, dy) into OT reference frame
            const cos = Math.cos(theta);
            const sin = Math.sin(theta);
            const rx = dx * cos + dy * sin;
            const ry = -dx * sin + dy * cos;

            // Snap to nearest 90-degree axis in OT frame
            let projX = 0;
            let projY = 0;
            if (Math.abs(rx) >= Math.abs(ry)) {
                projX = rx;
                projY = 0;
            } else {
                projX = 0;
                projY = ry;
            }

            // Rotate back from OT frame to world frame
            const resDx = projX * cos - projY * sin;
            const resDy = projX * sin + projY * cos;
            return {
                easting: anchor.easting + resDx,
                northing: anchor.northing + resDy,
            };
        }

        if (Math.abs(dx) >= Math.abs(dy)) {
            return { easting: target.easting, northing: anchor.northing };
        }
        return { easting: anchor.easting, northing: target.northing };
    }, [orientationTuple]);

    const isLinearDrawingMode = useCallback((mode: typeof drawingMode) => (
        mode === 'polylines' || mode === 'breaklines' || mode === 'inclusion' || mode === 'exclusion'
    ), []);

    const isDimensionDrawingMode = useCallback((mode: typeof drawingMode) => (
        mode === 'aligned-dim' ||
        mode === 'horiz-dim' ||
        mode === 'vert-dim' ||
        mode === 'radius-dim' ||
        mode === 'diameter-dim' ||
        mode === 'arc-length-dim' ||
        mode === 'curve-data-dim' ||
        mode === 'angular-dim' ||
        mode === 'ordinate-dim'
    ), []);

    const resetDimMode = useCallback(() => {
        setDimPhase(0);
        setDimP1(null);
        setDimP2(null);
        setDimP3(null);
        setDimSelectedCurve(null);
        setDimHoveredCurve(null);
        setDimPreviewMouse(null);
        setDimSnapPoint(null);
        setActiveOsnapPoint(null);
    }, []);

    const isPointNumberTaken = useCallback((pn: string): boolean => {
        if (!pn) return false;
        const normalized = pn.trim().toUpperCase();
        if (pointMap.has(pn) || pointMap.has(normalized)) return true;
        if (points.some(p => String(p.pointNumber).trim().toUpperCase() === normalized)) return true;
        if (pointLists?.some(l => l.points?.some(p => String(p.pointNumber).trim().toUpperCase() === normalized))) return true;
        return false;
    }, [pointMap, points, pointLists]);

    // CACP Point Authority: queries PointAgent / CACP protocol for the next available point number,
    // ensuring we never propose or assign a point number that already exists in any list or survey dataset.
    const queryCacpNextPointNumber = useCallback((excludePns?: Set<string>): string => {
        try {
            const agent = PointAgent.getInstance();
            agent.setLabelingSettings(settings.pointLabelingSettings);
            const allKnown = new Map<string, SurveyPoint>();
            points.forEach(p => { if (p?.pointNumber) allKnown.set(String(p.pointNumber), p); });
            pointLists?.forEach(l => l.points?.forEach(p => { if (p?.pointNumber) allKnown.set(String(p.pointNumber), p); }));
            agent.setAvailablePoints(Array.from(allKnown.values()));
            const [next] = agent.getNextNumbers(1, AgentType.CIVIL_DRAFTER);
            if (next && !allKnown.has(String(next)) && (!excludePns || !excludePns.has(String(next)))) {
                return next;
            }
        } catch (e) {
            console.warn('[DrawingCanvas] CACP queryNextPointNumber error:', e);
        }
        // Fallback if PointAgent unavailable: scan numbers until finding an untaken one
        const taken = new Set<string>();
        points.forEach(p => taken.add(String(p.pointNumber)));
        pointLists?.forEach(l => l.points?.forEach(p => taken.add(String(p.pointNumber))));
        if (excludePns) {
            excludePns.forEach(p => taken.add(p));
        }
        let cur = 1;
        while (taken.has(String(cur))) {
            cur++;
        }
        return String(cur);
    }, [points, pointLists, settings.pointLabelingSettings]);

    // Keep addPointNumber in sync with CACP authority whenever points or nextAvailablePointNumber change
    useEffect(() => {
        setAddPointNumber(prev => {
            if (!prev || isPointNumberTaken(prev)) {
                return nextAvailablePointNumber && !isPointNumberTaken(nextAvailablePointNumber)
                    ? nextAvailablePointNumber
                    : queryCacpNextPointNumber();
            }
            return prev;
        });
    }, [nextAvailablePointNumber, isPointNumberTaken, queryCacpNextPointNumber]);

    const commitManualAddPoint = useCallback(() => {
        const n = parseFloat(addPointNorthing);
        const e = parseFloat(addPointEasting);
        if (isNaN(n) || isNaN(e)) {
            notifyBoundaryError('Enter valid Northing and Easting coordinates.');
            return;
        }

        let ptNum = addPointNumber.trim();
        // CACP verification: check what is available and prevent duplicates
        if (!ptNum || isPointNumberTaken(ptNum)) {
            const cacpNext = queryCacpNextPointNumber();
            if (ptNum && isPointNumberTaken(ptNum)) {
                addNotification({
                    kind: 'point-add',
                    severity: 'warning',
                    title: 'Point Exists',
                    message: `Point ${ptNum} already exists. Using next available CACP point: ${cacpNext}`,
                });
            }
            ptNum = cacpNext;
        }

        const newPoint: SurveyPoint = {
            pointNumber: ptNum,
            northing: Math.round(n * 1000) / 1000,
            easting: Math.round(e * 1000) / 1000,
            elevation: parseFloat(addPointElevation) || 0,
            description: addPointDescription.trim(),
        };
        onAddPoint?.(newPoint);

        const nextNum = queryCacpNextPointNumber(new Set([ptNum]));
        setAddPointNumber(nextNum);
        addNotification({
            kind: 'point-add',
            severity: 'success',
            title: 'Point Added',
            message: `Added Point ${ptNum} (N: ${newPoint.northing.toFixed(2)}, E: ${newPoint.easting.toFixed(2)})`,
        });
    }, [addPointNorthing, addPointEasting, addPointNumber, addPointElevation, addPointDescription, onAddPoint, notifyBoundaryError, addNotification, isPointNumberTaken, queryCacpNextPointNumber]);

    const buildLineEndpoint = useCallback((point: SurveyPoint) => {
        const committedPoint = !point.hidden ? pointMap.get(point.pointNumber) : null;
        if (committedPoint) {
            return { ref: committedPoint.pointNumber, coords: undefined as { x: number; y: number; z: number } | undefined };
        }
        return {
            ref: point.pointNumber,
            coords: { x: point.easting, y: point.northing, z: 0 },
        };
    }, [pointMap]);

    const addCircleGeometry = useCallback((center: { easting: number; northing: number }, radius: number) => {
        if (!radius || radius <= 1e-6) return;
        const pCenter = makeTempWorldPoint(center.easting, center.northing);
        const pCenterLine = buildLineEndpoint(pCenter);
        const circumference = 2 * Math.PI * radius;

        // Single circle entity
        onAddLine({
            from: pCenterLine.ref,
            to: pCenterLine.ref,
            type: undefined,
            layer: activeDrawingLayer || undefined,
            fromPt: { x: center.easting, y: center.northing, z: 0 },
            toPt: { x: center.easting, y: center.northing, z: 0 },
            circleCenter: { x: center.easting, y: center.northing, z: 0 },
            isCurve: true,
            isCircle: true,
            curveRadius: radius,
            arcLength: circumference,
        });

        requestAnimationFrame(() => {
            drawRef.current?.();
        });
    }, [makeTempWorldPoint, buildLineEndpoint, onAddLine, activeDrawingLayer]);

    /** Resolve a SurveyLine's endpoints from its point records (fallback: stored coords). */
    const resolveLineEndpoints = useCallback((line: SurveyLine): { p1: { x: number; y: number }; p2: { x: number; y: number } } | null => {
        const pt1 = pointMap.get(line.from);
        const pt2 = pointMap.get(line.to);
        const p1 = pt1 ? { x: pt1.easting, y: pt1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
        const p2 = pt2 ? { x: pt2.easting, y: pt2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);
        if (!p1 || !p2) return null;
        return { p1, p2 };
    }, [pointMap]);

    /** True for a full-circle entity (handles both modern and legacy encodings). */
    const isCircleLine = useCallback((line: SurveyLine): boolean => {
        if (line.isCircle) return true;
        if (!line.isCurve || !line.curveRadius || line.chordBearing || line.tangentBearing) return false;
        const ends = resolveLineEndpoints(line);
        return !!ends && Math.abs(ends.p1.x - ends.p2.x) < 1e-6 && Math.abs(ends.p1.y - ends.p2.y) < 1e-6;
    }, [resolveLineEndpoints]);

    /** COPY: duplicate the current selection by a world-coordinate displacement. */
    const duplicateSelectedLines = useCallback((dx: number, dy: number): number => {
        if (selectedLineIds.size === 0 || (Math.abs(dx) < 1e-9 && Math.abs(dy) < 1e-9)) return 0;
        const stamp = Date.now();
        const polylineMap = new Map<string, string>();
        let copied = 0;

        selectedLineIds.forEach((selId, index) => {
            const line = lines.find(l => (l.id || `${l.from}-${l.to}`) === selId);
            if (!line || line.hidden) return;
            const ends = resolveLineEndpoints(line);
            if (!ends) return;

            const fromRef = `COPY_${stamp}_${index}_A`;
            const toRef = `COPY_${stamp}_${index}_B`;
            const newLine: SurveyLine = {
                ...line,
                id: undefined,
                from: fromRef,
                to: toRef,
                fromPt: { x: ends.p1.x + dx, y: ends.p1.y + dy, z: 0 },
                toPt: { x: ends.p2.x + dx, y: ends.p2.y + dy, z: 0 },
            };

            if (line.circleCenter) {
                newLine.circleCenter = {
                    x: line.circleCenter.x + dx,
                    y: line.circleCenter.y + dy,
                    z: line.circleCenter.z ?? 0,
                };
            }

            if (isCircleLine(line)) {
                const center = line.circleCenter
                    ? { x: line.circleCenter.x, y: line.circleCenter.y }
                    : ends.p1;
                newLine.fromPt = { x: center.x + dx, y: center.y + dy, z: 0 };
                newLine.toPt = { ...newLine.fromPt };
                newLine.circleCenter = { x: center.x + dx, y: center.y + dy, z: line.circleCenter?.z ?? 0 };
            }

            if (line.polylineId) {
                if (!polylineMap.has(line.polylineId)) {
                    polylineMap.set(line.polylineId, `COPY_${stamp}_${polylineMap.size}`);
                }
                newLine.polylineId = polylineMap.get(line.polylineId);
            }

            onAddLine(newLine);
            copied++;
        });

        return copied;
    }, [selectedLineIds, lines, resolveLineEndpoints, isCircleLine, onAddLine]);

    // ── Typed CAD drafting commands ──────────────────────────────────────
    // These are reachable only from the CAD command line (no toolbar buttons):
    // transforms, arrays, erase, and rectangle/polygon primitives.

    const getSelectedLineEntities = useCallback(() => {
        const entities: Array<{
            selectionId: string;
            line: SurveyLine;
            ends: { p1: { x: number; y: number }; p2: { x: number; y: number } };
            isCircle: boolean;
        }> = [];
        selectedLineIds.forEach(selectionId => {
            const line = lines.find(l => (l.id || `${l.from}-${l.to}`) === selectionId);
            if (!line || line.hidden) return;
            const ends = resolveLineEndpoints(line);
            if (!ends) return;
            entities.push({ selectionId, line, ends, isCircle: isCircleLine(line) });
        });
        return entities;
    }, [selectedLineIds, lines, resolveLineEndpoints, isCircleLine]);

    const getSelectionBoundingCenter = useCallback((): PlanarPoint | null => {
        const entities = getSelectedLineEntities();
        if (entities.length === 0) return null;
        let minX = Infinity;
        let minY = Infinity;
        let maxX = -Infinity;
        let maxY = -Infinity;
        const include = (x: number, y: number) => {
            minX = Math.min(minX, x);
            minY = Math.min(minY, y);
            maxX = Math.max(maxX, x);
            maxY = Math.max(maxY, y);
        };
        entities.forEach(entity => {
            if (entity.isCircle) {
                const center = entity.line.circleCenter ?? entity.ends.p1;
                const radius = entity.line.curveRadius ?? 0;
                include(center.x - radius, center.y - radius);
                include(center.x + radius, center.y + radius);
            } else {
                include(entity.ends.p1.x, entity.ends.p1.y);
                include(entity.ends.p2.x, entity.ends.p2.y);
            }
        });
        if (!Number.isFinite(minX) || !Number.isFinite(minY)) return null;
        return { x: (minX + maxX) / 2, y: (minY + maxY) / 2 };
    }, [getSelectedLineEntities]);

    const commitLineBatch = useCallback((removeIds: string[], addLines: SurveyLine[]) => {
        if (removeIds.length === 0 && addLines.length === 0) return;
        if (props.onBatchLinesChange) {
            props.onBatchLinesChange(removeIds, addLines);
        } else {
            removeIds.forEach(id => onDeleteLine(id));
            addLines.forEach(line => onAddLine(line));
        }
        requestAnimationFrame(() => drawRef.current?.());
    }, [props.onBatchLinesChange, onDeleteLine, onAddLine]);

    /** Replace the selected entities with transformed copies (move/rotate/scale/mirror). */
    const transformSelectedLines = useCallback((transform: PlanarTransform, resetAnnotations: boolean): number => {
        const entities = getSelectedLineEntities();
        if (entities.length === 0) return 0;
        const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const removeIds: string[] = [];
        const addLines: SurveyLine[] = [];
        entities.forEach((entity, index) => {
            addLines.push(transformSurveyLine(entity.line, entity.ends, transform, {
                refA: `XF_${stamp}_${index}_A`,
                refB: `XF_${stamp}_${index}_B`,
                polylineId: entity.line.polylineId ? `XF_${stamp}_${entity.line.polylineId}` : undefined,
                isCircle: entity.isCircle,
                resetAnnotations,
            }));
            removeIds.push(entity.selectionId);
        });
        commitLineBatch(removeIds, addLines);
        setSelectedLineIds(new Set());
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        return addLines.length;
    }, [getSelectedLineEntities, commitLineBatch]);

    const arraySelectionCommand = useCallback((rows: number, columns: number, rowSpacing: number, columnSpacing: number, angleDegrees = 0): number => {
        const entities = getSelectedLineEntities();
        if (entities.length === 0) return 0;
        const unit = settings.linearUnits || 'usSurveyFoot';
        const rowGap = convertLinearUnits(rowSpacing, unit, 'foot');
        const columnGap = convertLinearUnits(columnSpacing, unit, 'foot');
        const direction = (angleDegrees * Math.PI) / 180;
        const cos = Math.cos(direction);
        const sin = Math.sin(direction);
        const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const addLines: SurveyLine[] = [];
        for (let row = 0; row < rows; row++) {
            for (let column = 0; column < columns; column++) {
                if (row === 0 && column === 0) continue;
                const localX = column * columnGap;
                const localY = row * rowGap;
                const transform = translationTransform(localX * cos - localY * sin, localX * sin + localY * cos);
                entities.forEach((entity, index) => {
                    addLines.push(transformSurveyLine(entity.line, entity.ends, transform, {
                        refA: `AR_${stamp}_${row}_${column}_${index}_A`,
                        refB: `AR_${stamp}_${row}_${column}_${index}_B`,
                        polylineId: entity.line.polylineId ? `AR_${stamp}_${row}_${column}_${entity.line.polylineId}` : undefined,
                        isCircle: entity.isCircle,
                        resetAnnotations: false,
                    }));
                });
            }
        }
        commitLineBatch([], addLines);
        return addLines.length;
    }, [getSelectedLineEntities, commitLineBatch, settings.linearUnits]);

    const polarArraySelectionCommand = useCallback((count: number, fillAngleDegrees: number, centerArg?: { easting: number; northing: number }): number => {
        const entities = getSelectedLineEntities();
        const center = centerArg ? { x: centerArg.easting, y: centerArg.northing } : getSelectionBoundingCenter();
        if (!center || entities.length === 0) return 0;
        const step = fillAngleDegrees >= 359.999
            ? fillAngleDegrees / count
            : fillAngleDegrees / Math.max(1, count - 1);
        const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const addLines: SurveyLine[] = [];
        for (let item = 1; item < count; item++) {
            const transform = rotationTransform(center, (item * step * Math.PI) / 180);
            entities.forEach((entity, index) => {
                addLines.push(transformSurveyLine(entity.line, entity.ends, transform, {
                    refA: `PA_${stamp}_${item}_${index}_A`,
                    refB: `PA_${stamp}_${item}_${index}_B`,
                    polylineId: entity.line.polylineId ? `PA_${stamp}_${item}_${entity.line.polylineId}` : undefined,
                    isCircle: entity.isCircle,
                    resetAnnotations: true,
                }));
            });
        }
        commitLineBatch([], addLines);
        return addLines.length;
    }, [getSelectedLineEntities, getSelectionBoundingCenter, commitLineBatch]);

    const moveSelectionCommand = useCallback((dx: number, dy: number): number => {
        const unit = settings.linearUnits || 'usSurveyFoot';
        return transformSelectedLines(
            translationTransform(
                convertLinearUnits(dx, unit, 'foot'),
                convertLinearUnits(dy, unit, 'foot'),
            ),
            false,
        );
    }, [transformSelectedLines, settings.linearUnits]);

    const rotateSelectionCommand = useCallback((angleDegrees: number, pivot?: { easting: number; northing: number }): number => {
        const center = pivot ? { x: pivot.easting, y: pivot.northing } : getSelectionBoundingCenter();
        if (!center) return 0;
        return transformSelectedLines(rotationTransform(center, (angleDegrees * Math.PI) / 180), true);
    }, [getSelectionBoundingCenter, transformSelectedLines]);

    const scaleSelectionCommand = useCallback((factor: number, pivot?: { easting: number; northing: number }): number => {
        const center = pivot ? { x: pivot.easting, y: pivot.northing } : getSelectionBoundingCenter();
        if (!center) return 0;
        return transformSelectedLines(scalingTransform(center, factor), true);
    }, [getSelectionBoundingCenter, transformSelectedLines]);

    /** Farthest distance from a reference point to the selected geometry (drag baseline). */
    const selectionRadiusFrom = useCallback((reference: { easting: number; northing: number }): number => {
        let radius = 0;
        for (const entity of getSelectedLineEntities()) {
            for (const point of [entity.ends.p1, entity.ends.p2]) {
                radius = Math.max(radius, Math.hypot(point.x - reference.easting, point.y - reference.northing));
            }
            if (entity.line.isCircle && entity.line.curveRadius) {
                const center = entity.line.circleCenter ?? entity.ends.p1;
                radius = Math.max(
                    radius,
                    Math.hypot(center.x - reference.easting, center.y - reference.northing) + entity.line.curveRadius,
                );
            }
        }
        return radius;
    }, [getSelectedLineEntities]);

    const mirrorSelectionCommand = useCallback((p1: { easting: number; northing: number }, p2: { easting: number; northing: number }): number => {
        return transformSelectedLines(
            reflectionTransform({ x: p1.easting, y: p1.northing }, { x: p2.easting, y: p2.northing }),
            true,
        );
    }, [transformSelectedLines]);

    const joinSelectionCommand = useCallback((): number => {
        const entities = getSelectedLineEntities().filter(entity => !entity.isCircle);
        if (entities.length < 2) return 0;
        const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const polylineId = `JOIN_${stamp}`;
        const addLines: SurveyLine[] = entities.map(entity => ({
            ...entity.line,
            id: undefined,
            polylineId,
        }));
        commitLineBatch(entities.map(entity => entity.selectionId), addLines);
        setSelectedLineIds(new Set());
        setIsSelectionMode(false);
        return addLines.length;
    }, [getSelectedLineEntities, commitLineBatch]);

    const explodeSelectionCommand = useCallback((): number => {
        const entities = getSelectedLineEntities().filter(entity => entity.line.polylineId);
        if (entities.length === 0) return 0;
        const addLines: SurveyLine[] = entities.map(entity => ({
            ...entity.line,
            id: undefined,
            polylineId: undefined,
        }));
        commitLineBatch(entities.map(entity => entity.selectionId), addLines);
        setSelectedLineIds(new Set());
        setIsSelectionMode(false);
        return addLines.length;
    }, [getSelectedLineEntities, commitLineBatch]);

    const eraseSelectionCommand = useCallback((): number => {
        const ids = Array.from(selectedLineIds);
        if (ids.length === 0) return 0;
        commitLineBatch(ids, []);
        setSelectedLineIds(new Set());
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        return ids.length;
    }, [selectedLineIds, commitLineBatch]);

    /** Apply a property patch to every selected entity (one canvas refresh). */
    const handleUpdateSelectedLines = useCallback((ids: string[], changes: Partial<SurveyLine>) => {
        if (ids.length === 0) return;
        ids.forEach(id => props.onUpdateLine?.(id, changes));
        requestAnimationFrame(() => drawRef.current?.());
    }, [props.onUpdateLine]);

    const selectAllEntitiesCommand = useCallback((): number => {
        const ids = new Set(
            lines.filter(line => !line.hidden).map(line => line.id || `${line.from}-${line.to}`),
        );
        setSelectedLineIds(ids);
        setIsSelectionMode(ids.size > 0);
        setIsBoxSelectMode(false);
        return ids.size;
    }, [lines]);

    const deselectAllCommand = useCallback(() => {
        setSelectedLineIds(new Set());
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
    }, []);

    const drawRectangleCommand = useCallback((p1: { easting: number; northing: number }, p2: { easting: number; northing: number }): number => {
        const vertices = rectangleVertices(
            { x: p1.easting, y: p1.northing },
            { x: p2.easting, y: p2.northing },
        );
        if (!vertices) return 0;
        const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const segments = buildClosedSegments(vertices, {
            refPrefix: `REC_${stamp}`,
            polylineId: `REC_${stamp}`,
            layer: activeDrawingLayer || undefined,
        });
        commitLineBatch([], segments);
        return segments.length;
    }, [activeDrawingLayer, commitLineBatch]);

    const drawPolygonCommand = useCallback((sides: number, center: { easting: number; northing: number }, radius: number): number => {
        const worldRadius = convertLinearUnits(radius, settings.linearUnits || 'usSurveyFoot', 'foot');
        const vertices = polygonVertices({ x: center.easting, y: center.northing }, worldRadius, sides);
        if (vertices.length === 0) return 0;
        const stamp = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`;
        const segments = buildClosedSegments(vertices, {
            refPrefix: `POL_${stamp}`,
            polylineId: `POL_${stamp}`,
            layer: activeDrawingLayer || undefined,
        });
        commitLineBatch([], segments);
        return segments.length;
    }, [activeDrawingLayer, commitLineBatch, settings.linearUnits]);

    const commitPolygonFromRadius = useCallback((radiusInput: number): boolean => {
        if (!polygonCenter) return false;
        if (!Number.isFinite(radiusInput) || radiusInput <= 0) {
            setTerminalIncoming({ text: 'POLYGON: Radius must be a positive number', tone: 'error' });
            return false;
        }
        const segments = drawPolygonCommand(polygonSides, polygonCenter, radiusInput);
        if (segments <= 0) {
            setTerminalIncoming({ text: 'POLYGON: Could not create the polygon', tone: 'error' });
            return false;
        }
        setTerminalIncoming({
            text: `POLYGON: Created ${polygonSides}-sided polygon (${segments} segments) about E ${polygonCenter.easting.toFixed(2)}, N ${polygonCenter.northing.toFixed(2)} — diameter ${Number((radiusInput * 2).toFixed(3))} ${getLinearUnitAbbreviation(settings.linearUnits)}.`,
            tone: 'ok',
        });
        setPolygonCenter(null);
        setTypedPolygonDiameter('');
        setDrawingMode('none');
        setActiveOsnapPoint(null);
        setCommandFocusRequest(n => n + 1);
        requestAnimationFrame(() => drawRef.current?.());
        return true;
    }, [drawPolygonCommand, polygonCenter, polygonSides, settings.linearUnits]);

    /**
     * OFFSET: compute the offset geometry for the picked line/polyline/arc/circle
     * without committing it. Connected segments are treated as one chain and
     * mitered (line-line, line-arc, arc-arc) at their shared vertices.
     */
    const buildOffsetGeometry = useCallback((targetLine: SurveyLine, clickPoint: { x: number; y: number }): {
        ok: boolean;
        count: number;
        message: string;
        segments?: OffsetEntitySegment[];
        source?: SurveyLine;
    } => {
        if (offsetDistance === null || !(offsetDistance > 0)) {
            return { ok: false, count: 0, message: 'OFFSET: Specify a positive offset distance first.' };
        }
        const ends = resolveLineEndpoints(targetLine);
        if (!ends) return { ok: false, count: 0, message: 'OFFSET: Could not resolve that entity geometry.' };

        if (isCircleLine(targetLine)) {
            const radius = targetLine.curveRadius;
            if (!radius) return { ok: false, count: 0, message: 'OFFSET: That circle has no radius.' };
            const center = targetLine.circleCenter
                ? { x: targetLine.circleCenter.x, y: targetLine.circleCenter.y }
                : ends.p1;
            const newRadius = offsetCircleRadius(center, radius, offsetDistance, clickPoint);
            if (newRadius === null) {
                return { ok: false, count: 0, message: 'OFFSET: Distance is too large for that circle.' };
            }
            return {
                ok: true,
                count: 1,
                message: 'OFFSET: Circle offset ready.',
                segments: [{ kind: 'circle', center, radius: newRadius }],
                source: targetLine,
            };
        }

        const seedId = targetLine.id || `${targetLine.from}-${targetLine.to}`;
        const candidates = targetLine.polylineId
            ? lines.filter(l => l.polylineId === targetLine.polylineId && !l.hidden && !isCircleLine(l))
            : lines.filter(l => !l.hidden && !isCircleLine(l));

        const segments: ChainSegment[] = [];
        const segmentSources = new Map<string, SurveyLine>();
        for (const l of candidates) {
            const e = resolveLineEndpoints(l);
            if (!e) continue;
            const id = l.id || `${l.from}-${l.to}`;
            let curve: ChainSegment['curve'];
            if (l.isCurve) {
                if (!l.curveRadius || !l.arcLength) {
                    if (id === seedId || targetLine.polylineId) {
                        return { ok: false, count: 0, message: 'OFFSET: Could not resolve a curve in that linework.' };
                    }
                    continue;
                }
                const resolved = resolveArcGeometry(e.p1, e.p2, {
                    radius: l.curveRadius,
                    arcLength: l.arcLength,
                    center: l.circleCenter ? { x: l.circleCenter.x, y: l.circleCenter.y } : undefined,
                    curveDirection: l.curveDirection,
                    tangentBearing: l.tangentBearing,
                    chordBearing: l.chordBearing,
                    bearing: l.bearing,
                });
                if (!resolved) {
                    if (id === seedId || targetLine.polylineId) {
                        return { ok: false, count: 0, message: 'OFFSET: Could not resolve a curve in that linework.' };
                    }
                    continue;
                }
                curve = resolved;
            }
            segments.push({ id, from: e.p1, to: e.p2, curve });
            segmentSources.set(id, l);
        }

        const chain = orderConnectedChain(seedId, segments);
        if (!chain) return { ok: false, count: 0, message: 'OFFSET: Could not order the selected linework.' };

        const result = offsetOrderedChain(chain, offsetDistance, clickPoint, seedId);
        if (!result) return { ok: false, count: 0, message: 'OFFSET: Could not build the offset geometry.' };

        return {
            ok: true,
            count: result.segments.length,
            message: 'OFFSET: Offset ready.',
            segments: result.segments,
            source: segmentSources.get(seedId) ?? targetLine,
        };
    }, [offsetDistance, resolveLineEndpoints, isCircleLine, lines]);

    /** OFFSET: commit computed line/arc/circle segments onto the drawing. */
    const commitOffsetSegments = useCallback((offsetSegments: OffsetEntitySegment[], source: SurveyLine): number => {
        const stamp = Date.now();
        const newPolylineId = offsetSegments.length > 1 ? `OFFSET_${stamp}` : undefined;
        const hasBearingData = !!(source.bearing || source.distance);
        let created = 0;

        offsetSegments.forEach((seg, index) => {
            const fromRef = `OFFSET_${stamp}_${index}_A`;
            const toRef = `OFFSET_${stamp}_${index}_B`;

            if (seg.kind === 'circle') {
                onAddLine({
                    ...source,
                    id: undefined,
                    from: fromRef,
                    to: toRef,
                    fromPt: { x: seg.center.x, y: seg.center.y, z: 0 },
                    toPt: { x: seg.center.x, y: seg.center.y, z: 0 },
                    circleCenter: { x: seg.center.x, y: seg.center.y, z: source.circleCenter?.z ?? 0 },
                    isCurve: true,
                    isCircle: true,
                    curveRadius: seg.radius,
                    arcLength: 2 * Math.PI * seg.radius,
                    tangentBearing: undefined,
                    chordBearing: undefined,
                    chordDistance: undefined,
                    curveDirection: undefined,
                    polylineId: newPolylineId,
                });
                created++;
                return;
            }

            if (seg.kind === 'line') {
                const dx = seg.to.x - seg.from.x;
                const dy = seg.to.y - seg.from.y;
                const dist = Math.hypot(dx, dy);
                if (dist < 1e-6) return;
                let az = Math.atan2(dx, dy);
                if (az < 0) az += 2 * Math.PI;
                onAddLine({
                    ...source,
                    id: undefined,
                    from: fromRef,
                    to: toRef,
                    fromPt: { x: seg.from.x, y: seg.from.y, z: 0 },
                    toPt: { x: seg.to.x, y: seg.to.y, z: 0 },
                    isCurve: false,
                    isCircle: false,
                    circleCenter: undefined,
                    curveRadius: undefined,
                    arcLength: undefined,
                    tangentBearing: undefined,
                    chordBearing: undefined,
                    chordDistance: undefined,
                    curveDirection: undefined,
                    bearing: hasBearingData ? formatBearing(az) : undefined,
                    distance: hasBearingData ? dist.toFixed(2) : undefined,
                    polylineId: newPolylineId,
                });
                created++;
                return;
            }

            const arcLength = seg.radius * seg.centralAngle;
            const startAngle = Math.atan2(seg.from.y - seg.center.y, seg.from.x - seg.center.x);
            const radial = { x: Math.cos(startAngle), y: Math.sin(startAngle) };
            const tangent = seg.isLeftCurve
                ? { x: -radial.y, y: radial.x }
                : { x: radial.y, y: -radial.x };
            let tangentAz = Math.atan2(tangent.x, tangent.y);
            if (tangentAz < 0) tangentAz += 2 * Math.PI;
            let chordAz = Math.atan2(seg.to.x - seg.from.x, seg.to.y - seg.from.y);
            if (chordAz < 0) chordAz += 2 * Math.PI;
            const chordDist = Math.hypot(seg.to.x - seg.from.x, seg.to.y - seg.from.y);

            onAddLine({
                ...source,
                id: undefined,
                from: fromRef,
                to: toRef,
                fromPt: { x: seg.from.x, y: seg.from.y, z: 0 },
                toPt: { x: seg.to.x, y: seg.to.y, z: 0 },
                isCurve: true,
                isCircle: false,
                circleCenter: { x: seg.center.x, y: seg.center.y, z: 0 },
                curveRadius: seg.radius,
                arcLength,
                curveDirection: seg.isLeftCurve ? 'left' : 'right',
                tangentBearing: formatBearing(tangentAz),
                chordBearing: formatBearing(chordAz),
                chordDistance: chordDist,
                bearing: hasBearingData ? formatBearing(chordAz) : undefined,
                distance: hasBearingData ? chordDist.toFixed(2) : undefined,
                polylineId: newPolylineId,
            });
            created++;
        });

        return created;
    }, [onAddLine]);

    const applyOffsetAtPoint = useCallback((targetLine: SurveyLine, clickPoint: { x: number; y: number }): { ok: boolean; count: number; message: string } => {
        const built = buildOffsetGeometry(targetLine, clickPoint);
        if (!built.ok || !built.segments) {
            return { ok: false, count: 0, message: built.message };
        }
        const count = commitOffsetSegments(built.segments, built.source ?? targetLine);
        if (count === 0) {
            return { ok: false, count: 0, message: 'OFFSET: The offset produced no usable geometry.' };
        }
        return { ok: true, count, message: 'OFFSET: Offset created.' };
    }, [buildOffsetGeometry, commitOffsetSegments]);

    /** Cheap side key used to avoid rebuilding the preview on every mouse move. */
    const offsetSideKey = useCallback((target: SurveyLine, pt: { x: number; y: number }): string => {
        if (isCircleLine(target)) {
            const ends = resolveLineEndpoints(target);
            const center = target.circleCenter
                ? { x: target.circleCenter.x, y: target.circleCenter.y }
                : ends?.p1;
            if (!center || !target.curveRadius) return '?';
            return Math.hypot(pt.x - center.x, pt.y - center.y) >= target.curveRadius ? 'out' : 'in';
        }
        if (target.isCurve && target.curveRadius && target.arcLength) {
            const ends = resolveLineEndpoints(target);
            if (!ends) return '?';
            const arc = resolveArcGeometry(ends.p1, ends.p2, {
                radius: target.curveRadius,
                arcLength: target.arcLength,
                center: target.circleCenter ? { x: target.circleCenter.x, y: target.circleCenter.y } : undefined,
                curveDirection: target.curveDirection,
                tangentBearing: target.tangentBearing,
                chordBearing: target.chordBearing,
                bearing: target.bearing,
            });
            if (!arc) return '?';
            return Math.hypot(pt.x - arc.center.x, pt.y - arc.center.y) >= arc.radius ? 'out' : 'in';
        }
        const ends = resolveLineEndpoints(target);
        if (!ends) return '?';
        const cross = (ends.p2.x - ends.p1.x) * (pt.y - ends.p1.y) - (ends.p2.y - ends.p1.y) * (pt.x - ends.p1.x);
        return cross >= 0 ? 'L' : 'R';
    }, [isCircleLine, resolveLineEndpoints]);

    const clearOffsetPreview = useCallback(() => {
        offsetPreviewKeyRef.current = null;
        if (!offsetPreviewSegmentsRef.current) return;
        offsetPreviewSegmentsRef.current = null;
        requestAnimationFrame(() => drawRef.current?.());
    }, []);

    const updateOffsetPreview = useCallback((target: SurveyLine | null, clickPoint: { x: number; y: number }) => {
        if (!target || offsetDistance === null || offsetTargetId === null) {
            clearOffsetPreview();
            return;
        }
        const key = `${offsetTargetId}|${offsetDistance}|${offsetSideKey(target, clickPoint)}`;
        if (key === offsetPreviewKeyRef.current) return;
        offsetPreviewKeyRef.current = key;
        const built = buildOffsetGeometry(target, clickPoint);
        offsetPreviewSegmentsRef.current = built.ok && built.segments ? built.segments : null;
        requestAnimationFrame(() => drawRef.current?.());
    }, [offsetDistance, offsetTargetId, offsetSideKey, buildOffsetGeometry, clearOffsetPreview]);

    /** Rotated copies of the selection for the live ROTATE ghost at a trial angle. */
    const buildRotatePreview = useCallback((angleDegrees: number): OffsetEntitySegment[] => {
        if (!rotatePivot) return [];
        const radians = (angleDegrees * Math.PI) / 180;
        const cos = Math.cos(radians);
        const sin = Math.sin(radians);
        const px = rotatePivot.easting;
        const py = rotatePivot.northing;
        const rotatePoint = (p: { x: number; y: number }) => {
            const dx = p.x - px;
            const dy = p.y - py;
            return { x: px + dx * cos - dy * sin, y: py + dx * sin + dy * cos };
        };
        const preview: OffsetEntitySegment[] = [];
        for (const entity of getSelectedLineEntities()) {
            const line = entity.line;
            const circleLike = line.isCircle
                || (line.isCurve && line.curveRadius
                    && (!line.fromPt || (entity.ends.p1.x === entity.ends.p2.x && entity.ends.p1.y === entity.ends.p2.y))
                    && !line.tangentBearing && !line.chordBearing);
            if (circleLike) {
                const center = line.circleCenter ? { x: line.circleCenter.x, y: line.circleCenter.y } : entity.ends.p1;
                const radius = line.curveRadius ?? Math.hypot(entity.ends.p1.x - center.x, entity.ends.p1.y - center.y);
                if (radius > 0) preview.push({ kind: 'circle', center: rotatePoint(center), radius });
                continue;
            }
            if (line.isCurve && line.curveRadius && line.arcLength) {
                const arc = resolveArcGeometry(entity.ends.p1, entity.ends.p2, {
                    radius: line.curveRadius,
                    arcLength: line.arcLength,
                    center: line.circleCenter ? { x: line.circleCenter.x, y: line.circleCenter.y } : undefined,
                    curveDirection: line.curveDirection,
                    tangentBearing: line.tangentBearing,
                    chordBearing: line.chordBearing,
                    bearing: line.bearing,
                });
                if (arc) {
                    preview.push({
                        kind: 'arc',
                        from: rotatePoint(entity.ends.p1),
                        to: rotatePoint(entity.ends.p2),
                        center: rotatePoint(arc.center),
                        radius: arc.radius,
                        centralAngle: arc.centralAngle,
                        isLeftCurve: arc.isLeftCurve,
                    });
                    continue;
                }
            }
            preview.push({ kind: 'line', from: rotatePoint(entity.ends.p1), to: rotatePoint(entity.ends.p2) });
        }
        return preview;
    }, [rotatePivot, getSelectedLineEntities]);

    const clearRotatePreview = useCallback(() => {
        rotatePreviewAngleRef.current = null;
        previewKeyRef.current = null;
        scaleGhostActiveRef.current = false;
        if (!rotatePreviewSegmentsRef.current) return;
        rotatePreviewSegmentsRef.current = null;
        requestAnimationFrame(() => drawRef.current?.());
    }, []);

    const updateRotatePreview = useCallback((angleDegrees: number) => {
        // Ignore sub-0.05° jitter so a normal mouse move redraws at most a few times.
        if (rotatePreviewAngleRef.current !== null && Math.abs(rotatePreviewAngleRef.current - angleDegrees) < 0.05) return;
        rotatePreviewAngleRef.current = angleDegrees;
        previewKeyRef.current = null;
        const segments = buildRotatePreview(angleDegrees);
        rotatePreviewSegmentsRef.current = segments.length > 0 ? segments : null;
        requestAnimationFrame(() => drawRef.current?.());
    }, [buildRotatePreview]);

    /** Generic ghost builder for MOVE / COPY / MIRROR previews. */
    const buildSelectionPreview = useCallback((transformPoint: (p: { x: number; y: number }) => { x: number; y: number }): OffsetEntitySegment[] => {
        const preview: OffsetEntitySegment[] = [];
        for (const entity of getSelectedLineEntities()) {
            const line = entity.line;
            const circleLike = line.isCircle
                || (line.isCurve && line.curveRadius
                    && (!line.fromPt || (entity.ends.p1.x === entity.ends.p2.x && entity.ends.p1.y === entity.ends.p2.y))
                    && !line.tangentBearing && !line.chordBearing);
            if (circleLike) {
                const center = line.circleCenter ? { x: line.circleCenter.x, y: line.circleCenter.y } : entity.ends.p1;
                const radius = line.curveRadius ?? Math.hypot(entity.ends.p1.x - center.x, entity.ends.p1.y - center.y);
                if (radius > 0) preview.push({ kind: 'circle', center: transformPoint(center), radius });
                continue;
            }
            if (line.isCurve && line.curveRadius && line.arcLength) {
                const arc = resolveArcGeometry(entity.ends.p1, entity.ends.p2, {
                    radius: line.curveRadius,
                    arcLength: line.arcLength,
                    center: line.circleCenter ? { x: line.circleCenter.x, y: line.circleCenter.y } : undefined,
                    curveDirection: line.curveDirection,
                    tangentBearing: line.tangentBearing,
                    chordBearing: line.chordBearing,
                    bearing: line.bearing,
                });
                if (arc) {
                    preview.push({
                        kind: 'arc',
                        from: transformPoint(entity.ends.p1),
                        to: transformPoint(entity.ends.p2),
                        center: transformPoint(arc.center),
                        radius: arc.radius,
                        centralAngle: arc.centralAngle,
                        isLeftCurve: arc.isLeftCurve,
                    });
                    continue;
                }
            }
            preview.push({ kind: 'line', from: transformPoint(entity.ends.p1), to: transformPoint(entity.ends.p2) });
        }
      return preview;
  }, [getSelectedLineEntities]);

  /**
   * Live ARRAY preview. Built from the values owned by the X/Y manipulator so
   * every drag/typed change shows the resulting copies before Apply.
   */
  useEffect(() => {
    if (activeCommandPrompt?.id !== 'array') {
      if (!arrayPreviewRef.current) return;
      arrayPreviewRef.current = null;
      requestAnimationFrame(() => drawRef.current?.());
      return;
    }

    const read = (key: string, fallback: number): number => {
      const raw = commandPromptValues[key];
      if (raw === undefined || raw.trim() === '') return fallback;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const rows = Math.max(1, Math.round(read('rows', 1)));
    const columns = Math.max(1, Math.round(read('columns', 1)));
    const rowSpacing = read('rowSpacing', 0);
    const columnSpacing = read('columnSpacing', 0);
    const angle = read('angle', 0);

    const unit = settings.linearUnits || 'usSurveyFoot';
    const rowGap = convertLinearUnits(rowSpacing, unit, 'foot');
    const columnGap = convertLinearUnits(columnSpacing, unit, 'foot');
    // Preview every array the command will accept. Large arrays are drawn with
    // a sampled row/column grid instead of dropping the ghost entirely.
    const shouldPreview = rows * columns >= 2
      && rows * columns <= MAX_RECTANGULAR_ARRAY_ITEMS
      && (Math.abs(rowGap) > 1e-12 || Math.abs(columnGap) > 1e-12);
    const direction = (angle * Math.PI) / 180;
    const baseSegments = shouldPreview ? buildSelectionPreview(point => point) : [];
    arrayPreviewRef.current = baseSegments.length > 0
      ? {
          baseSegments,
          rows,
          columns,
          rowGap,
          columnGap,
          cos: Math.cos(direction),
          sin: Math.sin(direction),
        }
      : null;
    requestAnimationFrame(() => drawRef.current?.());
  }, [activeCommandPrompt, buildSelectionPreview, commandPromptValues, selectedLineIds, settings.linearUnits]);

  /**
   * Live POLARARRAY preview. The ring dot, count, fill angle and center are
   * owned by the manipulator, so every drag or typed change updates the ghost.
   */
  useEffect(() => {
    if (activeCommandPrompt?.id !== 'polar_array') {
      if (!polarPreviewRef.current) return;
      polarPreviewRef.current = null;
      requestAnimationFrame(() => drawRef.current?.());
      return;
    }

    const read = (key: string, fallback: number): number => {
      const raw = commandPromptValues[key];
      if (raw === undefined || raw.trim() === '') return fallback;
      const parsed = Number(raw);
      return Number.isFinite(parsed) ? parsed : fallback;
    };
    const count = Math.max(2, Math.round(read('count', 6)));
    const fillAngle = Math.min(360, Math.max(1, read('fillAngle', 360)));
    const centerToken = (commandPromptValues.center ?? '').trim();
    const parsedCenter = centerToken ? parseCoordinateToken(centerToken) : null;
    const center = parsedCenter
      ? { x: parsedCenter.easting, y: parsedCenter.northing }
      : getSelectionBoundingCenter();
    const baseSegments = center ? buildSelectionPreview(point => point) : [];
    polarPreviewRef.current = baseSegments.length > 0
      ? { baseSegments, center, count, fillAngle }
      : null;
    requestAnimationFrame(() => drawRef.current?.());
  }, [activeCommandPrompt, buildSelectionPreview, commandPromptValues, getSelectionBoundingCenter, selectedLineIds]);

  const showTransformPreview = useCallback((key: string, transformPoint: (p: { x: number; y: number }) => { x: number; y: number }) => {
        if (previewKeyRef.current === key) return;
        previewKeyRef.current = key;
        scaleGhostActiveRef.current = false;
        const segments = buildSelectionPreview(transformPoint);
        rotatePreviewSegmentsRef.current = segments.length > 0 ? segments : null;
        requestAnimationFrame(() => drawRef.current?.());
    }, [buildSelectionPreview]);

    /** Emerald ghost of the selection scaled about the current reference point. */
    const showScalePreview = useCallback((factor: number) => {
        const base = scaleBasePoint;
        if (!base || !Number.isFinite(factor) || factor <= 0) return;
        const key = `SC|${base.easting.toFixed(3)}|${base.northing.toFixed(3)}|${factor.toFixed(4)}`;
        if (previewKeyRef.current === key) return;
        previewKeyRef.current = key;
        scaleGhostActiveRef.current = true;
        const segments = buildSelectionPreview((point) => ({
            x: base.easting + (point.x - base.easting) * factor,
            y: base.northing + (point.y - base.northing) * factor,
        }));
        rotatePreviewSegmentsRef.current = segments.length > 0 ? segments : null;
        requestAnimationFrame(() => drawRef.current?.());
    }, [scaleBasePoint, buildSelectionPreview]);

    /** Stop ROTATE / MIRROR / MOVE and drop any ghost. */
    const stopInteractiveTransforms = useCallback(() => {
        setIsRotatingSelection(false);
        setRotatePivot(null);
        setTypedRotateAngle('');
        setIsMirroringSelection(false);
        setMirrorAxisStart(null);
        setIsPickingMirrorLine(false);
        setIsMovingSelection(false);
        setMoveBasePoint(null);
        setIsScalingSelection(false);
        setScaleBasePoint(null);
        setTypedScaleFactor('');
        scaleReferenceRadiusRef.current = null;
        clearRotatePreview();
    }, [clearRotatePreview]);

    const resolveOsnapAtScreen = useCallback((
        screenX: number,
        screenY: number,
        modes: Set<OsnapMode>,
        thresholdPx: number,
        anchorPoint?: { easting: number; northing: number } | null,
    ): OsnapResult | null => {
        // The global SNAP switch disables running snaps. An inline override is
        // still a deliberate one-pick snap, so it remains available while off.
        if (!isRunningOsnapsEnabled && !inlineOsnapOverride) return null;
        if (modes.size === 0) return null;

        let best: OsnapResult | null = null;
        let bestDistSq = thresholdPx * thresholdPx;
        const nearSegments: Array<{ x1: number; y1: number; x2: number; y2: number }> = [];

        const tryCandidate = (mode: OsnapMode, easting: number, northing: number, sourcePoint?: SurveyPoint) => {
            const ptScreen = worldToScreen(easting, northing);
            const dSq = (screenX - ptScreen.x) ** 2 + (screenY - ptScreen.y) ** 2;
            if (dSq <= bestDistSq) {
                bestDistSq = dSq;
                best = { easting, northing, mode, sourcePoint };
            }
        };

        if (modes.has('endpoint')) {
            for (const p of points) {
                if (p.hidden) continue;
                tryCandidate('endpoint', p.easting, p.northing, p);
            }
        }

        const mouseWorld = screenToWorld(screenX, screenY);
        const worldMouseE = mouseWorld.easting;
        const worldMouseN = mouseWorld.northing;
        const gatherIntersections = modes.has('intersection');

        for (const line of lines) {
            if (line.hidden) continue;
            const from = pointMap.get(line.from);
            const to = pointMap.get(line.to);
            const x1 = from ? from.easting : line.fromPt?.x;
            const y1 = from ? from.northing : line.fromPt?.y;
            const x2 = to ? to.easting : line.toPt?.x;
            const y2 = to ? to.northing : line.toPt?.y;

            // Handle Full Circles
            const isCircleEntity = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (x1 === x2 && y1 === y2)) && !line.tangentBearing && !line.chordBearing);
            if (isCircleEntity && line.curveRadius) {
                const cX = line.circleCenter ? line.circleCenter.x : (x1 ?? 0);
                const cY = line.circleCenter ? line.circleCenter.y : (y1 ?? 0);
                const radius = line.curveRadius;

                if (modes.has('center')) {
                    tryCandidate('center', cX, cY);
                }

                if (modes.has('nearest')) {
                    const dE = worldMouseE - cX;
                    const dN = worldMouseN - cY;
                    const dist = Math.hypot(dE, dN);
                    if (dist > 1e-9) {
                        const nearE = cX + radius * (dE / dist);
                        const nearN = cY + radius * (dN / dist);
                        tryCandidate('nearest', nearE, nearN);
                    }
                }

                if (modes.has('endpoint') || modes.has('midpoint')) {
                    // Circle quadrants (0°, 90°, 180°, 270°)
                    tryCandidate('endpoint', cX + radius, cY);
                    tryCandidate('endpoint', cX - radius, cY);
                    tryCandidate('endpoint', cX, cY + radius);
                    tryCandidate('endpoint', cX, cY - radius);
                }

                if (modes.has('perpendicular') && anchorPoint) {
                    const adE = anchorPoint.easting - cX;
                    const adN = anchorPoint.northing - cY;
                    const adist = Math.hypot(adE, adN);
                    if (adist > 1e-9) {
                        tryCandidate('perpendicular', cX + radius * (adE / adist), cY + radius * (adN / adist));
                        tryCandidate('perpendicular', cX - radius * (adE / adist), cY - radius * (adN / adist));
                    }
                }

                if (modes.has('tangent')) {
                    const refPt = anchorPoint || { easting: worldMouseE, northing: worldMouseN };
                    const adE = refPt.easting - cX;
                    const adN = refPt.northing - cY;
                    const distToCenter = Math.hypot(adE, adN);
                    if (distToCenter >= radius - 1e-9) {
                        const alpha = Math.atan2(adN, adE);
                        const gamma = Math.acos(Math.min(1, Math.max(-1, radius / Math.max(radius, distToCenter))));
                        const ang1 = alpha - gamma;
                        const ang2 = alpha + gamma;
                        tryCandidate('tangent', cX + radius * Math.cos(ang1), cY + radius * Math.sin(ang1));
                        tryCandidate('tangent', cX + radius * Math.cos(ang2), cY + radius * Math.sin(ang2));
                    }
                }
                continue;
            }

            if (x1 === undefined || y1 === undefined || x2 === undefined || y2 === undefined) continue;

            // Handle Arcs / Curves
            if (line.isCurve && line.curveRadius && line.arcLength) {
                const radius = line.curveRadius;
                const centralAngle = line.arcLength / radius;
                const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };

                let isLeftCurve = line.curveDirection === 'left';
                if (line.curveDirection !== 'left' && line.curveDirection !== 'right') {
                    const tempTangent = line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null;
                    if (tempTangent !== null) {
                        const dirBearingStr = line.chordBearing || line.bearing;
                        if (dirBearingStr) {
                            const dirRad = parseBearingToRadians(dirBearingStr);
                            if (dirRad !== null) {
                                const halfDelta = centralAngle / 2;
                                const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                                const expectedLeftChord  = normalizeAng(tempTangent - halfDelta);
                                const dirNorm = normalizeAng(dirRad);
                                const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                                const diffLeft  = Math.min(Math.abs(dirNorm - expectedLeftChord),  2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                                if (diffLeft < diffRight) isLeftCurve = true;
                            }
                        }
                    }
                }

                let chordRadArc: number | null = null;
                if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
                    const dE = x2 - x1;
                    const dN = y2 - y1;
                    if (Math.hypot(dE, dN) > 1e-4) {
                        chordRadArc = normalizeAng(Math.atan2(dE, dN));
                    }
                }
                if (chordRadArc === null) {
                    const chordStr = line.chordBearing || line.bearing;
                    if (chordStr) {
                        chordRadArc = parseBearingToRadians(chordStr);
                    }
                }

                const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                const tangentAngle: number | null = chordRadArc !== null
                    ? chordRadArc - signedDeltaArc / 2
                    : (line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null);

                if (tangentAngle !== null) {
                    const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                    const centerOffsetAngle = isLeftCurve
                        ? canvasTangentAngle + Math.PI / 2
                        : canvasTangentAngle - Math.PI / 2;
                    const cX = x1 + radius * Math.cos(centerOffsetAngle);
                    const cY = y1 + radius * Math.sin(centerOffsetAngle);

                    if (modes.has('center')) {
                        tryCandidate('center', cX, cY);
                    }

                    if (modes.has('endpoint')) {
                        tryCandidate('endpoint', x1, y1, from && !from.hidden ? from : undefined);
                        tryCandidate('endpoint', x2, y2, to && !to.hidden ? to : undefined);
                    }

                    const startAngle = Math.atan2(y1 - cY, x1 - cX);
                    const midAngle = isLeftCurve ? startAngle + centralAngle / 2 : startAngle - centralAngle / 2;

                    if (modes.has('midpoint')) {
                        const midE = cX + radius * Math.cos(midAngle);
                        const midN = cY + radius * Math.sin(midAngle);
                        tryCandidate('midpoint', midE, midN);
                    }

                    if (modes.has('nearest')) {
                        const mouseAngle = Math.atan2(worldMouseN - cY, worldMouseE - cX);
                        let inArc = false;
                        if (isLeftCurve) {
                            let sweep = (mouseAngle - startAngle) % (2 * Math.PI);
                            if (sweep < 0) sweep += 2 * Math.PI;
                            if (sweep <= centralAngle) inArc = true;
                        } else {
                            let sweep = (startAngle - mouseAngle) % (2 * Math.PI);
                            if (sweep < 0) sweep += 2 * Math.PI;
                            if (sweep <= centralAngle) inArc = true;
                        }

                        if (inArc) {
                            const nearE = cX + radius * Math.cos(mouseAngle);
                            const nearN = cY + radius * Math.sin(mouseAngle);
                            tryCandidate('nearest', nearE, nearN);
                        } else {
                            const d1Sq = (worldMouseE - x1) ** 2 + (worldMouseN - y1) ** 2;
                            const d2Sq = (worldMouseE - x2) ** 2 + (worldMouseN - y2) ** 2;
                            if (d1Sq < d2Sq) {
                                tryCandidate('nearest', x1, y1);
                            } else {
                                tryCandidate('nearest', x2, y2);
                            }
                        }
                    }

                    if (modes.has('perpendicular') && anchorPoint) {
                        const adE = anchorPoint.easting - cX;
                        const adN = anchorPoint.northing - cY;
                        const adist = Math.hypot(adE, adN);
                        if (adist > 1e-9) {
                            const pAng1 = Math.atan2(adN, adE);
                            const pAng2 = pAng1 + Math.PI;
                            for (const ang of [pAng1, pAng2]) {
                                let inArc = false;
                                if (isLeftCurve) {
                                    let sweep = (ang - startAngle) % (2 * Math.PI);
                                    if (sweep < 0) sweep += 2 * Math.PI;
                                    if (sweep <= centralAngle) inArc = true;
                                } else {
                                    let sweep = (startAngle - ang) % (2 * Math.PI);
                                    if (sweep < 0) sweep += 2 * Math.PI;
                                    if (sweep <= centralAngle) inArc = true;
                                }
                                if (inArc) {
                                    tryCandidate('perpendicular', cX + radius * Math.cos(ang), cY + radius * Math.sin(ang));
                                }
                            }
                        }
                    }

                    if (modes.has('tangent')) {
                        const refPt = anchorPoint || { easting: worldMouseE, northing: worldMouseN };
                        const adE = refPt.easting - cX;
                        const adN = refPt.northing - cY;
                        const distToCenter = Math.hypot(adE, adN);
                        if (distToCenter >= radius - 1e-9) {
                            const alpha = Math.atan2(adN, adE);
                            const gamma = Math.acos(Math.min(1, Math.max(-1, radius / Math.max(radius, distToCenter))));
                            const ang1 = alpha - gamma;
                            const ang2 = alpha + gamma;
                            for (const ang of [ang1, ang2]) {
                                let inArc = false;
                                if (isLeftCurve) {
                                    let sweep = (ang - startAngle) % (2 * Math.PI);
                                    if (sweep < 0) sweep += 2 * Math.PI;
                                    if (sweep <= centralAngle + 1e-5) inArc = true;
                                } else {
                                    let sweep = (startAngle - ang) % (2 * Math.PI);
                                    if (sweep < 0) sweep += 2 * Math.PI;
                                    if (sweep <= centralAngle + 1e-5) inArc = true;
                                }
                                if (inArc) {
                                    tryCandidate('tangent', cX + radius * Math.cos(ang), cY + radius * Math.sin(ang));
                                }
                            }
                        }
                    }
                }
                continue;
            }

            const dx = x2 - x1;
            const dy = y2 - y1;
            const lenSq = dx * dx + dy * dy;
            if (lenSq < 1e-12) continue;

            if (modes.has('endpoint')) {
                tryCandidate('endpoint', x1, y1, from && !from.hidden ? from : undefined);
                tryCandidate('endpoint', x2, y2, to && !to.hidden ? to : undefined);
            }

            if (modes.has('midpoint')) {
                tryCandidate('midpoint', (x1 + x2) / 2, (y1 + y2) / 2);
            }

            if (modes.has('nearest')) {
                let t = ((worldMouseE - x1) * dx + (worldMouseN - y1) * dy) / lenSq;
                t = Math.max(0, Math.min(1, t));
                tryCandidate('nearest', x1 + dx * t, y1 + dy * t);
            }

            if (modes.has('perpendicular') && anchorPoint) {
                let tPerp = ((anchorPoint.easting - x1) * dx + (anchorPoint.northing - y1) * dy) / lenSq;
                tPerp = Math.max(0, Math.min(1, tPerp));
                tryCandidate('perpendicular', x1 + dx * tPerp, y1 + dy * tPerp);
            }

            if (gatherIntersections) {
                let tMouse = ((worldMouseE - x1) * dx + (worldMouseN - y1) * dy) / lenSq;
                tMouse = Math.max(0, Math.min(1, tMouse));
                const cx = x1 + dx * tMouse;
                const cy = y1 + dy * tMouse;
                const worldDist = Math.hypot(worldMouseE - cx, worldMouseN - cy);
                if (worldDist <= (thresholdPx * 3) / transform.scale) {
                    nearSegments.push({ x1, y1, x2, y2 });
                }
            }
        }

        if (gatherIntersections && nearSegments.length > 1) {
            for (let i = 0; i < nearSegments.length; i++) {
                const a = nearSegments[i];
                const ax = a.x2 - a.x1;
                const ay = a.y2 - a.y1;
                for (let j = i + 1; j < nearSegments.length; j++) {
                    const b = nearSegments[j];
                    const bx = b.x2 - b.x1;
                    const by = b.y2 - b.y1;
                    const denom = ax * by - ay * bx;
                    if (Math.abs(denom) < 1e-12) continue;

                    const rx = b.x1 - a.x1;
                    const ry = b.y1 - a.y1;
                    const ta = (rx * by - ry * bx) / denom;
                    const tb = (rx * ay - ry * ax) / denom;
                    if (ta < 0 || ta > 1 || tb < 0 || tb > 1) continue;

                    tryCandidate('intersection', a.x1 + ta * ax, a.y1 + ta * ay);
                }
            }
        }

        if (boundaryFiles && boundaryFiles.length > 0) {
            for (const bf of boundaryFiles) {
                if (bf.hidden) continue;
                for (const call of bf.calls) {
                    const from = pointMap.get(call.from);
                    const to = pointMap.get(call.to);
                    if (!from || !to) continue;
                    const x1 = from.easting;
                    const y1 = from.northing;
                    const x2 = to.easting;
                    const y2 = to.northing;

                    if (call.isCurve && call.curveRadius && call.arcLength) {
                        const radius = call.curveRadius;
                        const centralAngle = call.arcLength / radius;
                        const isLeftCurve = call.curveDirection === 'left';
                        let chordRadArc: number | null = null;
                        const chordStr = call.chordBearing || call.bearing;
                        if (chordStr) {
                            chordRadArc = parseBearingToRadians(chordStr);
                        } else {
                            const dE = x2 - x1;
                            const dN = y2 - y1;
                            if (Math.hypot(dE, dN) > 1e-9) {
                                let az = Math.atan2(dE, dN);
                                if (az < 0) az += 2 * Math.PI;
                                chordRadArc = az;
                            }
                        }

                        const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                        const tangentAngle: number | null = chordRadArc !== null
                            ? chordRadArc - signedDeltaArc / 2
                            : (call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : null);

                        if (tangentAngle !== null) {
                            const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                            const centerOffsetAngle = isLeftCurve
                                ? canvasTangentAngle + Math.PI / 2
                                : canvasTangentAngle - Math.PI / 2;
                            const cX = x1 + radius * Math.cos(centerOffsetAngle);
                            const cY = y1 + radius * Math.sin(centerOffsetAngle);

                            if (modes.has('center')) {
                                tryCandidate('center', cX, cY);
                            }
                            if (modes.has('endpoint')) {
                                tryCandidate('endpoint', x1, y1, from);
                                tryCandidate('endpoint', x2, y2, to);
                            }
                            const startAngle = Math.atan2(y1 - cY, x1 - cX);
                            if (modes.has('midpoint')) {
                                const midAngle = isLeftCurve ? startAngle + centralAngle / 2 : startAngle - centralAngle / 2;
                                tryCandidate('midpoint', cX + radius * Math.cos(midAngle), cY + radius * Math.sin(midAngle));
                            }
                            if (modes.has('nearest')) {
                                const mouseAngle = Math.atan2(worldMouseN - cY, worldMouseE - cX);
                                let inArc = false;
                                if (isLeftCurve) {
                                    let sweep = (mouseAngle - startAngle) % (2 * Math.PI);
                                    if (sweep < 0) sweep += 2 * Math.PI;
                                    if (sweep <= centralAngle) inArc = true;
                                } else {
                                    let sweep = (startAngle - mouseAngle) % (2 * Math.PI);
                                    if (sweep < 0) sweep += 2 * Math.PI;
                                    if (sweep <= centralAngle) inArc = true;
                                }
                                if (inArc) {
                                    tryCandidate('nearest', cX + radius * Math.cos(mouseAngle), cY + radius * Math.sin(mouseAngle));
                                } else {
                                    const d1Sq = (worldMouseE - x1) ** 2 + (worldMouseN - y1) ** 2;
                                    const d2Sq = (worldMouseE - x2) ** 2 + (worldMouseN - y2) ** 2;
                                    tryCandidate('nearest', d1Sq < d2Sq ? x1 : x2, d1Sq < d2Sq ? y1 : y2);
                                }
                            }
                            if (modes.has('perpendicular') && anchorPoint) {
                                const adE = anchorPoint.easting - cX;
                                const adN = anchorPoint.northing - cY;
                                const adist = Math.hypot(adE, adN);
                                if (adist > 1e-9) {
                                    const pAng1 = Math.atan2(adN, adE);
                                    const pAng2 = pAng1 + Math.PI;
                                    for (const ang of [pAng1, pAng2]) {
                                        let inArc = false;
                                        if (isLeftCurve) {
                                            let sweep = (ang - startAngle) % (2 * Math.PI);
                                            if (sweep < 0) sweep += 2 * Math.PI;
                                            if (sweep <= centralAngle) inArc = true;
                                        } else {
                                            let sweep = (startAngle - ang) % (2 * Math.PI);
                                            if (sweep < 0) sweep += 2 * Math.PI;
                                            if (sweep <= centralAngle) inArc = true;
                                        }
                                        if (inArc) {
                                            tryCandidate('perpendicular', cX + radius * Math.cos(ang), cY + radius * Math.sin(ang));
                                        }
                                    }
                                }
                            }
                            if (modes.has('tangent')) {
                                const refPt = anchorPoint || { easting: worldMouseE, northing: worldMouseN };
                                const adE = refPt.easting - cX;
                                const adN = refPt.northing - cY;
                                const distToCenter = Math.hypot(adE, adN);
                                if (distToCenter >= radius - 1e-9) {
                                    const alpha = Math.atan2(adN, adE);
                                    const gamma = Math.acos(Math.min(1, Math.max(-1, radius / Math.max(radius, distToCenter))));
                                    const ang1 = alpha - gamma;
                                    const ang2 = alpha + gamma;
                                    for (const ang of [ang1, ang2]) {
                                        let inArc = false;
                                        if (isLeftCurve) {
                                            let sweep = (ang - startAngle) % (2 * Math.PI);
                                            if (sweep < 0) sweep += 2 * Math.PI;
                                            if (sweep <= centralAngle + 1e-5) inArc = true;
                                        } else {
                                            let sweep = (startAngle - ang) % (2 * Math.PI);
                                            if (sweep < 0) sweep += 2 * Math.PI;
                                            if (sweep <= centralAngle + 1e-5) inArc = true;
                                        }
                                        if (inArc) {
                                            tryCandidate('tangent', cX + radius * Math.cos(ang), cY + radius * Math.sin(ang));
                                        }
                                    }
                                }
                            }
                        }
                        continue;
                    }

                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    const lenSq = dx * dx + dy * dy;
                    if (lenSq < 1e-12) continue;

                    if (modes.has('endpoint')) {
                        tryCandidate('endpoint', x1, y1, from);
                        tryCandidate('endpoint', x2, y2, to);
                    }
                    if (modes.has('midpoint')) {
                        tryCandidate('midpoint', (x1 + x2) / 2, (y1 + y2) / 2);
                    }
                    if (modes.has('nearest')) {
                        let t = ((worldMouseE - x1) * dx + (worldMouseN - y1) * dy) / lenSq;
                        t = Math.max(0, Math.min(1, t));
                        tryCandidate('nearest', x1 + dx * t, y1 + dy * t);
                    }
                }
            }
        }

        return best;
    }, [lines, pointMap, points, transform, worldToScreen, screenToWorld, boundaryFiles, isRunningOsnapsEnabled, inlineOsnapOverride]);

    const symbolMatchers = useMemo(() => buildSymbolMatchers(customSymbols), [customSymbols]);

    // CAD Manager standards: when a point's field code (or a surveyor alias
    // pattern such as "CM*") resolves to a master code whose Symbol column
    // holds a real SymbolDefinition, render that SVG. Wrapped in a synthetic
    // CustomSymbol so the existing draw pipeline can use it unchanged.
    const cadManager = useCadManager();
    const cadStandard = cadManager.state.standard;
    const cadSymbolCache = useRef(new Map<string, CustomSymbol | null>());
    useEffect(() => { cadSymbolCache.current.clear(); }, [cadStandard]);
    // Toolbar-launched CAD Standards spreadsheet editor (full-screen modal).
    const [isCadStandardsEditorOpen, setIsCadStandardsEditorOpen] = useState(false);
    // Per-description symbol exclusion: when the user clicks a point in this
    // mode, the point's description (normalized) is added/removed from the set.
    // All points sharing that description have their symbol suppressed, so
    // disassociating one disassociates the entire group. Persisted to
    // localStorage so exclusions survive loading a new point set.
    const SYMBOL_EXCLUSION_STORAGE_KEY = 'landsurv.excludedSymbolDescriptions.v1';
    const [symbolExclusionMode, setSymbolExclusionMode] = useState(false);
    const [excludedSymbolDescriptions, setExcludedSymbolDescriptions] = useState<Set<string>>(() => {
        try {
            const raw = localStorage.getItem(SYMBOL_EXCLUSION_STORAGE_KEY);
            if (!raw) return new Set();
            const arr = JSON.parse(raw);
            return Array.isArray(arr) ? new Set(arr.filter((s): s is string => typeof s === 'string')) : new Set();
        } catch {
            return new Set();
        }
    });
    useEffect(() => {
        try {
            localStorage.setItem(SYMBOL_EXCLUSION_STORAGE_KEY, JSON.stringify([...excludedSymbolDescriptions]));
        } catch { /* quota / privacy mode — ignore */ }
    }, [excludedSymbolDescriptions]);
    // Hover tooltip shown while in symbol-exclusion mode: explains why the
    // hovered point's description currently resolves to its symbol (or not).
    const [exclusionHover, setExclusionHover] = useState<{
        clientX: number;
        clientY: number;
        description: string;
        symbolName: string | null;
        reason: string;
        excluded: boolean;
    } | null>(null);
    useEffect(() => { if (!symbolExclusionMode) setExclusionHover(null); }, [symbolExclusionMode]);

    const getCadManagerSymbol = useCallback((description: string): CustomSymbol | undefined => {
        if (!description || !cadStandard) return undefined;
        const cached = cadSymbolCache.current.get(description);
        if (cached !== undefined) return cached ?? undefined;

        const res = cadManager.resolveCode(description);
        if (res.source === 'unknown') {
            cadSymbolCache.current.set(description, null);
            return undefined;
        }
        const codeDef = cadStandard.codes.find(c => c.code.toUpperCase() === res.master.toUpperCase());
        const sym = codeDef?.symbol;
        if (!sym || typeof sym !== 'object' || !sym.svgPath) {
            cadSymbolCache.current.set(description, null);
            return undefined;
        }
        const synthetic: CustomSymbol = {
            id: `cad-${res.master}`,
            name: sym.name || res.master,
            description: sym.description || codeDef?.description || '',
            associatedTerms: [],
            svgPath: sym.svgPath,
            fillPath: sym.fillPath,
            viewBox: sym.viewBox || '0 0 24 24',
            scale: 1,
        };
        cadSymbolCache.current.set(description, synthetic);
        return synthetic;
    }, [cadManager, cadStandard]);

    // Built-in survey symbol library (tree, manhole, hydrant, etc.) — final
    // fallback when neither the custom-symbol library nor the CAD Manager
    // standard has an entry for this description. Without this, codes like
    // "TREE 12" rendered as the generic default dot instead of an actual
    // tree glyph. Cached per description since findMatchingSymbol re-scores
    // the whole library on every call.
    const builtinSymbolCache = useRef(new Map<string, CustomSymbol | null>());
    // Gate: a built-in library symbol only renders when the user has opted its
    // id into enabledBuiltinSymbolIds (or '*' for all). Default OFF. This is
    // the deterministic, request-scoped visibility model — e.g. "draw tree
    // symbols" enables just the tree ids; everything else stays the default
    // marker until asked for.
    const isBuiltinSymbolEnabled = useCallback((libId: string): boolean => {
        if (!enabledBuiltinSymbolIds || enabledBuiltinSymbolIds.size === 0) return false;
        return enabledBuiltinSymbolIds.has('*') || enabledBuiltinSymbolIds.has(libId);
    }, [enabledBuiltinSymbolIds]);
    const getBuiltinLibrarySymbol = useCallback((description: string): CustomSymbol | undefined => {
        if (!description) return undefined;
        let synthetic = builtinSymbolCache.current.get(description);
        if (synthetic === undefined) {
            const libMatch = findMatchingSymbol(description, description);
            if (!libMatch) {
                builtinSymbolCache.current.set(description, null);
                return undefined;
            }
            const def = libraryToSymbolDefinition(libMatch, description);
            synthetic = {
                id: `builtin-${libMatch.id}`,
                name: def.name,
                description: def.description || '',
                associatedTerms: [],
                svgPath: def.svgPath!,
                fillPath: def.fillPath,
                viewBox: def.viewBox || '0 0 24 24',
                scale: 1,
            };
            builtinSymbolCache.current.set(description, synthetic);
        }
        if (!synthetic) return undefined;
        // Apply the opt-in gate (kept out of the cache so toggling visibility
        // takes effect without invalidating the match cache).
        const libId = synthetic.id.replace(/^builtin-/, '');
        if (!isBuiltinSymbolEnabled(libId)) return undefined;
        return synthetic;
    }, [isBuiltinSymbolEnabled]);

    const getSymbolForPoint = useCallback((point: SurveyPoint): CustomSymbol | undefined => {
        if (settings.showPointSymbols !== true) return undefined;
        if (point.description && excludedSymbolDescriptions.has(point.description.trim().toUpperCase())) return undefined;
        if (point.description) {
            const res = resolveSymbol(point.description, customSymbols, symbolMatchers);
            if (res.symbol) {
                // Skip hidden symbols — point will fall through to a plain marker.
                if (res.symbol.hidden) return undefined;
                return res.symbol;
            }
            // Fall back to CAD Manager standard's per-code Symbol column,
            // matched via master code + surveyor aliases (wildcards OK).
            const cadSym = getCadManagerSymbol(point.description);
            if (cadSym) return cadSym;
            // Final fallback: the built-in survey symbol library (tree,
            // manhole, hydrant, etc.) matched by code/description keywords.
            const builtinSym = getBuiltinLibrarySymbol(point.description);
            if (builtinSym) return builtinSym;
        }
        return undefined;
    }, [settings.showPointSymbols, symbolMatchers, customSymbols, getCadManagerSymbol, getBuiltinLibrarySymbol, excludedSymbolDescriptions]);

    // Produce a human-readable explanation of *why* a point's description
    // currently maps (or doesn't map) to a symbol. Used by the exclusion-mode
    // hover tooltip so the user can see which rule they're about to disable.
    const explainSymbolAssociation = useCallback((description: string): { symbolName: string | null; reason: string } => {
        const desc = (description || '').trim();
        if (!desc) return { symbolName: null, reason: 'Point has no description.' };
        if (settings.showPointSymbols !== true) {
            return { symbolName: null, reason: 'Point symbols are turned off in Settings — enable Point Symbols to render matching symbols.' };
        }
        const res = resolveSymbol(desc, customSymbols, symbolMatchers);
        if (res.symbol && res.source !== 'none') {
            if (res.source === 'name-exact') {
                return { symbolName: res.symbol.name, reason: `Custom symbol "${res.symbol.name}" — exact name match on "${desc}".` };
            }
            if (res.source === 'fuzzy-term') {
                return { symbolName: res.symbol.name, reason: `Custom symbol "${res.symbol.name}" — matched associated term /${res.matchedTerm}/.` };
            }
            return { symbolName: res.symbol.name, reason: `Custom symbol "${res.symbol.name}" — ${res.source}.` };
        }
        const cad = cadManager.resolveCode(desc);
        if (cad.source !== 'unknown') {
            const sym = cadStandard?.codes.find(c => c.code === cad.master)?.symbol;
            const symName = typeof sym === 'string' ? sym : (sym?.name ?? null);
            const sourceLabel = cad.source === 'alias' ? 'surveyor alias'
                : cad.source === 'standard' ? 'master code match'
                : cad.source;
            return {
                symbolName: symName,
                reason: `CAD Manager → master code "${cad.master}" (${cad.description}) via ${sourceLabel}${cad.confidence != null ? ` · confidence ${Math.round(cad.confidence * 100)}%` : ''}.`,
            };
        }
        const builtinSym = getBuiltinLibrarySymbol(desc);
        if (builtinSym) {
            return { symbolName: builtinSym.name, reason: `Built-in survey symbol library — matched "${builtinSym.name}" from "${desc}".` };
        }
        // A built-in match may exist but be gated off (default). Surface that so
        // the user knows they can enable it (e.g. "draw tree symbols").
        const libMatch = findMatchingSymbol(desc, desc);
        if (libMatch && !isBuiltinSymbolEnabled(libMatch.id)) {
            return {
                symbolName: null,
                reason: `Built-in "${libMatch.name}" symbol is available but turned off — ask to "draw ${libMatch.name.toLowerCase()} symbols" to show it.`,
            };
        }
        return { symbolName: null, reason: 'No symbol rule matches this description — drawing a plain point marker.' };
    }, [settings.showPointSymbols, customSymbols, symbolMatchers, cadManager, cadStandard, getBuiltinLibrarySymbol, isBuiltinSymbolEnabled]);

    const annotationMatchers = useMemo(() => buildAnnotationMatchers(customAnnotations), [customAnnotations]);

    const getAnnotationForPoint = useCallback((point: SurveyPoint): AnnotationRule | null => {
        if (!customAnnotations || customAnnotations.length === 0) return null;
        if (!point.description) return null;
        const res = resolveAnnotation(point.description, customAnnotations, annotationMatchers);
        return res.rule;
    }, [customAnnotations, annotationMatchers]);

    // ── Flood ring chains (memoized — expensive O(n²) only runs when data changes) ──
    const floodRings = useMemo(() => {
        const byPid = new Map<string, Array<{ x: number; y: number }>>();
        // Group segments by polylineId
        const groups = new Map<string, Array<{ a: { x: number; y: number }; b: { x: number; y: number }; curve?: { center: { x: number; y: number }; radius: number; startAngle: number; endAngle: number; anticlockwise: boolean } }>>();
        for (const line of lines) {
            if (line.type !== 'flood' || line.hidden) continue;
            const pid = line.polylineId ?? `${line.layer ?? 'flood'}_nopid`;
            const a = line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : (() => { const p = pointMap.get(line.from); return p ? { x: p.easting, y: p.northing } : null; })();
            const b = line.toPt ? { x: line.toPt.x, y: line.toPt.y } : (() => { const p = pointMap.get(line.to); return p ? { x: p.easting, y: p.northing } : null; })();
            if (!a || !b) continue;

            let curve: { center: { x: number; y: number }; radius: number; startAngle: number; endAngle: number; anticlockwise: boolean } | undefined;
            if (line.isCurve && line.curveRadius && line.arcLength) {
                const radius = line.curveRadius;
                const centralAngle = line.arcLength / radius;
                const isLeftCurve = line.curveDirection === 'left';
                let centerX: number | null = null;
                let centerY: number | null = null;

                if (line.circleCenter) {
                    centerX = line.circleCenter.x;
                    centerY = line.circleCenter.y;
                } else {
                    const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                    const chordStrArc = line.chordBearing || line.bearing;
                    const chordRadArc = chordStrArc ? parseBearingToRadians(chordStrArc) : null;
                    const tangentAngle: number | null = chordRadArc !== null
                        ? chordRadArc - signedDeltaArc / 2
                        : (line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null);
                    if (tangentAngle !== null) {
                        const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                        const centerOffsetAngle = isLeftCurve
                            ? canvasTangentAngle + Math.PI / 2
                            : canvasTangentAngle - Math.PI / 2;
                        centerX = a.x + radius * Math.cos(centerOffsetAngle);
                        centerY = a.y + radius * Math.sin(centerOffsetAngle);
                    }
                }

                if (centerX !== null && centerY !== null) {
                    const startAngle = Math.atan2(a.y - centerY, a.x - centerX);
                    const endAngle = isLeftCurve ? startAngle + centralAngle : startAngle - centralAngle;
                    curve = {
                        center: { x: centerX, y: centerY },
                        radius,
                        startAngle,
                        endAngle,
                        anticlockwise: !isLeftCurve,
                    };
                }
            }

            if (!groups.has(pid)) groups.set(pid, []);
            groups.get(pid)!.push({ a, b, curve });
        }
        for (const [pid, pool] of groups) {
            if (pool.length < 2) continue;
            const remaining = pool.slice();
            const first = remaining.splice(0, 1)[0];
            const ring: { x: number; y: number }[] = [];

            const appendSeg = (seg: typeof first, reverse: boolean) => {
                if (seg.curve) {
                    const numSamples = Math.max(12, Math.round((seg.curve.radius * Math.abs(seg.curve.endAngle - seg.curve.startAngle)) / 4));
                    const sAng = reverse ? seg.curve.endAngle : seg.curve.startAngle;
                    const eAng = reverse ? seg.curve.startAngle : seg.curve.endAngle;
                    for (let s = (ring.length === 0 ? 0 : 1); s <= numSamples; s++) {
                        const t = s / numSamples;
                        const ang = sAng + (eAng - sAng) * t;
                        ring.push({
                            x: seg.curve.center.x + seg.curve.radius * Math.cos(ang),
                            y: seg.curve.center.y + seg.curve.radius * Math.sin(ang),
                        });
                    }
                } else {
                    const startPt = reverse ? seg.b : seg.a;
                    const endPt = reverse ? seg.a : seg.b;
                    if (ring.length === 0) ring.push(startPt);
                    ring.push(endPt);
                }
            };

            appendSeg(first, false);
            const EPS = 0.001;
            let extended = true;
            while (extended && remaining.length > 0) {
                extended = false;
                const tail = ring[ring.length - 1];
                for (let i = 0; i < remaining.length; i++) {
                    const cand = remaining[i];
                    if (Math.hypot(cand.a.x - tail.x, cand.a.y - tail.y) < EPS) {
                        appendSeg(cand, false);
                        remaining.splice(i, 1);
                        extended = true;
                        break;
                    } else if (Math.hypot(cand.b.x - tail.x, cand.b.y - tail.y) < EPS) {
                        appendSeg(cand, true);
                        remaining.splice(i, 1);
                        extended = true;
                        break;
                    }
                }
            }
            if (ring.length >= 3) byPid.set(pid, ring);
        }
        return byPid;
    }, [lines, pointMap]);

    /**
     * Hit-test the drawn centerline alignment — straight tangent runs plus the
     * sampled circular curves — so Stationing & CL alignments are pickable in
     * Select mode. Returns the owning centerline rather than a single segment.
     */
    const findCenterlineNearClick = useCallback((clickX: number, clickY: number, threshold: number = 12): Centerline | null => {
        if (!centerlinesVisible) return null;
        const distToSegment = (px: number, py: number, ax: number, ay: number, bx: number, by: number) => {
            const dx = bx - ax;
            const dy = by - ay;
            const len2 = dx * dx + dy * dy;
            if (len2 < 1e-12) return Math.hypot(px - ax, py - ay);
            let t = ((px - ax) * dx + (py - ay) * dy) / len2;
            t = Math.max(0, Math.min(1, t));
            return Math.hypot(px - (ax + t * dx), py - (ay + t * dy));
        };
        const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };

        let nearest: Centerline | null = null;
        let best = threshold;

        for (const cl of centerlines) {
            if (!cl || !cl.pis || cl.pis.length < 2) continue;
            let prevScreen = worldToScreen(cl.pis[0].easting, cl.pis[0].northing);

            const considerWorld = (worldPts: Array<{ easting: number; northing: number }>) => {
                let from = prevScreen;
                for (const w of worldPts) {
                    const to = worldToScreen(w.easting, w.northing);
                    const d = distToSegment(clickX, clickY, from.x, from.y, to.x, to.y);
                    if (d < best) {
                        best = d;
                        nearest = cl;
                    }
                    from = to;
                }
                prevScreen = from;
            };

            for (let i = 1; i < cl.pis.length; i++) {
                const pCurr = cl.pis[i];
                const curve = (i < cl.pis.length - 1 && pCurr.curveRadius)
                    ? calculateCurveGeometry(cl.pis[i - 1], pCurr, cl.pis[i + 1], pCurr.curveRadius)
                    : null;
                if (curve) {
                    considerWorld([{ easting: curve.pcEasting, northing: curve.pcNorthing }]);
                    const sweep = curve.turnRight
                        ? -normalizeAng(curve.startAngle - curve.endAngle)
                        : normalizeAng(curve.endAngle - curve.startAngle);
                    const samples: Array<{ easting: number; northing: number }> = [];
                    const steps = 32;
                    for (let k = 1; k <= steps; k++) {
                        const ang = curve.startAngle + sweep * (k / steps);
                        samples.push({
                            easting: curve.centerEasting + curve.radius * Math.cos(ang),
                            northing: curve.centerNorthing + curve.radius * Math.sin(ang),
                        });
                    }
                    considerWorld(samples);
                } else {
                    considerWorld([{ easting: pCurr.easting, northing: pCurr.northing }]);
                }
            }
        }

        return nearest;
    }, [centerlines, centerlinesVisible, worldToScreen]);

    // Helper function to find line near a click point
    const findLineNearClick = useCallback((clickX: number, clickY: number, threshold: number = 10): SurveyLine | null => {
        let nearestLine: SurveyLine | null = null;
        let minDistance = threshold;
        
        lines.forEach(line => {
            const p1 = pointMap.get(line.from);
            const p2 = pointMap.get(line.to);
            
            const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
            const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);
            
            if (!p1Coords) return;

            const isCircleEntity = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (p1Coords && p2Coords && p1Coords.x === p2Coords.x && p1Coords.y === p2Coords.y)) && !line.tangentBearing && !line.chordBearing);
            if (isCircleEntity && line.curveRadius) {
                const center = line.circleCenter || (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : p1Coords);
                if (center) {
                    const sCenter = worldToScreen(center.x, center.y);
                    const distFromScreenCenter = Math.hypot(clickX - sCenter.x, clickY - sCenter.y);
                    const screenRadius = line.curveRadius * transform.scale;
                    const distToCircumference = Math.abs(distFromScreenCenter - screenRadius);
                    if (distToCircumference <= minDistance) {
                        minDistance = distToCircumference;
                        nearestLine = line;
                    }
                }
                return;
            }

            if (line.isCurve && line.curveRadius && line.arcLength) {
                // Curve hit testing: compute arc geometry in screen coordinates
                let curveLine = line;
                const radius = curveLine.curveRadius!;
                const arcLength = curveLine.arcLength!;
                const centralAngle = arcLength / radius;

                if (!curveLine.tangentBearing && !curveLine.chordBearing && !curveLine.bearing && p2Coords) {
                    const dE = p2Coords.x - p1Coords.x;
                    const dN = p2Coords.y - p1Coords.y;
                    if (Math.hypot(dE, dN) > 1e-9) {
                        let az = Math.atan2(dE, dN);
                        if (az < 0) az += 2 * Math.PI;
                        curveLine = { ...curveLine, chordBearing: formatBearing(az) };
                    }
                }

                const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
                let isLeftCurve = false;
                if (curveLine.curveDirection === 'left') {
                    isLeftCurve = true;
                } else if (curveLine.curveDirection === 'right') {
                    isLeftCurve = false;
                } else {
                    const tempTangent = curveLine.tangentBearing ? parseBearingToRadians(curveLine.tangentBearing) : null;
                    if (tempTangent !== null) {
                        const dirBearingStr = curveLine.chordBearing || curveLine.bearing;
                        if (dirBearingStr) {
                            const dirRad = parseBearingToRadians(dirBearingStr);
                            if (dirRad !== null) {
                                const halfDelta = centralAngle / 2;
                                const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                                const expectedLeftChord  = normalizeAng(tempTangent - halfDelta);
                                const dirNorm = normalizeAng(dirRad);
                                const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                                const diffLeft  = Math.min(Math.abs(dirNorm - expectedLeftChord),  2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                                if (diffLeft < diffRight) isLeftCurve = true;
                            }
                        }
                    }
                }

                const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                const chordStrArc = curveLine.chordBearing || curveLine.bearing;
                const chordRadArc = chordStrArc ? parseBearingToRadians(chordStrArc) : null;
                const tangentAngle: number | null = chordRadArc !== null
                    ? chordRadArc - signedDeltaArc / 2
                    : (curveLine.tangentBearing ? parseBearingToRadians(curveLine.tangentBearing) : null);

                if (tangentAngle !== null) {
                    const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                    const centerOffsetAngle = isLeftCurve
                        ? canvasTangentAngle + Math.PI / 2
                        : canvasTangentAngle - Math.PI / 2;
                    const centerWorldX = p1Coords.x + radius * Math.cos(centerOffsetAngle);
                    const centerWorldY = p1Coords.y + radius * Math.sin(centerOffsetAngle);

                    const sCenter = worldToScreen(centerWorldX, centerWorldY);
                    const screenCenterX = sCenter.x;
                    const screenCenterY = sCenter.y;
                    const screenRadius = radius * transform.scale;

                    const startAngle = Math.atan2(p1Coords.y - centerWorldY, p1Coords.x - centerWorldX);
                    const endAngle = isLeftCurve ? startAngle + centralAngle : startAngle - centralAngle;

                    // Check distance from click to arc center vs screenRadius, and angle checks
                    const dx = clickX - screenCenterX;
                    const dy = clickY - screenCenterY;
                    const distFromCenter = Math.sqrt(dx * dx + dy * dy);

                    if (Math.abs(distFromCenter - screenRadius) <= minDistance) {
                        const clickAngle = Math.atan2(-dy, dx); // Note canvas Y is flipped
                        // Normalize angular check between startAngle and endAngle
                        const normalizeAngleRad = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
                        const sNorm = normalizeAngleRad(startAngle);
                        const eNorm = normalizeAngleRad(endAngle);
                        const cNorm = normalizeAngleRad(clickAngle);

                        let inArc = false;
                        if (!isLeftCurve) {
                            // Clockwise in world (CCW or CW in screen depending on Y flip)
                            // Let's sample points along the arc for robust hit testing
                            const numSamples = Math.max(10, Math.round(arcLength * transform.scale / 5));
                            let minDistToArc = minDistance;
                            for (let i = 0; i <= numSamples; i++) {
                                const frac = i / numSamples;
                                const interpAngle = startAngle + (isLeftCurve ? centralAngle * frac : -centralAngle * frac);
                                const sampleWorldX = centerWorldX + radius * Math.cos(interpAngle);
                                const sampleWorldY = centerWorldY + radius * Math.sin(interpAngle);
                                const sSample = worldToScreen(sampleWorldX, sampleWorldY);
                                const dSample = Math.hypot(clickX - sSample.x, clickY - sSample.y);
                                if (dSample < minDistToArc) {
                                    minDistToArc = dSample;
                                    nearestLine = line;
                                }
                            }
                            if (nearestLine === line) {
                                minDistance = minDistToArc;
                            }
                        } else {
                            const numSamples = Math.max(10, Math.round(arcLength * transform.scale / 5));
                            let minDistToArc = minDistance;
                            for (let i = 0; i <= numSamples; i++) {
                                const frac = i / numSamples;
                                const interpAngle = startAngle + (isLeftCurve ? centralAngle * frac : -centralAngle * frac);
                                const sampleWorldX = centerWorldX + radius * Math.cos(interpAngle);
                                const sampleWorldY = centerWorldY + radius * Math.sin(interpAngle);
                                const sSample = worldToScreen(sampleWorldX, sampleWorldY);
                                const dSample = Math.hypot(clickX - sSample.x, clickY - sSample.y);
                                if (dSample < minDistToArc) {
                                    minDistToArc = dSample;
                                    nearestLine = line;
                                }
                            }
                            if (nearestLine === line) {
                                minDistance = minDistToArc;
                            }
                        }
                    }
                }
            } else if (p2Coords) {
                // Convert to screen coordinates
                const s1 = worldToScreen(p1Coords.x, p1Coords.y);
                const s2 = worldToScreen(p2Coords.x, p2Coords.y);
                const screenX1 = s1.x;
                const screenY1 = s1.y;
                const screenX2 = s2.x;
                const screenY2 = s2.y;
                
                // Calculate distance from point to line segment
                const dx = screenX2 - screenX1;
                const dy = screenY2 - screenY1;
                const lengthSq = dx * dx + dy * dy;
                
                if (lengthSq === 0) return; // Points are the same
                
                let t = ((clickX - screenX1) * dx + (clickY - screenY1) * dy) / lengthSq;
                t = Math.max(0, Math.min(1, t)); // Clamp to line segment
                
                const projX = screenX1 + t * dx;
                const projY = screenY1 + t * dy;
                
                const distance = Math.sqrt((clickX - projX) ** 2 + (clickY - projY) ** 2);
                
                if (distance < minDistance) {
                    minDistance = distance;
                    nearestLine = line;
                }
            }
        });
        
        return nearestLine;
    }, [lines, pointMap, transform, worldToScreen]);

    const findDimensionNearClick = useCallback((clickX: number, clickY: number, thresholdPx: number = 18): AnnotationDimension | null => {
        if (!dimensions || dimensions.length === 0) return null;
        const worldCoords = screenToWorld(clickX, clickY);
        const worldThreshold = thresholdPx / transform.scale;
        for (let i = dimensions.length - 1; i >= 0; i--) {
            const dim = dimensions[i];
            if (hitTestDimension(dim, worldCoords, worldThreshold)) {
                return dim;
            }
        }
        return null;
    }, [dimensions, screenToWorld, transform.scale]);

    const getGripsForSelectedDimension = useCallback((dim: AnnotationDimension): DimensionGrip[] => {
        const grips: DimensionGrip[] = [];
        if (dim.type === 'aligned' || dim.type === 'horizontal' || dim.type === 'vertical') {
            const g = calculateLinearDimensionGeometry(dim);
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: dim.p1.easting, y: dim.p1.northing, dim });
            grips.push({ id: `${dim.id}-p2`, dimId: dim.id, type: 'p2', x: dim.p2.easting, y: dim.p2.northing, dim });
            grips.push({ id: `${dim.id}-offset`, dimId: dim.id, type: 'offset', x: g.midpoint.easting, y: g.midpoint.northing, dim });
        } else if (dim.type === 'radius') {
            const g = calculateRadialDimensionGeometry(dim);
            grips.push({ id: `${dim.id}-center`, dimId: dim.id, type: 'center', x: dim.center.easting, y: dim.center.northing, dim });
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: g.touchPoint.easting, y: g.touchPoint.northing, dim });
            grips.push({ id: `${dim.id}-text`, dimId: dim.id, type: 'text', x: dim.textPosition.easting, y: dim.textPosition.northing, dim });
        } else if (dim.type === 'diameter') {
            const g = calculateDiameterDimensionGeometry(dim);
            grips.push({ id: `${dim.id}-center`, dimId: dim.id, type: 'center', x: dim.center.easting, y: dim.center.northing, dim });
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: g.touch1.easting, y: g.touch1.northing, dim });
            grips.push({ id: `${dim.id}-p2`, dimId: dim.id, type: 'p2', x: g.touch2.easting, y: g.touch2.northing, dim });
            grips.push({ id: `${dim.id}-text`, dimId: dim.id, type: 'text', x: dim.textPosition.easting, y: dim.textPosition.northing, dim });
        } else if (dim.type === 'arc-length') {
            const g = calculateArcLengthDimensionGeometry(dim);
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: dim.p1.easting, y: dim.p1.northing, dim });
            grips.push({ id: `${dim.id}-p2`, dimId: dim.id, type: 'p2', x: dim.p2.easting, y: dim.p2.northing, dim });
            grips.push({ id: `${dim.id}-offset`, dimId: dim.id, type: 'offset', x: g.midpoint.easting, y: g.midpoint.northing, dim });
        } else if (dim.type === 'curve-data') {
            grips.push({ id: `${dim.id}-text`, dimId: dim.id, type: 'text', x: dim.textPosition.easting, y: dim.textPosition.northing, dim });
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: dim.p1.easting, y: dim.p1.northing, dim });
            grips.push({ id: `${dim.id}-p2`, dimId: dim.id, type: 'p2', x: dim.p2.easting, y: dim.p2.northing, dim });
        } else if (dim.type === 'angular') {
            const g = calculateAngularDimensionGeometry(dim);
            grips.push({ id: `${dim.id}-center`, dimId: dim.id, type: 'center', x: dim.vertex.easting, y: dim.vertex.northing, dim });
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: dim.p1.easting, y: dim.p1.northing, dim });
            grips.push({ id: `${dim.id}-p2`, dimId: dim.id, type: 'p2', x: dim.p2.easting, y: dim.p2.northing, dim });
            grips.push({ id: `${dim.id}-offset`, dimId: dim.id, type: 'offset', x: g.midpoint.easting, y: g.midpoint.northing, dim });
        } else if (dim.type === 'ordinate') {
            grips.push({ id: `${dim.id}-p1`, dimId: dim.id, type: 'p1', x: dim.point.easting, y: dim.point.northing, dim });
            grips.push({ id: `${dim.id}-text`, dimId: dim.id, type: 'text', x: dim.leaderEnd.easting, y: dim.leaderEnd.northing, dim });
        }
        return grips;
    }, []);

    const findDimensionGripNearClick = useCallback((clickX: number, clickY: number, thresholdPx = 12): DimensionGrip | null => {
        if (!selectedDimensionId) return null;
        const selDim = dimensions.find(d => d.id === selectedDimensionId);
        if (!selDim) return null;
        const grips = getGripsForSelectedDimension(selDim);
        let best: DimensionGrip | null = null;
        let minDist = thresholdPx;
        for (const grip of grips) {
            const sc = worldToScreen(grip.x, grip.y);
            const dist = Math.hypot(clickX - sc.x, clickY - sc.y);
            if (dist <= minDist) {
                minDist = dist;
                best = grip;
            }
        }
        return best;
    }, [selectedDimensionId, dimensions, getGripsForSelectedDimension, worldToScreen]);

    const updateDimensionFromGrip = useCallback((
        dim: AnnotationDimension,
        gripType: DimensionGripType,
        targetW: { easting: number; northing: number }
    ): AnnotationDimension => {
        if (dim.type === 'aligned') {
            if (gripType === 'p1') return { ...dim, p1: targetW };
            if (gripType === 'p2') return { ...dim, p2: targetW };
            if (gripType === 'offset') {
                const dx = dim.p2.easting - dim.p1.easting;
                const dy = dim.p2.northing - dim.p1.northing;
                const len = Math.hypot(dx, dy);
                if (len > 1e-6) {
                    const px = -dy / len;
                    const py = dx / len;
                    const offset = (targetW.easting - dim.p1.easting) * px + (targetW.northing - dim.p1.northing) * py;
                    return { ...dim, offsetDist: offset };
                }
            }
        } else if (dim.type === 'horizontal') {
            if (gripType === 'p1') return { ...dim, p1: targetW };
            if (gripType === 'p2') return { ...dim, p2: targetW };
            if (gripType === 'offset') return { ...dim, offsetDist: targetW.northing - dim.p1.northing };
        } else if (dim.type === 'vertical') {
            if (gripType === 'p1') return { ...dim, p1: targetW };
            if (gripType === 'p2') return { ...dim, p2: targetW };
            if (gripType === 'offset') return { ...dim, offsetDist: targetW.easting - dim.p1.easting };
        } else if (dim.type === 'radius') {
            if (gripType === 'center') return { ...dim, center: targetW };
            if (gripType === 'text') return { ...dim, textPosition: targetW };
            if (gripType === 'p1') {
                const r = Math.hypot(targetW.easting - dim.center.easting, targetW.northing - dim.center.northing);
                return { ...dim, radius: Math.max(0.1, r) };
            }
        } else if (dim.type === 'diameter') {
            if (gripType === 'center') return { ...dim, center: targetW };
            if (gripType === 'text') return { ...dim, textPosition: targetW };
            if (gripType === 'p1' || gripType === 'p2') {
                const r = Math.hypot(targetW.easting - dim.center.easting, targetW.northing - dim.center.northing);
                return { ...dim, radius: Math.max(0.1, r) };
            }
        } else if (dim.type === 'arc-length') {
            if (gripType === 'p1') return { ...dim, p1: targetW };
            if (gripType === 'p2') return { ...dim, p2: targetW };
            if (gripType === 'offset') {
                const dist = Math.hypot(targetW.easting - dim.center.easting, targetW.northing - dim.center.northing);
                return { ...dim, offsetDist: dist - dim.radius };
            }
        } else if (dim.type === 'curve-data') {
            if (gripType === 'text') return { ...dim, textPosition: targetW };
            if (gripType === 'p1') return { ...dim, p1: targetW };
            if (gripType === 'p2') return { ...dim, p2: targetW };
        } else if (dim.type === 'angular') {
            if (gripType === 'center') return { ...dim, vertex: targetW };
            if (gripType === 'p1') return { ...dim, p1: targetW };
            if (gripType === 'p2') return { ...dim, p2: targetW };
            if (gripType === 'offset') {
                const r = Math.hypot(targetW.easting - dim.vertex.easting, targetW.northing - dim.vertex.northing);
                return { ...dim, arcRadius: Math.max(0.1, r) };
            }
        } else if (dim.type === 'ordinate') {
            if (gripType === 'p1') return { ...dim, point: targetW };
            if (gripType === 'text') return { ...dim, leaderEnd: targetW };
        }
        return dim;
    }, []);

    const resolveTtrEntityFromLine = useCallback((line: SurveyLine, clickWorld: { easting: number; northing: number }): TtrEntity | null => {
        const p1 = pointMap.get(line.from);
        const p2 = pointMap.get(line.to);
        const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
        const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);
        if (!p1Coords) return null;

        const isCircleEntity = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (p1Coords && p2Coords && p1Coords.x === p2Coords.x && p1Coords.y === p2Coords.y)) && !line.tangentBearing && !line.chordBearing);
        if (isCircleEntity && line.curveRadius) {
            const center = line.circleCenter || (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : p1Coords);
            if (!center) return null;
            const dE = clickWorld.easting - center.x;
            const dN = clickWorld.northing - center.y;
            const d = Math.hypot(dE, dN);
            const pickPoint = d > 1e-9 ? { x: center.x + line.curveRadius * (dE / d), y: center.y + line.curveRadius * (dN / d) } : { x: center.x + line.curveRadius, y: center.y };
            return {
                kind: 'circle',
                line,
                center: { x: center.x, y: center.y },
                radius: line.curveRadius,
                pickPoint,
            };
        }

        if (line.isCurve && line.curveRadius && line.arcLength) {
            const radius = line.curveRadius;
            const centralAngle = line.arcLength / radius;
            const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };

            let isLeftCurve = line.curveDirection === 'left';
            if (line.curveDirection !== 'left' && line.curveDirection !== 'right') {
                const tempTangent = line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null;
                if (tempTangent !== null) {
                    const dirBearingStr = line.chordBearing || line.bearing;
                    if (dirBearingStr) {
                        const dirRad = parseBearingToRadians(dirBearingStr);
                        if (dirRad !== null) {
                            const halfDelta = centralAngle / 2;
                            const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                            const expectedLeftChord = normalizeAng(tempTangent - halfDelta);
                            const dirNorm = normalizeAng(dirRad);
                            const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                            const diffLeft = Math.min(Math.abs(dirNorm - expectedLeftChord), 2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                            if (diffLeft < diffRight) isLeftCurve = true;
                        }
                    }
                }
            }

            let chordRadArc: number | null = null;
            const chordStr = line.chordBearing || line.bearing;
            if (chordStr) {
                chordRadArc = parseBearingToRadians(chordStr);
            } else if (p2Coords) {
                const dE = p2Coords.x - p1Coords.x;
                const dN = p2Coords.y - p1Coords.y;
                if (Math.hypot(dE, dN) > 1e-9) {
                    let az = Math.atan2(dE, dN);
                    if (az < 0) az += 2 * Math.PI;
                    chordRadArc = az;
                }
            }

            const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
            const tangentAngle: number | null = chordRadArc !== null
                ? chordRadArc - signedDeltaArc / 2
                : (line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null);

            if (tangentAngle !== null) {
                const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                const centerOffsetAngle = isLeftCurve
                    ? canvasTangentAngle + Math.PI / 2
                    : canvasTangentAngle - Math.PI / 2;
                const cX = p1Coords.x + radius * Math.cos(centerOffsetAngle);
                const cY = p1Coords.y + radius * Math.sin(centerOffsetAngle);
                const startAngle = Math.atan2(p1Coords.y - cY, p1Coords.x - cX);
                const mouseAngle = Math.atan2(clickWorld.northing - cY, clickWorld.easting - cX);
                const pickPoint = { x: cX + radius * Math.cos(mouseAngle), y: cY + radius * Math.sin(mouseAngle) };
                return {
                    kind: 'arc',
                    line,
                    center: { x: cX, y: cY },
                    radius,
                    startAngle,
                    centralAngle,
                    isLeftCurve,
                    pickPoint,
                };
            }
        }

        if (p2Coords) {
            const dx = p2Coords.x - p1Coords.x;
            const dy = p2Coords.y - p1Coords.y;
            const lenSq = dx * dx + dy * dy;
            if (lenSq > 1e-12) {
                let t = ((clickWorld.easting - p1Coords.x) * dx + (clickWorld.northing - p1Coords.y) * dy) / lenSq;
                t = Math.max(0, Math.min(1, t));
                const pickPoint = { x: p1Coords.x + dx * t, y: p1Coords.y + dy * t };
                return {
                    kind: 'line',
                    line,
                    p1: { x: p1Coords.x, y: p1Coords.y },
                    p2: { x: p2Coords.x, y: p2Coords.y },
                    pickPoint,
                };
            }
        }

        return null;
    }, [pointMap]);

    const findTtrEntityNearClick = useCallback((clickX: number, clickY: number, threshold: number = 25): TtrEntity | null => {
        const mouseWorld = screenToWorld(clickX, clickY);
        const line = findLineNearClick(clickX, clickY, threshold);
        if (line) {
            return resolveTtrEntityFromLine(line, mouseWorld);
        }
        if (boundaryFiles && boundaryFiles.length > 0) {
            for (const bf of boundaryFiles) {
                if (bf.hidden) continue;
                for (const call of bf.calls) {
                    const from = pointMap.get(call.from);
                    const to = pointMap.get(call.to);
                    if (!from || !to) continue;
                    const p1Coords = { x: from.easting, y: from.northing };
                    const p2Coords = { x: to.easting, y: to.northing };
                    const fakeLine: SurveyLine = {
                        from: call.from,
                        to: call.to,
                        fromPt: { x: p1Coords.x, y: p1Coords.y, z: 0 },
                        toPt: { x: p2Coords.x, y: p2Coords.y, z: 0 },
                        isCurve: call.isCurve,
                        curveRadius: call.curveRadius,
                        arcLength: call.arcLength,
                        curveDirection: call.curveDirection,
                        tangentBearing: call.tangentBearing,
                        bearing: call.bearing,
                        chordBearing: call.chordBearing,
                    };
                    const ent = resolveTtrEntityFromLine(fakeLine, mouseWorld);
                    if (ent) {
                        const sPick = worldToScreen(ent.pickPoint.x, ent.pickPoint.y);
                        if (Math.hypot(clickX - sPick.x, clickY - sPick.y) <= threshold) {
                            return ent;
                        }
                    }
                }
            }
        }
        return null;
    }, [findLineNearClick, resolveTtrEntityFromLine, screenToWorld, worldToScreen, boundaryFiles, pointMap]);

    const resolveCurveTargetFromClick = useCallback((clickX: number, clickY: number) => {
        const ttr = findTtrEntityNearClick(clickX, clickY, 30);
        if (ttr && (ttr.kind === 'arc' || ttr.kind === 'circle')) {
            const radius = ttr.radius;
            const center = { easting: ttr.center.x, northing: ttr.center.y };
            if (ttr.kind === 'circle') {
                const circ = 2 * Math.PI * radius;
                return {
                    kind: 'circle' as const,
                    center,
                    radius,
                    arcLength: circ,
                    p1: { easting: center.easting + radius, northing: center.northing },
                    p2: { easting: center.easting - radius, northing: center.northing },
                    isLeftCurve: false,
                    deltaRad: 2 * Math.PI,
                };
            } else {
                const centralAngle = ttr.centralAngle ?? (ttr.line.arcLength ? ttr.line.arcLength / radius : Math.PI / 2);
                const p1 = ttr.line.fromPt ? { easting: ttr.line.fromPt.x, northing: ttr.line.fromPt.y } : { easting: center.easting + radius * Math.cos(ttr.startAngle), northing: center.northing + radius * Math.sin(ttr.startAngle) };
                const p2 = ttr.line.toPt ? { easting: ttr.line.toPt.x, northing: ttr.line.toPt.y } : { easting: center.easting + radius * Math.cos(ttr.startAngle + centralAngle), northing: center.northing + radius * Math.sin(ttr.startAngle + centralAngle) };
                const arcLength = ttr.line.arcLength ?? (radius * centralAngle);
                return {
                    kind: 'arc' as const,
                    center,
                    radius,
                    arcLength,
                    p1,
                    p2,
                    isLeftCurve: !!ttr.isLeftCurve,
                    chordBearing: ttr.line.chordBearing ?? ttr.line.bearing,
                    tangentBearing: ttr.line.tangentBearing,
                    deltaRad: centralAngle,
                };
            }
        }

        const line = findLineNearClick(clickX, clickY, 30);
        if (line && (line.isCircle || (line.isCurve && line.curveRadius))) {
            const radius = line.curveRadius!;
            const p1p = pointMap.get(line.from);
            const p2p = pointMap.get(line.to);
            const p1 = p1p ? { easting: p1p.easting, northing: p1p.northing } : (line.fromPt ? { easting: line.fromPt.x, northing: line.fromPt.y } : { easting: 0, northing: 0 });
            const p2 = p2p ? { easting: p2p.easting, northing: p2p.northing } : (line.toPt ? { easting: line.toPt.x, northing: line.toPt.y } : p1);
            const center = line.circleCenter ? { easting: line.circleCenter.x, northing: line.circleCenter.y } : p1;
            const arcLength = line.arcLength ?? (line.isCircle ? 2 * Math.PI * radius : radius * Math.PI / 2);
            const isLeft = line.curveDirection === 'left';
            return {
                kind: (line.isCircle ? 'circle' : 'arc') as 'circle' | 'arc',
                center,
                radius,
                arcLength,
                p1,
                p2,
                isLeftCurve: isLeft,
                chordBearing: line.chordBearing ?? line.bearing,
                tangentBearing: line.tangentBearing,
                deltaRad: arcLength / radius,
            };
        }
        return null;
    }, [findTtrEntityNearClick, findLineNearClick, pointMap]);

    const solveTtrCircle = useCallback((ent1: TtrEntity, ent2: TtrEntity, radius: number): {
        center: { easting: number; northing: number };
        radius: number;
        tangentPt1: { x: number; y: number };
        tangentPt2: { x: number; y: number };
    } | null => {
        if (!radius || radius <= 1e-6) return null;

        type Candidate = {
            center: { x: number; y: number };
            t1: { x: number; y: number };
            t2: { x: number; y: number };
            score: number;
        };
        const candidates: Candidate[] = [];

        const isEnt1Line = ent1.kind === 'line';
        const isEnt2Line = ent2.kind === 'line';

        if (isEnt1Line && isEnt2Line && ent1.p1 && ent1.p2 && ent2.p1 && ent2.p2) {
            // Line - Line
            const v1x = ent1.p2.x - ent1.p1.x;
            const v1y = ent1.p2.y - ent1.p1.y;
            const len1 = Math.hypot(v1x, v1y);
            const v2x = ent2.p2.x - ent2.p1.x;
            const v2y = ent2.p2.y - ent2.p1.y;
            const len2 = Math.hypot(v2x, v2y);
            if (len1 < 1e-9 || len2 < 1e-9) return null;

            const u1x = v1x / len1; const u1y = v1y / len1;
            const n1x = -u1y; const n1y = u1x;
            const u2x = v2x / len2; const u2y = v2y / len2;
            const n2x = -u2y; const n2y = u2x;

            const cross = u1x * u2y - u1y * u2x;
            if (Math.abs(cross) > 1e-6) {
                // Intersect 4 pairs of offset lines
                for (const s1 of [1, -1]) {
                    const off1x = ent1.p1.x + s1 * radius * n1x;
                    const off1y = ent1.p1.y + s1 * radius * n1y;
                    for (const s2 of [1, -1]) {
                        const off2x = ent2.p1.x + s2 * radius * n2x;
                        const off2y = ent2.p1.y + s2 * radius * n2y;

                        const dx = off2x - off1x;
                        const dy = off2y - off1y;
                        const t = (dx * u2y - dy * u2x) / cross;
                        const cx = off1x + t * u1x;
                        const cy = off1y + t * u1y;

                        const t1x = cx - s1 * radius * n1x;
                        const t1y = cy - s1 * radius * n1y;
                        const t2x = cx - s2 * radius * n2x;
                        const t2y = cy - s2 * radius * n2y;

                        const score = (t1x - ent1.pickPoint.x) ** 2 + (t1y - ent1.pickPoint.y) ** 2 +
                                      (t2x - ent2.pickPoint.x) ** 2 + (t2y - ent2.pickPoint.y) ** 2;
                        candidates.push({ center: { x: cx, y: cy }, t1: { x: t1x, y: t1y }, t2: { x: t2x, y: t2y }, score });
                    }
                }
            } else {
                // Parallel lines
                const distBetween = Math.abs((ent2.p1.x - ent1.p1.x) * n1x + (ent2.p1.y - ent1.p1.y) * n1y);
                if (Math.abs(distBetween - 2 * radius) < Math.max(0.05 * radius, 1.0)) {
                    const sgn = ((ent2.p1.x - ent1.p1.x) * n1x + (ent2.p1.y - ent1.p1.y) * n1y) >= 0 ? 1 : -1;
                    const midLinePx = ent1.p1.x + (sgn * distBetween / 2) * n1x;
                    const midLinePy = ent1.p1.y + (sgn * distBetween / 2) * n1y;
                    const midPickX = (ent1.pickPoint.x + ent2.pickPoint.x) / 2;
                    const midPickY = (ent1.pickPoint.y + ent2.pickPoint.y) / 2;
                    const t = (midPickX - midLinePx) * u1x + (midPickY - midLinePy) * u1y;
                    const cx = midLinePx + t * u1x;
                    const cy = midLinePy + t * u1y;
                    const t1x = cx - sgn * radius * n1x;
                    const t1y = cy - sgn * radius * n1y;
                    const t2x = cx + sgn * radius * n1x;
                    const t2y = cy + sgn * radius * n1y;
                    candidates.push({ center: { x: cx, y: cy }, t1: { x: t1x, y: t1y }, t2: { x: t2x, y: t2y }, score: 0 });
                }
            }
        } else if ((isEnt1Line && !isEnt2Line) || (!isEnt1Line && isEnt2Line)) {
            // Line - Circle / Arc
            const lineEnt = isEnt1Line ? ent1 : ent2;
            const circEnt = isEnt1Line ? ent2 : ent1;
            const isSwapped = !isEnt1Line;

            if (lineEnt.p1 && lineEnt.p2 && circEnt.center && circEnt.radius) {
                const v1x = lineEnt.p2.x - lineEnt.p1.x;
                const v1y = lineEnt.p2.y - lineEnt.p1.y;
                const len1 = Math.hypot(v1x, v1y);
                if (len1 > 1e-9) {
                    const ux = v1x / len1; const uy = v1y / len1;
                    const nx = -uy; const ny = ux;

                    const offsetRadii = [circEnt.radius + radius, Math.abs(circEnt.radius - radius)].filter(r => r > 1e-6);

                    for (const s of [1, -1]) {
                        const offLx = lineEnt.p1.x + s * radius * nx;
                        const offLy = lineEnt.p1.y + s * radius * ny;

                        for (const rOff of offsetRadii) {
                            const dx = offLx - circEnt.center.x;
                            const dy = offLy - circEnt.center.y;
                            const b = 2 * (dx * ux + dy * uy);
                            const c = dx * dx + dy * dy - rOff * rOff;
                            const disc = b * b - 4 * c;
                            if (disc >= 0) {
                                const sqrtDisc = Math.sqrt(disc);
                                const tVals = [(-b + sqrtDisc) / 2, (-b - sqrtDisc) / 2];
                                for (const t of tVals) {
                                    const cx = offLx + t * ux;
                                    const cy = offLy + t * uy;
                                    const tLineX = cx - s * radius * nx;
                                    const tLineY = cy - s * radius * ny;

                                    const dcx = cx - circEnt.center.x;
                                    const dcy = cy - circEnt.center.y;
                                    const dcLen = Math.hypot(dcx, dcy);
                                    if (dcLen < 1e-9) continue;
                                    const tCircX = circEnt.center.x + circEnt.radius * (dcx / dcLen);
                                    const tCircY = circEnt.center.y + circEnt.radius * (dcy / dcLen);

                                    const t1 = isSwapped ? { x: tCircX, y: tCircY } : { x: tLineX, y: tLineY };
                                    const t2 = isSwapped ? { x: tLineX, y: tLineY } : { x: tCircX, y: tCircY };
                                    const score = (t1.x - ent1.pickPoint.x) ** 2 + (t1.y - ent1.pickPoint.y) ** 2 +
                                                  (t2.x - ent2.pickPoint.x) ** 2 + (t2.y - ent2.pickPoint.y) ** 2;
                                    candidates.push({ center: { x: cx, y: cy }, t1, t2, score });
                                }
                            }
                        }
                    }
                }
            }
        } else if (!isEnt1Line && !isEnt2Line && ent1.center && ent1.radius && ent2.center && ent2.radius) {
            // Circle - Circle
            const offsetRadii1 = [ent1.radius + radius, Math.abs(ent1.radius - radius)].filter(r => r > 1e-6);
            const offsetRadii2 = [ent2.radius + radius, Math.abs(ent2.radius - radius)].filter(r => r > 1e-6);

            const dCentersX = ent2.center.x - ent1.center.x;
            const dCentersY = ent2.center.y - ent1.center.y;
            const distCenters = Math.hypot(dCentersX, dCentersY);

            if (distCenters > 1e-9) {
                for (const r1 of offsetRadii1) {
                    for (const r2 of offsetRadii2) {
                        if (distCenters <= r1 + r2 && distCenters >= Math.abs(r1 - r2)) {
                            const a = (r1 * r1 - r2 * r2 + distCenters * distCenters) / (2 * distCenters);
                            const h = Math.sqrt(Math.max(0, r1 * r1 - a * a));
                            const midX = ent1.center.x + (a / distCenters) * dCentersX;
                            const midY = ent1.center.y + (a / distCenters) * dCentersY;
                            const orthX = -(dCentersY / distCenters);
                            const orthY = (dCentersX / distCenters);

                            const candidateCenters = [
                                { x: midX + h * orthX, y: midY + h * orthY },
                                { x: midX - h * orthX, y: midY - h * orthY },
                            ];

                            for (const c of candidateCenters) {
                                const dc1x = c.x - ent1.center.x;
                                const dc1y = c.y - ent1.center.y;
                                const len1 = Math.hypot(dc1x, dc1y);
                                const dc2x = c.x - ent2.center.x;
                                const dc2y = c.y - ent2.center.y;
                                const len2 = Math.hypot(dc2x, dc2y);
                                if (len1 < 1e-9 || len2 < 1e-9) continue;

                                const t1x = ent1.center.x + ent1.radius * (dc1x / len1);
                                const t1y = ent1.center.y + ent1.radius * (dc1y / len1);
                                const t2x = ent2.center.x + ent2.radius * (dc2x / len2);
                                const t2y = ent2.center.y + ent2.radius * (dc2y / len2);

                                const score = (t1x - ent1.pickPoint.x) ** 2 + (t1y - ent1.pickPoint.y) ** 2 +
                                              (t2x - ent2.pickPoint.x) ** 2 + (t2y - ent2.pickPoint.y) ** 2;
                                candidates.push({ center: c, t1: { x: t1x, y: t1y }, t2: { x: t2x, y: t2y }, score });
                            }
                        }
                    }
                }
            }
        }

        if (candidates.length === 0) return null;
        candidates.sort((a, b) => a.score - b.score);
        const best = candidates[0];
        return {
            center: { easting: best.center.x, northing: best.center.y },
            radius,
            tangentPt1: best.t1,
            tangentPt2: best.t2,
        };
    }, []);

    const computeTrimAction = useCallback((clickedLine: SurveyLine, worldX: number, worldY: number): {
        lineId: string;
        newLines: SurveyLine[];
        preview: TrimPreviewData;
    } | null => {
        const p1 = pointMap.get(clickedLine.from);
        const p2 = pointMap.get(clickedLine.to);
        const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (clickedLine.fromPt ? { x: clickedLine.fromPt.x, y: clickedLine.fromPt.y } : null);
        const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (clickedLine.toPt ? { x: clickedLine.toPt.x, y: clickedLine.toPt.y } : null);
        if (!p1Coords) return null;

        const isCircleEntity = clickedLine.isCircle || (clickedLine.isCurve && clickedLine.curveRadius && (!clickedLine.fromPt || (p1Coords && p2Coords && p1Coords.x === p2Coords.x && p1Coords.y === p2Coords.y)) && !clickedLine.tangentBearing && !clickedLine.chordBearing);
        const lineId = clickedLine.id || `${clickedLine.from}-${clickedLine.to}`;

        // Gather all cutting entities
        type CuttingEntity =
            | { type: 'line'; p1: { x: number; y: number }; p2: { x: number; y: number } }
            | { type: 'circle'; center: { x: number; y: number }; radius: number }
            | { type: 'arc'; center: { x: number; y: number }; radius: number; startAngle: number; centralAngle: number; isLeftCurve: boolean };

        const cuttingEntities: CuttingEntity[] = [];

        for (const line of lines) {
            if (line === clickedLine || line.hidden) continue;
            const lp1 = pointMap.get(line.from);
            const lp2 = pointMap.get(line.to);
            const c1 = lp1 ? { x: lp1.easting, y: lp1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
            const c2 = lp2 ? { x: lp2.easting, y: lp2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);
            if (!c1) continue;

            const isCirc = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (c1 && c2 && c1.x === c2.x && c1.y === c2.y)) && !line.tangentBearing && !line.chordBearing);
            if (isCirc && line.curveRadius) {
                const center = line.circleCenter || (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : c1);
                cuttingEntities.push({ type: 'circle', center: { x: center.x, y: center.y }, radius: line.curveRadius });
            } else if (line.isCurve && line.curveRadius && line.arcLength && c2) {
                const radius = line.curveRadius;
                const centralAngle = line.arcLength / radius;
                const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
                let isLeftCurve = line.curveDirection === 'left';
                if (line.curveDirection !== 'left' && line.curveDirection !== 'right') {
                    const tempTangent = line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null;
                    if (tempTangent !== null) {
                        const dirBearingStr = line.chordBearing || line.bearing;
                        if (dirBearingStr) {
                            const dirRad = parseBearingToRadians(dirBearingStr);
                            if (dirRad !== null) {
                                const halfDelta = centralAngle / 2;
                                const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                                const expectedLeftChord = normalizeAng(tempTangent - halfDelta);
                                const dirNorm = normalizeAng(dirRad);
                                const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                                const diffLeft = Math.min(Math.abs(dirNorm - expectedLeftChord), 2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                                if (diffLeft < diffRight) isLeftCurve = true;
                            }
                        }
                    }
                }
                let chordRadArc: number | null = null;
                const chordStr = line.chordBearing || line.bearing;
                if (chordStr) {
                    chordRadArc = parseBearingToRadians(chordStr);
                } else {
                    const dE = c2.x - c1.x;
                    const dN = c2.y - c1.y;
                    if (Math.hypot(dE, dN) > 1e-9) {
                        let az = Math.atan2(dE, dN);
                        if (az < 0) az += 2 * Math.PI;
                        chordRadArc = az;
                    }
                }
                const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                const tangentAngle = chordRadArc !== null
                    ? chordRadArc - signedDeltaArc / 2
                    : (line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null);
                if (tangentAngle !== null) {
                    const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                    const centerOffsetAngle = isLeftCurve ? canvasTangentAngle + Math.PI / 2 : canvasTangentAngle - Math.PI / 2;
                    const cX = c1.x + radius * Math.cos(centerOffsetAngle);
                    const cY = c1.y + radius * Math.sin(centerOffsetAngle);
                    const startAngle = Math.atan2(c1.y - cY, c1.x - cX);
                    cuttingEntities.push({ type: 'arc', center: { x: cX, y: cY }, radius, startAngle, centralAngle, isLeftCurve });
                }
            } else if (c2) {
                cuttingEntities.push({ type: 'line', p1: c1, p2: c2 });
            }
        }

        if (boundaryFiles && boundaryFiles.length > 0) {
            for (const bf of boundaryFiles) {
                if (bf.hidden) continue;
                for (const call of bf.calls) {
                    const bp1 = pointMap.get(call.from);
                    const bp2 = pointMap.get(call.to);
                    if (!bp1 || !bp2) continue;
                    const c1 = { x: bp1.easting, y: bp1.northing };
                    const c2 = { x: bp2.easting, y: bp2.northing };
                    if (call.isCurve && call.curveRadius && call.arcLength) {
                        const radius = call.curveRadius;
                        const centralAngle = call.arcLength / radius;
                        const isLeftCurve = call.curveDirection === 'left';
                        let chordRadArc: number | null = null;
                        const chordStr = call.chordBearing || call.bearing;
                        if (chordStr) {
                            chordRadArc = parseBearingToRadians(chordStr);
                        } else {
                            const dE = c2.x - c1.x;
                            const dN = c2.y - c1.y;
                            if (Math.hypot(dE, dN) > 1e-9) {
                                let az = Math.atan2(dE, dN);
                                if (az < 0) az += 2 * Math.PI;
                                chordRadArc = az;
                            }
                        }
                        const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                        const tangentAngle = chordRadArc !== null ? chordRadArc - signedDeltaArc / 2 : (call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : null);
                        if (tangentAngle !== null) {
                            const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                            const centerOffsetAngle = isLeftCurve ? canvasTangentAngle + Math.PI / 2 : canvasTangentAngle - Math.PI / 2;
                            const cX = c1.x + radius * Math.cos(centerOffsetAngle);
                            const cY = c1.y + radius * Math.sin(centerOffsetAngle);
                            const startAngle = Math.atan2(c1.y - cY, c1.x - cX);
                            cuttingEntities.push({ type: 'arc', center: { x: cX, y: cY }, radius, startAngle, centralAngle, isLeftCurve });
                        }
                    } else {
                        cuttingEntities.push({ type: 'line', p1: c1, p2: c2 });
                    }
                }
            }
        }

        // Case 1: Full Circle
        if (isCircleEntity && clickedLine.curveRadius) {
            const center = clickedLine.circleCenter || (clickedLine.fromPt ? { x: clickedLine.fromPt.x, y: clickedLine.fromPt.y } : p1Coords);
            if (!center) return null;
            const R = clickedLine.curveRadius;
            let thetaClick = Math.atan2(worldY - center.y, worldX - center.x);
            if (thetaClick < 0) thetaClick += 2 * Math.PI;

            const angleHits: number[] = [];

            for (const cut of cuttingEntities) {
                if (cut.type === 'line') {
                    const ux = cut.p2.x - cut.p1.x;
                    const uy = cut.p2.y - cut.p1.y;
                    const lenSq = ux * ux + uy * uy;
                    if (lenSq > 1e-9) {
                        const fx = cut.p1.x - center.x;
                        const fy = cut.p1.y - center.y;
                        const a = lenSq;
                        const b = 2 * (fx * ux + fy * uy);
                        const c = fx * fx + fy * fy - R * R;
                        const disc = b * b - 4 * a * c;
                        if (disc >= 0) {
                            const sqrtDisc = Math.sqrt(disc);
                            for (const u of [(-b - sqrtDisc) / (2 * a), (-b + sqrtDisc) / (2 * a)]) {
                                if (u >= -0.001 && u <= 1.001) {
                                    const ix = cut.p1.x + u * ux;
                                    const iy = cut.p1.y + u * uy;
                                    let ang = Math.atan2(iy - center.y, ix - center.x);
                                    if (ang < 0) ang += 2 * Math.PI;
                                    angleHits.push(ang);
                                }
                            }
                        }
                    }
                } else if (cut.type === 'circle' || cut.type === 'arc') {
                    const C2 = cut.center;
                    const R2 = cut.radius;
                    const d = Math.hypot(C2.x - center.x, C2.y - center.y);
                    if (d > 1e-6 && d <= R + R2 && d >= Math.abs(R - R2)) {
                        const a = (R * R - R2 * R2 + d * d) / (2 * d);
                        const h = Math.sqrt(Math.max(0, R * R - a * a));
                        const midX = center.x + a * (C2.x - center.x) / d;
                        const midY = center.y + a * (C2.y - center.y) / d;
                        const rx = -(C2.y - center.y) / d;
                        const ry = (C2.x - center.x) / d;
                        for (const pt of [{ x: midX + h * rx, y: midY + h * ry }, { x: midX - h * rx, y: midY - h * ry }]) {
                            if (cut.type === 'arc') {
                                const angCut = Math.atan2(pt.y - C2.y, pt.x - C2.x);
                                let sweep = cut.isLeftCurve ? (angCut - cut.startAngle) % (2 * Math.PI) : (cut.startAngle - angCut) % (2 * Math.PI);
                                if (sweep < 0) sweep += 2 * Math.PI;
                                if (sweep > cut.centralAngle + 1e-4) continue;
                            }
                            let ang = Math.atan2(pt.y - center.y, pt.x - center.x);
                            if (ang < 0) ang += 2 * Math.PI;
                            angleHits.push(ang);
                        }
                    }
                }
            }

            angleHits.sort((a, b) => a - b);
            const uniqueAngles: number[] = [];
            for (const ang of angleHits) {
                if (uniqueAngles.length === 0 || Math.abs(ang - uniqueAngles[uniqueAngles.length - 1]) > 0.01) {
                    uniqueAngles.push(ang);
                }
            }
            if (uniqueAngles.length > 1 && (2 * Math.PI - (uniqueAngles[uniqueAngles.length - 1] - uniqueAngles[0])) < 0.01) {
                uniqueAngles.pop();
            }

            const hitPoint = { x: center.x + R * Math.cos(thetaClick), y: center.y + R * Math.sin(thetaClick) };

            if (uniqueAngles.length < 2) {
                return {
                    lineId,
                    newLines: [],
                    preview: {
                        kind: 'circle',
                        center,
                        radius: R,
                        hitPoint,
                    },
                };
            }

            const m = uniqueAngles.length;
            let clickedIntervalIdx = -1;
            for (let i = 0; i < m; i++) {
                const a1 = uniqueAngles[i];
                const a2 = i === m - 1 ? uniqueAngles[0] + 2 * Math.PI : uniqueAngles[i + 1];
                let clickA = thetaClick;
                if (i === m - 1 && clickA < a1) clickA += 2 * Math.PI;
                if (clickA >= a1 - 1e-4 && clickA <= a2 + 1e-4) {
                    clickedIntervalIdx = i;
                    break;
                }
            }

            const a1 = uniqueAngles[clickedIntervalIdx >= 0 ? clickedIntervalIdx : 0];
            const a2 = clickedIntervalIdx >= 0
                ? (clickedIntervalIdx === m - 1 ? uniqueAngles[0] + 2 * Math.PI : uniqueAngles[clickedIntervalIdx + 1])
                : uniqueAngles[1];

            const preview: TrimPreviewData = {
                kind: 'arc',
                center,
                radius: R,
                startAngle: a1,
                endAngle: a2,
                arcAnticlockwise: false,
                hitPoint,
            };

            const newLines: SurveyLine[] = [];
            for (let i = 0; i < m; i++) {
                if (i === clickedIntervalIdx) continue;
                const segA1 = uniqueAngles[i];
                const segA2 = i === m - 1 ? uniqueAngles[0] : uniqueAngles[i + 1];
                let sweep = (segA2 - segA1) % (2 * Math.PI);
                if (sweep <= 0) sweep += 2 * Math.PI;
                if (sweep < 0.001) continue;

                const pA = { x: center.x + R * Math.cos(segA1), y: center.y + R * Math.sin(segA1) };
                const pB = { x: center.x + R * Math.cos(segA1 + sweep), y: center.y + R * Math.sin(segA1 + sweep) };
                const arcLen = R * sweep;

                let chordAz = Math.atan2(pB.x - pA.x, pB.y - pA.y);
                if (chordAz < 0) chordAz += 2 * Math.PI;
                let tanAz = (Math.PI / 2) - (segA1 + Math.PI / 2);
                tanAz = (tanAz % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);

                newLines.push({
                    ...clickedLine,
                    id: undefined,
                    from: `TRIM_${Date.now()}_${i}_A`,
                    to: `TRIM_${Date.now()}_${i}_B`,
                    fromPt: { x: pA.x, y: pA.y, z: 0 },
                    toPt: { x: pB.x, y: pB.y, z: 0 },
                    isCurve: true,
                    isCircle: false,
                    curveRadius: R,
                    arcLength: arcLen,
                    curveDirection: 'left',
                    chordBearing: formatBearing(chordAz),
                    tangentBearing: formatBearing(tanAz),
                });
            }

            return { lineId, newLines, preview };
        }

        // Case 2: Curved Line / Arc
        if (clickedLine.isCurve && clickedLine.curveRadius && clickedLine.arcLength && p2Coords) {
            const radius = clickedLine.curveRadius;
            const centralAngle = clickedLine.arcLength / radius;
            const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };

            let isLeftCurve = clickedLine.curveDirection === 'left';
            if (clickedLine.curveDirection !== 'left' && clickedLine.curveDirection !== 'right') {
                const tempTangent = clickedLine.tangentBearing ? parseBearingToRadians(clickedLine.tangentBearing) : null;
                if (tempTangent !== null) {
                    const dirBearingStr = clickedLine.chordBearing || clickedLine.bearing;
                    if (dirBearingStr) {
                        const dirRad = parseBearingToRadians(dirBearingStr);
                        if (dirRad !== null) {
                            const halfDelta = centralAngle / 2;
                            const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                            const expectedLeftChord = normalizeAng(tempTangent - halfDelta);
                            const dirNorm = normalizeAng(dirRad);
                            const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                            const diffLeft = Math.min(Math.abs(dirNorm - expectedLeftChord), 2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                            if (diffLeft < diffRight) isLeftCurve = true;
                        }
                    }
                }
            }
            let chordRadArc: number | null = null;
            const chordStr = clickedLine.chordBearing || clickedLine.bearing;
            if (chordStr) {
                chordRadArc = parseBearingToRadians(chordStr);
            } else {
                const dE = p2Coords.x - p1Coords.x;
                const dN = p2Coords.y - p1Coords.y;
                if (Math.hypot(dE, dN) > 1e-9) {
                    let az = Math.atan2(dE, dN);
                    if (az < 0) az += 2 * Math.PI;
                    chordRadArc = az;
                }
            }
            const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
            const tangentAngle = chordRadArc !== null ? chordRadArc - signedDeltaArc / 2 : (clickedLine.tangentBearing ? parseBearingToRadians(clickedLine.tangentBearing) : null);
            if (tangentAngle !== null) {
                const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                const centerOffsetAngle = isLeftCurve ? canvasTangentAngle + Math.PI / 2 : canvasTangentAngle - Math.PI / 2;
                const cX = p1Coords.x + radius * Math.cos(centerOffsetAngle);
                const cY = p1Coords.y + radius * Math.sin(centerOffsetAngle);
                const startAngle = Math.atan2(p1Coords.y - cY, p1Coords.x - cX);
                const endAngle = isLeftCurve ? startAngle + centralAngle : startAngle - centralAngle;

                const clickAng = Math.atan2(worldY - cY, worldX - cX);
                let clickParam = isLeftCurve ? (clickAng - startAngle) % (2 * Math.PI) : (startAngle - clickAng) % (2 * Math.PI);
                if (clickParam < 0) clickParam += 2 * Math.PI;
                clickParam = Math.max(0, Math.min(centralAngle, clickParam));

                const hitPoint = {
                    x: cX + radius * Math.cos(isLeftCurve ? startAngle + clickParam : startAngle - clickParam),
                    y: cY + radius * Math.sin(isLeftCurve ? startAngle + clickParam : startAngle - clickParam),
                };

                const arcParams: number[] = [];

                for (const cut of cuttingEntities) {
                    if (cut.type === 'line') {
                        const ux = cut.p2.x - cut.p1.x;
                        const uy = cut.p2.y - cut.p1.y;
                        const lenSq = ux * ux + uy * uy;
                        if (lenSq > 1e-9) {
                            const fx = cut.p1.x - cX;
                            const fy = cut.p1.y - cY;
                            const a = lenSq;
                            const b = 2 * (fx * ux + fy * uy);
                            const c = fx * fx + fy * fy - radius * radius;
                            const disc = b * b - 4 * a * c;
                            if (disc >= 0) {
                                const sqrtDisc = Math.sqrt(disc);
                                for (const u of [(-b - sqrtDisc) / (2 * a), (-b + sqrtDisc) / (2 * a)]) {
                                    if (u >= -0.001 && u <= 1.001) {
                                        const ix = cut.p1.x + u * ux;
                                        const iy = cut.p1.y + u * uy;
                                        const ang = Math.atan2(iy - cY, ix - cX);
                                        let p = isLeftCurve ? (ang - startAngle) % (2 * Math.PI) : (startAngle - ang) % (2 * Math.PI);
                                        if (p < 0) p += 2 * Math.PI;
                                        if (p > 0.005 && p < centralAngle - 0.005) {
                                            arcParams.push(p);
                                        }
                                    }
                                }
                            }
                        }
                    } else if (cut.type === 'circle' || cut.type === 'arc') {
                        const C2 = cut.center;
                        const R2 = cut.radius;
                        const d = Math.hypot(C2.x - cX, C2.y - cY);
                        if (d > 1e-6 && d <= radius + R2 && d >= Math.abs(radius - R2)) {
                            const a = (radius * radius - R2 * R2 + d * d) / (2 * d);
                            const h = Math.sqrt(Math.max(0, radius * radius - a * a));
                            const midX = cX + a * (C2.x - cX) / d;
                            const midY = cY + a * (C2.y - cY) / d;
                            const rx = -(C2.y - cY) / d;
                            const ry = (C2.x - cX) / d;
                            for (const pt of [{ x: midX + h * rx, y: midY + h * ry }, { x: midX - h * rx, y: midY - h * ry }]) {
                                if (cut.type === 'arc') {
                                    const angCut = Math.atan2(pt.y - C2.y, pt.x - C2.x);
                                    let sweepCut = cut.isLeftCurve ? (angCut - cut.startAngle) % (2 * Math.PI) : (cut.startAngle - angCut) % (2 * Math.PI);
                                    if (sweepCut < 0) sweepCut += 2 * Math.PI;
                                    if (sweepCut > cut.centralAngle + 1e-4) continue;
                                }
                                const ang = Math.atan2(pt.y - cY, pt.x - cX);
                                let p = isLeftCurve ? (ang - startAngle) % (2 * Math.PI) : (startAngle - ang) % (2 * Math.PI);
                                if (p < 0) p += 2 * Math.PI;
                                if (p > 0.005 && p < centralAngle - 0.005) {
                                    arcParams.push(p);
                                }
                            }
                        }
                    }
                }

                arcParams.sort((a, b) => a - b);
                const uniqueP: number[] = [];
                for (const p of arcParams) {
                    if (uniqueP.length === 0 || Math.abs(p - uniqueP[uniqueP.length - 1]) > 0.01) {
                        uniqueP.push(p);
                    }
                }

                if (uniqueP.length === 0) {
                    return {
                        lineId,
                        newLines: [],
                        preview: {
                            kind: 'arc',
                            center: { x: cX, y: cY },
                            radius,
                            startAngle,
                            endAngle,
                            arcAnticlockwise: !isLeftCurve,
                            hitPoint,
                        },
                    };
                }

                const allP = [0, ...uniqueP, centralAngle];
                let clickedIdx = -1;
                for (let i = 0; i < allP.length - 1; i++) {
                    if (clickParam >= allP[i] - 1e-4 && clickParam <= allP[i + 1] + 1e-4) {
                        clickedIdx = i;
                        break;
                    }
                }
                if (clickedIdx === -1) clickedIdx = 0;

                const pA_param = allP[clickedIdx];
                const pB_param = allP[clickedIdx + 1];
                const angA = isLeftCurve ? startAngle + pA_param : startAngle - pA_param;
                const angB = isLeftCurve ? startAngle + pB_param : startAngle - pB_param;

                const preview: TrimPreviewData = {
                    kind: 'arc',
                    center: { x: cX, y: cY },
                    radius,
                    startAngle: angA,
                    endAngle: angB,
                    arcAnticlockwise: !isLeftCurve,
                    hitPoint,
                };

                const newLines: SurveyLine[] = [];
                for (let i = 0; i < allP.length - 1; i++) {
                    if (i === clickedIdx) continue;
                    const segA = allP[i];
                    const segB = allP[i + 1];
                    const deltaSub = segB - segA;
                    if (deltaSub < 0.001) continue;

                    const curAngA = isLeftCurve ? startAngle + segA : startAngle - segA;
                    const curAngB = isLeftCurve ? startAngle + segB : startAngle - segB;

                    const pA = { x: cX + radius * Math.cos(curAngA), y: cY + radius * Math.sin(curAngA) };
                    const pB = { x: cX + radius * Math.cos(curAngB), y: cY + radius * Math.sin(curAngB) };
                    const arcLen = radius * deltaSub;

                    let chordAz = Math.atan2(pB.x - pA.x, pB.y - pA.y);
                    if (chordAz < 0) chordAz += 2 * Math.PI;

                    const tanMathAngle = isLeftCurve ? curAngA + Math.PI / 2 : curAngA - Math.PI / 2;
                    let tanAz = (Math.PI / 2) - tanMathAngle;
                    tanAz = (tanAz % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);

                    const startPn = segA === 0 ? clickedLine.from : `TRIM_${Date.now()}_${i}_A`;
                    const endPn = segB === centralAngle ? clickedLine.to : `TRIM_${Date.now()}_${i}_B`;

                    newLines.push({
                        ...clickedLine,
                        id: undefined,
                        from: startPn,
                        to: endPn,
                        fromPt: { x: pA.x, y: pA.y, z: 0 },
                        toPt: { x: pB.x, y: pB.y, z: 0 },
                        isCurve: true,
                        isCircle: false,
                        curveRadius: radius,
                        arcLength: arcLen,
                        curveDirection: clickedLine.curveDirection,
                        chordBearing: formatBearing(chordAz),
                        tangentBearing: formatBearing(tanAz),
                    });
                }

                return { lineId, newLines, preview };
            }
        }

        // Case 3: Straight Line Segment
        if (!p2Coords) return null;
        const vx = p2Coords.x - p1Coords.x;
        const vy = p2Coords.y - p1Coords.y;
        const L = Math.hypot(vx, vy);
        if (L < 1e-6) return null;

        const tClick = Math.max(0, Math.min(1, ((worldX - p1Coords.x) * vx + (worldY - p1Coords.y) * vy) / (L * L)));
        const hitPoint = { x: p1Coords.x + tClick * vx, y: p1Coords.y + tClick * vy };

        const tHits: number[] = [];

        for (const cut of cuttingEntities) {
            if (cut.type === 'line') {
                const ux = cut.p2.x - cut.p1.x;
                const uy = cut.p2.y - cut.p1.y;
                const denom = vx * uy - vy * ux;
                if (Math.abs(denom) > 1e-9) {
                    const rx = cut.p1.x - p1Coords.x;
                    const ry = cut.p1.y - p1Coords.y;
                    const t = (rx * uy - ry * ux) / denom;
                    const s = (rx * vy - ry * vx) / denom;
                    if (t > 0.001 && t < 0.999 && s >= -0.001 && s <= 1.001) {
                        tHits.push(t);
                    }
                }
            } else if (cut.type === 'circle' || cut.type === 'arc') {
                const C = cut.center;
                const R = cut.radius;
                const fx = p1Coords.x - C.x;
                const fy = p1Coords.y - C.y;
                const a = L * L;
                const b = 2 * (fx * vx + fy * vy);
                const c = fx * fx + fy * fy - R * R;
                const disc = b * b - 4 * a * c;
                if (disc >= 0) {
                    const sqrtDisc = Math.sqrt(disc);
                    for (const t of [(-b - sqrtDisc) / (2 * a), (-b + sqrtDisc) / (2 * a)]) {
                        if (t > 0.001 && t < 0.999) {
                            const ix = p1Coords.x + t * vx;
                            const iy = p1Coords.y + t * vy;
                            if (cut.type === 'arc') {
                                const ang = Math.atan2(iy - C.y, ix - C.x);
                                let sweep = cut.isLeftCurve ? (ang - cut.startAngle) % (2 * Math.PI) : (cut.startAngle - ang) % (2 * Math.PI);
                                if (sweep < 0) sweep += 2 * Math.PI;
                                if (sweep <= cut.centralAngle + 1e-4) {
                                    tHits.push(t);
                                }
                            } else {
                                tHits.push(t);
                            }
                        }
                    }
                }
            }
        }

        tHits.sort((a, b) => a - b);
        const uniqueT: number[] = [];
        for (const t of tHits) {
            if (uniqueT.length === 0 || Math.abs(t - uniqueT[uniqueT.length - 1]) > 0.002) {
                uniqueT.push(t);
            }
        }

        if (uniqueT.length === 0) {
            return {
                lineId,
                newLines: [],
                preview: {
                    kind: 'line',
                    p1: p1Coords,
                    p2: p2Coords,
                    hitPoint,
                },
            };
        }

        const allT = [0, ...uniqueT, 1];
        let clickedSegmentIdx = -1;
        for (let i = 0; i < allT.length - 1; i++) {
            if (tClick >= allT[i] - 1e-4 && tClick <= allT[i + 1] + 1e-4) {
                clickedSegmentIdx = i;
                break;
            }
        }
        if (clickedSegmentIdx === -1) {
            let bestDist = Infinity;
            for (let i = 0; i < allT.length - 1; i++) {
                const midT = (allT[i] + allT[i + 1]) / 2;
                const d = Math.abs(tClick - midT);
                if (d < bestDist) {
                    bestDist = d;
                    clickedSegmentIdx = i;
                }
            }
        }

        const tA = allT[clickedSegmentIdx >= 0 ? clickedSegmentIdx : 0];
        const tB = allT[clickedSegmentIdx >= 0 ? clickedSegmentIdx + 1 : 1];
        const preview: TrimPreviewData = {
            kind: 'line',
            p1: { x: p1Coords.x + tA * vx, y: p1Coords.y + tA * vy },
            p2: { x: p1Coords.x + tB * vx, y: p1Coords.y + tB * vy },
            hitPoint,
        };

        const newLines: SurveyLine[] = [];
        for (let i = 0; i < allT.length - 1; i++) {
            if (i === clickedSegmentIdx) continue;
            const segTa = allT[i];
            const segTb = allT[i + 1];
            const pA = { x: p1Coords.x + segTa * vx, y: p1Coords.y + segTa * vy };
            const pB = { x: p1Coords.x + segTb * vx, y: p1Coords.y + segTb * vy };
            const segDist = Math.hypot(pB.x - pA.x, pB.y - pA.y);
            if (segDist < 0.001) continue;

            let az = Math.atan2(pB.x - pA.x, pB.y - pA.y);
            if (az < 0) az += 2 * Math.PI;

            const startPn = segTa === 0 ? clickedLine.from : `TRIM_${Date.now()}_${i}`;
            const endPn = segTb === 1 ? clickedLine.to : `TRIM_${Date.now()}_${i + 1}`;

            newLines.push({
                ...clickedLine,
                id: undefined,
                from: startPn,
                to: endPn,
                fromPt: { x: pA.x, y: pA.y, z: 0 },
                toPt: { x: pB.x, y: pB.y, z: 0 },
                bearing: formatBearing(az),
                distance: segDist.toFixed(2),
                isCurve: false,
                isCircle: false,
            });
        }

        return { lineId, newLines, preview };
    }, [lines, pointMap, boundaryFiles]);

    const getGripsForSelectedLines = useCallback((): EntityGrip[] => {
        if (selectedLineIds.size === 0) return [];
        const result: EntityGrip[] = [];

        selectedLineIds.forEach(selId => {
            const selLine = lines.find(l => (l.id || `${l.from}-${l.to}`) === selId);
            if (!selLine || selLine.hidden) return;
            const sp1 = pointMap.get(selLine.from);
            const sp2 = pointMap.get(selLine.to);
            const p1c = sp1 ? { x: sp1.easting, y: sp1.northing } : (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : null);
            const p2c = sp2 ? { x: sp2.easting, y: sp2.northing } : (selLine.toPt ? { x: selLine.toPt.x, y: selLine.toPt.y } : null);
            if (!p1c) return;

            const isCircleEntity = selLine.isCircle || (selLine.isCurve && selLine.curveRadius && (!selLine.fromPt || (p1c && p2c && p1c.x === p2c.x && p1c.y === p2c.y)) && !selLine.tangentBearing && !selLine.chordBearing);

            if (isCircleEntity && selLine.curveRadius) {
                const center = selLine.circleCenter || (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : p1c);
                if (center) {
                    const R = selLine.curveRadius;
                    result.push({ id: `${selId}-center`, lineId: selId, type: 'center', x: center.x, y: center.y, line: selLine });
                    result.push({ id: `${selId}-quad-0`, lineId: selId, type: 'quadrant', x: center.x + R, y: center.y, quadrantIndex: 0, line: selLine });
                    result.push({ id: `${selId}-quad-1`, lineId: selId, type: 'quadrant', x: center.x, y: center.y + R, quadrantIndex: 1, line: selLine });
                    result.push({ id: `${selId}-quad-2`, lineId: selId, type: 'quadrant', x: center.x - R, y: center.y, quadrantIndex: 2, line: selLine });
                    result.push({ id: `${selId}-quad-3`, lineId: selId, type: 'quadrant', x: center.x, y: center.y - R, quadrantIndex: 3, line: selLine });
                }
            } else if (selLine.isCurve && selLine.curveRadius && selLine.arcLength && p2c) {
                const radius = selLine.curveRadius;
                const centralAngle = selLine.arcLength / radius;
                const isLeftCurve = selLine.curveDirection === 'left';
                let chordRadArc: number | null = null;
                const chordStr = selLine.chordBearing || selLine.bearing;
                if (chordStr) {
                    chordRadArc = parseBearingToRadians(chordStr);
                } else {
                    const dE = p2c.x - p1c.x;
                    const dN = p2c.y - p1c.y;
                    if (Math.hypot(dE, dN) > 1e-9) {
                        let az = Math.atan2(dE, dN);
                        if (az < 0) az += 2 * Math.PI;
                        chordRadArc = az;
                    }
                }
                const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                const tangentAngle = chordRadArc !== null
                    ? chordRadArc - signedDeltaArc / 2
                    : (selLine.tangentBearing ? parseBearingToRadians(selLine.tangentBearing) : null);
                if (tangentAngle !== null) {
                    const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                    const centerOffsetAngle = isLeftCurve ? canvasTangentAngle + Math.PI / 2 : canvasTangentAngle - Math.PI / 2;
                    const cX = p1c.x + radius * Math.cos(centerOffsetAngle);
                    const cY = p1c.y + radius * Math.sin(centerOffsetAngle);
                    const startAngle = Math.atan2(p1c.y - cY, p1c.x - cX);
                    const midAngle = isLeftCurve ? startAngle + centralAngle / 2 : startAngle - centralAngle / 2;
                    const midX = cX + radius * Math.cos(midAngle);
                    const midY = cY + radius * Math.sin(midAngle);

                    result.push({ id: `${selId}-start`, lineId: selId, type: 'start', x: p1c.x, y: p1c.y, line: selLine });
                    result.push({ id: `${selId}-mid`, lineId: selId, type: 'mid', x: midX, y: midY, line: selLine });
                    result.push({ id: `${selId}-end`, lineId: selId, type: 'end', x: p2c.x, y: p2c.y, line: selLine });
                    result.push({ id: `${selId}-center`, lineId: selId, type: 'center', x: cX, y: cY, line: selLine });
                }
            } else if (p2c) {
                result.push({ id: `${selId}-start`, lineId: selId, type: 'start', x: p1c.x, y: p1c.y, line: selLine });
                result.push({ id: `${selId}-mid`, lineId: selId, type: 'mid', x: (p1c.x + p2c.x) / 2, y: (p1c.y + p2c.y) / 2, line: selLine });
                result.push({ id: `${selId}-end`, lineId: selId, type: 'end', x: p2c.x, y: p2c.y, line: selLine });
            }
        });

        return result;
    }, [selectedLineIds, lines, pointMap]);

    const findGripNearClick = useCallback((screenX: number, screenY: number, thresholdPx = 9): EntityGrip | null => {
        const grips = getGripsForSelectedLines();
        let best: EntityGrip | null = null;
        let bestDistSq = thresholdPx * thresholdPx;

        for (const g of grips) {
            const sc = worldToScreen(g.x, g.y);
            const dSq = (screenX - sc.x) ** 2 + (screenY - sc.y) ** 2;
            if (dSq <= bestDistSq) {
                bestDistSq = dSq;
                best = g;
            }
        }
        return best;
    }, [getGripsForSelectedLines, worldToScreen]);

    const commitActiveGrip = useCallback((
        grip: EntityGrip,
        targetWorldX: number,
        targetWorldY: number,
        snapResult?: OsnapResult | null
    ) => {
        const selLine = grip.line;
        const lineId = grip.lineId;
        const finalX = snapResult ? snapResult.easting : targetWorldX;
        const finalY = snapResult ? snapResult.northing : targetWorldY;
        const snappedPn = snapResult?.sourcePoint?.pointNumber;

        const p1 = pointMap.get(selLine.from);
        const p2 = pointMap.get(selLine.to);
        const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : null);
        const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (selLine.toPt ? { x: selLine.toPt.x, y: selLine.toPt.y } : null);

        const isCircleEntity = selLine.isCircle || (selLine.isCurve && selLine.curveRadius && (!selLine.fromPt || (p1Coords && p2Coords && p1Coords.x === p2Coords.x && p1Coords.y === p2Coords.y)) && !selLine.tangentBearing && !selLine.chordBearing);

        if (isCircleEntity && selLine.curveRadius) {
            if (grip.type === 'center') {
                const changes: Partial<SurveyLine> = {
                    circleCenter: { x: finalX, y: finalY, z: 0 },
                    fromPt: { x: finalX, y: finalY, z: 0 },
                    toPt: { x: finalX, y: finalY, z: 0 },
                };
                if (snappedPn) {
                    changes.from = snappedPn;
                    changes.to = snappedPn;
                }
                props.onUpdateLine?.(lineId, changes);
            } else if (grip.type === 'quadrant') {
                const center = selLine.circleCenter || (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : p1Coords);
                if (center) {
                    const newR = Math.hypot(finalX - center.x, finalY - center.y);
                    if (newR > 1e-4) {
                        props.onUpdateLine?.(lineId, {
                            curveRadius: newR,
                            arcLength: 2 * Math.PI * newR,
                        });
                    }
                }
            }
        } else if (selLine.isCurve && selLine.curveRadius && p1Coords && p2Coords) {
            // Arc / Curve
            if (grip.type === 'start') {
                const dx = p2Coords.x - finalX;
                const dy = p2Coords.y - finalY;
                const newChordLen = Math.hypot(dx, dy);
                let az = Math.atan2(dx, dy);
                if (az < 0) az += 2 * Math.PI;
                const changes: Partial<SurveyLine> = {
                    fromPt: { x: finalX, y: finalY, z: 0 },
                    chordBearing: formatBearing(az),
                    distance: newChordLen.toFixed(2),
                };
                if (snappedPn) changes.from = snappedPn;
                props.onUpdateLine?.(lineId, changes);
            } else if (grip.type === 'end') {
                const dx = finalX - p1Coords.x;
                const dy = finalY - p1Coords.y;
                const newChordLen = Math.hypot(dx, dy);
                let az = Math.atan2(dx, dy);
                if (az < 0) az += 2 * Math.PI;
                const changes: Partial<SurveyLine> = {
                    toPt: { x: finalX, y: finalY, z: 0 },
                    chordBearing: formatBearing(az),
                    distance: newChordLen.toFixed(2),
                };
                if (snappedPn) changes.to = snappedPn;
                props.onUpdateLine?.(lineId, changes);
            } else if (grip.type === 'mid' || grip.type === 'center') {
                const deltaX = finalX - grip.x;
                const deltaY = finalY - grip.y;
                props.onUpdateLine?.(lineId, {
                    fromPt: { x: p1Coords.x + deltaX, y: p1Coords.y + deltaY, z: 0 },
                    toPt: { x: p2Coords.x + deltaX, y: p2Coords.y + deltaY, z: 0 },
                });
            }
        } else if (p1Coords && p2Coords) {
            // Straight Line
            if (grip.type === 'start') {
                const dx = p2Coords.x - finalX;
                const dy = p2Coords.y - finalY;
                const newDist = Math.hypot(dx, dy);
                let az = Math.atan2(dx, dy);
                if (az < 0) az += 2 * Math.PI;
                const changes: Partial<SurveyLine> = {
                    fromPt: { x: finalX, y: finalY, z: 0 },
                    bearing: formatBearing(az),
                    distance: newDist.toFixed(2),
                };
                if (snappedPn) changes.from = snappedPn;
                props.onUpdateLine?.(lineId, changes);
            } else if (grip.type === 'end') {
                const dx = finalX - p1Coords.x;
                const dy = finalY - p1Coords.y;
                const newDist = Math.hypot(dx, dy);
                let az = Math.atan2(dx, dy);
                if (az < 0) az += 2 * Math.PI;
                const changes: Partial<SurveyLine> = {
                    toPt: { x: finalX, y: finalY, z: 0 },
                    bearing: formatBearing(az),
                    distance: newDist.toFixed(2),
                };
                if (snappedPn) changes.to = snappedPn;
                props.onUpdateLine?.(lineId, changes);
            } else if (grip.type === 'mid') {
                const deltaX = finalX - grip.x;
                const deltaY = finalY - grip.y;
                props.onUpdateLine?.(lineId, {
                    fromPt: { x: p1Coords.x + deltaX, y: p1Coords.y + deltaY, z: 0 },
                    toPt: { x: p2Coords.x + deltaX, y: p2Coords.y + deltaY, z: 0 },
                });
            }
        }

        setActiveGrip(null);
        setHoveredGrip(null);
        requestAnimationFrame(() => drawRef.current?.());
    }, [pointMap, props]);

    const zoomExtents = useCallback((padding = 0.1) => {
        const visibleBoundaryFiles = (boundaryFiles ?? []).filter(bf => !bf.hidden && bf.calls.length > 0);
        if (points.length === 0 && lines.length === 0 && centerlines.length === 0 && visibleBoundaryFiles.length === 0) return;
        const container = containerRef.current;
        if (!container) return;

        let minN = Infinity, maxN = -Infinity, minE = Infinity, maxE = -Infinity;

        points.forEach(p => {
            minN = Math.min(minN, p.northing); maxN = Math.max(maxN, p.northing);
            minE = Math.min(minE, p.easting); maxE = Math.max(maxE, p.easting);
        });
        
        const linePoints: Array<{ easting: number; northing: number }> = [];
        lines.forEach(line => {
            const p1 = pointMap.get(line.from);
            const p2 = pointMap.get(line.to);
            const p1Coords = p1
                ? { easting: p1.easting, northing: p1.northing }
                : (line.fromPt ? { easting: line.fromPt.x, northing: line.fromPt.y } : null);
            const p2Coords = p2
                ? { easting: p2.easting, northing: p2.northing }
                : (line.toPt ? { easting: line.toPt.x, northing: line.toPt.y } : null);
            if (p1Coords) linePoints.push(p1Coords);
            if (p2Coords) linePoints.push(p2Coords);
        });
        linePoints.forEach(p => {
             minN = Math.min(minN, p.northing); maxN = Math.max(maxN, p.northing);
             minE = Math.min(minE, p.easting); maxE = Math.max(maxE, p.easting);
        });
        
        // Also account for curve and circle extents in survey lines
        lines.forEach(line => {
            const p1 = pointMap.get(line.from);
            const p2 = pointMap.get(line.to);
            const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
            const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);

            const isCircleEntity = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (p1Coords && p2Coords && p1Coords.x === p2Coords.x && p1Coords.y === p2Coords.y)) && !line.tangentBearing && !line.chordBearing);
            if (isCircleEntity && line.curveRadius) {
                const center = line.circleCenter || (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : p1Coords);
                if (center) {
                    const r = line.curveRadius;
                    minN = Math.min(minN, center.y - r);
                    maxN = Math.max(maxN, center.y + r);
                    minE = Math.min(minE, center.x - r);
                    maxE = Math.max(maxE, center.x + r);
                }
            } else if ((line.isCurve || line.curveRadius) && line.curveRadius && line.arcLength) {
                if (p1) {
                    const radius = line.curveRadius;
                    
                    // Approximate bounds by adding radius to both endpoints
                    minN = Math.min(minN, p1.northing - radius, p1.northing + radius);
                    maxN = Math.max(maxN, p1.northing - radius, p1.northing + radius);
                    minE = Math.min(minE, p1.easting - radius, p1.easting + radius);
                    maxE = Math.max(maxE, p1.easting - radius, p1.easting + radius);
                }
                if (p2) {
                    const radius = line.curveRadius;
                    minN = Math.min(minN, p2.northing - radius, p2.northing + radius);
                    maxN = Math.max(maxN, p2.northing - radius, p2.northing + radius);
                    minE = Math.min(minE, p2.easting - radius, p2.easting + radius);
                    maxE = Math.max(maxE, p2.easting - radius, p2.easting + radius);
                }
            }
        });
        
        centerlines.forEach(cl => {
            cl.pis.forEach(p => {
                minN = Math.min(minN, p.northing); maxN = Math.max(maxN, p.northing);
                minE = Math.min(minE, p.easting); maxE = Math.max(maxE, p.easting);
            });

            // Also account for curve extents
            for (let i = 1; i < cl.pis.length - 1; i++) {
                const p_prev = cl.pis[i-1];
                const p_curr = cl.pis[i];
                const p_next = cl.pis[i+1];

                if (p_curr.curveRadius) {
                    const curve = calculateCurveGeometry(p_prev, p_curr, p_next, p_curr.curveRadius);
                    if (curve) {
                        minN = Math.min(minN, curve.pcNorthing, curve.ptNorthing);
                        maxN = Math.max(maxN, curve.pcNorthing, curve.ptNorthing);
                        minE = Math.min(minE, curve.pcEasting, curve.ptEasting);
                        maxE = Math.max(maxE, curve.pcEasting, curve.ptEasting);
                        
                        const { centerEasting: cx, centerNorthing: cy, radius: r, startAngle, endAngle } = curve;
                        const cardinalAngles = [
                            { angle: 0, update: () => maxE = Math.max(maxE, cx + r) }, // East
                            { angle: Math.PI / 2, update: () => maxN = Math.max(maxN, cy + r) }, // North
                            { angle: Math.PI, update: () => minE = Math.min(minE, cx - r) }, // West
                            { angle: 3 * Math.PI / 2, update: () => minN = Math.min(minN, cy - r) } // South
                        ];
                        
                        const normalize = (angle: number) => (angle + 2 * Math.PI) % (2 * Math.PI);
                        const s = normalize(startAngle);
                        const e = normalize(endAngle);
                        const isCcw = !curve.turnRight;

                        cardinalAngles.forEach(item => {
                            const angle = item.angle;
                            let isBetween = false;
                            
                            if (isCcw) {
                                if (s < e) { isBetween = angle >= s && angle <= e; } 
                                else { isBetween = angle >= s || angle <= e; }
                            } else { // Clockwise
                                if (s > e) { isBetween = angle <= s && angle >= e; } 
                                else { isBetween = angle <= s || angle >= e; }
                            }
                            
                            if (isBetween) {
                                item.update();
                            }
                        });
                    }
                }
            }
        });

        // --- Boundary file amber overlays ---
        // Walk each visible BoundaryFile using the same synth-point logic as the
        // draw pass so zoom-extents works before survey points are assigned.
        visibleBoundaryFiles.forEach(bf => {
            const synthMap = new Map<string, { easting: number; northing: number }>();
            const lookupPt = (pn: string | undefined): { easting: number; northing: number } | undefined => {
                if (!pn) return undefined;
                const pm = pointMap.get(pn);
                if (pm) return { easting: pm.easting, northing: pm.northing };
                return synthMap.get(pn);
            };
            const firstCall = bf.calls[0];
            const pobFromMap = firstCall.from ? pointMap.get(firstCall.from) : undefined;
            const pob: { easting: number; northing: number } = pobFromMap
                ? { easting: pobFromMap.easting, northing: pobFromMap.northing }
                : bf.pobOverride
                ? { easting: bf.pobOverride.easting, northing: bf.pobOverride.northing }
                : { easting: 0, northing: 0 };
            if (firstCall.from) synthMap.set(firstCall.from, pob);
            let cursor = pob;
            for (const call of bf.calls) {
                const fromHere = lookupPt(call.from) ?? cursor;
                if (!fromHere) continue;
                let endPt: { easting: number; northing: number } | null = null;
                if (call.isCurve && typeof call.curveRadius === 'number' && isFinite(call.curveRadius)
                    && typeof call.arcLength === 'number' && isFinite(call.arcLength) && call.arcLength !== 0) {
                    const centralAngle = call.arcLength / call.curveRadius;
                    const chordStr = call.chordBearing || call.bearing;
                    const chordRad = chordStr ? parseBearingToRadians(chordStr) : null;
                    if (chordRad !== null) {
                        endPt = directCurve(fromHere, chordRad - centralAngle / 2, call.curveRadius, centralAngle);
                    }
                } else {
                    const bStr = call.bearing || call.chordBearing;
                    const bRad = bStr ? parseBearingToRadians(bStr) : null;
                    const dist = call.distance
                        ? parseDistance(call.distance)
                        : (typeof (call as any).distanceFt === 'number' ? (call as any).distanceFt : null);
                    if (bRad !== null && typeof dist === 'number' && isFinite(dist)) {
                        endPt = direct(fromHere, bRad, dist);
                    }
                }
                if (endPt) {
                    if (call.to && !lookupPt(call.to)) synthMap.set(call.to, endPt);
                    cursor = endPt;
                }
            }
            // Apply the per-file transform (translate + rotate) the same way the draw pass does,
            // then fold each transformed point into the global bbox.
            const bfFirstPt = lookupPt(bf.calls[0].from);
            const bfTxE = bf.translationE ?? 0;
            const bfTxN = bf.translationN ?? 0;
            const bfRotRad = ((bf.rotationDeg ?? 0) * Math.PI) / 180;
            const hasTransform = (bfTxE !== 0 || bfTxN !== 0 || bfRotRad !== 0) && !!bfFirstPt;
            synthMap.forEach(p => {
                let e = p.easting, n = p.northing;
                if (hasTransform && bfFirstPt) {
                    const dx = e - bfFirstPt.easting;
                    const dy = n - bfFirstPt.northing;
                    e = bfFirstPt.easting + (bfTxE) + dx * Math.cos(bfRotRad) - dy * Math.sin(bfRotRad);
                    n = bfFirstPt.northing + (bfTxN) + dx * Math.sin(bfRotRad) + dy * Math.cos(bfRotRad);
                }
                minN = Math.min(minN, n); maxN = Math.max(maxN, n);
                minE = Math.min(minE, e); maxE = Math.max(maxE, e);
            });
        });

        if (minN === Infinity) return;

        const dataWidth = maxE - minE;
        const dataHeight = maxN - minN;
        
        // FIX: Handle cases where data has no spatial extent (e.g. single point) to prevent division by zero.
        if (dataWidth < 1e-9 && dataHeight < 1e-9) {
            const defaultScale = 0.5; // Apply a reasonable default zoom level
            const newOffsetX = container.clientWidth / 2 - minE * defaultScale;
            // Center vertically on the point's northing.
            const newOffsetY = container.clientHeight / 2 + minN * defaultScale; 
            setTransform({ scale: defaultScale, offsetX: newOffsetX, offsetY: newOffsetY });
            return;
        }

        const scaleX = container.clientWidth / (dataWidth * (1 + padding));
        const scaleY = container.clientHeight / (dataHeight * (1 + padding));
        const newScale = Math.min(scaleX, scaleY);
        
        const newOffsetX = (container.clientWidth - dataWidth * newScale) / 2 - minE * newScale;
        const newOffsetY = (container.clientHeight - dataHeight * newScale) / 2 + maxN * newScale;
        
        setTransform({ scale: newScale, offsetX: newOffsetX, offsetY: newOffsetY });
    }, [points, lines, centerlines, pointMap, boundaryFiles]);

    /** Zoom to a specific bounding box in project coordinates — avoids the zoom-to-all issue when GIS layers are loaded. */
    const zoomToBbox = useCallback((minE: number, maxE: number, minN: number, maxN: number, padding = 0.15) => {
        const container = containerRef.current;
        if (!container) return;
        const rangeE = maxE - minE;
        const rangeN = maxN - minN;
        if (rangeE < 1e-9 && rangeN < 1e-9) {
            // Single point or zero-size: just centre on it at a reasonable scale
            const scale = 8;
            const cx = (minE + maxE) / 2;
            const cy = (minN + maxN) / 2;
            setTransform({
                scale,
                offsetX: container.clientWidth / 2 - cx * scale,
                offsetY: container.clientHeight / 2 + cy * scale,
            });
            return;
        }
        const scaleX = container.clientWidth  / (rangeE * (1 + padding));
        const scaleY = container.clientHeight / (rangeN * (1 + padding));
        const scale   = Math.min(scaleX, scaleY);
        const offsetX = (container.clientWidth  - rangeE * scale) / 2 - minE * scale;
        const offsetY = (container.clientHeight - rangeN * scale) / 2 + maxN * scale;
        setTransform({ scale, offsetX, offsetY });
    }, []);

    const redraw = useCallback(() => {
        // This is a dummy function to trigger a re-render if needed, but the main draw effect depends on props.
        // We can force a re-render by updating a dummy state if necessary, but it's usually not.
        // For now, `draw` is called via useEffect on prop changes, which is sufficient.
    }, []);

    useImperativeHandle(ref, () => ({
        zoomExtents,
        zoomToBbox,
        redraw,
        finishDrawing,
        cancelDeleteMode: () => {
            setIsDeletingLines(false);
        },
        confirmDelete: () => {
            // No longer needed - lines delete immediately
            setIsDeletingLines(false);
        },
        getTransform: () => transformRef.current,
        setTransform: (newTransform: { scale: number; offsetX: number; offsetY: number }) => {
            setTransform(newTransform);
        },
        beginOtOriginPick: () => {
            setIsPickingOtOrigin(true);
            setIsPickingOtLineAlign(false);
        },
        beginOtLineAlign: () => {
            setIsPickingOtLineAlign(true);
            setIsPickingOtOrigin(false);
        },
        getViewCenterWorld: () => {
            const container = containerRef.current;
            const w = container?.clientWidth || 800;
            const h = container?.clientHeight || 600;
            return screenToWorld(w / 2, h / 2);
        },
        isPointInView: (easting: number, northing: number) => {
            const screen = worldToScreen(easting, northing);
            const container = containerRef.current;
            const w = container?.clientWidth || 800;
            const h = container?.clientHeight || 600;
            return screen.x >= 0 && screen.x <= w && screen.y >= 0 && screen.y <= h;
        },
        isBboxInView: (minE: number, maxE: number, minN: number, maxN: number) => {
            const p1 = worldToScreen(minE, minN);
            const p2 = worldToScreen(maxE, maxN);
            const container = containerRef.current;
            const w = container?.clientWidth || 800;
            const h = container?.clientHeight || 600;
            const sMinX = Math.min(p1.x, p2.x);
            const sMaxX = Math.max(p1.x, p2.x);
            const sMinY = Math.min(p1.y, p2.y);
            const sMaxY = Math.max(p1.y, p2.y);
            return sMaxX >= 0 && sMinX <= w && sMaxY >= 0 && sMinY <= h;
        },
        getViewportBboxWorld: () => {
            const container = containerRef.current;
            const w = container?.clientWidth || 800;
            const h = container?.clientHeight || 600;
            const p1 = screenToWorld(0, 0);
            const p2 = screenToWorld(w, h);
            const minE = Math.min(p1.easting, p2.easting);
            const maxE = Math.max(p1.easting, p2.easting);
            const minN = Math.min(p1.northing, p2.northing);
            const maxN = Math.max(p1.northing, p2.northing);
            return {
                minE, maxE, minN, maxN,
                minX: minE, maxX: maxE, minY: minN, maxY: maxN,
            };
        },
        getMapOverlayTiles: () => {
            const tiles: Array<{ image: CanvasImageSource; bbox: [number, number, number, number]; quad?: WorldQuad; opacity?: number }> = [];
            if (googleOverlayImage) {
                const sourceTiles = googleOverlayImage.tiles && googleOverlayImage.tiles.length > 0
                    ? googleOverlayImage.tiles
                    : [{ image: googleOverlayImage.image, bbox: googleOverlayImage.bbox, quad: undefined }];
                for (const t of sourceTiles) {
                    tiles.push({ image: t.image, bbox: t.bbox, quad: t.quad, opacity: googleOverlayImage.opacity });
                }
            } else if (overlayTilesRef.current.length > 0) {
                for (const t of overlayTilesRef.current) {
                    tiles.push({ image: t.image, bbox: t.bbox, quad: t.quad, opacity: 0.95 });
                }
            }
            for (const w of wmsImages) {
                tiles.push({ image: w.image, bbox: w.bbox, quad: w.quad, opacity: w.opacity });
            }
            return tiles;
        },
    }));
    
    useEffect(() => {
        const imageMap = new Map<string, HTMLImageElement>();
        let isMounted = true;
    
        const promises = offlineMapAreas.flatMap(area =>
          area.layers.map(layer => {
            const key = `${area.id}-${layer.layerName}`;
            if (offlineImages.has(key)) {
              imageMap.set(key, offlineImages.get(key)!);
              return Promise.resolve();
            }
            return new Promise<void>(resolve => {
              const img = new Image();
              img.onload = () => {
                if (isMounted) {
                  imageMap.set(key, img);
                }
                resolve();
              };
              img.onerror = () => resolve(); // Resolve even on error
              img.src = layer.imageData;
            });
          })
        );
    
        Promise.all(promises).then(() => {
          if (isMounted) {
            setOfflineImages(new Map(imageMap));
          }
        });
    
        return () => { isMounted = false; };
      }, [offlineMapAreas]);
    
    const wmsSeqRef = useRef(0);
    const fetchWmsImages = useMemo(() => debounce(async (
        currentTransform: typeof transform,
        currentActiveWmsLayers: ActiveWmsLayer[],
        currentWmsServices: WmsService[],
        currentSettings: Settings,
        canvasElement: HTMLCanvasElement | null
    ) => {
        const seq = ++wmsSeqRef.current;
        if (!canvasElement || !currentSettings.projection.epsg) {
            setWmsImages([]);
            return;
        }

        const { scale, offsetX, offsetY } = currentTransform;
        const width = canvasElement.clientWidth || canvasElement.width;
        const height = canvasElement.clientHeight || canvasElement.height;

        const minE_view = (0 - offsetX) / scale;
        const maxE_view = (width - offsetX) / scale;
        const maxN_view = -(0 - offsetY) / scale;
        const minN_view = -(height - offsetY) / scale;
        const viewBboxArray: [number, number, number, number] = [minE_view, minN_view, maxE_view, maxN_view];
        const projectCrs = `EPSG:${currentSettings.projection.epsg}`;

        const activeVisibleLayers = currentActiveWmsLayers.filter(l => l.isVisible);
        if (activeVisibleLayers.length === 0) {
            setWmsImages([]);
            return;
        }

        const dpr = Math.min(2, window.devicePixelRatio || 1);
        const tiles = planMapTiles(viewBboxArray, width * dpr);

        type WmsTileJob = { layer: ActiveWmsLayer; service: WmsService; crs: string; url: string; key: string; bbox: [number, number, number, number]; quad?: WorldQuad };
        const jobs: WmsTileJob[] = [];

        for (const layer of activeVisibleLayers) {
            const service = currentWmsServices.find(s => s.url === layer.serviceUrl);
            const layerInfo = service?.layers.find(l => l.name === layer.layerName);
            if (!service || !layerInfo) continue;

            const isProjectCrsSupported = layerInfo.supportedCrs.some(crs => crs.includes(String(currentSettings.projection.epsg)));
            let requestCrs = projectCrs;
            if (!isProjectCrsSupported) {
                const fallback = ['EPSG:3857', 'EPSG:4326'].find(crs =>
                    layerInfo.supportedCrs.some(supported => supported.includes(crs.split(':')[1]))
                );
                if (!fallback) {
                    console.warn(`Layer ${layer.layerName} from ${service.title} supports neither project CRS nor common web CRSs.`);
                    continue;
                }
                requestCrs = fallback;
            }

            for (const tile of tiles) {
                let requestBbox = tile.bbox;
                if (requestCrs !== projectCrs) {
                    try {
                        const corners = [
                            [tile.bbox[0], tile.bbox[1]], [tile.bbox[2], tile.bbox[1]],
                            [tile.bbox[0], tile.bbox[3]], [tile.bbox[2], tile.bbox[3]],
                        ].map(c => proj4(projectCrs, requestCrs, c));
                        requestBbox = [
                            Math.min(...corners.map(c => c[0])), Math.min(...corners.map(c => c[1])),
                            Math.max(...corners.map(c => c[0])), Math.max(...corners.map(c => c[1])),
                        ];
                    } catch (e) {
                        console.error(`Projection from ${projectCrs} to ${requestCrs} failed:`, e);
                        continue;
                    }
                }

                // WMS 1.3.0 uses lat,lon axis order for EPSG:4326.
                const [bx0, by0, bx1, by1] = requestBbox;
                const bboxString = (requestCrs === 'EPSG:4326' ? [by0, bx0, by1, bx1] : [bx0, by0, bx1, by1]).join(',');

                const wmsUrl = new URL(service.url);
                wmsUrl.searchParams.set('service', 'WMS');
                wmsUrl.searchParams.set('request', 'GetMap');
                wmsUrl.searchParams.set('layers', layer.layerName);
                wmsUrl.searchParams.set('styles', '');
                wmsUrl.searchParams.set('version', '1.3.0');
                wmsUrl.searchParams.set('width', String(tile.sizePx));
                wmsUrl.searchParams.set('height', String(tile.sizePx));
                wmsUrl.searchParams.set('CRS', requestCrs);
                wmsUrl.searchParams.set('bbox', bboxString);
                wmsUrl.searchParams.set('format', 'image/png');
                wmsUrl.searchParams.set('transparent', 'true');

                let drawBbox = tile.bbox;
                let quad: WorldQuad | undefined;
                if (requestCrs !== projectCrs) {
                    // The fetched rectangle is axis-aligned in the request CRS, so it is a rotated quad in project coordinates.
                    try {
                        const [qx0, qy0, qx1, qy1] = requestBbox;
                        quad = [
                            proj4(requestCrs, projectCrs, [qx0, qy1]) as [number, number],
                            proj4(requestCrs, projectCrs, [qx1, qy1]) as [number, number],
                            proj4(requestCrs, projectCrs, [qx1, qy0]) as [number, number],
                            proj4(requestCrs, projectCrs, [qx0, qy0]) as [number, number],
                        ];
                        const xs = quad.map(c => c[0]);
                        const ys = quad.map(c => c[1]);
                        drawBbox = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
                    } catch {
                        continue;
                    }
                }

                jobs.push({ layer, service, crs: requestCrs, url: wmsUrl.toString(), key: `wms|${wmsUrl.toString()}`, bbox: drawBbox, quad });
            }
        }

        const loaded = await mapWithConcurrency(jobs, 3, async job => {
            if (seq !== wmsSeqRef.current) return null;
            try {
                const bitmap = await getCachedBitmap(job.key, () => fetchImageBlobViaProxy(job.url));
                return { image: bitmap as CanvasImageSource, bbox: job.bbox, quad: job.quad, opacity: job.layer.opacity };
            } catch (err) {
                console.error(`[WMS] Tile failed for ${job.service.title} / ${job.layer.layerName}`, err);
                return null;
            }
        });

        if (seq !== wmsSeqRef.current) return;
        setWmsImages(loaded.filter((t): t is { image: CanvasImageSource; bbox: [number, number, number, number]; quad?: WorldQuad; opacity: number } => t !== null));
    }, 500), []);


    useEffect(() => {
        fetchWmsImages(transform, activeWmsLayers, wmsServices, settings, canvasRef.current);
    }, [transform, activeWmsLayers, wmsServices, settings, fetchWmsImages]);

    const fetchGoogleOverlayImage = useMemo(() => debounce(async (
        currentTransform: typeof transform,
        currentSettings: Settings,
        canvasElement: HTMLCanvasElement | null
    ) => {
        const seq = ++googleOverlaySeqRef.current;
        const gm = currentSettings.googleMaps;
        if (!canvasElement || !gm?.enabled) {
            setGoogleOverlayImage(null);
            setGoogleOverlayDiagnostic(null);
            return;
        }
        if (!currentSettings.projection.epsg) {
            setGoogleOverlayImage(null);
            setGoogleOverlayDiagnostic({
                tone: 'info',
                message: 'Map imagery is hidden: set a project EPSG in Settings. WMS layers require this too.',
            });
            return;
        }

        const { scale, offsetX, offsetY } = currentTransform;
        const { width, height } = canvasElement;
        const minEView = (0 - offsetX) / scale;
        const maxEView = (width - offsetX) / scale;
        const maxNView = -(0 - offsetY) / scale;
        const minNView = -(height - offsetY) / scale;

        const projectCrs = `EPSG:${currentSettings.projection.epsg}`;
        let cornersWgs84: Array<[number, number]>;
        try {
            cornersWgs84 = [
                proj4(projectCrs, 'EPSG:4326', [minEView, minNView]) as [number, number],
                proj4(projectCrs, 'EPSG:4326', [maxEView, minNView]) as [number, number],
                proj4(projectCrs, 'EPSG:4326', [maxEView, maxNView]) as [number, number],
                proj4(projectCrs, 'EPSG:4326', [minEView, maxNView]) as [number, number],
            ];
            if (cornersWgs84.some(([lon, latVal]) => !Number.isFinite(lon) || !Number.isFinite(latVal))) {
                setGoogleOverlayImage(null);
                setGoogleOverlayDiagnostic({
                    tone: 'error',
                    message: 'Map imagery reprojection failed: non-finite coordinates. Verify EPSG and georeferenced point values.',
                });
                return;
            }
        } catch (e) {
            console.warn('[GoogleOverlay] View reprojection to WGS84 failed:', e);
            setGoogleOverlayImage(null);
            setGoogleOverlayDiagnostic({
                tone: 'error',
                message: 'Map imagery reprojection failed. Verify project EPSG and coordinate units.',
            });
            return;
        }

        const lons = cornersWgs84.map(c => c[0]);
        const lats = cornersWgs84.map(c => c[1]);
        const wgsBbox: [number, number, number, number] = [
            Math.min(...lons),
            Math.min(...lats),
            Math.max(...lons),
            Math.max(...lats),
        ];
        const lat = (wgsBbox[1] + wgsBbox[3]) / 2;
        const lng = (wgsBbox[0] + wgsBbox[2]) / 2;
        if (!Number.isFinite(lat) || !Number.isFinite(lng) || lat < -90 || lat > 90 || lng < -180 || lng > 180) {
            setGoogleOverlayImage(null);
            setGoogleOverlayDiagnostic({
                tone: 'error',
                message: 'Map imagery is out of range after reprojection. Verify EPSG matches your drawing coordinates.',
            });
            return;
        }
        const zoom = zoomForBboxWgs84(wgsBbox);

        const selectedMapType = gm.mapType ?? 'naip';
        const resolvedMapType: StaticMapRequest['maptype'] =
            selectedMapType === 'hybrid'
                ? ((gm.showLabels ?? true) ? 'hybrid' : 'satellite')
                : selectedMapType;

        // ── CRS-native NAIP path ──────────────────────────────────────────
        // Request the imagery already reprojected into the project CRS for the
        // exact current view, then draw it 1:1. This removes the zoom-dependent
        // misalignment caused by stretching a geographic (lat/lng) tile onto a
        // grid-coordinate rectangle. Only NAIP can be reprojected server-side;
        // Google static tiles stay on the legacy center/zoom path below.
        if (resolvedMapType === 'naip') {
            const projWidth = maxEView - minEView;
            const projHeight = maxNView - minNView;
            if (!(projWidth > 0) || !(projHeight > 0)) {
                setGoogleOverlayImage(null);
                return;
            }

            // Fixed power-of-two tiles in project coordinates: zooming back to an
            // earlier level asks for the same tiles, which come from the cache.
            const naipPxWidth = Math.min(canvasElement.width, (canvasElement.clientWidth || canvasElement.width) * 2);
            const naipTiles = planMapTiles([minEView, minNView, maxEView, maxNView], naipPxWidth, { tilePx: 1024, maxTiles: 16 });
            const naipOpacity = gm.opacity ?? 1.0;
            const naipEpsg = currentSettings.projection.epsg;
            type NaipTile = { image: HTMLImageElement; bbox: [number, number, number, number]; quad?: WorldQuad; source: 'user-maps' | 'user-gemini' | 'proxy' | 'naip-proxy' };

            const loadNaipTile = async (
                reqBbox: [number, number, number, number],
                reqSr: number,
                extentToProject: boolean,
            ): Promise<NaipTile> => {
                const reqW = reqBbox[2] - reqBbox[0];
                const reqH = reqBbox[3] - reqBbox[1];
                const aspect = reqW / reqH;
                const long = 1024;
                const outW = aspect >= 1 ? long : Math.max(1, Math.round(long * aspect));
                const outH = aspect >= 1 ? Math.max(1, Math.round(long / aspect)) : long;
                const staticMap = await trackTileLoad(() => getStaticMap({
                    lat,
                    lng,
                    width: outW,
                    height: outH,
                    maptype: 'naip',
                    projectedBbox: reqBbox,
                    sr: reqSr,
                }));
                const image = await decodeImageCached(staticMap.dataUrl);
                let drawBbox: [number, number, number, number] = staticMap.extent ?? reqBbox;
                let quad: WorldQuad | undefined;
                if (extentToProject) {
                    // The image is an axis-aligned Mercator rectangle, which is a rotated quad in project coordinates.
                    const srCrs = `EPSG:${reqSr}`;
                    const [exMinX, exMinY, exMaxX, exMaxY] = staticMap.extent ?? reqBbox;
                    quad = [
                        proj4(srCrs, projectCrs, [exMinX, exMaxY]) as [number, number],
                        proj4(srCrs, projectCrs, [exMaxX, exMaxY]) as [number, number],
                        proj4(srCrs, projectCrs, [exMaxX, exMinY]) as [number, number],
                        proj4(srCrs, projectCrs, [exMinX, exMinY]) as [number, number],
                    ];
                    const xs = quad.map(c => c[0]);
                    const ys = quad.map(c => c[1]);
                    drawBbox = [Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)];
                }
                return { image, bbox: drawBbox, quad, source: staticMap.source };
            };

            // Many local/State-Plane EPSGs are rejected by the USGS ImageServer;
            // Web Mercator always works, so remember which EPSGs need it.
            const fetchNaipTile = async (tile: PlannedTile): Promise<NaipTile> => {
                const viaMercator = async (): Promise<NaipTile> => {
                    const [x0, y0, x1, y1] = tile.bbox;
                    const c = [
                        proj4(projectCrs, 'EPSG:3857', [x0, y0]) as [number, number],
                        proj4(projectCrs, 'EPSG:3857', [x1, y0]) as [number, number],
                        proj4(projectCrs, 'EPSG:3857', [x1, y1]) as [number, number],
                        proj4(projectCrs, 'EPSG:3857', [x0, y1]) as [number, number],
                    ];
                    if (c.some(([x, y]) => !Number.isFinite(x) || !Number.isFinite(y))) {
                        throw new Error('Web Mercator reprojection produced non-finite coordinates');
                    }
                    const xs = c.map(p => p[0]);
                    const ys = c.map(p => p[1]);
                    return loadNaipTile([Math.min(...xs), Math.min(...ys), Math.max(...xs), Math.max(...ys)], 3857, true);
                };
                if (naipMercatorEpsgRef.current.has(naipEpsg)) return viaMercator();
                try {
                    return await loadNaipTile(tile.bbox, naipEpsg, false);
                } catch (firstErr) {
                    try {
                        const result = await viaMercator();
                        naipMercatorEpsgRef.current.add(naipEpsg);
                        return result;
                    } catch {
                        throw firstErr;
                    }
                }
            };

            const arrived: NaipTile[] = [];
            let lastNaipError: unknown = null;
            const publishNaip = (final: boolean) => {
                if (seq !== googleOverlaySeqRef.current) return;
                // Translucent overlays would double-darken where tiles overlap, so only show a full set.
                if (naipOpacity < 0.99 && !final) return;
                const freshKeys = new Set(arrived.map(t => t.bbox.join(',')));
                const kept = naipOpacity >= 0.99
                    ? overlayTilesRef.current.filter(t => !freshKeys.has(t.bbox.join(',')))
                    : [];
                const merged = [...kept, ...arrived].slice(-48);
                if (merged.length === 0) return;
                overlayTilesRef.current = merged;
                const top = merged[merged.length - 1];
                setGoogleOverlayImage({
                    image: top.image,
                    bbox: top.bbox,
                    tiles: merged.map(t => ({ image: t.image, bbox: t.bbox, quad: t.quad })),
                    opacity: naipOpacity,
                    source: top.source,
                    mapType: 'naip',
                });
                setGoogleOverlayDiagnostic(null);
            };

            await mapWithConcurrency(naipTiles, 3, async tile => {
                if (seq !== googleOverlaySeqRef.current) return;
                try {
                    arrived.push(await fetchNaipTile(tile));
                    publishNaip(false);
                } catch (err) {
                    lastNaipError = err;
                }
            });
            if (seq !== googleOverlaySeqRef.current) return;

            if (arrived.length === 0) {
                console.warn('[GoogleOverlay] NAIP overlay fetch failed:', lastNaipError);
                overlayTilesRef.current = [];
                setGoogleOverlayImage(null);
                const detail = lastNaipError instanceof Error && lastNaipError.message ? ` ${lastNaipError.message}` : '';
                setGoogleOverlayDiagnostic({ tone: 'error', message: `Map imagery fetch failed.${detail}` });
                return;
            }
            publishNaip(true);
            return;
        }

        try {
            const staticMap = await getStaticMap(
                {
                    lat,
                    lng,
                    zoom,
                    width: 640,
                    height: 640,
                    maptype: resolvedMapType,
                },
                {
                    mapsKey: gm.apiKey || undefined,
                    geminiKey: currentSettings.userApiKey || undefined,
                }
            );

            const img = await decodeImageCached(staticMap.dataUrl);

            const imageWgs = bboxFromCenterZoom(lat, lng, zoom, 640, 640);
            const projectedCorners = [
                proj4('EPSG:4326', projectCrs, [imageWgs.west, imageWgs.south]) as [number, number],
                proj4('EPSG:4326', projectCrs, [imageWgs.east, imageWgs.south]) as [number, number],
                proj4('EPSG:4326', projectCrs, [imageWgs.east, imageWgs.north]) as [number, number],
                proj4('EPSG:4326', projectCrs, [imageWgs.west, imageWgs.north]) as [number, number],
            ];
            const eastings = projectedCorners.map(p => p[0]);
            const northings = projectedCorners.map(p => p[1]);
            if (seq !== googleOverlaySeqRef.current) return;

            setGoogleOverlayImage({
                image: img,
                bbox: [Math.min(...eastings), Math.min(...northings), Math.max(...eastings), Math.max(...northings)],
                opacity: gm.opacity ?? 1.0,
                source: staticMap.source,
                mapType: resolvedMapType,
            });
            setGoogleOverlayDiagnostic(null);
        } catch (e) {
            if (seq !== googleOverlaySeqRef.current) return;
            console.warn('[GoogleOverlay] fetch failed:', e);
            setGoogleOverlayDiagnostic(e instanceof Error ? e.message : String(e));
        }
    }, 500), []);

    useEffect(() => {
        let timer: number | undefined;
        const sync = () => {
            const n = getPendingTileCount();
            if (n === 0) {
                if (timer !== undefined) {
                    window.clearTimeout(timer);
                    timer = undefined;
                }
                setPendingTiles(0);
                return;
            }
            // Delay first appearance so cache hits don't flash the indicator.
            setPendingTiles(prev => (prev > 0 ? n : prev));
            if (timer === undefined) {
                timer = window.setTimeout(() => {
                    timer = undefined;
                    setPendingTiles(getPendingTileCount());
                }, 250);
            }
        };
        const unsubscribe = subscribeTileActivity(sync);
        return () => {
            unsubscribe();
            if (timer !== undefined) window.clearTimeout(timer);
        };
    }, []);

    useEffect(() => {
        fetchGoogleOverlayImage(transform, settings, canvasRef.current);
    }, [transform, settings, fetchGoogleOverlayImage]);

    // Auto-fit the view to all points **only on the first non-empty load**, not
    // every time the point count changes. Previous code re-ran on every
    // points.length delta, which fought against per-operation zoom calls
    // (e.g. Draw Boundary's zoomToBbox) and zoomed the user out into a
    // state-plane sized void whenever new geometry landed on the canvas.
    useEffect(() => {
        if (!hasAutoFitOnceRef.current && points.length > 0) {
            hasAutoFitOnceRef.current = true;
            zoomExtents();
        }
        // Reset the latch when the canvas is cleared so the next load re-fits if no initialTransform.
        if (points.length === 0 && !initialTransform) {
            hasAutoFitOnceRef.current = false;
        }
    }, [points.length, initialTransform, zoomExtents]);
    
     useEffect(() => {
        if (zoomTarget) {
            const { northing, easting } = zoomTarget;
            const newScale = settings.zoomToPointLevel;

            const newOffsetX = containerRef.current!.clientWidth / 2 - easting * newScale;
            const newOffsetY = containerRef.current!.clientHeight / 2 + northing * newScale;

            setTransform({ scale: newScale, offsetX: newOffsetX, offsetY: newOffsetY });
            onZoomComplete();
        }
    }, [zoomTarget, onZoomComplete, settings.zoomToPointLevel]);


    const draw = useCallback(() => {
        const canvas = canvasRef.current;
        // v26.05.25.2 — diagnostic BEFORE the canvas null guard so we can see if draw()
        // is being invoked at all but bailing because the ref isn't attached yet.
        console.log('[Draw] tick — canvasRef:', !!canvas, 'points:', points.length, 'lines:', lines.length,
            'boundaryFiles:', boundaryFiles ? boundaryFiles.length : 'undef',
            'bfVisible:', boundaryFiles ? boundaryFiles.filter(b => !b.hidden).length : 'n/a');
        if (!canvas) return;
        const rawCtx = canvas.getContext('2d');
        if (!rawCtx) return;
        const floatingCtx = createFloatingOriginContext(rawCtx);
        const ctx = floatingCtx.ctx;

        const isLightTheme = settings.theme === 'light';

        // HiDPI: bake devicePixelRatio into the base transform so every draw
        // call below can keep using CSS-pixel coordinates while the backing
        // store renders at full physical resolution — this is what keeps
        // vector strokes crisp at any zoom level.
        const dpr = window.devicePixelRatio || 1;
        const logicalWidth = canvas.width / dpr;   // CSS px
        const logicalHeight = canvas.height / dpr; // CSS px
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, logicalWidth, logicalHeight);

        // Image smoothing only affects drawImage (raster tiles, etc.) —
        // vector strokes are unaffected. Leave on so WMS / FEMA tiles stay
        // smooth when scaled.
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = 'high';

        // --- Faint futuristic background grid (screen-space, fixed density) ---
        // Drawn before the world-space transform so it stays a crisp fixed
        // reference grid regardless of pan/zoom — a subtle sci-fi HUD backdrop
        // rather than a survey-scale grid.
        if (isGridVisible) {
            ctx.save();
            const minorSpacing = 32; // CSS px
            const majorEvery = 4; // every 4th line is a "major" line
            const gridColor = isLightTheme ? 'rgba(15, 118, 140, 0.10)' : 'rgba(56, 224, 255, 0.07)';
            const majorGridColor = isLightTheme ? 'rgba(15, 118, 140, 0.18)' : 'rgba(56, 224, 255, 0.14)';
            // Offset the grid with the pan so it feels anchored to the world,
            // while spacing stays constant on screen (classic HUD grid look).
            const offX = ((transform.offsetX % minorSpacing) + minorSpacing) % minorSpacing;
            const offY = ((transform.offsetY % minorSpacing) + minorSpacing) % minorSpacing;
            ctx.lineWidth = 1;
            let col = Math.round(-offX / minorSpacing);
            for (let x = offX; x <= logicalWidth; x += minorSpacing, col++) {
                ctx.strokeStyle = (col % majorEvery === 0) ? majorGridColor : gridColor;
                ctx.beginPath();
                ctx.moveTo(x + 0.5, 0);
                ctx.lineTo(x + 0.5, logicalHeight);
                ctx.stroke();
            }
            let row = Math.round(-offY / minorSpacing);
            for (let y = offY; y <= logicalHeight; y += minorSpacing, row++) {
                ctx.strokeStyle = (row % majorEvery === 0) ? majorGridColor : gridColor;
                ctx.beginPath();
                ctx.moveTo(0, y + 0.5);
                ctx.lineTo(logicalWidth, y + 0.5);
                ctx.stroke();
            }
            ctx.restore();
        }

        // --- Pass 1: Draw world-space items ---
        ctx.save();

        // Apply Orientation Tuple (OT / UCS) View rotation and origin translation if explicitly set
        const viewAngle = orientationTuple?.viewAngle ?? (orientationTuple?.mode === 'view' ? orientationTuple.angle : 0);
        const viewOriginX = orientationTuple?.viewOriginX ?? (orientationTuple?.mode === 'view' ? orientationTuple.originX : 0);
        const viewOriginY = orientationTuple?.viewOriginY ?? (orientationTuple?.mode === 'view' ? orientationTuple.originY : 0);

        // Floating origin: the world point under the screen center. World coords are
        // shifted by it before reaching the canvas to avoid float32 quantization.
        const viewCos = Math.cos(viewAngle);
        const viewSin = Math.sin(viewAngle);
        const centerQx = (logicalWidth / 2 - transform.offsetX) / transform.scale;
        const centerQy = -(logicalHeight / 2 - transform.offsetY) / transform.scale;
        const fox = Math.round(viewOriginX + (centerQx - viewOriginX) * viewCos + (centerQy - viewOriginY) * viewSin);
        const foy = Math.round(viewOriginY - (centerQx - viewOriginX) * viewSin + (centerQy - viewOriginY) * viewCos);
        const rotatedOx = viewOriginX + (fox - viewOriginX) * viewCos - (foy - viewOriginY) * viewSin;
        const rotatedOy = viewOriginY + (fox - viewOriginX) * viewSin + (foy - viewOriginY) * viewCos;
        ctx.translate(transform.offsetX + transform.scale * rotatedOx, transform.offsetY - transform.scale * rotatedOy);
        ctx.scale(transform.scale, -transform.scale); // Flip Y-axis for standard coordinate systems
        if (viewAngle !== 0) ctx.rotate(viewAngle);
        floatingCtx.enable(fox, foy);

            const inverseScale = 1 / transform.scale;
            // All text except point attributes defaults to world-scaled so you can zoom in on it like true CAD entities.
            // Point attributes stay locked to a crisp, constant screen size separately so the system doesn't lag with large point sets.
            const lineAnnotationWorldTextHeight = (multiplier: number = 1) => {
                return 2.0 * lineLabelScale * multiplier;
            };
        // Minimum 1 CSS pixel — anything below produces sub-pixel alpha-blended
        // strokes that disappear at certain zooms, especially with transparent
        // colors (contours). A full screen pixel is the smallest crisp stroke a
        // canvas can render without fading out.
        const minScreenPixelWidth = 1 * inverseScale;
        const scaledLineWidth = Math.max(1 * inverseScale * lineThickness, minScreenPixelWidth);
        // Round caps/joins so single-pixel strokes never vanish at endpoints or
        // mitred joints between contour segments.
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.miterLimit = 3;
        
        // Basemaps draw on the WebGPU canvas when available; Canvas2D below is the fallback.
        const gpuBasemap = gpuBasemapRef.current;
        const useGpuBasemap = !!gpuBasemap && gpuBasemap.ready;
        const gpuLayers: BasemapLayer[] = [];
        if (gpuBasemap && useGpuBasemap) {
            if (googleOverlayImage) {
                for (const tile of googleOverlayImage.tiles ?? [{ image: googleOverlayImage.image, bbox: googleOverlayImage.bbox }]) {
                    gpuLayers.push({ source: tile.image, bbox: tile.bbox, quad: tile.quad, opacity: googleOverlayImage.opacity });
                }
            }
            for (const w of wmsImages) {
                gpuLayers.push({ source: w.image as BasemapSource, bbox: w.bbox, quad: w.quad, opacity: w.opacity });
            }
            for (const area of offlineMapAreas) {
                if (!area.isVisible) continue;
                for (const layer of area.layers) {
                    const img = offlineImages.get(`${area.id}-${layer.layerName}`);
                    if (img) gpuLayers.push({ source: img, bbox: area.bbox, opacity: layer.opacity });
                }
            }
        }

        // Filled 2D layers (steep-slope, TIN shading) sit above the GPU canvas, so linework stays on Canvas2D then.
        const gpuLineBatch: GpuLineBatch | null =
            gpuBasemap && useGpuBasemap && !isTinShadingEnabled
            && !lines.some(l => l.type === 'steep-slope' && l.fillPath && l.fillPath.length >= 3)
                ? gpuBasemap.lineBatch
                : null;
        gpuLineBatch?.reset();
        const gpuPxPerWorld = transform.scale * dpr;
        const projectToDevice = (e: number, n: number): [number, number] => {
            const dx = e - viewOriginX;
            const dy = n - viewOriginY;
            const rx = viewOriginX + dx * viewCos - dy * viewSin;
            const ry = viewOriginY + dx * viewSin + dy * viewCos;
            return [(transform.offsetX + transform.scale * rx) * dpr, (transform.offsetY - transform.scale * ry) * dpr];
        };
        /** Vector survey linework is authoritatively rendered on Canvas2D so linework is never hidden behind WebGPU basemaps or disabled in tool modes */
        const tryGpuSegment = (
            _x0: number, _y0: number, _x1: number, _y1: number,
            _color: string, _widthWorld: number, _dashWorld: number[], _phaseWorld: number,
        ): boolean => false;

        /** Queue an arc (or full circle) as a tessellated polyline on the GPU. */
        const tryGpuArc = (
            _cx: number, _cy: number, _radius: number, _startAngle: number, _endAngle: number,
            _color: string, _widthWorld: number, _dashWorld: number[], _phaseWorld: number,
        ): boolean => false;

        // Draw Google/NAIP basemap underlay
        const drawWarpedImage = (image: CanvasImageSource, quad: WorldQuad, opacity: number) => {
            const src = image as HTMLImageElement & ImageBitmap;
            const iw = src.naturalWidth || src.width;
            const ih = src.naturalHeight || src.height;
            if (!iw || !ih) return;
            const [tl, tr, , bl] = quad;
            ctx.save();
            ctx.globalAlpha = opacity;
            ctx.translate(tl[0], tl[1]);
            ctx.transform((tr[0] - tl[0]) / iw, (tr[1] - tl[1]) / iw, (bl[0] - tl[0]) / ih, (bl[1] - tl[1]) / ih, 0, 0);
            ctx.drawImage(image, 0, 0, iw, ih);
            ctx.restore();
        };
        if (googleOverlayImage && !useGpuBasemap) {
            for (const tile of googleOverlayImage.tiles ?? [{ image: googleOverlayImage.image, bbox: googleOverlayImage.bbox }]) {
                const { image, bbox } = tile;
                const opacity = googleOverlayImage.opacity;
                if (tile.quad) {
                    drawWarpedImage(image, tile.quad, opacity);
                    continue;
                }
                const [minE, minN, maxE, maxN] = bbox;
                const worldWidth = maxE - minE;
                const worldHeight = maxN - minN;
                if (worldWidth > 0 && worldHeight > 0) {
                    ctx.save();
                    ctx.globalAlpha = opacity;
                    ctx.translate(minE, maxN);
                    ctx.scale(1, -1);
                    ctx.drawImage(image, 0, 0, worldWidth, worldHeight);
                    ctx.restore();
                }
            }
        }

        // Draw WMS images
        (useGpuBasemap ? [] : wmsImages).forEach(wmsImage => {
            const { image, bbox, opacity } = wmsImage;
            if (wmsImage.quad) {
                drawWarpedImage(image, wmsImage.quad, opacity);
                return;
            }
            const [minE, minN, maxE, maxN] = bbox;
            const worldWidth = maxE - minE;
            const worldHeight = maxN - minN;

            if (worldWidth > 0 && worldHeight > 0) {
                ctx.save();
                ctx.globalAlpha = opacity;
                // The image needs to be flipped back vertically within the already-flipped canvas context.
                // Go to the top-left corner of the image in world coordinates.
                ctx.translate(minE, maxN);
                // Flip the context locally for this image drawing operation.
                ctx.scale(1, -1);
                // Draw the image at the new (0,0) of the translated-and-flipped context.
                ctx.drawImage(image, 0, 0, worldWidth, worldHeight);
                ctx.restore();
            }
        });
        
        // Draw offline maps
        offlineMapAreas.filter(area => area.isVisible && !useGpuBasemap).forEach(area => {
            area.layers.forEach(layer => {
                const key = `${area.id}-${layer.layerName}`;
                const img = offlineImages.get(key);
                if (img) {
                    ctx.save();
                    ctx.globalAlpha = layer.opacity;
                    const [minE, minN, maxE, maxN] = area.bbox;
                    const worldWidth = maxE - minE;
                    const worldHeight = maxN - minN;

                    if (worldWidth > 0 && worldHeight > 0) {
                        // Same logic as WMS: translate to top-left, local flip, then draw.
                        ctx.translate(minE, maxN);
                        ctx.scale(1, -1);
                        ctx.drawImage(img, 0, 0, worldWidth, worldHeight);
                    }
                    ctx.restore();
                }
            });
        });

        // Build a fast lookup of user-defined linetypes (from the CAD Manager standard).
        // Keys are upper-cased AND stripped of non-alphanumerics so "FENCE_LINE",
        // "FENCELINE", "FENCE-LINE", etc. all hash to the same entry.
        const normalizeLtName = (s: string) => s.toUpperCase().replace(/[^A-Z0-9]/g, '');
        const linetypeLookup = new Map<string, LinetypeDefinition>();
        if (standardLinetypes && standardLinetypes.length > 0) {
            for (const lt of standardLinetypes) {
                if (lt && lt.name) linetypeLookup.set(normalizeLtName(lt.name), lt);
            }
        }
        // One-shot diagnostic per *standard change* — uses standardLinetypes
        // length+name-signature as the key so HMR can't make it sticky.
        if (typeof window !== 'undefined') {
            const sig = `lt:${standardLinetypes?.length ?? 0}/lns:${lines.length}`;
            const last = (window as any).__ltDebugSig;
            if (last !== sig) {
                (window as any).__ltDebugSig = sig;
                const uniqueLineTypes = new Set<string>();
                const uniqueLayers = new Set<string>();
                let linesWithLineType = 0;
                let linesWithLayer = 0;
                const samples: any[] = [];
                for (const l of lines) {
                    if (l.lineType) { uniqueLineTypes.add(l.lineType); linesWithLineType++; }
                    if (l.layer) { uniqueLayers.add(l.layer); linesWithLayer++; }
                    if (samples.length < 5) {
                        samples.push({
                            id: l.id, from: l.from, to: l.to,
                            layer: l.layer, lineType: l.lineType, type: l.type,
                        });
                    }
                }
                // eslint-disable-next-line no-console
                console.log('[Linetype Debug]', {
                    standardLinetypesCount: standardLinetypes?.length ?? 0,
                    lookupKeys: Array.from(linetypeLookup.keys()),
                    totalLines: lines.length,
                    linesWithLineType,
                    linesWithLayer,
                    lineTypesOnCanvas: Array.from(uniqueLineTypes),
                    layersOnCanvas: Array.from(uniqueLayers),
                    sampleLines: samples,
                });
            }
        }
        // Common AutoCAD spelling aliases — when the canvas line references one
        // name but the standard's linetype library uses the equivalent shorter
        // name (a near-universal mismatch in real-world CAD workflows).
        const LT_ALIASES: Record<string, string[]> = {
            FENCELINE: ['FENCE'],
            FENCE: ['FENCELINE', 'FENCELINE1', 'FENCELINE2'],
            PROPERTYLINE: ['PROPERTY'],
            PROPERTY: ['PROPERTYLINE'],
            EASEMENTLINE: ['EASEMENT'],
            EASEMENT: ['EASEMENTLINE'],
            WATERLINE: ['WATER'],
            WATER: ['WATERLINE'],
            SEWERLINE: ['SEWER'],
            SEWER: ['SEWERLINE'],
            GASLINE: ['GAS'],
            GAS: ['GASLINE'],
            ELECTRICLINE: ['ELECTRIC'],
            ELECTRIC: ['ELECTRICLINE'],
        };
        const lookupLinetype = (rawName: string): LinetypeDefinition | undefined => {
            const key = normalizeLtName(rawName);
            const direct = linetypeLookup.get(key);
            if (direct) return direct;
            const aliases = LT_ALIASES[key];
            if (aliases) {
                for (const alt of aliases) {
                    const hit = linetypeLookup.get(normalizeLtName(alt));
                    if (hit) return hit;
                }
            }
            return undefined;
        };

        // Resolve the effective linetype name for a SurveyLine. Priority:
        //   1) explicit line.lineType
        //   2) lookup by line.layer in the standard's codes (layerLinetypeMap)
        //   3) undefined → caller falls back to CONTINUOUS
        const resolveLineTypeName = (l: { lineType?: string; layer?: string }): string | undefined => {
            if (l.lineType && l.lineType.length > 0) return l.lineType;
            if (l.layer && layerLinetypeMap) {
                const hit = layerLinetypeMap[l.layer];
                if (hit) return hit;
            }
            return undefined;
        };

        // World-units → screen-pixel multiplier used when rendering a standard's
        // raw .lin pattern (which is expressed in drawing units). Tuned so a
        // 0.5-unit dash matches the legacy hardcoded "DASHED" 12-px dash.
        const LIN_UNIT_TO_PX = 24;

        // True when a linetype carries embedded shape/text glyphs that should be
        // stamped along the line (e.g. fence X, sewer S). Such linetypes can't
        // be expressed via setLineDash alone and need the complex renderer.
        const isComplexTextLinetype = (def: LinetypeDefinition | undefined): boolean => {
            if (!def || !def.shapeData || def.shapeData.length === 0) return false;
            return def.shapeData.some(sd => !!sd && (sd.isText || (sd.name && sd.name.length > 0)));
        };

        // Helper function to get line dash pattern based on CAD linetype
        // Pattern values are zoom-independent (multiplied by inverseScale) and respect
        // the global LTSCALE, the per-linetype scale, and an optional per-entity scale.
        const getLinetypePattern = (
            lineType: string | undefined,
            inverseScale: number,
            entityScale: number = 1,
        ): number[] => {
            if (!lineType) return []; // CONTINUOUS (default)
            const lt = lineType.toUpperCase();
            // Combined scale: zoom-independent base * project LTSCALE * per-entity CELTSCALE
            const baseScale = inverseScale * (globalLinetypeScale || 1) * (entityScale || 1);

            // 1) Prefer the user-defined linetype from the loaded CAD Manager standard.
            const userDef = lookupLinetype(lineType);
            if (userDef && userDef.pattern && userDef.pattern.length > 0) {
                const perLtScale = userDef.scale && userDef.scale > 0 ? userDef.scale : 1;
                const s = baseScale * perLtScale * LIN_UNIT_TO_PX;
                // .lin pattern: positive=dash, negative=gap, 0=dot/shape slot.
                // setLineDash only renders dash/gap cadence — text glyphs are
                // stamped separately by drawComplexLinetype. Here we collapse
                // any 0 (shape slot) into the *gap* on either side so the
                // canvas pattern still shows breaks where the glyphs go.
                const out: number[] = [];
                for (const v of userDef.pattern) {
                    if (v === 0) {
                        // Tiny 1-screen-pixel dash so the cadence stays visible
                        // even on linetypes that consist only of shape/gap pairs.
                        out.push(Math.max(0.5, 1) * inverseScale);
                    } else {
                        out.push(Math.abs(v) * s);
                    }
                }
                return out;
            }
            if (userDef && (!userDef.pattern || userDef.pattern.length === 0)) {
                return []; // Explicit CONTINUOUS authored in the standard
            }

            // 2) Fall back to the legacy built-in AutoCAD-like patterns.
            const s = baseScale; // legacy values were already in screen px @ scale=1
            switch (lt) {
                case 'CONTINUOUS':
                    return []; // Solid line
                case 'DASHED':
                    return [12 * s, 6 * s]; // __ __ __
                case 'HIDDEN':
                case 'HIDDEN2':
                    return [6 * s, 3 * s]; // _ _ _ _
                case 'DOT':
                case 'DOTTED':
                    return [1 * s, 4 * s]; // . . . .
                case 'DASHDOT':
                case 'CENTER':
                case 'CENTER2':
                    return [18 * s, 4 * s, 2 * s, 4 * s]; // ____ . ____ .
                case 'DIVIDE':
                case 'DIVIDE2':
                    return [12 * s, 4 * s, 2 * s, 4 * s, 2 * s, 4 * s]; // __ . . __ . .
                case 'PHANTOM':
                case 'PHANTOM2':
                    return [25 * s, 4 * s, 6 * s, 4 * s, 6 * s, 4 * s]; // _____ __ __ _____
                case 'BORDER':
                case 'BORDER2':
                    return [12 * s, 4 * s, 12 * s, 4 * s, 2 * s, 4 * s]; // __ __ . __ __ .
                case 'FENCELINE':
                case 'FENCELINE1':
                case 'FENCELINE2':
                    return [18 * s, 6 * s, 3 * s, 6 * s]; // ____X____X (approximation)
                case 'BATTING':
                    return [3 * s, 10 * s]; // Short dashes spaced far apart
                case 'ZIGZAG':
                    return [6 * s, 2 * s]; // Short zigzag approximation
                default:
                    // For unknown linetypes, treat as dashed (visible indicator)
                    if (lt.includes('DASH')) return [12 * s, 6 * s];
                    if (lt.includes('DOT')) return [1 * s, 4 * s];
                    if (lt.includes('CENTER')) return [18 * s, 4 * s, 2 * s, 4 * s];
                    if (lt.includes('HIDDEN')) return [6 * s, 3 * s];
                    return []; // Default to continuous
            }
        };

        /**
         * Render a single line segment using a *complex* linetype that carries
         * embedded text or shape glyphs (e.g. fence X, sewer S, water W).
         *
         * Walks the pattern cycle along the segment; for each element:
         *   - positive  → stroke a dash of that length
         *   - negative  → advance through a gap
         *   - zero      → stamp the next shape/text glyph rotated to line angle
         *
         * Falls back gracefully to a simple dashed stroke if the pattern has
         * no positive elements (degenerate) so the line is never invisible.
         *
         * Coordinates are in world space (canvas is already world-aligned with
         * Y flipped). Text glyphs are drawn upright by locally inverting Y.
         */
        const drawComplexLinetype = (
            x1: number, y1: number, x2: number, y2: number,
            def: LinetypeDefinition,
            entityScale: number,
            strokeColor: string,
            strokeWidth: number,
        ) => {
            const dx = x2 - x1, dy = y2 - y1;
            const segLen = Math.hypot(dx, dy);
            if (segLen < 1e-9) return;
            const ux = dx / segLen, uy = dy / segLen;
            const angle = Math.atan2(dy, dx); // world frame (Y up)

            const perLtScale = def.scale && def.scale > 0 ? def.scale : 1;
            // .lin drawing-unit → world unit multiplier at current zoom.
            const unitScale =
                (globalLinetypeScale || 1) *
                perLtScale *
                (entityScale || 1) *
                LIN_UNIT_TO_PX *
                inverseScale;

            // Pre-build the cycle: alternating dash/gap/glyph entries.
            const cycle: Array<{ kind: 'dash' | 'gap' | 'glyph' | 'arc'; size: number; shape?: any }> = [];
            let shapeIdx = 0;
            let cycleLen = 0;
            for (const v of def.pattern) {
                if (v > 0) {
                    const sz = v * unitScale;
                    cycle.push({ kind: 'dash', size: sz });
                    cycleLen += sz;
                } else if (v < 0) {
                    const sz = Math.abs(v) * unitScale;
                    cycle.push({ kind: 'gap', size: sz });
                    cycleLen += sz;
                } else {
                    const sd = def.shapeData?.[shapeIdx++];
                    if (sd && (sd as any).arc) {
                        // Arc bump consumes chord length along the line.
                        const chord = ((sd.scale && sd.scale > 0 ? sd.scale : 0.5)) * unitScale;
                        cycle.push({ kind: 'arc', size: chord, shape: sd });
                        cycleLen += chord;
                    } else {
                        cycle.push({ kind: 'glyph', size: 0, shape: sd });
                    }
                }
            }

            // Defensive fallback if there's no cadence to advance through.
            if (cycleLen <= 0) {
                ctx.beginPath();
                ctx.moveTo(x1, y1);
                ctx.lineTo(x2, y2);
                ctx.stroke();
                return;
            }

            ctx.strokeStyle = strokeColor;
            ctx.lineWidth = strokeWidth;
            ctx.setLineDash([]);

            let dist = 0;
            // Cap iterations defensively — very long lines with a tiny cycle
            // could otherwise blow the loop counter on bad input.
            const maxIters = Math.ceil((segLen / cycleLen) * cycle.length) + cycle.length + 8;
            let iter = 0;
            while (dist < segLen && iter++ < maxIters) {
                for (const el of cycle) {
                    if (dist >= segLen) break;
                    if (el.kind === 'dash') {
                        const end = Math.min(dist + el.size, segLen);
                        ctx.beginPath();
                        ctx.moveTo(x1 + ux * dist, y1 + uy * dist);
                        ctx.lineTo(x1 + ux * end, y1 + uy * end);
                        ctx.stroke();
                        dist = end;
                    } else if (el.kind === 'gap') {
                        dist += el.size;
                    } else if (el.kind === 'arc' && el.shape) {
                        // Semicircle bump (revision-cloud / treeline foliage).
                        const chord = Math.min(el.size, segLen - dist);
                        if (chord > 0) {
                            const sx = x1 + ux * dist;
                            const sy = y1 + uy * dist;
                            const ex = x1 + ux * (dist + chord);
                            const ey = y1 + uy * (dist + chord);
                            const cx = (sx + ex) / 2;
                            const cy = (sy + ey) / 2;
                            const r = chord / 2;
                            const side: 1 | -1 = el.shape.arcSide === -1 ? -1 : 1;
                            // Arc from start angle (pointing back toward sx,sy)
                            // around to end angle (pointing toward ex,ey),
                            // bulging perpendicular to the line on `side`.
                            const baseAngle = Math.atan2(sy - cy, sx - cx);
                            const endAngle = Math.atan2(ey - cy, ex - cx);
                            ctx.beginPath();
                            // ccw flag chooses which side the arc bulges.
                            // Canvas Y is flipped (world Y-up), so side=+1
                            // (left of line dir in world) needs ccw=false here.
                            ctx.arc(cx, cy, r, baseAngle, endAngle, side === 1 ? false : true);
                            ctx.stroke();
                        }
                        dist += el.size;
                    } else if (el.kind === 'glyph' && el.shape) {
                        const sd = el.shape;
                        const label = (sd.name || '').trim();
                        if (label) {
                            // Place the glyph at the *centre* of the surrounding
                            // gap (i.e. current dist) so it sits cleanly in the
                            // break. Honours .lin X/Y offsets too.
                            const xOff = (sd.xOffset || 0) * unitScale;
                            const yOff = (sd.yOffset || 0) * unitScale;
                            const textScale = sd.scale && sd.scale > 0 ? sd.scale : 0.1;
                            // Glyph height in world units. The 2.0× multiplier
                            // tunes the .lin "S=" (which is half-height in
                            // AutoCAD) to a legible canvas character.
                            const glyphPx = textScale * unitScale * 2.0;
                            const px = x1 + ux * dist;
                            const py = y1 + uy * dist;

                            ctx.save();
                            ctx.translate(px, py);
                            ctx.rotate(angle);
                            // Counter-act the main canvas Y-flip so text reads
                            // right-way-up regardless of line angle.
                            ctx.scale(1, -1);
                            ctx.fillStyle = strokeColor;
                            ctx.font = `bold ${glyphPx}px Arial`;
                            ctx.textAlign = 'center';
                            ctx.textBaseline = 'middle';
                            ctx.fillText(label, xOff, -yOff);
                            ctx.restore();
                        }
                        // Shapes are zero-length in the pattern cycle.
                    }
                }
            }
        };

        // Draw selection highlights and grips (rendered before lines so highlight is under the line)
        if (selectedLineIds.size > 0) {
            const selStroke = isLightTheme ? '#0284C7' : '#38BDF8';
            const selWidth = Math.max(scaledLineWidth + 1.2 * inverseScale, minScreenPixelWidth * 1.5);

            selectedLineIds.forEach(selId => {
                const selLine = lines.find(l => (l.id || `${l.from}-${l.to}`) === selId);
                if (!selLine) return;
                const sp1 = pointMap.get(selLine.from);
                const sp2 = pointMap.get(selLine.to);
                const sp1c = sp1 ? { x: sp1.easting, y: sp1.northing } : (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : null);
                const sp2c = sp2 ? { x: sp2.easting, y: sp2.northing } : (selLine.toPt ? { x: selLine.toPt.x, y: selLine.toPt.y } : null);
                if (!sp1c) return;

                const isCircleEntity = selLine.isCircle || (selLine.isCurve && selLine.curveRadius && (!selLine.fromPt || (sp1c && sp2c && sp1c.x === sp2c.x && sp1c.y === sp2c.y)) && !selLine.tangentBearing && !selLine.chordBearing);
                if (isCircleEntity && selLine.curveRadius) {
                    const center = selLine.circleCenter || (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : sp1c);
                    if (center) {
                        ctx.save();
                        ctx.strokeStyle = selStroke;
                        ctx.lineWidth = selWidth;
                        ctx.setLineDash([5 * inverseScale, 3 * inverseScale]);
                        ctx.beginPath();
                        ctx.arc(center.x, center.y, selLine.curveRadius, 0, 2 * Math.PI);
                        ctx.stroke();
                        ctx.setLineDash([]);
                        ctx.restore();
                    }
                } else if (selLine.isCurve && selLine.curveRadius && selLine.arcLength) {
                    let curveSel = selLine;
                    const radius = curveSel.curveRadius!;
                    const arcLength = curveSel.arcLength!;
                    const centralAngle = arcLength / radius;

                    if (!curveSel.tangentBearing && !curveSel.chordBearing && !curveSel.bearing && sp2c) {
                        const dE = sp2c.x - sp1c.x;
                        const dN = sp2c.y - sp1c.y;
                        if (Math.hypot(dE, dN) > 1e-9) {
                            let az = Math.atan2(dE, dN);
                            if (az < 0) az += 2 * Math.PI;
                            curveSel = { ...curveSel, chordBearing: formatBearing(az) };
                        }
                    }

                    const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
                    let isLeftCurve = false;
                    if (curveSel.curveDirection === 'left') {
                        isLeftCurve = true;
                    } else if (curveSel.curveDirection === 'right') {
                        isLeftCurve = false;
                    } else {
                        const tempTangent = curveSel.tangentBearing ? parseBearingToRadians(curveSel.tangentBearing) : null;
                        if (tempTangent !== null) {
                            const dirBearingStr = curveSel.chordBearing || curveSel.bearing;
                            if (dirBearingStr) {
                                const dirRad = parseBearingToRadians(dirBearingStr);
                                if (dirRad !== null) {
                                    const halfDelta = centralAngle / 2;
                                    const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                                    const expectedLeftChord  = normalizeAng(tempTangent - halfDelta);
                                    const dirNorm = normalizeAng(dirRad);
                                    const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                                    const diffLeft  = Math.min(Math.abs(dirNorm - expectedLeftChord),  2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                                    if (diffLeft < diffRight) isLeftCurve = true;
                                }
                            }
                        }
                    }

                    const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                    const chordStrArc = curveSel.chordBearing || curveSel.bearing;
                    const chordRadArc = chordStrArc ? parseBearingToRadians(chordStrArc) : null;
                    const tangentAngle: number | null = chordRadArc !== null
                        ? chordRadArc - signedDeltaArc / 2
                        : (curveSel.tangentBearing ? parseBearingToRadians(curveSel.tangentBearing) : null);

                    if (tangentAngle !== null) {
                        const canvasTangentAngle = Math.PI / 2 - tangentAngle;
                        const centerOffsetAngle = isLeftCurve
                            ? canvasTangentAngle + Math.PI / 2
                            : canvasTangentAngle - Math.PI / 2;
                        const centerX = sp1c.x + radius * Math.cos(centerOffsetAngle);
                        const centerY = sp1c.y + radius * Math.sin(centerOffsetAngle);
                        const startAngle = Math.atan2(sp1c.y - centerY, sp1c.x - centerX);
                        const endAngle = isLeftCurve ? startAngle + centralAngle : startAngle - centralAngle;
                        const arcAnticlockwise = !isLeftCurve;
                        const ptX = centerX + radius * Math.cos(endAngle);
                        const ptY = centerY + radius * Math.sin(endAngle);

                        ctx.save();
                        ctx.strokeStyle = selStroke;
                        ctx.lineWidth = selWidth;
                        ctx.setLineDash([5 * inverseScale, 3 * inverseScale]);
                        ctx.beginPath();
                        ctx.moveTo(sp1c.x, sp1c.y);
                        ctx.arc(centerX, centerY, radius, startAngle, endAngle, arcAnticlockwise);
                        ctx.lineTo(ptX, ptY);
                        ctx.stroke();
                        ctx.setLineDash([]);
                        ctx.restore();
                    }
                } else if (sp1c && sp2c) {
                    ctx.save();
                    ctx.strokeStyle = selStroke;
                    ctx.lineWidth = selWidth;
                    ctx.setLineDash([5 * inverseScale, 3 * inverseScale]);
                    ctx.beginPath();
                    ctx.moveTo(sp1c.x, sp1c.y);
                    ctx.lineTo(sp2c.x, sp2c.y);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.restore();
                }
            });

            // Draw Grip handles on selected entities
            const allGrips = getGripsForSelectedLines();
            const gripSize = 6 * inverseScale;
            allGrips.forEach(grip => {
                const isAct = activeGrip?.id === grip.id;
                const isHov = hoveredGrip?.id === grip.id;
                ctx.save();
                ctx.beginPath();
                ctx.rect(grip.x - gripSize / 2, grip.y - gripSize / 2, gripSize, gripSize);
                ctx.fillStyle = isAct ? '#EF4444' : isHov ? '#F59E0B' : '#0284C7';
                ctx.fill();
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1 * inverseScale;
                ctx.stroke();
                ctx.restore();
            });

            // Draw Active Grip Stretch rubberband preview
            if (activeGrip && cursorWorldPosRef.current) {
                const curP = activeOsnapPoint
                    ? { x: activeOsnapPoint.easting, y: activeOsnapPoint.northing }
                    : cursorWorldPosRef.current;
                const selLine = activeGrip.line;
                const sp1 = pointMap.get(selLine.from);
                const sp2 = pointMap.get(selLine.to);
                const p1c = sp1 ? { x: sp1.easting, y: sp1.northing } : (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : null);
                const p2c = sp2 ? { x: sp2.easting, y: sp2.northing } : (selLine.toPt ? { x: selLine.toPt.x, y: selLine.toPt.y } : null);

                ctx.save();
                ctx.strokeStyle = '#F59E0B'; // Amber stretch line
                ctx.lineWidth = Math.max(scaledLineWidth * 1.2, minScreenPixelWidth);
                ctx.setLineDash([4 * inverseScale, 3 * inverseScale]);

                const isCircleEntity = selLine.isCircle || (selLine.isCurve && selLine.curveRadius && (!selLine.fromPt || (p1c && p2c && p1c.x === p2c.x && p1c.y === p2c.y)) && !selLine.tangentBearing && !selLine.chordBearing);

                if (isCircleEntity && selLine.curveRadius) {
                    if (activeGrip.type === 'center') {
                        ctx.beginPath();
                        ctx.arc(curP.x, curP.y, selLine.curveRadius, 0, 2 * Math.PI);
                        ctx.stroke();
                    } else if (activeGrip.type === 'quadrant') {
                        const center = selLine.circleCenter || (selLine.fromPt ? { x: selLine.fromPt.x, y: selLine.fromPt.y } : p1c);
                        if (center) {
                            const newR = Math.hypot(curP.x - center.x, curP.y - center.y);
                            ctx.beginPath();
                            ctx.arc(center.x, center.y, newR, 0, 2 * Math.PI);
                            ctx.stroke();
                            ctx.beginPath();
                            ctx.moveTo(center.x, center.y);
                            ctx.lineTo(curP.x, curP.y);
                            ctx.stroke();
                        }
                    }
                } else if (p1c && p2c) {
                    if (activeGrip.type === 'start') {
                        ctx.beginPath();
                        ctx.moveTo(curP.x, curP.y);
                        ctx.lineTo(p2c.x, p2c.y);
                        ctx.stroke();
                    } else if (activeGrip.type === 'end') {
                        ctx.beginPath();
                        ctx.moveTo(p1c.x, p1c.y);
                        ctx.lineTo(curP.x, curP.y);
                        ctx.stroke();
                    } else if (activeGrip.type === 'mid') {
                        const dx = curP.x - activeGrip.x;
                        const dy = curP.y - activeGrip.y;
                        ctx.beginPath();
                        ctx.moveTo(p1c.x + dx, p1c.y + dy);
                        ctx.lineTo(p2c.x + dx, p2c.y + dy);
                        ctx.stroke();
                    }
                }
                ctx.setLineDash([]);
                ctx.restore();
            }
        }

        // Draw lines
        // Keep steep-slope geometry behind contours and design lines so contour
        // generations remain readable after steep-slope analysis.
        const orderedLines = [...lines].sort((a, b) => {
            const aSteep = a.type === 'steep-slope' ? 0 : 1;
            const bSteep = b.type === 'steep-slope' ? 0 : 1;
            return aSteep - bSteep;
        });

        // Track cumulative distance per polyline chain so dashed linetypes maintain
        // a continuous phase across vertices rather than restarting on every segment.
        const polylineDashPhaseMap = new Map<string, number>();

        // Dashed polyline segments are batched into one path per polyline so Skia keeps
        // the dash phase and joins continuous; stroking per segment shimmers with zoom.
        let dashBatch: { key: string; pid: string; lastX: number; lastY: number; strokeStyle: string; lineWidth: number; dash: number[] } | null = null;
        const flushDashBatch = () => {
            if (!dashBatch) return;
            ctx.strokeStyle = dashBatch.strokeStyle;
            ctx.lineWidth = dashBatch.lineWidth;
            ctx.setLineDash(dashBatch.dash);
            ctx.lineDashOffset = 0;
            ctx.lineCap = 'butt';
            ctx.stroke();
            ctx.setLineDash([]);
            ctx.lineCap = 'round';
            dashBatch = null;
        };

        orderedLines.forEach(line => {
            // Skip curve lines - they will be rendered as smooth arcs in the curve rendering pass
            if (line.isCurve) {
                return; // Skip - we'll handle curves separately below
            }
            // In shaded TIN mode, suppress imported TIN edge overlay linework so
            // only the surface shading is shown.
            if (isTinShadingEnabled && line.type === 'tin-edge') {
                return;
            }
            // Skip lines toggled off (e.g. hidden steep-slope geometry).
            if (line.hidden) {
                return;
            }
            
            const p1 = pointMap.get(line.from);
            const p2 = pointMap.get(line.to);
            
            const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
            const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);

            if (p1Coords && p2Coords) {
                if (dashBatch && dashBatch.pid !== line.polylineId) flushDashBatch();
                // Determine line styling
                let strokeStyle = line.color || (isLightTheme ? '#111827' : '#FFFFFF');
                let lineWidth = scaledLineWidth;
                let isDashed = false;

                if (line.type === 'breakline') {
                    strokeStyle = '#FF69B4'; // Hot Pink
                    lineWidth = scaledLineWidth * 1.5;
                } else if (line.type === 'inclusion') {
                    strokeStyle = '#34D399'; // Green-400
                    lineWidth = scaledLineWidth * 1.5;
                } else if (line.type === 'shrinkwrap-preview') {
                    strokeStyle = '#A78BFA'; // Violet-400
                    lineWidth = scaledLineWidth * 1.5;
                    isDashed = true;
                } else if (line.type === 'exclusion') {
                    strokeStyle = '#F87171'; // Red-400
                    lineWidth = scaledLineWidth * 1.5;
                    isDashed = true;
                } else if (line.type?.startsWith('contour')) {
                     // Use fully opaque colors — combining low alpha with a 1px
                     // stroke previously made contours fade to invisible. Width
                     // (not transparency) distinguishes major from minor.
                     // Dark-theme colors are kept bright enough to read on the
                     // bare canvas, so contours stay visible when the steep-slope
                     // fill backdrop is hidden (previously they vanished without
                     // the green fill behind them).
                     const majorColor = isLightTheme ? '#4B5563' : '#CBD5E1';
                     const minorColor = isLightTheme ? '#6B7280' : '#94A3B8';
                     strokeStyle = line.type === 'contour-major' ? majorColor : minorColor;
                     // Floor at 1 CSS pixel so contours never disappear when
                     // zoomed out. Major contours render slightly thicker.
                     lineWidth = Math.max(
                         line.type === 'contour-major' ? scaledLineWidth * 1.25 : scaledLineWidth,
                         minScreenPixelWidth
                     );
                } else if (line.type === 'parcel') {
                    // County tax-parcel boundary — distinct amber so it stands
                    // apart from breaklines/contours and reads at any zoom.
                    strokeStyle = isLightTheme ? 'rgba(180, 83, 9, 0.85)' : 'rgba(251, 191, 36, 0.85)';
                    lineWidth = Math.max(scaledLineWidth * 0.7, minScreenPixelWidth);
                } else if (line.type === 'flood') {
                    // FEMA NFHL flood-hazard polygon ring — cyan to distinguish
                    // from parcel (amber) and contours (gray).
                    strokeStyle = isLightTheme ? 'rgba(8, 145, 178, 0.85)' : 'rgba(34, 211, 238, 0.85)';
                    lineWidth = Math.max(scaledLineWidth * 0.7, minScreenPixelWidth);
                    isDashed = true;
                } else if (line.type === 'steep-slope') {
                    const explicitColor = line.color;
                    strokeStyle = explicitColor
                        ? `${explicitColor}ff`
                        : (isLightTheme ? 'rgba(239, 68, 68, 0.92)' : 'rgba(248, 113, 113, 0.92)');
                    lineWidth = Math.max(scaledLineWidth * 0.55, minScreenPixelWidth * 0.6);
                    if (line.fillPath && line.fillPath.length >= 3) {
                        const alphaColor = explicitColor
                            ? `${explicitColor}e6`
                            : (isLightTheme ? 'rgba(239, 68, 68, 0.90)' : 'rgba(248, 113, 113, 0.90)');

                        ctx.beginPath();
                        ctx.moveTo(line.fillPath[0].x, line.fillPath[0].y);
                        for (let i = 1; i < line.fillPath.length; i++) {
                            ctx.lineTo(line.fillPath[i].x, line.fillPath[i].y);
                        }
                        ctx.closePath();
                        ctx.fillStyle = alphaColor;
                        ctx.fill();

                        // Subtle edge so adjacent triangles remain readable.
                        ctx.strokeStyle = strokeStyle;
                        ctx.lineWidth = Math.max(scaledLineWidth * 0.35, minScreenPixelWidth * 0.45);
                        ctx.stroke();
                        return;
                    }
                }

                ctx.strokeStyle = strokeStyle;
                ctx.lineWidth = lineWidth;

                // Complex linetypes (fence X, sewer S, water W, …) carry shape
                // glyphs that must be stamped along the line. Route those to
                // the dedicated walker so the text is actually visible.
                const effectiveLineTypeName = resolveLineTypeName(line);
                const complexDef = effectiveLineTypeName ? lookupLinetype(effectiveLineTypeName) : undefined;
                if (complexDef && isComplexTextLinetype(complexDef)) {
                    flushDashBatch();
                    drawComplexLinetype(
                        p1Coords.x, p1Coords.y,
                        p2Coords.x, p2Coords.y,
                        complexDef,
                        line.lineTypeScale ?? 1,
                        strokeStyle,
                        lineWidth,
                    );
                } else {
                    // Apply line pattern: CAD lineType takes precedence, then special types (exclusion), then solid
                    let dashPattern: number[] = [];
                    if (effectiveLineTypeName) {
                        // CAD linetype from CAD Manager standard (with per-entity CELTSCALE)
                        dashPattern = getLinetypePattern(effectiveLineTypeName, inverseScale, line.lineTypeScale);
                    } else if (isDashed) {
                        // Fallback for exclusion lines
                        const dashSize = 5 * inverseScale;
                        dashPattern = [dashSize, dashSize];
                    }

                    const gpuPhase = line.polylineId ? (polylineDashPhaseMap.get(line.polylineId) ?? 0) : 0;
                    if (tryGpuSegment(p1Coords.x, p1Coords.y, p2Coords.x, p2Coords.y, strokeStyle, lineWidth, dashPattern, gpuPhase)) {
                        if (line.polylineId && dashPattern.length > 0) {
                            polylineDashPhaseMap.set(line.polylineId, gpuPhase + Math.hypot(p2Coords.x - p1Coords.x, p2Coords.y - p1Coords.y));
                        }
                    } else if (dashPattern.length > 0 && line.polylineId) {
                        const key = `${line.polylineId}|${strokeStyle}|${lineWidth}|${dashPattern.join(',')}`;
                        const continues = dashBatch !== null && dashBatch.key === key
                            && Math.hypot(dashBatch.lastX - p1Coords.x, dashBatch.lastY - p1Coords.y) < 1e-6;
                        if (continues) {
                            ctx.lineTo(p2Coords.x, p2Coords.y);
                            dashBatch!.lastX = p2Coords.x;
                            dashBatch!.lastY = p2Coords.y;
                        } else {
                            flushDashBatch();
                            ctx.beginPath();
                            ctx.moveTo(p1Coords.x, p1Coords.y);
                            ctx.lineTo(p2Coords.x, p2Coords.y);
                            dashBatch = { key, pid: line.polylineId, lastX: p2Coords.x, lastY: p2Coords.y, strokeStyle, lineWidth, dash: dashPattern };
                        }
                    } else {
                        flushDashBatch();
                        ctx.beginPath();
                        ctx.moveTo(p1Coords.x, p1Coords.y);
                        ctx.lineTo(p2Coords.x, p2Coords.y);
                        if (dashPattern.length > 0) ctx.setLineDash(dashPattern);
                        ctx.stroke();
                        if (dashPattern.length > 0) ctx.setLineDash([]);
                    }
                }
            }
        });
        flushDashBatch();

        // ── FEMA Flood-Zone Hatch Fill ──────────────────────────────────────
        // Uses pre-computed rings (floodRings memo) + createPattern() tile so
        // each polygon is filled with a single ctx.fill() call — no per-line
        // stroke loops that crash the browser on large flood zones.
        {
            const hatchCfg = settings.floodHatch;
            const hatchStyle = hatchCfg?.style ?? 'lines';
            if (hatchStyle !== 'none' && floodRings.size > 0) {
                const hatchOpacity = hatchCfg?.opacity ?? 0.18;
                const hatchScaleWorld = hatchCfg?.scale ?? 50;
                const hatchSpacePx = Math.max(6, Math.min(60, hatchScaleWorld * transform.scale));
                const hatchColor = isLightTheme ? `rgba(8,145,178,${hatchOpacity})` : `rgba(34,211,238,${hatchOpacity})`;

                // Build a small repeating tile once per style/spacing/color
                const tileSize = Math.round(hatchSpacePx);
                const tile = document.createElement('canvas');
                tile.width = tileSize * (hatchStyle === 'cross' ? 1 : 1);
                tile.height = tileSize;
                const tc = tile.getContext('2d')!;
                tc.clearRect(0, 0, tile.width, tile.height);
                tc.strokeStyle = hatchColor;
                tc.fillStyle = hatchColor;
                tc.lineWidth = 0.8;

                if (hatchStyle === 'dots') {
                    const r = Math.max(1, tileSize * 0.12);
                    tc.beginPath();
                    tc.arc(0, 0, r, 0, 2 * Math.PI);
                    tc.arc(tileSize, 0, r, 0, 2 * Math.PI);
                    tc.arc(0, tileSize, r, 0, 2 * Math.PI);
                    tc.arc(tileSize / 2, tileSize / 2, r, 0, 2 * Math.PI);
                    tc.fill();
                } else if (hatchStyle === 'chevron') {
                    const h = tileSize / 2;
                    tc.beginPath();
                    tc.moveTo(0, h); tc.lineTo(tileSize / 2, 0); tc.lineTo(tileSize, h);
                    tc.moveTo(0, tileSize); tc.lineTo(tileSize / 2, h); tc.lineTo(tileSize, tileSize);
                    tc.stroke();
                } else {
                    // 'lines' — one diagonal line through tile; 'cross' — two
                    tc.beginPath();
                    tc.moveTo(0, 0); tc.lineTo(tileSize, tileSize);
                    tc.moveTo(-tileSize, 0); tc.lineTo(0, tileSize);
                    tc.moveTo(tileSize, 0); tc.lineTo(tileSize * 2, tileSize);
                    tc.stroke();
                    if (hatchStyle === 'cross') {
                        tc.beginPath();
                        tc.moveTo(tileSize, 0); tc.lineTo(0, tileSize);
                        tc.moveTo(0, 0); tc.lineTo(-tileSize, tileSize);
                        tc.moveTo(tileSize * 2, 0); tc.lineTo(tileSize, tileSize);
                        tc.stroke();
                    }
                }

                const pattern = ctx.createPattern(tile, 'repeat');

                // World→screen projection
                const toScreenX = (wx: number) => wx * transform.scale + transform.offsetX;
                const toScreenY = (wy: number) => -wy * transform.scale + transform.offsetY;

                for (const ring of floodRings.values()) {
                    const sx0 = toScreenX(ring[0].x), sy0 = toScreenY(ring[0].y);
                    // Quick off-screen cull
                    let bx0 = sx0, by0 = sy0, bx1 = sx0, by1 = sy0;
                    for (let i = 1; i < ring.length; i++) {
                        const sx = toScreenX(ring[i].x), sy = toScreenY(ring[i].y);
                        if (sx < bx0) bx0 = sx; if (sx > bx1) bx1 = sx;
                        if (sy < by0) by0 = sy; if (sy > by1) by1 = sy;
                    }
                    if (bx1 < 0 || by1 < 0 || bx0 > canvas.clientWidth || by0 > canvas.clientHeight) continue;

                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(sx0, sy0);
                    for (let i = 1; i < ring.length; i++) {
                        ctx.lineTo(toScreenX(ring[i].x), toScreenY(ring[i].y));
                    }
                    ctx.closePath();
                    if (pattern) {
                        ctx.fillStyle = pattern;
                    } else {
                        ctx.fillStyle = hatchColor;
                    }
                    ctx.fill();
                    ctx.restore();
                }
            }
        }

        // Draw curve lines (smooth arcs & full circles)
        curveLabelBoxesRef.current.clear();
        lines.forEach(line => {
            if (!line.isCurve && !line.isCircle) {
                return; // Only process curve/circle lines
            }
            
            const p1 = pointMap.get(line.from);
            const p2 = pointMap.get(line.to);

            const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : null);
            const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (line.toPt ? { x: line.toPt.x, y: line.toPt.y } : null);

            // Handle Full Circles
            const isCircleEntity = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (p1Coords && p2Coords && p1Coords.x === p2Coords.x && p1Coords.y === p2Coords.y)) && !line.tangentBearing && !line.chordBearing);
            if (isCircleEntity && line.curveRadius) {
                const center = line.circleCenter || (line.fromPt ? { x: line.fromPt.x, y: line.fromPt.y } : p1Coords);
                if (!center) return;

                let strokeStyle = isLightTheme ? '#111827' : '#FFFFFF';
                let lineWidth = scaledLineWidth;
                let isDashed = false;

                if (line.type === 'breakline') {
                    strokeStyle = '#FF69B4';
                    lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
                } else if (line.type === 'inclusion') {
                    strokeStyle = '#34D399';
                    lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
                } else if (line.type === 'shrinkwrap-preview') {
                    strokeStyle = '#A78BFA';
                    lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
                    isDashed = true;
                } else if (line.type === 'exclusion') {
                    strokeStyle = '#F87171';
                    lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
                    isDashed = true;
                }

                ctx.strokeStyle = strokeStyle;
                ctx.lineWidth = lineWidth;

                ctx.beginPath();
                ctx.arc(center.x, center.y, line.curveRadius, 0, 2 * Math.PI);
                {
                    const circleLineType = resolveLineTypeName(line);
                    const circleDash = circleLineType
                        ? getLinetypePattern(circleLineType, inverseScale, line.lineTypeScale)
                        : (isDashed ? [5 * inverseScale, 5 * inverseScale] : []);
                    if (tryGpuArc(center.x, center.y, line.curveRadius, 0, 2 * Math.PI, strokeStyle, lineWidth, circleDash, 0)) {
                        ctx.beginPath();
                        return;
                    }
                }

                let dashPattern: number[] = [];
                const effectiveCurveLineTypeName = resolveLineTypeName(line);
                if (effectiveCurveLineTypeName) {
                    dashPattern = getLinetypePattern(effectiveCurveLineTypeName, inverseScale, line.lineTypeScale);
                } else if (isDashed) {
                    const dashSize = 5 * inverseScale;
                    dashPattern = [dashSize, dashSize];
                }
                
                if (dashPattern.length > 0) {
                    ctx.setLineDash(dashPattern);
                }
                ctx.stroke();
                if (dashPattern.length > 0) {
                    ctx.setLineDash([]);
                }
                return;
            }

            // If the AI omitted bearings (common for full circles where the JSON only carries
            // R / arcLength / curveDirection), derive a chord bearing from the two endpoints.
            // Skip if the chord is degenerate (e.g. full-circle 360° case where p1==p2 — caller
            // is expected to split a full circle into two 180° arcs with distinct endpoints).
            if (!line.tangentBearing && !line.chordBearing && !line.bearing && p1Coords && p2Coords) {
                const dE = p2Coords.x - p1Coords.x;
                const dN = p2Coords.y - p1Coords.y;
                if (Math.hypot(dE, dN) > 1e-9) {
                    let az = Math.atan2(dE, dN); // bearing radians (N=0, CW)
                    if (az < 0) az += 2 * Math.PI;
                    line = { ...line, chordBearing: formatBearing(az) };
                }
            }

            if (!line.curveRadius || !line.arcLength || (!line.tangentBearing && !line.chordBearing && !line.bearing)) {
                return; // Need radius, arc length, and at least one bearing to work with
            }
            
            if (!p1Coords) {
                return; // Need start point
            }

            // Determine line styling
            let strokeStyle = isLightTheme ? '#111827' : '#FFFFFF';
            let lineWidth = scaledLineWidth; // Already has minimum applied
            let isDashed = false;

            if (line.type === 'breakline') {
                strokeStyle = '#FF69B4'; // Hot Pink
                lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
            } else if (line.type === 'inclusion') {
                strokeStyle = '#34D399'; // Green-400
                lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
            } else if (line.type === 'shrinkwrap-preview') {
                strokeStyle = '#A78BFA'; // Violet-400
                lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
                isDashed = true;
            } else if (line.type === 'exclusion') {
                strokeStyle = '#F87171'; // Red-400
                lineWidth = Math.max(scaledLineWidth * 1.5, minScreenPixelWidth);
                isDashed = true;
            }

            ctx.strokeStyle = strokeStyle;
            ctx.lineWidth = lineWidth;

            const radius = line.curveRadius;
            const arcLength = line.arcLength;
            const centralAngle = arcLength / radius; // Radians
            
            // Determine curve direction FIRST (independent of tangentBearing)
                const normalizeAng = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
                let isLeftCurve = false;
                if (line.curveDirection === 'left') {
                    isLeftCurve = true;
                } else if (line.curveDirection === 'right') {
                    isLeftCurve = false;
                } else {
                    // Fallback: use AI-provided tangentBearing (if any) to compare chord bearing
                    const tempTangent = line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null;
                    if (tempTangent !== null) {
                        const dirBearingStr = line.chordBearing || line.bearing;
                        if (dirBearingStr) {
                            const dirRad = parseBearingToRadians(dirBearingStr);
                            if (dirRad !== null) {
                                const halfDelta = centralAngle / 2;
                                const expectedRightChord = normalizeAng(tempTangent + halfDelta);
                                const expectedLeftChord  = normalizeAng(tempTangent - halfDelta);
                                const dirNorm = normalizeAng(dirRad);
                                const diffRight = Math.min(Math.abs(dirNorm - expectedRightChord), 2 * Math.PI - Math.abs(dirNorm - expectedRightChord));
                                const diffLeft  = Math.min(Math.abs(dirNorm - expectedLeftChord),  2 * Math.PI - Math.abs(dirNorm - expectedLeftChord));
                                if (diffLeft < diffRight) isLeftCurve = true;
                            }
                        }
                    }
                }

                // Resolve tangent bearing geometrically: tangentBearing = chordBearing - signedΔ/2
                // More reliable than AI-provided tangentBearing which is sometimes incorrect.
                // When endpoints exist, derive chord bearing directly from them so the arc
                // remains locked to p1Coords and p2Coords across any transformations.
                const signedDeltaArc = isLeftCurve ? -centralAngle : centralAngle;
                let chordRadArc: number | null = null;
                if (p1Coords && p2Coords) {
                    const dE = p2Coords.x - p1Coords.x;
                    const dN = p2Coords.y - p1Coords.y;
                    if (Math.hypot(dE, dN) > 1e-4) {
                        chordRadArc = normalizeAng(Math.atan2(dE, dN));
                    }
                }
                if (chordRadArc === null) {
                    const chordStrArc = line.chordBearing || line.bearing;
                    chordRadArc = chordStrArc ? parseBearingToRadians(chordStrArc) : null;
                }
                const tangentAngle: number | null = chordRadArc !== null
                    ? chordRadArc - signedDeltaArc / 2
                    : (line.tangentBearing ? parseBearingToRadians(line.tangentBearing) : null);

            if (tangentAngle !== null) {
                // Convert bearing angle to canvas angle
                // Bearing: 0=North, π/2=East, π=South, 3π/2=West (clockwise from north)
                // Canvas: 0=East, π/2=North, π=West, 3π/2=South (counter-clockwise from east)
                const canvasTangentAngle = Math.PI / 2 - tangentAngle;

                // Center is 90° to the right (right curve) or left (left curve) of tangent direction
                const centerOffsetAngle = isLeftCurve
                    ? canvasTangentAngle + Math.PI / 2   // Left curve: center to the left
                    : canvasTangentAngle - Math.PI / 2;  // Right curve: center to the right
                const centerX = p1Coords.x + radius * Math.cos(centerOffsetAngle);
                const centerY = p1Coords.y + radius * Math.sin(centerOffsetAngle);

                // Calculate start angle from center to PC (start point)
                const startAngle = Math.atan2(p1Coords.y - centerY, p1Coords.x - centerX);
                // In Y-flipped canvas: anticlockwise=true → CW in world (right curve)
                //                     anticlockwise=false → CCW in world (left curve)
                const endAngle = isLeftCurve ? startAngle + centralAngle : startAngle - centralAngle;
                const arcAnticlockwise = !isLeftCurve;

                // Calculate the endpoint PT coordinates for visual closure
                const ptX = centerX + radius * Math.cos(endAngle);
                const ptY = centerY + radius * Math.sin(endAngle);

                // Draw the arc
                const arcLineType = resolveLineTypeName(line);
                const arcDash = arcLineType
                    ? getLinetypePattern(arcLineType, inverseScale, line.lineTypeScale)
                    : (isDashed ? [5 * inverseScale, 5 * inverseScale] : []);
                const arcPhase = line.polylineId ? (polylineDashPhaseMap.get(line.polylineId) ?? 0) : 0;
                const arcOnGpu = tryGpuArc(centerX, centerY, radius, startAngle, endAngle, strokeStyle, lineWidth, arcDash, arcPhase);
                if (arcOnGpu && line.polylineId && arcDash.length > 0) {
                    polylineDashPhaseMap.set(line.polylineId, arcPhase + arcLength);
                }
                ctx.beginPath();
                if (!arcOnGpu) {
                    ctx.moveTo(p1Coords.x, p1Coords.y);
                    ctx.arc(centerX, centerY, radius, startAngle, endAngle, arcAnticlockwise);
                    ctx.lineTo(ptX, ptY); // Ensure connection ends cleanly at PT
                }
                
                // Apply line pattern: CAD lineType takes precedence, then special types (exclusion), then solid
                let dashPattern: number[] = [];
                const effectiveCurveLineTypeName = resolveLineTypeName(line);
                if (effectiveCurveLineTypeName) {
                    dashPattern = getLinetypePattern(effectiveCurveLineTypeName, inverseScale, line.lineTypeScale);
                } else if (isDashed) {
                    const dashSize = 5 * inverseScale;
                    dashPattern = [dashSize, dashSize];
                }
                
                if (dashPattern.length > 0 && !arcOnGpu) {
                    ctx.setLineDash(dashPattern);
                    ctx.lineCap = 'butt';
                    if (line.polylineId) {
                        const curPhase = polylineDashPhaseMap.get(line.polylineId) || 0;
                        ctx.lineDashOffset = -curPhase;
                        polylineDashPhaseMap.set(line.polylineId, curPhase + arcLength);
                    } else {
                        ctx.lineDashOffset = 0;
                    }
                }
                if (!arcOnGpu) ctx.stroke();
                if (dashPattern.length > 0 && !arcOnGpu) {
                    ctx.setLineDash([]);
                    ctx.lineDashOffset = 0;
                    ctx.lineCap = 'round';
                }

                // Draw curve annotation with curve parameters — but only when this looks
                // like a boundary call (AI-provided bearing/distance, or an explicit boundary
                // line type). Ad-hoc curves such as user-drawn circles or geometric arcs that
                // arrive with only R/L/Δ/curveDirection should render silently.
                const isBoundaryCurve = !!(line.bearing || line.distance
                    || line.type === 'parcel' || line.type === 'inclusion' || line.type === 'exclusion');
                if (line.curveRadius && line.arcLength && isBoundaryCurve) {
                    // Calculate delta angle in degrees from radians
                    const deltaDegreesTotal = (centralAngle * 180) / Math.PI;
                    const deltaDegrees = Math.floor(deltaDegreesTotal);
                    const deltaMinutes = Math.floor((deltaDegreesTotal - deltaDegrees) * 60);
                    const deltaSeconds = Math.round(((deltaDegreesTotal - deltaDegrees) * 60 - deltaMinutes) * 60);

                    // Position annotation at the midpoint of the arc
                    const midAngle = startAngle - centralAngle / 2;
                    const annotationDistance = radius * 1.3; // Position slightly beyond the arc
                    const offsetX = line.labelOffset?.x || 0;
                    const offsetY = line.labelOffset?.y || 0;
                    const annotX = centerX + annotationDistance * Math.cos(midAngle) + offsetX;
                    const annotY = centerY + annotationDistance * Math.sin(midAngle) + offsetY;

                    // Create stacked annotation text (same format as B&D labels)
                    const chordParts: string[] = [];
                    if (agentAnnotMode !== 'distance') chordParts.push(line.bearing ?? '');
                    if (agentAnnotMode !== 'bearing') chordParts.push(line.distance ?? '');
                    const chordText = chordParts.filter(Boolean).join(' / ') || (line.bearing ?? '');
                    const radiusText = `R = ${line.curveRadius.toFixed(2)}'`;
                    const arcLengthText = `L = ${line.arcLength.toFixed(2)}'`;
                    const deltaText = `Δ = ${deltaDegrees}°${deltaMinutes}'${deltaSeconds}"`;

                    // Draw annotation using same scale and style as B&D labels
                        const worldTextHeight = lineAnnotationWorldTextHeight();
                    ctx.save();
                    ctx.translate(annotX, annotY);
                    ctx.scale(1, -1); // Counter-act the main canvas y-flip for text

                    ctx.font = `600 ${worldTextHeight}px Arial`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillStyle = isLightTheme ? '#374151' : '#D1D5DB';
                    
                    // Calculate dimensions for stacked text
                    const lineHeight = worldTextHeight * 1.2;
                    const lines_text = [chordText, radiusText, arcLengthText, deltaText];
                    let maxWidth = 0;
                    lines_text.forEach(textLine => {
                        const width = ctx.measureText(textLine).width;
                        if (width > maxWidth) maxWidth = width;
                    });

                    // Draw background for all three lines
                    const padding = worldTextHeight * 0.2;
                    const totalHeight = lineHeight * lines_text.length;
                    ctx.clearRect(-maxWidth / 2 - padding, -totalHeight / 2 - padding, maxWidth + 2 * padding, totalHeight + 2 * padding);

                    // Record world-space hit box for Ctrl+drag repositioning.
                    const curveLineId = line.id || `${line.from}-${line.to}`;
                    curveLabelBoxesRef.current.set(curveLineId, {
                        wx: annotX,
                        wy: annotY,
                        ww: maxWidth + 2 * padding,
                        wh: totalHeight + 2 * padding,
                    });
                    
                    // Draw each line of text stacked
                    const startY = -(totalHeight / 2) + lineHeight / 2;
                    lines_text.forEach((textLine, index) => {
                        const y = startY + index * lineHeight;
                        ctx.fillText(textLine, 0, y);
                    });

                    ctx.restore();
                }
            }
        });

        // Draw centerlines
        centerlines.forEach(cl => {
            if (cl.pis.length < 2) return;
            const isClSelected = selectedCenterlineIds.has(cl.id);
            ctx.save();
            ctx.setLineDash([]);
            if (isClSelected) {
                ctx.shadowColor = 'rgba(34, 211, 238, 0.9)';
                ctx.shadowBlur = 10;
            }
            
            ctx.strokeStyle = isClSelected ? '#22d3ee' : 'rgba(66, 153, 225, 0.8)';
            ctx.lineWidth = isClSelected
                ? Math.max(scaledLineWidth * 2.2, 2.5)
                : Math.max(scaledLineWidth * 1.2, minScreenPixelWidth);

            let lastPt = { easting: cl.pis[0].easting, northing: cl.pis[0].northing };

            for (let i = 1; i < cl.pis.length; i++) {
                const p_prev = cl.pis[i-1];
                const p_curr = cl.pis[i];
                
                if (i < cl.pis.length - 1 && p_curr.curveRadius) {
                    const p_next = cl.pis[i+1];
                    const curve = calculateCurveGeometry(p_prev, p_curr, p_next, p_curr.curveRadius);
                    if (curve) {
                        ctx.beginPath();
                        ctx.moveTo(lastPt.easting, lastPt.northing);
                        ctx.lineTo(curve.pcEasting, curve.pcNorthing);
                        ctx.stroke();

                        ctx.beginPath();
                        // With a flipped Y-axis, a CW turn in world-space (turnRight=true) becomes CCW on canvas, so anticlockwise must be true.
                        ctx.arc(curve.centerEasting, curve.centerNorthing, curve.radius, curve.startAngle, curve.endAngle, curve.turnRight);
                        ctx.stroke();

                        lastPt = { easting: curve.ptEasting, northing: curve.ptNorthing };
                    } else { // straight line if curve fails
                        ctx.beginPath();
                        ctx.moveTo(lastPt.easting, lastPt.northing);
                        ctx.lineTo(p_curr.easting, p_curr.northing);
                        ctx.stroke();
                        lastPt = p_curr;
                    }
                } else {
                    ctx.beginPath();
                    ctx.moveTo(lastPt.easting, lastPt.northing);
                    ctx.lineTo(p_curr.easting, p_curr.northing);
                    ctx.stroke();
                    lastPt = p_curr;
                }
            }
            ctx.restore();
        });

        if (gpuBasemap && useGpuBasemap) {
            gpuBasemap.render(gpuLayers, {
                width: logicalWidth,
                height: logicalHeight,
                project: (e, n) => {
                    const [dx, dy] = projectToDevice(e, n);
                    return { x: dx / dpr, y: dy / dpr };
                },
            }, gpuLineBatch);
            if (!gpuBasemap.ready) {
                if (gpuCanvasRef.current) gpuCanvasRef.current.style.display = 'none';
                requestAnimationFrame(() => drawRef.current?.());
            }
        }

        // Draw visible TIN surfaces (optional elevation shading + wireframe).
        tinSurfaces.filter(t => !t.hidden).forEach(surface => {
            const verts = surface.vertices;
            if (!verts || verts.length === 0 || !surface.triangles || surface.triangles.length === 0) return;
            ctx.save();

            let minZ = Infinity;
            let maxZ = -Infinity;
            for (const v of verts) {
                if (v.elevation < minZ) minZ = v.elevation;
                if (v.elevation > maxZ) maxZ = v.elevation;
            }
            const zRange = Math.max(maxZ - minZ, 1e-9);
            const toShadeColor = (z: number): string => {
                const t = Math.max(0, Math.min(1, (z - minZ) / zRange));
                const hue = 8 + t * 112; // red->yellow->green style ramp
                return `hsla(${hue.toFixed(1)}, 92%, 56%, 0.38)`;
            };

            for (const tri of surface.triangles) {
                const ia = Array.isArray(tri) ? tri[0] : (tri as any).a;
                const ib = Array.isArray(tri) ? tri[1] : (tri as any).b;
                const ic = Array.isArray(tri) ? tri[2] : (tri as any).c;
                const a = verts[ia];
                const b = verts[ib];
                const c = verts[ic];
                if (!a || !b || !c) continue;

                ctx.beginPath();
                ctx.moveTo(a.easting, a.northing);
                ctx.lineTo(b.easting, b.northing);
                ctx.lineTo(c.easting, c.northing);
                ctx.closePath();

                if (isTinShadingEnabled) {
                    const zAvg = (a.elevation + b.elevation + c.elevation) / 3;
                    ctx.fillStyle = toShadeColor(zAvg);
                    ctx.fill();
                }

                if (!isTinShadingEnabled) {
                    ctx.strokeStyle = isLightTheme ? 'rgba(245, 158, 11, 0.35)' : 'rgba(251, 191, 36, 0.5)';
                    ctx.lineWidth = Math.max(scaledLineWidth * 0.3, minScreenPixelWidth * 0.45);
                    ctx.setLineDash([]);
                    ctx.stroke();
                }
            }
            ctx.restore();
        });
        
        // Draw rubber band line for all continuous drawing modes (polylines, breaklines, inclusion, exclusion)
        if ((drawingMode === 'polylines' || drawingMode === 'breaklines' || drawingMode === 'inclusion' || drawingMode === 'exclusion') && polylinePoints.length > 0 && cursorWorldPosRef.current) {
            const lastPolyPoint = polylinePoints[polylinePoints.length - 1];
            if (lastPolyPoint) {
                const cursorPos = cursorWorldPosRef.current;
                let targetX = cursorPos.x;
                let targetY = cursorPos.y;
                if (typedSegmentLength.trim().length > 0) {
                    const num = Number(typedSegmentLength);
                    if (Number.isFinite(num) && num > 0) {
                        const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                        const worldLength = convertLinearUnits(num, fromUnit, 'foot');
                        const dx = cursorPos.x - lastPolyPoint.easting;
                        const dy = cursorPos.y - lastPolyPoint.northing;
                        const mag = Math.hypot(dx, dy);
                        if (mag > 1e-9) {
                            targetX = lastPolyPoint.easting + (dx / mag) * worldLength;
                            targetY = lastPolyPoint.northing + (dy / mag) * worldLength;
                        }
                    }
                }
                // Arc submode preview: arc leaving the anchor tangent to the
                // previous segment, through the cursor. Falls back to the
                // straight rubber band when the cursor sits on the tangent line.
                const previewArc = drawingMode === 'polylines' && polylineArcMode && lastSegmentTangentRef.current
                    ? computeTangentArc(
                        { easting: lastPolyPoint.easting, northing: lastPolyPoint.northing },
                        lastSegmentTangentRef.current,
                        { easting: targetX, northing: targetY })
                    : null;
                ctx.beginPath();
                ctx.moveTo(lastPolyPoint.easting, lastPolyPoint.northing);
                if (previewArc) {
                    // Same angle convention as the committed-curve pass below.
                    const startAngle = Math.atan2(lastPolyPoint.northing - previewArc.centerNorthing, lastPolyPoint.easting - previewArc.centerEasting);
                    const endAngle = previewArc.direction === 'left' ? startAngle + previewArc.sweep : startAngle - previewArc.sweep;
                    ctx.arc(previewArc.centerEasting, previewArc.centerNorthing, previewArc.radius, startAngle, endAngle, previewArc.direction !== 'left');
                    ctx.lineTo(targetX, targetY);
                } else {
                    ctx.lineTo(targetX, targetY);
                }
                // Color based on mode
                const colors = {
                    'polylines': '#FFFF00',
                    'breaklines': '#FF69B4',
                    'inclusion': '#34D399',
                    'exclusion': '#F87171'
                };
                ctx.strokeStyle = colors[drawingMode] || '#FFFF00';
                ctx.lineWidth = Math.max(1 * inverseScale, minScreenPixelWidth);
                const dashSize = 5 * inverseScale;
                const gapSize = 3 * inverseScale;
                ctx.setLineDash([dashSize, gapSize]);
                ctx.stroke();
                ctx.setLineDash([]);
            }
        }

        // Draw rubber band polygon preview
        if (drawingMode === 'polygon' && polygonCenter) {
            const cursorPos = cursorWorldPosRef.current;
            let radiusWorld = cursorPos
                ? Math.hypot(cursorPos.x - polygonCenter.easting, cursorPos.y - polygonCenter.northing)
                : 0;
            if (typedPolygonDiameter.trim().length > 0) {
                const num = Number(typedPolygonDiameter);
                if (Number.isFinite(num) && num > 0) {
                    const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                    radiusWorld = convertLinearUnits(num / 2, fromUnit, 'foot');
                }
            }
            if (radiusWorld > 1e-6) {
                const vertices = polygonVertices(
                    { x: polygonCenter.easting, y: polygonCenter.northing },
                    radiusWorld,
                    polygonSides,
                );
                if (vertices.length > 0) {
                    ctx.save();
                    ctx.beginPath();
                    ctx.moveTo(vertices[0].x, vertices[0].y);
                    for (let i = 1; i < vertices.length; i++) ctx.lineTo(vertices[i].x, vertices[i].y);
                    ctx.closePath();
                    ctx.fillStyle = 'rgba(167, 139, 250, 0.08)';
                    ctx.fill();
                    ctx.strokeStyle = '#A78BFA';
                    ctx.lineWidth = Math.max(1.5 * inverseScale, minScreenPixelWidth);
                    const dashSize = 5 * inverseScale;
                    const gapSize = 3 * inverseScale;
                    ctx.setLineDash([dashSize, gapSize]);
                    ctx.stroke();
                    ctx.setLineDash([]);

                    // Center crosshair and radius connector
                    const cross = 5 * inverseScale;
                    ctx.beginPath();
                    ctx.moveTo(polygonCenter.easting - cross, polygonCenter.northing);
                    ctx.lineTo(polygonCenter.easting + cross, polygonCenter.northing);
                    ctx.moveTo(polygonCenter.easting, polygonCenter.northing - cross);
                    ctx.lineTo(polygonCenter.easting, polygonCenter.northing + cross);
                    ctx.moveTo(polygonCenter.easting, polygonCenter.northing);
                    const pointerDx = cursorPos ? cursorPos.x - polygonCenter.easting : 0;
                    const pointerDy = cursorPos ? cursorPos.y - polygonCenter.northing : 0;
                    const pointerLength = Math.hypot(pointerDx, pointerDy);
                    const radiusEnd = pointerLength > 1e-9
                        ? {
                            x: polygonCenter.easting + (pointerDx / pointerLength) * radiusWorld,
                            y: polygonCenter.northing + (pointerDy / pointerLength) * radiusWorld,
                        }
                        : {
                            x: polygonCenter.easting,
                            y: polygonCenter.northing + radiusWorld,
                        };
                    ctx.lineTo(radiusEnd.x, radiusEnd.y);
                    ctx.strokeStyle = 'rgba(167, 139, 250, 0.55)';
                    ctx.lineWidth = 1.2 * inverseScale;
                    ctx.setLineDash([3 * inverseScale, 3 * inverseScale]);
                    ctx.stroke();
                    ctx.setLineDash([]);
                    ctx.restore();
                }
            }
        }

        // Draw rubber band circle preview
        if (drawingMode === 'circle' && circleSubMode === 'center-radius' && circleCenter && cursorWorldPosRef.current) {
            const cursorPos = cursorWorldPosRef.current;
            let r = Math.hypot(cursorPos.x - circleCenter.easting, cursorPos.y - circleCenter.northing);
            if (typedCircleValue.trim().length > 0) {
                const num = Number(typedCircleValue);
                if (Number.isFinite(num) && num > 0) {
                    const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                    const worldVal = convertLinearUnits(num, fromUnit, 'foot');
                    r = circleSizeParam === 'diameter' ? worldVal / 2 : worldVal;
                }
            } else if (circleSizeParam === 'diameter') {
                r = r / 2;
            }

            if (r > 1e-6) {
                ctx.save();
                ctx.beginPath();
                ctx.arc(circleCenter.easting, circleCenter.northing, r, 0, 2 * Math.PI);
                ctx.strokeStyle = '#38BDF8';
                ctx.lineWidth = Math.max(1.5 * inverseScale, minScreenPixelWidth);
                const dashSize = 5 * inverseScale;
                const gapSize = 3 * inverseScale;
                ctx.setLineDash([dashSize, gapSize]);
                ctx.stroke();
                ctx.setLineDash([]);

                // Center crosshair marker
                const cross = 5 * inverseScale;
                ctx.beginPath();
                ctx.moveTo(circleCenter.easting - cross, circleCenter.northing);
                ctx.lineTo(circleCenter.easting + cross, circleCenter.northing);
                ctx.moveTo(circleCenter.easting, circleCenter.northing - cross);
                ctx.lineTo(circleCenter.easting, circleCenter.northing + cross);
                ctx.strokeStyle = '#38BDF8';
                ctx.lineWidth = 1.2 * inverseScale;
                ctx.stroke();

                // Radius connector line from center to cursor
                ctx.beginPath();
                ctx.moveTo(circleCenter.easting, circleCenter.northing);
                ctx.lineTo(cursorPos.x, cursorPos.y);
                ctx.strokeStyle = 'rgba(56, 189, 248, 0.5)';
                ctx.setLineDash([3 * inverseScale, 3 * inverseScale]);
                ctx.stroke();
                ctx.setLineDash([]);
                ctx.restore();
            }
        }

        // Draw TTR circle preview & tangent glyphs
        if (drawingMode === 'circle' && circleSubMode === 'ttr') {
            const drawTangentMarker = (x: number, y: number, color: string = '#F59E0B') => {
                const r = 6 * inverseScale;
                ctx.save();
                ctx.strokeStyle = color;
                ctx.lineWidth = 1.5 * inverseScale;
                ctx.beginPath();
                ctx.arc(x, y, r, 0, 2 * Math.PI);
                ctx.stroke();
                ctx.beginPath();
                ctx.moveTo(x - r * 1.4, y - r);
                ctx.lineTo(x + r * 1.4, y - r);
                ctx.stroke();
                const fontSize = 8 * inverseScale;
                ctx.font = `bold ${fontSize}px Arial`;
                ctx.fillStyle = color;
                ctx.fillText('T', x + r + 2 * inverseScale, y + r + 2 * inverseScale);
                ctx.restore();
            };

            if (ttrEntity1) {
                drawTangentMarker(ttrEntity1.pickPoint.x, ttrEntity1.pickPoint.y, '#F59E0B');
            }
            if (ttrEntity2) {
                drawTangentMarker(ttrEntity2.pickPoint.x, ttrEntity2.pickPoint.y, '#F59E0B');
            }
            if (ttrStep < 3 && ttrHoverEntity) {
                drawTangentMarker(ttrHoverEntity.pickPoint.x, ttrHoverEntity.pickPoint.y, '#38BDF8');
            }

            if (ttrStep === 3 && ttrEntity1 && ttrEntity2) {
                const cursorPos = cursorWorldPosRef.current;
                const defaultR = cursorPos ? Math.hypot(cursorPos.x - ttrEntity2.pickPoint.x, cursorPos.y - ttrEntity2.pickPoint.y) : 25;
                let rVal = defaultR;
                if (typedCircleValue.trim().length > 0) {
                    const num = Number(typedCircleValue);
                    if (Number.isFinite(num) && num > 0) {
                        const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                        rVal = convertLinearUnits(num, fromUnit, 'foot');
                    }
                }
                if (circleSizeParam === 'diameter') rVal = rVal / 2;

                if (rVal > 1e-6) {
                    const sol = solveTtrCircle(ttrEntity1, ttrEntity2, rVal);
                    if (sol) {
                        ctx.save();
                        ctx.beginPath();
                        ctx.arc(sol.center.easting, sol.center.northing, sol.radius, 0, 2 * Math.PI);
                        ctx.strokeStyle = '#38BDF8';
                        ctx.lineWidth = Math.max(1.5 * inverseScale, minScreenPixelWidth);
                        const dashSize = 5 * inverseScale;
                        const gapSize = 3 * inverseScale;
                        ctx.setLineDash([dashSize, gapSize]);
                        ctx.stroke();
                        ctx.setLineDash([]);

                        // Center crosshair marker
                        const cross = 5 * inverseScale;
                        ctx.beginPath();
                        ctx.moveTo(sol.center.easting - cross, sol.center.northing);
                        ctx.lineTo(sol.center.easting + cross, sol.center.northing);
                        ctx.moveTo(sol.center.easting, sol.center.northing - cross);
                        ctx.lineTo(sol.center.easting, sol.center.northing + cross);
                        ctx.strokeStyle = '#38BDF8';
                        ctx.lineWidth = 1.2 * inverseScale;
                        ctx.stroke();

                        // Dashed radial lines to tangent points
                        ctx.beginPath();
                        ctx.moveTo(sol.center.easting, sol.center.northing);
                        ctx.lineTo(sol.tangentPt1.x, sol.tangentPt1.y);
                        ctx.moveTo(sol.center.easting, sol.center.northing);
                        ctx.lineTo(sol.tangentPt2.x, sol.tangentPt2.y);
                        ctx.strokeStyle = 'rgba(56, 189, 248, 0.6)';
                        ctx.setLineDash([3 * inverseScale, 3 * inverseScale]);
                        ctx.stroke();
                        ctx.setLineDash([]);

                        drawTangentMarker(sol.tangentPt1.x, sol.tangentPt1.y, '#34D399');
                        drawTangentMarker(sol.tangentPt2.x, sol.tangentPt2.y, '#34D399');
                        ctx.restore();
                    }
                }
            }
        }

        // Draw Trim preview highlight (AutoCAD-style clean red dashed highlight with sleek '✕' cut badge)
        if (isTrimmingLines && trimHoverPreview) {
            ctx.save();
            ctx.strokeStyle = 'rgba(239, 68, 68, 0.9)'; // Red-500
            ctx.lineWidth = Math.max(scaledLineWidth + 1.2 * inverseScale, minScreenPixelWidth * 1.5);
            ctx.shadowBlur = 0;
            const dashSize = 5 * inverseScale;
            const gapSize = 3 * inverseScale;
            ctx.setLineDash([dashSize, gapSize]);

            if (trimHoverPreview.kind === 'line' && trimHoverPreview.p1 && trimHoverPreview.p2) {
                ctx.beginPath();
                ctx.moveTo(trimHoverPreview.p1.x, trimHoverPreview.p1.y);
                ctx.lineTo(trimHoverPreview.p2.x, trimHoverPreview.p2.y);
                ctx.stroke();
            } else if ((trimHoverPreview.kind === 'arc' || trimHoverPreview.kind === 'circle') && trimHoverPreview.center && trimHoverPreview.radius) {
                ctx.beginPath();
                if (trimHoverPreview.kind === 'circle') {
                    ctx.arc(trimHoverPreview.center.x, trimHoverPreview.center.y, trimHoverPreview.radius, 0, 2 * Math.PI);
                } else {
                    ctx.arc(
                        trimHoverPreview.center.x,
                        trimHoverPreview.center.y,
                        trimHoverPreview.radius,
                        trimHoverPreview.startAngle ?? 0,
                        trimHoverPreview.endAngle ?? 2 * Math.PI,
                        trimHoverPreview.arcAnticlockwise ?? false
                    );
                }
                ctx.stroke();
            }
            ctx.setLineDash([]);
            ctx.restore();

            // Draw a clean subtle '✕' badge at the hit point
            if (trimHoverPreview.hitPoint) {
                const hp = trimHoverPreview.hitPoint;
                const r = 5 * inverseScale;
                ctx.save();
                ctx.beginPath();
                ctx.arc(hp.x, hp.y, r, 0, 2 * Math.PI);
                ctx.fillStyle = 'rgba(239, 68, 68, 0.85)';
                ctx.fill();
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1 * inverseScale;
                ctx.stroke();

                const xArm = 2.2 * inverseScale;
                ctx.beginPath();
                ctx.moveTo(hp.x - xArm, hp.y - xArm);
                ctx.lineTo(hp.x + xArm, hp.y + xArm);
                ctx.moveTo(hp.x + xArm, hp.y - xArm);
                ctx.lineTo(hp.x - xArm, hp.y + xArm);
                ctx.strokeStyle = '#FFFFFF';
                ctx.lineWidth = 1.4 * inverseScale;
                ctx.stroke();
                ctx.restore();
            }
        }
        
        // Draw line labels (skip curve lines as they have their own annotations)
        lines.forEach(line => {
            if (line.bearing && line.distance && !line.isCurve) {
                const p1 = pointMap.get(line.from);
                const p2 = pointMap.get(line.to);
        
                if (p1 && p2) {
                    const midX = (p1.easting + p2.easting) / 2;
                    const midY = (p1.northing + p2.northing) / 2;

                    const dx = p2.easting - p1.easting;
                    const dy = p2.northing - p1.northing;
                    let angle = Math.atan2(dy, dx);

                    // Flip text if it would be upside down
                    if (Math.abs(angle) > Math.PI / 2) {
                        angle += Math.PI;
                    }

                    // Apply additional 180-degree rotation if labelRotation is set
                    const labelRotation = line.labelRotation || 0;
                    const rotationRadians = (labelRotation * Math.PI) / 180;

                    // Build label text based on annotation mode
                    const textParts: string[] = [];
                    if (agentAnnotMode !== 'distance') textParts.push(line.bearing!);
                    if (agentAnnotMode !== 'bearing') textParts.push(line.distance!);
                    const text = textParts.join(' / ');
                    if (!text) return;
                    // Define text height in world units, controlled by lineLabelScale.
                        const worldTextHeight = lineAnnotationWorldTextHeight();
                    
                    // Apply label offset if it exists
                    const offsetX = line.labelOffset?.x || 0;
                    const offsetY = line.labelOffset?.y || 0;
                    
                    ctx.save();
                    ctx.translate(midX + offsetX, midY + offsetY);
                    ctx.rotate(angle + rotationRadians);
                    ctx.scale(1, -1); // Counter-act the main canvas y-flip to draw text upright.
                    
                    ctx.font = `italic 600 ${worldTextHeight}px Arial`;
                    const textWidth = ctx.measureText(text).width;

                    // Clear a background for the text for readability.
                    const padding = worldTextHeight * 0.2;
                    ctx.clearRect(-textWidth / 2 - padding, -worldTextHeight / 2 - padding, textWidth + 2 * padding, worldTextHeight + 2 * padding);
                    
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillStyle = isLightTheme ? '#374151' : '#D1D5DB';
                    ctx.fillText(text, 0, 0);

                    ctx.restore();
                }
            }
        });

        // Draw deed metadata as one stacked text block (for DEED_READER agent) - in world space.
        // Suppressed when per-tract BoundaryFiles exist: each file gets its own
        // centroid annotation rendered inside the BFOverlay loop below, so the
        // labels follow their individual tract transforms instead of being
        // averaged into a single position.
        const hasBoundaryAnnotations = !!(boundaryFiles && boundaryFiles.some(bf => !bf.hidden && (bf.parcelId || bf.ownerName || bf.lotDescription)));
        if (deedMetadata && points.length > 0 && activeAgent === AgentType.DEED_READER && !hasBoundaryAnnotations) {
            // Calculate center of all deed points
            let sumNorthing = 0;
            let sumEasting = 0;
            let count = 0;
            
            points.forEach(p => {
                // Skip intermediate curve points
                const pNumberLower = p.pointNumber?.toLowerCase() || '';
                if (pNumberLower.includes('int-') || pNumberLower.startsWith('int_')) {
                    return;
                }
                sumNorthing += p.northing;
                sumEasting += p.easting;
                count++;
            });
            
            if (count > 0) {
                const centerNorthing = sumNorthing / count;
                const centerEasting = sumEasting / count;
                
                // Build all text lines to display (owner, book/page, parcel)
                const textLines: string[] = [];
                if (deedMetadata.owners && deedMetadata.owners.length > 0) {
                    textLines.push(`Owner: ${deedMetadata.owners.join(' to ')}`);
                }
                if (deedMetadata.deedBook || deedMetadata.deedPage) {
                    const bookPage = [
                        deedMetadata.deedBook ? `Book ${deedMetadata.deedBook}` : '',
                        deedMetadata.deedPage ? `Page ${deedMetadata.deedPage}` : ''
                    ].filter(Boolean).join(', ');
                    if (bookPage) textLines.push(bookPage);
                }
                if (deedMetadata.parcelId) {
                    textLines.push(`Parcel: ${deedMetadata.parcelId}`);
                }
                
                if (textLines.length > 0) {
                    const worldTextHeight = lineAnnotationWorldTextHeight();
                    const lineHeight = worldTextHeight * 1.4;
                    
                    // Use ownerNameOffset for positioning (this is now the unified metadata offset)
                    const offsetX = props.ownerNameOffset?.x || 0;
                    const offsetY = props.ownerNameOffset?.y || 10;
                    
                    ctx.save();
                    ctx.translate(centerEasting + offsetX, centerNorthing + offsetY);
                    ctx.scale(1, -1); // Counter-act the main canvas y-flip
                    
                    ctx.font = `italic 600 ${worldTextHeight}px Arial`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    
                    // Measure text for background clearing
                    const maxWidth = Math.max(...textLines.map(line => ctx.measureText(line).width));
                    const padding = worldTextHeight * 0.2;
                    const totalHeight = textLines.length * lineHeight;
                    
                    // Clear a background for the entire text block (like B&D labels)
                    ctx.clearRect(
                        -maxWidth / 2 - padding,
                        -totalHeight / 2 - padding,
                        maxWidth + 2 * padding,
                        totalHeight + 2 * padding
                    );
                    
                    // Draw all text lines in white (matching B&D labels)
                    ctx.fillStyle = isLightTheme ? '#374151' : '#D1D5DB';
                    const startY = -(textLines.length - 1) * lineHeight / 2;
                    textLines.forEach((line, i) => {
                        ctx.fillText(line, 0, startY + i * lineHeight);
                    });
                    
                    ctx.restore();
                }
            }
        }

        // --- Draw annotation dimensions & previews in world-space ---
        const drawArrow = (
            tipX: number,
            tipY: number,
            dirX: number,
            dirY: number,
            color: string,
            styleOverride?: string,
            sizeOverride?: number
        ) => {
            const style = styleOverride || dimStyleSettings.arrowStyle || 'closed';
            const rawSize = sizeOverride ?? dimStyleSettings.arrowSize ?? 8;
            const arrowSize = rawSize * inverseScale * (dimensionScale);
            if (style === 'tick') {
                const tickSize = arrowSize * 0.9;
                ctx.strokeStyle = color;
                ctx.lineWidth = 2 * inverseScale;
                ctx.beginPath();
                ctx.moveTo(tipX - (dirX + dirY) * tickSize * 0.5, tipY - (dirY - dirX) * tickSize * 0.5);
                ctx.lineTo(tipX + (dirX + dirY) * tickSize * 0.5, tipY + (dirY - dirX) * tickSize * 0.5);
                ctx.stroke();
                return;
            }
            if (style === 'dot') {
                ctx.fillStyle = color;
                ctx.beginPath();
                ctx.arc(tipX, tipY, arrowSize * 0.35, 0, 2 * Math.PI);
                ctx.fill();
                return;
            }
            if (style === 'open') {
                const arrowWidth = arrowSize * 0.35;
                ctx.strokeStyle = color;
                ctx.lineWidth = 1.5 * inverseScale;
                ctx.beginPath();
                ctx.moveTo(tipX - dirX * arrowSize + dirY * arrowWidth, tipY - dirY * arrowSize - dirX * arrowWidth);
                ctx.lineTo(tipX, tipY);
                ctx.lineTo(tipX - dirX * arrowSize - dirY * arrowWidth, tipY - dirY * arrowSize + dirX * arrowWidth);
                ctx.stroke();
                return;
            }
            // Closed filled arrow
            const arrowWidth = arrowSize * 0.3;
            ctx.fillStyle = color;
            ctx.beginPath();
            ctx.moveTo(tipX, tipY);
            ctx.lineTo(tipX - dirX * arrowSize + dirY * arrowWidth, tipY - dirY * arrowSize - dirX * arrowWidth);
            ctx.lineTo(tipX - dirX * arrowSize - dirY * arrowWidth, tipY - dirY * arrowSize + dirX * arrowWidth);
            ctx.closePath();
            ctx.fill();
        };

        const drawCenterCross = (cx: number, cy: number, color: string) => {
            const markSize = 4 * inverseScale;
            ctx.strokeStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.beginPath();
            ctx.moveTo(cx - markSize, cy);
            ctx.lineTo(cx + markSize, cy);
            ctx.moveTo(cx, cy - markSize);
            ctx.lineTo(cx, cy + markSize);
            ctx.stroke();
        };

        const getDimWorldTextHeight = (baseHeight: number = 2.5, scale: number = 1, scalingModeOverride?: 'world' | 'screen') => {
            const mode = scalingModeOverride || dimStyleSettings.scalingMode || 'world';
            if (mode === 'screen') {
                return baseHeight * inverseScale * dimensionScale * scale;
            }
            // World-relative: fixed size in survey world units, expands visually when zooming in
            return baseHeight * dimensionScale * scale;
        };

        const renderDimText = (
            label: string,
            midX: number,
            midY: number,
            angle: number,
            color: string,
            scale: number = 1,
            textHeightOverride?: number,
            scalingModeOverride?: 'world' | 'screen'
        ) => {
            let textAngle = angle;
            if (Math.abs(textAngle) > Math.PI / 2) textAngle += Math.PI;

            const baseH = textHeightOverride ?? dimStyleSettings.textHeight ?? 2.5;
            const worldTextHeight = getDimWorldTextHeight(baseH, scale, scalingModeOverride);
            ctx.save();
            ctx.translate(midX, midY);
            ctx.rotate(textAngle);
            ctx.scale(1, -1); // Counter-act Y-flip
            ctx.font = `bold ${worldTextHeight}px Arial`;
            const textWidth = ctx.measureText(label).width;
            const padX = worldTextHeight * 0.25;
            const padY = worldTextHeight * 0.15;
            ctx.fillStyle = 'rgba(17, 24, 39, 0.88)';
            ctx.fillRect(-textWidth / 2 - padX, -worldTextHeight / 2 - padY, textWidth + padX * 2, worldTextHeight + padY * 2);
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillStyle = color;
            ctx.fillText(label, 0, 0);
            ctx.restore();
        };

        // Linear dimension (aligned, horizontal, vertical)
        const drawLinearDimension = (
            dim: AlignedAnnotationDimension | HorizontalAnnotationDimension | VerticalAnnotationDimension,
            color: string
        ) => {
            const geom = calculateLinearDimensionGeometry(dim);
            const { d1, d2, midpoint, angle, length } = geom;
            if (length < 1e-9) return;

            const prec = dim.precision ?? dimStyleSettings.precision ?? 2;
            const dimText = dim.textOverride ?? (length.toFixed(prec) + (dimStyleSettings.linearSuffix || ''));
            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const arrowSt = dim.arrowStyle || dimStyleSettings.arrowStyle || 'closed';
            const arrowSz = dim.arrowSize ?? dimStyleSettings.arrowSize ?? 8.0;
            const textPlace = dim.textPlacement || dimStyleSettings.textPlacement || 'centered';
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';

            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.setLineDash([]);

            // Witness lines
            ctx.beginPath();
            ctx.moveTo(geom.p1.easting, geom.p1.northing);
            ctx.lineTo(d1.easting, d1.northing);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(geom.p2.easting, geom.p2.northing);
            ctx.lineTo(d2.easting, d2.northing);
            ctx.stroke();

            // Dimension line
            ctx.beginPath();
            ctx.moveTo(d1.easting, d1.northing);
            ctx.lineTo(d2.easting, d2.northing);
            ctx.stroke();

            // Arrowheads
            const dx = d2.easting - d1.easting;
            const dy = d2.northing - d1.northing;
            const dlen = Math.hypot(dx, dy);
            if (dlen > 1e-6) {
                const ux = dx / dlen;
                const uy = dy / dlen;
                drawArrow(d1.easting, d1.northing, -ux, -uy, color, arrowSt, arrowSz);
                drawArrow(d2.easting, d2.northing, ux, uy, color, arrowSt, arrowSz);
            }

            // Text
            let textY = midpoint.northing;
            let textX = midpoint.easting;
            if (textPlace === 'above') {
                const perpX = dlen > 1e-6 ? -dy / dlen : 0;
                const perpY = dlen > 1e-6 ? dx / dlen : 0;
                const worldTextH = getDimWorldTextHeight(textH, dimScale, scMode);
                textX += perpX * (worldTextH * 0.9);
                textY += perpY * (worldTextH * 0.9);
            }
            renderDimText(dimText, textX, textY, angle, color, dimScale, textH, scMode);
        };

        // Radial dimension
        const drawRadialDimension = (dim: RadiusAnnotationDimension, color: string) => {
            const geom = calculateRadialDimensionGeometry(dim);
            const { center, touchPoint, textPosition, isInside } = geom;

            const prec = dim.precision ?? dimStyleSettings.precision ?? 2;
            const dimText = dim.textOverride ?? `R = ${dim.radius.toFixed(prec)}'`;
            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const arrowSt = dim.arrowStyle || dimStyleSettings.arrowStyle || 'closed';
            const arrowSz = dim.arrowSize ?? dimStyleSettings.arrowSize ?? 8.0;
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';

            drawCenterCross(center.easting, center.northing, color);

            ctx.strokeStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.setLineDash([]);

            // Leader from touchPoint to textPosition
            ctx.beginPath();
            ctx.moveTo(touchPoint.easting, touchPoint.northing);
            ctx.lineTo(textPosition.easting, textPosition.northing);
            ctx.stroke();

            // Arrowhead at touchPoint
            const ldx = textPosition.easting - touchPoint.easting;
            const ldy = textPosition.northing - touchPoint.northing;
            const llen = Math.hypot(ldx, ldy);
            if (llen > 1e-6) {
                const arrowDirX = isInside ? ldx / llen : -ldx / llen;
                const arrowDirY = isInside ? ldy / llen : -ldy / llen;
                drawArrow(touchPoint.easting, touchPoint.northing, arrowDirX, arrowDirY, color, arrowSt, arrowSz);
            }

            renderDimText(dimText, textPosition.easting, textPosition.northing, 0, color, dimScale, textH, scMode);
        };

        // Diameter dimension
        const drawDiameterDimension = (dim: DiameterAnnotationDimension, color: string) => {
            const geom = calculateDiameterDimensionGeometry(dim);
            const { center, touch1, touch2, textPosition } = geom;

            const prec = dim.precision ?? dimStyleSettings.precision ?? 2;
            const dimText = dim.textOverride ?? `Ø = ${(dim.radius * 2).toFixed(prec)}'`;
            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const arrowSt = dim.arrowStyle || dimStyleSettings.arrowStyle || 'closed';
            const arrowSz = dim.arrowSize ?? dimStyleSettings.arrowSize ?? 8.0;
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';

            drawCenterCross(center.easting, center.northing, color);

            ctx.strokeStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.setLineDash([]);

            // Line through center connecting touch1 and touch2 and extending to text
            ctx.beginPath();
            ctx.moveTo(touch1.easting, touch1.northing);
            ctx.lineTo(touch2.easting, touch2.northing);
            ctx.lineTo(textPosition.easting, textPosition.northing);
            ctx.stroke();

            // Arrows at touch1 and touch2
            const dx = touch2.easting - touch1.easting;
            const dy = touch2.northing - touch1.northing;
            const dlen = Math.hypot(dx, dy);
            if (dlen > 1e-6) {
                const ux = dx / dlen;
                const uy = dy / dlen;
                drawArrow(touch1.easting, touch1.northing, -ux, -uy, color, arrowSt, arrowSz);
                drawArrow(touch2.easting, touch2.northing, ux, uy, color, arrowSt, arrowSz);
            }

            renderDimText(dimText, textPosition.easting, textPosition.northing, 0, color, dimScale, textH, scMode);
        };

        // Arc length dimension
        const drawArcLengthDimension = (dim: ArcLengthAnnotationDimension, color: string) => {
            const geom = calculateArcLengthDimensionGeometry(dim);
            const { center, dimRadius, startAngle, endAngle, midpoint, w1Start, w1End, w2Start, w2End } = geom;

            const prec = dim.precision ?? dimStyleSettings.precision ?? 2;
            const dimText = dim.textOverride ?? `⌒ L = ${dim.arcLength.toFixed(prec)}'`;
            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const arrowSt = dim.arrowStyle || dimStyleSettings.arrowStyle || 'closed';
            const arrowSz = dim.arrowSize ?? dimStyleSettings.arrowSize ?? 8.0;
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';

            ctx.strokeStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.setLineDash([]);

            // Radial witness lines
            ctx.beginPath();
            ctx.moveTo(w1Start.easting, w1Start.northing);
            ctx.lineTo(w1End.easting, w1End.northing);
            ctx.stroke();

            ctx.beginPath();
            ctx.moveTo(w2Start.easting, w2Start.northing);
            ctx.lineTo(w2End.easting, w2End.northing);
            ctx.stroke();

            // Concentric arc
            ctx.beginPath();
            ctx.arc(center.easting, center.northing, dimRadius, startAngle, endAngle, !dim.isLeftCurve);
            ctx.stroke();

            // Tangent arrowheads at w1End and w2End
            const t1Angle = dim.isLeftCurve ? startAngle + Math.PI / 2 : startAngle - Math.PI / 2;
            const t2Angle = dim.isLeftCurve ? endAngle - Math.PI / 2 : endAngle + Math.PI / 2;
            drawArrow(w1End.easting, w1End.northing, Math.cos(t1Angle), Math.sin(t1Angle), color, arrowSt, arrowSz);
            drawArrow(w2End.easting, w2End.northing, Math.cos(t2Angle), Math.sin(t2Angle), color, arrowSt, arrowSz);

            renderDimText(dimText, midpoint.easting, midpoint.northing, geom.midAngle + Math.PI / 2, color, dimScale, textH, scMode);
        };

        // Curve data callout card
        const drawCurveDataDimension = (dim: CurveDataAnnotationDimension, color: string, isSel: boolean) => {
            const curveInfo = calculateCurveData({
                radius: dim.radius,
                arcLength: dim.arcLength,
                deltaRad: dim.deltaRad,
                p1: dim.p1,
                p2: dim.p2,
                chordBearing: dim.chordBearing,
                chordLength: dim.chordLength,
                tangentBearing: dim.tangentBearing,
                curveDirection: dim.curveDirection,
            });

            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';

            const chordMidX = (dim.p1.easting + dim.p2.easting) / 2;
            const chordMidY = (dim.p1.northing + dim.p2.northing) / 2;
            const midAngle = Math.atan2(chordMidY - dim.center.northing, chordMidX - dim.center.easting);
            const curveMidX = dim.center.easting + dim.radius * Math.cos(midAngle);
            const curveMidY = dim.center.northing + dim.radius * Math.sin(midAngle);

            ctx.strokeStyle = color;
            ctx.lineWidth = inverseScale * (isSel ? 1.8 : 1);
            ctx.setLineDash([]);
            ctx.beginPath();
            ctx.moveTo(curveMidX, curveMidY);
            ctx.lineTo(dim.textPosition.easting, dim.textPosition.northing);
            ctx.stroke();

            const ldx = curveMidX - dim.textPosition.easting;
            const ldy = curveMidY - dim.textPosition.northing;
            const llen = Math.hypot(ldx, ldy);
            if (llen > 1e-6) {
                drawArrow(curveMidX, curveMidY, -ldx / llen, -ldy / llen, color);
            }

            const worldTextHeight = getDimWorldTextHeight(textH, dimScale, scMode);
            const lineHeight = worldTextHeight * 1.35;
            const pad = worldTextHeight * 0.6;

            ctx.save();
            ctx.translate(dim.textPosition.easting, dim.textPosition.northing);
            ctx.scale(1, -1);

            ctx.font = `bold ${worldTextHeight}px monospace`;
            let maxW = ctx.measureText('CURVE DATA').width;
            curveInfo.calloutLines.forEach(l => {
                const w = ctx.measureText(l).width;
                if (w > maxW) maxW = w;
            });

            const cardW = maxW + pad * 2;
            const cardH = (curveInfo.calloutLines.length + 1) * lineHeight + pad * 2;
            const cardX = 0;
            const cardY = -cardH / 2;

            ctx.fillStyle = 'rgba(15, 23, 42, 0.94)';
            ctx.fillRect(cardX, cardY, cardW, cardH);
            ctx.strokeStyle = isSel ? '#F59E0B' : color;
            ctx.lineWidth = inverseScale * (isSel ? 2 : 1.2);
            ctx.strokeRect(cardX, cardY, cardW, cardH);

            ctx.fillStyle = '#38BDF8';
            ctx.font = `bold ${worldTextHeight * 1.05}px sans-serif`;
            ctx.textAlign = 'left';
            ctx.textBaseline = 'top';
            ctx.fillText('CURVE DATA', cardX + pad, cardY + pad);

            ctx.strokeStyle = 'rgba(56, 189, 248, 0.35)';
            ctx.lineWidth = inverseScale * 0.8;
            ctx.beginPath();
            ctx.moveTo(cardX + pad, cardY + pad + lineHeight * 0.95);
            ctx.lineTo(cardX + cardW - pad, cardY + pad + lineHeight * 0.95);
            ctx.stroke();

            ctx.font = `600 ${worldTextHeight * 0.95}px monospace`;
            ctx.fillStyle = '#F8FAFC';
            curveInfo.calloutLines.forEach((row, i) => {
                ctx.fillText(row, cardX + pad, cardY + pad + (i + 1.2) * lineHeight);
            });

            ctx.restore();
        };

        // Angular dimension
        const drawAngularDimension = (dim: AngularAnnotationDimension, color: string) => {
            const geom = calculateAngularDimensionGeometry(dim);
            const { vertex, arcRadius, startAngle, endAngle, midpoint } = geom;

            const dimText = dim.textOverride ?? geom.dimensionText;
            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const arrowSt = dim.arrowStyle || dimStyleSettings.arrowStyle || 'closed';
            const arrowSz = dim.arrowSize ?? dimStyleSettings.arrowSize ?? 8.0;
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';

            ctx.strokeStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.setLineDash([]);

            ctx.beginPath();
            ctx.arc(vertex.easting, vertex.northing, arcRadius, startAngle, endAngle);
            ctx.stroke();

            const a1Tan = startAngle + Math.PI / 2;
            const a2Tan = endAngle - Math.PI / 2;
            const p1Arc = { x: vertex.easting + arcRadius * Math.cos(startAngle), y: vertex.northing + arcRadius * Math.sin(startAngle) };
            const p2Arc = { x: vertex.easting + arcRadius * Math.cos(endAngle), y: vertex.northing + arcRadius * Math.sin(endAngle) };
            drawArrow(p1Arc.x, p1Arc.y, Math.cos(a1Tan), Math.sin(a1Tan), color, arrowSt, arrowSz);
            drawArrow(p2Arc.x, p2Arc.y, Math.cos(a2Tan), Math.sin(a2Tan), color, arrowSt, arrowSz);

            renderDimText(dimText, midpoint.easting, midpoint.northing, geom.midAngle + Math.PI / 2, color, dimScale, textH, scMode);
        };

        // Ordinate dimension
        const drawOrdinateDimension = (dim: OrdinateAnnotationDimension, color: string) => {
            const { point, leaderEnd } = dim;
            const textH = dim.textHeight ?? dimStyleSettings.textHeight ?? 2.5;
            const dimScale = dim.scale ?? 1.0;
            const scMode = dim.scalingMode || dimStyleSettings.scalingMode || 'world';
            const prec = dim.precision ?? dimStyleSettings.precision ?? 2;

            ctx.strokeStyle = color;
            ctx.fillStyle = color;
            ctx.lineWidth = inverseScale;
            ctx.setLineDash([]);

            ctx.beginPath();
            ctx.arc(point.easting, point.northing, 3 * inverseScale, 0, 2 * Math.PI);
            ctx.fill();

            ctx.beginPath();
            ctx.moveTo(point.easting, point.northing);
            ctx.lineTo(leaderEnd.easting, leaderEnd.northing);
            ctx.stroke();

            const label = dim.textOverride ?? `N: ${point.northing.toFixed(prec)}\nE: ${point.easting.toFixed(prec)}`;
            const lines = label.split('\n');
            const worldTextHeight = getDimWorldTextHeight(textH, dimScale, scMode);

            ctx.save();
            ctx.translate(leaderEnd.easting, leaderEnd.northing);
            ctx.scale(1, -1);
            ctx.font = `600 ${worldTextHeight}px monospace`;
            ctx.fillStyle = color;
            lines.forEach((l, i) => {
                ctx.fillText(l, 4 * inverseScale, i * worldTextHeight * 1.2);
            });
            ctx.restore();
        };

        // Draw saved dimensions
        dimensions.forEach(dim => {
            const isSel = selectedDimensionId === dim.id;
            const color = isSel ? '#F59E0B' : (dim.color || dimStyleSettings.color || '#00E5FF');

            switch (dim.type) {
                case 'aligned':
                case 'horizontal':
                case 'vertical':
                    drawLinearDimension(dim, color);
                    break;
                case 'radius':
                    drawRadialDimension(dim, color);
                    break;
                case 'diameter':
                    drawDiameterDimension(dim, color);
                    break;
                case 'arc-length':
                    drawArcLengthDimension(dim, color);
                    break;
                case 'curve-data':
                    drawCurveDataDimension(dim, color, isSel);
                    break;
                case 'angular':
                    drawAngularDimension(dim, color);
                    break;
                case 'ordinate':
                    drawOrdinateDimension(dim, color);
                    break;
            }

            // Draw CAD selection grips when selected
            if (isSel) {
                const dimGrips = getGripsForSelectedDimension(dim);
                const gripSize = 6 * inverseScale;
                dimGrips.forEach(grip => {
                    const isAct = activeDimGrip?.id === grip.id;
                    const isHov = hoveredDimGrip?.id === grip.id;
                    ctx.save();
                    ctx.beginPath();
                    ctx.rect(grip.x - gripSize / 2, grip.y - gripSize / 2, gripSize, gripSize);
                    ctx.fillStyle = isAct ? '#EF4444' : isHov ? '#F59E0B' : '#0284C7';
                    ctx.fill();
                    ctx.strokeStyle = '#FFFFFF';
                    ctx.lineWidth = 1.2 * inverseScale;
                    ctx.stroke();
                    ctx.restore();
                });
            }
        });

        // Highlight curve in phase 0 for curve tools
        if (dimHoveredCurve) {
            ctx.save();
            ctx.strokeStyle = '#38BDF8';
            ctx.lineWidth = 3 * inverseScale;
            ctx.shadowColor = '#38BDF8';
            ctx.shadowBlur = 8;
            ctx.beginPath();
            if (dimHoveredCurve.kind === 'circle') {
                ctx.arc(dimHoveredCurve.center.easting, dimHoveredCurve.center.northing, dimHoveredCurve.radius, 0, 2 * Math.PI);
            } else if (dimHoveredCurve.startAngle !== undefined && dimHoveredCurve.centralAngle !== undefined) {
                const endAngle = dimHoveredCurve.isLeftCurve
                    ? dimHoveredCurve.startAngle + dimHoveredCurve.centralAngle
                    : dimHoveredCurve.startAngle - dimHoveredCurve.centralAngle;
                ctx.arc(dimHoveredCurve.center.easting, dimHoveredCurve.center.northing, dimHoveredCurve.radius, dimHoveredCurve.startAngle, endAngle, !dimHoveredCurve.isLeftCurve);
            }
            ctx.stroke();
            ctx.restore();
        }

        // Live preview for dimension tools
        if (isDimensionDrawingMode(drawingMode)) {
            const previewColor = 'rgba(0, 229, 255, 0.6)';
            if ((drawingMode === 'aligned-dim' || drawingMode === 'horiz-dim' || drawingMode === 'vert-dim') && dimP1 && dimP2 && dimPreviewMouse) {
                let offsetDist = 0;
                if (drawingMode === 'aligned-dim') {
                    const dx = dimP2.easting - dimP1.easting;
                    const dy = dimP2.northing - dimP1.northing;
                    const len = Math.hypot(dx, dy);
                    if (len > 1e-9) {
                        const px = -dy / len;
                        const py = dx / len;
                        offsetDist = (dimPreviewMouse.easting - dimP1.easting) * px + (dimPreviewMouse.northing - dimP1.northing) * py;
                    }
                } else if (drawingMode === 'horiz-dim') {
                    offsetDist = dimPreviewMouse.northing - dimP1.northing;
                } else {
                    offsetDist = dimPreviewMouse.easting - dimP1.easting;
                }
                const dimType = drawingMode === 'horiz-dim' ? 'horizontal' : drawingMode === 'vert-dim' ? 'vertical' : 'aligned';
                drawLinearDimension({ id: 'preview', type: dimType, p1: dimP1, p2: dimP2, offsetDist }, previewColor);
            } else if ((drawingMode === 'aligned-dim' || drawingMode === 'horiz-dim' || drawingMode === 'vert-dim') && dimP1 && !dimP2 && dimPreviewMouse) {
                ctx.strokeStyle = previewColor;
                ctx.lineWidth = inverseScale;
                const dashSize = 5 * inverseScale;
                ctx.setLineDash([dashSize, dashSize]);
                ctx.beginPath();
                ctx.moveTo(dimP1.easting, dimP1.northing);
                ctx.lineTo(dimPreviewMouse.easting, dimPreviewMouse.northing);
                ctx.stroke();
                ctx.setLineDash([]);

                ctx.fillStyle = '#00E5FF';
                ctx.beginPath();
                ctx.arc(dimP1.easting, dimP1.northing, 3 * inverseScale, 0, 2 * Math.PI);
                ctx.fill();
            } else if (drawingMode === 'radius-dim' && dimSelectedCurve && dimPreviewMouse) {
                drawRadialDimension({ id: 'preview', type: 'radius', center: dimSelectedCurve.center, radius: dimSelectedCurve.radius, textPosition: dimPreviewMouse }, previewColor);
            } else if (drawingMode === 'diameter-dim' && dimSelectedCurve && dimPreviewMouse) {
                drawDiameterDimension({ id: 'preview', type: 'diameter', center: dimSelectedCurve.center, radius: dimSelectedCurve.radius, textPosition: dimPreviewMouse }, previewColor);
            } else if (drawingMode === 'arc-length-dim' && dimSelectedCurve && dimPreviewMouse) {
                const distFromCenter = Math.hypot(dimPreviewMouse.easting - dimSelectedCurve.center.easting, dimPreviewMouse.northing - dimSelectedCurve.center.northing);
                const offsetDist = distFromCenter - dimSelectedCurve.radius;
                drawArcLengthDimension({ id: 'preview', type: 'arc-length', center: dimSelectedCurve.center, radius: dimSelectedCurve.radius, p1: dimSelectedCurve.p1, p2: dimSelectedCurve.p2, arcLength: dimSelectedCurve.arcLength, offsetDist, isLeftCurve: dimSelectedCurve.isLeftCurve }, previewColor);
            } else if (drawingMode === 'curve-data-dim' && dimSelectedCurve && dimPreviewMouse) {
                drawCurveDataDimension({ id: 'preview', type: 'curve-data', center: dimSelectedCurve.center, radius: dimSelectedCurve.radius, arcLength: dimSelectedCurve.arcLength, deltaRad: dimSelectedCurve.deltaRad, chordBearing: dimSelectedCurve.chordBearing, chordLength: dimSelectedCurve.chordLength, tangentBearing: dimSelectedCurve.tangentBearing, p1: dimSelectedCurve.p1, p2: dimSelectedCurve.p2, textPosition: dimPreviewMouse }, previewColor, false);
            } else if (drawingMode === 'angular-dim' && dimP1 && dimP2 && dimPreviewMouse) {
                const arcRadius = Math.hypot(dimPreviewMouse.easting - dimP1.easting, dimPreviewMouse.northing - dimP1.northing);
                drawAngularDimension({ id: 'preview', type: 'angular', vertex: dimP1, p1: dimP2, p2: dimPreviewMouse, arcRadius: Math.max(0.1, arcRadius) }, previewColor);
            } else if (drawingMode === 'angular-dim' && dimP1 && !dimP2 && dimPreviewMouse) {
                ctx.strokeStyle = previewColor;
                ctx.lineWidth = inverseScale;
                ctx.setLineDash([4 * inverseScale, 4 * inverseScale]);
                ctx.beginPath();
                ctx.moveTo(dimP1.easting, dimP1.northing);
                ctx.lineTo(dimPreviewMouse.easting, dimPreviewMouse.northing);
                ctx.stroke();
                ctx.setLineDash([]);
            } else if (drawingMode === 'ordinate-dim' && dimP1 && dimPreviewMouse) {
                drawOrdinateDimension({ id: 'preview', type: 'ordinate', point: dimP1, leaderEnd: dimPreviewMouse }, previewColor);
            }

            // Snap indicator
            if (dimSnapPoint) {
                const sr = 5 * inverseScale;
                ctx.save();
                ctx.strokeStyle = '#FFD700';
                ctx.lineWidth = 1.5 * inverseScale;
                ctx.shadowColor = '#FFD700';
                ctx.shadowBlur = 6;
                ctx.beginPath();
                ctx.moveTo(dimSnapPoint.easting, dimSnapPoint.northing - sr);
                ctx.lineTo(dimSnapPoint.easting + sr, dimSnapPoint.northing);
                ctx.lineTo(dimSnapPoint.easting, dimSnapPoint.northing + sr);
                ctx.lineTo(dimSnapPoint.easting - sr, dimSnapPoint.northing);
                ctx.closePath();
                ctx.stroke();
                ctx.restore();
            }
        }

        // Live preview for Add Point tool
        if (drawingMode === 'add-point' && dimPreviewMouse) {
            const px = dimPreviewMouse.easting;
            const py = dimPreviewMouse.northing;
            const r1 = 3 * inverseScale;
            const r2 = 7 * inverseScale;
            const arm = 12 * inverseScale;

            ctx.save();
            ctx.strokeStyle = '#10B981'; // Emerald
            ctx.fillStyle = 'rgba(16, 185, 129, 0.2)';
            ctx.lineWidth = 1.5 * inverseScale;

            // Concentric target circles
            ctx.beginPath();
            ctx.arc(px, py, r1, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();

            ctx.beginPath();
            ctx.arc(px, py, r2, 0, 2 * Math.PI);
            ctx.stroke();

            // Crosshairs
            ctx.beginPath();
            ctx.moveTo(px - arm, py);
            ctx.lineTo(px - r2, py);
            ctx.moveTo(px + r2, py);
            ctx.lineTo(px + arm, py);
            ctx.moveTo(px, py - arm);
            ctx.lineTo(px, py - r2);
            ctx.moveTo(px, py + r2);
            ctx.lineTo(px, py + arm);
            ctx.stroke();

            // Text label floating near point
            const ptLabelNum = isPointNumberTaken(addPointNumber) ? queryCacpNextPointNumber() : (addPointNumber || nextAvailablePointNumber || '1');
            const labelText = `PT ${ptLabelNum}`;
            const worldTextHeight = getDimWorldTextHeight(2.4);
            ctx.translate(px + 10 * inverseScale, py + 10 * inverseScale);
            ctx.scale(1, -1);
            ctx.font = `bold ${worldTextHeight}px sans-serif`;
            const tw = ctx.measureText(labelText).width;
            ctx.fillStyle = 'rgba(15, 23, 42, 0.85)';
            ctx.fillRect(-2 * inverseScale, -worldTextHeight, tw + 4 * inverseScale, worldTextHeight + 4 * inverseScale);
            ctx.fillStyle = '#10B981';
            ctx.fillText(labelText, 0, 0);
            ctx.restore();
        }

        if (activeOsnapPoint && (activeGrip || isPickingOtOrigin || isPickingPolarCenter || drawingMode === 'circle' || drawingMode === 'polygon' || isDimensionDrawingMode(drawingMode) || drawingMode === 'add-point' || drawingMode === 'boundary-line' || drawingMode === 'polylines' || drawingMode === 'breaklines' || drawingMode === 'inclusion' || drawingMode === 'exclusion')) {
            const sr = 5 * inverseScale;
            ctx.save();
            ctx.strokeStyle = '#FFD700';
            ctx.lineWidth = 1.5 * inverseScale;
            ctx.shadowColor = '#FFD700';
            ctx.shadowBlur = 5;
            ctx.beginPath();
            ctx.moveTo(activeOsnapPoint.easting, activeOsnapPoint.northing - sr);
            ctx.lineTo(activeOsnapPoint.easting + sr, activeOsnapPoint.northing);
            ctx.lineTo(activeOsnapPoint.easting, activeOsnapPoint.northing + sr);
            ctx.lineTo(activeOsnapPoint.easting - sr, activeOsnapPoint.northing);
            ctx.closePath();
            ctx.stroke();
            const glyph = OSNAP_GLYPHS[activeOsnapPoint.mode] || '*';
            const fontSize = 9 * inverseScale;
            ctx.font = `bold ${fontSize}px Arial`;
            const tw = ctx.measureText(glyph).width;
            const px = activeOsnapPoint.easting + 8 * inverseScale;
            const py = activeOsnapPoint.northing + 8 * inverseScale;
            ctx.fillStyle = 'rgba(10, 14, 25, 0.9)';
            ctx.fillRect(px - 2 * inverseScale, py - fontSize * 0.8, tw + 4 * inverseScale, fontSize + 2 * inverseScale);
            ctx.fillStyle = '#FFD700';
            ctx.fillText(glyph, px, py);
            ctx.restore();
        }

        // --- Draw boundary file overlays ---
        if (boundaryFiles && boundaryFiles.length > 0) {
            const normalizeAngBnd = (a: number) => { let n = a % (2 * Math.PI); if (n < 0) n += 2 * Math.PI; return n; };
            // Union of ALL boundary files' drawn point IDs. Used to prevent cross-tract
            // pointMap contamination: when tract 2 is drafted/redrafted with a transform,
            // its drawn POB (often sharing PN "1" with tract 1) overwrites tract 1's POB
            // entry in pointMap. Any tract resolving its POB or COGO origin via pointMap
            // must exclude ALL drawn IDs, not just its own.
            const allBoundaryDrawnIds = new Set<string>();
            boundaryFiles.forEach(other => {
                (other.drawnPointIds ?? []).forEach(id => allBoundaryDrawnIds.add(id));
            });
            // Rebuild bf geometry cache from scratch each frame so the
            // boundary-align hit-test sees current bf transforms + synth maps.
            bfGeometryCacheRef.current.clear();
            boundaryFiles.forEach(bf => {
                if (bf.hidden) { console.debug('[BFOverlay] skip (hidden)', bf.id, bf.name); return; }
                if (bf.calls.length === 0) { console.debug('[BFOverlay] skip (no calls)', bf.id); return; }
                // Build a per-bf synthesized point map so the amber overlay can draw
                // even before any survey points exist in pointMap. Mirrors the same
                // POB-resolution priority order as handleDrawBoundaryToLinework.
                //
                // FIX (v26.05.29): Previously this code used pointMap.get(firstCall.from)
                // as the COGO walk origin without excluding bf.drawnPointIds. After a
                // Draft+transform sequence, pointMap holds already-transformed positions,
                // causing the amber overlay to start its walk from the post-transform POB
                // and then apply the canvas transform AGAIN — doubling the effect. The
                // fix mirrors the Draft code exactly: skip drawnPointIds when resolving
                // the POB, and walk the COGO chain using only synthMap so stale pointMap
                // entries cannot contaminate the intermediate vertices.
                const synthMap = new Map<string, { easting: number; northing: number }>();
                // The true POB (before the closing call overwrites synthMap[from])
                // and the final walked vertex. Their delta is the raw misclosure
                // used below to paint the closing segment red when the deed doesn't
                // mathematically close back onto the POB.
                let bfPobOriginal: { easting: number; northing: number } | undefined;
                let bfFinalCursor: { easting: number; northing: number } | undefined;
                // Points that are already drawn carry the previous transform baked in;
                // never use them as walk origins.
                const priorDrawnIds = new Set(bf.drawnPointIds ?? []);
                // lookupPt for the DRAWING phase (after synthMap is fully built):
                // synthMap always wins so the fresh walk result is rendered, not the
                // stale drawn positions; fall back to external (non-drawn) pointMap entries.
                const lookupPt = (pn: string | undefined) => {
                    if (!pn) return undefined;
                    return synthMap.get(pn) ?? (!allBoundaryDrawnIds.has(pn) ? pointMap.get(pn) : undefined);
                };
                {
                    const firstCall = bf.calls[0];
                    // POB resolution priority:
                    //   1. If this boundary has been drafted, recover the UN-BAKED
                    //      POB by subtracting the baked translation from the drafted
                    //      POB position. The amber walk then matches white's pre-
                    //      transform geometry, and the full current transform is
                    //      applied via ctx below. Without un-baking, post-draft
                    //      transform edits would double-apply translation and
                    //      mis-rotate (walked-then-rotated ≠ rotated-while-walking).
                    //   2. Pre-draft: try the deed-side key, but skip if any tract has
                    //      already drafted that PN (cross-tract POB pollution guard).
                    //   3. Explicit pobOverride set by the user in the editor.
                    //   4. Centroid of non-drawn project points (matches Draft fallback).
                    //   5. (0,0).
                    const draftedPob = bf.drawnPointIds && bf.drawnPointIds.length > 0
                        ? pointMap.get(bf.drawnPointIds[0])
                        : undefined;
                    let unbakedPob: { easting: number; northing: number } | undefined;
                    if (draftedPob) {
                        const baked0 = bf.bakedTransform;
                        unbakedPob = {
                            easting: draftedPob.easting - (baked0?.translationE ?? 0),
                            northing: draftedPob.northing - (baked0?.translationN ?? 0),
                        };
                    }
                    const pobFromMap = unbakedPob
                        ?? ((firstCall.from && !allBoundaryDrawnIds.has(firstCall.from))
                            ? pointMap.get(firstCall.from)
                            : undefined);
                    let pob: { easting: number; northing: number };
                    if (pobFromMap) {
                        pob = { easting: pobFromMap.easting, northing: pobFromMap.northing };
                    } else if (bf.pobOverride) {
                        pob = { easting: bf.pobOverride.easting, northing: bf.pobOverride.northing };
                    } else {
                        // Mirror handleDrawBoundaryToLinework's centroid fallback so the
                        // amber preview anchors at the exact same location the white Draft
                        // will land at. Without this, "PN match → override → (0,0)" diverges
                        // from the documented "PN → override → centroid → (0,0)" chain.
                        let sumE = 0, sumN = 0, cnt = 0;
                        pointMap.forEach((p) => {
                            if (!allBoundaryDrawnIds.has(p.pointNumber)) {
                                sumE += p.easting; sumN += p.northing; cnt++;
                            }
                        });
                        // Last resort: local origin. Manual / CSV / AI-created
                        // files carry a persisted `pobOverride`, so this only
                        // applies to legacy or restored files. This MUST be a
                        // fixed world point, never the live view center: a
                        // view-derived anchor is recomputed every frame, which
                        // pins the silhouette to the viewport while panning and
                        // makes Draft commit at a different location.
                        pob = cnt > 0
                            ? { easting: sumE / cnt, northing: sumN / cnt }
                            : { easting: 0, northing: 0 };
                    }
                    if (firstCall.from) synthMap.set(firstCall.from, pob);
                    bfPobOriginal = { easting: pob.easting, northing: pob.northing };
                    let cursor = pob;
                    let lastExitBearingRad: number | null = null;
                    for (const call of bf.calls) {
                        // Walk using synthMap ONLY — pointMap entries are potentially stale
                        // drawn positions and must not influence intermediate vertices.
                        const fromHere = (call.from ? synthMap.get(call.from) : undefined) ?? cursor;
                        if (!fromHere) continue;
                        let endPt: { easting: number; northing: number } | null = null;
                        if (call.isCurve && typeof call.curveRadius === 'number' && isFinite(call.curveRadius)
                            && typeof call.arcLength === 'number' && isFinite(call.arcLength) && call.arcLength !== 0) {
                            const centralAngle = call.arcLength / call.curveRadius;
                            let left = false;
                            if ((call as any).curveDirection === 'left') left = true;
                            else if ((call as any).curveDirection === 'right') left = false;
                            else {
                                const tempT = call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : (lastExitBearingRad !== null ? lastExitBearingRad : null);
                                const dirStr = call.chordBearing || call.bearing;
                                if (tempT !== null && dirStr) {
                                    const dirRad = parseBearingToRadians(dirStr);
                                    if (dirRad !== null) {
                                        const half = centralAngle / 2;
                                        const expR = ((tempT + half) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
                                        const expL = ((tempT - half) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
                                        const dnorm = ((dirRad) % (2 * Math.PI) + 2 * Math.PI) % (2 * Math.PI);
                                        const dR = Math.min(Math.abs(dnorm - expR), 2 * Math.PI - Math.abs(dnorm - expR));
                                        const dL = Math.min(Math.abs(dnorm - expL), 2 * Math.PI - Math.abs(dnorm - expL));
                                        if (dL < dR) left = true;
                                    }
                                }
                            }
                            const signedDelta = left ? -centralAngle : centralAngle;
                            const chordStr = call.chordBearing || call.bearing;
                            const chordRad = chordStr ? parseBearingToRadians(chordStr) : null;
                            const tRad: number | null = chordRad !== null
                                ? chordRad - signedDelta / 2
                                : (call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : (lastExitBearingRad !== null ? lastExitBearingRad : null));
                            if (tRad !== null) {
                                endPt = directCurve(fromHere, tRad, call.curveRadius, signedDelta);
                                lastExitBearingRad = tRad + signedDelta;
                            }
                        } else {
                            const bStr = call.bearing || call.chordBearing;
                            const bRad = bStr ? parseBearingToRadians(bStr) : null;
                            const dist = call.distance ? parseDistance(call.distance) : (typeof (call as any).distanceFt === 'number' ? (call as any).distanceFt : null);
                            if (bRad !== null && typeof dist === 'number' && isFinite(dist)) {
                                endPt = direct(fromHere, bRad, dist);
                                lastExitBearingRad = bRad;
                            }
                        }
                        if (endPt) {
                            // Always write into synthMap — removes the old !lookupPt guard
                            // which could suppress storage when pointMap had a stale entry.
                            if (call.to) synthMap.set(call.to, endPt);
                            cursor = endPt;
                        }
                    }
                    bfFinalCursor = cursor;
                }
                // Per-file synth diagnostic — how many points we resolved + bbox.
                // Also drives the per-BF annotation auto-scale below: very
                // large parcels need physically larger world-space text so
                // labels remain legible when zoomed to extents.
                let bMinE = Infinity, bMaxE = -Infinity, bMinN = Infinity, bMaxN = -Infinity;
                {
                    synthMap.forEach(p => {
                        if (p.easting < bMinE) bMinE = p.easting;
                        if (p.easting > bMaxE) bMaxE = p.easting;
                        if (p.northing < bMinN) bMinN = p.northing;
                        if (p.northing > bMaxN) bMaxN = p.northing;
                    });
                    console.debug('[BFOverlay] synth', bf.id, bf.name,
                        'calls:', bf.calls.length, 'synthPts:', synthMap.size,
                        'bbox:', isFinite(bMinE) ? `E[${bMinE.toFixed(1)}..${bMaxE.toFixed(1)}] N[${bMinN.toFixed(1)}..${bMaxN.toFixed(1)}]` : 'empty');
                }
                // Auto-scale for bearing/distance labels: reference parcel is
                // ~300 ft across (= 1.0x). A 3,000-ft parcel becomes 10x, a
                // 15,000-ft parcel hits the 50x ceiling. The user's
                // `lineLabelScale` slider still multiplies on top of this.
                const bfMaxDim = isFinite(bMinE) ? Math.max(bMaxE - bMinE, bMaxN - bMinN) : 0;
                const bfAutoLabelScale = bfMaxDim > 0
                    ? Math.max(1, Math.min(50, bfMaxDim / 300))
                    : 1;
                // Per-BoundaryFile transform (translate + rotate around POB).
                // Apply via canvas context so geometry math stays untouched —
                // the renderer just re-frames the result.
                //
                // The walk above used the UN-BAKED POB (drafted POB minus baked
                // translation), so applying the FULL current transform here gives
                // the same formula white linework uses (just with current params
                // instead of baked). When current == baked, amber lands exactly on
                // white; any edit to ΔE/ΔN/Rot° moves amber by that change only.
                const bfTxE = bf.translationE ?? 0;
                const bfTxN = bf.translationN ?? 0;
                const bfRotDeg = bf.rotationDeg ?? 0;
                const bfFirstPt = lookupPt(bf.calls[0].from);
                const hasBfTransform = (bfTxE !== 0 || bfTxN !== 0 || bfRotDeg !== 0) && !!bfFirstPt;
                // Stash for boundary-align hit-testing (un-baked POB + current transform).
                if (bfFirstPt) {
                    bfGeometryCacheRef.current.set(bf.id, {
                        synthMap: new Map(synthMap),
                        pob: { easting: bfFirstPt.easting, northing: bfFirstPt.northing },
                        txE: bfTxE,
                        txN: bfTxN,
                        rotDeg: bfRotDeg,
                    });
                }
                ctx.save();
                if (hasBfTransform && bfFirstPt) {
                    // Translate to anchor, rotate, translate back, then apply offset.
                    ctx.translate(bfFirstPt.easting + bfTxE, bfFirstPt.northing + bfTxN);
                    ctx.rotate((bfRotDeg * Math.PI) / 180);
                    ctx.translate(-(bfFirstPt.easting - fox), -(bfFirstPt.northing - foy));
                    floatingCtx.setShift(true);
                }
                // Active boundary = blue, inactive (but visible) = amber. Lets the
                // user tell at a glance which tract their transform/POB edits hit.
                const isActiveBf = activeBoundaryFileId && bf.id === activeBoundaryFileId;
                ctx.strokeStyle = isActiveBf
                    ? 'rgba(96, 165, 250, 0.9)'   // blue-400
                    : 'rgba(255, 180, 50, 0.75)'; // amber
                ctx.lineWidth = 2.5 * inverseScale;
                ctx.setLineDash([6 * inverseScale, 3 * inverseScale]);
                ctx.shadowColor = isActiveBf
                    ? 'rgba(96, 165, 250, 0.55)'
                    : 'rgba(255, 180, 50, 0.45)';
                ctx.shadowBlur = 8;
                ctx.beginPath();
                let pathStarted = false;
                for (const call of bf.calls) {
                    const fromPt = lookupPt(call.from);
                    const toPt   = lookupPt(call.to);
                    if (!fromPt) continue;
                    if (!pathStarted) {
                        // Anchor the path at the TRUE POB. synthMap[calls[0].from]
                        // may have been overwritten by an explicit closing call's
                        // computed endpoint, so prefer bfPobOriginal to keep the
                        // walk starting from the deed's point-of-beginning.
                        const startPt = bfPobOriginal ?? fromPt;
                        ctx.moveTo(startPt.easting, startPt.northing);
                        pathStarted = true;
                    }
                    if (call.isCurve && call.curveRadius && call.arcLength) {
                        const bndCentralAngle = call.arcLength / call.curveRadius;
                        // Determine curve direction first
                        let bndLeft = false;
                        if ((call as any).curveDirection === 'left') {
                            bndLeft = true;
                        } else if ((call as any).curveDirection === 'right') {
                            bndLeft = false;
                        } else {
                            const tempTRad = call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : null;
                            const bndDirStr = call.chordBearing || call.bearing;
                            if (tempTRad !== null && bndDirStr) {
                                const bndDirRad = parseBearingToRadians(bndDirStr);
                                if (bndDirRad !== null) {
                                    const half = bndCentralAngle / 2;
                                    const expR = normalizeAngBnd(tempTRad + half);
                                    const expL = normalizeAngBnd(tempTRad - half);
                                    const bndNorm = normalizeAngBnd(bndDirRad);
                                    const dR = Math.min(Math.abs(bndNorm - expR), 2 * Math.PI - Math.abs(bndNorm - expR));
                                    const dL = Math.min(Math.abs(bndNorm - expL), 2 * Math.PI - Math.abs(bndNorm - expL));
                                    if (dL < dR) bndLeft = true;
                                }
                            }
                        }
                        // Resolve tangent bearing geometrically: tangentBearing = chordBearing - signedΔ/2
                        const bndSignedDelta = bndLeft ? -bndCentralAngle : bndCentralAngle;
                        const bndChordStr = call.chordBearing || call.bearing;
                        const bndChordRad = bndChordStr ? parseBearingToRadians(bndChordStr) : null;
                        const tRad: number | null = bndChordRad !== null
                            ? bndChordRad - bndSignedDelta / 2
                            : (call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : null);
                        if (tRad !== null) {
                            const bndCanvasTangent = Math.PI / 2 - tRad;
                            const bndCenterOffset = bndLeft ? bndCanvasTangent + Math.PI / 2 : bndCanvasTangent - Math.PI / 2;
                            const bndCx = fromPt.easting  + call.curveRadius * Math.cos(bndCenterOffset);
                            const bndCy = fromPt.northing + call.curveRadius * Math.sin(bndCenterOffset);
                            const bndStartAngle = Math.atan2(fromPt.northing - bndCy, fromPt.easting - bndCx);
                            const bndEndAngle   = bndLeft ? bndStartAngle + bndCentralAngle : bndStartAngle - bndCentralAngle;
                            ctx.arc(bndCx, bndCy, call.curveRadius, bndStartAngle, bndEndAngle, !bndLeft);
                        } else if (toPt) {
                            ctx.lineTo(toPt.easting, toPt.northing);
                        }
                    } else if (toPt) {
                        ctx.lineTo(toPt.easting, toPt.northing);
                    }
                }
                // Close back to start. synthMap[calls[0].from] may have been
                // overwritten by an explicit closing call's computed endpoint, so
                // the path was anchored at bfPobOriginal (the true POB) above.
                // Decide how to render the closing segment from geometry, not
                // endpoint labels. Draft preserves a misclosed endpoint as a
                // distinct point, so label topology is not a reliable warning
                // signal after commit.
                const lastCall = bf.calls[bf.calls.length - 1];
                const firstFrom = bf.calls[0]?.from;
                const isExplicitClose = !!(lastCall && firstFrom && lastCall.to === firstFrom);
                let bfMisclosureDist = 0;
                if (bfPobOriginal && bfFinalCursor) {
                    bfMisclosureDist = Math.hypot(
                        bfFinalCursor.easting - bfPobOriginal.easting,
                        bfFinalCursor.northing - bfPobOriginal.northing,
                    );
                }
                const closeTol = Math.max(0.1, bfMaxDim * 0.001);
                const isOpenDeed = bfMisclosureDist > closeTol;
                if (isOpenDeed && bfPobOriginal && bfFinalCursor) {
                    // Stroke the walked calls first, then overlay the unclosed gap
                    // in red so an open deed no longer masquerades as closed.
                    ctx.stroke();
                    ctx.save();
                    ctx.strokeStyle = 'rgba(239, 68, 68, 0.95)'; // red-500
                    ctx.shadowColor = 'rgba(239, 68, 68, 0.6)';
                    ctx.shadowBlur = 10;
                    ctx.lineWidth = 3 * inverseScale;
                    ctx.setLineDash([]);
                    ctx.beginPath();
                    ctx.moveTo(bfFinalCursor.easting, bfFinalCursor.northing);
                    ctx.lineTo(bfPobOriginal.easting, bfPobOriginal.northing);
                    ctx.stroke();
                    ctx.restore();
                } else {
                    // Closed (or implied-close) deed: draw the closing segment in the
                    // boundary color back to the true POB when there is no explicit
                    // closing call already terminating the walk there.
                    if (pathStarted && bfPobOriginal && !isExplicitClose) {
                        ctx.lineTo(bfPobOriginal.easting, bfPobOriginal.northing);
                    }
                    ctx.stroke();
                }
                ctx.setLineDash([]);

                // Boundary-align highlight — overdraw the picked call on this bf
                // in a saturated blue (movable) or green (target) so the user can
                // tell which lines drive the alignment math.
                const alignHighlight: { callId: string; color: string } | null =
                    (boundaryAlignMovable?.bfId === bf.id)
                        ? { callId: boundaryAlignMovable.callId, color: 'rgba(59,130,246,1)' }
                        : (boundaryAlignTarget?.bfId === bf.id)
                            ? { callId: boundaryAlignTarget.callId, color: 'rgba(34,197,94,1)' }
                            : null;
                if (alignHighlight) {
                    const hCall = bf.calls.find(c => c.id === alignHighlight.callId);
                    const hFrom = hCall ? lookupPt(hCall.from) : undefined;
                    const hTo   = hCall ? lookupPt(hCall.to)   : undefined;
                    if (hFrom && hTo) {
                        ctx.save();
                        ctx.strokeStyle = alignHighlight.color;
                        ctx.lineWidth = 5 * inverseScale;
                        ctx.shadowColor = alignHighlight.color;
                        ctx.shadowBlur = 12;
                        ctx.beginPath();
                        ctx.moveTo(hFrom.easting, hFrom.northing);
                        ctx.lineTo(hTo.easting,   hTo.northing);
                        ctx.stroke();
                        ctx.restore();
                    }
                }
                // NOTE: ctx.restore() deferred to after the labels loop so the
                // per-BoundaryFile transform also applies to the B&D labels.

                // Draw B&D labels on each boundary call (amber, matching the overlay)
                bf.calls.forEach(call => {
                    const bFromPt = lookupPt(call.from);
                    const bToPt   = lookupPt(call.to);
                    if (!bFromPt || !bToPt) return;
                    let bText = call.chordBearing || call.bearing;
                    // When the user has toggled "Rotated brg" on the active
                    // boundary in the editor, replace the deed bearing on the
                    // canvas annotation with the as-drawn (rotated) bearing.
                    // Geometry rotates CCW (Y-up) by bfRotDeg → compass bearings shift by -bfRotDeg.
                    if (isActiveBf && showRotatedBearings && bfRotDeg && bText) {
                        const rad = parseBearingToRadians(bText);
                        if (rad !== null) {
                            bText = formatBearing(rad - (bfRotDeg * Math.PI) / 180);
                        }
                    }
                    const dText = call.arcLength ? `L=${call.arcLength.toFixed(2)}'` : call.distance;
                    const labelParts: string[] = [];
                    if (agentAnnotMode !== 'distance' && bText) labelParts.push(bText);
                    if (agentAnnotMode !== 'bearing' && dText) labelParts.push(dText);
                    const labelText = labelParts.join(' / ');
                    if (!labelText) return;

                    // Compute label position: arc midpoint for curves, chord midpoint for straights
                    let lmx: number, lmy: number, langle: number;
                    if (call.isCurve && call.curveRadius && call.arcLength) {
                        const bndCA = call.arcLength / call.curveRadius;
                        const bndLeft = (call as any).curveDirection === 'left';
                        const bndSD = bndLeft ? -bndCA : bndCA;
                        const bndCS = call.chordBearing || call.bearing;
                        const bndCR = bndCS ? parseBearingToRadians(bndCS) : null;
                        const tRad_ = bndCR !== null ? bndCR - bndSD / 2 : (call.tangentBearing ? parseBearingToRadians(call.tangentBearing) : null);
                        if (tRad_ !== null) {
                            const ct = Math.PI / 2 - tRad_;
                            const co = bndLeft ? ct + Math.PI / 2 : ct - Math.PI / 2;
                            const cx_ = bFromPt.easting  + call.curveRadius * Math.cos(co);
                            const cy_ = bFromPt.northing + call.curveRadius * Math.sin(co);
                            const sa_ = Math.atan2(bFromPt.northing - cy_, bFromPt.easting - cx_);
                            const ma_ = bndLeft ? sa_ + bndCA / 2 : sa_ - bndCA / 2;
                            lmx = cx_ + call.curveRadius * Math.cos(ma_);
                            lmy = cy_ + call.curveRadius * Math.sin(ma_);
                        } else {
                            lmx = (bFromPt.easting + bToPt.easting) / 2;
                            lmy = (bFromPt.northing + bToPt.northing) / 2;
                        }
                        langle = 0;
                    } else {
                        lmx = (bFromPt.easting + bToPt.easting) / 2;
                        lmy = (bFromPt.northing + bToPt.northing) / 2;
                        const ldx = bToPt.easting - bFromPt.easting;
                        const ldy = bToPt.northing - bFromPt.northing;
                        langle = Math.atan2(ldy, ldx);
                        if (Math.abs(langle) > Math.PI / 2) langle += Math.PI;
                    }

                    const worldTextHeight = 2.0 * lineLabelScale * bfAutoLabelScale;
                    ctx.save();
                    ctx.translate(lmx, lmy);
                    const callLabelRot = (call.labelRotation || 0) * Math.PI / 180;
                    ctx.rotate(langle + callLabelRot);
                    ctx.scale(1, -1);
                    ctx.font = `italic 600 ${worldTextHeight}px Arial`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillStyle = isActiveBf
                        ? 'rgba(96, 165, 250, 0.95)'
                        : 'rgba(255, 180, 50, 0.95)';
                    const tw = ctx.measureText(labelText).width;
                    const pad = worldTextHeight * 0.2;
                    ctx.clearRect(-tw / 2 - pad, -worldTextHeight / 2 - pad, tw + 2 * pad, worldTextHeight + 2 * pad);
                    ctx.fillText(labelText, 0, 0);
                    ctx.restore();
                });

                // Per-tract centroid annotation — parcel ID / owner / lot description.
                // Rendered inside the bf transform so it follows the tract's
                // translation/rotation just like the B&D labels. Centroid is the
                // average of the walked (un-baked) vertices in synthMap.
                const bfTextLines: string[] = [];
                if (bf.parcelId) bfTextLines.push(`Parcel: ${bf.parcelId}`);
                if (bf.ownerName) bfTextLines.push(`N/F ${bf.ownerName}`);
                if (bf.lotDescription) bfTextLines.push(bf.lotDescription);
                if (bfTextLines.length > 0 && synthMap.size > 0) {
                    let sumE = 0, sumN = 0, cnt = 0;
                    synthMap.forEach(p => { sumE += p.easting; sumN += p.northing; cnt++; });
                    const cE = sumE / cnt;
                    const cN = sumN / cnt;
                    // Use lineLabelScale alone (no per-bf autoscale) so every
                    // tract's annotation renders at the same physical size
                    // regardless of parcel bbox.
                    const annTextHeight = lineAnnotationWorldTextHeight();
                    const annLineHeight = annTextHeight * 1.4;
                    ctx.save();
                    ctx.translate(cE, cN);
                    ctx.scale(1, -1);
                    ctx.font = `italic 600 ${annTextHeight}px Arial`;
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    const maxW = Math.max(...bfTextLines.map(l => ctx.measureText(l).width));
                    const padA = annTextHeight * 0.25;
                    const totalH = bfTextLines.length * annLineHeight;
                    ctx.clearRect(-maxW / 2 - padA, -totalH / 2 - padA, maxW + 2 * padA, totalH + 2 * padA);
                    ctx.fillStyle = isActiveBf
                        ? 'rgba(96, 165, 250, 0.95)'
                        : (isLightTheme ? '#374151' : '#D1D5DB');
                    const startY = -(bfTextLines.length - 1) * annLineHeight / 2;
                    bfTextLines.forEach((line, i) => {
                        ctx.fillText(line, 0, startY + i * annLineHeight);
                    });
                    ctx.restore();
                }

                // Close the outer transform save() started before strokeStyle.
                ctx.restore();
            });
        }

        // Draw World/UCS Origin Tuple on canvas at (originX, originY) if active or placed
        const otMode = orientationTuple?.mode ?? 'drafting';
        const otAngle = otMode === 'drafting' 
            ? (orientationTuple?.draftingAngle ?? orientationTuple?.angle ?? 0)
            : (orientationTuple?.viewAngle ?? orientationTuple?.angle ?? 0);
        const otOriginX = otMode === 'drafting'
            ? (orientationTuple?.draftingOriginX ?? orientationTuple?.originX ?? 0)
            : (orientationTuple?.viewOriginX ?? orientationTuple?.originX ?? 0);
        const otOriginY = otMode === 'drafting'
            ? (orientationTuple?.draftingOriginY ?? orientationTuple?.originY ?? 0)
            : (orientationTuple?.viewOriginY ?? orientationTuple?.originY ?? 0);

        if (orientationTuple && (orientationTuple.placed || otAngle !== 0 || otOriginX !== 0 || otOriginY !== 0)) {
            const ox = otOriginX;
            const oy = otOriginY;
            const axisLen = 42 * inverseScale;
            const arrowHead = 8 * inverseScale;
            const textOffset = 8 * inverseScale;
            const fontH = 11 * inverseScale;
            const localAngle = otMode === 'view' ? 0 : otAngle;

            ctx.save();
            ctx.translate(ox, oy);
            if (localAngle !== 0) {
                ctx.rotate(localAngle);
            }
            ctx.lineWidth = Math.max(1.8 * inverseScale, 1.5 * minScreenPixelWidth);

            // Origin box
            ctx.strokeStyle = 'rgba(255, 255, 255, 0.9)';
            ctx.strokeRect(-3 * inverseScale, -3 * inverseScale, 6 * inverseScale, 6 * inverseScale);

            // X-Axis (Green)
            ctx.strokeStyle = '#34D399';
            ctx.fillStyle = '#34D399';
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(axisLen, 0);
            ctx.stroke();
            // X Arrowhead
            ctx.beginPath();
            ctx.moveTo(axisLen, 0);
            ctx.lineTo(axisLen - arrowHead, arrowHead * 0.4);
            ctx.lineTo(axisLen - arrowHead, -arrowHead * 0.4);
            ctx.closePath();
            ctx.fill();

            // Y-Axis (Red)
            ctx.strokeStyle = '#F87171';
            ctx.fillStyle = '#F87171';
            ctx.beginPath();
            ctx.moveTo(0, 0);
            ctx.lineTo(0, axisLen);
            ctx.stroke();
            // Y Arrowhead
            ctx.beginPath();
            ctx.moveTo(0, axisLen);
            ctx.lineTo(arrowHead * 0.4, axisLen - arrowHead);
            ctx.lineTo(-arrowHead * 0.4, axisLen - arrowHead);
            ctx.closePath();
            ctx.fill();

            // Labels 'X' and 'Y'
            ctx.save();
            ctx.scale(1, -1);
            ctx.font = `bold ${fontH}px sans-serif`;
            ctx.fillStyle = '#34D399';
            ctx.textAlign = 'left';
            ctx.textBaseline = 'middle';
            ctx.fillText('X', axisLen + textOffset, 0);

            ctx.fillStyle = '#F87171';
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText('Y', 0, -(axisLen + textOffset));
            ctx.restore();

            ctx.restore();
        }

        ctx.restore(); // --- End world-space pass ---

        // --- Pass 2: Draw screen-space items (Points, Labels, GPS) ---
        // Draw contour labels
        if (contourLabels) {
            // Category style for contour labels (v26.05.19.15)
            const clCat = annotationCategories?.find(c => c.id === 'contour-labels');
            const clStyle = resolveAnnotationCategoryTextStyle(clCat, props.parcelCadTextStyles);
            const clColor = clCat?.color ?? (isLightTheme ? 'rgba(50,50,50,0.9)' : 'rgba(200,200,200,0.9)');
            if (clStyle.visible) {
                const contourBaseSize = 10 * attributeScale * contourLabelScale * clStyle.scale * 0.8;
                const screenTextSize = resolveAnnotationTextSize(contourBaseSize, 'world', transform.scale);
                ctx.font = `${clStyle.fontItalic ? 'italic ' : ''}${clStyle.fontBold ? 'bold ' : ''}${screenTextSize}px ${clStyle.fontFamily}`;
                ctx.fillStyle = clColor;

                contourLabels.forEach(label => {
                    const pt = worldToScreen(label.x, label.y);
                    const screenX = pt.x;
                    const screenY = pt.y;
                
                    if (screenX < -50 || screenX > canvas.clientWidth + 50 || screenY < -20 || screenY > canvas.clientHeight + 20) {
                        return;
                    }

                    ctx.save();
                    ctx.translate(screenX, screenY);
                    ctx.rotate(-label.angle * Math.PI / 180);
                
                    const textWidth = ctx.measureText(label.text).width;
                    ctx.clearRect(-textWidth / 2 - 2, -screenTextSize / 2, textWidth + 4, screenTextSize);
                
                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(applyAnnotationTextCase(label.text, clStyle.textCase), 0, 0);
                    ctx.restore();
                });
            }
        }

        // Draw street-name labels (v26.05.19.4) — road names aligned with road bearing.
        if (props.streetLabels && props.streetLabels.length > 0) {
            // Category style for roads (v26.05.19.15)
            const rdCat = annotationCategories?.find(c => c.id === 'roads');
            const rdStyle = resolveAnnotationCategoryTextStyle(rdCat, props.parcelCadTextStyles);
            const rdColor = rdCat?.color ?? (isLightTheme ? 'rgba(30,80,160,0.92)' : 'rgba(100,180,255,0.92)');
            if (rdStyle.visible) {
                const streetBaseSize = Math.max(8, 11 * attributeScale * rdStyle.scale);
                const streetTextSize = resolveAnnotationTextSize(streetBaseSize, 'world', transform.scale);
                ctx.font = `${rdStyle.fontItalic ? 'italic ' : ''}${rdStyle.fontBold ? 'bold ' : ''}${streetTextSize}px ${rdStyle.fontFamily}`;
                ctx.fillStyle = rdColor;

                props.streetLabels.forEach(label => {
                    const pt = worldToScreen(label.x, label.y);
                    const screenX = pt.x;
                    const screenY = pt.y;

                    if (screenX < -100 || screenX > canvas.clientWidth + 100 || screenY < -20 || screenY > canvas.clientHeight + 20) {
                        return;
                    }

                    ctx.save();
                    ctx.translate(screenX, screenY);
                    ctx.rotate(-label.angle * Math.PI / 180);

                    const textWidth = ctx.measureText(label.text).width;
                    // Slight background clear so text is readable over contours/lines
                    const pad = 3;
                    ctx.clearRect(-textWidth / 2 - pad, -streetTextSize / 2 - 1, textWidth + pad * 2, streetTextSize + 2);

                    ctx.textAlign = 'center';
                    ctx.textBaseline = 'middle';
                    ctx.fillText(applyAnnotationTextCase(label.text, rdStyle.textCase), 0, 0);
                    ctx.restore();
                });
            }
        }

        // Draw parcel centroid labels (v26.05.19.15) — "N/F [OWNER]" annotation.
        if (parcelLabels && parcelLabels.length > 0) {
            const parcelTextStyle = resolveParcelTextStyle(props.parcelLabelFormatter, props.parcelCadTextStyles);
            const poCat = annotationCategories?.find(c => c.id === 'property-owners');
            const poCategoryStyle = resolveAnnotationCategoryTextStyle(poCat, props.parcelCadTextStyles);
            const poVisible = poCategoryStyle.visible;
            const poColor = poCat?.color ?? '#86efac';
            const poUsesCadCategory = poCat?.styleSource === 'cad-manager' && !!poCat?.cadTextStyleName;
            const poFamily = poUsesCadCategory ? poCategoryStyle.fontFamily : (parcelTextStyle.fontFamily || poCat?.fontFamily || 'Arial Narrow');
            const poItalic = poUsesCadCategory ? poCategoryStyle.fontItalic : parcelTextStyle.fontItalic;
            const poBold = poUsesCadCategory ? poCategoryStyle.fontBold : parcelTextStyle.fontBold;
            const poLineSpacing = poUsesCadCategory ? poCategoryStyle.lineSpacing : parcelTextStyle.lineSpacing;
            const poTextCase = poUsesCadCategory ? poCategoryStyle.textCase : parcelTextStyle.textCase;
            const poCatScale = poCategoryStyle.scale || 0.8;
            const parcelBaseFontSize = Math.max(7, 10 * attributeScale * annotationScale * poCatScale);
            const poFontSize = resolveAnnotationTextSize(parcelBaseFontSize, 'world', transform.scale);
            const poFontStr = `${poItalic ? 'italic ' : ''}${poBold ? 'bold ' : ''}${poFontSize}px ${poFamily}`;
            if (poVisible) {
                ctx.font = poFontStr;
                ctx.fillStyle = poColor;
                ctx.textAlign = 'center';
                ctx.textBaseline = 'middle';

                parcelLabels.forEach(label => {
                    const pt = worldToScreen(label.x, label.y);
                    const screenX = pt.x;
                    const screenY = pt.y;

                    if (screenX < -120 || screenX > canvas.clientWidth + 120 || screenY < -30 || screenY > canvas.clientHeight + 30) {
                        return;
                    }

                    ctx.save();
                    ctx.translate(screenX, screenY);
                    ctx.rotate(-label.angle * Math.PI / 180);

                    const parcelLines = buildParcelLabelLines(label, props.parcelLabelFormatter);
                    if (parcelLines.length === 0) {
                        ctx.restore();
                        return;
                    }

                    const lines = parcelLines
                        .map(line => applyAnnotationTextCase(line, poTextCase));
                    const lineHeight = poFontSize * poLineSpacing;
                    const lineCount = lines.length;
                    const yOffset = -((lineCount - 1) * lineHeight) / 2;

                    // Background clear for readability
                    ctx.font = poFontStr;
                    const maxW = Math.max(...lines.map(line => ctx.measureText(line).width));
                    const totalH = lineHeight * lineCount;
                    ctx.clearRect(-maxW / 2 - 2, yOffset - lineHeight / 2 - 1, maxW + 4, totalH + 2);

                    lines.forEach((line, index) => {
                        ctx.fillText(line, 0, yOffset + (index * lineHeight));
                    });

                    ctx.restore();
                });
            }
        }
        
        // Draw points and labels
        // Reset annotation hit-boxes each frame so stale entries don't trigger
        // Ctrl/Shift click handlers after a point loses its annotation.
        annotationHitBoxesRef.current.clear();
        // Shrinkwrap source pns set for O(1) lookup during point rendering
        const shrinkwrapPnSet: Set<string> | null = props.shrinkwrapSourcePns?.length
            ? new Set(props.shrinkwrapSourcePns)
            : null;
        const pendingDeleteSet: Set<string> | null = props.pendingDeletePointNumbers?.size
            ? props.pendingDeletePointNumbers
            : null;
        points.forEach(p => {
            // Skip intermediate curve points - they should not be drawn on screen
            const pNumberLower = p.pointNumber?.toLowerCase() || '';
            if (pNumberLower.includes('int-') || pNumberLower.startsWith('int_')) {
                return; // Skip intermediate curve approximation points
            }

            const pt = worldToScreen(p.easting, p.northing);
            const screenX = pt.x;
            const screenY = pt.y;

            if (screenX < -20 || screenX > canvas.clientWidth + 20 || screenY < -20 || screenY > canvas.clientHeight + 20) {
                return;
            }

            // Select-mode pick ring — drawn under the glyph like the shrinkwrap ring.
            if (selectedPointNumbers.has(p.pointNumber)) {
                ctx.save();
                ctx.beginPath();
                ctx.arc(screenX, screenY, 8, 0, 2 * Math.PI);
                ctx.strokeStyle = '#22d3ee';
                ctx.lineWidth = 2;
                ctx.setLineDash([3, 2]);
                ctx.stroke();
                ctx.restore();
            }

            // Shrinkwrap highlight ring — drawn before the point itself so it sits underneath
            if (shrinkwrapPnSet?.has(p.pointNumber)) {
                const isHov = p.pointNumber === hoveredShrinkwrapPn;
                ctx.save();
                ctx.beginPath();
                ctx.arc(screenX, screenY, isHov ? 11 : 7, 0, 2 * Math.PI);
                ctx.strokeStyle = isHov ? '#EF4444' : '#A78BFA';
                ctx.lineWidth = isHov ? 2.5 : 1.5;
                ctx.stroke();
                ctx.restore();
            }

            ctx.fillStyle = '#FFD700';
            ctx.strokeStyle = '#000000';
            ctx.lineWidth = 0.5;

            const symbol = getSymbolForPoint(p);
            if (symbol && symbol.svgPath) {
                const viewBoxParts = symbol.viewBox.split(' ').map(Number);
                const [vx, vy, vw, vh] = viewBoxParts.length === 4 ? viewBoxParts : [0, 0, 24, 24];
                // Symbols are world geometry (like CAD blocks): they must grow
                // and shrink with zoom exactly like linework. We normalize every
                // glyph to a consistent world size regardless of its source
                // viewBox (built-in 24×24 vs arbitrary DXF extents), then
                // multiply by transform.scale so it tracks zoom. Baseline of
                // 9.6 world units preserves the previous on-screen size at 1:1
                // zoom (24 × 0.4) so existing scenes look unchanged there.
                const viewExtent = Math.max(vw, vh) || 24;
                const targetWorldExtent = 9.6 * (symbol.scale || 1) * (symbolScale || 1);
                const symbolScreenScale = (targetWorldExtent / viewExtent) * transform.scale;
                const strokePath = new Path2D(symbol.svgPath);
                const fillPath = symbol.fillPath ? new Path2D(symbol.fillPath) : null;

                ctx.save();
                ctx.translate(screenX, screenY);
                ctx.scale(symbolScreenScale, -symbolScreenScale);
                ctx.translate(-(vx + vw / 2), -(vy + vh / 2));

                const symbolColor = isLightTheme ? '#111827' : '#FFD700';

                if (fillPath) {
                    ctx.fillStyle = symbolColor;
                    ctx.fill(fillPath);
                }

                ctx.strokeStyle = symbolColor;
                // Keep the outline a roughly constant on-screen weight (~1.5px)
                // even as the glyph scales with zoom — the CAD "lineweight is
                // screen-constant, geometry scales" convention. Guard against a
                // zero scale (fully zoomed out) to avoid a non-finite width.
                ctx.lineWidth = symbolScreenScale > 0 ? 1.5 / symbolScreenScale : 1.5;
                ctx.stroke(strokePath);

                ctx.restore();
            } else {
                const pointRadius = 1.5;
                ctx.beginPath();
                ctx.arc(screenX, screenY, pointRadius, 0, 2 * Math.PI);
                ctx.fill();
                ctx.stroke();
            }

            // Draw labels
            // Check if we should use world-relative (scaling with zoom) or screen-relative (fixed size)
            const useWorldScaling = settings.pointAttributeScaling === 'world';
            const screenTextSize = useWorldScaling ? (10 * attributeScale * transform.scale) : (10 * attributeScale);
            
            // Optimization: Skip labels if they are too small to read
            if (screenTextSize < 5) return;

            ctx.font = `${screenTextSize}px Arial`;
            
            // Base text offset in screen pixels
            const baseTextOffset = 5 * attributeScale;
            
            // Label offsets are always stored in world coordinates
            // Leader line length ALWAYS scales with zoom (for both modes)
            // Only the text size differs between modes
            const labelOffsetX = p.labelOffset 
              ? p.labelOffset.x * transform.scale
              : baseTextOffset;
            const labelOffsetY = p.labelOffset 
              ? p.labelOffset.y * transform.scale
              : baseTextOffset;
            
            const areAnyLabelsVisible = pointLayers.pointNumber.visible || pointLayers.elevation.visible || pointLayers.description.visible;

            if (p.labelOffset && (p.labelOffset.x !== 0 || p.labelOffset.y !== 0) && areAnyLabelsVisible) {
                ctx.beginPath();
                ctx.moveTo(screenX, screenY);
                ctx.lineTo(screenX + labelOffsetX, screenY + labelOffsetY);
                ctx.strokeStyle = "rgba(128, 128, 128, 0.7)";
                ctx.lineWidth = 1;
                ctx.setLineDash([4, 2]);
                ctx.stroke();
                ctx.setLineDash([]);
            }
            
            if (pointLayers.pointNumber.visible) {
                ctx.fillStyle = pointLayers.pointNumber.color;
                ctx.fillText(p.pointNumber, screenX + labelOffsetX, screenY + labelOffsetY - screenTextSize);
            }
            if (pointLayers.elevation.visible && p.elevation !== undefined) {
                ctx.fillStyle = pointLayers.elevation.color;
                ctx.fillText(p.elevation.toFixed(settings.coordinatePrecision), screenX + labelOffsetX, screenY + labelOffsetY);
            }
            if (pointLayers.description.visible && p.description) {
                ctx.fillStyle = pointLayers.description.color;
                ctx.fillText(p.description, screenX + labelOffsetX, screenY + labelOffsetY + screenTextSize);
            }

                    // Pending-delete ring — pulsing red halo so the user can verify
                    // which points are about to be removed before confirming.
                    if (pendingDeleteSet?.has(p.pointNumber)) {
                        const pulse = 0.75 + 0.25 * Math.sin(Date.now() / 180);
                        ctx.save();
                        ctx.beginPath();
                        ctx.arc(screenX, screenY, 14 * pulse, 0, 2 * Math.PI);
                        ctx.strokeStyle = 'rgba(239, 68, 68, 0.9)'; // red-500
                        ctx.lineWidth = 2.5;
                        ctx.stroke();
                        ctx.beginPath();
                        ctx.arc(screenX, screenY, 20 * pulse, 0, 2 * Math.PI);
                        ctx.strokeStyle = 'rgba(239, 68, 68, 0.35)';
                        ctx.lineWidth = 1.5;
                        ctx.stroke();
                        ctx.restore();
                    }

                    // -----------------------------------------------------------------
                    // Annotation rules (leader + note) — applies only to matching points.
                    // Mirrors the symbol pattern: per-rule scale × global annotationScale.
                    // Offset is in world units; the leader bends at that point and a
                    // horizontal underline carries the text to the right.
                    // -----------------------------------------------------------------
            const annotRule = getAnnotationForPoint(p);
            if (annotRule) {
                const noteText = renderAnnotationTemplate(annotRule.template, p);
                const annCat = annotationCategories?.find(c => c.id === 'callout') ?? null;
                const annStyle = resolveAnnotationCategoryTextStyle(annCat, props.parcelCadTextStyles);
                const noteLines = noteText.split('\n').map(line => applyAnnotationTextCase(line, annStyle.textCase));
                // Apply category-driven existing overrides (v26.05.19.15)
                const isExisting = annotRule.category === 'existing';
                const existingItalic = annCat ? annCat.existingItalic : true;
                const existingScaleMult = annCat ? annCat.existingScale : 0.25;
                const ruleScaleMult = isExisting ? existingScaleMult : 1;
                const combinedScale = (annotRule.scale || 1) * (annotationScale || 1) * ruleScaleMult;
                const applyItalic = (isExisting && existingItalic) || annStyle.fontItalic;
                const noteFontSize = resolveAnnotationTextSize(11 * combinedScale, 'world', transform.scale);
                // Per-point override (Ctrl+drag / Shift+click mirror) takes
                // precedence over the rule's default leaderOffset.
                const effDx = p.annotationOffset ? p.annotationOffset.dx : annotRule.leaderOffset.dx;
                const effDy = p.annotationOffset ? p.annotationOffset.dy : annotRule.leaderOffset.dy;
                const mirrored = effDx < 0;
                // Bend point in screen coords (world offset → pixels via transform.scale)
                const bendX = screenX + effDx * transform.scale;
                const bendY = screenY - effDy * transform.scale;
                // Horizontal underline length sized to widest line; on the
                // mirrored side, underline extends to the LEFT of the bend.
                ctx.font = `${applyItalic ? 'italic ' : ''}${annStyle.fontBold ? 'bold ' : ''}${noteFontSize}px ${annStyle.fontFamily}`;
                const widest = Math.max(...noteLines.map(l => ctx.measureText(l).width));
                const underlineLen = widest + 6 * combinedScale;
                const underlineEndX = mirrored ? bendX - underlineLen : bendX + underlineLen;
                const color = annotRule.color || annStyle.color || (annotRule.category === 'proposed' ? '#f472b6' : '#22d3ee');
                // Leader: point → bend → end of underline
                ctx.beginPath();
                ctx.moveTo(screenX, screenY);
                ctx.lineTo(bendX, bendY);
                ctx.lineTo(underlineEndX, bendY);
                ctx.strokeStyle = color;
                ctx.lineWidth = Math.max(1, 1.2 * combinedScale);
                ctx.setLineDash([]);
                ctx.stroke();
                // Tiny arrowhead at point
                const ah = 4 * combinedScale;
                const ang = Math.atan2(bendY - screenY, bendX - screenX);
                ctx.beginPath();
                ctx.moveTo(screenX, screenY);
                ctx.lineTo(screenX + ah * Math.cos(ang - 0.4), screenY + ah * Math.sin(ang - 0.4));
                ctx.lineTo(screenX + ah * Math.cos(ang + 0.4), screenY + ah * Math.sin(ang + 0.4));
                ctx.closePath();
                ctx.fillStyle = color;
                ctx.fill();
                // Text — stacked upward from the underline. Right-align on the
                // mirrored side so the underline reads naturally toward the bend.
                ctx.fillStyle = color;
                ctx.textBaseline = 'alphabetic';
                const prevAlign = ctx.textAlign;
                ctx.textAlign = mirrored ? 'right' : 'left';
                const textX = mirrored ? bendX - 3 * combinedScale : bendX + 3 * combinedScale;
                noteLines.forEach((ln, i) => {
                    const ty = bendY - 3 * combinedScale - (noteLines.length - 1 - i) * (noteFontSize * annStyle.lineSpacing);
                    ctx.fillText(ln, textX, ty);
                });
                ctx.textAlign = prevAlign;
                // Record screen-space hit box for Ctrl/Shift click handlers.
                const totalH = noteLines.length * noteFontSize * 1.15 + 4 * combinedScale;
                const boxX = mirrored ? bendX - underlineLen : bendX;
                const boxY = bendY - totalH;
                annotationHitBoxesRef.current.set(p.pointNumber, {
                    x: boxX,
                    y: boxY,
                    w: underlineLen,
                    h: totalH + 4 * combinedScale,
                });
            }
        });
        
        // Draw current GPS position
        if (projectedPosition) {
            const pt = worldToScreen(projectedPosition.easting, projectedPosition.northing);
            const screenX = pt.x;
            const screenY = pt.y;

            ctx.fillStyle = 'rgba(0, 191, 255, 0.8)';
            ctx.strokeStyle = 'white';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(screenX, screenY, 8, 0, 2 * Math.PI);
            ctx.fill();
            ctx.stroke();
        }

        // OFFSET ghost preview — dashed parallel geometry following the cursor side.
        const previewOffsetSegments = offsetPreviewSegmentsRef.current;
        if (previewOffsetSegments && previewOffsetSegments.length > 0) {
            ctx.save();
            ctx.setLineDash([6, 4]);
            ctx.lineWidth = 2;
            ctx.strokeStyle = 'rgba(34, 211, 238, 0.95)';
            ctx.beginPath();
            for (const seg of previewOffsetSegments) {
                if (seg.kind === 'circle') {
                    const c = worldToScreen(seg.center.x, seg.center.y);
                    const r = seg.radius * transform.scale;
                    ctx.moveTo(c.x + r, c.y);
                    ctx.arc(c.x, c.y, r, 0, 2 * Math.PI);
                    continue;
                }
                if (seg.kind === 'arc') {
                    const c = worldToScreen(seg.center.x, seg.center.y);
                    const start = worldToScreen(seg.from.x, seg.from.y);
                    const startAngle = Math.atan2(start.y - c.y, start.x - c.x);
                    const endAngle = startAngle + (seg.isLeftCurve ? -seg.centralAngle : seg.centralAngle);
                    ctx.moveTo(start.x, start.y);
                    ctx.arc(c.x, c.y, seg.radius * transform.scale, startAngle, endAngle, seg.isLeftCurve);
                    continue;
                }
                const a = worldToScreen(seg.from.x, seg.from.y);
                const b = worldToScreen(seg.to.x, seg.to.y);
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
            }
            ctx.stroke();
            ctx.restore();
        }

        // ARRAY ghost preview — dashed copies while the X/Y manipulator is open.
        // Draw from one base geometry and per-copy offsets instead of allocating
        // a translated segment for every copy, so large arrays stay light.
        const previewArray = arrayPreviewRef.current;
        if (previewArray && previewArray.baseSegments.length > 0) {
            ctx.save();
            ctx.setLineDash([7, 5]);
            ctx.lineWidth = 1.75;
            ctx.strokeStyle = 'rgba(196, 181, 253, 0.9)';
            ctx.beginPath();
            // At most a 64 × 64 representative grid of ghosts is drawn. This
            // keeps 100 × 100 arrays smooth while still showing their extent;
            // Apply always creates the full array.
            const previewRows = previewArray.rows;
            const previewColumns = previewArray.columns;
            const previewSampleAxis = 64;
            const rowStep = Math.max(1, Math.ceil((previewRows - 1) / previewSampleAxis));
            const columnStep = Math.max(1, Math.ceil((previewColumns - 1) / previewSampleAxis));
            for (let row = 0; row < previewRows; row++) {
                if (row !== 0 && row !== previewRows - 1 && row % rowStep !== 0) continue;
                for (let column = 0; column < previewColumns; column++) {
                    if (column !== 0 && column !== previewColumns - 1 && column % columnStep !== 0) continue;
                    if (row === 0 && column === 0) continue;
                    const localX = column * previewArray.columnGap;
                    const localY = row * previewArray.rowGap;
                    const dx = localX * previewArray.cos - localY * previewArray.sin;
                    const dy = localX * previewArray.sin + localY * previewArray.cos;
                    for (const seg of previewArray.baseSegments) {
                        if (seg.kind === 'circle') {
                            const c = worldToScreen(seg.center.x + dx, seg.center.y + dy);
                            const r = seg.radius * transform.scale;
                            ctx.moveTo(c.x + r, c.y);
                            ctx.arc(c.x, c.y, r, 0, 2 * Math.PI);
                            continue;
                        }
                        if (seg.kind === 'arc') {
                            const c = worldToScreen(seg.center.x + dx, seg.center.y + dy);
                            const start = worldToScreen(seg.from.x + dx, seg.from.y + dy);
                            const startAngle = Math.atan2(start.y - c.y, start.x - c.x);
                            const endAngle = startAngle + (seg.isLeftCurve ? -seg.centralAngle : seg.centralAngle);
                            ctx.moveTo(start.x, start.y);
                            ctx.arc(c.x, c.y, seg.radius * transform.scale, startAngle, endAngle, seg.isLeftCurve);
                            continue;
                        }
                        const a = worldToScreen(seg.from.x + dx, seg.from.y + dy);
                        const b = worldToScreen(seg.to.x + dx, seg.to.y + dy);
                        ctx.moveTo(a.x, a.y);
                        ctx.lineTo(b.x, b.y);
                    }
                }
            }
            ctx.stroke();
            ctx.restore();
        }

        // POLARARRAY ghost preview — dashed rotational copies while the ring
        // manipulator is open. The step matches the commit path exactly.
        const previewPolar = polarPreviewRef.current;
        if (previewPolar && previewPolar.baseSegments.length > 0) {
            ctx.save();
            ctx.setLineDash([7, 5]);
            ctx.lineWidth = 1.75;
            ctx.strokeStyle = 'rgba(196, 181, 253, 0.9)';
            ctx.beginPath();
            const step = previewPolar.fillAngle >= 359.999
                ? previewPolar.fillAngle / previewPolar.count
                : previewPolar.fillAngle / Math.max(1, previewPolar.count - 1);
            const previewSampleStep = Math.max(1, Math.ceil((previewPolar.count - 1) / 240));
            const rotate = (point: { x: number; y: number }, cos: number, sin: number) => {
                const dx = point.x - previewPolar.center.x;
                const dy = point.y - previewPolar.center.y;
                return {
                    x: previewPolar.center.x + dx * cos - dy * sin,
                    y: previewPolar.center.y + dx * sin + dy * cos,
                };
            };
            for (let item = 1; item < previewPolar.count; item++) {
                if (item !== previewPolar.count - 1 && item % previewSampleStep !== 0) continue;
                const radians = (item * step * Math.PI) / 180;
                const cos = Math.cos(radians);
                const sin = Math.sin(radians);
                for (const seg of previewPolar.baseSegments) {
                    if (seg.kind === 'circle') {
                        const center = rotate(seg.center, cos, sin);
                        const c = worldToScreen(center.x, center.y);
                        const r = seg.radius * transform.scale;
                        ctx.moveTo(c.x + r, c.y);
                        ctx.arc(c.x, c.y, r, 0, 2 * Math.PI);
                        continue;
                    }
                    if (seg.kind === 'arc') {
                        const center = rotate(seg.center, cos, sin);
                        const from = rotate(seg.from, cos, sin);
                        const c = worldToScreen(center.x, center.y);
                        const start = worldToScreen(from.x, from.y);
                        const startAngle = Math.atan2(start.y - c.y, start.x - c.x);
                        const endAngle = startAngle + (seg.isLeftCurve ? -seg.centralAngle : seg.centralAngle);
                        ctx.moveTo(start.x, start.y);
                        ctx.arc(c.x, c.y, seg.radius * transform.scale, startAngle, endAngle, seg.isLeftCurve);
                        continue;
                    }
                    const from = rotate(seg.from, cos, sin);
                    const to = rotate(seg.to, cos, sin);
                    const a = worldToScreen(from.x, from.y);
                    const b = worldToScreen(to.x, to.y);
                    ctx.moveTo(a.x, a.y);
                    ctx.lineTo(b.x, b.y);
                }
            }
            ctx.stroke();
            ctx.restore();

            // Show the rotation pivot itself; without this the copies move but
            // the user has no visual confirmation of the selected center.
            const pivot = worldToScreen(previewPolar.center.x, previewPolar.center.y);
            ctx.save();
            ctx.strokeStyle = 'rgba(196, 181, 253, 0.95)';
            ctx.lineWidth = 1.5;
            ctx.beginPath();
            ctx.arc(pivot.x, pivot.y, 6, 0, 2 * Math.PI);
            ctx.moveTo(pivot.x - 11, pivot.y);
            ctx.lineTo(pivot.x + 11, pivot.y);
            ctx.moveTo(pivot.x, pivot.y - 11);
            ctx.lineTo(pivot.x, pivot.y + 11);
            ctx.stroke();
            ctx.restore();
        }

        // Center-pick target follows the cursor, including free points where no
        // entity/survey snap marker is available.
        const polarPickCursor = cursorWorldPosRef.current;
        if (isPickingPolarCenter && polarPickCursor) {
            const target = worldToScreen(polarPickCursor.x, polarPickCursor.y);
            ctx.save();
            ctx.strokeStyle = 'rgba(34, 211, 238, 0.95)';
            ctx.lineWidth = 1.25;
            ctx.beginPath();
            ctx.arc(target.x, target.y, 8, 0, 2 * Math.PI);
            ctx.moveTo(target.x - 13, target.y);
            ctx.lineTo(target.x - 9, target.y);
            ctx.moveTo(target.x + 9, target.y);
            ctx.lineTo(target.x + 13, target.y);
            ctx.moveTo(target.x, target.y - 13);
            ctx.lineTo(target.x, target.y - 9);
            ctx.moveTo(target.x, target.y + 9);
            ctx.lineTo(target.x, target.y + 13);
            ctx.stroke();
            ctx.restore();
        }

        // ROTATE ghost preview — dashed rotated selection following the cursor.
        const previewRotateSegments = rotatePreviewSegmentsRef.current;
        if (previewRotateSegments && previewRotateSegments.length > 0) {
            ctx.save();
            ctx.setLineDash([5, 4]);
            ctx.lineWidth = 2;
            ctx.strokeStyle = scaleGhostActiveRef.current ? 'rgba(52, 211, 153, 0.95)' : 'rgba(251, 191, 36, 0.9)';
            ctx.beginPath();
            for (const seg of previewRotateSegments) {
                if (seg.kind === 'circle') {
                    const c = worldToScreen(seg.center.x, seg.center.y);
                    const r = seg.radius * transform.scale;
                    ctx.moveTo(c.x + r, c.y);
                    ctx.arc(c.x, c.y, r, 0, 2 * Math.PI);
                    continue;
                }
                if (seg.kind === 'arc') {
                    const c = worldToScreen(seg.center.x, seg.center.y);
                    const start = worldToScreen(seg.from.x, seg.from.y);
                    const startAngle = Math.atan2(start.y - c.y, start.x - c.x);
                    const endAngle = startAngle + (seg.isLeftCurve ? -seg.centralAngle : seg.centralAngle);
                    ctx.moveTo(start.x, start.y);
                    ctx.arc(c.x, c.y, seg.radius * transform.scale, startAngle, endAngle, seg.isLeftCurve);
                    continue;
                }
                const a = worldToScreen(seg.from.x, seg.from.y);
                const b = worldToScreen(seg.to.x, seg.to.y);
                ctx.moveTo(a.x, a.y);
                ctx.lineTo(b.x, b.y);
            }
            ctx.stroke();
            ctx.restore();
        }

        ctx.restore(); // --- End screen-space pass ---
    }, [transform, points, lines, centerlines, customSymbols, pointLayers, attributeScale, lineLabelScale, lineThickness, contourLabelScale, settings, getSymbolForPoint, getAnnotationForPoint, customAnnotations, annotationScale, offlineMapAreas, contourLabels, props.streetLabels, parcelLabels, props.parcelLabelFormatter, props.parcelCadTextStyles, annotationCategories, pointMap, drawingMode, polylinePoints, polylineArcMode, projectedPosition, googleOverlayImage, wmsImages, offlineImages, isDeletingLines, isTrimmingLines, trimHoverPreview, deedMetadata, activeAgent, props.ownerNameOffset, props.deedMetadataOffset, dimensions, dimensionScale, symbolScale, dimP1, dimP2, dimPreviewMouse, dimSnapPoint, activeOsnapPoint, selectedLineIds, selectedCenterlineIds, selectedPointNumbers, tinSurfaces, isTinShadingEnabled, boundaryFiles, activeBoundaryFileId, showRotatedBearings, agentAnnotMode, hoveredShrinkwrapPn, props.shrinkwrapSourcePns, props.pendingDeletePointNumbers, floodRings, boundaryAlignMovable, boundaryAlignTarget, isGridVisible, worldToScreen, circleCenter, circleSizeParam, typedCircleValue, circleSubMode, polygonCenter, polygonSides, typedPolygonDiameter, ttrStep, ttrEntity1, ttrEntity2, ttrHoverEntity, solveTtrCircle, activeGrip, hoveredGrip, getGripsForSelectedLines, isPickingPolarCenter]);

    // Throttled draw using requestAnimationFrame to prevent flickering during rapid updates
    const scheduleDraw = useCallback(() => {
        console.log('[DC] scheduleDraw called, pendingFrame:', animationFrameRef.current);
        if (animationFrameRef.current !== null) {
            // Already have a pending frame, skip
            return;
        }
        animationFrameRef.current = requestAnimationFrame(() => {
            console.log('[DC] rAF fired, drawRef set?:', !!drawRef.current);
            animationFrameRef.current = null;
            drawRef.current?.();
        });
    }, []);

    // Mount/unmount sentinel
    useEffect(() => {
        console.log('[DC] MOUNT');
        return () => console.log('[DC] UNMOUNT');
    }, []);

    // Store draw function in ref for stable access
    useEffect(() => {
        console.log('[DC] drawRef <- draw (draw fn changed)');
        drawRef.current = draw;
    }, [draw]);

    // Draw when draw function changes, using requestAnimationFrame for smoothness
    useEffect(() => {
        console.log('[DC] schedule effect firing');
        scheduleDraw();
    }, [draw, scheduleDraw]);

    // Keep re-drawing while pending-delete points are on screen so the red
    // halo actually pulses instead of freezing on a single frame.
    useEffect(() => {
        if (!props.pendingDeletePointNumbers || props.pendingDeletePointNumbers.size === 0) return;
        let alive = true;
        const tick = () => {
            if (!alive) return;
            scheduleDraw();
            requestAnimationFrame(tick);
        };
        const raf = requestAnimationFrame(tick);
        return () => { alive = false; cancelAnimationFrame(raf); };
    }, [props.pendingDeletePointNumbers, scheduleDraw]);
    
    // Cleanup animation frame on unmount
    useEffect(() => {
        return () => {
            if (animationFrameRef.current !== null) {
                cancelAnimationFrame(animationFrameRef.current);
                animationFrameRef.current = null;
            }
        };
    }, []);

    useEffect(() => {
        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const applySize = () => {
            const { width, height } = container.getBoundingClientRect();
            // HiDPI: backing store is CSS px × devicePixelRatio so 1-px strokes
            // land on a single physical pixel instead of being bilinearly upscaled
            // by the browser (which is what made every line look fuzzy when
            // zoomed in). The draw() function applies a setTransform(dpr,...) so
            // all existing CSS-pixel coordinates keep working unchanged.
            const dpr = window.devicePixelRatio || 1;
            canvas.width = Math.max(1, Math.round(width * dpr));
            canvas.height = Math.max(1, Math.round(height * dpr));
            canvas.style.width = width + 'px';
            canvas.style.height = height + 'px';
            const gpuCanvas = gpuCanvasRef.current;
            if (gpuCanvas) {
                gpuCanvas.width = canvas.width;
                gpuCanvas.height = canvas.height;
            }
        };

        applySize();
        const resizeObserver = new ResizeObserver(() => {
            if (isChatResizingRef.current) {
                // While the chat edge is being dragged, keep the CSS box in sync
                // but leave both DPR-scaled backing stores alone. Reallocating
                // them on every pointer frame was a major part of the drag cost;
                // the exact bitmap is restored in one go on release.
                const { width, height } = container.getBoundingClientRect();
                canvas.style.width = width + 'px';
                canvas.style.height = height + 'px';
                return;
            }
            applySize();
            scheduleDraw();
            pushToolbarOutOfChatIfNeeded();
        });
        resizeObserver.observe(container);

        // Re-apply when the display’s DPR changes (zoom level or moving to a
        // different-density monitor).
        const mql = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
        const onDprChange = () => { applySize(); scheduleDraw(); };
        mql.addEventListener?.('change', onDprChange);

        return () => {
            resizeObserver.disconnect();
            mql.removeEventListener?.('change', onDprChange);
        };
    }, [scheduleDraw, pushToolbarOutOfChatIfNeeded]);

    // Restore the exact backing-store size once a chat-panel drag finishes.
    useEffect(() => {
        const wasResizing = wasChatResizingRef.current;
        wasChatResizingRef.current = isChatResizing;
        if (!wasResizing || isChatResizing) return;

        const canvas = canvasRef.current;
        const container = containerRef.current;
        if (!canvas || !container) return;

        const { width, height } = container.getBoundingClientRect();
        const dpr = window.devicePixelRatio || 1;
        canvas.width = Math.max(1, Math.round(width * dpr));
        canvas.height = Math.max(1, Math.round(height * dpr));
        canvas.style.width = width + 'px';
        canvas.style.height = height + 'px';
        const gpuCanvas = gpuCanvasRef.current;
        if (gpuCanvas) {
            gpuCanvas.width = canvas.width;
            gpuCanvas.height = canvas.height;
        }
        scheduleDraw();
    }, [isChatResizing, scheduleDraw]);

    useEffect(() => {
        const onPrefsChanged = () => setGpuPrefVersion(v => v + 1);
        window.addEventListener(WEBGPU_PREF_EVENT, onPrefsChanged);
        return () => window.removeEventListener(WEBGPU_PREF_EVENT, onPrefsChanged);
    }, []);

    useEffect(() => {
        const gpuCanvas = gpuCanvasRef.current;
        const canvas = canvasRef.current;
        if (!gpuCanvas || !canvas) return;

        // Settings → Graphics & WebGPU can turn hardware acceleration off at
        // runtime; the Canvas2D basemap underneath takes over without a reload.
        if (!isWebGpuEnabled() || !WebGpuBasemap.isSupported()) {
            gpuCanvas.style.display = 'none';
            scheduleDraw();
            return;
        }

        let disposed = false;
        let created: WebGpuBasemap | null = null;

        gpuCanvas.style.display = '';
        gpuCanvas.width = canvas.width;
        gpuCanvas.height = canvas.height;
        WebGpuBasemap.create(gpuCanvas, () => {
            gpuCanvas.style.display = 'none';
            scheduleDraw();
        }).then(renderer => {
            if (!renderer) return;
            if (disposed) {
                renderer.dispose();
                return;
            }
            created = renderer;
            gpuBasemapRef.current = renderer;
            scheduleDraw();
        });

        return () => {
            disposed = true;
            gpuBasemapRef.current = null;
            created?.dispose();
        };
    }, [scheduleDraw, gpuPrefVersion]);

    const isOverLabelArea = useCallback((mouseX: number, mouseY: number, p: SurveyPoint) => {
        const { scale } = transform;

        const ptScreen = worldToScreen(p.easting, p.northing);
        const pointScreenX = ptScreen.x;
        const pointScreenY = ptScreen.y;

        const screenTextSize = 10 * attributeScale;
        const textOffset = 5 * attributeScale;
        
        // Convert label offset from world coordinates to screen coordinates
        const labelOffsetWorldX = p.labelOffset ? p.labelOffset.x : textOffset / scale;
        const labelOffsetWorldY = p.labelOffset ? p.labelOffset.y : textOffset / scale;
        
        const labelOffsetScreenX = labelOffsetWorldX * scale;
        const labelOffsetScreenY = labelOffsetWorldY * scale;
        
        const labelBlockX = pointScreenX + labelOffsetScreenX;
        const labelCenterY = pointScreenY + labelOffsetScreenY;
        
        const topOfBlock = labelCenterY - screenTextSize * 1.5;
        const bottomOfBlock = labelCenterY + screenTextSize * 1.5;

        const canvas = canvasRef.current;
        if (!canvas) return false;
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;

        ctx.font = `${screenTextSize.toFixed(2)}px Arial`;
        const texts = [
            pointLayers.pointNumber.visible ? p.pointNumber : '',
            pointLayers.elevation.visible && p.elevation !== undefined ? p.elevation.toFixed(settings.coordinatePrecision) : '',
            pointLayers.description.visible ? p.description : ''
        ].filter((text): text is string => typeof text === 'string' && text.length > 0);
        const blockWidth = texts.length > 0 ? Math.max(...texts.map(t => ctx.measureText(t).width)) : 0;
        
        const PADDING = 15;

        return (
            mouseX >= labelBlockX - PADDING &&
            mouseX <= labelBlockX + blockWidth + PADDING &&
            mouseY >= topOfBlock - PADDING &&
            mouseY <= bottomOfBlock + PADDING
        );
    }, [transform, attributeScale, pointLayers, settings.coordinatePrecision, worldToScreen]);

    // Hit-test the annotation note (leader underline + stacked text) for a point.
    // Uses the screen-space box captured during the most recent draw frame, so
    // the hit area always matches what the user sees regardless of mirror/scale.
    const isOverAnnotationArea = useCallback((mouseX: number, mouseY: number, p: SurveyPoint): boolean => {
        const box = annotationHitBoxesRef.current.get(p.pointNumber);
        if (!box) return false;
        const PAD = 6;
        return (
            mouseX >= box.x - PAD &&
            mouseX <= box.x + box.w + PAD &&
            mouseY >= box.y - PAD &&
            mouseY <= box.y + box.h + PAD
        );
    }, []);

    const isOverLineLabelArea = useCallback((mouseX: number, mouseY: number, line: SurveyLine): boolean => {
        if (!line.bearing || !line.distance || line.isCurve) return false;
        
        const p1 = pointMap.get(line.from);
        const p2 = pointMap.get(line.to);
        if (!p1 || !p2) return false;

        const midX = (p1.easting + p2.easting) / 2;
        const midY = (p1.northing + p2.northing) / 2;
        
        const offsetX = line.labelOffset?.x || 0;
        const offsetY = line.labelOffset?.y || 0;
        
        const worldTextHeight = 2.0 * lineLabelScale;
        const text = `${line.bearing} / ${line.distance}`;
        
        const canvas = canvasRef.current;
        if (!canvas) return false;
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;
        
        ctx.font = `600 ${worldTextHeight}px Arial`;
        const textWidth = ctx.measureText(text).width;
        const padding = worldTextHeight * 0.2;
        
        // Convert world coordinates to screen coordinates
        const labelWorldX = midX + offsetX;
        const labelWorldY = midY + offsetY;
        const pt = worldToScreen(labelWorldX, labelWorldY);
        const screenX = pt.x;
        const screenY = pt.y;
        
        const screenWidth = (textWidth + 2 * padding) * transform.scale;
        const screenHeight = (worldTextHeight + 2 * padding) * transform.scale;
        
        const PADDING_SCREEN = 10;
        
        return (
            mouseX >= screenX - screenWidth / 2 - PADDING_SCREEN &&
            mouseX <= screenX + screenWidth / 2 + PADDING_SCREEN &&
            mouseY >= screenY - screenHeight / 2 - PADDING_SCREEN &&
            mouseY <= screenY + screenHeight / 2 + PADDING_SCREEN
        );
    }, [transform, lineLabelScale, pointMap, worldToScreen]);

    const isOverCurveLabelArea = useCallback((mouseX: number, mouseY: number, line: SurveyLine): boolean => {
        if (!line.isCurve) return false;
        const lineId = line.id || `${line.from}-${line.to}`;
        const box = curveLabelBoxesRef.current.get(lineId);
        if (!box) return false;
        const pt = worldToScreen(box.wx, box.wy);
        const screenX = pt.x;
        const screenY = pt.y;
        const screenWidth = box.ww * transform.scale;
        const screenHeight = box.wh * transform.scale;
        const PADDING_SCREEN = 10;
        return (
            mouseX >= screenX - screenWidth / 2 - PADDING_SCREEN &&
            mouseX <= screenX + screenWidth / 2 + PADDING_SCREEN &&
            mouseY >= screenY - screenHeight / 2 - PADDING_SCREEN &&
            mouseY <= screenY + screenHeight / 2 + PADDING_SCREEN
        );
    }, [transform, worldToScreen]);

    const isOverBoundaryCallLabelArea = useCallback((mouseX: number, mouseY: number, call: BoundaryFileCall): boolean => {
        const bFromPt = pointMap.get(call.from);
        const bToPt   = pointMap.get(call.to);
        if (!bFromPt || !bToPt) return false;
        const bText = call.chordBearing || call.bearing;
        const dText = call.arcLength ? `L=${call.arcLength.toFixed(2)}'` : call.distance;
        if (!bText && !dText) return false;
        const labelText = [bText, dText].filter(Boolean).join(' / ');
        const worldTextHeight = 2.0 * lineLabelScale;
        const canvas = canvasRef.current;
        if (!canvas) return false;
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;
        ctx.font = `italic 600 ${worldTextHeight}px Arial`;
        const textWidth = ctx.measureText(labelText).width;
        const padding = worldTextHeight * 0.2;
        const lmx = (bFromPt.easting + bToPt.easting) / 2;
        const lmy = (bFromPt.northing + bToPt.northing) / 2;
        const pt = worldToScreen(lmx, lmy);
        const screenX = pt.x;
        const screenY = pt.y;
        const screenWidth  = (textWidth + 2 * padding) * transform.scale;
        const screenHeight = (worldTextHeight + 2 * padding) * transform.scale;
        const PAD = 10;
        return (
            mouseX >= screenX - screenWidth / 2 - PAD &&
            mouseX <= screenX + screenWidth / 2 + PAD &&
            mouseY >= screenY - screenHeight / 2 - PAD &&
            mouseY <= screenY + screenHeight / 2 + PAD
        );
    }, [transform, lineLabelScale, pointMap, worldToScreen]);

    const isOverDeedMetadataArea = useCallback((mouseX: number, mouseY: number): boolean => {
        if (!deedMetadata || points.length === 0 || activeAgent !== AgentType.DEED_READER) return false;
        
        // Calculate center of all deed points
        let sumNorthing = 0;
        let sumEasting = 0;
        let count = 0;
        
        points.forEach(p => {
            const pNumberLower = p.pointNumber?.toLowerCase() || '';
            if (pNumberLower.includes('int-') || pNumberLower.startsWith('int_')) {
                return;
            }
            sumNorthing += p.northing;
            sumEasting += p.easting;
            count++;
        });
        
        if (count === 0) return false;
        
        const centerNorthing = sumNorthing / count;
        const centerEasting = sumEasting / count;
        
        const offsetX = props.deedMetadataOffset?.x || 0;
        const offsetY = props.deedMetadataOffset?.y || 0;
        
        // Build text lines to measure box size
        const textLines: string[] = [];
        if (deedMetadata.owners && deedMetadata.owners.length > 0) {
            textLines.push(`Owner: ${deedMetadata.owners.join(' to ')}`);
        }
        if (deedMetadata.deedBook || deedMetadata.deedPage) {
            const bookPage = [
                deedMetadata.deedBook ? `Book ${deedMetadata.deedBook}` : '',
                deedMetadata.deedPage ? `Page ${deedMetadata.deedPage}` : ''
            ].filter(Boolean).join(', ');
            if (bookPage) textLines.push(bookPage);
        }
        if (deedMetadata.parcelId) {
            textLines.push(`Parcel: ${deedMetadata.parcelId}`);
        }
        
        if (textLines.length === 0) return false;
        
        const worldTextHeight = 2.0 * lineLabelScale;
        const lineHeight = worldTextHeight * 1.4;
        
        const canvas = canvasRef.current;
        if (!canvas) return false;
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;
        
        ctx.font = `600 ${worldTextHeight}px Arial`;
        const maxWidth = Math.max(...textLines.map(line => ctx.measureText(line).width));
        const padding = worldTextHeight * 0.5;
        const boxWidth = maxWidth + padding * 2;
        const boxHeight = textLines.length * lineHeight + padding;
        
        // Convert world coordinates to screen coordinates
        const labelWorldX = centerEasting + offsetX;
        const labelWorldY = centerNorthing + offsetY;
        const pt = worldToScreen(labelWorldX, labelWorldY);
        const screenX = pt.x;
        const screenY = pt.y;
        
        const screenWidth = boxWidth * transform.scale;
        const screenHeight = boxHeight * transform.scale;
        
        const PADDING_SCREEN = 10;
        
        return (
            mouseX >= screenX - screenWidth / 2 - PADDING_SCREEN &&
            mouseX <= screenX + screenWidth / 2 + PADDING_SCREEN &&
            mouseY >= screenY - screenHeight / 2 - PADDING_SCREEN &&
            mouseY <= screenY + screenHeight / 2 + PADDING_SCREEN
        );
    }, [deedMetadata, points, activeAgent, props.deedMetadataOffset, lineLabelScale, transform, worldToScreen]);

    const isOverOwnerNameArea = useCallback((mouseX: number, mouseY: number): boolean => {
        if (!deedMetadata || points.length === 0 || activeAgent !== AgentType.DEED_READER) return false;
        
        // Calculate center of all deed points
        let sumNorthing = 0;
        let sumEasting = 0;
        let count = 0;
        
        points.forEach(p => {
            const pNumberLower = p.pointNumber?.toLowerCase() || '';
            if (pNumberLower.includes('int-') || pNumberLower.startsWith('int_')) {
                return;
            }
            sumNorthing += p.northing;
            sumEasting += p.easting;
            count++;
        });
        
        if (count === 0) return false;
        
        const centerNorthing = sumNorthing / count;
        const centerEasting = sumEasting / count;
        
        const offsetX = props.ownerNameOffset?.x || 0;
        const offsetY = props.ownerNameOffset?.y || 10;
        
        // Build all text lines (matching rendering logic)
        const textLines: string[] = [];
        if (deedMetadata.owners && deedMetadata.owners.length > 0) {
            textLines.push(`Owner: ${deedMetadata.owners.join(' to ')}`);
        }
        if (deedMetadata.deedBook || deedMetadata.deedPage) {
            const bookPage = [
                deedMetadata.deedBook ? `Book ${deedMetadata.deedBook}` : '',
                deedMetadata.deedPage ? `Page ${deedMetadata.deedPage}` : ''
            ].filter(Boolean).join(', ');
            if (bookPage) textLines.push(bookPage);
        }
        if (deedMetadata.parcelId) {
            textLines.push(`Parcel: ${deedMetadata.parcelId}`);
        }
        
        if (textLines.length === 0) return false;
        
        const worldTextHeight = 2.0 * lineLabelScale;
        const lineHeight = worldTextHeight * 1.4;
        
        const canvas = canvasRef.current;
        if (!canvas) return false;
        const ctx = canvas.getContext('2d');
        if (!ctx) return false;
        
        ctx.font = `600 ${worldTextHeight}px Arial`;
        const maxWidth = Math.max(...textLines.map(line => ctx.measureText(line).width));
        const padding = worldTextHeight * 0.2;
        const totalHeight = textLines.length * lineHeight;
        
        // Convert world coordinates to screen coordinates
        const labelWorldX = centerEasting + offsetX;
        const labelWorldY = centerNorthing + offsetY;
        const pt = worldToScreen(labelWorldX, labelWorldY);
        const screenX = pt.x;
        const screenY = pt.y;
        
        const screenWidth = (maxWidth + 2 * padding) * transform.scale;
        const screenHeight = (totalHeight + 2 * padding) * transform.scale;
        
        return (
            mouseX >= screenX - screenWidth / 2 &&
            mouseX <= screenX + screenWidth / 2 &&
            mouseY >= screenY - screenHeight / 2 &&
            mouseY <= screenY + screenHeight / 2
        );
    }, [deedMetadata, points, activeAgent, props.ownerNameOffset, lineLabelScale, transform, worldToScreen]);

    /** Rotate the movable boundary 180° during the align-slide phase by
     *  swapping which movable-line endpoint snaps to which target-line
     *  endpoint. Re-bases the slide so the cursor doesn't jump. Bound to R. */
    const rotateAlign180 = useCallback(() => {
        if (!boundaryAlignSliding || !boundaryAlignMovable || !boundaryAlignTarget) return;
        if (!props.onUpdateBoundaryTransform) return;
        const nextFlipped = !boundaryAlignFlipped;
        const aligned = computeBoundaryAlignSnap(
            { bfId: boundaryAlignMovable.bfId, callId: boundaryAlignMovable.callId },
            boundaryAlignTarget,
            nextFlipped,
        );
        if (!aligned) return;
        setBoundaryAlignFlipped(nextFlipped);
        props.onUpdateBoundaryTransform(boundaryAlignMovable.bfId, {
            translationE: aligned.txE,
            translationN: aligned.txN,
            rotationDeg: aligned.rotDeg,
        });
        const cur = cursorWorldPosRef.current;
        const proj = cur ? cur.x * aligned.dirE + cur.y * aligned.dirN : 0;
        boundaryAlignSlideRef.current = {
            movableBfId: boundaryAlignMovable.bfId,
            rotDegFixed: aligned.rotDeg,
            baseTxE: aligned.txE,
            baseTxN: aligned.txN,
            dirE: aligned.dirE,
            dirN: aligned.dirN,
            targetLen: aligned.targetLen,
            movableLen: aligned.movableLen,
            mouseStartProj: proj,
            sMin: -1e9,
            sMax:  1e9,
        };
    // `computeBoundaryAlignSnap` is intentionally omitted from deps — it's declared later
    // in this component body, so listing it here would TDZ on every render. Closure resolves
    // it lazily when this callback actually fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [boundaryAlignSliding, boundaryAlignMovable, boundaryAlignTarget, boundaryAlignFlipped, props]);

    /** Close the active multi-value prompt and hand the caret back. */
    const cancelCommandPrompt = useCallback(() => {
        const prompt = activeCommandPrompt;
        if (!prompt) return;
        setActiveCommandPrompt(null);
        setCommandPromptValues({});
        setCommandPromptIndex(0);
        setIsPickingPolarCenter(false);
        setActiveOsnapPoint(null);
        setTerminalIncoming({ text: `${prompt.title}: Cancelled`, tone: 'info' });
        setCommandFocusRequest(n => n + 1);
    }, [activeCommandPrompt]);

    /** Leave just the polar center pick, keeping the array prompt and values intact. */
    const cancelPolarCenterPick = useCallback(() => {
        setIsPickingPolarCenter(false);
        setActiveOsnapPoint(null);
        setTerminalIncoming({
            text: 'POLARARRAY: Center pick cancelled — blank uses the selection center, or type E,N.',
            tone: 'info',
        });
        setCommandFocusRequest(n => n + 1);
    }, []);

    /** Toggle the canvas pick target without closing the polar prompt. */
    const togglePolarCenterPick = useCallback(() => {
        if (activeCommandPrompt?.id !== 'polar_array') return;
        const next = !isPickingPolarCenter;
        setIsPickingPolarCenter(next);
        setActiveOsnapPoint(null);
        setCommandPromptIndex(2);
        setTerminalIncoming({
            text: next
                ? 'POLARARRAY: Pick rotation center — click a free point or snap to an entity / survey point [MMB pans, Esc cancels pick]'
                : 'POLARARRAY: Center pick cancelled — blank uses the selection center, or type E,N.',
            tone: 'info',
        });
        setCommandFocusRequest(n => n + 1);
    }, [activeCommandPrompt, isPickingPolarCenter]);

    /**
     * Arm pick mode for a command that needs entities. Left clicks then select
     * (toggle) instead of activating grips or drawing tools, middle-drag still
     * pans, Enter re-runs the original command, and Esc cancels it.
     */
    const beginSelectionForCommand = useCallback((raw: string, label: string) => {
        setPendingSelectionCommand({ raw, label });
        setDrawingMode('none');
        setPolylinePoints([]);
        setPolylineArcMode(false);
        setTypedSegmentLength('');
        setCircleCenter(null);
        setTypedCircleValue('');
        setCircleSubMode('center-radius');
        setTtrStep(1);
        setTtrEntity1(null);
        setTtrEntity2(null);
        setTtrHoverEntity(null);
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsCopyingSelection(false);
        setCopyBasePoint(null);
        setIsOffsetMode(false);
        setOffsetDistance(null);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        stopInteractiveTransforms();
        setSelectedLineIds(new Set());
        setIsSelectionMode(true);
        setIsBoxSelectMode(false);
        setTerminalIncoming({
            text: `${label}: Select entities — left-click to pick, drag a window to box select, MMB pans. [Enter] apply, [Esc] cancel.`,
            tone: 'info',
        });
    // Setters are stable; clearOffsetPreview may be declared later in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /**
     * Enter the interactive ROTATE loop. The selection is kept; the first canvas
     * click (snaps honored) sets the base point, the second click or a typed
     * number sets the angle.
     */
    const startRotateMode = useCallback(() => {
        if (selectedLineIds.size === 0) {
            setTerminalIncoming({ text: 'ROTATE: Nothing selected — pick entities first', tone: 'error' });
            return;
        }
        setPendingSelectionCommand(null);
        setDrawingMode('none');
        setPolylinePoints([]);
        setTypedSegmentLength('');
        setCircleCenter(null);
        setTypedCircleValue('');
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsCopyingSelection(false);
        setCopyBasePoint(null);
        setIsOffsetMode(false);
        setOffsetDistance(null);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        stopInteractiveTransforms();
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        setIsRotatingSelection(true);
        setTerminalIncoming({
            text: 'ROTATE: Specify base point — click, snap, or type E,N [Esc to cancel]',
            tone: 'info',
        });
    // clearOffsetPreview may be declared later in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineIds.size]);

    /** Interactive MIRROR: two snapped axis points, live reflected ghost. */
    const startMirrorMode = useCallback(() => {
        if (selectedLineIds.size === 0) {
            setTerminalIncoming({ text: 'MIRROR: Nothing selected — pick entities first', tone: 'error' });
            return;
        }
        stopInteractiveTransforms();
        setPendingSelectionCommand(null);
        setDrawingMode('none');
        setPolylinePoints([]);
        setTypedSegmentLength('');
        setCircleCenter(null);
        setTypedCircleValue('');
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsCopyingSelection(false);
        setCopyBasePoint(null);
        setIsOffsetMode(false);
        setOffsetDistance(null);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        setMirrorAxisStart(null);
        setIsPickingMirrorLine(false);
        setIsMirroringSelection(true);
        setTerminalIncoming({
            text: 'MIRROR: Specify first axis point — click, snap, or type E,N · [L] pick a line to mirror about, [Esc] to cancel',
            tone: 'info',
        });
    // clearOffsetPreview may be declared later in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineIds.size, stopInteractiveTransforms]);

    /** Interactive MOVE: snapped base point, then snapped destination. */
    const startMoveMode = useCallback(() => {
        if (selectedLineIds.size === 0) {
            setTerminalIncoming({ text: 'MOVE: Nothing selected — pick entities first', tone: 'error' });
            return;
        }
        stopInteractiveTransforms();
        setPendingSelectionCommand(null);
        setDrawingMode('none');
        setPolylinePoints([]);
        setTypedSegmentLength('');
        setCircleCenter(null);
        setTypedCircleValue('');
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsCopyingSelection(false);
        setCopyBasePoint(null);
        setIsOffsetMode(false);
        setOffsetDistance(null);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        setMoveBasePoint(null);
        setIsMovingSelection(true);
        setTerminalIncoming({
            text: 'MOVE: Specify base point — click, snap, or type E,N [Esc to cancel]',
            tone: 'info',
        });
    // clearOffsetPreview may be declared later in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineIds.size, stopInteractiveTransforms]);

    /** Interactive SCALE: reference point, then a clicked point or typed factor. */
    const startScaleMode = useCallback(() => {
        if (selectedLineIds.size === 0) {
            setTerminalIncoming({ text: 'SCALE: Nothing selected — pick entities first', tone: 'error' });
            return;
        }
        stopInteractiveTransforms();
        setPendingSelectionCommand(null);
        setDrawingMode('none');
        setPolylinePoints([]);
        setTypedSegmentLength('');
        setCircleCenter(null);
        setTypedCircleValue('');
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsCopyingSelection(false);
        setCopyBasePoint(null);
        setIsOffsetMode(false);
        setOffsetDistance(null);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        setScaleBasePoint(null);
        setTypedScaleFactor('');
        scaleReferenceRadiusRef.current = null;
        setIsScalingSelection(true);
        setTerminalIncoming({
            text: 'SCALE: Specify reference point — click, snap, or type E,N [Esc to cancel]',
            tone: 'info',
        });
    // clearOffsetPreview may be declared later in the body.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedLineIds.size, stopInteractiveTransforms]);

    const handleKeyDown = useCallback((e: KeyboardEvent) => {
        const target = e.target as HTMLElement | null;
        const tag = target?.tagName;
        const isTextInput = tag === 'INPUT' || tag === 'TEXTAREA' || !!target?.isContentEditable;
        // Escape always backs out of an open canvas menu before it cancels any
        // drawing/tool state. This runs even while a flyout input has focus, so
        // the menu can always be dismissed with the keyboard.
        if (e.key === 'Escape') {
            const hasOpenCanvasMenu =
                isAttributeFlyoutOpen ||
                isZoomExtentsOpen ||
                isZoomToPointOpen ||
                isFindPointsOpen ||
                isDrawingToolsMenuOpen ||
                isAddPointFlyoutOpen ||
                isDimFlyoutOpen ||
                isDimSettingsOpen ||
                isOtPopupOpen ||
                isPropertiesOpen ||
                inlineOsnapMenuPos !== null ||
                dimContextMenu !== null;
            if (hasOpenCanvasMenu) {
                e.preventDefault();
                e.stopPropagation();
                setIsAttributeFlyoutOpen(false);
                setIsZoomExtentsOpen(false);
                setIsZoomToPointOpen(false);
                setIsFindPointsOpen(false);
                setIsDrawingToolsMenuOpen(false);
                setIsAddPointFlyoutOpen(false);
                setIsDimFlyoutOpen(false);
                setIsDimSettingsOpen(false);
                setIsOtPopupOpen(false);
                setIsPropertiesOpen(false);
                setInlineOsnapMenuPos(null);
                setDimContextMenu(null);
                setFindPointsQuery('');
                setZoomToPointNumber('');
                return;
            }
        }
        // While a multi-value prompt is open, its boxes and the command line are
        // the only input surfaces; don't let canvas shortcuts edit the drawing.
        if (activeCommandPrompt && !isTextInput && e.key !== 'Escape') {
            return;
        }
        if (e.key === 'Control' || e.key === 'Meta') {
            setIsCtrlPressed(true);
        }
        if (e.key === 'Alt') {
            setIsAltPressed(true);
        }
        // ROTATE takes a typed angle at any time, even while the cursor is
        // showing the preview — no need to click into the command line.
        if (!isTextInput && isRotatingSelection) {
            if (/^[0-9.-]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                setTypedRotateAngle(prev => (e.key === '.' && prev.includes('.') ? prev : `${prev}${e.key}`));
                return;
            }
            if (e.key === 'Backspace' && typedRotateAngle.length > 0) {
                e.preventDefault();
                setTypedRotateAngle(prev => prev.slice(0, -1));
                return;
            }
            if (e.key === 'Enter' && typedRotateAngle.trim().length > 0) {
                e.preventDefault();
                const value = Number(typedRotateAngle);
                setTypedRotateAngle('');
                if (!Number.isFinite(value)) {
                    setTerminalIncoming({ text: 'ROTATE: Angle must be numeric degrees', tone: 'error' });
                    return;
                }
                const about = rotatePivot
                    ? `E ${rotatePivot.easting.toFixed(2)}, N ${rotatePivot.northing.toFixed(2)}`
                    : 'the selection center';
                const turned = rotateSelectionCommand(value, rotatePivot ?? undefined);
                stopInteractiveTransforms();
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({ text: `ROTATE: Rotated ${turned} ${turned === 1 ? 'entity' : 'entities'} ${value}° about ${about}.`, tone: 'ok' });
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }
        }
        // SCALE takes a typed factor at any time, mirroring ROTATE's inline entry.
        if (!isTextInput && isScalingSelection) {
            if (/^[0-9.]$/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
                e.preventDefault();
                setTypedScaleFactor(prev => (e.key === '.' && prev.includes('.') ? prev : `${prev}${e.key}`));
                return;
            }
            if (e.key === 'Backspace' && typedScaleFactor.length > 0) {
                e.preventDefault();
                setTypedScaleFactor(prev => prev.slice(0, -1));
                return;
            }
            if (e.key === 'Enter' && typedScaleFactor.trim().length > 0) {
                e.preventDefault();
                const value = Number(typedScaleFactor);
                setTypedScaleFactor('');
                if (!Number.isFinite(value) || value <= 0) {
                    setTerminalIncoming({ text: 'SCALE: Factor must be a positive number', tone: 'error' });
                    return;
                }
                const about = scaleBasePoint
                    ? `E ${scaleBasePoint.easting.toFixed(2)}, N ${scaleBasePoint.northing.toFixed(2)}`
                    : 'the selection center';
                const scaled = scaleSelectionCommand(value, scaleBasePoint ?? undefined);
                stopInteractiveTransforms();
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({ text: `SCALE: Scaled ${scaled} ${scaled === 1 ? 'entity' : 'entities'} by ${value}× (${(value * 100).toFixed(1)}%) about ${about}.`, tone: 'ok' });
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }
        }
        // A command waiting for canvas picks: Enter applies it to the picks,
        // Esc abandons it. Typing in the command line is left alone.
        if (e.key === 'Enter' && !isTextInput && pendingSelectionCommand) {
            e.preventDefault();
            runPendingSelectionCommandRef.current();
            return;
        }
        if (e.key === 'Escape' && activeCommandPrompt) {
            if (isTextInput) return;
            e.preventDefault();
            if (isPickingPolarCenter) cancelPolarCenterPick();
            else cancelCommandPrompt();
            return;
        }
        if (e.key === 'Escape' && !isTextInput && pendingSelectionCommand) {
            e.preventDefault();
            setPendingSelectionCommand(null);
            setIsSelectionMode(false);
            setIsBoxSelectMode(false);
            setTerminalIncoming({ text: `${pendingSelectionCommand.label}: Cancelled`, tone: 'info' });
            setCommandFocusRequest(n => n + 1);
            return;
        }
        if (e.key === 'Escape' && !isTextInput && isRotatingSelection) {
            e.preventDefault();
            stopInteractiveTransforms();
            setTerminalIncoming({ text: 'ROTATE: Cancelled', tone: 'info' });
            return;
        }
        if (e.key === 'Escape' && !isTextInput && isScalingSelection) {
            e.preventDefault();
            stopInteractiveTransforms();
            setTerminalIncoming({ text: 'SCALE: Cancelled', tone: 'info' });
            return;
        }
        if (e.key === 'Escape' && !isTextInput && isMirroringSelection) {
            e.preventDefault();
            stopInteractiveTransforms();
            setTerminalIncoming({ text: 'MIRROR: Cancelled', tone: 'info' });
            return;
        }
        if (e.key === 'Escape' && !isTextInput && isMovingSelection) {
            e.preventDefault();
            stopInteractiveTransforms();
            setTerminalIncoming({ text: 'MOVE: Cancelled', tone: 'info' });
            return;
        }
        if (e.key === 'Escape' && !isTextInput && isCopyingSelection) {
            e.preventDefault();
            setIsCopyingSelection(false);
            setCopyBasePoint(null);
            setTerminalIncoming({ text: 'COPY: Exited', tone: 'info' });
            return;
        }
        if (e.key === 'Escape' && !isTextInput && isOffsetMode) {
            e.preventDefault();
            setIsOffsetMode(false);
            setOffsetDistance(null);
            setOffsetTargetId(null);
            setOffsetPickStart(null);
            offsetPreviewSegmentsRef.current = null;
            offsetPreviewKeyRef.current = null;
            requestAnimationFrame(() => drawRef.current?.());
            setTerminalIncoming({ text: 'OFFSET: Exited', tone: 'info' });
            return;
        }
        if (!isTextInput && !e.ctrlKey && !e.metaKey && !e.altKey && isLinearDrawingMode(drawingMode) && polylinePoints.length > 0) {
            if (/^[0-9.]$/.test(e.key)) {
                e.preventDefault();
                setTypedSegmentLength(prev => {
                    if (e.key === '.' && prev.includes('.')) return prev;
                    return `${prev}${e.key}`;
                });
                if (drawRef.current) {
                    requestAnimationFrame(() => drawRef.current?.());
                }
                return;
            }
            if (e.key === 'Backspace' && typedSegmentLength.length > 0) {
                e.preventDefault();
                setTypedSegmentLength(prev => prev.slice(0, -1));
                if (drawRef.current) {
                    requestAnimationFrame(() => drawRef.current?.());
                }
                return;
            }
            if (e.key === 'Enter' && typedSegmentLength.length > 0) {
                e.preventDefault();
                const lengthVal = Number(typedSegmentLength);
                const anchor = polylinePoints[polylinePoints.length - 1];
                if (anchor && Number.isFinite(lengthVal) && lengthVal > 0) {
                    const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                    const worldLength = convertLinearUnits(lengthVal, fromUnit, 'foot');
                    let target = activeOsnapPoint
                        ? { easting: activeOsnapPoint.easting, northing: activeOsnapPoint.northing }
                        : (cursorWorldPosRef.current
                            ? { easting: cursorWorldPosRef.current.x, northing: cursorWorldPosRef.current.y }
                            : { easting: anchor.easting + 1, northing: anchor.northing });
                    if (isOrthoEnabled && !activeOsnapPoint) {
                        target = applyOrthoConstraint(anchor, target);
                    }
                    let dx = target.easting - anchor.easting;
                    let dy = target.northing - anchor.northing;
                    const mag = Math.hypot(dx, dy);
                    if (mag < 1e-9) {
                        dx = 1;
                        dy = 0;
                    } else {
                        dx /= mag;
                        dy /= mag;
                    }
                    const next = makeTempWorldPoint(anchor.easting + dx * worldLength, anchor.northing + dy * worldLength);
                    setPolylinePoints(prev => [...prev, next]);
                }
                setTypedSegmentLength('');
                return;
            }
        }
        // Polygon mode typing and side controls
        if (!isTextInput && !e.ctrlKey && !e.metaKey && !e.altKey && drawingMode === 'polygon') {
            if (e.key === '[' || e.key === ']' || e.key === '-' || e.key === '+') {
                e.preventDefault();
                const delta = e.key === ']' || e.key === '+' ? 1 : -1;
                setPolygonSides(prev => Math.max(3, Math.min(256, prev + delta)));
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }
            if (/^[0-9.]$/.test(e.key)) {
                e.preventDefault();
                setTypedPolygonDiameter(prev => {
                    if (e.key === '.' && prev.includes('.')) return prev;
                    return `${prev}${e.key}`;
                });
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }
            if (e.key === 'Backspace' && typedPolygonDiameter.length > 0) {
                e.preventDefault();
                setTypedPolygonDiameter(prev => prev.slice(0, -1));
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (!polygonCenter) {
                    setTerminalIncoming({ text: 'POLYGON: Pick a center first', tone: 'error' });
                    return;
                }
                let radiusInput: number | null = null;
                if (typedPolygonDiameter.trim().length > 0) {
                    const num = Number(typedPolygonDiameter);
                    if (Number.isFinite(num) && num > 0) radiusInput = num / 2;
                }
                if (radiusInput === null && cursorWorldPosRef.current) {
                    const worldRadius = Math.hypot(
                        cursorWorldPosRef.current.x - polygonCenter.easting,
                        cursorWorldPosRef.current.y - polygonCenter.northing,
                    );
                    radiusInput = convertLinearUnits(worldRadius, 'foot', settings.linearUnits || 'usSurveyFoot');
                }
                if (radiusInput === null) {
                    setTerminalIncoming({ text: 'POLYGON: Move the cursor or type a diameter', tone: 'error' });
                    return;
                }
                commitPolygonFromRadius(radiusInput);
                return;
            }
            if (e.key === 'Escape') {
                e.preventDefault();
                if (polygonCenter) {
                    setPolygonCenter(null);
                    setTypedPolygonDiameter('');
                    setActiveOsnapPoint(null);
                    setTerminalIncoming({ text: `POLYGON: Pick the center [${polygonSides} sides — scroll or [ ] changes sides]`, tone: 'info' });
                    requestAnimationFrame(() => drawRef.current?.());
                } else {
                    setDrawingMode('none');
                    setTypedPolygonDiameter('');
                    setActiveOsnapPoint(null);
                    setTerminalIncoming({ text: 'POLYGON: Cancelled', tone: 'info' });
                    requestAnimationFrame(() => drawRef.current?.());
                }
                return;
            }
        }

        // Circle mode typing & commands
        if (!isTextInput && !e.ctrlKey && !e.metaKey && !e.altKey && drawingMode === 'circle') {
            if (e.key === 't' || e.key === 'T') {
                e.preventDefault();
                setCircleSubMode(prev => {
                    if (prev === 'ttr') {
                        setTtrStep(1);
                        setTtrEntity1(null);
                        setTtrEntity2(null);
                        setTtrHoverEntity(null);
                        setTypedCircleValue('');
                        return 'center-radius';
                    } else {
                        setCircleCenter(null);
                        setTtrStep(1);
                        setTtrEntity1(null);
                        setTtrEntity2(null);
                        setTtrHoverEntity(null);
                        setTypedCircleValue('');
                        return 'ttr';
                    }
                });
                return;
            }
            if (/^[0-9.]$/.test(e.key)) {
                e.preventDefault();
                setTypedCircleValue(prev => {
                    if (e.key === '.' && prev.includes('.')) return prev;
                    return `${prev}${e.key}`;
                });
                return;
            }
            if (e.key === 'Backspace' && typedCircleValue.length > 0) {
                e.preventDefault();
                setTypedCircleValue(prev => prev.slice(0, -1));
                return;
            }
            if (e.key === 'd' || e.key === 'D') {
                e.preventDefault();
                setCircleSizeParam(prev => prev === 'diameter' ? 'radius' : 'diameter');
                return;
            }
            if (e.key === 'r' || e.key === 'R') {
                e.preventDefault();
                setCircleSizeParam('radius');
                return;
            }
            if (e.key === 'Enter') {
                e.preventDefault();
                if (circleSubMode === 'ttr') {
                    if (ttrStep === 3 && ttrEntity1 && ttrEntity2) {
                        let rVal = 25;
                        if (typedCircleValue.trim().length > 0) {
                            const num = Number(typedCircleValue);
                            if (Number.isFinite(num) && num > 0) {
                                const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                                rVal = convertLinearUnits(num, fromUnit, 'foot');
                            }
                        } else if (cursorWorldPosRef.current) {
                            rVal = Math.hypot(cursorWorldPosRef.current.x - ttrEntity2.pickPoint.x, cursorWorldPosRef.current.y - ttrEntity2.pickPoint.y);
                        }
                        if (circleSizeParam === 'diameter') rVal = rVal / 2;
                        const sol = solveTtrCircle(ttrEntity1, ttrEntity2, rVal);
                        if (sol) {
                            addCircleGeometry(sol.center, sol.radius);
                            setCircleSubMode('center-radius');
                            setTtrStep(1);
                            setTtrEntity1(null);
                            setTtrEntity2(null);
                            setTypedCircleValue('');
                            setDrawingMode('none');
                        }
                    }
                    return;
                }
                if (circleCenter) {
                    let radiusVal = 0;
                    if (typedCircleValue.trim().length > 0) {
                        const num = Number(typedCircleValue);
                        if (Number.isFinite(num) && num > 0) {
                            const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                            const worldVal = convertLinearUnits(num, fromUnit, 'foot');
                            radiusVal = circleSizeParam === 'diameter' ? worldVal / 2 : worldVal;
                        }
                    } else {
                        const target = activeOsnapPoint
                            ? { easting: activeOsnapPoint.easting, northing: activeOsnapPoint.northing }
                            : (cursorWorldPosRef.current
                                ? { easting: cursorWorldPosRef.current.x, northing: cursorWorldPosRef.current.y }
                                : null);
                        if (target) {
                            const dist = Math.hypot(target.easting - circleCenter.easting, target.northing - circleCenter.northing);
                            radiusVal = circleSizeParam === 'diameter' ? dist / 2 : dist;
                        }
                    }
                    if (radiusVal > 1e-6) {
                        addCircleGeometry(circleCenter, radiusVal);
                    }
                    setCircleCenter(null);
                    setTypedCircleValue('');
                    setDrawingMode('none');
                }
                return;
            }
        }
        // L / I / C / S / B / R — start drawing & selection commands (AutoCAD-like command shortcuts).
        if (!isTextInput && !e.ctrlKey && !e.metaKey && !e.altKey) {
            if (e.key === 'r' || e.key === 'R') {
                e.preventDefault();
                if (isRotatingSelection) {
                    stopInteractiveTransforms();
                    setTerminalIncoming({ text: 'Command: R (ROTATE) cancelled', tone: 'info' });
                } else if (selectedLineIds.size === 0) {
                    beginSelectionForCommand('ro', 'ROTATE');
                } else {
                    startRotateMode();
                }
                return;
            }
            // MIRROR: [L] swaps the axis pick between two points and a picked line.
        if (isMirroringSelection && (e.key === 'l' || e.key === 'L')) {
            if (isTextInput) return;
            e.preventDefault();
            const next = !isPickingMirrorLine;
            setIsPickingMirrorLine(next);
            setMirrorAxisStart(null);
            setTerminalIncoming({
                text: next
                    ? 'MIRROR: Pick the mirror line — click a line to reflect the selection across it [L] two points, [Esc] cancel'
                    : 'MIRROR: Two-point axis — click, snap, or type E,N [L] pick a line, [Esc] cancel',
                tone: 'info',
            });
            setCommandFocusRequest(n => n + 1);
            return;
        }
        if (e.key === 's' || e.key === 'S') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: S (SELECT) — click lines or chain [Esc to clear]', tone: 'ok' });
                if (isSelectionMode) {
                    setIsSelectionMode(false);
                    setIsBoxSelectMode(false);
                    setSelectedLineIds(new Set());
                } else {
                    finishDrawing();
                    setIsSelectionMode(true);
                    setIsBoxSelectMode(false);
                }
                return;
            }
            if (e.key === 'b' || e.key === 'B') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: B (BOX SELECT) — drag window across entities', tone: 'ok' });
                if (!isSelectionMode) {
                    finishDrawing();
                    setIsSelectionMode(true);
                    setIsBoxSelectMode(true);
                } else {
                    setIsBoxSelectMode(prev => !prev);
                }
                return;
            }
            if (e.key === 't' || e.key === 'T') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: T (TRIM) — click line segment to cut at intersection', tone: 'ok' });
                if (isTrimmingLines) {
                    setIsTrimmingLines(false);
                    setTrimPoint(null);
                } else {
                    finishDrawing();
                    setIsTrimmingLines(true);
                    setIsExtendingLines(false);
                    setIsDeletingLines(false);
                    setTrimPoint(null);
                    setExtendFromLine(null);
                    setIsSelectionMode(false);
                    setSelectedLineIds(new Set());
                }
                return;
            }
            if (e.key === 'c' || e.key === 'C') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: C (CIRCLE) — pick center point or type T for TTR', tone: 'ok' });
                if (drawingMode === 'circle') {
                    finishDrawing();
                } else {
                    setDrawingMode('circle');
                    setCircleCenter(null);
                    setCircleSizeParam('radius');
                    setTypedCircleValue('');
                    setIsTrimmingLines(false);
                    setIsExtendingLines(false);
                    setIsDeletingLines(false);
                    setTrimPoint(null);
                    setExtendFromLine(null);
                    setIsSelectionMode(false);
                    setSelectedLineIds(new Set());
                    setPolylinePoints([]);
                    setTypedSegmentLength('');
                }
                return;
            }
            if (e.key === 'o' || e.key === 'O') {
                e.preventDefault();
                setIsOrthoEnabled(prev => {
                    const next = !prev;
                    setTerminalIncoming({ text: `ORTHO: ${next ? 'ON' : 'OFF'}`, tone: 'info' });
                    return next;
                });
                return;
            }
            if (e.key === 'l' || e.key === 'L') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: L (LINE) — pick start point or type length [A for Arc]', tone: 'ok' });
                // Mid-polyline: L switches back to straight segments (CAD
                // polyline Line submode) instead of restarting the polyline.
                if (drawingMode === 'polylines' && polylinePoints.length > 0) {
                    setPolylineArcMode(false);
                    return;
                }
                setDrawingMode('polylines');
                setIsTrimmingLines(false);
                setIsExtendingLines(false);
                setIsDeletingLines(false);
                setTrimPoint(null);
                setExtendFromLine(null);
                setIsSelectionMode(false);
                setSelectedLineIds(new Set());
                setPolylinePoints([]);
                setPolylineArcMode(false);
                lastSegmentTangentRef.current = null;
                setTypedSegmentLength('');
                return;
            }
            if ((e.key === 'a' || e.key === 'A') && drawingMode === 'polylines') {
                // CAD polyline Arc submode: only meaningful once a segment
                // exists, since the arc locks tangent to the previous segment.
                if (polylinePoints.length > 0 && lastSegmentTangentRef.current) {
                    e.preventDefault();
                    setTerminalIncoming({ text: 'Polyline Submode: ARC (tangent to previous segment)', tone: 'info' });
                    setPolylineArcMode(true);
                    return;
                }
            }
            if (e.key === 'i' || e.key === 'I') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: I (INCLUSION) — pick boundary vertices', tone: 'ok' });
                setDrawingMode('inclusion');
                setIsTrimmingLines(false);
                setIsExtendingLines(false);
                setIsDeletingLines(false);
                setTrimPoint(null);
                setExtendFromLine(null);
                setIsSelectionMode(false);
                setSelectedLineIds(new Set());
                setPolylinePoints([]);
                setTypedSegmentLength('');
                return;
            }
            if (e.key === 'p' || e.key === 'P') {
                e.preventDefault();
                setTerminalIncoming({ text: 'Command: P (ADD POINT) — click canvas to drop point [Esc to finish]', tone: 'ok' });
                if (drawingMode === 'add-point') {
                    finishDrawing();
                } else {
                    finishDrawing();
                    const cacpNext = queryCacpNextPointNumber();
                    setAddPointNumber(cacpNext);
                    setDrawingMode('add-point');
                    setIsAddPointFlyoutOpen(true);
                }
                return;
            }
        }
        // R — rotate movable boundary 180° during align-slide.
        if ((e.key === 'r' || e.key === 'R') && boundaryAlignSliding) {
            if (!isTextInput) {
                e.preventDefault();
                rotateAlign180();
                return;
            }
        }
        if ((e.key === 'Delete' || e.key === 'Backspace' || e.key === 'd' || e.key === 'D') && selectedDimensionId) {
            if (isTextInput) return;
            e.preventDefault();
            onDeleteDimension?.(selectedDimensionId);
            setSelectedDimensionId(null);
            setTerminalIncoming({ text: 'Deleted dimension', tone: 'info' });
            return;
        }
        if ((e.key === 'Delete' || e.key === 'Backspace' || e.key === 'd' || e.key === 'D')
            && (selectedLineIds.size > 0 || selectedPointNumbers.size > 0 || selectedCenterlineIds.size > 0)) {
            if (isTextInput) return;
            e.preventDefault();
            const removedLineCount = selectedLineIds.size;
            const removedPointCount = selectedPointNumbers.size;
            const removedClCount = selectedCenterlineIds.size;
            selectedLineIds.forEach(id => onDeleteLine(id));
            if (removedPointCount > 0) onDeletePoints?.(Array.from(selectedPointNumbers));
            if (removedClCount > 0) selectedCenterlineIds.forEach(id => onDeleteCenterline?.(id));
            setSelectedLineIds(new Set());
            setSelectedPointNumbers(new Set());
            setSelectedCenterlineIds(new Set());
            setTerminalIncoming({
                text: `Deleted ${removedLineCount} line${removedLineCount === 1 ? '' : 's'}, ${removedPointCount} point${removedPointCount === 1 ? '' : 's'}, ${removedClCount} centerline${removedClCount === 1 ? '' : 's'}.`,
                tone: 'ok',
            });
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }
        // Escape unwinds the corner-align tool one step at a time: nest picker →
        // pending corner → whole tool (handled by the generic drawingMode branch).
        if (e.key === 'Escape' && drawingMode === 'boundary-corner-align' && (cornerAlignPicker || cornerAlignPendingCorner)) {
            if (isTextInput) return;
            e.preventDefault();
            if (cornerAlignPicker) setCornerAlignPicker(null);
            else setCornerAlignPendingCorner(null);
            return;
        }
        if (e.key === 'Escape' && activeDimGrip) {
            if (isTextInput) return;
            e.preventDefault();
            setActiveDimGrip(null);
            setHoveredDimGrip(null);
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }
        if (e.key === 'Escape' && activeGrip) {
            if (isTextInput) return;
            e.preventDefault();
            setActiveGrip(null);
            setHoveredGrip(null);
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }
        if (e.key === 'Escape' && (isPickingOtOrigin || isPickingOtLineAlign)) {
            if (isTextInput) return;
            e.preventDefault();
            setIsPickingOtOrigin(false);
            setIsPickingOtLineAlign(false);
            return;
        }
        if (e.key === 'Escape' && drawingMode === 'circle') {
            if (isTextInput) return;
            e.preventDefault();
            if (circleSubMode === 'ttr') {
                if (ttrStep === 3) {
                    setTtrStep(2);
                    setTtrEntity2(null);
                    setTypedCircleValue('');
                } else if (ttrStep === 2) {
                    setTtrStep(1);
                    setTtrEntity1(null);
                } else {
                    setCircleSubMode('center-radius');
                    setDrawingMode('none');
                }
            } else if (circleCenter) {
                setCircleCenter(null);
                setTypedCircleValue('');
            } else {
                setDrawingMode('none');
            }
            return;
        }
        if (e.key === 'Escape' && inlineOsnapMenuPos) {
            if (isTextInput) return;
            e.preventDefault();
            setInlineOsnapMenuPos(null);
            return;
        }
        if (e.key === 'Escape' && typedSegmentLength.length > 0) {
            if (isTextInput) return;
            e.preventDefault();
            setTypedSegmentLength('');
            return;
        }
        if (e.key === 'Escape' && selectedDimensionId) {
            setSelectedDimensionId(null);
            return;
        }
        if (e.key === 'Escape' && isSelectionMode) {
            if (isTextInput) return;
            e.preventDefault();
            if (selectedLineIds.size > 0 || selectedPointNumbers.size > 0 || selectedCenterlineIds.size > 0) {
                setSelectedLineIds(new Set());
                setSelectedPointNumbers(new Set());
                setSelectedCenterlineIds(new Set());
            } else {
                setIsSelectionMode(false);
                setIsBoxSelectMode(false);
            }
            return;
        }
        if (e.key === 'Escape' && (selectedLineIds.size > 0 || selectedPointNumbers.size > 0 || selectedCenterlineIds.size > 0)) {
            setSelectedLineIds(new Set());
            setSelectedPointNumbers(new Set());
            setSelectedCenterlineIds(new Set());
            return;
        }
        if (e.key === 'Escape' && (drawingMode !== 'none' || isTrimmingLines || isExtendingLines || isDeletingLines)) {
            if (isTextInput) return;
            e.preventDefault();
            finishDrawing();
            setIsTrimmingLines(false);
            setIsExtendingLines(false);
            setIsDeletingLines(false);
            setExtendFromLine(null);
            setTrimPoint(null);
            setTypedSegmentLength('');
            setCircleCenter(null);
            setTypedCircleValue('');
            setCircleSubMode('center-radius');
            setTtrStep(1);
            setTtrEntity1(null);
            setTtrEntity2(null);
            setTtrHoverEntity(null);
        }
    }, [selectedLineIds, selectedPointNumbers, selectedCenterlineIds, isPickingMirrorLine, onDeletePoints, onDeleteCenterline, selectedDimensionId, onDeleteDimension, onDeleteLine, boundaryAlignSliding, rotateAlign180, drawingMode, isTrimmingLines, isExtendingLines, isDeletingLines, inlineOsnapMenuPos, typedSegmentLength, polylinePoints, activeOsnapPoint, isOrthoEnabled, applyOrthoConstraint, makeTempWorldPoint, isLinearDrawingMode, cornerAlignPicker, cornerAlignPendingCorner, circleCenter, circleSizeParam, typedCircleValue, circleSubMode, polygonCenter, polygonSides, typedPolygonDiameter, commitPolygonFromRadius, ttrStep, ttrEntity1, ttrEntity2, solveTtrCircle, addCircleGeometry, settings.linearUnits, isPickingOtOrigin, isPickingOtLineAlign, isSelectionMode, isBoxSelectMode, isCopyingSelection, isOffsetMode, pendingSelectionCommand, isRotatingSelection, isMirroringSelection, isMovingSelection, typedRotateAngle, rotateSelectionCommand, rotatePivot, stopInteractiveTransforms, startRotateMode, beginSelectionForCommand, isScalingSelection, typedScaleFactor, scaleBasePoint, scaleSelectionCommand, activeCommandPrompt, cancelCommandPrompt, isPickingPolarCenter, cancelPolarCenterPick, isAttributeFlyoutOpen, isZoomExtentsOpen, isZoomToPointOpen, isFindPointsOpen, isDrawingToolsMenuOpen, isAddPointFlyoutOpen, isDimFlyoutOpen, isDimSettingsOpen, isOtPopupOpen, isPropertiesOpen, dimContextMenu]);

    const handleKeyUp = useCallback((e: KeyboardEvent) => {
        if (e.key === 'Control' || e.key === 'Meta') {
            setIsCtrlPressed(false);
        }
        if (e.key === 'Alt') {
            setIsAltPressed(false);
        }
    }, []);

    useEffect(() => {
        window.addEventListener('keydown', handleKeyDown);
        window.addEventListener('keyup', handleKeyUp);
        return () => {
            window.removeEventListener('keydown', handleKeyDown);
            window.removeEventListener('keyup', handleKeyUp);
        };
    }, [handleKeyDown, handleKeyUp]);

    // Auto-exit selection mode when another tool becomes active.
    useEffect(() => {
        if (!isSelectionMode) return;
        if (drawingMode !== 'none' || isTrimmingLines || isExtendingLines || isDeletingLines) {
            setIsSelectionMode(false);
            setSelectedLineIds(new Set());
        }
    }, [isSelectionMode, drawingMode, isTrimmingLines, isExtendingLines, isDeletingLines]);

    // A drawing/command mode taking over ends any pending pick-for-command state.
    useEffect(() => {
        if (!pendingSelectionCommand) return;
        if (drawingMode !== 'none' || isTrimmingLines || isExtendingLines || isDeletingLines || isCopyingSelection || isOffsetMode) {
            setPendingSelectionCommand(null);
        }
    }, [pendingSelectionCommand, drawingMode, isTrimmingLines, isExtendingLines, isDeletingLines, isCopyingSelection, isOffsetMode]);

    // COPY / OFFSET are command-line modes; any canvas tool taking over cancels them.
    useEffect(() => {
        if (!isCopyingSelection && !isOffsetMode) return;
        if (drawingMode !== 'none' || isTrimmingLines || isExtendingLines || isDeletingLines || isSelectionMode || isBoxSelectMode) {
            setIsCopyingSelection(false);
            setCopyBasePoint(null);
            setIsOffsetMode(false);
            setOffsetDistance(null);
            setOffsetTargetId(null);
            setOffsetPickStart(null);
            offsetPreviewSegmentsRef.current = null;
            offsetPreviewKeyRef.current = null;
            requestAnimationFrame(() => drawRef.current?.());
        }
    }, [drawingMode, isTrimmingLines, isExtendingLines, isDeletingLines, isSelectionMode, isBoxSelectMode, isCopyingSelection, isOffsetMode]);
    
    // Toolbar dragging handlers (pointer-event rAF loop)
    useEffect(() => {
        const handlePointerMove = (e: PointerEvent) => {
            if (!isViewToolbarDragging || !viewToolbarDragStart.current) return;
            const deltaX = e.clientX - viewToolbarDragStart.current.clientX;
            const deltaY = e.clientY - viewToolbarDragStart.current.clientY;

            if (viewToolbarRafRef.current) cancelAnimationFrame(viewToolbarRafRef.current);
            viewToolbarRafRef.current = requestAnimationFrame(() => {
                const container = containerRef.current;
                const maxW = container?.clientWidth ?? window.innerWidth;
                const maxH = container?.clientHeight ?? window.innerHeight;
                const usableH = maxH - (commandBarHeight > 0 ? commandBarHeight : 0);
                const toolbarW = toolbarRef.current?.offsetWidth ?? (toolbarOrientation === 'vertical' ? 50 : 380);
                const toolbarH = toolbarRef.current?.offsetHeight ?? (toolbarOrientation === 'vertical' ? 450 : 50);

                const rawX = viewToolbarDragStart.current!.toolbarX + deltaX;
                const rawY = viewToolbarDragStart.current!.toolbarY + deltaY;

                const nextPos = calculateToolbarDockingPosition({
                    rawX,
                    rawY,
                    maxW,
                    maxH: usableH,
                    toolbarW,
                    toolbarH,
                    isDesktop,
                    snapThreshold: 36,
                });

                latestToolbarPosRef.current = nextPos;
                setViewToolbarPosition(nextPos);
            });
        };

        const handlePointerUp = () => {
            if (isViewToolbarDragging) {
                if (viewToolbarRafRef.current) {
                    cancelAnimationFrame(viewToolbarRafRef.current);
                    viewToolbarRafRef.current = null;
                }
                setIsViewToolbarDragging(false);
                viewToolbarDragStart.current = null;
                const finalPos = latestToolbarPosRef.current;
                if (finalPos) {
                    setViewToolbarPosition(finalPos);
                    try {
                        localStorage.setItem('landsurv-canvas-toolbar-position-v3', JSON.stringify(finalPos));
                    } catch { /* ignore */ }
                }
            }
        };

        if (isViewToolbarDragging) {
            window.addEventListener('pointermove', handlePointerMove);
            window.addEventListener('pointerup', handlePointerUp);
            window.addEventListener('pointercancel', handlePointerUp);
            return () => {
                window.removeEventListener('pointermove', handlePointerMove);
                window.removeEventListener('pointerup', handlePointerUp);
                window.removeEventListener('pointercancel', handlePointerUp);
            };
        }
    }, [isViewToolbarDragging, isDesktop, toolbarOrientation, commandBarHeight]);
    
    const handleContainerMouseMove = useCallback((e: React.MouseEvent) => {
        // Floating canvas dialogs (Properties, etc.) own their own hover: skip
        // hit-testing so selection mode doesn't bleed through them.
        if ((e.target as HTMLElement | null)?.closest('[data-canvas-ui]')) {
            if (isOverLine) setIsOverLine(false);
            return;
        }
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        setCursorScreenPos({ x: mouseX, y: mouseY });

        // POLARARRAY center pick: hover with the same osnaps the canvas uses,
        // plus survey points, so the picked pivot is exact.
        if (activeCommandPrompt?.id === 'polar_array' && isPickingPolarCenter) {
            const activeModes = inlineOsnapOverride
                ? new Set<OsnapMode>([inlineOsnapOverride])
                : new Set<OsnapMode>([
                    ...(runningOsnaps.size > 0
                        ? runningOsnaps
                        : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest'])),
                    'endpoint',
                ]);
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 25);
            const nearestExisting = snapped ? null : findNearestSurveyPoint(mouseX, mouseY, 25);
            const surveySnap: OsnapResult | null = nearestExisting
                ? {
                    easting: nearestExisting.easting,
                    northing: nearestExisting.northing,
                    mode: 'endpoint',
                    sourcePoint: nearestExisting,
                }
                : null;
            const target = snapped || surveySnap || screenToWorld(mouseX, mouseY);
            cursorWorldPosRef.current = { x: target.easting, y: target.northing };
            setActiveOsnapPoint(snapped ?? surveySnap);
            if (drawRef.current) requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        if (isCtrlPressed && !isLabelDragging) {
            let foundLabel = false;
            for (let i = points.length - 1; i >= 0; i--) {
                if (isOverLabelArea(mouseX, mouseY, points[i])) {
                    foundLabel = true;
                    break;
                }
            }
            if (foundLabel !== isOverLabel) {
                setIsOverLabel(foundLabel);
            }
        } else if (isOverLabel) {
            setIsOverLabel(false);
        }

        // Update hover state for line selection / trim / extend mode
        const isLineSelectMode = (isSelectionMode || isTrimmingLines || isExtendingLines) && drawingMode === 'none' && !isDeletingLines;
        if (isLineSelectMode) {
            const hovered = findLineNearClick(mouseX, mouseY, 15) !== null;
            if (hovered !== isOverLine) setIsOverLine(hovered);
        } else if (isOverLine) {
            setIsOverLine(false);
        }

        // Update hover state for dimension selection
        if (drawingMode === 'none' && !isDeletingLines) {
            const hoveredDim = findDimensionNearClick(mouseX, mouseY, 18) !== null;
            if (hoveredDim !== isOverDim) setIsOverDim(hoveredDim);
        } else if (isOverDim) {
            setIsOverDim(false);
        }

        if (activeDimGrip) {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'center', 'nearest']));
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 20, null);
            setActiveOsnapPoint(snapped);
            const targetW = snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: mouseWorld.easting, northing: mouseWorld.northing };
            const updated = updateDimensionFromGrip(activeDimGrip.dim, activeDimGrip.type, targetW);
            if (updated && props.onUpdateDimension) {
                props.onUpdateDimension(updated);
            }
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
            return;
        }

        if (selectedDimensionId && drawingMode === 'none' && !isDeletingLines) {
            const hDimGrip = findDimensionGripNearClick(mouseX, mouseY, 12);
            if (hDimGrip?.id !== hoveredDimGrip?.id) {
                setHoveredDimGrip(hDimGrip);
                if (drawRef.current) {
                    requestAnimationFrame(() => drawRef.current?.());
                }
            }
        } else if (hoveredDimGrip) {
            setHoveredDimGrip(null);
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
        }

        if (activeGrip) {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'center', 'nearest', 'perpendicular', 'tangent']));
            const anchor = activeGrip.type === 'start'
                ? (pointMap.get(activeGrip.line.to) ? { easting: pointMap.get(activeGrip.line.to)!.easting, northing: pointMap.get(activeGrip.line.to)!.northing } : (activeGrip.line.toPt ? { easting: activeGrip.line.toPt.x, northing: activeGrip.line.toPt.y } : null))
                : (pointMap.get(activeGrip.line.from) ? { easting: pointMap.get(activeGrip.line.from)!.easting, northing: pointMap.get(activeGrip.line.from)!.northing } : (activeGrip.line.fromPt ? { easting: activeGrip.line.fromPt.x, northing: activeGrip.line.fromPt.y } : null));
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 20, anchor);
            setActiveOsnapPoint(snapped);
            let previewPoint = snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: mouseWorld.easting, northing: mouseWorld.northing };
            if (isOrthoEnabled && anchor && !snapped) {
                previewPoint = applyOrthoConstraint(anchor, previewPoint);
            }
            cursorWorldPosRef.current = { x: previewPoint.easting, y: previewPoint.northing };
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
            return;
        }

        if (selectedLineIds.size > 0 && drawingMode === 'none' && !isTrimmingLines && !isExtendingLines) {
            const hGrip = findGripNearClick(mouseX, mouseY, 9);
            if (hGrip?.id !== hoveredGrip?.id) {
                setHoveredGrip(hGrip);
                if (drawRef.current) {
                    requestAnimationFrame(() => drawRef.current?.());
                }
            }
        } else if (hoveredGrip) {
            setHoveredGrip(null);
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
        }

        if (isPickingOtOrigin) {
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 25);
            setActiveOsnapPoint(snapped);
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
            return;
        }

        if (isTrimmingLines) {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            cursorWorldPosRef.current = { x: mouseWorld.easting, y: mouseWorld.northing };
            const hoveredLine = findLineNearClick(mouseX, mouseY, 15);
            if (hoveredLine) {
                const action = computeTrimAction(hoveredLine, mouseWorld.easting, mouseWorld.northing);
                setTrimHoverPreview(action?.preview || null);
            } else {
                setTrimHoverPreview(null);
            }
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
            return;
        } else if (trimHoverPreview) {
            setTrimHoverPreview(null);
        }

        if (drawingMode === 'polygon') {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
            const anchor = polygonCenter;
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 20, anchor);
            setActiveOsnapPoint(snapped);
            const previewPoint = snapped
                ? { easting: snapped.easting, northing: snapped.northing }
                : { easting: mouseWorld.easting, northing: mouseWorld.northing };
            cursorWorldPosRef.current = { x: previewPoint.easting, y: previewPoint.northing };
            if (drawRef.current) requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        if (drawingMode === 'circle') {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            if (circleSubMode === 'ttr') {
                if (ttrStep < 3) {
                    const ent = findTtrEntityNearClick(mouseX, mouseY, 25);
                    setTtrHoverEntity(ent);
                    cursorWorldPosRef.current = { x: mouseWorld.easting, y: mouseWorld.northing };
                } else {
                    cursorWorldPosRef.current = { x: mouseWorld.easting, y: mouseWorld.northing };
                }
                if (drawRef.current) {
                    requestAnimationFrame(() => drawRef.current?.());
                }
                return;
            }

            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
            const anchor = circleCenter;
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 20, anchor);
            setActiveOsnapPoint(snapped);
            let previewPoint = snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: mouseWorld.easting, northing: mouseWorld.northing };
            if (isOrthoEnabled && anchor && !snapped) {
                previewPoint = applyOrthoConstraint(anchor, previewPoint);
            }
            cursorWorldPosRef.current = { x: previewPoint.easting, y: previewPoint.northing };
            if (drawRef.current) {
                requestAnimationFrame(() => drawRef.current?.());
            }
            return;
        }

        if (drawingMode === 'polylines' || drawingMode === 'breaklines' || drawingMode === 'inclusion' || drawingMode === 'exclusion') {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            const worldX = mouseWorld.easting;
            const worldY = mouseWorld.northing;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const anchor = polylinePoints.length > 0
                ? { easting: polylinePoints[polylinePoints.length - 1].easting, northing: polylinePoints[polylinePoints.length - 1].northing }
                : null;
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 15, anchor);
            setActiveOsnapPoint(snapped);
            if (polylinePoints.length > 0) {
                let previewPoint = snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: worldX, northing: worldY };
                if (isOrthoEnabled && anchor && !snapped) {
                    previewPoint = applyOrthoConstraint(anchor, previewPoint);
                }
                cursorWorldPosRef.current = { x: previewPoint.easting, y: previewPoint.northing };
                // Manually trigger draw to update rubberband using stable ref
                if (drawRef.current) {
                    requestAnimationFrame(() => drawRef.current?.());
                }
            }
        } else if (isDimensionDrawingMode(drawingMode)) {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            const worldX = mouseWorld.easting;
            const worldY = mouseWorld.northing;
            const dimSnapThresh = 20;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const anchor = dimPhase > 0 && dimP1 ? { easting: dimP1.easting, northing: dimP1.northing } : null;
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, dimSnapThresh, anchor);
            setDimSnapPoint(snapped ? { easting: snapped.easting, northing: snapped.northing } : null);
            setActiveOsnapPoint(snapped);
            setDimPreviewMouse(snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: worldX, northing: worldY });

            if (['radius-dim', 'diameter-dim', 'arc-length-dim', 'curve-data-dim'].includes(drawingMode) && dimPhase === 0) {
                const curveTarget = resolveCurveTargetFromClick(mouseX, mouseY);
                setDimHoveredCurve(curveTarget ? {
                    kind: curveTarget.kind,
                    center: curveTarget.center,
                    radius: curveTarget.radius,
                    startAngle: curveTarget.p1 ? Math.atan2(curveTarget.p1.northing - curveTarget.center.northing, curveTarget.p1.easting - curveTarget.center.easting) : undefined,
                    centralAngle: curveTarget.deltaRad,
                    isLeftCurve: curveTarget.isLeftCurve,
                } : null);
            } else {
                setDimHoveredCurve(null);
            }
        } else if (drawingMode === 'add-point') {
            const mouseWorld = screenToWorld(mouseX, mouseY);
            const worldX = mouseWorld.easting;
            const worldY = mouseWorld.northing;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 20, null);
            setActiveOsnapPoint(snapped);
            setDimPreviewMouse(snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: worldX, northing: worldY });
            if (snapped) {
                setAddPointNorthing(snapped.northing.toFixed(3));
                setAddPointEasting(snapped.easting.toFixed(3));
            } else {
                setAddPointNorthing(worldY.toFixed(3));
                setAddPointEasting(worldX.toFixed(3));
            }
        } else if (drawingMode === 'boundary-line') {
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const anchor = boundaryLineFrom ? { easting: boundaryLineFrom.easting, northing: boundaryLineFrom.northing } : null;
            const snapped = resolveOsnapAtScreen(mouseX, mouseY, activeModes, 20, anchor);
            setActiveOsnapPoint(snapped);
        } else {
            if (cursorWorldPosRef.current !== null) {
                cursorWorldPosRef.current = null;
            }
            if (activeOsnapPoint !== null) {
                setActiveOsnapPoint(null);
            }
        }
    }, [
        isCtrlPressed, isLabelDragging, points, isOverLabel, isOverLabelArea,
        drawingMode, polylinePoints.length, transform,
        isSelectionMode, isDeletingLines, isTrimmingLines, isExtendingLines, findLineNearClick, isOverLine,
        inlineOsnapOverride, runningOsnaps, resolveOsnapAtScreen, findNearestSurveyPoint, activeOsnapPoint, dimPhase, dimP1, boundaryLineFrom, activeCommandPrompt, isPickingPolarCenter,
        isOrthoEnabled, applyOrthoConstraint, screenToWorld, isPickingOtOrigin,
        circleSubMode, ttrStep, polygonCenter, findTtrEntityNearClick, computeTrimAction, trimHoverPreview,
        activeGrip, hoveredGrip, findGripNearClick, selectedLineIds, pointMap,
        isDimensionDrawingMode, resolveCurveTargetFromClick, findDimensionNearClick, isOverDim
    ]);

    const handleCanvasContextMenu = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        const container = containerRef.current;
        if (!container) return;
        const rect = container.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        if (e.shiftKey) {
            e.preventDefault();
            setInlineOsnapMenuPos(placeFlippingMenu({
                x: clickX,
                y: clickY,
                containerWidth: rect.width,
                containerHeight: rect.height,
                menuWidth: OSNP_MENU_WIDTH,
                menuHeight: OSNP_MENU_MAX_HEIGHT,
            }));
            return;
        }

        // Check if right-clicked on a dimension
        const clickedDim = findDimensionNearClick(clickX, clickY, 18);
        if (clickedDim) {
            e.preventDefault();
            setSelectedDimensionId(clickedDim.id);
            setDimContextMenu({ x: clickX, y: clickY, dimId: clickedDim.id });
            return;
        }
    }, [findDimensionNearClick]);


    const handleWheel = useCallback((e: React.WheelEvent) => {
        e.preventDefault();
        if (drawingMode === 'polygon') {
            const delta = e.deltaY < 0 ? 1 : -1;
            setPolygonSides(prev => Math.max(3, Math.min(256, prev + delta)));
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }
        const container = containerRef.current;
        if (!container) return;

        const rect = container.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const mouseY = e.clientY - rect.top;

        const zoomFactor = e.deltaY < 0 ? 1.1 : 1 / 1.1;

        setTransform(prev => {
            const newScale = Math.max(0.00001, Math.min(50000, prev.scale * zoomFactor));
            const actualZoomFactor = newScale / prev.scale;
            if (Math.abs(actualZoomFactor - 1) < 1e-9) return prev;

            const newOffsetX = mouseX - (mouseX - prev.offsetX) * actualZoomFactor;
            const newOffsetY = mouseY - (mouseY - prev.offsetY) * actualZoomFactor;
            return { scale: newScale, offsetX: newOffsetX, offsetY: newOffsetY };
        });
    }, [drawingMode]);

    // -----------------------------------------------------------------------
    // Boundary-align helpers
    // -----------------------------------------------------------------------
    /** Resolve a boundary call's world-space from/to endpoints by reading
     *  the bf's cached synthMap and applying its translate-then-rotate transform.
     *  Returns null if either endpoint is missing from synthMap. */
    const getBoundaryCallWorldEndpoints = useCallback((bfId: string, callId: string): {
        from: { x: number; y: number };
        to: { x: number; y: number };
    } | null => {
        const cache = bfGeometryCacheRef.current.get(bfId);
        if (!cache) return null;
        const bf = boundaryFiles?.find(b => b.id === bfId);
        if (!bf) return null;
        const call = bf.calls.find(c => c.id === callId);
        if (!call || !call.from || !call.to) return null;
        const fromUn = cache.synthMap.get(call.from);
        const toUn   = cache.synthMap.get(call.to);
        if (!fromUn || !toUn) return null;
        const rotDeg = bf.rotationDeg ?? cache.rotDeg ?? 0;
        const txE = bf.translationE ?? cache.txE ?? 0;
        const txN = bf.translationN ?? cache.txN ?? 0;
        const rotRad = (rotDeg * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const applyTx = (p: { easting: number; northing: number }) => {
            const dx = p.easting - cache.pob.easting;
            const dy = p.northing - cache.pob.northing;
            return {
                x: dx * cosR - dy * sinR + cache.pob.easting + txE,
                y: dx * sinR + dy * cosR + cache.pob.northing + txN,
            };
        };
        return { from: applyTx(fromUn), to: applyTx(toUn) };
    }, [boundaryFiles]);

    /** Find the boundary call segment closest to (worldX, worldY) within
     *  worldThreshold. Returns { bfId, callId } or null. Curves are tested
     *  as straight chords (good enough for picking — alignment uses chord
     *  direction anyway). */
    const hitTestBoundaryCall = useCallback((worldX: number, worldY: number, worldThreshold: number): {
        bfId: string;
        callId: string;
    } | null => {
        if (!boundaryFiles) return null;
        let bestBf: string | null = null;
        let bestCall: string | null = null;
        let bestDistSq = worldThreshold * worldThreshold;
        for (const bf of boundaryFiles) {
            if (bf.hidden) continue;
            const cache = bfGeometryCacheRef.current.get(bf.id);
            if (!cache) continue;
            for (const call of bf.calls) {
                const ends = getBoundaryCallWorldEndpoints(bf.id, call.id);
                if (!ends) continue;
                // Point-to-segment squared distance.
                const vx = ends.to.x - ends.from.x;
                const vy = ends.to.y - ends.from.y;
                const lenSq = vx * vx + vy * vy;
                if (lenSq < 1e-12) continue;
                let t = ((worldX - ends.from.x) * vx + (worldY - ends.from.y) * vy) / lenSq;
                t = Math.max(0, Math.min(1, t));
                const cx = ends.from.x + t * vx;
                const cy = ends.from.y + t * vy;
                const dSq = (worldX - cx) ** 2 + (worldY - cy) ** 2;
                if (dSq < bestDistSq) {
                    bestDistSq = dSq;
                    bestBf = bf.id;
                    bestCall = call.id;
                }
            }
        }
        if (bestBf && bestCall) return { bfId: bestBf, callId: bestCall };
        return null;
    }, [boundaryFiles, getBoundaryCallWorldEndpoints]);

    /** Compute the (translationE, translationN, rotationDeg) needed on the
     *  movable bf so its picked line becomes collinear with the target line,
     *  with movable.from snapped to target.from. Also returns the unit
     *  direction along the target line and both line lengths (used by the
     *  slide-phase mouse handler). */
    const computeBoundaryAlignSnap = useCallback((
        movablePick: { bfId: string; callId: string },
        targetPick:  { bfId: string; callId: string },
        flipped: boolean = false,
    ): {
        txE: number; txN: number; rotDeg: number;
        dirE: number; dirN: number;
        movableLen: number; targetLen: number;
    } | null => {
        const movCache = bfGeometryCacheRef.current.get(movablePick.bfId);
        const tgtEnds  = getBoundaryCallWorldEndpoints(targetPick.bfId, targetPick.callId);
        if (!movCache || !tgtEnds) return null;
        const bf = boundaryFiles?.find(b => b.id === movablePick.bfId);
        const call = bf?.calls.find(c => c.id === movablePick.callId);
        if (!bf || !call || !call.from || !call.to) return null;
        const rawFromUn = movCache.synthMap.get(call.from);
        const rawToUn   = movCache.synthMap.get(call.to);
        if (!rawFromUn || !rawToUn) return null;
        // When flipped, swap which movable endpoint snaps to target.from — yields a 180° rotation.
        const movFromUn = flipped ? rawToUn   : rawFromUn;
        const movToUn   = flipped ? rawFromUn : rawToUn;
        // Solve F(p) = R(θ)(p - POB) + POB + T where F(movFromUn) = tgtEnds.from
        // and F(movToUn) lies on the target ray with same orientation.
        const targetDx = tgtEnds.to.x - tgtEnds.from.x;
        const targetDy = tgtEnds.to.y - tgtEnds.from.y;
        const targetLen = Math.hypot(targetDx, targetDy);
        const movDx = movToUn.easting - movFromUn.easting;
        const movDy = movToUn.northing - movFromUn.northing;
        const movableLen = Math.hypot(movDx, movDy);
        if (targetLen < 1e-9 || movableLen < 1e-9) return null;
        const targetAng = Math.atan2(targetDy, targetDx);
        const movAng    = Math.atan2(movDy, movDx);
        const thetaRad  = targetAng - movAng;
        const cosT = Math.cos(thetaRad);
        const sinT = Math.sin(thetaRad);
        // T = Q_from - POB - R(θ)(M_from - POB)
        const ax = movFromUn.easting  - movCache.pob.easting;
        const ay = movFromUn.northing - movCache.pob.northing;
        const rotAx = ax * cosT - ay * sinT;
        const rotAy = ax * sinT + ay * cosT;
        const txE = tgtEnds.from.x - movCache.pob.easting  - rotAx;
        const txN = tgtEnds.from.y - movCache.pob.northing - rotAy;
        return {
            txE,
            txN,
            rotDeg: (thetaRad * 180) / Math.PI,
            dirE: targetDx / targetLen,
            dirN: targetDy / targetLen,
            movableLen,
            targetLen,
        };
    }, [boundaryFiles, getBoundaryCallWorldEndpoints]);

    /** Enumerate world-space corners of a bf using its cached synthMap +
     *  (optionally overridden) transform. Used by slide-time corner snap to
     *  find coincident movable<->target vertices. */
    const getBoundaryCornersWorld = useCallback((bfId: string, overrideTx?: { txE: number; txN: number; rotDeg: number }): { x: number; y: number }[] => {
        const cache = bfGeometryCacheRef.current.get(bfId);
        if (!cache) return [];
        const bf = boundaryFiles?.find(b => b.id === bfId);
        const tx = overrideTx ?? {
            txE: bf?.translationE ?? cache.txE ?? 0,
            txN: bf?.translationN ?? cache.txN ?? 0,
            rotDeg: bf?.rotationDeg ?? cache.rotDeg ?? 0,
        };
        const rotRad = (tx.rotDeg * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const out: { x: number; y: number }[] = [];
        cache.synthMap.forEach(p => {
            const dx = p.easting - cache.pob.easting;
            const dy = p.northing - cache.pob.northing;
            out.push({
                x: dx * cosR - dy * sinR + cache.pob.easting + tx.txE,
                y: dx * sinR + dy * cosR + cache.pob.northing + tx.txN,
            });
        });
        return out;
    }, [boundaryFiles]);

    // =====================================================================
    // Corner-align tool helpers
    // =====================================================================

    /** Enumerate the deed's vertices with their point-number labels, in BOTH the
     *  un-baked COGO frame (the fit's source space) and current world space (for
     *  hit-testing / drawing markers). Incorporates the bf's current translation & rotation
     *  so corner highlights stay locked to the silhouette when transforms update. */
    const getBoundaryCornerEntries = useCallback((bfId: string): { pn: string; srcE: number; srcN: number; x: number; y: number }[] => {
        let cache = bfGeometryCacheRef.current.get(bfId);
        if (!cache && drawRef.current) {
            drawRef.current();
            cache = bfGeometryCacheRef.current.get(bfId);
        }
        if (!cache) return [];
        const bf = boundaryFiles?.find(b => b.id === bfId);
        const rotDeg = bf?.rotationDeg ?? cache.rotDeg ?? 0;
        const txE = bf?.translationE ?? cache.txE ?? 0;
        const txN = bf?.translationN ?? cache.txN ?? 0;
        const rotRad = (rotDeg * Math.PI) / 180;
        const cosR = Math.cos(rotRad);
        const sinR = Math.sin(rotRad);
        const out: { pn: string; srcE: number; srcN: number; x: number; y: number }[] = [];
        cache.synthMap.forEach((p, pn) => {
            const dx = p.easting - cache.pob.easting;
            const dy = p.northing - cache.pob.northing;
            out.push({
                pn,
                srcE: p.easting,
                srcN: p.northing,
                x: dx * cosR - dy * sinR + cache.pob.easting + txE,
                y: dx * sinR + dy * cosR + cache.pob.northing + txN,
            });
        });
        return out;
    }, [boundaryFiles]);

    /** Nearest deed corner to a world coordinate, within `tol` world units. */
    const hitTestBoundaryCorner = useCallback((bfId: string, worldX: number, worldY: number, tol: number) => {
        let best: { pn: string; srcE: number; srcN: number; x: number; y: number } | null = null;
        let bestD2 = tol * tol;
        for (const c of getBoundaryCornerEntries(bfId)) {
            const d2 = (c.x - worldX) ** 2 + (c.y - worldY) ** 2;
            if (d2 <= bestD2) { bestD2 = d2; best = c; }
        }
        return best;
    }, [getBoundaryCornerEntries]);

    // Deed corner vertices with current transform applied, used for SVG markers.
    // Derived directly from the boundary geometry and current transform so it updates
    // immediately whenever a corner pair is aligned, without waiting for rAF draw frames.
    const cornerAlignCorners = useMemo(() => {
        if (drawingMode !== 'boundary-corner-align' || !cornerAlignBfId) return [];
        return getBoundaryCornerEntries(cornerAlignBfId);
    }, [drawingMode, cornerAlignBfId, getBoundaryCornerEntries]);

    /**
     * Rigid-body (2D Helmert without scale) fit of the deed's un-baked vertices onto
     * the found points the user paired them with. See utils/cornerAlign.ts — the solve
     * is expressed in the same frame the BFOverlay renderer uses, so the result drops
     * straight into translationE/translationN/rotationDeg.
     *
     *  • 1 pair   → pure translation, the boundary keeps its current rotation.
     *  • 2+ pairs → least-squares rotation + translation (deed shape preserved:
     *    bearings and distances are never stretched).
     */
    const solveCornerAlign = useCallback((bfId: string, pairs: CornerAlignPair[], fallbackRotDeg: number) => {
        let cache = bfGeometryCacheRef.current.get(bfId);
        if (!cache && drawRef.current) {
            drawRef.current();
            cache = bfGeometryCacheRef.current.get(bfId);
        }
        if (!cache) return null;
        return solveRigidCornerAlign(cache.pob, pairs, fallbackRotDeg);
    }, []);

    /** Re-solve and live-preview the fit for the given pair set. */
    const applyCornerAlign = useCallback((bfId: string, pairs: CornerAlignPair[]) => {
        if (!props.onUpdateBoundaryTransform) return;
        const base = cornerAlignBaseTxRef.current;
        const fit = solveCornerAlign(bfId, pairs, base?.rotationDeg ?? 0);
        if (!fit) return;
        // Synchronously update the cache entry so immediate non-React lookups see the new values
        const cache = bfGeometryCacheRef.current.get(bfId);
        if (cache) {
            cache.txE = fit.translationE;
            cache.txN = fit.translationN;
            cache.rotDeg = fit.rotationDeg;
        }
        props.onUpdateBoundaryTransform(bfId, {
            translationE: fit.translationE,
            translationN: fit.translationN,
            rotationDeg: fit.rotationDeg,
        }, { immediate: true });
        setCornerAlignFit({ rmse: fit.rmse, maxResidual: fit.maxResidual, rotDeg: fit.rotationDeg });
    }, [props, solveCornerAlign]);

    /** Tear down corner-align state. `restore` puts the boundary back where it started. */
    const resetCornerAlign = useCallback((restore: boolean) => {
        if (restore && cornerAlignBfId && cornerAlignBaseTxRef.current && props.onUpdateBoundaryTransform) {
            const cache = bfGeometryCacheRef.current.get(cornerAlignBfId);
            if (cache) {
                cache.txE = cornerAlignBaseTxRef.current.translationE;
                cache.txN = cornerAlignBaseTxRef.current.translationN;
                cache.rotDeg = cornerAlignBaseTxRef.current.rotationDeg;
            }
            props.onUpdateBoundaryTransform(cornerAlignBfId, cornerAlignBaseTxRef.current, { immediate: true });
        }
        cornerAlignBaseTxRef.current = null;
        setCornerAlignBfId(null);
        setCornerAlignPairs([]);
        setCornerAlignPendingCorner(null);
        setCornerAlignPicker(null);
        setCornerAlignFit(null);
        setCornerAlignHover(null);
    }, [cornerAlignBfId, props]);

    /** Pair the pending corner with a found point and refresh the fit. */
    const commitCornerAlignPoint = useCallback((sp: SurveyPoint) => {
        const bfId = cornerAlignBfId;
        const corner = cornerAlignPendingCorner;
        if (!bfId || !corner) return;
        // Re-picking the same corner (or the same found point) replaces the old pair
        // rather than double-weighting it in the least-squares solve.
        const next = cornerAlignPairs.filter(p => p.pn !== corner.pn && p.targetPn !== sp.pointNumber);
        next.push({
            pn: corner.pn,
            srcE: corner.srcE,
            srcN: corner.srcN,
            targetPn: sp.pointNumber,
            targetE: sp.easting,
            targetN: sp.northing,
        });
        setCornerAlignPairs(next);
        setCornerAlignPendingCorner(null);
        setCornerAlignPicker(null);
        applyCornerAlign(bfId, next);
    }, [cornerAlignBfId, cornerAlignPendingCorner, cornerAlignPairs, applyCornerAlign]);

    /** Snap-aware world point for a canvas pick: hovered osnap → resolved osnap → survey point → raw. */
    const resolveSnappedWorld = useCallback((screenX: number, screenY: number, hoveredSnap: { easting: number; northing: number } | null) => {
        const activeOsnapModes = inlineOsnapOverride
            ? new Set<OsnapMode>([inlineOsnapOverride])
            : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
        const snapped = hoveredSnap || resolveOsnapAtScreen(screenX, screenY, activeOsnapModes, 25);
        if (snapped) return { easting: snapped.easting, northing: snapped.northing };
        const nearest = findNearestSurveyPoint(screenX, screenY, 25);
        if (nearest) return { easting: nearest.easting, northing: nearest.northing };
        return screenToWorld(screenX, screenY);
    }, [inlineOsnapOverride, runningOsnaps, resolveOsnapAtScreen, findNearestSurveyPoint, screenToWorld]);

    /** Move by a world-space displacement (canvas picks are already in feet). */
    const moveSelectionByWorld = useCallback((dE: number, dN: number): number => {
        if (selectedLineIds.size === 0) return 0;
        return transformSelectedLines(translationTransform(dE, dN), false);
    }, [selectedLineIds.size, transformSelectedLines]);

    const handleMouseDown = useCallback((e: React.MouseEvent) => {
        // Middle-drag panning stays live even while a command prompt is open.
        if (e.button === 1) {
            e.preventDefault();
            document.body.classList.add('panning');
            setIsPanning(true);
            lastMousePos.current = { x: e.clientX, y: e.clientY };
            return;
        }

        // POLARARRAY center pick is the one canvas click a prompt allows.
        if (activeCommandPrompt?.id === 'polar_array' && isPickingPolarCenter) {
            if (e.button !== 0) return;
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const activeModes = inlineOsnapOverride
                ? new Set<OsnapMode>([inlineOsnapOverride])
                : new Set<OsnapMode>([
                    ...(runningOsnaps.size > 0
                        ? runningOsnaps
                        : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest'])),
                    'endpoint',
                ]);
            const snapped = activeOsnapPoint || resolveOsnapAtScreen(clickX, clickY, activeModes, 25);
            const nearestExisting = snapped ? null : findNearestSurveyPoint(clickX, clickY, 25);
            const target = snapped
                ? { easting: snapped.easting, northing: snapped.northing }
                : nearestExisting
                ? { easting: nearestExisting.easting, northing: nearestExisting.northing }
                : screenToWorld(clickX, clickY);
            const roundedEasting = Math.round(target.easting * 1000) / 1000;
            const roundedNorthing = Math.round(target.northing * 1000) / 1000;
            const source = snapped?.sourcePoint
                ? `survey point ${snapped.sourcePoint.pointNumber}`
                : snapped
                ? `${snapped.mode} snap`
                : nearestExisting
                ? `survey point ${nearestExisting.pointNumber}`
                : 'free point';
            setCommandPromptValues(prev => ({ ...prev, center: `${roundedEasting},${roundedNorthing}` }));
            setCommandPromptIndex(2);
            setIsPickingPolarCenter(false);
            setActiveOsnapPoint(null);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            setTerminalIncoming({
                text: `POLARARRAY: Rotation center set to E ${roundedEasting.toFixed(3)}, N ${roundedNorthing.toFixed(3)} (${source}) — drag the ring dot or press Enter to apply [Esc cancels].`,
                tone: 'ok',
            });
            setCommandFocusRequest(n => n + 1);
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        // Prompt boxes own their own clicks; canvas picks stay paused until the
        // prompt is applied or cancelled.
        if (activeCommandPrompt) return;
        if (inlineOsnapMenuPos) {
            setInlineOsnapMenuPos(null);
        }
        if (dimContextMenu) {
            setDimContextMenu(null);
        }
        const currentSnap = activeOsnapPoint;
        if (activeOsnapPoint !== null) {
            setActiveOsnapPoint(null);
        }

        if (e.button !== 0) return;

        // COPY command — first click sets the base point, later clicks place
        // copies at that displacement and keep the base point for repeat copies.
        if (isCopyingSelection) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const world = resolveSnappedWorld(clickX, clickY, currentSnap);

            if (!copyBasePoint) {
                setCopyBasePoint(world);
                setTerminalIncoming({ text: 'COPY: Base point set — click a destination to place copies [Esc to exit]', tone: 'info' });
                return;
            }

            const dx = world.easting - copyBasePoint.easting;
            const dy = world.northing - copyBasePoint.northing;
            if (Math.hypot(dx, dy) < 1e-9) {
                setTerminalIncoming({ text: 'COPY: Pick a destination away from the base point', tone: 'error' });
                return;
            }

            const count = duplicateSelectedLines(dx, dy);
            setTerminalIncoming(count > 0
                ? { text: `COPY: Placed ${count} ${count === 1 ? 'entity' : 'entities'} (ΔE ${dx.toFixed(2)}, ΔN ${dy.toFixed(2)}) — click another destination or Esc`, tone: 'ok' }
                : { text: 'COPY: Nothing copied — select entities first (S), then run COPY', tone: 'error' });
            return;
        }

        // OFFSET command — first pick chooses the entity, second click chooses
        // the side. The distance stays armed for repeated offsets.
        if (isOffsetMode) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const world = screenToWorld(clickX, clickY);

            if (offsetDistance === null) {
                const measurePoint = currentSnap
                    ? { easting: currentSnap.easting, northing: currentSnap.northing }
                    : world;
                if (!offsetPickStart) {
                    setOffsetPickStart(measurePoint);
                    setTerminalIncoming({ text: 'OFFSET: Click a second point to measure the distance, or type one', tone: 'info' });
                } else {
                    const measured = Math.hypot(measurePoint.easting - offsetPickStart.easting, measurePoint.northing - offsetPickStart.northing);
                    if (measured <= 1e-6) {
                        setTerminalIncoming({ text: 'OFFSET: Pick a second point away from the first', tone: 'error' });
                        return;
                    }
                    setOffsetDistance(measured);
                    setOffsetPickStart(null);
                    offsetPreviewKeyRef.current = null;
                    const shownDistance = convertLinearUnits(measured, 'foot', settings.linearUnits || 'usSurveyFoot');
                    setTerminalIncoming({
                        text: `OFFSET: Distance ${shownDistance.toFixed(2)} ${getLinearUnitAbbreviation(settings.linearUnits)} — click the entity to offset`,
                        tone: 'ok',
                    });
                }
                return;
            }

            if (!offsetTargetId) {
                const clicked = findLineNearClick(clickX, clickY, 15);
                if (!clicked) {
                    setTerminalIncoming({ text: 'OFFSET: Click a line, polyline, arc or circle to offset', tone: 'error' });
                    return;
                }
                setOffsetTargetId(clicked.id || `${clicked.from}-${clicked.to}`);
                offsetPreviewKeyRef.current = null;
                setTerminalIncoming({
                    text: isCircleLine(clicked)
                        ? 'OFFSET: Click inside or outside the circle to choose the side [Esc to exit]'
                        : 'OFFSET: Click the side to offset toward [Esc to exit]',
                    tone: 'info',
                });
                return;
            }

            const target = lines.find(l => (l.id || `${l.from}-${l.to}`) === offsetTargetId);
            if (!target) {
                setOffsetTargetId(null);
                clearOffsetPreview();
                setTerminalIncoming({ text: 'OFFSET: That entity is no longer available — pick another', tone: 'error' });
                return;
            }

            const result = applyOffsetAtPoint(target, { x: world.easting, y: world.northing });
            if (result.ok) {
                clearOffsetPreview();
                setTerminalIncoming({
                    text: `OFFSET: Created ${result.count} ${result.count === 1 ? 'entity' : 'entities'} — select another entity to offset, or Esc to exit`,
                    tone: 'ok',
                });
                setOffsetTargetId(null);
            } else {
                setTerminalIncoming({ text: result.message, tone: 'error' });
            }
            return;
        }

        // SCALE — first click sets the reference point (snaps honored); the next
        // click scales relative to the selection radius, or a factor can be typed.
        if (isScalingSelection) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const world = resolveSnappedWorld(clickX, clickY, currentSnap);

            if (!scaleBasePoint) {
                const referenceRadius = selectionRadiusFrom(world);
                scaleReferenceRadiusRef.current = referenceRadius > 1e-9 ? referenceRadius : null;
                setScaleBasePoint(world);
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({
                    text: `SCALE: Reference point E ${world.easting.toFixed(2)}, N ${world.northing.toFixed(2)} — click to scale or type a factor [Esc to cancel]`,
                    tone: 'ok',
                });
                return;
            }

            const distance = Math.hypot(world.easting - scaleBasePoint.easting, world.northing - scaleBasePoint.northing);
            if (distance < 1e-9) {
                setTerminalIncoming({ text: 'SCALE: Pick a point away from the reference point', tone: 'error' });
                return;
            }
            const referenceRadius = scaleReferenceRadiusRef.current;
            const factor = referenceRadius && referenceRadius > 1e-9 ? distance / referenceRadius : 1;
            if (!Number.isFinite(factor) || factor <= 0) {
                setTerminalIncoming({ text: 'SCALE: Could not resolve a positive scale factor', tone: 'error' });
                return;
            }
            const scaled = scaleSelectionCommand(factor, scaleBasePoint);
            const baseLabel = `E ${scaleBasePoint.easting.toFixed(2)}, N ${scaleBasePoint.northing.toFixed(2)}`;
            stopInteractiveTransforms();
            setCommandFocusRequest(n => n + 1);
            setTerminalIncoming({
                text: `SCALE: Scaled ${scaled} ${scaled === 1 ? 'entity' : 'entities'} by ${factor.toFixed(3)}× (${(factor * 100).toFixed(1)}%) about ${baseLabel}.`,
                tone: 'ok',
            });
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        // ROTATE — first click sets the base point (snaps honored); the next
        // click uses the angle from the base point to that click. A typed number
        // in the command line sets the angle directly.
        if (isRotatingSelection) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const world = resolveSnappedWorld(clickX, clickY, currentSnap);

            if (!rotatePivot) {
                setRotatePivot({ easting: world.easting, northing: world.northing });
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({
                    text: `ROTATE: Base point E ${world.easting.toFixed(2)}, N ${world.northing.toFixed(2)} — click a point or type the angle [Esc to cancel]`,
                    tone: 'ok',
                });
                return;
            }

            const dE = world.easting - rotatePivot.easting;
            const dN = world.northing - rotatePivot.northing;
            if (Math.hypot(dE, dN) < 1e-9) {
                setTerminalIncoming({ text: 'ROTATE: Pick a point away from the base point', tone: 'error' });
                return;
            }
            const rawDegrees = (Math.atan2(dN, dE) * 180) / Math.PI;
            const degrees = e.shiftKey ? Math.round(rawDegrees / 15) * 15 : rawDegrees;
            const turned = rotateSelectionCommand(degrees, rotatePivot);
            const pivotLabel = `E ${rotatePivot.easting.toFixed(2)}, N ${rotatePivot.northing.toFixed(2)}`;
            stopInteractiveTransforms();
            setCommandFocusRequest(n => n + 1);
            setTerminalIncoming({
                text: `ROTATE: Rotated ${turned} ${turned === 1 ? 'entity' : 'entities'} ${degrees.toFixed(2)}° about ${pivotLabel}.`,
                tone: 'ok',
            });
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        // MIRROR — two snapped axis points; the second one applies the reflection.
        if (isMirroringSelection) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            // [L] armed: the picked line's endpoints become the mirror axis.
            if (isPickingMirrorLine) {
                const pickedLine = findLineNearClick(clickX, clickY, 15);
                const pickedEnds = pickedLine ? resolveLineEndpoints(pickedLine) : null;
                if (!pickedLine || !pickedEnds) {
                    setTerminalIncoming({ text: 'MIRROR: No line under the cursor — click a line, or press [L] to go back to two points.', tone: 'error' });
                    return;
                }
                const axisA = { easting: pickedEnds.p1.x, northing: pickedEnds.p1.y };
                const axisB = { easting: pickedEnds.p2.x, northing: pickedEnds.p2.y };
                if (Math.hypot(axisB.easting - axisA.easting, axisB.northing - axisA.northing) < 1e-9) {
                    setTerminalIncoming({ text: 'MIRROR: That line has zero length — pick another line.', tone: 'error' });
                    return;
                }
                const mirroredByLine = mirrorSelectionCommand(axisA, axisB);
                stopInteractiveTransforms();
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({
                    text: `MIRROR: Mirrored ${mirroredByLine} ${mirroredByLine === 1 ? 'entity' : 'entities'} across the picked line (E ${axisA.easting.toFixed(2)}, N ${axisA.northing.toFixed(2)} → E ${axisB.easting.toFixed(2)}, N ${axisB.northing.toFixed(2)}).`,
                    tone: 'ok',
                });
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }

            const world = resolveSnappedWorld(clickX, clickY, currentSnap);

            if (!mirrorAxisStart) {
                setMirrorAxisStart(world);
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({
                    text: `MIRROR: First axis point E ${world.easting.toFixed(2)}, N ${world.northing.toFixed(2)} — click or type the second point [Esc to cancel]`,
                    tone: 'ok',
                });
                return;
            }

            if (Math.hypot(world.easting - mirrorAxisStart.easting, world.northing - mirrorAxisStart.northing) < 1e-9) {
                setTerminalIncoming({ text: 'MIRROR: Axis points must be different', tone: 'error' });
                return;
            }
            const mirrored = mirrorSelectionCommand(mirrorAxisStart, world);
            stopInteractiveTransforms();
            setCommandFocusRequest(n => n + 1);
            setTerminalIncoming({
                text: `MIRROR: Mirrored ${mirrored} ${mirrored === 1 ? 'entity' : 'entities'} across E ${mirrorAxisStart.easting.toFixed(2)}, N ${mirrorAxisStart.northing.toFixed(2)} → E ${world.easting.toFixed(2)}, N ${world.northing.toFixed(2)}.`,
                tone: 'ok',
            });
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        // MOVE — snapped base point, then a snapped destination.
        if (isMovingSelection) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const world = resolveSnappedWorld(clickX, clickY, currentSnap);

            if (!moveBasePoint) {
                setMoveBasePoint(world);
                setCommandFocusRequest(n => n + 1);
                setTerminalIncoming({
                    text: `MOVE: Base point E ${world.easting.toFixed(2)}, N ${world.northing.toFixed(2)} — click or type the destination [Esc to cancel]`,
                    tone: 'ok',
                });
                return;
            }

            const dE = world.easting - moveBasePoint.easting;
            const dN = world.northing - moveBasePoint.northing;
            if (Math.hypot(dE, dN) < 1e-9) {
                setTerminalIncoming({ text: 'MOVE: Pick a destination away from the base point', tone: 'error' });
                return;
            }
            const moved = moveSelectionByWorld(dE, dN);
            const baseLabel = `E ${moveBasePoint.easting.toFixed(2)}, N ${moveBasePoint.northing.toFixed(2)}`;
            stopInteractiveTransforms();
            setCommandFocusRequest(n => n + 1);
            setTerminalIncoming({
                text: `MOVE: Moved ${moved} ${moved === 1 ? 'entity' : 'entities'} from ${baseLabel} (ΔE ${dE.toFixed(2)}, ΔN ${dN.toFixed(2)}).`,
                tone: 'ok',
            });
            requestAnimationFrame(() => drawRef.current?.());
            return;
        }

        // OT (UCS) Origin Pick Mode — click on canvas or snap to point/line to set origin
        if (isPickingOtOrigin) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
            const snapped = currentSnap || resolveOsnapAtScreen(clickX, clickY, activeModes, 25);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            const nearestExisting = findNearestSurveyPoint(clickX, clickY, 25);

            let pickedE = 0;
            let pickedN = 0;
            if (snapped) {
                pickedE = snapped.easting;
                pickedN = snapped.northing;
            } else if (nearestExisting) {
                pickedE = nearestExisting.easting;
                pickedN = nearestExisting.northing;
            } else {
                const worldCoords = screenToWorld(clickX, clickY);
                pickedE = worldCoords.easting;
                pickedN = worldCoords.northing;
            }

            const roundedE = Math.round(pickedE * 1000) / 1000;
            const roundedN = Math.round(pickedN * 1000) / 1000;

            if (onSetOrientationTuple) {
                onSetOrientationTuple(prev => {
                    const m = prev.mode ?? 'drafting';
                    if (m === 'drafting') {
                        return {
                            ...prev,
                            draftingOriginX: roundedE,
                            draftingOriginY: roundedN,
                            originX: roundedE,
                            originY: roundedN,
                            placed: true,
                        };
                    } else {
                        return {
                            ...prev,
                            viewOriginX: roundedE,
                            viewOriginY: roundedN,
                            originX: roundedE,
                            originY: roundedN,
                            placed: true,
                        };
                    }
                });
            }
            setIsPickingOtOrigin(false);
            return;
        }

        // OT (UCS) Line Align Mode — click any line or boundary call to align orientation angle to it
        if (isPickingOtLineAlign) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            const clickedLine = findLineNearClick(clickX, clickY, 20);
            let targetAngleRad: number | null = null;
            let p1E: number | undefined;
            let p1N: number | undefined;

            if (clickedLine) {
                const p1 = pointMap.get(clickedLine.from);
                const p2 = pointMap.get(clickedLine.to);
                const x1 = p1 ? p1.easting : clickedLine.fromPt?.x;
                const y1 = p1 ? p1.northing : clickedLine.fromPt?.y;
                const x2 = p2 ? p2.easting : clickedLine.toPt?.x;
                const y2 = p2 ? p2.northing : clickedLine.toPt?.y;
                if (x1 !== undefined && y1 !== undefined && x2 !== undefined && y2 !== undefined) {
                    const dx = x2 - x1;
                    const dy = y2 - y1;
                    if (Math.hypot(dx, dy) > 1e-9) {
                        targetAngleRad = Math.atan2(dy, dx);
                        p1E = x1;
                        p1N = y1;
                    }
                }
            } else if (boundaryFiles) {
                const worldCoords = screenToWorld(clickX, clickY);
                const hit = hitTestBoundaryCall(worldCoords.easting, worldCoords.northing, 20 / transform.scale);
                if (hit) {
                    const endpoints = getBoundaryCallWorldEndpoints(hit.bfId, hit.callId);
                    if (endpoints) {
                        const dx = endpoints.to.x - endpoints.from.x;
                        const dy = endpoints.to.y - endpoints.from.y;
                        if (Math.hypot(dx, dy) > 1e-9) {
                            targetAngleRad = Math.atan2(dy, dx);
                            p1E = endpoints.from.x;
                            p1N = endpoints.from.y;
                        }
                    }
                }
            }

            if (targetAngleRad !== null && onSetOrientationTuple) {
                onSetOrientationTuple(prev => {
                    const m = prev.mode ?? 'drafting';
                    const rad = targetAngleRad!;
                    const originE = p1E !== undefined ? Math.round(p1E * 1000) / 1000 : (prev.originX || 0);
                    const originN = p1N !== undefined ? Math.round(p1N * 1000) / 1000 : (prev.originY || 0);
                    if (m === 'drafting') {
                        return {
                            ...prev,
                            draftingAngle: rad,
                            angle: rad,
                            draftingOriginX: originE,
                            draftingOriginY: originN,
                            originX: originE,
                            originY: originN,
                            placed: true,
                        };
                    } else {
                        return {
                            ...prev,
                            viewAngle: rad,
                            angle: rad,
                            viewOriginX: originE,
                            viewOriginY: originN,
                            originX: originE,
                            originY: originN,
                            placed: true,
                        };
                    }
                });
            }

            setIsPickingOtLineAlign(false);
            return;
        }

        // Symbol exclusion mode — click any point and we suppress symbols for
        // every point sharing that description (so disassociating one
        // disassociates the entire group). Click again to re-enable.
        if (symbolExclusionMode) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const HIT = 14;
                let nearestDesc: string | null = null;
                let bestDist = HIT * HIT;
                for (const p of points) {
                    if (p.hidden) continue;
                    if (!p.description) continue;
                    const pt = worldToScreen(p.easting, p.northing);
                    const sx = pt.x;
                    const sy = pt.y;
                    const d = (mx - sx) ** 2 + (my - sy) ** 2;
                    if (d < bestDist) { bestDist = d; nearestDesc = p.description; }
                }
                if (nearestDesc) {
                    const key = nearestDesc.trim().toUpperCase();
                    setExcludedSymbolDescriptions(prev => {
                        const next = new Set(prev);
                        if (next.has(key)) next.delete(key); else next.add(key);
                        return next;
                    });
                    return;
                }
            }
        }

        // Corner-align tool — alternate corner pick / found-point pick.
        // Shift+click on the found-point step opens the nest picker.
        if (drawingMode === 'boundary-corner-align' && cornerAlignBfId) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            const worldCoords = screenToWorld(mx, my);
            const worldX = worldCoords.easting;
            const worldY = worldCoords.northing;

            // An open nest picker swallows the click — the user chooses from the list.
            if (cornerAlignPicker) {
                setCornerAlignPicker(null);
                return;
            }

            if (!cornerAlignPendingCorner) {
                const CORNER_HIT_PX = 14;
                const corner = hitTestBoundaryCorner(cornerAlignBfId, worldX, worldY, CORNER_HIT_PX / transform.scale);
                if (corner) {
                    setCornerAlignPendingCorner(corner);
                    setCornerAlignHover(null);
                }
                return;
            }

            // Found-point step. Collect candidates around the cursor; Shift always
            // opens the list, and a plain click falls back to the list whenever the
            // nest holds more than one point so nothing is silently guessed.
            const PICK_PX = e.shiftKey ? 30 : 16;
            const tolWorld = PICK_PX / transform.scale;
            const nest = points
                .filter(p => !p.hidden && Math.hypot(p.easting - worldX, p.northing - worldY) <= tolWorld)
                .sort((a, b) =>
                    Math.hypot(a.easting - worldX, a.northing - worldY) -
                    Math.hypot(b.easting - worldX, b.northing - worldY));
            if (nest.length === 0) return;
            if (nest.length === 1 && !e.shiftKey) {
                commitCornerAlignPoint(nest[0]);
                return;
            }
            setCornerAlignPicker({ x: mx, y: my, items: nest.slice(0, 12) });
            return;
        }

        // Boundary-align tool — pick movable line, then target line, then slide.
        if (drawingMode === 'boundary-align') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            const worldCoords = screenToWorld(mx, my);
            const worldX = worldCoords.easting;
            const worldY = worldCoords.northing;
            // Pixel threshold → world threshold via current scale.
            const HIT_PX = 12;
            const hit = hitTestBoundaryCall(worldX, worldY, HIT_PX / transform.scale);
            const exitTool = () => {
                setBoundaryAlignSliding(false);
                setBoundaryAlignFlipped(false);
                boundaryAlignSlideRef.current = null;
                setBoundaryAlignMovable(null);
                setBoundaryAlignTarget(null);
                setDrawingMode('none');
                props.onBoundaryAlignRequestHandled?.();
            };
            // Active slide: a click commits the current position and exits the tool.
            if (boundaryAlignSliding) {
                exitTool();
                return;
            }
            // Movable preset from parent (BoundaryEditor) but no line picked yet:
            // the click must land on a line belonging to the preset bf.
            if (boundaryAlignMovable && boundaryAlignMovable.callId === '__pending__') {
                if (!hit) return;
                if (hit.bfId !== boundaryAlignMovable.bfId) return; // ignore clicks on other bfs
                setBoundaryAlignMovable({ bfId: hit.bfId, callId: hit.callId });
                return;
            }
            if (!boundaryAlignMovable) {
                if (hit) setBoundaryAlignMovable({ bfId: hit.bfId, callId: hit.callId });
                return;
            }
            if (!boundaryAlignTarget) {
                if (!hit || hit.bfId === boundaryAlignMovable.bfId) return; // must be a DIFFERENT bf
                setBoundaryAlignTarget({ bfId: hit.bfId, callId: hit.callId });
                setBoundaryAlignFlipped(false);
                // Snap movable so the picked line is collinear with the target line.
                const aligned = computeBoundaryAlignSnap(boundaryAlignMovable, { bfId: hit.bfId, callId: hit.callId }, false);
                if (aligned && props.onUpdateBoundaryTransform) {
                    props.onUpdateBoundaryTransform(boundaryAlignMovable.bfId, {
                        translationE: aligned.txE,
                        translationN: aligned.txN,
                        rotationDeg: aligned.rotDeg,
                    });
                    // Enter slide mode — mouse projection onto target line
                    // becomes the slide parameter. Click anywhere to commit.
                    boundaryAlignSlideRef.current = {
                        movableBfId: boundaryAlignMovable.bfId,
                        rotDegFixed: aligned.rotDeg,
                        baseTxE: aligned.txE,
                        baseTxN: aligned.txN,
                        dirE: aligned.dirE,
                        dirN: aligned.dirN,
                        targetLen: aligned.targetLen,
                        movableLen: aligned.movableLen,
                        mouseStartProj: worldX * aligned.dirE + worldY * aligned.dirN,
                        sMin: -1e9,
                        sMax:  1e9,
                    };
                    setBoundaryAlignSliding(true);
                }
                return;
            }
            return;
        }

        // Shrinkwrap point removal — click nearest source point to exclude it
        if (props.shrinkwrapSourcePns?.length && props.onShrinkwrapPointClick && drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const HIT = 14;
                let nearest: string | null = null;
                let bestDist = HIT * HIT;
                for (const p of points) {
                    if (!props.shrinkwrapSourcePns.includes(p.pointNumber)) continue;
                    const pt = worldToScreen(p.easting, p.northing);
                    const sx = pt.x;
                    const sy = pt.y;
                    const d = (mx - sx) ** 2 + (my - sy) ** 2;
                    if (d < bestDist) { bestDist = d; nearest = p.pointNumber; }
                }
                if (nearest) {
                    props.onShrinkwrapPointClick(nearest);
                    hoveredShrinkwrapPnRef.current = null;
                    setHoveredShrinkwrapPn(null);
                    return;
                }
            }
        }

        // General point selection — fires whenever a point is clicked in normal mode.
        // Does NOT consume the event (no return), so panning still works when clicking empty space.
        if (props.onPointSelected && !isSelectionMode && !pendingSelectionCommand && drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const HIT = 16;
                let nearest: SurveyPoint | null = null;
                let bestDist = HIT * HIT;
                for (const p of points) {
                    const pt = worldToScreen(p.easting, p.northing);
                    const sx = pt.x;
                    const sy = pt.y;
                    const d = (mx - sx) ** 2 + (my - sy) ** 2;
                    if (d < bestDist) { bestDist = d; nearest = p; }
                }
                if (nearest) {
                    props.onPointSelected(nearest);
                    // Don't return — allow normal canvas behavior to continue
                }
            }
        }

        // Dimension Grip selection and manipulation
        if (selectedDimensionId && !pendingSelectionCommand && drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const clickY = e.clientY - rect.top;
                const clickedDimGrip = findDimensionGripNearClick(clickX, clickY, 12);
                if (clickedDimGrip) {
                    setActiveDimGrip(clickedDimGrip);
                    activeDimGripDragStartRef.current = { clientX: e.clientX, clientY: e.clientY };
                    return;
                }
            }
        }

        // Direct dimension selection in normal mode
        if (!isSelectionMode && drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const clickY = e.clientY - rect.top;
                const clickedDim = findDimensionNearClick(clickX, clickY, 18);
                if (clickedDim) {
                    setSelectedDimensionId(prev => (prev === clickedDim.id ? null : clickedDim.id));
                    return;
                } else if (selectedDimensionId) {
                    setSelectedDimensionId(null);
                }
            }
        }

        // Grip selection and stretch manipulation
        if (selectedLineIds.size > 0 && !pendingSelectionCommand && drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const clickX = e.clientX - rect.left;
                const clickY = e.clientY - rect.top;
                const worldCoords = screenToWorld(clickX, clickY);

                if (activeGrip) {
                    commitActiveGrip(activeGrip, worldCoords.easting, worldCoords.northing, activeOsnapPoint);
                    return;
                }

                const clickedGrip = findGripNearClick(clickX, clickY, 10);
                if (clickedGrip) {
                    setActiveGrip(clickedGrip);
                    activeGripDragStartRef.current = { clientX: e.clientX, clientY: e.clientY };
                    return;
                }
            }
        }

        // Line selection mode: dedicated selection tool, or a command that is
        // currently waiting for picks. Keeping this independent of the toolbar
        // toggle means an armed command can never lose its click-to-select.
        if ((isSelectionMode || pendingSelectionCommand) && drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            const clickedDim = findDimensionNearClick(clickX, clickY, 18);
            if (clickedDim && !isBoxSelectMode) {
                setSelectedDimensionId(prev => (prev === clickedDim.id ? null : clickedDim.id));
                setSelectedLineIds(new Set());
                setSelectedPointNumbers(new Set());
                setSelectedCenterlineIds(new Set());
                return;
            }

            // Survey points are small; pick them before linework so a shot that
            // sits on a line endpoint is still selectable in Select mode.
            // Point / CL picks belong to the SELECT tool, not to a command that is
            // still gathering linework (MIRROR, MOVE, ERASE …).
            const allowScenePicks = !pendingSelectionCommand && !isBoxSelectMode;
            const clickedPoint = allowScenePicks ? findNearestSurveyPoint(clickX, clickY, 10, true) : null;
            if (clickedPoint) {
                const pickedPn = clickedPoint.pointNumber;
                setSelectedPointNumbers(prev => {
                    const next = new Set(prev);
                    if (next.has(pickedPn)) next.delete(pickedPn);
                    else next.add(pickedPn);
                    return next;
                });
                return;
            }

            const clickedLine = findLineNearClick(clickX, clickY, 15);
            if (clickedLine && !isBoxSelectMode) {
                // If line belongs to a polyline group, select/deselect all segments of the polyline together
                const targetPolylineId = clickedLine.polylineId;
                const memberLineIds: string[] = [];
                if (targetPolylineId) {
                    lines.forEach(l => {
                        if (l.polylineId === targetPolylineId && !l.hidden) {
                            memberLineIds.push(l.id || `${l.from}-${l.to}`);
                        }
                    });
                } else {
                    memberLineIds.push(clickedLine.id || `${clickedLine.from}-${clickedLine.to}`);
                }

                setSelectedLineIds(prev => {
                    const next = new Set(prev);
                    const allSelected = memberLineIds.every(id => next.has(id));
                    if (allSelected) {
                        memberLineIds.forEach(id => next.delete(id));
                    } else {
                        memberLineIds.forEach(id => next.add(id));
                    }
                    return next;
                });
                return;
            }

            // Centerline alignment (Stationing & CL) — pick the whole alignment.
            const clickedCenterline = allowScenePicks ? findCenterlineNearClick(clickX, clickY, 12) : null;
            if (clickedCenterline) {
                const clId = clickedCenterline.id;
                setSelectedCenterlineIds(prev => {
                    const next = new Set(prev);
                    if (next.has(clId)) next.delete(clId);
                    else next.add(clId);
                    return next;
                });
                return;
            }
            // Click on empty space (or in box mode) — initiate AutoCAD-style window/crossing selection box
            setShiftBoxSelect({ startX: clickX, startY: clickY, endX: clickX, endY: clickY });
            return;
        }

        // Handle line deletion mode
        if (isDeletingLines) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            
            const clickedDim = findDimensionNearClick(clickX, clickY, 18);
            if (clickedDim) {
                onDeleteDimension?.(clickedDim.id);
                if (selectedDimensionId === clickedDim.id) setSelectedDimensionId(null);
                addNotification({
                    kind: 'dimension',
                    severity: 'info',
                    title: 'Dimension Deleted',
                });
                return;
            }

            const clickedLine = findLineNearClick(clickX, clickY, 15);
            if (clickedLine?.id) {
                // If member of a polyline, delete all constituent segments together
                const targetPolylineId = clickedLine.polylineId;
                if (targetPolylineId) {
                    lines.forEach(l => {
                        if (l.polylineId === targetPolylineId && l.id) {
                            onDeleteLine(l.id);
                        }
                    });
                } else {
                    onDeleteLine(clickedLine.id);
                }
            }
            return;
        }
        
        // Handle line trimming mode - delete line or trim at intersections
        if (isTrimmingLines) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            
            // Convert screen to world coordinates
            const worldCoords = screenToWorld(clickX, clickY);
            const worldX = worldCoords.easting;
            const worldY = worldCoords.northing;
            
            // Find the line clicked
            const clickedLine = findLineNearClick(clickX, clickY, 15);
            if (!clickedLine) return;
            
            const action = computeTrimAction(clickedLine, worldX, worldY);
            if (action) {
                onDeleteLine(action.lineId);
                action.newLines.forEach(nl => onAddLine(nl));
                setTrimHoverPreview(null);
                requestAnimationFrame(() => {
                    drawRef.current?.();
                });
            }
            return;
        }
        
        // Handle line extending mode - click line to select, click target to extend
        if (isExtendingLines) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            
            // Convert screen to world coordinates
            const worldCoords = screenToWorld(clickX, clickY);
            const worldX = worldCoords.easting;
            const worldY = worldCoords.northing;
            
            if (!extendFromLine) {
                // First click: select the line to extend
                const clickedLine = findLineNearClick(clickX, clickY, 15);
                if (clickedLine) {
                    const lineId = clickedLine.id || `${clickedLine.from}-${clickedLine.to}`;
                    setExtendFromLine(lineId);
                    console.log('Extend mode: Selected line', lineId);
                }
            } else {
                // Second click: extend to another line or point
                const targetLine = findLineNearClick(clickX, clickY, 15);
                const sourceLine = lines.find(l => (l.id || `${l.from}-${l.to}`) === extendFromLine);
                
                if (!sourceLine) {
                    setExtendFromLine(null);
                    return;
                }
                
                const p1 = pointMap.get(sourceLine.from);
                const p2 = pointMap.get(sourceLine.to);
                const p1Coords = p1 ? { x: p1.easting, y: p1.northing } : (sourceLine.fromPt ? { x: sourceLine.fromPt.x, y: sourceLine.fromPt.y } : null);
                const p2Coords = p2 ? { x: p2.easting, y: p2.northing } : (sourceLine.toPt ? { x: sourceLine.toPt.x, y: sourceLine.toPt.y } : null);
                
                if (!p1Coords || !p2Coords) {
                    setExtendFromLine(null);
                    return;
                }
                
                // Determine which end of the line is closer to the click
                const dist1 = Math.sqrt((worldX - p1Coords.x) ** 2 + (worldY - p1Coords.y) ** 2);
                const dist2 = Math.sqrt((worldX - p2Coords.x) ** 2 + (worldY - p2Coords.y) ** 2);
                const extendFromEnd = dist1 < dist2 ? 'start' : 'end';
                
                let newEndPoint: { x: number; y: number; z: number };
                
                if (targetLine) {
                    // Extend to intersection with target line
                    const tp1 = pointMap.get(targetLine.from);
                    const tp2 = pointMap.get(targetLine.to);
                    const tp1Coords = tp1 ? { x: tp1.easting, y: tp1.northing } : (targetLine.fromPt ? { x: targetLine.fromPt.x, y: targetLine.fromPt.y } : null);
                    const tp2Coords = tp2 ? { x: tp2.easting, y: tp2.northing } : (targetLine.toPt ? { x: targetLine.toPt.x, y: targetLine.toPt.y } : null);
                    
                    if (!tp1Coords || !tp2Coords) {
                        setExtendFromLine(null);
                        return;
                    }
                    
                    // Calculate line intersection
                    const denom = (p1Coords.x - p2Coords.x) * (tp1Coords.y - tp2Coords.y) - (p1Coords.y - p2Coords.y) * (tp1Coords.x - tp2Coords.x);
                    if (Math.abs(denom) < 1e-10) {
                        console.log('Extend mode: Lines are parallel, cannot extend');
                        setExtendFromLine(null);
                        return;
                    }
                    
                    const t = ((p1Coords.x - tp1Coords.x) * (tp1Coords.y - tp2Coords.y) - (p1Coords.y - tp1Coords.y) * (tp1Coords.x - tp2Coords.x)) / denom;
                    const intX = p1Coords.x + t * (p2Coords.x - p1Coords.x);
                    const intY = p1Coords.y + t * (p2Coords.y - p1Coords.y);
                    
                    newEndPoint = { x: intX, y: intY, z: 0 };
                } else {
                    // Extend to clicked point
                    newEndPoint = { x: worldX, y: worldY, z: 0 };
                }
                
                // Delete the original line
                onDeleteLine(extendFromLine);
                
                // Create extended line
                const newLine: SurveyLine = {
                    from: extendFromEnd === 'start' ? `EXT_${Date.now()}` : sourceLine.from,
                    to: extendFromEnd === 'end' ? `EXT_${Date.now()}` : sourceLine.to,
                    type: sourceLine.type,
                    fromPt: extendFromEnd === 'start' ? newEndPoint : (sourceLine.fromPt || { x: p1Coords.x, y: p1Coords.y, z: 0 }),
                    toPt: extendFromEnd === 'end' ? newEndPoint : (sourceLine.toPt || { x: p2Coords.x, y: p2Coords.y, z: 0 })
                };
                
                onAddLine(newLine);
                console.log('Extend mode: Extended line to new point');
                
                // Reset for next extend operation
                setExtendFromLine(null);
            }
            
            return;
        }
        
        if (e.ctrlKey || e.metaKey) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            // Prioritize owner name over deed metadata if both are visible
            if (isOverOwnerNameArea(clickX, clickY)) {
                document.body.classList.add('label-dragging');
                setIsLabelDragging(true);

                const offsetX = props.ownerNameOffset?.x || 0;
                const offsetY = props.ownerNameOffset?.y || 10;
                
                ownerNameDragTarget.current = {
                    startX: e.clientX,
                    startY: e.clientY,
                    startOffsetX: offsetX,
                    startOffsetY: offsetY,
                };
                return;
            }

            // Check deed metadata next (if visible)
            if (isOverDeedMetadataArea(clickX, clickY)) {
                document.body.classList.add('label-dragging');
                setIsLabelDragging(true);

                const offsetX = props.deedMetadataOffset?.x || 0;
                const offsetY = props.deedMetadataOffset?.y || 0;
                
                deedMetadataDragTarget.current = {
                    startX: e.clientX,
                    startY: e.clientY,
                    startOffsetX: offsetX,
                    startOffsetY: offsetY,
                };
                return;
            }

            // Check line labels
            for (const line of [...lines].reverse()) {
                if (line.bearing && line.distance && !line.isCurve && isOverLineLabelArea(clickX, clickY, line)) {
                    document.body.classList.add('label-dragging');
                    setIsLabelDragging(true);

                    lineLabelDragTarget.current = {
                        lineId: line.id || `${line.from}-${line.to}`,
                        startX: e.clientX,
                        startY: e.clientY,
                        startOffsetX: line.labelOffset?.x || 0,
                        startOffsetY: line.labelOffset?.y || 0,
                    };
                    return;
                }
            }

            // Check curve labels (R / L / Δ stack) — same drag mechanism,
            // shares the labelOffset field.
            for (const line of [...lines].reverse()) {
                if (line.isCurve && isOverCurveLabelArea(clickX, clickY, line)) {
                    document.body.classList.add('label-dragging');
                    setIsLabelDragging(true);
                    lineLabelDragTarget.current = {
                        lineId: line.id || `${line.from}-${line.to}`,
                        startX: e.clientX,
                        startY: e.clientY,
                        startOffsetX: line.labelOffset?.x || 0,
                        startOffsetY: line.labelOffset?.y || 0,
                    };
                    return;
                }
            }

            // Check point labels
            for (const p of [...points].reverse()) {
                if (isOverLabelArea(clickX, clickY, p)) {
                    document.body.classList.add('label-dragging');
                    setIsLabelDragging(true);

                    // If no label offset exists, use default in world coordinates
                    const defaultWorldOffset = (5 * attributeScale) / transform.scale;
                    labelDragTarget.current = {
                        pointNumber: p.pointNumber,
                        startX: e.clientX,
                        startY: e.clientY,
                        startOffsetX: p.labelOffset?.x || defaultWorldOffset,
                        startOffsetY: p.labelOffset?.y || defaultWorldOffset,
                    };
                    return;
                }
            }

            // Check annotation notes (Ctrl+drag to reposition leader/note).
            if (onUpdatePointAnnotationOffset) {
                for (const p of [...points].reverse()) {
                    if (isOverAnnotationArea(clickX, clickY, p)) {
                        const rule = getAnnotationForPoint(p);
                        if (!rule) continue;
                        const eff = p.annotationOffset || { dx: rule.leaderOffset.dx, dy: rule.leaderOffset.dy };
                        document.body.classList.add('label-dragging');
                        setIsLabelDragging(true);
                        annotationDragTarget.current = {
                            pointNumber: p.pointNumber,
                            startX: e.clientX,
                            startY: e.clientY,
                            startDx: eff.dx,
                            startDy: eff.dy,
                        };
                        return;
                    }
                }
            }
        }

        if (e.altKey) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            // Check line labels for rotation
            for (const line of [...lines].reverse()) {
                if (line.bearing && line.distance && !line.isCurve && isOverLineLabelArea(clickX, clickY, line)) {
                    const lineId = line.id || `${line.from}-${line.to}`;
                    if (props.onToggleLineLabelRotation) {
                        props.onToggleLineLabelRotation(lineId);
                    }
                    return;
                }
            }
            // Check boundary call labels for rotation
            if (props.boundaryFiles && props.onToggleBoundaryCallLabelRotation) {
                for (const bf of [...(props.boundaryFiles)].reverse()) {
                    for (const call of [...bf.calls].reverse()) {
                        if (isOverBoundaryCallLabelArea(clickX, clickY, call)) {
                            props.onToggleBoundaryCallLabelRotation(bf.id, call.id);
                            return;
                        }
                    }
                }
            }
        }

        // Shift+Click to swap bearing direction
        if (e.shiftKey) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            // Check annotation notes first — Shift+click mirrors dx left↔right.
            if (onUpdatePointAnnotationOffset) {
                for (const p of [...points].reverse()) {
                    if (isOverAnnotationArea(clickX, clickY, p)) {
                        const rule = getAnnotationForPoint(p);
                        if (!rule) continue;
                        const eff = p.annotationOffset || { dx: rule.leaderOffset.dx, dy: rule.leaderOffset.dy };
                        onUpdatePointAnnotationOffset(p.pointNumber, { dx: -eff.dx, dy: eff.dy });
                        return;
                    }
                }
            }

            // Check line labels for direction swap
            for (const line of [...lines].reverse()) {
                if (line.bearing && line.distance && !line.isCurve && isOverLineLabelArea(clickX, clickY, line)) {
                    const lineId = line.id || `${line.from}-${line.to}`;
                    if (props.onSwapLineBearingDirection) {
                        props.onSwapLineBearingDirection(lineId);
                    }
                    return;
                }
            }
            // Check boundary call labels for bearing swap
            if (props.boundaryFiles && props.onSwapBoundaryCallBearingDirection) {
                for (const bf of [...(props.boundaryFiles)].reverse()) {
                    for (const call of [...bf.calls].reverse()) {
                        if (isOverBoundaryCallLabelArea(clickX, clickY, call)) {
                            props.onSwapBoundaryCallBearingDirection(bf.id, call.id);
                            return;
                        }
                    }
                }
            }

            // Shift+drag selection window (CAD style):
            // left->right = window (fully inside), right->left = crossing.
            if (drawingMode === 'none' && !isDeletingLines && !isTrimmingLines && !isExtendingLines && !boundaryAlignSliding) {
                setShiftBoxSelect({ startX: clickX, startY: clickY, endX: clickX, endY: clickY });
                return;
            }
        }

        if (drawingMode === 'polylines' || drawingMode === 'breaklines' || drawingMode === 'inclusion' || drawingMode === 'exclusion') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const worldCoords = screenToWorld(clickX, clickY);
            const worldEasting = worldCoords.easting;
            const worldNorthing = worldCoords.northing;
            const clickThreshold = 15; // pixels
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const anchor = polylinePoints.length > 0
                ? { easting: polylinePoints[polylinePoints.length - 1].easting, northing: polylinePoints[polylinePoints.length - 1].northing }
                : null;
            const snapped = resolveOsnapAtScreen(clickX, clickY, activeModes, clickThreshold, anchor);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            const nearestExisting = findNearestSurveyPoint(clickX, clickY, clickThreshold);

            let nextPoint: SurveyPoint | null = null;
            if (typedSegmentLength.trim().length > 0 && anchor) {
                const num = Number(typedSegmentLength);
                if (Number.isFinite(num) && num > 0) {
                    const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                    const worldLength = convertLinearUnits(num, fromUnit, 'foot');
                    let target = snapped
                        ? { easting: snapped.easting, northing: snapped.northing }
                        : { easting: worldEasting, northing: worldNorthing };
                    if (isOrthoEnabled) {
                        target = applyOrthoConstraint(anchor, target);
                    }
                    let dx = target.easting - anchor.easting;
                    let dy = target.northing - anchor.northing;
                    const mag = Math.hypot(dx, dy);
                    const dirX = mag > 1e-9 ? dx / mag : 1;
                    const dirY = mag > 1e-9 ? dy / mag : 0;
                    nextPoint = makeTempWorldPoint(anchor.easting + dirX * worldLength, anchor.northing + dirY * worldLength);
                }
            }
            if (!nextPoint) {
                if (snapped?.sourcePoint) {
                    nextPoint = snapped.sourcePoint;
                } else if (snapped) {
                    nextPoint = makeTempSnapPoint(snapped);
                } else if (nearestExisting) {
                    nextPoint = nearestExisting;
                } else {
                    let freePoint = { easting: worldEasting, northing: worldNorthing };
                    if (isOrthoEnabled && anchor) {
                        freePoint = applyOrthoConstraint(anchor, freePoint);
                    }
                    nextPoint = makeTempWorldPoint(freePoint.easting, freePoint.northing);
                }
            }

            if (nextPoint) {
                if (typedSegmentLength.length > 0) setTypedSegmentLength('');
                setPolylinePoints(prev => [...prev, nextPoint]);
            }
            return;
        }

        if (drawingMode === 'polygon') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
            const anchor = polygonCenter;
            const snapped = currentSnap || resolveOsnapAtScreen(clickX, clickY, activeModes, 20, anchor);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            const nearestExisting = findNearestSurveyPoint(clickX, clickY, 20);
            let pickedPt = { easting: 0, northing: 0 };
            if (snapped) {
                pickedPt = { easting: snapped.easting, northing: snapped.northing };
            } else if (nearestExisting) {
                pickedPt = { easting: nearestExisting.easting, northing: nearestExisting.northing };
            } else {
                const worldCoords = screenToWorld(clickX, clickY);
                pickedPt = { easting: worldCoords.easting, northing: worldCoords.northing };
            }

            if (!polygonCenter) {
                setPolygonCenter(pickedPt);
                setTerminalIncoming({
                    text: `POLYGON: Center E ${pickedPt.easting.toFixed(2)}, N ${pickedPt.northing.toFixed(2)} — drag, click, or type the diameter [${polygonSides} sides; scroll or [ ] changes sides, Esc cancels]`,
                    tone: 'ok',
                });
                requestAnimationFrame(() => drawRef.current?.());
                return;
            }

            let radiusInput: number | null = null;
            if (typedPolygonDiameter.trim().length > 0) {
                const num = Number(typedPolygonDiameter);
                if (Number.isFinite(num) && num > 0) radiusInput = num / 2;
            }
            if (radiusInput === null) {
                const worldRadius = Math.hypot(pickedPt.easting - polygonCenter.easting, pickedPt.northing - polygonCenter.northing);
                radiusInput = convertLinearUnits(worldRadius, 'foot', settings.linearUnits || 'usSurveyFoot');
            }
            commitPolygonFromRadius(radiusInput);
            return;
        }

        if (drawingMode === 'circle') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            if (circleSubMode === 'ttr') {
                if (ttrStep === 1) {
                    const ent = ttrHoverEntity || findTtrEntityNearClick(clickX, clickY, 25);
                    if (ent) {
                        setTtrEntity1(ent);
                        setTtrStep(2);
                        setTtrHoverEntity(null);
                    }
                    return;
                } else if (ttrStep === 2) {
                    const ent = ttrHoverEntity || findTtrEntityNearClick(clickX, clickY, 25);
                    if (ent) {
                        setTtrEntity2(ent);
                        setTtrStep(3);
                        setTtrHoverEntity(null);
                        setTypedCircleValue('');
                    }
                    return;
                } else if (ttrStep === 3 && ttrEntity1 && ttrEntity2) {
                    let radiusVal = 25;
                    if (typedCircleValue.trim().length > 0) {
                        const num = Number(typedCircleValue);
                        if (Number.isFinite(num) && num > 0) {
                            const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                            radiusVal = convertLinearUnits(num, fromUnit, 'foot');
                        }
                    } else {
                        const worldCoords = screenToWorld(clickX, clickY);
                        radiusVal = Math.hypot(worldCoords.easting - ttrEntity2.pickPoint.x, worldCoords.northing - ttrEntity2.pickPoint.y);
                    }
                    if (circleSizeParam === 'diameter') radiusVal = radiusVal / 2;

                    const sol = solveTtrCircle(ttrEntity1, ttrEntity2, radiusVal);
                    if (sol) {
                        addCircleGeometry(sol.center, sol.radius);
                        setCircleSubMode('center-radius');
                        setTtrStep(1);
                        setTtrEntity1(null);
                        setTtrEntity2(null);
                        setTypedCircleValue('');
                        setDrawingMode('none');
                    }
                    return;
                }
            }

            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
            const anchor = circleCenter;
            const snapped = currentSnap || resolveOsnapAtScreen(clickX, clickY, activeModes, 20, anchor);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            const nearestExisting = findNearestSurveyPoint(clickX, clickY, 20);

            let pickedPt = { easting: 0, northing: 0 };
            if (snapped) {
                pickedPt = { easting: snapped.easting, northing: snapped.northing };
            } else if (nearestExisting) {
                pickedPt = { easting: nearestExisting.easting, northing: nearestExisting.northing };
            } else {
                const worldCoords = screenToWorld(clickX, clickY);
                pickedPt = { easting: worldCoords.easting, northing: worldCoords.northing };
            }

            if (!circleCenter) {
                setCircleCenter(pickedPt);
                setTypedCircleValue('');
            } else {
                let radiusVal = 0;
                if (typedCircleValue.trim().length > 0) {
                    const num = Number(typedCircleValue);
                    if (Number.isFinite(num) && num > 0) {
                        const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                        const worldVal = convertLinearUnits(num, fromUnit, 'foot');
                        radiusVal = circleSizeParam === 'diameter' ? worldVal / 2 : worldVal;
                    }
                } else {
                    const dist = Math.hypot(pickedPt.easting - circleCenter.easting, pickedPt.northing - circleCenter.northing);
                    radiusVal = circleSizeParam === 'diameter' ? dist / 2 : dist;
                }

                if (radiusVal > 1e-6) {
                    addCircleGeometry(circleCenter, radiusVal);
                }
                setCircleCenter(null);
                setTypedCircleValue('');
                setDrawingMode('none');
            }
            return;
        }

        if (drawingMode === 'add-point') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const worldCoords = screenToWorld(clickX, clickY);
            const worldEasting = worldCoords.easting;
            const worldNorthing = worldCoords.northing;
            const snapThreshold = 20;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const snapped = resolveOsnapAtScreen(clickX, clickY, activeModes, snapThreshold, null);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);

            const ptNorthing = snapped ? snapped.northing : worldNorthing;
            const ptEasting = snapped ? snapped.easting : worldEasting;

            let ptNum = addPointNumber.trim();
            // CACP verification: check what is available and prevent duplicates
            if (!ptNum || isPointNumberTaken(ptNum)) {
                const cacpNext = queryCacpNextPointNumber();
                if (ptNum && isPointNumberTaken(ptNum)) {
                    addNotification({
                        kind: 'point-add',
                        severity: 'warning',
                        title: 'Point Exists',
                        message: `Point ${ptNum} already exists. Using next available CACP point: ${cacpNext}`,
                    });
                }
                ptNum = cacpNext;
            }

            const newPt: SurveyPoint = {
                pointNumber: ptNum,
                northing: Math.round(ptNorthing * 1000) / 1000,
                easting: Math.round(ptEasting * 1000) / 1000,
                elevation: parseFloat(addPointElevation) || 0,
                description: addPointDescription.trim(),
            };
            onAddPoint?.(newPt);

            const nextNum = queryCacpNextPointNumber(new Set([ptNum]));
            setAddPointNumber(nextNum);
            setAddPointNorthing(newPt.northing.toFixed(3));
            setAddPointEasting(newPt.easting.toFixed(3));
            addNotification({
                kind: 'point-add',
                severity: 'success',
                title: 'Point Added',
                message: `Added Point ${ptNum} (N: ${newPt.northing.toFixed(2)}, E: ${newPt.easting.toFixed(2)})`,
            });
            return;
        }

        if (isDimensionDrawingMode(drawingMode)) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;
            const worldCoords = screenToWorld(clickX, clickY);
            const worldEasting = worldCoords.easting;
            const worldNorthing = worldCoords.northing;
            const snapThreshold = 20;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const anchor = dimPhase > 0 && dimP1 ? { easting: dimP1.easting, northing: dimP1.northing } : null;
            const snapped = resolveOsnapAtScreen(clickX, clickY, activeModes, snapThreshold, anchor);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            const worldPt = snapped ? { easting: snapped.easting, northing: snapped.northing } : { easting: worldEasting, northing: worldNorthing };

            if (drawingMode === 'aligned-dim') {
                if (dimPhase === 0) {
                    setDimP1(worldPt);
                    setDimPhase(1);
                } else if (dimPhase === 1) {
                    setDimP2(worldPt);
                    setDimPhase(2);
                } else if (dimPhase === 2 && dimP1 && dimP2) {
                    const dxLine = dimP2.easting - dimP1.easting;
                    const dyLine = dimP2.northing - dimP1.northing;
                    const lineLen = Math.hypot(dxLine, dyLine);
                    if (lineLen > 1e-9) {
                        const pxPerp = -dyLine / lineLen;
                        const pyPerp = dxLine / lineLen;
                        const offsetDist = (worldPt.easting - dimP1.easting) * pxPerp + (worldPt.northing - dimP1.northing) * pyPerp;
                        const newDim: AlignedAnnotationDimension = {
                            id: `dim-${Date.now()}`,
                            type: 'aligned',
                            p1: dimP1,
                            p2: dimP2,
                            offsetDist,
                        };
                        onAddDimension(newDim);
                    }
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'horiz-dim') {
                if (dimPhase === 0) {
                    setDimP1(worldPt);
                    setDimPhase(1);
                } else if (dimPhase === 1) {
                    setDimP2(worldPt);
                    setDimPhase(2);
                } else if (dimPhase === 2 && dimP1 && dimP2) {
                    const offsetDist = worldPt.northing - dimP1.northing;
                    const newDim: HorizontalAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'horizontal',
                        p1: dimP1,
                        p2: dimP2,
                        offsetDist,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'vert-dim') {
                if (dimPhase === 0) {
                    setDimP1(worldPt);
                    setDimPhase(1);
                } else if (dimPhase === 1) {
                    setDimP2(worldPt);
                    setDimPhase(2);
                } else if (dimPhase === 2 && dimP1 && dimP2) {
                    const offsetDist = worldPt.easting - dimP1.easting;
                    const newDim: VerticalAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'vertical',
                        p1: dimP1,
                        p2: dimP2,
                        offsetDist,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'radius-dim') {
                if (dimPhase === 0) {
                    const curveTarget = resolveCurveTargetFromClick(clickX, clickY);
                    if (curveTarget) {
                        setDimSelectedCurve(curveTarget);
                        setDimPhase(1);
                    } else {
                        notifyBoundaryError('Click on an arc or circle to dimension radius.');
                    }
                } else if (dimPhase === 1 && dimSelectedCurve) {
                    const newDim: RadiusAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'radius',
                        center: dimSelectedCurve.center,
                        radius: dimSelectedCurve.radius,
                        textPosition: worldPt,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'diameter-dim') {
                if (dimPhase === 0) {
                    const curveTarget = resolveCurveTargetFromClick(clickX, clickY);
                    if (curveTarget) {
                        setDimSelectedCurve(curveTarget);
                        setDimPhase(1);
                    } else {
                        notifyBoundaryError('Click on an arc or circle to dimension diameter.');
                    }
                } else if (dimPhase === 1 && dimSelectedCurve) {
                    const newDim: DiameterAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'diameter',
                        center: dimSelectedCurve.center,
                        radius: dimSelectedCurve.radius,
                        textPosition: worldPt,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'arc-length-dim') {
                if (dimPhase === 0) {
                    const curveTarget = resolveCurveTargetFromClick(clickX, clickY);
                    if (curveTarget) {
                        setDimSelectedCurve(curveTarget);
                        setDimPhase(1);
                    } else {
                        notifyBoundaryError('Click on an arc to dimension arc length.');
                    }
                } else if (dimPhase === 1 && dimSelectedCurve) {
                    const distFromCenter = Math.hypot(worldPt.easting - dimSelectedCurve.center.easting, worldPt.northing - dimSelectedCurve.center.northing);
                    const offsetDist = distFromCenter - dimSelectedCurve.radius;
                    const newDim: ArcLengthAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'arc-length',
                        center: dimSelectedCurve.center,
                        radius: dimSelectedCurve.radius,
                        p1: dimSelectedCurve.p1,
                        p2: dimSelectedCurve.p2,
                        arcLength: dimSelectedCurve.arcLength,
                        offsetDist,
                        isLeftCurve: dimSelectedCurve.isLeftCurve,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'curve-data-dim') {
                if (dimPhase === 0) {
                    const curveTarget = resolveCurveTargetFromClick(clickX, clickY);
                    if (curveTarget) {
                        setDimSelectedCurve(curveTarget);
                        setDimPhase(1);
                    } else {
                        notifyBoundaryError('Click on an arc or curve to place Curve Data.');
                    }
                } else if (dimPhase === 1 && dimSelectedCurve) {
                    const newDim: CurveDataAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'curve-data',
                        center: dimSelectedCurve.center,
                        radius: dimSelectedCurve.radius,
                        arcLength: dimSelectedCurve.arcLength,
                        deltaRad: dimSelectedCurve.deltaRad,
                        chordBearing: dimSelectedCurve.chordBearing,
                        chordLength: dimSelectedCurve.chordLength,
                        tangentBearing: dimSelectedCurve.tangentBearing,
                        curveDirection: dimSelectedCurve.isLeftCurve ? 'left' : 'right',
                        p1: dimSelectedCurve.p1,
                        p2: dimSelectedCurve.p2,
                        textPosition: worldPt,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'angular-dim') {
                if (dimPhase === 0) {
                    setDimP1(worldPt);
                    setDimPhase(1);
                } else if (dimPhase === 1) {
                    setDimP2(worldPt);
                    setDimPhase(2);
                } else if (dimPhase === 2 && dimP1 && dimP2) {
                    const arcRadius = Math.hypot(worldPt.easting - dimP1.easting, worldPt.northing - dimP1.northing);
                    const newDim: AngularAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'angular',
                        vertex: dimP1,
                        p1: dimP2,
                        p2: worldPt,
                        arcRadius: Math.max(0.1, arcRadius),
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }

            if (drawingMode === 'ordinate-dim') {
                if (dimPhase === 0) {
                    setDimP1(worldPt);
                    setDimPhase(1);
                } else if (dimPhase === 1 && dimP1) {
                    const newDim: OrdinateAnnotationDimension = {
                        id: `dim-${Date.now()}`,
                        type: 'ordinate',
                        point: dimP1,
                        leaderEnd: worldPt,
                    };
                    onAddDimension(newDim);
                    resetDimMode();
                }
                return;
            }
            return;
        }

        if (drawingMode === 'boundary-line') {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const clickX = e.clientX - rect.left;
            const clickY = e.clientY - rect.top;

            // Convert canvas pixel → world coords
            const worldCoords = screenToWorld(clickX, clickY);
            const worldEasting = worldCoords.easting;
            const worldNorthing = worldCoords.northing;
            const clickThreshold = 20;
            const activeModes = inlineOsnapOverride ? new Set<OsnapMode>([inlineOsnapOverride]) : runningOsnaps;
            const anchor = boundaryLineFrom ? { easting: boundaryLineFrom.easting, northing: boundaryLineFrom.northing } : null;
            const snapped = resolveOsnapAtScreen(clickX, clickY, activeModes, clickThreshold, anchor);
            if (inlineOsnapOverride) setInlineOsnapOverride(null);
            const nearestPoint = snapped?.sourcePoint ?? findNearestSurveyPoint(clickX, clickY, clickThreshold, true);

            if (!boundaryLineFrom) {
                // First click: must snap to an existing point
                if (nearestPoint) {
                    setBoundaryLineFrom(nearestPoint);
                    setBoundaryClickedTo(null);
                    setBoundaryBearing('');
                    setBoundaryDistance('');
                    setBoundaryInputError('');
                } else {
                    notifyBoundaryError('No endpoint found — start must snap to an existing point.');
                }
            } else {
                // Second click: use selected osnap target or raw world coords
                const toEasting = snapped ? snapped.easting : worldEasting;
                const toNorthing = snapped ? snapped.northing : worldNorthing;
                const toCoord = { easting: toEasting, northing: toNorthing };
                const inv = inverse(boundaryLineFrom, toCoord);
                if (inv.distance < 0.001) {
                    notifyBoundaryError('Second click is too close to the start point.');
                    return;
                }
                const bearingStr = formatBearing(inv.bearing);
                const distStr = inv.distance.toFixed(2);
                setBoundaryClickedTo(toCoord);
                setBoundaryBearing(bearingStr);
                setBoundaryDistance(distStr);
                setBoundaryInputError('');
                setTimeout(() => boundaryBearingRef.current?.focus(), 50);
            }
            return;
        }

        document.body.classList.add('panning');
        setIsPanning(true);
        lastMousePos.current = { x: e.clientX, y: e.clientY };
    }, [drawingMode, isDeletingLines, isTrimmingLines, isExtendingLines, extendFromLine, boundaryLineFrom, points, lines, pointMap, transform, attributeScale, isOverLabelArea, isOverLineLabelArea, isOverDeedMetadataArea, isOverOwnerNameArea, isOverAnnotationArea, getAnnotationForPoint, onUpdatePointAnnotationOffset, findLineNearClick, onDeleteLine, onAddLine, props, dimPhase, dimP1, dimP2, onAddDimension, isSelectionMode, selectedLineIds, boundaryAlignMovable, boundaryAlignTarget, boundaryAlignSliding, hitTestBoundaryCall, computeBoundaryAlignSnap, cornerAlignBfId, cornerAlignPendingCorner, cornerAlignPicker, hitTestBoundaryCorner, commitCornerAlignPoint, symbolExclusionMode, inlineOsnapMenuPos, inlineOsnapOverride, runningOsnaps, resolveOsnapAtScreen, findNearestSurveyPoint, activeOsnapPoint, makeTempWorldPoint, makeTempSnapPoint, isOrthoEnabled, applyOrthoConstraint, typedSegmentLength, screenToWorld, worldToScreen, isPickingOtOrigin, isPickingOtLineAlign, onSetOrientationTuple, boundaryFiles, getBoundaryCallWorldEndpoints, circleCenter, circleSizeParam, typedCircleValue, circleSubMode, polygonCenter, polygonSides, typedPolygonDiameter, commitPolygonFromRadius, ttrStep, ttrEntity1, ttrEntity2, ttrHoverEntity, findTtrEntityNearClick, solveTtrCircle, computeTrimAction, addCircleGeometry, settings.linearUnits, isDimensionDrawingMode, resolveCurveTargetFromClick, addPointNumber, nextAvailablePointNumber, addPointElevation, addPointDescription, onAddPoint, dimSelectedCurve, resetDimMode, isCopyingSelection, copyBasePoint, duplicateSelectedLines, isOffsetMode, offsetDistance, offsetTargetId, offsetPickStart, clearOffsetPreview, applyOffsetAtPoint, isCircleLine, pendingSelectionCommand, isRotatingSelection, rotatePivot, rotateSelectionCommand, clearRotatePreview, isMirroringSelection, mirrorAxisStart, isMovingSelection, moveBasePoint, resolveSnappedWorld, moveSelectionByWorld, mirrorSelectionCommand, isScalingSelection, scaleBasePoint, selectionRadiusFrom, scaleSelectionCommand, activeCommandPrompt, isPickingPolarCenter]);

    const handleMouseMove = useCallback((e: React.MouseEvent) => {
        if (shiftBoxSelect) {
            const canvas = canvasRef.current;
            if (!canvas) return;
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            setShiftBoxSelect(prev => prev ? { ...prev, endX: mx, endY: my } : prev);
            return;
        }

        // OFFSET ghost preview — mostly redrawn only when the picked side flips.
        if (isOffsetMode && offsetDistance !== null && offsetTargetId) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const world = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                const target = lines.find(l => (l.id || `${l.from}-${l.to}`) === offsetTargetId) ?? null;
                updateOffsetPreview(target, { x: world.easting, y: world.northing });
            }
        } else if (offsetPreviewSegmentsRef.current) {
            clearOffsetPreview();
        }

        // Live transform ghosts for ROTATE / MIRROR / MOVE / COPY.
        const previewCanvas = canvasRef.current;
        if (previewCanvas) {
            const previewRect = previewCanvas.getBoundingClientRect();
            const previewScreenX = e.clientX - previewRect.left;
            const previewScreenY = e.clientY - previewRect.top;
            let previewWorld = screenToWorld(previewScreenX, previewScreenY);
            // Transform picks (ROTATE / MIRROR / MOVE / SCALE / COPY) honour the
            // same running osnaps as drafting, so the crosshair shows a snap
            // marker and the ghost locks to the exact endpoint / intersection.
            if (isRotatingSelection || isMirroringSelection || isMovingSelection || isScalingSelection || isCopyingSelection) {
                const transformModes = inlineOsnapOverride
                    ? new Set<OsnapMode>([inlineOsnapOverride])
                    : (runningOsnaps.size > 0 ? runningOsnaps : new Set<OsnapMode>(['endpoint', 'midpoint', 'intersection', 'nearest']));
                const transformAnchor = mirrorAxisStart ?? moveBasePoint ?? rotatePivot ?? scaleBasePoint ?? copyBasePoint ?? null;
                const snappedTransform = resolveOsnapAtScreen(previewScreenX, previewScreenY, transformModes, 25, transformAnchor);
                const surveyTransform = snappedTransform ? null : findNearestSurveyPoint(previewScreenX, previewScreenY, 25);
                const resolvedTransform: OsnapResult | null = snappedTransform ?? (surveyTransform
                    ? {
                        easting: surveyTransform.easting,
                        northing: surveyTransform.northing,
                        mode: 'endpoint',
                        sourcePoint: surveyTransform,
                    }
                    : null);
                setActiveOsnapPoint(resolvedTransform);
                if (resolvedTransform) {
                    previewWorld = { easting: resolvedTransform.easting, northing: resolvedTransform.northing };
                }
            }
            if (isRotatingSelection && rotatePivot) {
                const dE = previewWorld.easting - rotatePivot.easting;
                const dN = previewWorld.northing - rotatePivot.northing;
                if (Math.hypot(dE, dN) > 1e-9) {
                    const rawDegrees = (Math.atan2(dN, dE) * 180) / Math.PI;
                    updateRotatePreview(e.shiftKey ? Math.round(rawDegrees / 15) * 15 : rawDegrees);
                }
            } else if (isScalingSelection && scaleBasePoint) {
                // Drag preview: factor is the cursor distance over the reference
                // radius, so the ghost is 1× at the selection's outer edge.
                const referenceRadius = scaleReferenceRadiusRef.current;
                const distance = Math.hypot(previewWorld.easting - scaleBasePoint.easting, previewWorld.northing - scaleBasePoint.northing);
                if (referenceRadius && referenceRadius > 1e-9 && distance > 1e-9) {
                    showScalePreview(distance / referenceRadius);
                }
            } else if (isMirroringSelection && mirrorAxisStart) {
                const ax = mirrorAxisStart.easting;
                const ay = mirrorAxisStart.northing;
                const dx = previewWorld.easting - ax;
                const dy = previewWorld.northing - ay;
                const len2 = dx * dx + dy * dy;
                if (len2 > 1e-12) {
                    showTransformPreview(
                        `MI|${ax.toFixed(2)}|${ay.toFixed(2)}|${previewWorld.easting.toFixed(2)}|${previewWorld.northing.toFixed(2)}`,
                        (p) => {
                            const t = ((p.x - ax) * dx + (p.y - ay) * dy) / len2;
                            const projX = ax + t * dx;
                            const projY = ay + t * dy;
                            return { x: 2 * projX - p.x, y: 2 * projY - p.y };
                        },
                    );
                }
            } else if (isMovingSelection && moveBasePoint) {
                const dE = previewWorld.easting - moveBasePoint.easting;
                const dN = previewWorld.northing - moveBasePoint.northing;
                showTransformPreview(`MV|${dE.toFixed(3)}|${dN.toFixed(3)}`, (p) => ({ x: p.x + dE, y: p.y + dN }));
            } else if (isCopyingSelection && copyBasePoint) {
                const dE = previewWorld.easting - copyBasePoint.easting;
                const dN = previewWorld.northing - copyBasePoint.northing;
                showTransformPreview(`CP|${dE.toFixed(3)}|${dN.toFixed(3)}`, (p) => ({ x: p.x + dE, y: p.y + dN }));
            } else if (rotatePreviewSegmentsRef.current) {
                clearRotatePreview();
            }
        } else if (rotatePreviewSegmentsRef.current) {
            clearRotatePreview();
        }

        // Symbol-exclusion hover tooltip — show why the nearest point's
        // symbol is associated (or note it's already excluded).
        if (symbolExclusionMode) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const HIT = 18;
                let nearest: SurveyPoint | null = null;
                let bestDist = HIT * HIT;
                for (const p of points) {
                    if (p.hidden) continue;
                    if (!p.description) continue;
                    const sx = p.easting * transform.scale + transform.offsetX;
                    const sy = -p.northing * transform.scale + transform.offsetY;
                    const d = (mx - sx) ** 2 + (my - sy) ** 2;
                    if (d < bestDist) { bestDist = d; nearest = p; }
                }
                if (nearest) {
                    const key = (nearest.description || '').trim().toUpperCase();
                    const excluded = excludedSymbolDescriptions.has(key);
                    const info = explainSymbolAssociation(nearest.description || '');
                    setExclusionHover(prev => {
                        if (prev && prev.description === nearest!.description && prev.excluded === excluded
                            && prev.clientX === e.clientX && prev.clientY === e.clientY) return prev;
                        return {
                            clientX: e.clientX,
                            clientY: e.clientY,
                            description: nearest!.description || '',
                            symbolName: info.symbolName,
                            reason: info.reason,
                            excluded,
                        };
                    });
                } else if (exclusionHover) {
                    setExclusionHover(null);
                }
            }
        }

        // Corner-align hover — highlight the deed corner under the cursor while
        // the tool is waiting for a corner pick.
        if (drawingMode === 'boundary-corner-align' && cornerAlignBfId && !cornerAlignPendingCorner && !cornerAlignPicker) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const worldX = (mx - transform.offsetX) / transform.scale;
                const worldY = -(my - transform.offsetY) / transform.scale;
                const corner = hitTestBoundaryCorner(cornerAlignBfId, worldX, worldY, 14 / transform.scale);
                setCornerAlignHover(prev => {
                    if (!corner) return prev ? null : prev;
                    if (prev && prev.pn === corner.pn && prev.x === corner.x && prev.y === corner.y) return prev;
                    return { pn: corner.pn, x: corner.x, y: corner.y };
                });
            }
        } else if (cornerAlignHover) {
            setCornerAlignHover(null);
        }

        // Boundary-align slide — drive movable bf translation along target line.
        if (boundaryAlignSliding && boundaryAlignSlideRef.current && props.onUpdateBoundaryTransform && boundaryAlignTarget) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const worldX = (mx - transform.offsetX) / transform.scale;
                const worldY = -(my - transform.offsetY) / transform.scale;
                const s = boundaryAlignSlideRef.current;
                const proj = worldX * s.dirE + worldY * s.dirN;
                let delta = proj - s.mouseStartProj;
                if (delta < s.sMin) delta = s.sMin;
                if (delta > s.sMax) delta = s.sMax;
                // Corner-coincidence snap — if any movable corner (after slide)
                // would land within snapPxWorld of any target corner, snap to it.
                const snapPxWorld = 10 / transform.scale;
                const perpDirE = -s.dirN;
                const perpDirN =  s.dirE;
                const movCornersBase = getBoundaryCornersWorld(s.movableBfId, {
                    txE: s.baseTxE, txN: s.baseTxN, rotDeg: s.rotDegFixed,
                });
                const tgtCorners = getBoundaryCornersWorld(boundaryAlignTarget.bfId);
                let bestSnapDelta: number | null = null;
                let bestSnapDist = snapPxWorld;
                for (const m of movCornersBase) {
                    for (const t of tgtCorners) {
                        const diffX = t.x - m.x;
                        const diffY = t.y - m.y;
                        const along = diffX * s.dirE + diffY * s.dirN;
                        const perp  = diffX * perpDirE + diffY * perpDirN;
                        if (Math.abs(perp) > snapPxWorld) continue;       // not on the slide line
                        if (Math.abs(along - delta) > snapPxWorld * 4) continue; // too far from cursor
                        if (Math.abs(perp) < bestSnapDist) {
                            bestSnapDist = Math.abs(perp);
                            bestSnapDelta = along;
                        }
                    }
                }
                if (bestSnapDelta !== null) delta = bestSnapDelta;
                props.onUpdateBoundaryTransform(s.movableBfId, {
                    translationE: s.baseTxE + s.dirE * delta,
                    translationN: s.baseTxN + s.dirN * delta,
                    rotationDeg: s.rotDegFixed,
                });
            }
            return;
        }

        if (isLabelDragging) {
            if (ownerNameDragTarget.current) {
                const dx = e.clientX - ownerNameDragTarget.current.startX;
                const dy = e.clientY - ownerNameDragTarget.current.startY;
                
                const worldDx = dx / transform.scale;
                const worldDy = -dy / transform.scale; // Invert Y for world coords
                
                const newOffsetX = ownerNameDragTarget.current.startOffsetX + worldDx;
                const newOffsetY = ownerNameDragTarget.current.startOffsetY + worldDy;

                if (props.onUpdateOwnerNameOffset) {
                    props.onUpdateOwnerNameOffset({ x: newOffsetX, y: newOffsetY });
                }
                return;
            }

            if (deedMetadataDragTarget.current) {
                const dx = e.clientX - deedMetadataDragTarget.current.startX;
                const dy = e.clientY - deedMetadataDragTarget.current.startY;
                
                const worldDx = dx / transform.scale;
                const worldDy = -dy / transform.scale; // Invert Y for world coords
                
                const newOffsetX = deedMetadataDragTarget.current.startOffsetX + worldDx;
                const newOffsetY = deedMetadataDragTarget.current.startOffsetY + worldDy;

                if (props.onUpdateDeedMetadataOffset) {
                    props.onUpdateDeedMetadataOffset({ x: newOffsetX, y: newOffsetY });
                }
                return;
            }
            
            if (lineLabelDragTarget.current) {
                const dx = e.clientX - lineLabelDragTarget.current.startX;
                const dy = e.clientY - lineLabelDragTarget.current.startY;
                
                const worldDx = dx / transform.scale;
                const worldDy = -dy / transform.scale; // Invert Y for world coords
                
                const newOffsetX = lineLabelDragTarget.current.startOffsetX + worldDx;
                const newOffsetY = lineLabelDragTarget.current.startOffsetY + worldDy;

                if (props.onUpdateLineLabelOffset) {
                    props.onUpdateLineLabelOffset(lineLabelDragTarget.current.lineId, { x: newOffsetX, y: newOffsetY });
                }
                return;
            }
            
            if (labelDragTarget.current) {
                const dx = e.clientX - labelDragTarget.current.startX;
                const dy = e.clientY - labelDragTarget.current.startY;
                
                // Always store offsets in world coordinates
                // Convert screen pixel delta to world units
                const worldDx = dx / transform.scale;
                const worldDy = dy / transform.scale;
                
                const newOffsetX = labelDragTarget.current.startOffsetX + worldDx;
                const newOffsetY = labelDragTarget.current.startOffsetY + worldDy;

                onUpdatePointLabelOffset(labelDragTarget.current.pointNumber, { x: newOffsetX, y: newOffsetY });
                return;
            }

            if (annotationDragTarget.current && onUpdatePointAnnotationOffset) {
                const dx = e.clientX - annotationDragTarget.current.startX;
                const dy = e.clientY - annotationDragTarget.current.startY;
                // Annotation offset is { dx, dy } in world units. Note: dy is
                // "up positive" (subtracted in render), so invert screen dy.
                const worldDx = dx / transform.scale;
                const worldDy = -dy / transform.scale;
                const newDx = annotationDragTarget.current.startDx + worldDx;
                const newDy = annotationDragTarget.current.startDy + worldDy;
                onUpdatePointAnnotationOffset(annotationDragTarget.current.pointNumber, { dx: newDx, dy: newDy });
                return;
            }
        }

        if (isPanning) {
            const dx = e.clientX - lastMousePos.current.x;
            const dy = e.clientY - lastMousePos.current.y;
            setTransform(prev => ({ ...prev, offsetX: prev.offsetX + dx, offsetY: prev.offsetY + dy }));
            lastMousePos.current = { x: e.clientX, y: e.clientY };
        }

        // Shrinkwrap hover detection — find nearest source point within 14px
        if (props.shrinkwrapSourcePns?.length) {
            const canvas = canvasRef.current;
            if (canvas) {
                const rect = canvas.getBoundingClientRect();
                const mx = e.clientX - rect.left;
                const my = e.clientY - rect.top;
                const HIT = 14;
                let found: string | null = null;
                for (const p of points) {
                    if (!props.shrinkwrapSourcePns.includes(p.pointNumber)) continue;
                    const sx = p.easting * transform.scale + transform.offsetX;
                    const sy = -p.northing * transform.scale + transform.offsetY;
                    if ((mx - sx) ** 2 + (my - sy) ** 2 < HIT * HIT) { found = p.pointNumber; break; }
                }
                if (found !== hoveredShrinkwrapPnRef.current) {
                    hoveredShrinkwrapPnRef.current = found;
                    setHoveredShrinkwrapPn(found);
                }
            }
        } else if (hoveredShrinkwrapPnRef.current !== null) {
            hoveredShrinkwrapPnRef.current = null;
            setHoveredShrinkwrapPn(null);
        }

        // Shift+hover quick-info tooltip — show point details near cursor after a short dwell.
        const canvas = canvasRef.current;
        if (canvas && e.shiftKey && !isPanning && !isLabelDragging && !shiftBoxSelect) {
            const rect = canvas.getBoundingClientRect();
            const mx = e.clientX - rect.left;
            const my = e.clientY - rect.top;
            const anchor = shiftHoverAnchorRef.current;
            // Cancel existing dwell if cursor moved >18px from anchor
            if (anchor && Math.hypot(mx - anchor.mx, my - anchor.my) > 18) {
                if (shiftHoverTimerRef.current !== null) {
                    clearTimeout(shiftHoverTimerRef.current);
                    shiftHoverTimerRef.current = null;
                }
                setShiftHoverPoints([]);
                setShiftHoverPos(null);
                shiftHoverAnchorRef.current = null;
            }
            // Start dwell timer if none in progress
            if (shiftHoverTimerRef.current === null) {
                shiftHoverAnchorRef.current = { mx, my };
                const capturedClientX = e.clientX;
                const capturedClientY = e.clientY;
                shiftHoverTimerRef.current = setTimeout(() => {
                    shiftHoverTimerRef.current = null;
                    const RADIUS_PX = 30;
                    const nearby: SurveyPoint[] = [];
                    for (const p of points) {
                        const sx = p.easting * transform.scale + transform.offsetX;
                        const sy = -p.northing * transform.scale + transform.offsetY;
                        if ((mx - sx) ** 2 + (my - sy) ** 2 < RADIUS_PX * RADIUS_PX) {
                            nearby.push(p);
                        }
                    }
                    nearby.sort((a, b) => {
                        const da = (mx - (a.easting * transform.scale + transform.offsetX)) ** 2
                                 + (my - (-a.northing * transform.scale + transform.offsetY)) ** 2;
                        const db = (mx - (b.easting * transform.scale + transform.offsetX)) ** 2
                                 + (my - (-b.northing * transform.scale + transform.offsetY)) ** 2;
                        return da - db;
                    });
                    if (nearby.length > 0) {
                        setShiftHoverPoints(nearby.slice(0, 8));
                        setShiftHoverPos({ clientX: capturedClientX, clientY: capturedClientY });
                    }
                }, 480);
            }
        } else if (!e.shiftKey) {
            // Shift released — cancel dwell and hide tooltip
            if (shiftHoverTimerRef.current !== null) {
                clearTimeout(shiftHoverTimerRef.current);
                shiftHoverTimerRef.current = null;
            }
            if (shiftHoverPoints.length > 0) {
                setShiftHoverPoints([]);
                setShiftHoverPos(null);
                shiftHoverAnchorRef.current = null;
            }
        }
    }, [isPanning, isLabelDragging, onUpdatePointLabelOffset, onUpdatePointAnnotationOffset, props, transform, points, hoveredShrinkwrapPn, shiftHoverPoints, boundaryAlignSliding, boundaryAlignTarget, getBoundaryCornersWorld, symbolExclusionMode, excludedSymbolDescriptions, explainSymbolAssociation, exclusionHover, shiftBoxSelect, drawingMode, cornerAlignBfId, cornerAlignPendingCorner, cornerAlignPicker, cornerAlignHover, hitTestBoundaryCorner, isRotatingSelection, rotatePivot, updateRotatePreview, clearRotatePreview, isMirroringSelection, mirrorAxisStart, isMovingSelection, moveBasePoint, isCopyingSelection, copyBasePoint, showTransformPreview, isScalingSelection, scaleBasePoint, showScalePreview, inlineOsnapOverride, runningOsnaps, findNearestSurveyPoint, resolveOsnapAtScreen]);

    const handleMouseUp = useCallback(() => {
        if (shiftBoxSelect) {
            const minX = Math.min(shiftBoxSelect.startX, shiftBoxSelect.endX);
            const maxX = Math.max(shiftBoxSelect.startX, shiftBoxSelect.endX);
            const minY = Math.min(shiftBoxSelect.startY, shiftBoxSelect.endY);
            const maxY = Math.max(shiftBoxSelect.startY, shiftBoxSelect.endY);
            const dx = Math.abs(shiftBoxSelect.endX - shiftBoxSelect.startX);
            const dy = Math.abs(shiftBoxSelect.endY - shiftBoxSelect.startY);
            const isDrag = dx > 3 || dy > 3;
            const isWindow = shiftBoxSelect.endX >= shiftBoxSelect.startX;

            if (isDrag) {
                const inRect = (x: number, y: number) => x >= minX && x <= maxX && y >= minY && y <= maxY;
                const segIntersectsRect = (x1: number, y1: number, x2: number, y2: number) => {
                    if (inRect(x1, y1) || inRect(x2, y2)) return true;
                    const intersects = (ax: number, ay: number, bx: number, by: number, cx: number, cy: number, dx2: number, dy2: number) => {
                        const o = (px: number, py: number, qx: number, qy: number, rx: number, ry: number) => (qy - py) * (rx - qx) - (qx - px) * (ry - qy);
                        const o1 = o(ax, ay, bx, by, cx, cy);
                        const o2 = o(ax, ay, bx, by, dx2, dy2);
                        const o3 = o(cx, cy, dx2, dy2, ax, ay);
                        const o4 = o(cx, cy, dx2, dy2, bx, by);
                        return (o1 * o2 < 0) && (o3 * o4 < 0);
                    };
                    return (
                        intersects(x1, y1, x2, y2, minX, minY, maxX, minY) ||
                        intersects(x1, y1, x2, y2, maxX, minY, maxX, maxY) ||
                        intersects(x1, y1, x2, y2, maxX, maxY, minX, maxY) ||
                        intersects(x1, y1, x2, y2, minX, maxY, minX, minY)
                    );
                };

                const distToSegment = (px: number, py: number, x1: number, y1: number, x2: number, y2: number) => {
                    const vx = x2 - x1;
                    const vy = y2 - y1;
                    const lSq = vx * vx + vy * vy;
                    if (lSq < 1e-9) return Math.hypot(px - x1, py - y1);
                    const t = Math.max(0, Math.min(1, ((px - x1) * vx + (py - y1) * vy) / lSq));
                    return Math.hypot(px - (x1 + t * vx), py - (y1 + t * vy));
                };

                const circleIntersectsRect = (cx: number, cy: number, r: number) => {
                    if (inRect(cx, cy)) return true;
                    if (Math.hypot(minX - cx, minY - cy) <= r ||
                        Math.hypot(maxX - cx, minY - cy) <= r ||
                        Math.hypot(maxX - cx, maxY - cy) <= r ||
                        Math.hypot(minX - cx, maxY - cy) <= r) return true;
                    if (distToSegment(cx, cy, minX, minY, maxX, minY) <= r) return true;
                    if (distToSegment(cx, cy, maxX, minY, maxX, maxY) <= r) return true;
                    if (distToSegment(cx, cy, maxX, maxY, minX, maxY) <= r) return true;
                    if (distToSegment(cx, cy, minX, maxY, minX, minY) <= r) return true;
                    return false;
                };

                const selected = new Set<string>();
                for (const line of lines) {
                    if (line.hidden) continue;
                    const p1 = pointMap.get(line.from);
                    const p2 = pointMap.get(line.to);
                    const x1w = p1 ? p1.easting : line.fromPt?.x;
                    const y1w = p1 ? p1.northing : line.fromPt?.y;
                    const x2w = p2 ? p2.easting : line.toPt?.x;
                    const y2w = p2 ? p2.northing : line.toPt?.y;
                    if (x1w === undefined || y1w === undefined) continue;

                    const lineId = line.id || `${line.from}-${line.to}`;
                    const isCircleEntity = line.isCircle || (line.isCurve && line.curveRadius && (!line.fromPt || (x1w === x2w && y1w === y2w)) && !line.tangentBearing && !line.chordBearing);

                    if (isCircleEntity && line.curveRadius) {
                        const cXw = line.circleCenter ? line.circleCenter.x : x1w;
                        const cYw = line.circleCenter ? line.circleCenter.y : y1w;
                        const sc = worldToScreen(cXw, cYw);
                        const sr = line.curveRadius * transform.scale;
                        if (isWindow) {
                            if (sc.x - sr >= minX && sc.x + sr <= maxX && sc.y - sr >= minY && sc.y + sr <= maxY) {
                                selected.add(lineId);
                            }
                        } else {
                            if (circleIntersectsRect(sc.x, sc.y, sr)) {
                                selected.add(lineId);
                            }
                        }
                        continue;
                    }

                    if (x2w === undefined || y2w === undefined) continue;
                    const sp1 = worldToScreen(x1w, y1w);
                    const sp2 = worldToScreen(x2w, y2w);
                    const sx1 = sp1.x;
                    const sy1 = sp1.y;
                    const sx2 = sp2.x;
                    const sy2 = sp2.y;

                    if (line.isCurve && line.curveRadius && line.arcLength) {
                        if (isWindow) {
                            let allIn = inRect(sx1, sy1) && inRect(sx2, sy2);
                            if (allIn) {
                                for (let k = 1; k < 6; k++) {
                                    const frac = k / 6;
                                    const sampleX = sx1 + (sx2 - sx1) * frac;
                                    const sampleY = sy1 + (sy2 - sy1) * frac;
                                    if (!inRect(sampleX, sampleY)) {
                                        allIn = false;
                                        break;
                                    }
                                }
                            }
                            if (allIn) selected.add(lineId);
                        } else {
                            if (inRect(sx1, sy1) || inRect(sx2, sy2) || segIntersectsRect(sx1, sy1, sx2, sy2)) {
                                selected.add(lineId);
                            }
                        }
                        continue;
                    }

                    if (isWindow) {
                        if (inRect(sx1, sy1) && inRect(sx2, sy2)) selected.add(lineId);
                    } else {
                        if (segIntersectsRect(sx1, sy1, sx2, sy2)) selected.add(lineId);
                    }
                }

                // Box-select dimensions
                if (dimensions && dimensions.length > 0) {
                    for (const dim of dimensions) {
                        let refPt: { easting: number; northing: number } | null = null;
                        if (dim.type === 'aligned' || dim.type === 'horizontal' || dim.type === 'vertical') {
                            refPt = { easting: (dim.p1.easting + dim.p2.easting) / 2, northing: (dim.p1.northing + dim.p2.northing) / 2 };
                        } else if (dim.type === 'radius' || dim.type === 'diameter' || dim.type === 'curve-data') {
                            refPt = dim.textPosition;
                        } else if (dim.type === 'arc-length') {
                            refPt = dim.center;
                        } else if (dim.type === 'angular') {
                            refPt = dim.vertex;
                        } else if (dim.type === 'ordinate') {
                            refPt = dim.point;
                        }
                        if (refPt) {
                            const sc = worldToScreen(refPt.easting, refPt.northing);
                            if (inRect(sc.x, sc.y)) {
                                setSelectedDimensionId(dim.id);
                                break;
                            }
                        }
                    }
                }

                setSelectedLineIds(selected);
                setIsSelectionMode(true);
            } else {
                if (selectedLineIds.size > 0 && !isBoxSelectMode) {
                    setSelectedLineIds(new Set());
                }
            }

            setShiftBoxSelect(null);
            return;
        }

        if (activeDimGrip) {
            setActiveDimGrip(null);
            activeDimGripDragStartRef.current = null;
            return;
        }

        if (activeGrip && activeGripDragStartRef.current && canvasRef.current) {
            const dragDist = Math.hypot(e.clientX - activeGripDragStartRef.current.clientX, e.clientY - activeGripDragStartRef.current.clientY);
            if (dragDist > 4) {
                const rect = canvasRef.current.getBoundingClientRect();
                const worldCoords = screenToWorld(e.clientX - rect.left, e.clientY - rect.top);
                commitActiveGrip(activeGrip, worldCoords.easting, worldCoords.northing, activeOsnapPoint);
                activeGripDragStartRef.current = null;
                return;
            }
            activeGripDragStartRef.current = null;
        }

        if (isLabelDragging) {
            document.body.classList.remove('label-dragging');
            setIsLabelDragging(false);
            labelDragTarget.current = null;
            lineLabelDragTarget.current = null;
            deedMetadataDragTarget.current = null;
            ownerNameDragTarget.current = null;
            annotationDragTarget.current = null;
        }
        if (isPanning) {
            document.body.classList.remove('panning');
            setIsPanning(false);
        }
    }, [isLabelDragging, isPanning, shiftBoxSelect, lines, pointMap, transform, activeGrip, isBoxSelectMode, selectedLineIds, commitActiveGrip, screenToWorld, activeOsnapPoint, isOffsetMode, offsetDistance, offsetTargetId, updateOffsetPreview, clearOffsetPreview]);
    
    const handleTouchStart = useCallback((e: React.TouchEvent) => {
        if (drawingMode !== 'none') return;
        if (e.touches.length === 1) {
            lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        } else if (e.touches.length === 2) {
            const t1 = e.touches[0];
            const t2 = e.touches[1];
            pinchDistRef.current = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);
        }
    }, [drawingMode]);

    const handleTouchMove = useCallback((e: React.TouchEvent) => {
        e.preventDefault();
        const container = containerRef.current;
        if (!container || drawingMode !== 'none') return;
        
        if (e.touches.length === 1) { // Panning
            const touch = e.touches[0];
            const dx = touch.clientX - lastMousePos.current.x;
            const dy = touch.clientY - lastMousePos.current.y;
            setTransform(prev => ({...prev, offsetX: prev.offsetX + dx, offsetY: prev.offsetY + dy}));
            lastMousePos.current = { x: touch.clientX, y: touch.clientY };
        } else if (e.touches.length === 2) { // Zooming
            const t1 = e.touches[0];
            const t2 = e.touches[1];
            const currentDist = Math.hypot(t1.clientX - t2.clientX, t1.clientY - t2.clientY);

            if (pinchDistRef.current > 0) {
                const zoomFactor = currentDist / pinchDistRef.current;
                
                const rect = container.getBoundingClientRect();
                const midPointX = (t1.clientX + t2.clientX) / 2 - rect.left;
                const midPointY = (t1.clientY + t2.clientY) / 2 - rect.top;

                setTransform(prev => {
                    const newScale = Math.max(0.00001, Math.min(50000, prev.scale * zoomFactor));
                    const actualZoomFactor = newScale / prev.scale;
                    
                    const newOffsetX = midPointX - (midPointX - prev.offsetX) * actualZoomFactor;
                    const newOffsetY = midPointY - (midPointY - prev.offsetY) * actualZoomFactor;
                    
                    return { scale: newScale, offsetX: newOffsetX, offsetY: newOffsetY };
                });
            }
            pinchDistRef.current = currentDist;
        }
    }, [drawingMode]);

    const handleTouchEnd = useCallback((e: React.TouchEvent) => {
        if (e.touches.length < 2) {
            pinchDistRef.current = 0; // Stop zooming
        }
        if (e.touches.length === 1) {
            // Transitioning from zoom to pan
            lastMousePos.current = { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
    }, []);
    
    // Notify parent of tool state changes
    useEffect(() => {
        if (onToolStateChange) {
            const lineType = drawingMode === 'breaklines' ? 'breakline' : drawingMode === 'inclusion' ? 'inclusion' : drawingMode === 'exclusion' ? 'exclusion' : 'normal';
            onToolStateChange({
                isDrawing: drawingMode !== 'none',
                isTrimming: isTrimmingLines,
                isExtending: isExtendingLines,
                isDeleting: isDeletingLines,
                selectedCount: selectedLineIds.size + selectedPointNumbers.size + selectedCenterlineIds.size,
                segmentCount: polylinePoints.length > 0 ? polylinePoints.length - 1 : 0,
                lineType
            });
        }
    }, [drawingMode, isTrimmingLines, isExtendingLines, isDeletingLines, polylinePoints.length, onToolStateChange, selectedLineIds.size, selectedPointNumbers.size, selectedCenterlineIds.size]);

    const handleZoomToPointSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        const pointToZoom = points.find(p => p.pointNumber === zoomToPointNumber);
        if (pointToZoom) {
            onZoomToPoint(pointToZoom);
            setZoomToPointNumber('');
            setIsZoomToPointOpen(false);
        } else {
            reportError({ title: 'Point not found', message: `Point "${zoomToPointNumber}" not found.` });
        }
    };

    // Find Points: filter the loaded points by a free-text description query.
    // Strips natural-language prefixes like "show me", "find", "where are"
    // and stop-words like "points"/"the" so the user can type the way they'd
    // ask an agent. Falls back to point-number and layer matching too.
    const findPointsMatches = useMemo(() => {
        const raw = findPointsQuery.trim().toLowerCase();
        if (!raw) return [] as SurveyPoint[];
        const cleaned = raw
            .replace(/^(show\s+me|find|where\s+are|list|give\s+me|locate|search\s+for)\s+/i, '')
            .replace(/\b(the|all|every|any|please|points?)\b/g, ' ')
            .trim();
        const tokens = (cleaned || raw).split(/\s+/).filter(t => t.length > 0);
        if (tokens.length === 0) return [] as SurveyPoint[];
        return points.filter(p => {
            const hay = [
                p.description ?? '',
                p.pointNumber ?? '',
                p.layer ?? '',
            ].join(' ').toLowerCase();
            return tokens.every(t => hay.includes(t));
        }).slice(0, 200); // cap render cost for huge jobs
    }, [findPointsQuery, points]);
    
    // Leaving the active tool (Escape, toolbar switch, …) resets the arc
    // submode and the chained tangent so the next polyline starts fresh.
    useEffect(() => {
        setPolylineArcMode(false);
        lastSegmentTangentRef.current = null;
    }, [drawingMode]);

    useEffect(() => {
        if (drawingMode === 'polylines' && polylinePoints.length === 2) {
            // For polylines mode, when user has 2 points, create a line between them
            const p1 = polylinePoints[0];
            const p2 = polylinePoints[1];
            const p1Line = buildLineEndpoint(p1);
            const p2Line = buildLineEndpoint(p2);
            // Arc submode: unique arc leaving p1 tangent to the previous
            // segment and passing through p2. Degenerate geometry (p2 on the
            // tangent line) falls back to a straight segment.
            const arc = polylineArcMode && lastSegmentTangentRef.current
                ? computeTangentArc(
                    { easting: p1.easting, northing: p1.northing },
                    lastSegmentTangentRef.current,
                    { easting: p2.easting, northing: p2.northing })
                : null;
            if (arc) {
                onAddLine({
                    from: p1Line.ref,
                    to: p2Line.ref,
                    type: undefined,
                    layer: activeDrawingLayer || undefined,
                    fromPt: p1Line.coords,
                    toPt: p2Line.coords,
                    isCurve: true,
                    curveRadius: arc.radius,
                    arcLength: arc.arcLength,
                    curveDirection: arc.direction,
                    tangentBearing: arc.tangentBearing,
                    chordBearing: arc.chordBearing,
                });
                lastSegmentTangentRef.current = arc.endTangent;
            } else {
                onAddLine({
                    from: p1Line.ref,
                    to: p2Line.ref,
                    type: undefined,
                    layer: activeDrawingLayer || undefined,
                    fromPt: p1Line.coords,
                    toPt: p2Line.coords,
                });
                const segLen = Math.hypot(p2.easting - p1.easting, p2.northing - p1.northing);
                if (segLen > 1e-9) {
                    lastSegmentTangentRef.current = { x: (p2.easting - p1.easting) / segLen, y: (p2.northing - p1.northing) / segLen };
                }
            }

            requestAnimationFrame(() => {
                draw();
            });

            // Keep last point as start for next segment (continuous drawing)
            setPolylinePoints([p2]);
        } else if (drawingMode === 'breaklines' && polylinePoints.length === 2) {
            // Draw breaklines (2 points at a time, continuous)
            const p1 = polylinePoints[0];
            const p2 = polylinePoints[1];
            const p1Line = buildLineEndpoint(p1);
            const p2Line = buildLineEndpoint(p2);
            onAddLine({
                from: p1Line.ref,
                to: p2Line.ref,
                type: 'breakline',
                fromPt: p1Line.coords,
                toPt: p2Line.coords,
            });
            
            requestAnimationFrame(() => {
                draw();
            });
            
            // Keep last point as start for next segment (continuous drawing)
            setPolylinePoints([p2]);
        } else if (drawingMode === 'inclusion' && polylinePoints.length === 2) {
            // Draw inclusion lines (2 points at a time, continuous)
            const p1 = polylinePoints[0];
            const p2 = polylinePoints[1];
            const p1Line = buildLineEndpoint(p1);
            const p2Line = buildLineEndpoint(p2);
            onAddLine({
                from: p1Line.ref,
                to: p2Line.ref,
                type: 'inclusion',
                fromPt: p1Line.coords,
                toPt: p2Line.coords,
            });
            
            requestAnimationFrame(() => {
                draw();
            });
            
            // Keep last point as start for next segment (continuous drawing)
            setPolylinePoints([p2]);
        } else if (drawingMode === 'exclusion' && polylinePoints.length === 2) {
            // Draw exclusion lines (2 points at a time, continuous)
            const p1 = polylinePoints[0];
            const p2 = polylinePoints[1];
            const p1Line = buildLineEndpoint(p1);
            const p2Line = buildLineEndpoint(p2);
            onAddLine({
                from: p1Line.ref,
                to: p2Line.ref,
                type: 'exclusion',
                fromPt: p1Line.coords,
                toPt: p2Line.coords,
            });
            
            requestAnimationFrame(() => {
                draw();
            });
            
            // Keep last point as start for next segment (continuous drawing)
            setPolylinePoints([p2]);
        }
    }, [polylinePoints, onAddLine, drawingMode, draw, buildLineEndpoint, activeDrawingLayer, polylineArcMode]);
    
    const finishDrawing = () => {
        setDrawingMode('none');
        setPolylinePoints([]);
        setTypedSegmentLength('');
        setCircleCenter(null);
        setTypedCircleValue('');
        setPolygonCenter(null);
        setTypedPolygonDiameter('');
        setCircleSubMode('center-radius');
        setTtrStep(1);
        setTtrEntity1(null);
        setTtrEntity2(null);
        setTtrHoverEntity(null);
        setIsTrimmingLines(false);
        setTrimPoint(null);
        setTrimHoverPreview(null);
        setIsExtendingLines(false);
        setExtendFromLine(null);
        setIsMultiSegmentMode(false);
        resetBoundaryLineMode();
        resetDimMode();
        if (cornerAlignBfId) {
            resetCornerAlign(true);
            props.onBoundaryCornerAlignRequestHandled?.();
        }
    };

    const startPolygonMode = useCallback((sides?: number, center?: { easting: number; northing: number }) => {
        finishDrawing();
        const nextSides = sides !== undefined && Number.isInteger(sides)
            ? Math.min(256, Math.max(3, sides))
            : polygonSides;
        setPolygonSides(nextSides);
        setPolygonCenter(center ? { ...center } : null);
        setTypedPolygonDiameter('');
        setDrawingMode('polygon');
        setActiveOsnapPoint(null);
        setInlineOsnapOverride(null);
        setTerminalIncoming({
            text: center
                ? `POLYGON: ${nextSides} sides about E ${center.easting.toFixed(2)}, N ${center.northing.toFixed(2)} — drag, click, or type the diameter; scroll or [ ] changes sides [Esc cancels]`
                : `POLYGON: Pick the center — then drag, click, or type the diameter; scroll or [ ] changes sides [Esc cancels]`,
            tone: 'ok',
        });
        setCommandFocusRequest(n => n + 1);
        requestAnimationFrame(() => drawRef.current?.());
    }, [finishDrawing, polygonSides]);

    // Shared entry points for the command line and the drawing-tools toolbar.
    const startCopyMode = () => {
        finishDrawing();
        stopInteractiveTransforms();
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        setIsOffsetMode(false);
        setOffsetDistance(null);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        setIsCopyingSelection(true);
        setCopyBasePoint(null);
    };

    const startOffsetMode = (distance?: number) => {
        finishDrawing();
        stopInteractiveTransforms();
        setIsTrimmingLines(false);
        setIsExtendingLines(false);
        setIsDeletingLines(false);
        setTrimPoint(null);
        setExtendFromLine(null);
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
        setSelectedLineIds(new Set());
        setIsCopyingSelection(false);
        setCopyBasePoint(null);
        setIsOffsetMode(true);
        setOffsetTargetId(null);
        setOffsetPickStart(null);
        clearOffsetPreview();
        setOffsetDistance(
            distance !== undefined && Number.isFinite(distance) && distance > 0
                ? convertLinearUnits(distance, settings.linearUnits || 'usSurveyFoot', 'foot')
                : null,
        );
    };

    /** Open an operation-anchored prompt for a command that needs several values. */
    const openCommandPrompt = useCallback((prompt: CommandPromptSpec) => {
        commandPromptOpenedRef.current = true;
        if (prompt.id === 'array' || prompt.id === 'polar_array') arrayWidgetSideRef.current = null;
        setActiveCommandPrompt(prompt);
        setCommandPromptValues({ ...(prompt.initialValues ?? {}) });
        setCommandPromptIndex(0);
        setIsPickingPolarCenter(false);
        setPendingSelectionCommand(null);
        setIsSelectionMode(false);
        setIsBoxSelectMode(false);
    }, []);

    /** Route one value typed on the command line into the active prompt field. */
    const applyCommandPromptValue = useCallback((raw: string, kind: 'number' | 'point'): boolean => {
        const prompt = activeCommandPrompt;
        if (!prompt) return false;
        const field = prompt.fields[commandPromptIndex];
        if (!field) return false;
        if (field.kind !== kind) {
            setTerminalIncoming({
                text: `${prompt.title}: ${field.label} expects ${field.kind === 'point' ? 'an E,N point (e.g. 0,0)' : 'a number'}.`,
                tone: 'error',
            });
            return true;
        }
        const value = raw.trim();
        if (!value) return true;
        if (field.key === 'center') {
            setIsPickingPolarCenter(false);
            setActiveOsnapPoint(null);
        }
        setCommandPromptValues(prev => ({ ...prev, [field.key]: value }));
        const nextIndex = Math.min(prompt.fields.length - 1, commandPromptIndex + 1);
        setCommandPromptIndex(nextIndex);
        setTerminalIncoming({
            text: nextIndex === commandPromptIndex
                ? `${prompt.title}: ${field.label} = ${value} — press Enter or Apply to run`
                : `${prompt.title}: ${field.label} = ${value} — next: ${prompt.fields[nextIndex]?.label ?? 'Apply'} (${nextIndex + 1}/${prompt.fields.length})`,
            tone: 'ok',
        });
        return true;
    }, [activeCommandPrompt, commandPromptIndex]);

    const submitCommandPrompt = useCallback(() => {
        const prompt = activeCommandPrompt;
        if (!prompt) return;
        const result = prompt.submit(commandPromptValues);
        if (result.status === 'error') {
            setTerminalIncoming({ text: result.message, tone: 'error' });
            return;
        }
        setActiveCommandPrompt(null);
        setCommandPromptValues({});
        setCommandPromptIndex(0);
        setIsPickingPolarCenter(false);
        setActiveOsnapPoint(null);
        setPendingSelectionCommand(null);
        setTerminalIncoming({ text: result.message, tone: result.status === 'ok' ? 'ok' : 'info' });
        setCommandFocusRequest(n => n + 1);
        requestAnimationFrame(() => drawRef.current?.());
    }, [activeCommandPrompt, commandPromptValues]);

    const handleCommandPromptChange = useCallback((key: string, value: string) => {
        // Typing a center overrides an armed canvas pick.
        if (key === 'center') {
            setIsPickingPolarCenter(false);
            setActiveOsnapPoint(null);
        }
        setCommandPromptValues(prev => ({ ...prev, [key]: value }));
    }, []);

    const handleCommandPromptActivate = useCallback((index: number) => {
        setCommandPromptIndex(index);
    }, []);

    /** Apply a command that was waiting for canvas picks (Enter in pick mode). */
    const runPendingSelectionCommand = useCallback(() => {
        const pending = pendingSelectionCommand;
        if (!pending) return;
        if (selectedLineIds.size === 0) {
            setTerminalIncoming({
                text: `${pending.label}: Select at least one entity, then press Enter [Esc to cancel]`,
                tone: 'error',
            });
            setCommandFocusRequest(n => n + 1);
            return;
        }
        commandPromptOpenedRef.current = false;
        const result = executeCanvasCommand(parseCanvasCommand(pending.raw), {
            hasSelection: true,
            onStartCopy: startCopyMode,
            onStartRotate: startRotateMode,
            onStartScale: startScaleMode,
            onStartMirror: startMirrorMode,
            onStartMove: startMoveMode,
            onEraseSelection: eraseSelectionCommand,
            onMoveSelection: moveSelectionCommand,
            onRotateSelection: rotateSelectionCommand,
            onScaleSelection: scaleSelectionCommand,
            onMirrorSelection: mirrorSelectionCommand,
            onJoinSelection: joinSelectionCommand,
            onExplodeSelection: explodeSelectionCommand,
            onArraySelection: arraySelectionCommand,
            onPolarArraySelection: polarArraySelectionCommand,
            onPrompt: openCommandPrompt,
            linearUnitLabel: getLinearUnitAbbreviation(settings.linearUnits),
            onAwaitSelection: beginSelectionForCommand,
        });
        setPendingSelectionCommand(null);
        setTerminalIncoming({
            text: result.message,
            tone: result.status === 'error' ? 'error' : result.status === 'ok' ? 'ok' : 'info',
        });
        // Hand the caret back to the command line unless the command opened a
        // prompt — the prompt's own input takes focus in that case.
        if (!commandPromptOpenedRef.current) {
            setCommandFocusRequest(n => n + 1);
        }
        requestAnimationFrame(() => drawRef.current?.());
    }, [pendingSelectionCommand, selectedLineIds.size, startCopyMode, eraseSelectionCommand, moveSelectionCommand, rotateSelectionCommand, scaleSelectionCommand, mirrorSelectionCommand, joinSelectionCommand, explodeSelectionCommand, arraySelectionCommand, polarArraySelectionCommand, openCommandPrompt, beginSelectionForCommand, startRotateMode, startMirrorMode, startMoveMode, startScaleMode, settings.linearUnits]);

    useEffect(() => {
        runPendingSelectionCommandRef.current = runPendingSelectionCommand;
    }, [runPendingSelectionCommand]);

    const resetBoundaryLineMode = () => {
        setBoundaryLineFrom(null);
        setBoundaryClickedTo(null);
        setBoundaryBearing('');
        setBoundaryDistance('');
        setBoundaryInputError('');
        setBoundaryCurveMode(false);
        setBoundaryCurveRadius('');
        setBoundaryCurveArcLength('');
        setBoundaryCurveChordBearing('');
        setBoundaryCurveDirection('right');
        setDrawnBoundaryCalls([]);
        setBoundaryClosureResult(null);
        setBoundaryClosureError('');
    };

    const startDimensionMode = useCallback((mode: typeof drawingMode) => {
        if (drawingMode === mode) {
            setDrawingMode('none');
            resetDimMode();
        } else {
            finishDrawing();
            setDrawingMode(mode);
            resetDimMode();
        }
        setIsDimFlyoutOpen(false);
    }, [drawingMode, resetDimMode]);

    const handleBoundaryCallConfirm = () => {
        if (!boundaryLineFrom) {
            notifyBoundaryError('Click on a point first to set the start.');
            return;
        }
        if (!boundaryBearing.trim()) {
            notifyBoundaryError(`Enter a ${boundaryCurveMode ? 'tangent bearing' : 'bearing'}.`);
            return;
        }
        setBoundaryClosureResult(null);
        setBoundaryClosureError('');

        if (boundaryCurveMode) {
            const radius = parseFloat(boundaryCurveRadius);
            const arcLen = parseFloat(boundaryCurveArcLength);
            if (!boundaryCurveRadius.trim() || isNaN(radius) || radius <= 0) { notifyBoundaryError('Enter a valid radius.'); return; }
            if (!boundaryCurveArcLength.trim() || isNaN(arcLen) || arcLen <= 0) { notifyBoundaryError('Enter a valid arc length.'); return; }
            const brRad = parseBearingToRadians(boundaryBearing);
            if (brRad === null) { notifyBoundaryError('Invalid tangent bearing format.'); return; }
            setBoundaryInputError('');
            if (onAddBoundaryCall) {
                // Compute chord bearing from tangent + curve geometry if user left it blank
                const chordBearingRaw = boundaryCurveChordBearing.trim();
                let resolvedChordBearing = chordBearingRaw || '';
                if (!resolvedChordBearing) {
                    const deltaM = arcLen / radius;
                    const deltaS = boundaryCurveDirection === 'left' ? -deltaM : deltaM;
                    const chordRad = brRad + deltaS / 2;
                    resolvedChordBearing = formatBearing(chordRad);
                }
                onAddBoundaryCall(boundaryLineFrom, boundaryBearing.trim(), arcLen.toFixed(2), (endPt) => {
                    setDrawnBoundaryCalls(prev => [...prev, {
                        from: boundaryLineFrom.pointNumber,
                        to: endPt.pointNumber,
                        bearing: resolvedChordBearing,
                        distance: arcLen.toFixed(2),
                        isCurve: true,
                        curveRadius: radius,
                        arcLength: arcLen,
                        curveDirection: boundaryCurveDirection,
                        tangentBearing: boundaryBearing.trim(),
                        chordBearing: resolvedChordBearing,
                    }]);
                    setBoundaryLineFrom(endPt);
                    setBoundaryClickedTo(null);
                    setBoundaryBearing('');
                    setBoundaryCurveRadius('');
                    setBoundaryCurveArcLength('');
                    setBoundaryCurveChordBearing('');
                    setBoundaryInputError('');
                    setTimeout(() => boundaryBearingRef.current?.focus(), 50);
                }, { curveRadius: radius, arcLength: arcLen, curveDirection: boundaryCurveDirection });
            }
        } else {
            if (!boundaryDistance.trim()) {
                notifyBoundaryError('Enter a distance.');
                return;
            }
            const brRad = parseBearingToRadians(boundaryBearing);
            const dist = parseDistance(boundaryDistance);
            if (brRad === null) { notifyBoundaryError('Invalid bearing format.'); return; }
            if (dist === null) { notifyBoundaryError('Invalid distance.'); return; }
            setBoundaryInputError('');
            if (onAddBoundaryCall) {
                onAddBoundaryCall(boundaryLineFrom, boundaryBearing.trim(), boundaryDistance.trim(), (endPt) => {
                    setDrawnBoundaryCalls(prev => [...prev, {
                        from: boundaryLineFrom.pointNumber,
                        to: endPt.pointNumber,
                        bearing: boundaryBearing.trim(),
                        distance: boundaryDistance.trim(),
                    }]);
                    // Chain: set the newly created endpoint as the new from, clear second-click state
                    setBoundaryLineFrom(endPt);
                    setBoundaryClickedTo(null);
                    setBoundaryBearing('');
                    setBoundaryDistance('');
                    setBoundaryInputError('');
                    // Focus bearing input for next call
                    setTimeout(() => boundaryBearingRef.current?.focus(), 50);
                });
            }
        }
    };

    const handleBoundaryClosureReport = () => {
        if (!onComputeCurrentClosure || drawnBoundaryCalls.length < 2) return;
        setBoundaryClosureResult(null);
        setBoundaryClosureError('');
        try {
            const result = onComputeCurrentClosure(drawnBoundaryCalls);
            if (result) {
                setBoundaryClosureResult(result);
            } else {
                notifyBoundaryError('Could not compute closure.', true);
            }
        } catch (e: any) {
            notifyBoundaryError(e?.message ?? 'Closure error.', true);
        }
    };

    const handleDoubleClick = useCallback((e: React.MouseEvent<HTMLCanvasElement>) => {
        // COPY/OFFSET consume canvas clicks; a double-click must not hijack
        // the second pick (copy destination / offset side) into a selection.
        if (isCopyingSelection || isOffsetMode) return;
        const canvas = canvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const clickX = e.clientX - rect.left;
        const clickY = e.clientY - rect.top;

        const clickedLine = findLineNearClick(clickX, clickY, 15);
        if (!clickedLine) return;

        const targetPolylineId = clickedLine.polylineId;
        const matchingIds = new Set<string>();

        // Step 1: If polylineId exists, add all lines with the same polylineId
        if (targetPolylineId) {
            lines.forEach(l => {
                if (l.polylineId === targetPolylineId && !l.hidden) {
                    matchingIds.add(l.id || `${l.from}-${l.to}`);
                }
            });
        }

        // Step 2: Continuous connected chain traversal
        const getEndpoints = (l: SurveyLine) => {
            const p1 = pointMap.get(l.from);
            const p2 = pointMap.get(l.to);
            const pt1 = p1 ? { x: p1.easting, y: p1.northing } : (l.fromPt ? { x: l.fromPt.x, y: l.fromPt.y } : null);
            const pt2 = p2 ? { x: p2.easting, y: p2.northing } : (l.toPt ? { x: l.toPt.x, y: l.toPt.y } : null);
            return { fromPn: l.from, toPn: l.to, pt1, pt2 };
        };

        const queue: SurveyLine[] = [clickedLine];
        const visited = new Set<SurveyLine>([clickedLine]);

        while (queue.length > 0) {
            const curr = queue.shift()!;
            const currId = curr.id || `${curr.from}-${curr.to}`;
            matchingIds.add(currId);
            const currEnds = getEndpoints(curr);

            for (const other of lines) {
                if (visited.has(other) || other.hidden) continue;
                const otherEnds = getEndpoints(other);

                let connected = false;
                // Point Number ID matching
                if (currEnds.fromPn && (currEnds.fromPn === otherEnds.fromPn || currEnds.fromPn === otherEnds.toPn)) connected = true;
                if (currEnds.toPn && (currEnds.toPn === otherEnds.fromPn || currEnds.toPn === otherEnds.toPn)) connected = true;

                // Coordinate proximity matching (< 0.05 ft)
                if (!connected && currEnds.pt1 && currEnds.pt2 && otherEnds.pt1 && otherEnds.pt2) {
                    const d11 = Math.hypot(currEnds.pt1.x - otherEnds.pt1.x, currEnds.pt1.y - otherEnds.pt1.y);
                    const d12 = Math.hypot(currEnds.pt1.x - otherEnds.pt2.x, currEnds.pt1.y - otherEnds.pt2.y);
                    const d21 = Math.hypot(currEnds.pt2.x - otherEnds.pt1.x, currEnds.pt2.y - otherEnds.pt1.y);
                    const d22 = Math.hypot(currEnds.pt2.x - otherEnds.pt2.x, currEnds.pt2.y - otherEnds.pt2.y);
                    if (d11 < 0.05 || d12 < 0.05 || d21 < 0.05 || d22 < 0.05) {
                        connected = true;
                    }
                }

                if (connected) {
                    visited.add(other);
                    queue.push(other);
                }
            }
        }

        setSelectedLineIds(matchingIds);
        setIsSelectionMode(true);
        requestAnimationFrame(() => drawRef.current?.());
    }, [findLineNearClick, lines, pointMap, isCopyingSelection, isOffsetMode]);

    // Live rotation angle for the ROTATE prompt (base point already picked).
    const rotateAnglePreview = (() => {
        if (!isRotatingSelection || !rotatePivot || !cursorScreenPos) return null;
        const world = screenToWorld(cursorScreenPos.x, cursorScreenPos.y);
        const radians = Math.atan2(world.northing - rotatePivot.northing, world.easting - rotatePivot.easting);
        return ((radians * 180) / Math.PI + 360) % 360;
    })();

    // Live factor for the SCALE prompt (reference point already picked).
    const scaleFactorPreview = (() => {
        if (!isScalingSelection || !scaleBasePoint || !cursorScreenPos) return null;
        const referenceRadius = scaleReferenceRadiusRef.current;
        if (!referenceRadius || referenceRadius < 1e-9) return null;
        const world = screenToWorld(cursorScreenPos.x, cursorScreenPos.y);
        const distance = Math.hypot(world.easting - scaleBasePoint.easting, world.northing - scaleBasePoint.northing);
        return distance > 1e-9 ? distance / referenceRadius : null;
    })();

    // Live displacement for the MOVE prompt (base point already picked).
    const moveDeltaPreview = (() => {
        if (!isMovingSelection || !moveBasePoint || !cursorScreenPos) return null;
        const world = screenToWorld(cursorScreenPos.x, cursorScreenPos.y);
        return { dE: world.easting - moveBasePoint.easting, dN: world.northing - moveBasePoint.northing };
    })();

    return (
        <div 
            ref={containerRef} 
            className="w-full h-full bg-[#0a0f16] relative light-theme:bg-gray-200" 
            onWheel={handleWheel}
            onMouseMove={handleContainerMouseMove}
            onMouseLeave={() => {
                setCursorScreenPos(null);
                cursorWorldPosRef.current = null;
                clearOffsetPreview();
                // Clear shift-hover tooltip when pointer leaves the canvas area
                if (shiftHoverTimerRef.current !== null) {
                    clearTimeout(shiftHoverTimerRef.current);
                    shiftHoverTimerRef.current = null;
                }
                if (shiftHoverPoints.length > 0) {
                    setShiftHoverPoints([]);
                    setShiftHoverPos(null);
                    shiftHoverAnchorRef.current = null;
                }
            }}
            style={{ cursor: isPanning ? 'grabbing' : (isCopyingSelection || isOffsetMode || isPickingOtOrigin || isPickingPolarCenter ? 'crosshair' : (shiftBoxSelect ? 'crosshair' : (isCtrlPressed && isOverLabel ? 'grab' : (drawingMode !== 'none' || isDeletingLines || isTrimmingLines || isExtendingLines ? 'crosshair' : (activeDimGrip ? 'grabbing' : (hoveredDimGrip || hoveredShrinkwrapPn || isOverDim ? 'pointer' : ((isSelectionMode || pendingSelectionCommand || isRotatingSelection || isScalingSelection || isMirroringSelection || isMovingSelection) ? (isOverLine ? 'pointer' : 'crosshair') : 'default'))))))), overflow: 'visible', isolation: 'isolate' }}
        >
            <canvas
                ref={gpuCanvasRef}
                aria-hidden="true"
                style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}
            />
            <canvas
                ref={canvasRef}
                className="w-full h-full gesture-capture relative z-10"
                onMouseDown={handleMouseDown}
                onDoubleClick={handleDoubleClick}
                onMouseMove={handleMouseMove}
                onMouseUp={handleMouseUp}
                onMouseLeave={handleMouseUp}
                onContextMenu={handleCanvasContextMenu}
                onTouchStart={handleTouchStart}
                onTouchMove={handleTouchMove}
                onTouchEnd={handleTouchEnd}
            />

            {inlineOsnapMenuPos && (
                <div
                    data-testid="inline-osnap-menu"
                    className="absolute z-50 w-56 bg-gray-900/95 border border-cyan-500/40 rounded-lg shadow-2xl p-2 text-xs"
                    style={{
                        left: inlineOsnapMenuPos.left,
                        top: inlineOsnapMenuPos.top,
                        width: inlineOsnapMenuPos.width,
                        maxHeight: inlineOsnapMenuPos.maxHeight,
                        overflowY: 'auto',
                    }}
                >
                    <div className="text-cyan-300 font-semibold px-1 pb-1 border-b border-cyan-500/20">
                        Shift+RightClick Snaps
                    </div>
                    <div className="mt-2">
                        <div className="text-gray-400 px-1 mb-1">Inline (next pick only)</div>
                        {OSNAP_ORDER.map(mode => (
                            <button
                                key={`inline-${mode}`}
                                onClick={() => {
                                    setInlineOsnapOverride(mode);
                                    setInlineOsnapMenuPos(null);
                                }}
                                className={`w-full text-left px-2 py-1 rounded mb-1 transition-colors ${inlineOsnapOverride === mode ? 'bg-cyan-700/70 text-cyan-100' : 'hover:bg-gray-800 text-gray-200'}`}
                            >
                                {OSNAP_LABELS[mode]}
                            </button>
                        ))}
                        <button
                            onClick={() => {
                                setInlineOsnapOverride(null);
                                setInlineOsnapMenuPos(null);
                            }}
                            className="w-full text-left px-2 py-1 rounded hover:bg-gray-800 text-gray-300"
                        >
                            Clear Inline Override
                        </button>
                    </div>
                    <div className="mt-2 pt-2 border-t border-cyan-500/20">
                        <div className="mb-1 flex items-center justify-between gap-2 px-1">
                            <span className="text-gray-400">Running snaps</span>
                            <button
                                type="button"
                                data-testid="inline-osnap-global-toggle"
                                aria-pressed={isRunningOsnapsEnabled}
                                onClick={toggleRunningOsnapsEnabled}
                                className={`rounded border px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider transition-colors ${
                                    isRunningOsnapsEnabled
                                        ? 'border-emerald-500/40 bg-emerald-500/10 text-emerald-300'
                                        : 'border-white/10 bg-white/[0.03] text-gray-500'
                                }`}
                            >
                                {isRunningOsnapsEnabled ? 'On' : 'Off'}
                            </button>
                        </div>
                        {OSNAP_ORDER.map(mode => {
                            const isLastSelected = runningOsnaps.size === 1 && runningOsnaps.has(mode);
                            return (
                                <label
                                    key={`running-${mode}`}
                                    className="flex items-center gap-2 px-2 py-1 rounded hover:bg-gray-800 cursor-pointer"
                                    title={isLastSelected ? 'Keep one snap type selected, or turn Running snaps off globally.' : undefined}
                                >
                                    <input
                                        type="checkbox"
                                        checked={runningOsnaps.has(mode)}
                                        disabled={isLastSelected}
                                        onChange={() => toggleRunningOsnap(mode)}
                                        className="accent-cyan-500"
                                    />
                                    <span className="text-gray-200">{OSNAP_LABELS[mode]}</span>
                                </label>
                            );
                        })}
                    </div>
                </div>
            )}

            {settings.googleMaps?.enabled && googleOverlayDiagnostic && (
                <button
                    type="button"
                    className={`absolute top-3 right-3 z-30 max-w-[540px] text-left px-3 py-2 rounded border text-xs font-mono shadow-lg transition-colors ${
                        googleOverlayDiagnostic.tone === 'error'
                            ? 'bg-rose-900/80 border-rose-400/50 text-rose-100 hover:bg-rose-900/90'
                            : 'bg-amber-900/80 border-amber-400/50 text-amber-100 hover:bg-amber-900/90'
                    }`}
                    title="Map imagery diagnostics (click to open map settings)"
                    onClick={() => {
                        if (!onShowSettings) return;
                        onShowSettings();
                        setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('landsurv-open-settings-google-maps'));
                        }, 80);
                    }}
                >
                    <span>{googleOverlayDiagnostic.message}</span>
                </button>
            )}

            {pendingTiles > 0 && (
                <div
                    role="status"
                    aria-live="polite"
                    className="absolute z-30 px-2.5 py-1 rounded bg-gray-900/85 border border-cyan-500/40 text-[11px] text-cyan-100 font-mono flex items-center gap-2 pointer-events-none"
                    style={{ right: 12, bottom: 56 }}
                >
                    <svg viewBox="0 0 24 24" className="w-3 h-3 animate-spin text-cyan-300" fill="none" stroke="currentColor" strokeWidth="3" aria-hidden="true">
                        <circle cx="12" cy="12" r="9" opacity="0.25" />
                        <path d="M21 12a9 9 0 0 0-9-9" strokeLinecap="round" />
                    </svg>
                    <span>Downloading map tiles ({pendingTiles} left)</span>
                </div>
            )}

            {googleOverlayImage && settings.googleMaps?.enabled && (() => {
                const sourceLabel =
                    googleOverlayImage.source === 'naip-proxy' ? 'NAIP proxy'
                    : googleOverlayImage.source === 'user-maps' ? 'User Maps key'
                    : googleOverlayImage.source === 'user-gemini' ? 'User AI key'
                    : 'Server proxy';
                const mapTypeLabel =
                    googleOverlayImage.mapType === 'naip' ? 'NAIP'
                    : googleOverlayImage.mapType === 'hybrid' ? 'Hybrid'
                    : googleOverlayImage.mapType === 'satellite' ? 'Satellite'
                    : googleOverlayImage.mapType === 'terrain' ? 'Terrain'
                    : 'Roadmap';
                return (
                    <button
                        type="button"
                        className="absolute z-30 px-2 py-1 rounded bg-gray-900/85 border border-emerald-500/30 text-[10px] text-emerald-100 font-mono hover:bg-gray-800/90 transition-colors flex items-center gap-1"
                        style={{ right: 12, bottom: 28 }}
                        title="Active basemap imagery (click to open map settings)"
                        onClick={() => {
                            if (!onShowSettings) return;
                            onShowSettings();
                            setTimeout(() => {
                                window.dispatchEvent(new CustomEvent('landsurv-open-settings-google-maps'));
                            }, 80);
                        }}
                    >
                        <svg viewBox="0 0 24 24" className="w-3 h-3 text-emerald-300/90" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <path d="M12 2v3" />
                            <path d="M12 19v3" />
                            <path d="M4.93 4.93l2.12 2.12" />
                            <path d="M16.95 16.95l2.12 2.12" />
                            <path d="M2 12h3" />
                            <path d="M19 12h3" />
                            <path d="M4.93 19.07l2.12-2.12" />
                            <path d="M16.95 7.05l2.12-2.12" />
                            <circle cx="12" cy="12" r="3" />
                        </svg>
                        <span>{`IMAGERY: ${mapTypeLabel} | SOURCE: ${sourceLabel}`}</span>
                    </button>
                );
            })()}

            {shiftBoxSelect && (() => {
                const left = Math.min(shiftBoxSelect.startX, shiftBoxSelect.endX);
                const top = Math.min(shiftBoxSelect.startY, shiftBoxSelect.endY);
                const width = Math.abs(shiftBoxSelect.endX - shiftBoxSelect.startX);
                const height = Math.abs(shiftBoxSelect.endY - shiftBoxSelect.startY);
                const isWindow = shiftBoxSelect.endX >= shiftBoxSelect.startX;
                return (
                    <div
                        className="absolute pointer-events-none z-40"
                        style={{
                            left,
                            top,
                            width,
                            height,
                            border: isWindow ? '1.5px solid rgba(59,130,246,0.95)' : '1.5px dashed rgba(34,197,94,0.95)',
                            background: isWindow ? 'rgba(59,130,246,0.20)' : 'rgba(34,197,94,0.20)',
                        }}
                    />
                );
            })()}

            {/* Shift+hover quick-info tooltip */}
            {shiftHoverPoints.length > 0 && shiftHoverPos && (() => {
                const container = containerRef.current;
                const rect = container?.getBoundingClientRect();
                const tooltipX = rect ? shiftHoverPos.clientX - rect.left + 16 : 16;
                const tooltipY = rect ? shiftHoverPos.clientY - rect.top - 10  : 0;
                return (
                    <div
                        className="absolute z-50 pointer-events-none select-none"
                        style={{ left: tooltipX, top: tooltipY, transform: 'translateY(-100%)' }}
                    >
                        <div className="bg-gray-900/95 border border-gray-600 rounded-lg shadow-xl px-3 py-2 text-xs font-mono min-w-[200px] max-w-[320px]">
                            <div className="text-gray-400 text-[10px] mb-1 tracking-wide uppercase">
                                {shiftHoverPoints.length === 1 ? '1 point' : `${shiftHoverPoints.length} points`} nearby
                            </div>
                            <table className="w-full border-collapse">
                                <tbody>
                                    {shiftHoverPoints.map(p => (
                                        <tr key={p.pointNumber} className="border-t border-gray-700/50 first:border-t-0">
                                            <td className="py-0.5 pr-2 text-amber-400 font-bold whitespace-nowrap">{p.pointNumber}</td>
                                            <td className="py-0.5 pr-2 text-gray-200 truncate max-w-[120px]" title={p.description}>{p.description || '—'}</td>
                                            <td className="py-0.5 text-cyan-400 whitespace-nowrap text-right">
                                                {p.elevation != null && p.elevation !== 0 ? p.elevation.toFixed(2) : '—'}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                );
            })()}

            {/* Standardized Dynamic Cursor HUD for geometry input */}
            {cursorScreenPos && (() => {
                const isDrawingActive = 
                    drawingMode !== 'none' ||
                    isSelectionMode ||
                    !!activeGrip ||
                    isPickingOtOrigin ||
                    isPickingOtLineAlign ||
                    isTrimmingLines ||
                    isExtendingLines;

                if (!isDrawingActive) return null;
                // Polygon uses its own operation-anchored input strip below.
                if (drawingMode === 'polygon') return null;

                const unitAbbrev = getLinearUnitAbbreviation(settings.linearUnits);
                const mouseWorld = screenToWorld(cursorScreenPos.x, cursorScreenPos.y);
                const container = containerRef.current;
                const containerW = container ? container.clientWidth : (typeof window !== 'undefined' ? window.innerWidth : 800);
                const containerH = container ? container.clientHeight : (typeof window !== 'undefined' ? window.innerHeight : 600);

                let hudLeft = cursorScreenPos.x + 20;
                let hudTop = cursorScreenPos.y + 20;
                if (hudLeft + 250 > containerW) hudLeft = Math.max(10, cursorScreenPos.x - 260);
                if (hudTop + 110 > containerH) hudTop = Math.max(10, cursorScreenPos.y - 110);

                let badgeText = '';
                let badgeColor = 'bg-cyan-900/80 text-cyan-200 border-cyan-500/50';
                let primaryInput: React.ReactNode = null;
                let secondaryText: React.ReactNode = null;

                if (activeGrip) {
                    badgeText = 'STRETCH';
                    badgeColor = 'bg-rose-900/80 text-rose-200 border-rose-500/50';
                    primaryInput = (
                        <span className="font-medium text-white">
                            Move {activeGrip.type.toUpperCase()} endpoint
                        </span>
                    );
                    secondaryText = (
                        <div className="flex items-center gap-1 text-[10px] text-gray-400">
                            {activeOsnapPoint ? (
                                <span className="text-amber-300 font-mono">◈ {activeOsnapPoint.mode} · </span>
                            ) : null}
                            <span>[Click] Place · [Esc] Cancel · [O] Ortho</span>
                        </div>
                    );
                } else if (isSelectionMode || pendingSelectionCommand) {
                    badgeText = pendingSelectionCommand ? pendingSelectionCommand.label : (isBoxSelectMode ? 'SELECT (BOX)' : 'SELECT');
                    badgeColor = pendingSelectionCommand ? 'bg-amber-900/80 text-amber-200 border-amber-500/50' : 'bg-cyan-900/80 text-cyan-200 border-cyan-500/50';
                    primaryInput = (
                        <span className="font-medium text-white">
                            {selectedLineIds.size > 0
                                ? `${selectedLineIds.size} ${selectedLineIds.size === 1 ? 'entity' : 'entities'} selected`
                                : (isBoxSelectMode ? 'Drag window/crossing box' : 'Click entity or drag box')}
                        </span>
                    );
                    secondaryText = (
                        <div className="flex items-center gap-1 text-[10px] text-gray-400">
                            {pendingSelectionCommand ? (
                                <>
                                    <span className="text-gray-300 font-semibold">[Enter]</span> Apply · <span className="text-gray-300 font-semibold">[Esc]</span> Cancel · <span className="text-gray-300 font-semibold">[MMB]</span> Pan
                                </>
                            ) : (
                                <>
                                    <span className="text-gray-300 font-semibold">[B]</span> Box · <span className="text-gray-300 font-semibold">[Dbl-Click]</span> Chain · <span className="text-gray-300 font-semibold">[D/Del]</span> Delete · <span className="text-gray-300 font-semibold">[S/Esc]</span> Exit
                                </>
                            )}
                        </div>
                    );
                } else if (drawingMode === 'circle') {
                    if (circleSubMode === 'ttr') {
                        badgeText = 'CIRCLE (TTR)';
                        badgeColor = 'bg-amber-900/80 text-amber-200 border-amber-500/50';
                        if (ttrStep === 1) {
                            primaryInput = <span className="font-medium text-white">Select first tangent object</span>;
                            secondaryText = (
                                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                    <span>Click line, arc, or circle</span> · <span className="text-gray-300 font-semibold">[T]</span> Center/Rad · <span className="text-gray-300 font-semibold">[Esc]</span> Cancel
                                </div>
                            );
                        } else if (ttrStep === 2) {
                            primaryInput = <span className="font-medium text-white">Select second tangent object</span>;
                            secondaryText = (
                                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                    <span>Click line, arc, or circle</span> · <span className="text-gray-300 font-semibold">[Esc]</span> Back
                                </div>
                            );
                        } else {
                            const currentP = cursorWorldPosRef.current || { x: mouseWorld.easting, y: mouseWorld.northing };
                            const defaultR = ttrEntity2 ? Math.hypot(currentP.x - ttrEntity2.pickPoint.x, currentP.y - ttrEntity2.pickPoint.y) : 25;
                            let rVal = defaultR;
                            if (typedCircleValue.trim().length > 0) {
                                const num = Number(typedCircleValue);
                                if (Number.isFinite(num) && num > 0) {
                                    const fromUnit: LinearUnit = settings.linearUnits || 'usSurveyFoot';
                                    rVal = convertLinearUnits(num, fromUnit, 'foot');
                                }
                            }
                            if (circleSizeParam === 'diameter') rVal = rVal / 2;
                            const sol = ttrEntity1 && ttrEntity2 ? solveTtrCircle(ttrEntity1, ttrEntity2, rVal) : null;
                            const displayVal = typedCircleValue.length > 0 ? typedCircleValue : (circleSizeParam === 'diameter' ? rVal * 2 : rVal).toFixed(2);

                            primaryInput = (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-300 text-[11px] font-semibold">{circleSizeParam === 'diameter' ? 'Diameter:' : 'Radius:'}</span>
                                    <div className="px-1.5 py-0.5 rounded bg-gray-900 border border-amber-400 text-amber-300 font-mono font-bold text-xs flex items-center gap-1 shadow-inner">
                                        <span>{displayVal}</span>
                                        <span className="text-[10px] text-amber-400 font-normal">{unitAbbrev}</span>
                                    </div>
                                </div>
                            );
                            secondaryText = sol ? (
                                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                    <span className="text-emerald-400 font-semibold">✓ Tangent found</span> · <span className="text-cyan-300 font-semibold">[Enter]</span> Commit · <span className="text-gray-300 font-semibold">[D]</span> Diam
                                </div>
                            ) : (
                                <div className="flex items-center gap-1 text-[10px] text-amber-400">
                                    <span>⚠ No tangent circle for this radius</span> · <span className="text-gray-300 font-semibold">[D]</span> Diam
                                </div>
                            );
                        }
                    } else {
                        badgeText = 'CIRCLE';
                        badgeColor = 'bg-sky-900/80 text-sky-200 border-sky-500/50';
                        if (!circleCenter) {
                            primaryInput = <span className="font-medium text-white">Specify center point</span>;
                            secondaryText = activeOsnapPoint
                                ? <span className="text-amber-300 font-mono">◈ {activeOsnapPoint.mode} · <span className="text-gray-300 font-semibold">[T]</span> TTR</span>
                                : <span className="text-gray-400 font-mono">E: {mouseWorld.easting.toFixed(2)}, N: {mouseWorld.northing.toFixed(2)} · <span className="text-gray-300 font-semibold">[T]</span> TTR</span>;
                        } else {
                            const currentP = cursorWorldPosRef.current || { x: mouseWorld.easting, y: mouseWorld.northing };
                            const liveRadius = Math.hypot(currentP.x - circleCenter.easting, currentP.y - circleCenter.northing);
                            const liveVal = circleSizeParam === 'diameter' ? liveRadius * 2 : liveRadius;
                            const displayVal = typedCircleValue.length > 0 ? typedCircleValue : liveVal.toFixed(2);

                            primaryInput = (
                                <div className="flex items-center gap-1.5">
                                    <span className="text-gray-300 text-[11px] font-semibold">{circleSizeParam === 'diameter' ? 'Diameter:' : 'Radius:'}</span>
                                    <div className="px-1.5 py-0.5 rounded bg-gray-900 border border-sky-400 text-sky-300 font-mono font-bold text-xs flex items-center gap-1 shadow-inner">
                                        <span>{displayVal}</span>
                                        <span className="text-[10px] text-sky-400 font-normal">{unitAbbrev}</span>
                                    </div>
                                </div>
                            );
                            secondaryText = (
                                <div className="flex items-center gap-1 text-[10px] text-gray-400">
                                    <span className="text-gray-300 font-semibold">[D]</span> Diam / <span className="text-gray-300 font-semibold">[R]</span> Rad · <span className="text-cyan-300 font-semibold">[Enter]</span> Commit · <span className="text-gray-300 font-semibold">[T]</span> TTR
                                </div>
                            );
                        }
                    }
                } else if (isLinearDrawingMode(drawingMode)) {
                    const modeLabel = drawingMode === 'breaklines' ? 'BREAKLINE' : drawingMode === 'inclusion' ? 'INCLUSION' : drawingMode === 'exclusion' ? 'EXCLUSION' : 'POLYLINE';
                    badgeText = modeLabel;
                    badgeColor = drawingMode === 'breaklines' ? 'bg-pink-900/80 text-pink-200 border-pink-500/50'
                        : drawingMode === 'inclusion' ? 'bg-emerald-900/80 text-emerald-200 border-emerald-500/50'
                        : drawingMode === 'exclusion' ? 'bg-rose-900/80 text-rose-200 border-rose-500/50'
                        : 'bg-yellow-900/80 text-yellow-200 border-yellow-500/50';

                    if (polylinePoints.length === 0) {
                        primaryInput = <span className="font-medium text-white">Specify start point</span>;
                        secondaryText = activeOsnapPoint
                            ? <span className="text-amber-300 font-mono">◈ {activeOsnapPoint.mode}</span>
                            : <span className="text-gray-400 font-mono">E: {mouseWorld.easting.toFixed(2)}, N: {mouseWorld.northing.toFixed(2)}</span>;
                    } else {
                        const anchor = polylinePoints[polylinePoints.length - 1];
                        const currentP = cursorWorldPosRef.current || { x: mouseWorld.easting, y: mouseWorld.northing };
                        const liveDist = Math.hypot(currentP.x - anchor.easting, currentP.y - anchor.northing);
                        const inv = inverse(anchor, { easting: currentP.x, northing: currentP.y });
                        const bearingStr = formatBearing(inv.bearing);
                        const displayDist = typedSegmentLength.length > 0 ? typedSegmentLength : liveDist.toFixed(2);

                        primaryInput = (
                            <div className="flex items-center gap-1.5">
                                <span className="text-gray-300 text-[11px] font-semibold">Length:</span>
                                <div className="px-1.5 py-0.5 rounded bg-gray-900 border border-yellow-400 text-yellow-300 font-mono font-bold text-xs flex items-center gap-1 shadow-inner">
                                    <span>{displayDist}</span>
                                    <span className="text-[10px] text-yellow-500 font-normal">{unitAbbrev}</span>
                                </div>
                            </div>
                        );
                        secondaryText = (
                            <div className="flex flex-col gap-0.5">
                                <span className="text-cyan-300 font-mono text-[11px] font-medium">{bearingStr}</span>
                                <span className="text-[10px] text-gray-400"><span className="text-gray-300 font-semibold">[Enter]</span> Commit · <span className="text-gray-300 font-semibold">[O]</span> Ortho {isOrthoEnabled ? 'ON' : 'OFF'}</span>
                            </div>
                        );
                    }
                } else if (isDimensionDrawingMode(drawingMode)) {
                    badgeText = drawingMode.replace('-dim', '').toUpperCase();
                    badgeColor = 'bg-cyan-900/80 text-cyan-200 border-cyan-500/50';
                    let promptStr = '';
                    if (drawingMode === 'radius-dim' || drawingMode === 'diameter-dim' || drawingMode === 'arc-length-dim' || drawingMode === 'curve-data-dim') {
                        promptStr = dimPhase === 0 ? 'Click curve or circle' : 'Click label location';
                    } else if (drawingMode === 'angular-dim') {
                        promptStr = dimPhase === 0 ? 'Pick vertex' : dimPhase === 1 ? 'Pick ray 1' : 'Pick ray 2';
                    } else if (drawingMode === 'ordinate-dim') {
                        promptStr = dimPhase === 0 ? 'Pick feature point' : 'Pick leader location';
                    } else {
                        promptStr = dimPhase === 0 ? 'Specify 1st point' : dimPhase === 1 ? 'Specify 2nd point' : 'Specify offset';
                    }
                    primaryInput = <span className="font-medium text-white">{promptStr}</span>;
                    secondaryText = activeOsnapPoint ? <span className="text-amber-300 font-mono">◈ {activeOsnapPoint.mode}</span> : null;
                } else if (drawingMode === 'add-point') {
                    badgeText = 'ADD POINT';
                    badgeColor = 'bg-emerald-900/80 text-emerald-200 border-emerald-500/50';
                    primaryInput = <span className="font-medium text-white">Click canvas to drop PT {addPointNumber || nextAvailablePointNumber || '1'}</span>;
                    secondaryText = activeOsnapPoint ? <span className="text-amber-300 font-mono">◈ {activeOsnapPoint.mode} (snapping)</span> : null;
                } else if (drawingMode === 'boundary-line') {
                    badgeText = 'BOUNDARY';
                    badgeColor = 'bg-amber-900/80 text-amber-200 border-amber-500/50';
                    primaryInput = <span className="font-medium text-white">{!boundaryLineFrom ? 'Click start point (snap)' : 'Specify next call direction & distance'}</span>;
                } else if (isPickingOtOrigin) {
                    badgeText = 'OT ORIGIN';
                    badgeColor = 'bg-emerald-900/80 text-emerald-200 border-emerald-500/50';
                    primaryInput = <span className="font-medium text-white">Click to set OT Origin</span>;
                    secondaryText = activeOsnapPoint ? <span className="text-amber-300 font-mono">◈ {activeOsnapPoint.mode}</span> : null;
                } else if (isPickingOtLineAlign) {
                    badgeText = 'ALIGN TO LINE';
                    badgeColor = 'bg-indigo-900/80 text-indigo-200 border-indigo-500/50';
                    primaryInput = <span className="font-medium text-white">Click line to align rotation</span>;
                } else if (isTrimmingLines) {
                    badgeText = 'TRIM';
                    badgeColor = 'bg-orange-900/80 text-orange-200 border-orange-500/50';
                    primaryInput = <span className="font-medium text-white">Click line segment to trim</span>;
                } else if (isExtendingLines) {
                    badgeText = 'EXTEND';
                    badgeColor = 'bg-blue-900/80 text-blue-200 border-blue-500/50';
                    primaryInput = <span className="font-medium text-white">{!extendFromLine ? 'Click line to extend' : 'Click boundary/line to extend to'}</span>;
                }

                return (
                    <div
                        className="absolute z-50 pointer-events-none select-none transition-all duration-75 ease-out"
                        style={{ left: hudLeft, top: hudTop }}
                    >
                        <div className="bg-gray-950/95 border border-cyan-500/40 rounded-lg shadow-2xl px-2.5 py-1.5 text-xs backdrop-blur-md flex flex-col gap-1 min-w-[160px] max-w-[280px]">
                            <div className="flex items-center justify-between gap-2">
                                <span className={`px-1.5 py-0.2 text-[9px] font-bold rounded border uppercase tracking-wide ${badgeColor}`}>
                                    {badgeText}
                                </span>
                                {activeOsnapPoint && (
                                    <span className="text-[10px] text-amber-400 font-mono font-semibold flex items-center gap-0.5">
                                        ◈ {activeOsnapPoint.mode}
                                    </span>
                                )}
                            </div>
                            {primaryInput}
                            {secondaryText}
                        </div>
                    </div>
                );
            })()}
            
            {/* POLYGON operation input — anchored to the polygon center */}
            {drawingMode === 'polygon' && polygonCenter && (() => {
                const container = containerRef.current;
                const containerW = container ? container.clientWidth : (typeof window !== 'undefined' ? window.innerWidth : 800);
                const containerH = container ? container.clientHeight : (typeof window !== 'undefined' ? window.innerHeight : 600);
                const anchor = worldToScreen(polygonCenter.easting, polygonCenter.northing);
                const hudW = 286;
                const hudH = 58;
                let left = anchor.x + 26;
                let top = anchor.y + 26;
                if (left + hudW > containerW - 10) left = anchor.x - hudW - 26;
                if (top + hudH > containerH - 82) top = anchor.y - hudH - 26;
                left = Math.max(10, Math.min(left, Math.max(10, containerW - hudW - 10)));
                top = Math.max(10, Math.min(top, Math.max(10, containerH - hudH - 82)));
                const pointer = cursorWorldPosRef.current;
                const liveDiameter = pointer
                    ? convertLinearUnits(
                        2 * Math.hypot(pointer.x - polygonCenter.easting, pointer.y - polygonCenter.northing),
                        'foot',
                        settings.linearUnits || 'usSurveyFoot',
                    )
                    : null;
                return (
                    <PolygonInputHud
                        x={left}
                        y={top}
                        sides={polygonSides}
                        typedDiameter={typedPolygonDiameter}
                        liveDiameter={liveDiameter}
                        unitLabel={getLinearUnitAbbreviation(settings.linearUnits)}
                        onSidesChange={sides => {
                            setPolygonSides(sides);
                            requestAnimationFrame(() => drawRef.current?.());
                        }}
                        onDiameterChange={value => {
                            setTypedPolygonDiameter(value);
                            requestAnimationFrame(() => drawRef.current?.());
                        }}
                        onApply={text => {
                            const parsed = Number(text);
                            if (!Number.isFinite(parsed) || parsed <= 0) {
                                setTerminalIncoming({ text: 'POLYGON: Diameter must be a positive number', tone: 'error' });
                                return;
                            }
                            setTypedPolygonDiameter(text);
                            commitPolygonFromRadius(parsed / 2);
                        }}
                        onCancel={() => {
                            setPolygonCenter(null);
                            setTypedPolygonDiameter('');
                            setActiveOsnapPoint(null);
                            setTerminalIncoming({ text: `POLYGON: Pick the center [${polygonSides} sides]`, tone: 'info' });
                            requestAnimationFrame(() => drawRef.current?.());
                        }}
                    />
                );
            })()}

            {/* Line Type Status Banner */}
            {false && drawingMode !== 'none' && (
                <div className={`absolute top-0 left-0 right-0 z-30 p-3 flex items-center justify-center gap-4 ${
                    drawingMode === 'breaklines' ? 'bg-pink-600/90' : 
                    drawingMode === 'inclusion' ? 'bg-green-600/90' : 
                    drawingMode === 'exclusion' ? 'bg-red-600/90' :
                    'bg-yellow-600/90'
                } backdrop-blur-sm`}>
                    <span className="text-white font-semibold text-lg">
                        {drawingMode === 'polylines' && '✏️ Drawing Polylines'}
                        {drawingMode === 'breaklines' && '🟥 Drawing Breaklines'} 
                        {drawingMode === 'inclusion' && '🟩 Drawing Inclusion Lines'}
                        {drawingMode === 'exclusion' && '🟥 Drawing Exclusion Lines'}
                    </span>
                    <span className="text-white/80 text-sm">Click points to draw. Click last point again or press Escape to finish.</span>
                </div>
            )}

            {/* Corner-align tool — markers, nest picker and status banner. */}
            {drawingMode === 'boundary-corner-align' && cornerAlignBfId && (() => {
                const toScreen = (e: number, n: number) => ({
                    x: e * transform.scale + transform.offsetX,
                    y: -n * transform.scale + transform.offsetY,
                });
                const corners = cornerAlignCorners;
                const pairedCornerPns = new Set(cornerAlignPairs.map(p => p.pn));
                const stage: 'corner' | 'point' = cornerAlignPendingCorner ? 'point' : 'corner';
                return (
                    <>
                        {/* Selectable deed corners */}
                        <svg className="absolute inset-0 w-full h-full pointer-events-none z-30" style={{ overflow: 'visible' }}>
                            {corners.map(c => {
                                const s = toScreen(c.x, c.y);
                                const isPaired = pairedCornerPns.has(c.pn);
                                const isPending = cornerAlignPendingCorner?.pn === c.pn;
                                const isHover = cornerAlignHover?.pn === c.pn;
                                const color = isPending ? '#f472b6' : isPaired ? '#34d399' : '#60a5fa';
                                const r = isPending || isHover ? 9 : 6;
                                return (
                                    <g key={`ca-corner-${c.pn}`}>
                                        <rect
                                            x={s.x - r} y={s.y - r} width={r * 2} height={r * 2}
                                            fill={isPending ? 'rgba(244,114,182,0.28)' : 'rgba(96,165,250,0.16)'}
                                            stroke={color}
                                            strokeWidth={isPending || isHover ? 2.5 : 1.5}
                                        />
                                        {(isPending || isHover) && (
                                            <text x={s.x + r + 4} y={s.y - r - 2} fill={color} fontSize={11} fontFamily="monospace">
                                                {c.pn}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                            {/* Committed corner → found-point tie lines */}
                            {cornerAlignPairs.map(p => {
                                const t = toScreen(p.targetE, p.targetN);
                                const pairedCorner = corners.find(c => c.pn === p.pn);
                                const cs = pairedCorner ? toScreen(pairedCorner.x, pairedCorner.y) : null;
                                return (
                                    <g key={`ca-pair-${p.pn}-${p.targetPn}`}>
                                        <circle cx={t.x} cy={t.y} r={7} fill="none" stroke="#34d399" strokeWidth={2} />
                                        <text x={t.x + 10} y={t.y + 4} fill="#34d399" fontSize={11} fontFamily="monospace">
                                            {p.pn}→{p.targetPn}
                                        </text>
                                        {cs && (Math.abs(cs.x - t.x) > 1 || Math.abs(cs.y - t.y) > 1) && (
                                            <line
                                                x1={cs.x} y1={cs.y}
                                                x2={t.x} y2={t.y}
                                                stroke="#34d399"
                                                strokeWidth={1.5}
                                                strokeDasharray="4 2"
                                            />
                                        )}
                                    </g>
                                );
                            })}
                        </svg>

                        {/* Nest picker — Shift+click (or any ambiguous click) lists every
                            point stacked under the cursor so the right monument is chosen. */}
                        {cornerAlignPicker && (
                            <div
                                className="absolute z-50 bg-gray-900/97 border border-pink-500/50 rounded-lg shadow-2xl text-xs overflow-hidden"
                                style={{
                                    left: Math.min(cornerAlignPicker.x + 12, Math.max(0, (containerRef.current?.clientWidth ?? 0) - 280)),
                                    top: Math.min(cornerAlignPicker.y + 12, Math.max(0, (containerRef.current?.clientHeight ?? 0) - 240)),
                                    width: 268,
                                }}
                                onMouseDown={ev => ev.stopPropagation()}
                            >
                                <div className="px-3 py-1.5 bg-pink-900/50 border-b border-pink-500/30 text-pink-100 font-semibold">
                                    {cornerAlignPicker.items.length} point{cornerAlignPicker.items.length === 1 ? '' : 's'} in this nest
                                    <span className="block text-[10px] font-normal text-pink-200/70">
                                        Pin corner {cornerAlignPendingCorner?.pn} to…
                                    </span>
                                </div>
                                <div className="max-h-56 overflow-y-auto">
                                    {cornerAlignPicker.items.map(p => (
                                        <button
                                            key={p.pointNumber}
                                            type="button"
                                            onClick={() => commitCornerAlignPoint(p)}
                                            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-pink-800/40 border-b border-gray-700/40 last:border-b-0"
                                        >
                                            <span className="text-amber-400 font-mono font-bold w-14 shrink-0">{p.pointNumber}</span>
                                            <span className="text-gray-200 truncate flex-1" title={p.description}>{p.description || '—'}</span>
                                            <span className="text-cyan-400 font-mono shrink-0">
                                                {p.elevation != null && p.elevation !== 0 ? p.elevation.toFixed(2) : '—'}
                                            </span>
                                        </button>
                                    ))}
                                </div>
                                <button
                                    type="button"
                                    onClick={() => setCornerAlignPicker(null)}
                                    className="w-full px-3 py-1 bg-gray-800 hover:bg-gray-700 text-gray-300 text-[11px]"
                                >
                                    Cancel
                                </button>
                            </div>
                        )}

                        {/* Status banner + running pair list */}
                        <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 w-[520px] max-w-[92%] rounded-lg shadow-lg bg-indigo-950/90 border border-indigo-400/60 backdrop-blur-sm">
                            <div className="flex items-center gap-3 px-4 py-2">
                                <span className="text-indigo-100 text-sm font-semibold flex-1">
                                    {stage === 'corner'
                                        ? '⌖ Click a CORNER of the highlighted deed'
                                        : `⌖ Corner ${cornerAlignPendingCorner?.pn} — now click the found point it belongs on`}
                                </span>
                                {cornerAlignPairs.length > 0 && (
                                    <button
                                        onClick={() => {
                                            const next = cornerAlignPairs.slice(0, -1);
                                            setCornerAlignPairs(next);
                                            setCornerAlignPendingCorner(null);
                                            if (next.length > 0) {
                                                applyCornerAlign(cornerAlignBfId, next);
                                            } else if (cornerAlignBaseTxRef.current) {
                                                const cache = bfGeometryCacheRef.current.get(cornerAlignBfId);
                                                if (cache) {
                                                    cache.txE = cornerAlignBaseTxRef.current.translationE;
                                                    cache.txN = cornerAlignBaseTxRef.current.translationN;
                                                    cache.rotDeg = cornerAlignBaseTxRef.current.rotationDeg;
                                                }
                                                props.onUpdateBoundaryTransform?.(cornerAlignBfId, cornerAlignBaseTxRef.current, { immediate: true });
                                                setCornerAlignFit(null);
                                            }
                                        }}
                                        className="px-2 py-0.5 rounded text-xs bg-indigo-800 hover:bg-indigo-700 text-indigo-100 border border-indigo-400/50"
                                        title="Remove the last corner ↔ point pair and re-fit"
                                    >
                                        Undo pair
                                    </button>
                                )}
                                <button
                                    onClick={() => {
                                        const ids = cornerAlignPairs.map(p => p.targetPn);
                                        const bfId = cornerAlignBfId;
                                        setDrawingMode('none');
                                        resetCornerAlign(false);
                                        if (ids.length > 0) props.onBoundaryCornerAlignCommitted?.(bfId, ids);
                                        props.onBoundaryCornerAlignRequestHandled?.();
                                    }}
                                    disabled={cornerAlignPairs.length === 0}
                                    className="px-2 py-0.5 rounded text-xs bg-emerald-700 hover:bg-emerald-600 disabled:opacity-40 disabled:hover:bg-emerald-700 text-white border border-emerald-400/60"
                                    title="Keep the current fit and exit the tool"
                                >
                                    Done
                                </button>
                                <button
                                    onClick={() => {
                                        setDrawingMode('none');
                                        resetCornerAlign(true);
                                        props.onBoundaryCornerAlignRequestHandled?.();
                                    }}
                                    className="px-2 py-0.5 rounded text-xs bg-gray-700 hover:bg-gray-600 text-white border border-gray-500/60"
                                    title="Discard the fit and restore the boundary's original position"
                                >
                                    Cancel
                                </button>
                            </div>
                            <div className="px-4 pb-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-indigo-200/80">
                                <span>Shift+click a found point to pick from a nest of stacked points.</span>
                                {cornerAlignPairs.length > 0 && (
                                    <span className="font-mono text-emerald-300">
                                        {cornerAlignPairs.length} pair{cornerAlignPairs.length === 1 ? '' : 's'}
                                        {cornerAlignPairs.length === 1 ? ' (translation only — add a 2nd for rotation)' : ''}
                                    </span>
                                )}
                                {cornerAlignFit && cornerAlignPairs.length >= 2 && (
                                    <span className="font-mono text-amber-300">
                                        RMS {cornerAlignFit.rmse.toFixed(2)}′ · max {cornerAlignFit.maxResidual.toFixed(2)}′ · rot {cornerAlignFit.rotDeg.toFixed(4)}°
                                    </span>
                                )}
                            </div>
                        </div>
                    </>
                );
            })()}

            {/* Boundary-align status banner — visible while the tool is armed. */}
            {drawingMode === 'boundary-align' && (
                <div className="absolute top-3 left-1/2 -translate-x-1/2 z-40 flex items-center gap-3 px-4 py-2 rounded-lg shadow-lg bg-blue-900/85 border border-blue-400/60 backdrop-blur-sm">
                    <span className="text-blue-100 text-sm font-semibold">
                        {boundaryAlignMovable && boundaryAlignMovable.callId === '__pending__'
                            ? '↔ Align: click a line on THIS boundary to use as the source'
                            : !boundaryAlignTarget
                                ? '↔ Align: now click the matching line on the OTHER deed'
                                : boundaryAlignSliding
                                    ? '↔ Slide along line — corners snap to target. Press R to rotate 180°. Click to commit.'
                                    : '↔ Aligning…'}
                    </span>
                    <button
                        onClick={() => {
                            setDrawingMode('none');
                            setBoundaryAlignMovable(null);
                            setBoundaryAlignTarget(null);
                            setBoundaryAlignSliding(false);
                            setBoundaryAlignFlipped(false);
                            boundaryAlignSlideRef.current = null;
                            props.onBoundaryAlignRequestHandled?.();
                        }}
                        className="px-2 py-0.5 rounded text-xs bg-blue-700 hover:bg-blue-600 text-white border border-blue-400/60"
                        title="Cancel align"
                    >
                        Cancel
                    </button>
                </div>
            )}
            
            {/* Line Type Indicators Legend */}
            {false && drawingMode !== 'none' && (
                <div className="absolute top-16 left-4 z-20 bg-gray-900/80 backdrop-blur-md rounded-lg border border-gray-700/50 shadow-lg p-3">
                    <div className="text-xs font-semibold text-gray-300 mb-2">Line Type Legend:</div>
                    <div className="flex flex-col gap-1.5 text-xs">
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-pink-500"></div>
                            <span className="text-gray-300">Breaklines</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-green-500"></div>
                            <span className="text-gray-300">Inclusion Lines</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-red-500"></div>
                            <span className="text-gray-300">Exclusion Lines</span>
                        </div>
                        <div className="flex items-center gap-2">
                            <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                            <span className="text-gray-300">Normal Lines</span>
                        </div>
                    </div>
                </div>
            )}
            
            <NorthArrow 
                style={settings.northArrowStyle} 
                position={settings.northArrowPosition} 
                isLightTheme={settings.theme === 'light'} 
                rotationRad={orientationTuple?.viewAngle ?? (orientationTuple?.mode === 'view' ? (orientationTuple?.angle ?? 0) : 0)}
                onClick={onShowSettings} 
            />

            {/* Selection hints live in the command line — no floating banner. */}

            {/* Selection Properties dialog (opened from the canvas header) */}
            <PropertiesDialog
                isOpen={isPropertiesOpen}
                onClose={() => setIsPropertiesOpen(false)}
                selectedIds={Array.from(selectedLineIds)}
                lines={lines}
                availableLayers={props.availableLayers}
                onUpdateLines={handleUpdateSelectedLines}
                onDeleteLines={(ids) => {
                    ids.forEach(id => onDeleteLine(id));
                    setSelectedLineIds(new Set());
                    requestAnimationFrame(() => drawRef.current?.());
                }}
                onSelectAll={() => { selectAllEntitiesCommand(); }}
                onClearSelection={deselectAllCommand}
                onConvertToBoundary={onConvertSelectionToBoundary
                    ? (sel) => {
                        onConvertSelectionToBoundary(sel);
                        setSelectedLineIds(new Set());
                    }
                    : undefined}
            />

            {/* Line Selection Info Panel — retired by default; the Properties dialog replaces it. */}
            {isSelectionInfoEnabled && selectedLineIds.size > 0 && isSelectionMode && (() => {
                const selId = [...selectedLineIds][0];
                const selLine = lines.find(l => (l.id || `${l.from}-${l.to}`) === selId);
                if (!selLine) return null;
                const currentLayer = selLine.layer || '(no layer)';
                const defaultPos = { x: Math.max(0, window.innerWidth / 2 - 140), y: window.innerHeight - 220 };
                const panelPos = lineQueryPanelPos || defaultPos;
                const handleDragStart = (e: React.MouseEvent) => {
                    e.preventDefault();
                    lineQueryDragState.current = { dragging: true, startX: e.clientX, startY: e.clientY, startPosX: panelPos.x, startPosY: panelPos.y };
                    const onMove = (ev: MouseEvent) => {
                        if (!lineQueryDragState.current.dragging) return;
                        setLineQueryPanelPos({ x: lineQueryDragState.current.startPosX + (ev.clientX - lineQueryDragState.current.startX), y: lineQueryDragState.current.startPosY + (ev.clientY - lineQueryDragState.current.startY) });
                    };
                    const onUp = () => { lineQueryDragState.current.dragging = false; window.removeEventListener('mousemove', onMove); window.removeEventListener('mouseup', onUp); };
                    window.addEventListener('mousemove', onMove);
                    window.addEventListener('mouseup', onUp);
                };
                return (
                    <div className="fixed z-40 bg-gray-900/96 backdrop-blur-md border border-cyan-500/40 rounded-xl shadow-2xl text-xs text-gray-200 flex flex-col gap-0 min-w-[280px]" style={{ left: panelPos.x, top: panelPos.y }}>
                        <div className="flex items-center justify-between px-3 py-2 cursor-grab active:cursor-grabbing select-none border-b border-cyan-500/20 rounded-t-xl" onMouseDown={handleDragStart}>
                            <span className="font-semibold text-cyan-300">{selectedLineIds.size === 1 ? '1 line selected' : `${selectedLineIds.size} lines selected`}</span>
                            <button onClick={() => setSelectedLineIds(new Set())} className="p-0.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white" title="Clear selection">
                                <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
                            </button>
                        </div>
                        <div className="flex flex-col gap-2 p-3">
                        {selectedLineIds.size === 1 && (
                            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                                <span className="text-gray-400">From → To</span>
                                <span className="font-mono">{selLine.from} → {selLine.to}</span>
                                {selLine.bearing && <><span className="text-gray-400">Bearing</span><span className="font-mono">{selLine.bearing}</span></>}
                                {selLine.distance && <><span className="text-gray-400">Distance</span><span className="font-mono">{selLine.distance}</span></>}
                            </div>
                        )}
                        <div className="flex items-center gap-2">
                            <span className="text-gray-400 flex-shrink-0">Layer</span>
                            {props.availableLayers && props.availableLayers.length > 0 ? (
                                <select
                                    value={currentLayer === '(no layer)' ? '' : currentLayer}
                                    onChange={e => {
                                        if (props.onUpdateLine) {
                                            selectedLineIds.forEach(id => props.onUpdateLine!(id, { layer: e.target.value }));
                                        }
                                    }}
                                    className="flex-1 bg-gray-800 border border-gray-600 rounded px-2 py-1 text-xs font-mono text-cyan-200 focus:outline-none focus:border-cyan-500"
                                >
                                    <option value="">(no layer)</option>
                                    {props.availableLayers.map(l => <option key={l} value={l}>{l}</option>)}
                                </select>
                            ) : (
                                <span className="font-mono text-cyan-200">{currentLayer}</span>
                            )}
                        </div>
                        <p className="text-[10px] text-gray-500">Del/Backspace/D to delete · Esc to deselect · Alt+click label to flip · Shift+click label to swap bearing</p>
                        {onConvertSelectionToBoundary && (
                            <button
                                onClick={() => {
                                    const selected = lines.filter(l => selectedLineIds.has(l.id || `${l.from}-${l.to}`));
                                    onConvertSelectionToBoundary(selected);
                                    setSelectedLineIds(new Set());
                                }}
                                className="mt-1 w-full p-1.5 rounded-md text-xs flex items-center justify-center gap-2 bg-amber-700 hover:bg-amber-600 text-white"
                                title="Convert selected lines to a Boundary object in the Boundary Editor"
                            >
                                <svg viewBox="0 0 24 24" className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21l18-18M6 18l3-3M15 9l3-3"/><circle cx="3" cy="21" r="1.5" fill="currentColor"/><circle cx="21" cy="3" r="1.5" fill="currentColor"/></svg>
                                Convert to Boundary ({selectedLineIds.size})
                            </button>
                        )}
                        </div>
                    </div>
                );
            })()}

            {/* Dimension Right-Click Context Menu */}
            {dimContextMenu && (
                <div
                    className="fixed z-50 bg-gray-900 border border-amber-500/50 rounded-xl shadow-2xl p-1.5 text-xs text-gray-200 min-w-[170px] animate-fade-in"
                    style={{ left: dimContextMenu.x, top: dimContextMenu.y }}
                >
                    <button
                        onClick={() => {
                            onDeleteDimension?.(dimContextMenu.dimId);
                            if (selectedDimensionId === dimContextMenu.dimId) setSelectedDimensionId(null);
                            setDimContextMenu(null);
                            addNotification({
                                kind: 'dimension',
                                severity: 'info',
                                title: 'Dimension Deleted',
                            });
                        }}
                        className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-red-950/80 hover:text-red-300 flex items-center gap-2 text-red-400 font-medium transition-colors"
                    >
                        <TrashIcon className="w-3.5 h-3.5" />
                        Delete Dimension
                    </button>
                    <button
                        onClick={() => {
                            setIsDimSettingsOpen(true);
                            setDimContextMenu(null);
                        }}
                        className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-gray-800 flex items-center gap-2 text-gray-300 transition-colors"
                    >
                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="1.8">
                            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
                        </svg>
                        Dimension Style
                    </button>
                    <button
                        onClick={() => {
                            setSelectedDimensionId(null);
                            setDimContextMenu(null);
                        }}
                        className="w-full text-left px-2.5 py-1.5 rounded-lg hover:bg-gray-800 text-gray-400 hover:text-white transition-colors"
                    >
                        Deselect
                    </button>
                </div>
            )}

            {/* Dimension Selection Info & Delete Action Card */}
            {selectedDimensionId && !isDimSettingsOpen && (() => {
                const selDim = dimensions.find(d => d.id === selectedDimensionId);
                if (!selDim) return null;
                const typeLabel = selDim.type.toUpperCase().replace('-', ' ') + ' DIMENSION';
                let valueSummary = '';
                if (selDim.type === 'aligned' || selDim.type === 'horizontal' || selDim.type === 'vertical') {
                    const geom = calculateLinearDimensionGeometry(selDim);
                    valueSummary = `Measured: ${geom.dimensionText}`;
                } else if (selDim.type === 'radius') {
                    valueSummary = `Radius: R = ${selDim.radius.toFixed(2)}'`;
                } else if (selDim.type === 'diameter') {
                    valueSummary = `Diameter: Ø = ${(selDim.radius * 2).toFixed(2)}'`;
                } else if (selDim.type === 'arc-length') {
                    valueSummary = `Arc Length: L = ${selDim.arcLength.toFixed(2)}'`;
                } else if (selDim.type === 'curve-data') {
                    valueSummary = `Curve Data (R=${selDim.radius.toFixed(2)}', L=${selDim.arcLength.toFixed(2)}')`;
                } else if (selDim.type === 'angular') {
                    const geom = calculateAngularDimensionGeometry(selDim);
                    valueSummary = `Angle: ${geom.dimensionText}`;
                } else if (selDim.type === 'ordinate') {
                    valueSummary = `N: ${selDim.point.northing.toFixed(2)}, E: ${selDim.point.easting.toFixed(2)}`;
                }

                return (
                    <div className="fixed z-40 bottom-16 left-1/2 -translate-x-1/2 bg-gray-900/96 backdrop-blur-md border border-amber-500/60 rounded-xl shadow-2xl text-xs text-gray-200 flex flex-col gap-2 p-3 min-w-[340px] animate-fade-in-up">
                        <div className="flex items-center justify-between border-b border-white/[0.07] pb-1.5">
                            <span className="font-bold text-amber-400 flex items-center gap-1.5">
                                <span className="w-2 h-2 rounded-full bg-amber-400 animate-pulse" />
                                {typeLabel}
                            </span>
                            <button
                                onClick={() => setSelectedDimensionId(null)}
                                className="p-0.5 rounded hover:bg-gray-800 text-gray-400 hover:text-white"
                                title="Deselect"
                            >
                                <XMarkIcon className="w-3.5 h-3.5" />
                            </button>
                        </div>
                        <div className="flex items-center justify-between text-xs font-mono text-gray-300">
                            <span className="font-semibold text-white">{valueSummary}</span>
                            <span className="text-[10px] text-gray-500 font-sans">Del / D to delete</span>
                        </div>
                        <div className="flex items-center gap-2 pt-1">
                            <button
                                type="button"
                                onClick={() => setIsDimSettingsOpen(true)}
                                className="py-1.5 px-3 bg-cyan-950/70 hover:bg-cyan-900 text-cyan-300 border border-cyan-500/40 font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors shadow-sm"
                                title="Edit this dimension's style live in scene"
                            >
                                Style HUD
                            </button>
                            <button
                                type="button"
                                onClick={() => {
                                    onDeleteDimension?.(selDim.id);
                                    setSelectedDimensionId(null);
                                    addNotification({
                                        kind: 'dimension',
                                        severity: 'info',
                                        title: 'Dimension Deleted',
                                    });
                                }}
                                className="flex-1 py-1.5 px-3 bg-red-600 hover:bg-red-500 text-white font-semibold rounded-lg flex items-center justify-center gap-1.5 transition-colors shadow-md"
                            >
                                <TrashIcon className="w-3.5 h-3.5" />
                                Delete (D)
                            </button>
                            <button
                                type="button"
                                onClick={() => setSelectedDimensionId(null)}
                                className="py-1.5 px-3 bg-gray-800 hover:bg-gray-700 text-gray-300 rounded-lg transition-colors border border-gray-700"
                            >
                                Deselect
                            </button>
                        </div>
                    </div>
                );
            })()}

            {/* Boundary-Line Draw Dialog */}
            {drawingMode === 'boundary-line' && (
                <div className="absolute top-1/2 right-4 -translate-y-1/2 z-30 w-64 bg-gray-900/96 backdrop-blur-md border border-amber-500/50 rounded-xl shadow-2xl p-3 text-xs">
                    <div className="flex items-center justify-between mb-2">
                        <span className="font-semibold text-amber-300">Draw Boundary Line</span>
                        <button
                            onClick={() => { setDrawingMode('none'); resetBoundaryLineMode(); }}
                            className="p-0.5 rounded hover:bg-gray-700 text-gray-400 hover:text-white"
                            title="Exit boundary line mode"
                        >
                            <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
                        </button>
                    </div>
                    <div className="text-gray-400 mb-2 text-[10px] leading-snug">
                        {!boundaryLineFrom
                            ? 'Click an existing point to set the start.'
                            : !boundaryClickedTo && !boundaryBearing
                            ? <>From: <span className="text-amber-200 font-mono">{boundaryLineFrom.pointNumber}</span> — click anywhere or type bearing &amp; {boundaryCurveMode ? 'curve data' : 'distance'} below.</>
                            : <>From: <span className="text-amber-200 font-mono">{boundaryLineFrom.pointNumber}</span> — confirm or adjust, then click <strong>Add</strong>. Chains automatically.</> }
                    </div>
                    {/* Curve mode toggle */}
                    <div className="flex items-center gap-2 mb-2">
                        <button
                            onClick={() => { setBoundaryCurveMode(m => !m); setBoundaryInputError(''); setBoundaryClosureResult(null); }}
                            className={`flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border transition-colors ${boundaryCurveMode ? 'bg-sky-700 border-sky-500 text-sky-100' : 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'}`}
                        >
                            ⌒ Curve
                        </button>
                        {drawnBoundaryCalls.length >= 2 && onComputeCurrentClosure && (
                            <button
                                onClick={handleBoundaryClosureReport}
                                className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium border bg-gray-800 border-gray-600 text-gray-400 hover:border-emerald-500 hover:text-emerald-300 transition-colors"
                                title="Compute closure for drawn calls"
                            >
                                ⬡ Closure
                            </button>
                        )}
                    </div>
                    {boundaryLineFrom && (
                        <>
                            <div className="mb-2">
                                <label className="text-gray-400 block mb-0.5">{boundaryCurveMode ? 'Tangent Bearing (in)' : 'Bearing'}</label>
                                <input
                                    ref={boundaryBearingRef}
                                    value={boundaryBearing}
                                    onChange={e => setBoundaryBearing(e.target.value)}
                                    onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBoundaryCallConfirm(); } }}
                                    placeholder='e.g. N 45°30′00″ E'
                                    className="w-full bg-gray-800 border border-gray-600 focus:border-amber-500 rounded px-2 py-1 text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                                    spellCheck={false}
                                />
                            </div>
                            {!boundaryCurveMode && (
                                <div className="mb-2">
                                    <label className="text-gray-400 block mb-0.5">Distance</label>
                                    <input
                                        value={boundaryDistance}
                                        onChange={e => setBoundaryDistance(e.target.value)}
                                        onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBoundaryCallConfirm(); } }}
                                        placeholder='e.g. 125.00'
                                        className="w-full bg-gray-800 border border-gray-600 focus:border-amber-500 rounded px-2 py-1 text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-amber-500/50"
                                        spellCheck={false}
                                    />
                                </div>
                            )}
                            {boundaryCurveMode && (
                                <>
                                    <div className="mb-2">
                                        <label className="text-gray-400 block mb-0.5">Radius</label>
                                        <input
                                            value={boundaryCurveRadius}
                                            onChange={e => setBoundaryCurveRadius(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBoundaryCallConfirm(); } }}
                                            placeholder='e.g. 200.00'
                                            className="w-full bg-gray-800 border border-gray-600 focus:border-sky-500 rounded px-2 py-1 text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-sky-500/50"
                                            spellCheck={false}
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="text-gray-400 block mb-0.5">Arc Length</label>
                                        <input
                                            value={boundaryCurveArcLength}
                                            onChange={e => setBoundaryCurveArcLength(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBoundaryCallConfirm(); } }}
                                            placeholder='e.g. 85.42'
                                            className="w-full bg-gray-800 border border-gray-600 focus:border-sky-500 rounded px-2 py-1 text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-sky-500/50"
                                            spellCheck={false}
                                        />
                                    </div>
                                    <div className="mb-2">
                                        <label className="text-gray-400 block mb-0.5">Direction</label>
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => setBoundaryCurveDirection('left')}
                                                className={`flex-1 py-0.5 rounded text-[10px] font-medium border transition-colors ${boundaryCurveDirection === 'left' ? 'bg-sky-700 border-sky-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'}`}
                                            >
                                                ↺ Left
                                            </button>
                                            <button
                                                onClick={() => setBoundaryCurveDirection('right')}
                                                className={`flex-1 py-0.5 rounded text-[10px] font-medium border transition-colors ${boundaryCurveDirection === 'right' ? 'bg-sky-700 border-sky-500 text-white' : 'bg-gray-800 border-gray-600 text-gray-400 hover:border-gray-500'}`}
                                            >
                                                ↻ Right
                                            </button>
                                        </div>
                                    </div>
                                    <div className="mb-2">
                                        <label className="text-gray-400 block mb-0.5">Chord Bearing <span className="text-gray-600">(optional)</span></label>
                                        <input
                                            value={boundaryCurveChordBearing}
                                            onChange={e => setBoundaryCurveChordBearing(e.target.value)}
                                            onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleBoundaryCallConfirm(); } }}
                                            placeholder='auto-computed if blank'
                                            className="w-full bg-gray-800 border border-gray-600 focus:border-sky-500 rounded px-2 py-1 text-white text-xs font-mono focus:outline-none focus:ring-1 focus:ring-sky-500/50"
                                            spellCheck={false}
                                        />
                                    </div>
                                </>
                            )}

                            <div className="flex gap-1.5">
                                <button
                                    onClick={handleBoundaryCallConfirm}
                                    className="flex-1 py-1 bg-amber-600 hover:bg-amber-700 text-white rounded font-semibold transition-colors"
                                >
                                    Add ➜
                                </button>
                                <button
                                    onClick={resetBoundaryLineMode}
                                    className="py-1 px-2 bg-gray-700 hover:bg-gray-600 text-gray-300 rounded transition-colors"
                                    title="Reset start point"
                                >
                                    ↺
                                </button>
                            </div>
                        </>
                    )}

                    {/* Closure result */}
                    {boundaryClosureResult && (
                        <div className="mt-2 p-2 bg-gray-800/80 rounded border border-emerald-700/50 text-[10px] space-y-0.5">
                            <div className="text-emerald-400 font-semibold mb-1">Closure Report</div>
                            <div className="flex justify-between"><span className="text-gray-400">Precision</span><span className="text-white font-mono">{boundaryClosureResult.precision}</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">Misclosure</span><span className="text-white font-mono">{boundaryClosureResult.misclosureDistance.toFixed(3)} ft</span></div>
                            <div className="flex justify-between"><span className="text-gray-400">Area</span><span className="text-white font-mono">{(boundaryClosureResult.area / 43560).toFixed(4)} ac</span></div>
                        </div>
                    )}

                </div>
            )}
            {/* View/Navigation Toolbar — Default Vertical, flippable */}
            {(() => {
                const isDocked = viewToolbarPosition ? (viewToolbarPosition.isDocked ?? false) : false;
                const containerW = containerRef.current?.clientWidth ?? (typeof window !== 'undefined' ? window.innerWidth : 1000);
                const containerH = containerRef.current?.clientHeight ?? (typeof window !== 'undefined' ? window.innerHeight : 800);
                const toolbarW = toolbarRef.current?.offsetWidth ?? (toolbarOrientation === 'vertical' ? 50 : 380);
                const toolbarH = toolbarRef.current?.offsetHeight ?? (toolbarOrientation === 'vertical' ? 450 : 50);
                // Until CanvasTerminal reports its measured height, use the
                // established fallback. The default seat still clears it.
                const commandBarInset = commandBarHeight > 0 ? commandBarHeight : 148;
                const defaultPos = calculateDefaultToolbarPosition({
                    containerWidth: containerW,
                    containerHeight: containerH,
                    toolbarWidth: toolbarW,
                    toolbarHeight: toolbarH,
                    commandBarInset,
                    northArrowHeight: (NORTH_ARROW_SIZE[settings.northArrowStyle] ?? NORTH_ARROW_SIZE.classic).height,
                });
                const effectiveX = isDocked ? (containerW - toolbarW - NORTH_ARROW_RIGHT_INSET) : (viewToolbarPosition?.x ?? defaultPos.x);
                const maxAnchoredY = Math.max(8, containerH - toolbarH - commandBarInset - 8);
                const effectiveY = viewToolbarPosition?.y === undefined
                    ? defaultPos.y
                    : Math.min(viewToolbarPosition.y, maxAnchoredY);

                // Placement must be decided in viewport coordinates, not container
                // coordinates, and the toolbar's real size is part of its center.
                const containerRect = containerRef.current?.getBoundingClientRect();
                const viewportX = (containerRect?.left ?? 0) + effectiveX + toolbarW / 2;
                const viewportY = (containerRect?.top ?? 0) + effectiveY + toolbarH / 2;
                const viewDirection = getFlyoutDirection(viewportX, viewportY);
                const isVertical = toolbarOrientation === 'vertical';

                // Pick the side with real room for the flyout, then clamp its max
                // height to that room. This is what keeps a tall flyout on screen
                // whether the toolbar is at the top, bottom, or middle.
                const availableAbove = effectiveY + (isVertical ? toolbarH : 0);
                const availableBelow = containerH - effectiveY - (isVertical ? 0 : toolbarH);
                const shouldFlyoutGoAbove = availableBelow < availableAbove;
                const flyoutMaxHeight = Math.max(220, Math.floor((shouldFlyoutGoAbove ? availableAbove : availableBelow) - 16));

                const handleToolbarPointerDown = (e: React.PointerEvent) => {
                    if ((e.target as HTMLElement).closest('[data-no-drag]')) return;
                    const el = toolbarRef.current ?? (e.currentTarget.parentElement as HTMLElement | null);
                    const container = containerRef.current;
                    if (!container || !el) return;

                    const containerRect = container.getBoundingClientRect();
                    const elRect = el.getBoundingClientRect();
                    const curX = elRect.left - containerRect.left;
                    const curY = elRect.top - containerRect.top;

                    setIsViewToolbarDragging(true);
                    viewToolbarDragStart.current = {
                        clientX: e.clientX,
                        clientY: e.clientY,
                        toolbarX: curX,
                        toolbarY: curY,
                    };
                    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
                    e.preventDefault();
                };

                const toggleOrientation = (e: React.MouseEvent) => {
                    e.stopPropagation();
                    setToolbarOrientation(prev => {
                        const next = prev === 'vertical' ? 'horizontal' : 'vertical';
                        try {
                            localStorage.setItem('landsurv-canvas-toolbar-orientation-v2', next);
                        } catch { /* ignore */ }
                        return next;
                    });
                };

                const flyoutPlacementClass = isVertical
                    ? `${viewDirection.horizontal === 'left' ? 'right-full mr-2' : 'left-full ml-2'} ${shouldFlyoutGoAbove ? 'bottom-0' : 'top-0'}`
                    : `${shouldFlyoutGoAbove ? 'bottom-full mb-2' : 'top-full mt-2'} ${viewDirection.horizontal === 'left' ? 'right-0' : 'left-0'}`;
                const FLYOUT_SHELL = 'max-h-[var(--flyout-max-height)] overflow-y-auto backdrop-blur-xl bg-[#0b1119]/95 border border-white/[0.09] shadow-[0_24px_70px_-16px_rgba(0,0,0,0.9)] text-slate-200';

                const wrapperStyle: React.CSSProperties = isViewToolbarDragging
                    ? { left: `${viewToolbarPosition?.x ?? effectiveX}px`, top: `${effectiveY}px` }
                    : isDocked
                    ? { right: '1rem', top: `${effectiveY}px` }
                    : !viewToolbarPosition
                    ? { right: `${NORTH_ARROW_RIGHT_INSET}px`, top: `${effectiveY}px` }
                    : { left: `${viewToolbarPosition.x ?? effectiveX}px`, top: `${effectiveY}px` };

                return (
                    <div 
                        ref={toolbarRef}
                        style={{
                            ...wrapperStyle,
                            transition: isViewToolbarDragging ? 'none' : 'left 0.3s cubic-bezier(0.4, 0, 0.2, 1), right 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                            '--flyout-max-height': `${flyoutMaxHeight}px`,
                        } as React.CSSProperties}
                        className="absolute z-20"
                    >
                        {/* Flyouts with orientation-aware placement */}
                            {isAttributeFlyoutOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-3.5 w-64 animate-fade-in-up z-30 ${FLYOUT_SHELL}`}>
                                    <div className="mb-3 flex items-center justify-between border-b border-white/[0.07] pb-2">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Display Scales</span>
                                        <span className="font-mono text-[9px] uppercase tracking-[0.12em] text-cyan-400/70">Live</span>
                                    </div>
                                    <label htmlFor="attributeScale" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Point Attribute Scale</label>
                                    <div className="flex items-center gap-4 mb-4">
                                        <input type="range" id="attributeScale" min="0.5" max="5" step="0.1" value={attributeScale} onChange={(e) => setAttributeScale(parseFloat(e.target.value))} className="w-full accent-cyan-500" />
                                        <span className="min-w-[3.25rem] rounded-md border border-white/[0.08] bg-white/[0.035] px-2 py-1 text-center font-mono text-xs tabular-nums text-cyan-200">{attributeScale.toFixed(1)}x</span>
                                    </div>
                                    <label htmlFor="lineLabelScale" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Bearing &amp; Distance Scale</label>
                                    <div className="flex items-center gap-4 mb-4">
                                        <input type="range" id="lineLabelScale" min="0.5" max="20" step="0.1" value={lineLabelScale} onChange={(e) => setLineLabelScale(parseFloat(e.target.value))} className="w-full accent-cyan-500" />
                                        <span className="min-w-[3.25rem] rounded-md border border-white/[0.08] bg-white/[0.035] px-2 py-1 text-center font-mono text-xs tabular-nums text-cyan-200">{lineLabelScale.toFixed(1)}x</span>
                                    </div>
                                    <label htmlFor="dimensionScale_above" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Dimension Scale</label>
                                    <div className="flex items-center gap-4 mb-4">
                                        <input type="range" id="dimensionScale_above" min="0.5" max="5" step="0.1" value={dimensionScale} onChange={(e) => setDimensionScale(parseFloat(e.target.value))} className="w-full accent-cyan-500" />
                                        <span className="min-w-[3.25rem] rounded-md border border-white/[0.08] bg-white/[0.035] px-2 py-1 text-center font-mono text-xs tabular-nums text-cyan-200">{dimensionScale.toFixed(1)}x</span>
                                    </div>
                                    <label htmlFor="symbolScale_above" className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Symbol Scale (global)</label>
                                    <div className="flex items-center gap-4 mb-4">
                                        <input type="range" id="symbolScale_above" min="0.25" max="5" step="0.05" value={symbolScale} onChange={(e) => setSymbolScale(parseFloat(e.target.value))} className="w-full accent-cyan-500" />
                                        <span className="min-w-[3.25rem] rounded-md border border-white/[0.08] bg-white/[0.035] px-2 py-1 text-center font-mono text-xs tabular-nums text-cyan-200">{symbolScale.toFixed(2)}x</span>
                                    </div>
                                    <label className="mb-1.5 block text-[11px] font-semibold uppercase tracking-[0.14em] text-slate-500">Point Attribute Text</label>
                                    <div className="flex items-center gap-2 mb-2">
                                        <button
                                            type="button"
                                            onClick={() => setSettings(prev => ({ ...prev, pointAttributeScaling: 'screen' }))}
                                            className={`flex-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors ${settings.pointAttributeScaling === 'screen' ? 'border-cyan-400/50 bg-cyan-400/15 text-cyan-200' : 'border-white/[0.08] bg-white/[0.03] text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'}`}
                                            title="Constant screen pixel size (locked - avoids lag with thousands of points)"
                                        >
                                            Locked (Fast)
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setSettings(prev => ({ ...prev, pointAttributeScaling: 'world' }))}
                                            className={`flex-1 rounded-md border px-2 py-1 text-[11px] font-semibold transition-colors ${settings.pointAttributeScaling === 'world' ? 'border-cyan-400/50 bg-cyan-400/15 text-cyan-200' : 'border-white/[0.08] bg-white/[0.03] text-slate-400 hover:bg-white/[0.06] hover:text-slate-200'}`}
                                            title="Scale with zoom in world units"
                                        >
                                            Zoom with Map
                                        </button>
                                    </div>
                                    {setAnnotationScale && (
                                        <>
                                            <label htmlFor="annotationScale_above" className="block text-sm font-medium text-gray-400 mb-1 mt-3">Annotation Scale (global)</label>
                                            <div className="flex items-center gap-4">
                                                <input type="range" id="annotationScale_above" min="0.25" max="5" step="0.05" value={annotationScale} onChange={(e) => setAnnotationScale(parseFloat(e.target.value))} className="w-full accent-cyan-500" />
                                                <span className="min-w-[3.25rem] rounded-md border border-white/[0.08] bg-white/[0.035] px-2 py-1 text-center font-mono text-xs tabular-nums text-cyan-200">{annotationScale.toFixed(2)}x</span>
                                            </div>
                                        </>
                                    )}
                                </div>
                            )}
                            
                            {isZoomExtentsOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-1.5 animate-fade-in-up z-30 ${FLYOUT_SHELL}`}>
                                    <div className="space-y-1">
                                        <button onClick={() => { zoomExtents(); setIsZoomExtentsOpen(false); }} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white" title="Zoom Extents"><ZoomExtentsIcon className="w-5 h-5"/> Zoom Extents</button>
                                        <button onClick={() => {
                                            setIsZoomExtentsOpen(false);
                                            setIsZoomToPointOpen(true);
                                        }} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white" title="Zoom to Point"><ZoomToPointIcon className="w-5 h-5"/> Zoom to Point</button>
                                        <button onClick={() => {
                                            setIsZoomExtentsOpen(false);
                                            setIsFindPointsOpen(true);
                                        }} className="flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-[13px] text-slate-300 transition-colors hover:bg-white/[0.06] hover:text-white" title="Find Points by Description"><ListBulletIcon className="w-5 h-5"/> Find Points</button>
                                    </div>
                                </div>
                            )}
                            
                            {isZoomToPointOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-3 w-60 animate-fade-in-up z-30 ${FLYOUT_SHELL}`}>
                                    <form onSubmit={handleZoomToPointSubmit} className="flex items-center gap-2 mb-3">
                                        <input type="text" value={zoomToPointNumber} onChange={(e) => setZoomToPointNumber(e.target.value)} placeholder="Point Number" className="flex-1 p-1.5 text-sm border border-white/[0.08] bg-white/[0.035] text-slate-100 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/15" autoFocus />
                                        <button type="submit" className="px-3 py-1.5 text-sm font-semibold bg-cyan-600 text-white rounded-md hover:bg-cyan-700">Go</button>
                                    </form>
                                    {points.length > 0 && (
                                        <div className="max-h-48 overflow-y-auto pr-2">
                                            <h5 className="text-xs font-bold text-gray-400 uppercase mb-2">Points</h5>
                                            <div className="space-y-1">
                                                {points.map(point => (
                                                    <button key={point.pointNumber} onClick={() => { onZoomToPoint(point); setZoomToPointNumber(''); setIsZoomToPointOpen(false); }} className="w-full truncate rounded-md bg-white/[0.04] p-1.5 text-left text-xs text-slate-300 transition-colors hover:bg-white/[0.08] hover:text-white">{point.pointNumber}</button>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}

                            {isFindPointsOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-3 w-72 animate-fade-in-up z-30 ${FLYOUT_SHELL}`}>
                                    <div className="flex items-center justify-between mb-2">
                                        <h5 className="text-xs font-bold text-gray-300 uppercase">Find Points</h5>
                                        <button onClick={() => { setIsFindPointsOpen(false); setFindPointsQuery(''); }} className="text-gray-400 hover:text-white" title="Close"><XMarkIcon className="w-4 h-4"/></button>
                                    </div>
                                    <input type="text" value={findPointsQuery} onChange={(e) => setFindPointsQuery(e.target.value)} placeholder='e.g. "show me sign points"' className="w-full p-1.5 text-sm border border-white/[0.08] bg-white/[0.035] text-slate-100 placeholder:text-slate-600 focus:border-cyan-400/50 focus:outline-none focus:ring-2 focus:ring-cyan-400/15 mb-2" autoFocus />
                                    <div className="max-h-64 overflow-y-auto pr-1">
                                        {findPointsQuery.trim() === '' ? (
                                            <p className="text-xs text-gray-400 italic px-1">Type a description to filter the {points.length} loaded point{points.length !== 1 ? 's' : ''}.</p>
                                        ) : findPointsMatches.length === 0 ? (
                                            <p className="text-xs text-gray-400 italic px-1">No matching points.</p>
                                        ) : (
                                            <>
                                                <p className="text-[10px] text-gray-500 uppercase mb-1 px-1">{findPointsMatches.length} match{findPointsMatches.length !== 1 ? 'es' : ''}{findPointsMatches.length >= 200 ? ' (capped)' : ''}</p>
                                                <div className="space-y-1">
                                                    {findPointsMatches.map(point => (
                                                        <button key={point.pointNumber} onClick={() => onZoomToPoint(point)} className="flex w-full items-center gap-2 rounded-md bg-white/[0.04] p-1.5 text-left text-xs text-slate-300 transition-colors hover:bg-white/[0.08] hover:text-white" title="Zoom to this point">
                                                            <span className="font-mono text-cyan-300 flex-shrink-0">{point.pointNumber}</span>
                                                            <span className="text-gray-300 truncate">{point.description || '(no description)'}</span>
                                                        </button>
                                                    ))}
                                                </div>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {isDrawingToolsMenuOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-1.5 animate-fade-in-up w-60 z-30 ${FLYOUT_SHELL}`}>
                                    <div className="mb-1.5 flex items-center justify-between border-b border-white/[0.07] px-1.5 pb-1.5">
                                        <span className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400">Draw &amp; Edit</span>
                                        <button
                                            type="button"
                                            onClick={() => setIsDrawingToolsMenuOpen(false)}
                                            className="rounded p-0.5 text-slate-500 transition-colors hover:bg-white/[0.06] hover:text-white"
                                            title="Close"
                                        >
                                            <XMarkIcon className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                    <div className="space-y-1.5">
                                        <button onClick={() => { 
                                            if (drawingMode === 'polylines') {
                                                finishDrawing();
                                            } else {
                                                setDrawingMode('polylines');
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${drawingMode === 'polylines' ? 'bg-yellow-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Draw Polylines (L)"><PolylineIcon className="w-5 h-5"/> Draw Polylines (L)</button>
                                        
                                        <button onClick={() => { 
                                            if (drawingMode === 'circle') {
                                                finishDrawing();
                                            } else {
                                                setDrawingMode('circle');
                                                setCircleCenter(null);
                                                setCircleSizeParam('radius');
                                                setTypedCircleValue('');
                                                setCircleSubMode('center-radius');
                                                setTtrStep(1);
                                                setTtrEntity1(null);
                                                setTtrEntity2(null);
                                                setTtrHoverEntity(null);
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${drawingMode === 'circle' ? 'bg-sky-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Draw Circle (C: Center/Radius, T: Tangent-Tangent-Radius)"><CircleIcon className="w-5 h-5"/> Draw Circle (C)</button>
                                        
                                        <button onClick={() => { 
                                            if (drawingMode === 'breaklines') {
                                                finishDrawing();
                                            } else {
                                                setDrawingMode('breaklines');
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${drawingMode === 'breaklines' ? 'bg-pink-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Draw Breaklines"><BreaklineIcon className="w-5 h-5"/> Draw Breaklines</button>
                                        
                                        <button onClick={() => { 
                                            if (drawingMode === 'inclusion') {
                                                finishDrawing();
                                            } else {
                                                setDrawingMode('inclusion');
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${drawingMode === 'inclusion' ? 'bg-green-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Draw Inclusion Lines (I)"><InclusionLineIcon className="w-5 h-5"/> Draw Inclusion Lines (I)</button>
                                        
                                        <button onClick={() => { 
                                            if (drawingMode === 'exclusion') {
                                                finishDrawing();
                                            } else {
                                                setDrawingMode('exclusion');
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${drawingMode === 'exclusion' ? 'bg-red-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Draw Exclusion Lines"><ExclusionLineIcon className="w-5 h-5"/> Draw Exclusion Lines</button>

                                        <button onClick={() => {
                                            if (drawingMode === 'boundary-line') {
                                                setDrawingMode('none');
                                                resetBoundaryLineMode();
                                            } else {
                                                setDrawingMode('boundary-line');
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                                resetBoundaryLineMode();
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${drawingMode === 'boundary-line' ? 'bg-amber-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Draw Boundary Line (click point, enter bearing + distance)">
                                            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21l18-18M6 18l3-3M15 9l3-3"/><circle cx="3" cy="21" r="1.5" fill="currentColor"/><circle cx="21" cy="3" r="1.5" fill="currentColor"/></svg>
                                            Draw Boundary Line
                                        </button>
                                        
                                        <div className="border-t border-white/[0.07]"></div>

                                        <button onClick={() => {
                                            if (isCopyingSelection) {
                                                setIsCopyingSelection(false);
                                                setCopyBasePoint(null);
                                            } else if (selectedLineIds.size === 0) {
                                                finishDrawing();
                                                setIsSelectionMode(true);
                                                setIsBoxSelectMode(false);
                                                setTerminalIncoming({ text: 'COPY: Select entities on the canvas, then press COPY again', tone: 'error' });
                                            } else {
                                                startCopyMode();
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${isCopyingSelection ? 'bg-cyan-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Copy Selected Entities (CP)">
                                            <DocumentDuplicateIcon className="w-5 h-5"/> Copy Selected (CP)
                                        </button>

                                        <button onClick={() => {
                                            if (isOffsetMode) {
                                                setIsOffsetMode(false);
                                                setOffsetDistance(null);
                                                setOffsetTargetId(null);
                                                setOffsetPickStart(null);
                                                clearOffsetPreview();
                                            } else {
                                                startOffsetMode();
                                            }
                                            setIsDrawingToolsMenuOpen(false);
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${isOffsetMode ? 'bg-cyan-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Offset Lines, Polylines, Arcs or Circles (OS)">
                                            <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                                                <path d="M4 18 16 6M10 20 20 10" />
                                                <path d="M12 12 16 8" strokeDasharray="2 2" />
                                            </svg>
                                            Offset (OS)
                                        </button>

                                        <button onClick={() => {
                                            if (isTrimmingLines) {
                                                setIsTrimmingLines(false);
                                                setTrimPoint(null);
                                            } else {
                                                setIsTrimmingLines(true);
                                                setIsDeletingLines(false);
                                                setDrawingMode('none');
                                                setIsExtendingLines(false);
                                                setExtendFromLine(null);
                                            }
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${isTrimmingLines ? 'bg-orange-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Trim Lines (T)"><ScissorsIcon className="w-5 h-5"/> Trim Lines (T)</button>
                                        
                                        <button onClick={() => {
                                            if (isExtendingLines) {
                                                setIsExtendingLines(false);
                                                setExtendFromLine(null);
                                            } else {
                                                setIsExtendingLines(true);
                                                setIsDeletingLines(false);
                                                setDrawingMode('none');
                                                setIsTrimmingLines(false);
                                                setTrimPoint(null);
                                            }
                                        }} className={`flex w-full items-center gap-2 rounded-lg px-2 py-1.5 text-[13px] text-slate-300 transition-colors ${isExtendingLines ? 'bg-blue-600 text-white' : 'hover:bg-white/[0.06] hover:text-white'}`} title="Extend Lines"><ExtendIcon className="w-5 h-5"/> Extend Lines</button>

                                        {selectedLineIds.size > 0 && onConvertSelectionToBoundary && (
                                            <>
                                                <div className="border-t border-white/[0.07]"></div>
                                                <button onClick={() => {
                                                    const selected = lines.filter(l => selectedLineIds.has(l.id || `${l.from}-${l.to}`));
                                                    onConvertSelectionToBoundary(selected);
                                                    setSelectedLineIds(new Set());
                                                    setIsDrawingToolsMenuOpen(false);
                                                }} className="flex w-full items-center gap-2 rounded-lg bg-amber-400/15 px-2 py-1.5 text-[13px] font-medium text-amber-200 ring-1 ring-inset ring-amber-400/30 transition-colors hover:bg-amber-400/25" title="Convert selected lines to a Boundary object in the Boundary Editor">
                                                    <svg viewBox="0 0 24 24" className="w-5 h-5 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M3 21l18-18M6 18l3-3M15 9l3-3"/><circle cx="3" cy="21" r="1.5" fill="currentColor"/><circle cx="21" cy="3" r="1.5" fill="currentColor"/></svg>
                                                    Convert to Boundary ({selectedLineIds.size})
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Add Point Flyout */}
                            {isAddPointFlyoutOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-3 w-72 animate-fade-in-up z-30 space-y-2.5 ${FLYOUT_SHELL}`}>
                                    <div className="flex items-center justify-between border-b border-white/[0.07] pb-1.5">
                                        <div className="flex items-center gap-1.5">
                                            <CrosshairsIcon className="w-4 h-4 text-emerald-400" />
                                            <span className="text-xs font-bold text-emerald-400 uppercase tracking-wide">
                                                Add Survey Point
                                            </span>
                                            <span className="text-[9px] font-mono text-emerald-300 bg-emerald-950/80 px-1 py-0.2 rounded border border-emerald-700/60" title="Managed by PointAgent CACP Authority">
                                                CACP
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => setIsAddPointFlyoutOpen(false)}
                                            className="text-gray-400 hover:text-white"
                                            title="Close"
                                        >
                                            <XMarkIcon className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Pick on Canvas button */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            if (drawingMode === 'add-point') {
                                                finishDrawing();
                                            } else {
                                                finishDrawing();
                                                if (isPointNumberTaken(addPointNumber)) {
                                                    setAddPointNumber(queryCacpNextPointNumber());
                                                }
                                                setDrawingMode('add-point');
                                            }
                                        }}
                                        className={`w-full py-1.5 px-3 rounded-lg text-xs font-semibold flex items-center justify-center gap-2 transition-all ${
                                            drawingMode === 'add-point'
                                                ? 'bg-emerald-600 text-white shadow-md shadow-emerald-900/50 ring-2 ring-emerald-400'
                                                : 'bg-emerald-700/70 hover:bg-emerald-600 text-white'
                                        }`}
                                    >
                                        <CrosshairsIcon className="w-3.5 h-3.5" />
                                        {drawingMode === 'add-point' ? 'Snapping Active (Click Canvas)' : 'Pick on Canvas (Osnap Active)'}
                                    </button>

                                    {/* Point Number & Elev */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <div className="flex items-center justify-between mb-0.5">
                                                <label className="text-[10px] font-medium text-gray-400">Point #</label>
                                                {isPointNumberTaken(addPointNumber) ? (
                                                    <span className="text-[9px] text-amber-400 font-semibold" title="Point already exists in survey database">Exists!</span>
                                                ) : (
                                                    <span className="text-[9px] text-emerald-400 font-mono" title="Verified available by PointAgent">CACP OK</span>
                                                )}
                                            </div>
                                            <input
                                                type="text"
                                                value={addPointNumber}
                                                onChange={e => setAddPointNumber(e.target.value)}
                                                placeholder={nextAvailablePointNumber || "1"}
                                                className={`w-full px-2 py-1 text-xs bg-gray-800 border rounded text-white font-mono focus:outline-none ${
                                                    isPointNumberTaken(addPointNumber)
                                                        ? 'border-amber-500 focus:border-amber-400'
                                                        : 'border-gray-700 focus:border-emerald-500'
                                                }`}
                                            />
                                            {isPointNumberTaken(addPointNumber) && (
                                                <button
                                                    type="button"
                                                    onClick={() => setAddPointNumber(queryCacpNextPointNumber())}
                                                    className="mt-0.5 text-[9px] text-cyan-300 hover:text-cyan-200 underline block"
                                                    title="Query PointAgent for next available number"
                                                >
                                                    Use CACP Next ({queryCacpNextPointNumber()})
                                                </button>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Elev (Z)</label>
                                            <input
                                                type="text"
                                                value={addPointElevation}
                                                onChange={e => setAddPointElevation(e.target.value)}
                                                placeholder="0.00"
                                                className="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-white font-mono focus:outline-none focus:border-emerald-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Northing & Easting */}
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Northing (Y)</label>
                                            <input
                                                type="text"
                                                value={addPointNorthing}
                                                onChange={e => setAddPointNorthing(e.target.value)}
                                                placeholder="5000.00"
                                                className="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-white font-mono focus:outline-none focus:border-emerald-500"
                                            />
                                        </div>
                                        <div>
                                            <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Easting (X)</label>
                                            <input
                                                type="text"
                                                value={addPointEasting}
                                                onChange={e => setAddPointEasting(e.target.value)}
                                                placeholder="5000.00"
                                                className="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-white font-mono focus:outline-none focus:border-emerald-500"
                                            />
                                        </div>
                                    </div>

                                    {/* Description + Chips */}
                                    <div>
                                        <label className="block text-[10px] font-medium text-gray-400 mb-0.5">Description</label>
                                        <input
                                            type="text"
                                            value={addPointDescription}
                                            onChange={e => setAddPointDescription(e.target.value)}
                                            placeholder="e.g. CALC, MON, FND_IP"
                                            className="w-full px-2 py-1 text-xs bg-gray-800 border border-gray-700 rounded text-white focus:outline-none focus:border-emerald-500 mb-1"
                                        />
                                        <div className="flex flex-wrap gap-1">
                                            {['CALC', 'PROP', 'MON', 'FND_IP', 'EP', 'BLDG'].map(chip => (
                                                <button
                                                    key={chip}
                                                    type="button"
                                                    onClick={() => setAddPointDescription(chip)}
                                                    className={`text-[9px] px-1.5 py-0.5 rounded border transition-colors ${
                                                        addPointDescription === chip
                                                            ? 'bg-emerald-900/60 border-emerald-500 text-emerald-200'
                                                            : 'bg-gray-800 border-gray-700 text-gray-400 hover:text-white'
                                                    }`}
                                                >
                                                    {chip}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Direct Add Point button */}
                                    <button
                                        type="button"
                                        onClick={commitManualAddPoint}
                                        disabled={!addPointNorthing.trim() || !addPointEasting.trim()}
                                        className="w-full py-1.5 px-3 rounded-lg text-xs font-semibold bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 disabled:cursor-not-allowed text-white flex items-center justify-center gap-1.5 transition-colors"
                                    >
                                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2.5">
                                            <path d="M5 13l4 4L19 7" strokeLinecap="round" strokeLinejoin="round"/>
                                        </svg>
                                        Add Point to Project
                                    </button>
                                </div>
                            )}

                            {/* Dimension flyout */}
                            {isDimFlyoutOpen && (
                                <div className={`absolute ${flyoutPlacementClass} rounded-xl p-3 w-72 space-y-3 animate-fade-in-up z-30 ${FLYOUT_SHELL}`}>
                                    <div className="flex items-center justify-between border-b border-white/[0.07] pb-1.5">
                                        <span className="text-xs font-bold text-cyan-400 uppercase tracking-wide flex items-center gap-1.5">
                                            <svg viewBox="0 0 24 24" className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                <line x1="3" y1="19" x2="21" y2="19"/>
                                                <line x1="3" y1="14" x2="3" y2="24" strokeWidth="1.8"/><line x1="21" y1="14" x2="21" y2="24" strokeWidth="1.8"/>
                                                <line x1="3" y1="8" x2="21" y2="8" strokeDasharray="3,2"/>
                                            </svg>
                                            Dimensions ({dimensions.length})
                                        </span>
                                        <button
                                            onClick={() => setIsDimFlyoutOpen(false)}
                                            className="text-gray-400 hover:text-white"
                                            title="Close"
                                        >
                                            <XMarkIcon className="w-4 h-4" />
                                        </button>
                                    </div>

                                    {/* Dimension Style Settings CTA */}
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setIsDimSettingsOpen(true);
                                            setIsDimFlyoutOpen(false);
                                        }}
                                        className="w-full py-1.5 px-2.5 rounded-lg text-xs font-semibold bg-cyan-950/70 hover:bg-cyan-900/80 text-cyan-300 border border-cyan-500/40 flex items-center justify-center gap-2 transition-all shadow-sm"
                                        title="Open dimension style settings"
                                    >
                                        <svg viewBox="0 0 24 24" className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="1.8">
                                            <path d="M12 15a3 3 0 100-6 3 3 0 000 6z" />
                                            <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-4 0v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83-2.83l.06-.06a1.65 1.65 0 00.33-1.82 1.65 1.65 0 00-1.51-1H3a2 2 0 010-4h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 012.83-2.83l.06.06a1.65 1.65 0 001.82.33H9a1.65 1.65 0 001-1.51V3a2 2 0 014 0v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 2.83l-.06.06a1.65 1.65 0 00-.33 1.82V9a1.65 1.65 0 001.51 1H21a2 2 0 010 4h-.09a1.65 1.65 0 00-1.51 1z" />
                                        </svg>
                                        Dimension Style Settings
                                    </button>

                                    {/* Linear Dimensions Section */}
                                    <div className="space-y-1">
                                        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider px-1">Linear Dimensions</p>
                                        <button
                                            onClick={() => startDimensionMode('aligned-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'aligned-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Aligned Dimension: Pick Point 1, Point 2, and offset distance"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <line x1="4" y1="18" x2="20" y2="18"/>
                                                    <line x1="4" y1="14" x2="4" y2="22"/>
                                                    <line x1="20" y1="14" x2="20" y2="22"/>
                                                    <line x1="4" y1="7" x2="20" y2="7" strokeDasharray="3 2"/>
                                                </svg>
                                                <span>Aligned</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">3-click</span>
                                        </button>

                                        <button
                                            onClick={() => startDimensionMode('horiz-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'horiz-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Horizontal Dimension: Measures ΔX (Easting) between points"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <line x1="4" y1="12" x2="20" y2="12"/>
                                                    <line x1="4" y1="7" x2="4" y2="17"/>
                                                    <line x1="20" y1="7" x2="20" y2="17"/>
                                                    <polygon points="4,12 8,10 8,14" fill="currentColor"/>
                                                    <polygon points="20,12 16,10 16,14" fill="currentColor"/>
                                                </svg>
                                                <span>Horizontal (ΔX)</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">3-click</span>
                                        </button>

                                        <button
                                            onClick={() => startDimensionMode('vert-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'vert-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Vertical Dimension: Measures ΔY (Northing) between points"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-cyan-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <line x1="12" y1="4" x2="12" y2="20"/>
                                                    <line x1="7" y1="4" x2="17" y2="4"/>
                                                    <line x1="7" y1="20" x2="17" y2="20"/>
                                                    <polygon points="12,4 10,8 14,8" fill="currentColor"/>
                                                    <polygon points="12,20 10,16 14,16" fill="currentColor"/>
                                                </svg>
                                                <span>Vertical (ΔY)</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">3-click</span>
                                        </button>
                                    </div>

                                    {/* Curves & Circles Section */}
                                    <div className="space-y-1 border-t border-gray-800 pt-2">
                                        <p className="text-[10px] font-bold text-sky-400 uppercase tracking-wider px-1">Curve &amp; Arc Dimensions</p>
                                        <button
                                            onClick={() => startDimensionMode('radius-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'radius-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Radius Dimension: Click any arc or circle, then drag leader position"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-sky-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <path d="M4 19a15 15 0 0 1 15-15" strokeDasharray="2 2"/>
                                                    <line x1="4" y1="19" x2="15" y2="8"/>
                                                    <polygon points="15,8 11.5,8.5 14.5,11.5" fill="currentColor"/>
                                                    <circle cx="4" cy="19" r="1.5" fill="currentColor"/>
                                                </svg>
                                                <span>Radius (R)</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">R=...</span>
                                        </button>

                                        <button
                                            onClick={() => startDimensionMode('diameter-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'diameter-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Diameter Dimension: Click circle or arc to place diameter line"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-sky-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <circle cx="12" cy="12" r="8"/>
                                                    <line x1="5" y1="19" x2="19" y2="5"/>
                                                </svg>
                                                <span>Diameter (Ø)</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">Ø=...</span>
                                        </button>

                                        <button
                                            onClick={() => startDimensionMode('arc-length-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'arc-length-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Arc Length Dimension: Concentric arc with radial witness lines"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-sky-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <path d="M4 18 A 12 12 0 0 1 20 18"/>
                                                    <path d="M6 13 A 8 8 0 0 1 18 13" strokeDasharray="2 2"/>
                                                    <line x1="4" y1="18" x2="6" y2="13"/>
                                                    <line x1="20" y1="18" x2="18" y2="13"/>
                                                </svg>
                                                <span>Arc Length (⌒ L)</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">⌒ L=...</span>
                                        </button>

                                        <button
                                            onClick={() => startDimensionMode('curve-data-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'curve-data-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Survey Curve Data Callout: Full R, L, Δ, Chord, Bearing table"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-amber-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <rect x="7" y="4" width="14" height="16" rx="2"/>
                                                    <line x1="10" y1="8" x2="18" y2="8"/>
                                                    <line x1="10" y1="12" x2="18" y2="12"/>
                                                    <line x1="10" y1="16" x2="16" y2="16"/>
                                                    <path d="M2 18 Q 4 11 7 11"/>
                                                </svg>
                                                <span>Curve Data Callout</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">Table</span>
                                        </button>
                                    </div>

                                    {/* Angles & Coordinates */}
                                    <div className="space-y-1 border-t border-gray-800 pt-2">
                                        <p className="text-[10px] font-bold text-violet-400 uppercase tracking-wider px-1">Angles &amp; Coords</p>
                                        <button
                                            onClick={() => startDimensionMode('angular-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'angular-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Angular Dimension: Pick vertex, 1st ray, and 2nd ray"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-violet-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <line x1="4" y1="20" x2="20" y2="20"/>
                                                    <line x1="4" y1="20" x2="16" y2="6"/>
                                                    <path d="M11 20 A 7 7 0 0 0 9.5 14"/>
                                                </svg>
                                                <span>Angular</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">D°M'S"</span>
                                        </button>

                                        <button
                                            onClick={() => startDimensionMode('ordinate-dim')}
                                            className={`w-full p-1.5 rounded-lg text-xs flex items-center justify-between transition-colors ${drawingMode === 'ordinate-dim' ? 'bg-cyan-600 text-white font-semibold' : 'hover:bg-gray-800 text-gray-200'}`}
                                            title="Ordinate Leader: Pick coordinate point, then leader position"
                                        >
                                            <div className="flex items-center gap-2">
                                                <svg viewBox="0 0 24 24" className="w-4 h-4 text-emerald-400" fill="none" stroke="currentColor" strokeWidth="1.5">
                                                    <circle cx="5" cy="19" r="2" fill="currentColor"/>
                                                    <path d="M5 19 L12 10 L19 10"/>
                                                </svg>
                                                <span>Coordinate Leader</span>
                                            </div>
                                            <span className="text-[10px] opacity-70">N/E</span>
                                        </button>
                                    </div>

                                    {/* Dimension Management */}
                                    {dimensions.length > 0 && (
                                        <div className="border-t border-gray-800 pt-2 space-y-1">
                                            {selectedDimensionId && (
                                                <button
                                                    onClick={() => {
                                                        if (selectedDimensionId) {
                                                            onDeleteDimension?.(selectedDimensionId);
                                                            setSelectedDimensionId(null);
                                                        }
                                                    }}
                                                    className="w-full p-1.5 rounded-lg text-xs flex items-center justify-center gap-1.5 bg-amber-900/60 hover:bg-amber-800 text-amber-200 transition-colors"
                                                >
                                                    <TrashIcon className="w-3.5 h-3.5" />
                                                    Delete Selected Dim
                                                </button>
                                            )}
                                            <button
                                                onClick={() => {
                                                    onClearDimensions?.();
                                                    setSelectedDimensionId(null);
                                                    setIsDimFlyoutOpen(false);
                                                }}
                                                className="w-full p-1.5 rounded-lg text-xs flex items-center justify-center gap-1.5 bg-red-950/60 hover:bg-red-900 text-red-300 transition-colors"
                                            >
                                                <TrashIcon className="w-3.5 h-3.5" />
                                                Clear All Dimensions ({dimensions.length})
                                            </button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Orientation Tuple (OT / UCS) Flyout */}
                            {isOtPopupOpen && (() => {
                                const curMode = orientationTuple.mode ?? 'drafting';
                                const curAngle = curMode === 'drafting'
                                    ? (orientationTuple.draftingAngle ?? orientationTuple.angle ?? 0)
                                    : (orientationTuple.viewAngle ?? orientationTuple.angle ?? 0);
                                const curOriginX = curMode === 'drafting'
                                    ? (orientationTuple.draftingOriginX ?? orientationTuple.originX ?? 0)
                                    : (orientationTuple.viewOriginX ?? orientationTuple.originX ?? 0);
                                const curOriginY = curMode === 'drafting'
                                    ? (orientationTuple.draftingOriginY ?? orientationTuple.originY ?? 0)
                                    : (orientationTuple.viewOriginY ?? orientationTuple.originY ?? 0);
                                const curAngleDeg = Math.round((curAngle * 180) / Math.PI * 100) / 100;

                                const setModeAngle = (rad: number) => {
                                    if (!onSetOrientationTuple) return;
                                    onSetOrientationTuple(prev => {
                                        const m = prev.mode ?? 'drafting';
                                        return m === 'drafting'
                                            ? { ...prev, draftingAngle: rad, angle: rad, placed: true }
                                            : { ...prev, viewAngle: rad, angle: rad, placed: true };
                                    });
                                };

                                const setModeOriginX = (val: number) => {
                                    if (!onSetOrientationTuple) return;
                                    onSetOrientationTuple(prev => {
                                        const m = prev.mode ?? 'drafting';
                                        return m === 'drafting'
                                            ? { ...prev, draftingOriginX: val, originX: val, placed: true }
                                            : { ...prev, viewOriginX: val, originX: val, placed: true };
                                    });
                                };

                                const setModeOriginY = (val: number) => {
                                    if (!onSetOrientationTuple) return;
                                    onSetOrientationTuple(prev => {
                                        const m = prev.mode ?? 'drafting';
                                        return m === 'drafting'
                                            ? { ...prev, draftingOriginY: val, originY: val, placed: true }
                                            : { ...prev, viewOriginY: val, originY: val, placed: true };
                                    });
                                };

                                const resetModeOrigin = () => {
                                    if (!onSetOrientationTuple) return;
                                    onSetOrientationTuple(prev => {
                                        const m = prev.mode ?? 'drafting';
                                        return m === 'drafting'
                                            ? { ...prev, draftingOriginX: 0, draftingOriginY: 0, originX: 0, originY: 0, placed: prev.draftingAngle !== 0 }
                                            : { ...prev, viewOriginX: 0, viewOriginY: 0, originX: 0, originY: 0, placed: prev.viewAngle !== 0 };
                                    });
                                };

                                return (
                                    <div
                                        role="dialog"
                                        aria-label="Orientation Tuple settings"
                                        className={`absolute ${flyoutPlacementClass} rounded-xl p-3.5 w-72 text-xs z-30 animate-fade-in-up ${FLYOUT_SHELL}`}
                                    >
                                        <div className="mb-2.5 flex items-center justify-between border-b border-white/[0.07] pb-2">
                                            <div className="flex items-center gap-1.5">
                                                <span className="font-bold text-cyan-400">Orientation Tuple (OT)</span>
                                                <span className="text-[10px] px-1.5 py-0.2 rounded bg-cyan-950 text-cyan-300 border border-cyan-700/50 font-mono">UCS</span>
                                            </div>
                                            <button
                                                type="button"
                                                onClick={() => setIsOtPopupOpen(false)}
                                                className="text-gray-400 hover:text-white p-0.5 rounded"
                                                title="Close orientation controls"
                                            >
                                                ✕
                                            </button>
                                        </div>

                                        <div className="space-y-3.5">
                                            <div>
                                                <div className="flex rounded-lg bg-gray-900 p-0.5 border border-gray-800">
                                                    <button
                                                        type="button"
                                                        onClick={() => onSetOrientationTuple?.(prev => ({ ...prev, mode: 'drafting' }))}
                                                        className={`flex-1 py-1 px-2 rounded-md text-[11px] font-medium transition-all ${
                                                            curMode === 'drafting'
                                                                ? 'bg-cyan-600 text-white shadow-sm'
                                                                : 'text-gray-400 hover:text-gray-200'
                                                        }`}
                                                        title="Drafting: Geometry & North stay put, new lines with Ortho follow OT angle"
                                                    >
                                                        Drafting
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => onSetOrientationTuple?.(prev => ({ ...prev, mode: 'view' }))}
                                                        className={`flex-1 py-1 px-2 rounded-md text-[11px] font-medium transition-all ${
                                                            curMode === 'view'
                                                                ? 'bg-cyan-600 text-white shadow-sm'
                                                                : 'text-gray-400 hover:text-gray-200'
                                                        }`}
                                                        title="View: Rotates the entire canvas viewport and all geometry"
                                                    >
                                                        View
                                                    </button>
                                                </div>
                                            </div>

                                            <div>
                                                <div className="flex justify-between items-center text-[11px] text-gray-400 mb-1.5">
                                                    <span className="font-semibold text-gray-300">Rotation Angle:</span>
                                                    <div className="flex items-center gap-1">
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            value={curAngleDeg}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value);
                                                                if (!isNaN(val)) setModeAngle((val * Math.PI) / 180);
                                                            }}
                                                            className="w-16 px-1.5 py-0.5 rounded bg-gray-900 border border-gray-700 text-cyan-300 font-mono text-right text-xs focus:border-cyan-400 focus:outline-none"
                                                        />
                                                        <span className="text-cyan-300 font-mono">°</span>
                                                    </div>
                                                </div>
                                                <input
                                                    type="range"
                                                    min="-180"
                                                    max="180"
                                                    step="0.5"
                                                    value={curAngleDeg}
                                                    onChange={(e) => setModeAngle((parseFloat(e.target.value) * Math.PI) / 180)}
                                                    className="w-full accent-cyan-500 cursor-pointer"
                                                />
                                                <div className="flex items-center gap-1 pt-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => setModeAngle(0)}
                                                        className="flex-1 py-0.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 font-mono text-[10px] transition-colors"
                                                    >
                                                        0°
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setModeAngle(curAngle + Math.PI / 2)}
                                                        className="flex-1 py-0.5 rounded bg-cyan-900/60 hover:bg-cyan-700 text-cyan-200 font-mono text-[10px] transition-colors"
                                                        title="+90°"
                                                    >
                                                        +90°
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setModeAngle(curAngle - Math.PI / 2)}
                                                        className="flex-1 py-0.5 rounded bg-cyan-900/60 hover:bg-cyan-700 text-cyan-200 font-mono text-[10px] transition-colors"
                                                        title="-90°"
                                                    >
                                                        -90°
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={() => setModeAngle(curAngle + Math.PI)}
                                                        className="flex-1 py-0.5 rounded bg-cyan-900/60 hover:bg-cyan-700 text-cyan-200 font-mono text-[10px] transition-colors"
                                                        title="180°"
                                                    >
                                                        180°
                                                    </button>
                                                </div>
                                                <button
                                                    type="button"
                                                    onClick={() => {
                                                        setIsPickingOtLineAlign(true);
                                                        setIsOtPopupOpen(false);
                                                    }}
                                                    className="mt-2 w-full py-1 rounded bg-indigo-700/80 hover:bg-indigo-600 text-indigo-100 font-medium text-xs transition-colors flex items-center justify-center gap-1.5 shadow-sm border border-indigo-500/40"
                                                    title="Click any line or boundary segment on canvas to align rotation angle to it"
                                                >
                                                    <span>📏</span> Align to Line
                                                </button>
                                            </div>

                                            <div className="pt-2 border-t border-gray-800/80">
                                                <div className="flex justify-between items-center text-[11px] text-gray-400 mb-1.5">
                                                    <span className="font-semibold text-gray-300">Origin / Pivot Point:</span>
                                                    <span className="text-[10px] text-gray-500 font-mono">World Coords</span>
                                                </div>
                                                <div className="grid grid-cols-2 gap-2 mb-2">
                                                    <div>
                                                        <label className="text-[10px] text-emerald-400 font-mono block mb-0.5">X (Easting):</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            value={curOriginX}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value);
                                                                setModeOriginX(isNaN(val) ? 0 : val);
                                                            }}
                                                            className="w-full px-2 py-1 rounded bg-gray-900 border border-gray-700 text-emerald-300 font-mono text-xs focus:border-emerald-400 focus:outline-none"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                    <div>
                                                        <label className="text-[10px] text-red-400 font-mono block mb-0.5">Y (Northing):</label>
                                                        <input
                                                            type="number"
                                                            step="any"
                                                            value={curOriginY}
                                                            onChange={(e) => {
                                                                const val = parseFloat(e.target.value);
                                                                setModeOriginY(isNaN(val) ? 0 : val);
                                                            }}
                                                            className="w-full px-2 py-1 rounded bg-gray-900 border border-gray-700 text-red-300 font-mono text-xs focus:border-red-400 focus:outline-none"
                                                            placeholder="0.00"
                                                        />
                                                    </div>
                                                </div>
                                                <div className="flex items-center gap-1.5">
                                                    <button
                                                        type="button"
                                                        onClick={() => {
                                                            setIsPickingOtOrigin(true);
                                                            setIsOtPopupOpen(false);
                                                        }}
                                                        className="flex-1 py-1 rounded bg-emerald-700 hover:bg-emerald-600 text-white font-medium text-xs transition-colors flex items-center justify-center gap-1 shadow-sm"
                                                        title="Click canvas or snap to point/line to set origin"
                                                    >
                                                        <span>🎯</span> Pick Origin
                                                    </button>
                                                    <button
                                                        type="button"
                                                        onClick={resetModeOrigin}
                                                        className="py-1 px-2.5 rounded bg-gray-800 hover:bg-gray-700 text-gray-300 font-medium text-xs transition-colors"
                                                        title="Reset origin to (0,0)"
                                                    >
                                                        Reset (0,0)
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}
                            
                            {/* Main toolbar - vertical (default) or horizontal */}
                            <div
                                onPointerDown={handleToolbarPointerDown}
                                style={{ touchAction: 'none' }}
                                className={`p-1 bg-[#0a0f16]/90 backdrop-blur-md rounded-lg border border-white/[0.08] shadow-[0_10px_30px_rgba(0,0,0,0.45)] text-slate-300 flex ${isVertical ? 'flex-col items-center' : 'flex-row items-center'} gap-1`}
                            >
                                {/* Drag handle + Flip button */}
                                <div className={`flex ${isVertical ? 'flex-col py-1' : 'flex-row px-1'} items-center gap-1 cursor-grab active:cursor-grabbing text-gray-500 hover:text-gray-300`}>
                                    <div className="toolbar-drag-handle flex items-center justify-center p-0.5" title={isDocked ? "Drag toolbar (docked to agent chat)" : "Drag toolbar"}>
                                        {isVertical ? (
                                            <div className="flex flex-row gap-0.5">
                                                <div className="w-1 h-1 rounded-full bg-current"></div>
                                                <div className="w-1 h-1 rounded-full bg-current"></div>
                                                <div className="w-1 h-1 rounded-full bg-current"></div>
                                            </div>
                                        ) : (
                                            <div className="flex flex-col gap-0.5">
                                                <div className="w-1 h-1 rounded-full bg-current"></div>
                                                <div className="w-1 h-1 rounded-full bg-current"></div>
                                                <div className="w-1 h-1 rounded-full bg-current"></div>
                                            </div>
                                        )}
                                    </div>
                                    <button
                                        data-no-drag
                                        type="button"
                                        onClick={toggleOrientation}
                                        className="p-1 rounded hover:bg-gray-800 text-gray-400 hover:text-cyan-300 transition-colors"
                                        title={isVertical ? 'Flip toolbar to Horizontal' : 'Flip toolbar to Vertical'}
                                        aria-label={isVertical ? 'Flip to horizontal' : 'Flip to vertical'}
                                    >
                                        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2">
                                            {isVertical ? (
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M8 7h8m0 0l-3-3m3 3l-3 3M16 17H8m0 0l3 3m-3-3l3-3" />
                                            ) : (
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M7 8v8m0 0l-3-3m3 3l3-3M17 16V8m0 0l3 3m-3-3l-3 3" />
                                            )}
                                        </svg>
                                    </button>
                                </div>

                                <button
                                    data-no-drag
                                    onClick={() => {
                                        setIsZoomExtentsOpen(p => !p);
                                        if (!isZoomExtentsOpen) {
                                            setIsAttributeFlyoutOpen(false);
                                            setIsZoomToPointOpen(false);
                                            setIsLayerPanelOpen(false);
                                            setIsDrawingToolsMenuOpen(false);
                                            setIsAddPointFlyoutOpen(false);
                                            setIsDimFlyoutOpen(false);
                                            setIsOtPopupOpen(false);
                                        }
                                    }}
                                    className={`p-2.5 rounded-lg transition-colors ${isZoomExtentsOpen ? 'bg-cyan-600' : 'hover:bg-gray-800'}`}
                                    title="Zoom Options (ZE)"
                                >
                                    <ToolbarZoomIcon className="w-5 h-5"/>
                                </button>
                                
                                <button
                                    data-no-drag
                                    onClick={() => {
                                        setIsAttributeFlyoutOpen(p => !p);
                                        if (!isAttributeFlyoutOpen) {
                                            setIsZoomExtentsOpen(false);
                                            setIsZoomToPointOpen(false);
                                            setIsLayerPanelOpen(false);
                                            setIsDrawingToolsMenuOpen(false);
                                            setIsAddPointFlyoutOpen(false);
                                            setIsDimFlyoutOpen(false);
                                            setIsOtPopupOpen(false);
                                        }
                                    }}
                                    className={`p-2.5 rounded-lg transition-colors ${isAttributeFlyoutOpen ? 'bg-cyan-600' : 'hover:bg-gray-800'}`}
                                    title="Annotation Scale"
                                >
                                    <ToolbarAnnotationScaleIcon className="w-5 h-5"/>
                                </button>

                                <button
                                    data-no-drag
                                    onClick={() => {
                                        setIsSelectionMode(p => {
                                            const next = !p;
                                            if (next) {
                                                setIsZoomExtentsOpen(false);
                                                setIsAttributeFlyoutOpen(false);
                                                setIsZoomToPointOpen(false);
                                                setIsLayerPanelOpen(false);
                                                setIsDrawingToolsMenuOpen(false);
                                                setIsAddPointFlyoutOpen(false);
                                                setIsDimFlyoutOpen(false);
                                                setIsOtPopupOpen(false);
                                                setDrawingMode('none');
                                                setIsTrimmingLines(false);
                                                setIsExtendingLines(false);
                                                setIsDeletingLines(false);
                                                setTrimPoint(null);
                                                setExtendFromLine(null);
                                            } else {
                                                setSelectedLineIds(new Set());
                                                setSelectedDimensionId(null);
                                            }
                                            return next;
                                        });
                                    }}
                                    className={`p-2.5 rounded-lg transition-colors ${isSelectionMode ? 'bg-cyan-600' : 'hover:bg-gray-800'}`}
                                    title="Selection Mode (S) — click lines, press D to delete, then Convert to Boundary"
                                >
                                    <ToolbarSelectIcon className="w-5 h-5"/>
                                </button>

                                {/* Add Point button */}
                                <button
                                    data-no-drag
                                    onClick={() => {
                                        setIsAddPointFlyoutOpen(p => {
                                            const willOpen = !p;
                                            if (willOpen) {
                                                const cacpNum = queryCacpNextPointNumber();
                                                setAddPointNumber(cacpNum);
                                                setIsZoomExtentsOpen(false);
                                                setIsAttributeFlyoutOpen(false);
                                                setIsZoomToPointOpen(false);
                                                setIsLayerPanelOpen(false);
                                                setIsDrawingToolsMenuOpen(false);
                                                setIsDimFlyoutOpen(false);
                                                setIsOtPopupOpen(false);
                                            }
                                            return willOpen;
                                        });
                                    }}
                                    className={`p-2.5 rounded-lg transition-colors ${isAddPointFlyoutOpen || drawingMode === 'add-point' ? 'bg-emerald-600 text-white' : 'hover:bg-gray-800 text-emerald-400'}`}
                                    title="Add Point (P) — Pick on canvas or enter coordinates"
                                >
                                    <ToolbarAddPointIcon className="w-5 h-5"/>
                                </button>

                                <button
                                    data-no-drag
                                    onClick={() => {
                                        setIsDrawingToolsMenuOpen(p => !p);
                                        if (!isDrawingToolsMenuOpen) {
                                            setIsZoomExtentsOpen(false);
                                            setIsAttributeFlyoutOpen(false);
                                            setIsZoomToPointOpen(false);
                                            setIsLayerPanelOpen(false);
                                            setIsAddPointFlyoutOpen(false);
                                            setIsDimFlyoutOpen(false);
                                            setIsOtPopupOpen(false);
                                        } else {
                                            setSelectedLineIds(new Set());
                                        }
                                    }}
                                    className={`p-2.5 rounded-lg transition-colors ${isDrawingToolsMenuOpen || drawingMode !== 'none' || isTrimmingLines || isExtendingLines || isDeletingLines || isCopyingSelection || isOffsetMode ? 'bg-cyan-600' : 'hover:bg-gray-800'}`}
                                    title="Drawing Tools (L, C, T, I)"
                                >
                                    <ToolbarDrawToolsIcon className="w-5 h-5"/>
                                </button>

                                <button
                                    data-no-drag
                                    onClick={() => {
                                        setIsDimFlyoutOpen(p => !p);
                                        if (!isDimFlyoutOpen) {
                                            setIsZoomExtentsOpen(false);
                                            setIsAttributeFlyoutOpen(false);
                                            setIsZoomToPointOpen(false);
                                            setIsLayerPanelOpen(false);
                                            setIsDrawingToolsMenuOpen(false);
                                            setIsAddPointFlyoutOpen(false);
                                            setIsOtPopupOpen(false);
                                        }
                                    }}
                                    className={`p-2.5 rounded-lg transition-colors ${isDimFlyoutOpen || isDimensionDrawingMode(drawingMode) ? 'bg-cyan-600' : 'hover:bg-gray-800'}`}
                                    title="Dimension Tools"
                                >
                                    <ToolbarDimensionIcon className="w-5 h-5"/>
                                </button>

                                {/* Symbol exclusion mode — click points to hide their symbols */}
                                <button
                                    data-no-drag
                                    onClick={() => setSymbolExclusionMode(m => !m)}
                                    className={`p-2.5 rounded-lg transition-colors flex items-center ${symbolExclusionMode ? 'bg-red-600 hover:bg-red-700' : 'hover:bg-gray-800'}`}
                                    title={symbolExclusionMode
                                        ? `Exit symbol exclusion mode (${excludedSymbolDescriptions.size} hidden)`
                                        : 'Exclude point symbols by description'}
                                >
                                    <ToolbarSymbolExclusionIcon className="w-5 h-5"/>
                                </button>

                                {props.onOpenBoundaryEditor && (boundaryFiles?.length ?? 0) > 0 && (
                                    <button
                                        data-no-drag
                                        onClick={props.onOpenBoundaryEditor}
                                        className={`p-2.5 rounded-lg transition-colors ${props.isBoundaryEditorOpen ? 'bg-amber-600 hover:bg-amber-700' : 'hover:bg-gray-800'} ${props.isBoundaryEditorButtonPulsing && !props.isBoundaryEditorOpen ? 'animate-pulse-pink-cta' : ''}`}
                                        title={props.isBoundaryEditorOpen ? 'Close Boundary Editor' : 'Open Boundary Editor'}
                                        aria-label={props.isBoundaryEditorOpen ? 'Close Boundary Editor' : 'Open Boundary Editor'}
                                    >
                                        <BoundaryEditorGlyph />
                                    </button>
                                )}

                                {props.onOpenShrinkwrap && (
                                    <button
                                        data-no-drag
                                        onClick={props.onOpenShrinkwrap}
                                        className={`p-2.5 rounded-lg transition-colors ${props.isShrinkwrapOpen ? 'bg-violet-600 hover:bg-violet-700' : 'hover:bg-gray-800'}`}
                                        title="Shrinkwrap visible survey points"
                                    >
                                        <ToolbarShrinkwrapIcon className="w-5 h-5"/>
                                    </button>
                                )}

                                {/* UCS / Orientation Tuple (OT) button on the floating toolbar */}
                                {(() => {
                                    const curMode = orientationTuple.mode ?? 'drafting';
                                    const curAngle = curMode === 'drafting'
                                        ? (orientationTuple.draftingAngle ?? orientationTuple.angle ?? 0)
                                        : (orientationTuple.viewAngle ?? orientationTuple.angle ?? 0);
                                    const curOriginX = curMode === 'drafting'
                                        ? (orientationTuple.draftingOriginX ?? orientationTuple.originX ?? 0)
                                        : (orientationTuple.viewOriginX ?? orientationTuple.originX ?? 0);
                                    const curOriginY = curMode === 'drafting'
                                        ? (orientationTuple.draftingOriginY ?? orientationTuple.originY ?? 0)
                                        : (orientationTuple.viewOriginY ?? orientationTuple.originY ?? 0);
                                    const curAngleDeg = Math.round((curAngle * 180) / Math.PI * 100) / 100;
                                    const p0 = worldToScreen(curOriginX, curOriginY);
                                    const localTheta = curMode === 'drafting' ? curAngle : 0;
                                    const p1 = worldToScreen(curOriginX + Math.cos(localTheta), curOriginY + Math.sin(localTheta));
                                    const screenAngleDeg = Math.round(Math.atan2(p1.y - p0.y, p1.x - p0.x) * (180 / Math.PI) * 100) / 100;
                                    const isOtActive = isOtPopupOpen || isPickingOtOrigin || isPickingOtLineAlign || Math.abs(curAngleDeg) > 0.01;

                                    return (
                                        <button
                                            data-no-drag
                                            type="button"
                                            data-testid="toolbar-ot-button"
                                            onClick={() => {
                                                setIsOtPopupOpen(p => {
                                                    const willOpen = !p;
                                                    if (willOpen) {
                                                        setIsZoomExtentsOpen(false);
                                                        setIsAttributeFlyoutOpen(false);
                                                        setIsZoomToPointOpen(false);
                                                        setIsLayerPanelOpen(false);
                                                        setIsDrawingToolsMenuOpen(false);
                                                        setIsAddPointFlyoutOpen(false);
                                                        setIsDimFlyoutOpen(false);
                                                    }
                                                    return willOpen;
                                                });
                                            }}
                                            className={`p-2 rounded-lg transition-colors flex flex-col items-center justify-center gap-0.5 ${
                                                isOtPopupOpen
                                                    ? 'bg-cyan-600 text-white'
                                                    : isOtActive
                                                    ? 'bg-cyan-950/60 text-cyan-200 ring-1 ring-cyan-500/40 hover:bg-gray-800'
                                                    : 'hover:bg-gray-800 text-slate-300'
                                            }`}
                                            title={`Orientation Tuple (OT / UCS) — ${curAngleDeg.toFixed(1)}°. Click to adjust orientation and pivot origin.`}
                                            aria-label="Open orientation tuple settings"
                                            aria-expanded={isOtPopupOpen}
                                        >
                                            <svg className="w-5 h-5 overflow-visible" viewBox="0 0 32 32" fill="none" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                                                <g transform={`rotate(${screenAngleDeg} 9 23)`}>
                                                    <path d="M9 6.5A16.5 16.5 0 0 1 25.5 23" stroke="#22d3ee" strokeOpacity="0.28" strokeWidth="0.8" strokeDasharray="1.5 2" />
                                                    <line x1="9" y1="23" x2="27.4" y2="23" stroke="#34d399" />
                                                    <path d="M27.6 23l-4.1-2.2v4.4z" fill="#34d399" stroke="none" />
                                                    <text x="24.6" y="20.4" fontSize="4.1" fontWeight="700" fill="#34d399" stroke="none" fontFamily="ui-monospace, SFMono-Regular, monospace">X</text>
                                                    <line x1="9" y1="23" x2="9" y2="4.6" stroke="#f87171" />
                                                    <path d="M9 4.4L6.8 8.6h4.4z" fill="#f87171" stroke="none" />
                                                    <text x="10.8" y="7.6" fontSize="4.1" fontWeight="700" fill="#f87171" stroke="none" fontFamily="ui-monospace, SFMono-Regular, monospace">Y</text>
                                                    <circle cx="9" cy="23" r="2.4" fill="#0b1119" stroke="#e2e8f0" strokeWidth="1.2" />
                                                    <line x1="7.2" y1="23" x2="10.8" y2="23" stroke="#e2e8f0" strokeWidth="0.9" />
                                                    <line x1="9" y1="21.2" x2="9" y2="24.8" stroke="#e2e8f0" strokeWidth="0.9" />
                                                </g>
                                            </svg>
                                            <span className="font-mono text-[7px] font-bold leading-none tracking-[0.16em] text-cyan-300/90">
                                                {Math.abs(curAngleDeg) > 0.01 ? `${curAngleDeg.toFixed(0)}°` : 'OT'}
                                            </span>
                                        </button>
                                    );
                                })()}
                            </div>
                        </div>
                    );
                })()}
            {/* Quick-access CAD Standards spreadsheet editor (full-screen modal) */}
            {isCadStandardsEditorOpen && cadStandard && (
                <StandardsEditor
                    standard={cadStandard}
                    onSave={(updated: StandardDefinition) => {
                        cadManager.dispatch({ type: 'SET_STANDARD', payload: updated });
                        setIsCadStandardsEditorOpen(false);
                    }}
                    onClose={() => setIsCadStandardsEditorOpen(false)}
                />
            )}

            {/* Subtle Live-Scene Dimension Settings HUD (Light Overlay) */}
            {isDimSettingsOpen && (
                <DimensionSettingsPanel
                    globalSettings={dimStyleSettings}
                    onUpdateGlobalSettings={(updated: DimensionStyleSettings) => {
                        setDimStyleSettings(updated);
                        if (updated.scale !== dimensionScale) {
                            setDimensionScale(updated.scale);
                        }
                        try {
                            localStorage.setItem('landsurv-dim-style-settings', JSON.stringify(updated));
                        } catch { /* ignore */ }
                    }}
                    selectedDimension={selectedDimensionId ? (dimensions.find(d => d.id === selectedDimensionId) || null) : null}
                    onUpdateSelectedDimension={(updated: AnnotationDimension) => {
                        props.onUpdateDimension?.(updated);
                    }}
                    onDeleteSelectedDimension={(id: string) => {
                        props.onDeleteDimension?.(id);
                        setSelectedDimensionId(null);
                    }}
                    onDeselectDimension={() => setSelectedDimensionId(null)}
                    onClose={() => setIsDimSettingsOpen(false)}
                />
            )}

            {/* Interactive CAD Command Terminal */}
            <CanvasTerminal
                onOpenProperties={() => setIsPropertiesOpen(true)}
                onOpenTextEditor={props.onOpenTextEditor}
                activePrompt={
                    activeCommandPrompt
                        ? activeCommandPrompt.id === 'polar_array' && isPickingPolarCenter
                            ? 'POLARARRAY: Pick rotation center — click a free point or snap to an entity / survey point [MMB pans · Esc cancels pick]'
                            : (() => {
                                const field = activeCommandPrompt.fields[commandPromptIndex];
                                const value = field ? commandPromptValues[field.key] : '';
                                return `${activeCommandPrompt.title}: ${field ? field.label : 'Apply'}${value ? ` = ${value}` : ''} — type a value${field?.kind === 'point' ? ' (E,N)' : ''} or use the on-canvas controls [${commandPromptIndex + 1}/${activeCommandPrompt.fields.length}] [Enter applies, Esc cancels]`;
                            })()
                        : pendingSelectionCommand
                        ? `${pendingSelectionCommand.label}: Select entities — left-click to pick, drag a window, MMB pans [Enter to apply, Esc to cancel]`
                        : isScalingSelection
                        ? scaleBasePoint
                            ? `SCALE: Specify scale factor — ${typedScaleFactor ? `${typedScaleFactor}× [Enter]` : scaleFactorPreview !== null ? `${scaleFactorPreview.toFixed(3)}× (${(scaleFactorPreview * 100).toFixed(1)}%)` : 'click a point or type a factor'} [Esc cancels]`
                            : 'SCALE: Specify reference point — click, snap, or type E,N [Esc to cancel]'
                        : isRotatingSelection
                        ? rotatePivot
                            ? `ROTATE: Specify rotation angle — ${typedRotateAngle ? `${typedRotateAngle}° [Enter]` : rotateAnglePreview !== null ? `${rotateAnglePreview.toFixed(2)}°` : 'click a point or type degrees'} [Shift snaps 15°, Esc cancels]`
                            : 'ROTATE: Specify base point — click, snap, or type E,N [Esc to cancel]'
                        : isMirroringSelection
                        ? mirrorAxisStart
                            ? 'MIRROR: Specify second axis point — click or snap [Esc to cancel]'
                            : 'MIRROR: Specify first axis point — click, snap, or type E,N [Esc to cancel]'
                        : isMovingSelection
                        ? moveBasePoint
                            ? `MOVE: Specify destination — ΔE ${moveDeltaPreview ? moveDeltaPreview.dE.toFixed(2) : '0.00'}, ΔN ${moveDeltaPreview ? moveDeltaPreview.dN.toFixed(2) : '0.00'} [Esc to cancel]`
                            : 'MOVE: Specify base point — click, snap, or type E,N [Esc to cancel]'
                        : isCopyingSelection
                        ? copyBasePoint
                            ? 'COPY: Specify destination point — click to place a copy [Esc to exit]'
                            : 'COPY: Specify base point [Esc to exit]'
                        : isOffsetMode
                        ? offsetDistance === null
                            ? offsetPickStart
                                ? 'OFFSET: Click a second point to set the distance [Esc to exit]'
                                : 'OFFSET: Specify offset distance — type a number or click two points [Esc to exit]'
                            : !offsetTargetId
                            ? 'OFFSET: Select a line, polyline, arc or circle to offset [Esc to exit]'
                            : 'OFFSET: Click the side to offset toward [Esc to exit]'
                        : drawingMode === 'polylines' && polylinePoints.length > 0
                        ? `${polylineArcMode ? 'ARC' : 'LINE'} — Length: ${typedSegmentLength || '_'} ${getLinearUnitAbbreviation(settings.linearUnits)} (Enter to commit, A for Arc, L for Line, Esc to finish)`
                        : drawingMode === 'polygon'
                        ? !polygonCenter
                            ? `POLYGON: Pick center — ${polygonSides} sides; scroll or [ ] changes sides [Esc to cancel]`
                            : `POLYGON: Diameter = ${typedPolygonDiameter || (cursorWorldPosRef.current ? convertLinearUnits(Math.hypot(cursorWorldPosRef.current.x - polygonCenter.easting, cursorWorldPosRef.current.y - polygonCenter.northing) * 2, 'foot', settings.linearUnits || 'usSurveyFoot').toFixed(2) : '_')} ${getLinearUnitAbbreviation(settings.linearUnits)} · ${polygonSides} sides [scroll/[ ] sides, Enter]`
                        : drawingMode === 'circle'
                        ? circleSubMode === 'ttr'
                            ? ttrStep === 1
                                ? 'CIRCLE (TTR): Select first tangent entity [T for Center/Radius, Esc]'
                                : ttrStep === 2
                                ? 'CIRCLE (TTR): Select second tangent entity [Esc]'
                                : `CIRCLE (TTR): ${circleSizeParam.toUpperCase()} = ${typedCircleValue || '_'} ${getLinearUnitAbbreviation(settings.linearUnits)} [D to switch, Enter]`
                            : !circleCenter
                            ? 'CIRCLE: Pick center point or type T for TTR [Esc to cancel]'
                            : `CIRCLE: ${circleSizeParam.toUpperCase()} = ${typedCircleValue || (cursorWorldPosRef.current ? (Math.hypot(cursorWorldPosRef.current.x - circleCenter.easting, cursorWorldPosRef.current.y - circleCenter.northing) * (circleSizeParam === 'diameter' ? 2 : 1)).toFixed(2) : '_')} [D to switch, Enter]`
                        : drawingMode === 'add-point'
                        ? `ADD POINT: Click canvas to drop PT ${addPointNumber || nextAvailablePointNumber || '1'} [Osnap active] or enter coords in flyout [Esc to finish]`
                        : drawingMode === 'aligned-dim'
                        ? dimPhase === 0 ? 'DIM ALIGNED: Specify 1st point [Esc to cancel]' : dimPhase === 1 ? 'DIM ALIGNED: Specify 2nd point [Esc to cancel]' : 'DIM ALIGNED: Specify dimension line offset [Esc to cancel]'
                        : drawingMode === 'horiz-dim'
                        ? dimPhase === 0 ? 'DIM HORIZONTAL: Specify 1st point [Esc to cancel]' : dimPhase === 1 ? 'DIM HORIZONTAL: Specify 2nd point [Esc to cancel]' : 'DIM HORIZONTAL: Specify dimension line offset [Esc to cancel]'
                        : drawingMode === 'vert-dim'
                        ? dimPhase === 0 ? 'DIM VERTICAL: Specify 1st point [Esc to cancel]' : dimPhase === 1 ? 'DIM VERTICAL: Specify 2nd point [Esc to cancel]' : 'DIM VERTICAL: Specify dimension line offset [Esc to cancel]'
                        : drawingMode === 'radius-dim'
                        ? dimPhase === 0 ? 'DIM RADIUS: Click arc or circle to dimension [Esc to cancel]' : 'DIM RADIUS: Specify leader text position [Esc to cancel]'
                        : drawingMode === 'diameter-dim'
                        ? dimPhase === 0 ? 'DIM DIAMETER: Click arc or circle to dimension [Esc to cancel]' : 'DIM DIAMETER: Specify leader text position [Esc to cancel]'
                        : drawingMode === 'arc-length-dim'
                        ? dimPhase === 0 ? 'DIM ARC LENGTH: Click arc segment to dimension [Esc to cancel]' : 'DIM ARC LENGTH: Specify concentric offset [Esc to cancel]'
                        : drawingMode === 'curve-data-dim'
                        ? dimPhase === 0 ? 'CURVE DATA: Click arc or curve to place table [Esc to cancel]' : 'CURVE DATA: Specify callout box position [Esc to cancel]'
                        : drawingMode === 'angular-dim'
                        ? dimPhase === 0 ? 'DIM ANGULAR: Specify angle vertex [Esc to cancel]' : dimPhase === 1 ? 'DIM ANGULAR: Specify 1st ray point [Esc to cancel]' : 'DIM ANGULAR: Specify 2nd ray point [Esc to cancel]'
                        : drawingMode === 'ordinate-dim'
                        ? dimPhase === 0 ? 'DIM ORDINATE: Specify point location [Esc to cancel]' : 'DIM ORDINATE: Specify leader text position [Esc to cancel]'
                        : isTrimmingLines
                        ? 'TRIM: Click line segment to cut at intersection [Esc to exit]'
                        : isExtendingLines
                        ? 'EXTEND: Click line to extend [Esc to exit]'
                        : isDeletingLines
                        ? 'DELETE: Click line to remove [Esc to exit]'
                        : isSelectionMode
                        ? `SELECTION: ${selectedLineIds.size} lines, ${selectedDimensionId ? '1 dim' : '0 dims'} selected [S to exit, B for box select, D to delete]`
                        : drawingMode === 'boundary-align'
                        ? 'ALIGN: Click matching lines between deeds to align'
                        : undefined
                }
                commandContext={{
                    onAddPoint: () => {
                        finishDrawing();
                        const nextPn = queryCacpNextPointNumber();
                        setAddPointNumber(nextPn);
                        setDrawingMode('add-point');
                        setIsAddPointFlyoutOpen(true);
                    },
                    onStartLine: () => {
                        setDrawingMode('polylines');
                        setIsTrimmingLines(false);
                        setIsExtendingLines(false);
                        setIsDeletingLines(false);
                        setTrimPoint(null);
                        setExtendFromLine(null);
                        setIsSelectionMode(false);
                        setSelectedLineIds(new Set());
                        setPolylinePoints([]);
                        setPolylineArcMode(false);
                        lastSegmentTangentRef.current = null;
                        setTypedSegmentLength('');
                    },
                    onStartCircle: () => {
                        setDrawingMode('circle');
                        setCircleCenter(null);
                        setCircleSizeParam('radius');
                        setTypedCircleValue('');
                        setIsTrimmingLines(false);
                        setIsExtendingLines(false);
                        setIsDeletingLines(false);
                        setTrimPoint(null);
                        setExtendFromLine(null);
                        setIsSelectionMode(false);
                        setSelectedLineIds(new Set());
                        setPolylinePoints([]);
                    },
                    onStartInclusion: () => {
                        setDrawingMode('inclusion');
                        setIsTrimmingLines(false);
                        setIsExtendingLines(false);
                        setIsDeletingLines(false);
                        setTrimPoint(null);
                        setExtendFromLine(null);
                        setIsSelectionMode(false);
                        setSelectedLineIds(new Set());
                    },
                    onToggleOrtho: () => {
                        setIsOrthoEnabled(prev => !prev);
                    },
                    onToggleSelection: () => {
                        // While a command is armed for picks, S/B keep pick mode on
                        // instead of toggling it off and clearing the picks.
                        if (pendingSelectionCommand) {
                            setIsSelectionMode(true);
                            setIsBoxSelectMode(false);
                            return;
                        }
                        setIsSelectionMode(p => !p);
                    },
                    onToggleBoxSelect: () => {
                        setIsBoxSelectMode(p => !p);
                    },
                    onStartTrim: () => {
                        setIsTrimmingLines(p => !p);
                        setIsExtendingLines(false);
                        setIsDeletingLines(false);
                        setDrawingMode('none');
                    },
                    onStartExtend: () => {
                        setIsExtendingLines(p => !p);
                        setIsTrimmingLines(false);
                        setIsDeletingLines(false);
                        setDrawingMode('none');
                        setExtendFromLine(null);
                    },
                    onStartCopy: startCopyMode,
                    onOpenProperties: () => setIsPropertiesOpen(true),
                    onStartRotate: startRotateMode,
                    onStartScale: startScaleMode,
                    onStartMirror: startMirrorMode,
                    onStartMove: startMoveMode,
                    onStartOffset: startOffsetMode,
                    onSelectAll: selectAllEntitiesCommand,
                    onDeselectAll: deselectAllCommand,
                    onPrompt: openCommandPrompt,
                    linearUnitLabel: getLinearUnitAbbreviation(settings.linearUnits),
                    onAwaitSelection: beginSelectionForCommand,
                    onApplyPendingSelection: () => {
                        if (activeCommandPrompt) {
                            submitCommandPrompt();
                            return;
                        }
                        if (pendingSelectionCommand) {
                            runPendingSelectionCommand();
                            return;
                        }
                        if (drawingMode === 'polygon') {
                            if (!polygonCenter) {
                                setTerminalIncoming({ text: 'POLYGON: Pick a center first', tone: 'error' });
                                return;
                            }
                            let radiusInput: number | null = null;
                            if (typedPolygonDiameter.trim().length > 0) {
                                const num = Number(typedPolygonDiameter);
                                if (Number.isFinite(num) && num > 0) radiusInput = num / 2;
                            }
                            if (radiusInput === null && cursorWorldPosRef.current) {
                                const worldRadius = Math.hypot(
                                    cursorWorldPosRef.current.x - polygonCenter.easting,
                                    cursorWorldPosRef.current.y - polygonCenter.northing,
                                );
                                radiusInput = convertLinearUnits(worldRadius, 'foot', settings.linearUnits || 'usSurveyFoot');
                            }
                            if (radiusInput === null) {
                                setTerminalIncoming({ text: 'POLYGON: Move the cursor or type a diameter', tone: 'error' });
                                return;
                            }
                            commitPolygonFromRadius(radiusInput);
                            return;
                        }
                        // Plain selection mode: Enter ends the pick session and
                        // hands the caret back to the command line.
                        setIsSelectionMode(false);
                        setIsBoxSelectMode(false);
                        setCommandFocusRequest(n => n + 1);
                    },
                    onCommandSubmitted: (verb: string) => {
                        // Numbers and points belong to the active prompt; any other
                        // command supersedes it. Selection helpers keep a pending
                        // command armed (which also re-arms it if needed).
                        if (verb === 'numeric' || verb === 'coordinate') return;
                        if (activeCommandPrompt) cancelCommandPrompt();
                        if (verb === 'select' || verb === 'box' || verb === 'select_all' || verb === 'deselect' || verb === 'help' || verb === 'key_info' || verb === 'clear' || verb === 'properties') return;
                        setPendingSelectionCommand(null);
                        stopInteractiveTransforms();
                    },
                    onEraseSelection: eraseSelectionCommand,
                    onMoveSelection: moveSelectionCommand,
                    onRotateSelection: rotateSelectionCommand,
                    onScaleSelection: scaleSelectionCommand,
                    onMirrorSelection: mirrorSelectionCommand,
                    onJoinSelection: joinSelectionCommand,
                    onExplodeSelection: explodeSelectionCommand,
                    onArraySelection: arraySelectionCommand,
                    onPolarArraySelection: polarArraySelectionCommand,
                    onCreateRectangle: drawRectangleCommand,
                    onStartPolygon: startPolygonMode,
                    onCreatePolygon: drawPolygonCommand,
                    selectionCount: selectedLineIds.size,
                    hasSelection: selectedLineIds.size > 0,
                    onUndo: props.onUndo,
                    onRedo: props.onRedo,
                    onZoomExtents: () => zoomExtents(),
                    onOpenSettings: onShowSettings,
                    onOpenKeyModal: props.keyInfo?.onClick,
                    onActivateSelectionMode: () => {
                        finishDrawing();
                        setPendingSelectionCommand(null);
                        stopInteractiveTransforms();
                        setIsSelectionMode(true);
                    },
                    onCancel: () => {
                        if (activeCommandPrompt) {
                            if (isPickingPolarCenter) {
                                cancelPolarCenterPick();
                                return;
                            }
                            cancelCommandPrompt();
                            return;
                        }
                        finishDrawing();
                        setPendingSelectionCommand(null);
                        stopInteractiveTransforms();
                        setIsSelectionMode(false);
                        setIsBoxSelectMode(false);
                        setIsTrimmingLines(false);
                        setIsExtendingLines(false);
                        setIsDeletingLines(false);
                        setIsCopyingSelection(false);
                        setCopyBasePoint(null);
                        setIsOffsetMode(false);
                        setOffsetDistance(null);
                        setOffsetTargetId(null);
                        setOffsetPickStart(null);
                        clearOffsetPreview();
                        setSelectedLineIds(new Set());
                    },
                    onNumericInput: (val: number) => {
                        if (applyCommandPromptValue(String(val), 'number')) return;
                        if (isScalingSelection) {
                            if (!Number.isFinite(val) || val <= 0) {
                                setTerminalIncoming({ text: 'SCALE: Factor must be a positive number', tone: 'error' });
                                return;
                            }
                            const about = scaleBasePoint
                                ? `the reference point E ${scaleBasePoint.easting.toFixed(2)}, N ${scaleBasePoint.northing.toFixed(2)}`
                                : 'the selection center';
                            const scaled = scaleSelectionCommand(val, scaleBasePoint ?? undefined);
                            stopInteractiveTransforms();
                            setCommandFocusRequest(n => n + 1);
                            setTerminalIncoming({
                                text: `SCALE: Scaled ${scaled} ${scaled === 1 ? 'entity' : 'entities'} by ${val}× (${(val * 100).toFixed(1)}%) about ${about}.`,
                                tone: 'ok',
                            });
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (isRotatingSelection) {
                            const turned = rotateSelectionCommand(val, rotatePivot ?? undefined);
                            const about = rotatePivot
                                ? `the base point E ${rotatePivot.easting.toFixed(2)}, N ${rotatePivot.northing.toFixed(2)}`
                                : 'the selection center';
                            stopInteractiveTransforms();
                            setCommandFocusRequest(n => n + 1);
                            setTerminalIncoming({
                                text: `ROTATE: Rotated ${turned} ${turned === 1 ? 'entity' : 'entities'} ${val}° about ${about}.`,
                                tone: 'ok',
                            });
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (isOffsetMode) {
                            if (!Number.isFinite(val) || val <= 0) {
                                setTerminalIncoming({ text: 'OFFSET: Enter a positive distance', tone: 'error' });
                                return;
                            }
                            setOffsetDistance(convertLinearUnits(val, settings.linearUnits || 'usSurveyFoot', 'foot'));
                            setOffsetTargetId(null);
                            setOffsetPickStart(null);
                            clearOffsetPreview();
                            setTerminalIncoming({
                                text: `OFFSET: Distance ${val} ${getLinearUnitAbbreviation(settings.linearUnits)} — click the entity to offset`,
                                tone: 'ok',
                            });
                            return;
                        }
                        if (drawingMode === 'polygon') {
                            if (polygonCenter) {
                                commitPolygonFromRadius(val / 2);
                                return;
                            }
                            setTypedPolygonDiameter(String(val));
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (drawingMode === 'circle') {
                            setTypedCircleValue(String(val));
                        } else if (isLinearDrawingMode(drawingMode)) {
                            setTypedSegmentLength(String(val));
                        }
                    },
                    onCoordinateInput: (pt: { easting: number; northing: number }) => {
                        if (applyCommandPromptValue(`${pt.easting},${pt.northing}`, 'point')) return;
                        if (isScalingSelection) {
                            if (!scaleBasePoint) {
                                const referenceRadius = selectionRadiusFrom(pt);
                                scaleReferenceRadiusRef.current = referenceRadius > 1e-9 ? referenceRadius : null;
                                setScaleBasePoint(pt);
                                setCommandFocusRequest(n => n + 1);
                                setTerminalIncoming({
                                    text: `SCALE: Reference point E ${pt.easting.toFixed(2)}, N ${pt.northing.toFixed(2)} — click to scale or type a factor [Esc to cancel]`,
                                    tone: 'ok',
                                });
                                return;
                            }
                            const distance = Math.hypot(pt.easting - scaleBasePoint.easting, pt.northing - scaleBasePoint.northing);
                            const referenceRadius = scaleReferenceRadiusRef.current;
                            if (distance < 1e-9) {
                                setTerminalIncoming({ text: 'SCALE: Pick a point away from the reference point', tone: 'error' });
                                return;
                            }
                            const factor = referenceRadius && referenceRadius > 1e-9 ? distance / referenceRadius : 1;
                            const scaled = scaleSelectionCommand(factor, scaleBasePoint);
                            stopInteractiveTransforms();
                            setCommandFocusRequest(n => n + 1);
                            setTerminalIncoming({
                                text: `SCALE: Scaled ${scaled} ${scaled === 1 ? 'entity' : 'entities'} by ${factor.toFixed(3)}× (${(factor * 100).toFixed(1)}%) about E ${scaleBasePoint.easting.toFixed(2)}, N ${scaleBasePoint.northing.toFixed(2)}.`,
                                tone: 'ok',
                            });
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (isMirroringSelection) {
                            if (!mirrorAxisStart) {
                                setMirrorAxisStart(pt);
                                setCommandFocusRequest(n => n + 1);
                                setTerminalIncoming({
                                    text: `MIRROR: First axis point E ${pt.easting.toFixed(2)}, N ${pt.northing.toFixed(2)} — click or type the second point [Esc to cancel]`,
                                    tone: 'ok',
                                });
                                return;
                            }
                            if (Math.hypot(pt.easting - mirrorAxisStart.easting, pt.northing - mirrorAxisStart.northing) < 1e-9) {
                                setTerminalIncoming({ text: 'MIRROR: Axis points must be different', tone: 'error' });
                                return;
                            }
                            const mirrored = mirrorSelectionCommand(mirrorAxisStart, pt);
                            stopInteractiveTransforms();
                            setCommandFocusRequest(n => n + 1);
                            setTerminalIncoming({
                                text: `MIRROR: Mirrored ${mirrored} ${mirrored === 1 ? 'entity' : 'entities'}.`,
                                tone: 'ok',
                            });
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (isMovingSelection) {
                            if (!moveBasePoint) {
                                setMoveBasePoint(pt);
                                setCommandFocusRequest(n => n + 1);
                                setTerminalIncoming({
                                    text: `MOVE: Base point E ${pt.easting.toFixed(2)}, N ${pt.northing.toFixed(2)} — click or type the destination [Esc to cancel]`,
                                    tone: 'ok',
                                });
                                return;
                            }
                            const dE = pt.easting - moveBasePoint.easting;
                            const dN = pt.northing - moveBasePoint.northing;
                            if (Math.hypot(dE, dN) < 1e-9) {
                                setTerminalIncoming({ text: 'MOVE: Pick a destination away from the base point', tone: 'error' });
                                return;
                            }
                            const moved = moveSelectionByWorld(dE, dN);
                            stopInteractiveTransforms();
                            setCommandFocusRequest(n => n + 1);
                            setTerminalIncoming({
                                text: `MOVE: Moved ${moved} ${moved === 1 ? 'entity' : 'entities'} (ΔE ${dE.toFixed(2)}, ΔN ${dN.toFixed(2)}).`,
                                tone: 'ok',
                            });
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (isRotatingSelection) {
                            if (!rotatePivot) {
                                setRotatePivot(pt);
                                setCommandFocusRequest(n => n + 1);
                                setTerminalIncoming({
                                    text: `ROTATE: Base point E ${pt.easting.toFixed(2)}, N ${pt.northing.toFixed(2)} — click a point or type the angle [Esc to cancel]`,
                                    tone: 'ok',
                                });
                                return;
                            }
                            const dE = pt.easting - rotatePivot.easting;
                            const dN = pt.northing - rotatePivot.northing;
                            if (Math.hypot(dE, dN) < 1e-9) {
                                setTerminalIncoming({ text: 'ROTATE: Pick a point away from the base point', tone: 'error' });
                                return;
                            }
                            const degrees = (Math.atan2(dN, dE) * 180) / Math.PI;
                            const turned = rotateSelectionCommand(degrees, rotatePivot);
                            stopInteractiveTransforms();
                            setCommandFocusRequest(n => n + 1);
                            setTerminalIncoming({
                                text: `ROTATE: Rotated ${turned} ${turned === 1 ? 'entity' : 'entities'} ${degrees.toFixed(2)}° about E ${rotatePivot.easting.toFixed(2)}, N ${rotatePivot.northing.toFixed(2)}.`,
                                tone: 'ok',
                            });
                            requestAnimationFrame(() => drawRef.current?.());
                            return;
                        }
                        if (drawingMode === 'polygon') {
                            if (!polygonCenter) {
                                setPolygonCenter(makeTempWorldPoint(pt.easting, pt.northing));
                                setTerminalIncoming({
                                    text: `POLYGON: Center E ${pt.easting.toFixed(2)}, N ${pt.northing.toFixed(2)} — drag, click, or type the diameter [${polygonSides} sides]`,
                                    tone: 'ok',
                                });
                                requestAnimationFrame(() => drawRef.current?.());
                                return;
                            }
                            const worldRadius = Math.hypot(pt.easting - polygonCenter.easting, pt.northing - polygonCenter.northing);
                            commitPolygonFromRadius(convertLinearUnits(worldRadius, 'foot', settings.linearUnits || 'usSurveyFoot'));
                            return;
                        }
                        if (drawingMode === 'circle' && !circleCenter) {
                            setCircleCenter(makeTempWorldPoint(pt.easting, pt.northing));
                        } else if (isLinearDrawingMode(drawingMode)) {
                            const newPt = makeTempWorldPoint(pt.easting, pt.northing);
                            setPolylinePoints(prev => [...prev, newPt]);
                        }
                    },
                    keyInfo: props.keyInfo,
                }}
                focusRequest={commandFocusRequest}
                keyInfo={props.keyInfo}
                incomingMessage={terminalIncoming}
                statusBadges={{
                    runningOsnaps: runningOsnapLabel,
                    osnapOverride: inlineOsnapOverride ? OSNAP_LABELS[inlineOsnapOverride] : null,
                    isOrthoEnabled,
                }}
                snapControls={{
                    enabled: isRunningOsnapsEnabled,
                    options: OSNAP_ORDER.map(mode => ({
                        id: mode,
                        label: OSNAP_LABELS[mode],
                        enabled: runningOsnaps.has(mode),
                    })),
                    onToggleEnabled: toggleRunningOsnapsEnabled,
                    onToggleOption: id => {
                        if (OSNAP_ORDER.includes(id as OsnapMode)) toggleRunningOsnap(id as OsnapMode);
                    },
                }}
                agentDrawMode={agentDrawMode}
                agentAnnotMode={agentAnnotMode}
                onCycleAgentDrawMode={onCycleAgentDrawMode}
                onCycleAgentAnnotMode={onCycleAgentAnnotMode}
                activeAgent={activeAgent}
                messages={props.agentMessages}
                onSendMessage={props.onSendAgentMessage}
                onStopGenerating={props.onStopGenerating}
                isLoading={props.isAgentLoading}
                thinkingTime={props.thinkingTime}
                currentModelName={props.currentModelName}
                suggestedQuestions={props.suggestedQuestions}
                onToggleExpandMessage={props.onToggleExpandMessage}
                headerControls={props.chatHeaderControls}
                chatBarAccessories={props.chatBarAccessories}
                toolsRowAccessories={props.toolsRowAccessories}
                hasDockableTools={props.hasDockableTools}
                isToolDockOpen={props.isToolDockOpen}
                onToggleToolDock={props.onToggleToolDock}
                dockedA2UIKey={props.dockedA2UIKey}
                onToggleA2UIDock={props.onToggleA2UIDock}
                chatToolDialogs={props.chatToolDialogs}
                agentChatOpenSignal={props.agentChatOpenSignal}
                boundaryQuickActions={props.boundaryQuickActions}
                onHeightChange={handleCommandBarHeightChange}
                rightExtension={props.commandBarRightExtension ?? 0}
            />

            {/* Operation-anchored arrow pad for multi-value commands */}
            {activeCommandPrompt && (() => {
                const container = containerRef.current;
                const containerW = container ? container.clientWidth : (typeof window !== 'undefined' ? window.innerWidth : 800);
                const containerH = container ? container.clientHeight : (typeof window !== 'undefined' ? window.innerHeight : 600);
                const isArrayStylePrompt = activeCommandPrompt.id === 'array' || activeCommandPrompt.id === 'polar_array';
                const hudW = isArrayStylePrompt ? 320 : 240;
                const hudH = isArrayStylePrompt ? 144 : 244;

                // Selection commands dock beside the selected geometry; drawing
                // and measure commands dock at the view center. ARRAY and
                // POLARARRAY try to sit outside the entire ghost result so the
                // widget never covers the copies being previewed.
                let selectionBounds: { minX: number; minY: number; maxX: number; maxY: number } | null = null;
                let anchorScreen: { x: number; y: number } | null = null;
                const usesSelection = activeCommandPrompt.id === 'array' || activeCommandPrompt.id === 'polar_array';
                const entities = usesSelection ? getSelectedLineEntities() : [];
                if (entities.length > 0) {
                    let minX = Infinity;
                    let minY = Infinity;
                    let maxX = -Infinity;
                    let maxY = -Infinity;
                    const include = (x: number, y: number) => {
                        minX = Math.min(minX, x);
                        minY = Math.min(minY, y);
                        maxX = Math.max(maxX, x);
                        maxY = Math.max(maxY, y);
                    };
                    entities.forEach(entity => {
                        if (entity.isCircle) {
                            const center = entity.line.circleCenter ?? entity.ends.p1;
                            const radius = entity.line.curveRadius ?? 0;
                            include(center.x - radius, center.y - radius);
                            include(center.x + radius, center.y + radius);
                        } else {
                            include(entity.ends.p1.x, entity.ends.p1.y);
                            include(entity.ends.p2.x, entity.ends.p2.y);
                        }
                    });
                    if (Number.isFinite(minX) && Number.isFinite(minY)) {
                        selectionBounds = { minX, minY, maxX, maxY };
                        anchorScreen = worldToScreen(maxX, (minY + maxY) / 2);
                    }
                }

                let hudLeft = (containerW - hudW) / 2;
                let hudTop = (containerH - hudH) / 2;

                if (isArrayStylePrompt && selectionBounds) {
                    const read = (key: string, fallback: number): number => {
                        const raw = commandPromptValues[key];
                        if (raw === undefined || raw.trim() === '') return fallback;
                        const parsed = Number(raw);
                        return Number.isFinite(parsed) ? parsed : fallback;
                    };
                    const rows = Math.max(1, Math.round(read('rows', 1)));
                    const columns = Math.max(1, Math.round(read('columns', 1)));
                    const rowSpacing = read('rowSpacing', 0);
                    const columnSpacing = read('columnSpacing', 0);
                    const angle = read('angle', 0);
                    let arrayLeft = 0;
                    let arrayRight = 0;
                    let arrayTop = 0;
                    let arrayBottom = 0;

                    if (activeCommandPrompt.id === 'array') {
                        const unit = settings.linearUnits || 'usSurveyFoot';
                        const rowGap = convertLinearUnits(rowSpacing, unit, 'foot');
                        const columnGap = convertLinearUnits(columnSpacing, unit, 'foot');
                        const direction = (angle * Math.PI) / 180;
                        const cos = Math.cos(direction);
                        const sin = Math.sin(direction);
                        const xOffsets = [0, (columns - 1) * columnGap];
                        const yOffsets = [0, (rows - 1) * rowGap];
                        let minDx = 0;
                        let maxDx = 0;
                        let minDy = 0;
                        let maxDy = 0;
                        for (const x of xOffsets) {
                            for (const y of yOffsets) {
                                const dx = x * cos - y * sin;
                                const dy = x * sin + y * cos;
                                minDx = Math.min(minDx, dx);
                                maxDx = Math.max(maxDx, dx);
                                minDy = Math.min(minDy, dy);
                                maxDy = Math.max(maxDy, dy);
                            }
                        }
                        const worldMinX = selectionBounds.minX + minDx;
                        const worldMaxX = selectionBounds.maxX + maxDx;
                        const worldMinY = selectionBounds.minY + minDy;
                        const worldMaxY = selectionBounds.maxY + maxDy;
                        const topLeft = worldToScreen(worldMinX, worldMaxY);
                        const bottomRight = worldToScreen(worldMaxX, worldMinY);
                        arrayLeft = Math.min(topLeft.x, bottomRight.x);
                        arrayRight = Math.max(topLeft.x, bottomRight.x);
                        arrayTop = Math.min(topLeft.y, bottomRight.y);
                        arrayBottom = Math.max(topLeft.y, bottomRight.y);
                    } else {
                        const count = Math.max(2, Math.round(read('count', 6)));
                        const fillAngle = Math.min(360, Math.max(1, read('fillAngle', 360)));
                        const centerToken = (commandPromptValues.center ?? '').trim();
                        const parsedCenter = centerToken ? parseCoordinateToken(centerToken) : null;
                        const center = parsedCenter
                            ? { x: parsedCenter.easting, y: parsedCenter.northing }
                            : {
                                x: (selectionBounds.minX + selectionBounds.maxX) / 2,
                                y: (selectionBounds.minY + selectionBounds.maxY) / 2,
                            };
                        const step = fillAngle >= 359.999
                            ? fillAngle / count
                            : fillAngle / Math.max(1, count - 1);
                        const corners = [
                            { x: selectionBounds.minX, y: selectionBounds.minY },
                            { x: selectionBounds.maxX, y: selectionBounds.minY },
                            { x: selectionBounds.maxX, y: selectionBounds.maxY },
                            { x: selectionBounds.minX, y: selectionBounds.maxY },
                        ];
                        let worldMinX = Infinity;
                        let worldMinY = Infinity;
                        let worldMaxX = -Infinity;
                        let worldMaxY = -Infinity;
                        for (let item = 0; item < count; item++) {
                            const radians = (item * step * Math.PI) / 180;
                            const cos = Math.cos(radians);
                            const sin = Math.sin(radians);
                            for (const corner of corners) {
                                const dx = corner.x - center.x;
                                const dy = corner.y - center.y;
                                const x = center.x + dx * cos - dy * sin;
                                const y = center.y + dx * sin + dy * cos;
                                worldMinX = Math.min(worldMinX, x);
                                worldMinY = Math.min(worldMinY, y);
                                worldMaxX = Math.max(worldMaxX, x);
                                worldMaxY = Math.max(worldMaxY, y);
                            }
                        }
                        const topLeft = worldToScreen(worldMinX, worldMaxY);
                        const bottomRight = worldToScreen(worldMaxX, worldMinY);
                        arrayLeft = Math.min(topLeft.x, bottomRight.x);
                        arrayRight = Math.max(topLeft.x, bottomRight.x);
                        arrayTop = Math.min(topLeft.y, bottomRight.y);
                        arrayBottom = Math.max(topLeft.y, bottomRight.y);
                    }
                    const maxLeft = Math.max(10, containerW - hudW - 10);
                    const maxTop = Math.max(10, containerH - hudH - 78);
                    const place = (left: number, top: number) => ({
                        left: Math.max(10, Math.min(left, maxLeft)),
                        top: Math.max(10, Math.min(top, maxTop)),
                    });
                    const overlapArea = (candidate: { left: number; top: number }) => {
                        const width = Math.max(0, Math.min(candidate.left + hudW, arrayRight) - Math.max(candidate.left, arrayLeft));
                        const height = Math.max(0, Math.min(candidate.top + hudH, arrayBottom) - Math.max(candidate.top, arrayTop));
                        return width * height;
                    };
                    const gap = 18;
                    const arrayCenterX = (arrayLeft + arrayRight) / 2;
                    const arrayCenterY = (arrayTop + arrayBottom) / 2;
                    const candidates: Array<{ key: string; left: number; top: number }> = [
                        { key: 'right', ...place(arrayRight + gap, arrayTop + (arrayBottom - arrayTop - hudH) / 2) },
                        { key: 'left', ...place(arrayLeft - gap - hudW, arrayTop + (arrayBottom - arrayTop - hudH) / 2) },
                        { key: 'below', ...place(arrayLeft + (arrayRight - arrayLeft - hudW) / 2, arrayBottom + gap) },
                        { key: 'above', ...place(arrayLeft + (arrayRight - arrayLeft - hudW) / 2, arrayTop - gap - hudH) },
                        { key: 'right-below', ...place(arrayRight + gap, arrayBottom + gap) },
                        { key: 'right-above', ...place(arrayRight + gap, arrayTop - gap - hudH) },
                        { key: 'left-below', ...place(arrayLeft - gap - hudW, arrayBottom + gap) },
                        { key: 'left-above', ...place(arrayLeft - gap - hudW, arrayTop - gap - hudH) },
                        { key: 'container-tl', ...place(10, 10) },
                        { key: 'container-tr', ...place(maxLeft, 10) },
                        { key: 'container-bl', ...place(10, maxTop) },
                        { key: 'container-br', ...place(maxLeft, maxTop) },
                    ];
                    const score = (candidate: { left: number; top: number }) => {
                        const overlap = overlapArea(candidate);
                        const cx = candidate.left + hudW / 2;
                        const cy = candidate.top + hudH / 2;
                        return overlap * 1000 + Math.hypot(cx - arrayCenterX, cy - arrayCenterY);
                    };
                    const isClear = (candidate: { left: number; top: number }) => overlapArea(candidate) <= 0.5;
                    const stickyKey = arrayWidgetSideRef.current;
                    const sticky = stickyKey ? candidates.find(candidate => candidate.key === stickyKey) : undefined;
                    let chosen = sticky && isClear(sticky) ? sticky : undefined;

                    // Hysteresis: keep the current side while it is clear. Only
                    // reconsider when it is actually covered; if every side is
                    // covered, hold the current one rather than bouncing between
                    // equally bad candidates.
                    if (!chosen) {
                        const clearCandidates = candidates.filter(isClear);
                        if (clearCandidates.length > 0) {
                            chosen = clearCandidates.reduce(
                                (best, candidate) => (score(candidate) < score(best) ? candidate : best),
                                clearCandidates[0],
                            );
                        } else if (sticky) {
                            chosen = sticky;
                        } else {
                            chosen = candidates.reduce(
                                (best, candidate) => (score(candidate) < score(best) ? candidate : best),
                                candidates[0],
                            );
                        }
                    }

                    arrayWidgetSideRef.current = chosen.key;
                    hudLeft = chosen.left;
                    hudTop = chosen.top;
                } else if (!isArrayStylePrompt) {
                    hudLeft = anchorScreen ? anchorScreen.x + 20 : (containerW - hudW) / 2;
                    if (anchorScreen && hudLeft + hudW > containerW - 10) {
                        hudLeft = anchorScreen.x - hudW - 20;
                    }
                    hudLeft = Math.max(10, Math.min(hudLeft, Math.max(10, containerW - hudW - 10)));
                    const anchorTop = anchorScreen ? anchorScreen.y - hudH / 2 : (containerH - hudH) / 2;
                    hudTop = Math.max(10, Math.min(anchorTop, Math.max(10, containerH - hudH - 78)));
                }
                if (activeCommandPrompt.id === 'array') {
                    return (
                        <ArrayManipulator
                            values={commandPromptValues}
                            onChange={handleCommandPromptChange}
                            onActivate={handleCommandPromptActivate}
                            onSubmit={submitCommandPrompt}
                            onCancel={cancelCommandPrompt}
                            x={hudLeft}
                            y={hudTop}
                            pixelsPerUnit={Math.max(1e-6, transform.scale * convertLinearUnits(1, settings.linearUnits || 'usSurveyFoot', 'foot'))}
                            unitLabel={getLinearUnitAbbreviation(settings.linearUnits)}
                        />
                    );
                }
                if (activeCommandPrompt.id === 'polar_array') {
                    return (
                        <PolarArrayManipulator
                            values={commandPromptValues}
                            onChange={handleCommandPromptChange}
                            onActivate={handleCommandPromptActivate}
                            onSubmit={submitCommandPrompt}
                            onCancel={() => {
                                if (isPickingPolarCenter) cancelPolarCenterPick();
                                else cancelCommandPrompt();
                            }}
                            isPickingCenter={isPickingPolarCenter}
                            onPickCenter={togglePolarCenterPick}
                            x={hudLeft}
                            y={hudTop}
                        />
                    );
                }
                return (
                    <CommandPromptHud
                        prompt={activeCommandPrompt}
                        values={commandPromptValues}
                        activeIndex={commandPromptIndex}
                        x={hudLeft}
                        y={hudTop}
                        onChange={handleCommandPromptChange}
                        onActivate={handleCommandPromptActivate}
                        onSubmit={submitCommandPrompt}
                        onCancel={cancelCommandPrompt}
                    />
                );
            })()}

            {/* Symbol-exclusion hover tooltip */}
            {symbolExclusionMode && exclusionHover && (
                <div
                    className="pointer-events-none fixed z-[10000] max-w-xs px-3 py-2 rounded-md text-xs shadow-lg border bg-gray-900/95 border-gray-700 text-gray-100"
                    style={{ left: exclusionHover.clientX + 16, top: exclusionHover.clientY + 16 }}
                >
                    <div className="font-semibold text-cyan-300">{exclusionHover.description}</div>
                    {exclusionHover.symbolName && (
                        <div className="text-gray-300">Symbol: <span className="text-white">{exclusionHover.symbolName}</span></div>
                    )}
                    <div className="mt-1 text-gray-400">{exclusionHover.reason}</div>
                    <div className={`mt-1 font-medium ${exclusionHover.excluded ? 'text-red-400' : 'text-green-400'}`}>
                        {exclusionHover.excluded ? 'Currently excluded — click to restore' : 'Click to exclude this description'}
                    </div>
                </div>
            )}
        </div>
    );
});
